import { TextSelection, NodeSelection, PluginKey, Selection, AllSelection, Plugin } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { NodeExtension } from '../../../core/index.js';
import { exitCode } from 'prosemirror-commands';
import { InputRule } from 'prosemirror-inputrules';
import { Slice, Fragment } from 'prosemirror-model';
import { GapCursor, gapCursorSituation } from '../../../citadel/extensions/gap-cursor-extension/index.js';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { undo, redo } from 'prosemirror-history';
import CodeMirror from 'codemirror';
import DOMPurify from 'dompurify';

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var noop = function() {};
function getInsertBlockNodes(state, node) {
    var schema = state.schema, selection = state.selection, doc = state.doc;
    var paragraph = schema.nodes.paragraph;
    var $from = selection.$from, $to = selection.$to, to = selection.to;
    var afterNode = $to.nodeAfter;
    var fromNodeGrandParent = $from.node(-1);
    var fromNodeParent = $from.node();
    // 光标在文档尾部
    if (!afterNode) {
        var nodes = [
            node,
            paragraph.create()
        ];
        // 是否在文档尾部后单元格尾部
        var isInTail = to === doc.content.size - 1 || (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;
        if (isInTail) return nodes;
        // 后面一行是否为文本块节点（即有内联内容的块级节点，schema中定义content为inline或者text的节点例如段落、标题, ）
        var nextNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;
        if (nextNode && !nextNode.isTextblock && !nextNode.isText) {
            // 后面一行为非文本块节点
            return nodes;
        }
    }
    return [
        node
    ];
}
var isParagraph = function(node) {
    var _node_type;
    return (node === null || node === void 0 ? void 0 : (_node_type = node.type) === null || _node_type === void 0 ? void 0 : _node_type.name) === "paragraph";
};
// 插入块处理
function insertBlock(state, node) {
    var options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        selection: null,
        cursorMove: true
    };
    if (!node.isBlock) {
        throw new Error("必须传入 block node");
    }
    var tr = state.tr, schema = state.schema;
    var selection = options.selection || state.selection;
    var cursorMove = options.cursorMove;
    var _schema_nodes = schema.nodes, table = _schema_nodes.table, note = _schema_nodes.note;
    var from = selection.from, $from = selection.$from;
    // TODO: 为什么需要清除格式
    tr.setStoredMarks([]); // 清除格式
    var insertingNodes = getInsertBlockNodes(state, node);
    // TODO: selection.replace 并不是常用的函数，是否可以替换成 tr.replaceWith
    selection.replace(tr, new Slice(Fragment.from(insertingNodes), 0, 0));
    // transform 1.1 版本会在 replace 过程中增加多余的空行，此处进行判断并清理
    // transform 升级到 1.3 之后可以恢复此段代码的处理，不需要清理多余的空行
    // https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/20632944
    var step = tr.steps[0];
    if (step) {
        var slice = step === null || step === void 0 ? void 0 : step.slice;
        var content = (slice === null || slice === void 0 ? void 0 : slice.content).content;
        var size = content.length;
        // 末尾两个元素是否为 p 标签
        var removeParagraph = size > 2 && isParagraph(content[size - 1]) && isParagraph(content[size - 2]);
        if (removeParagraph) {
            // 如果替换时最后两个node都是 paragraph，说明有多余的空行，需要删除最后一个空行
            var lastPos = tr.mapping.map(step.to);
            tr.delete(lastPos - 2, lastPos);
        }
    }
    tr.setSelection(new TextSelection(tr.doc.resolve(tr.mapping.map(state.selection.from)), tr.doc.resolve(tr.mapping.map(state.selection.to))));
    // TODO:下面这段代码优化
    // 移动光标位置
    if (cursorMove) {
        var nodeBefore = $from.nodeBefore; // 光标前相邻节点
        var offset = nodeBefore ? 2 : 0;
        if (node.type.isLeaf) {
            offset += 1;
        } else if (node.type === note) {
            offset += 4;
        } else if (node.type === table) {
            offset += 3;
        }
        var newPos = selection.constructor.name === "GapCursor" ? from : from + offset;
        tr.setSelection(TextSelection.create(tr.doc, newPos));
    }
    return {
        transaction: tr
    };
}
var execAfterCommands = function(commands) {
    var someThingFn = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : noop;
    return function() {
        for(var _len = arguments.length, cmdArgs = new Array(_len), _key = 0; _key < _len; _key++){
            cmdArgs[_key] = arguments[_key];
        }
        return function(state, dispatch, view) {
            var _ = commands.apply(void 0, _toConsumableArray(cmdArgs))(state, dispatch, view);
            someThingFn(_);
            return _;
        };
    };
};

var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
var findParentNode = function(predicate) {
    return function(param) {
        var $from = param.$from;
        return findParentNodeClosestToPos($from, predicate);
    };
};
// 类型是否可以插入富文本
var canInsertTypes = function(state) {
    var parentNode = state.selection.$from.parent;
    var textType = [
        "status",
        "code_block",
        "note_title",
        "collapse_title"
    ];
    return !textType.includes(parentNode.type.name);
};
var blockInputRule = function(regexp, nodeType) {
    return new InputRule(regexp, function(state, match, start, end) {
        var transaction = insertBlock(state, nodeType.create()).transaction;
        // 删除快捷键
        transaction.deleteRange(start - 1, end);
        return transaction;
    });
};

function insert() {
    return function(state, dispatch) {
        var schema = state.schema;
        var node = schema.nodes.code_block.create({
            isCreate: true,
            language: localStorage.getItem("cmLanguage") || ""
        });
        var transaction = insertBlock(state, node).transaction;
        dispatch(transaction.scrollIntoView());
        return true;
    };
}

function isInCode() {
    return function(state) {
        if (!state) return false;
        var selection = state.selection;
        var foundNode = findParentNode(function(node) {
            return node.type && node.type.name === "code_block";
        })(selection);
        return !!foundNode;
    };
}

var convertCommand = function(commandFunction) {
    return function(param) {
        var state = param.state, dispatch = param.dispatch, view = param.view, args = param.args;
        return commandFunction(state, dispatch, view, args);
    };
};

function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var backspaceAfterCode = function() {
    return function(state, dispatch) {
        var tr = state.tr, selection = state.selection;
        if (_instanceof(selection, GapCursor)) {
            var targetNode = selection.targetNode, $from = selection.$from;
            var res = GapCursor.findFrom($from, -1);
            if (targetNode.type.name === "code_block" && selection.gapCursorType === gapCursorSituation.afterNode) {
                var newSelection = NodeSelection.create(tr.doc, res.pos - targetNode.nodeSize);
                dispatch(tr.setSelection(newSelection));
                return true;
            }
        }
        return false;
    };
};

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = ".ct-code{min-width:233px;margin:5px 0;font-size:14px;border:1px solid #ddd;border-radius:2px 2px;background:#fcfcfc;font-family:monospace;line-height:21px;position:relative}.ProseMirror-selectednode.ct-code{outline:none;border:1px solid #3d91f2;box-shadow:0 0 4px 0 rgba(0,0,0,.1),0 3px 6px 0 rgba(0,0,0,.14)}.ct-code-selected{border-radius:3px;border:1px solid #0082a6;margin:5px 0;opacity:.7}.ct-code-selected .CodeMirror,.ct-code-selected .ct-code{margin:0}";
styleInject(css_248z);

/**
 * keys--不区分大小写
 */ // "META_KEY-0"-正文
// 代码块
var regexpCode = /^```\n$/;

var ExtensionType;
(function(ExtensionType) {
    ExtensionType["Node"] = "node";
    ExtensionType["Mark"] = "mark";
    ExtensionType["NodeEmbed"] = "node-embed";
})(ExtensionType || (ExtensionType = {}));

/**
 * 通用配置
 */ var pluginKey = new PluginKey("image");

var AFTER_INSERT = "after_insert";

var IS_MAC = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
var Keycodes;
(function(Keycodes) {
    Keycodes[Keycodes["Enter"] = 13] = "Enter";
    Keycodes[Keycodes["BackSpace"] = 8] = "BackSpace";
    Keycodes[Keycodes["Tab"] = 9] = "Tab";
    Keycodes[Keycodes["Esc"] = 27] = "Esc";
    Keycodes[Keycodes["Space"] = 32] = "Space";
    Keycodes[Keycodes["Left"] = 37] = "Left";
    Keycodes[Keycodes["Up"] = 38] = "Up";
    Keycodes[Keycodes["Right"] = 39] = "Right";
    Keycodes[Keycodes["Down"] = 40] = "Down";
    Keycodes[Keycodes["Delete"] = 46] = "Delete";
    Keycodes[Keycodes["a"] = 65] = "a";
    Keycodes[Keycodes["c"] = 67] = "c";
    Keycodes[Keycodes["LeftBracket"] = 219] = "LeftBracket";
    Keycodes[Keycodes["RightBracket"] = 221] = "RightBracket";
    Keycodes[Keycodes["CompositionEnd"] = 229] = "CompositionEnd";
})(Keycodes || (Keycodes = {}));

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var codemirror = createCommonjsModule(function (module, exports) {
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// This is CodeMirror (https://codemirror.net), a code editor
// implemented in JavaScript on top of the browser's DOM.
//
// You can find some technical background for some of the code below
// at http://marijnhaverbeke.nl/blog/#cm-internals .
(function(global, factory) {
     module.exports = factory() ;
})(commonjsGlobal, function() {
    var classTest = function classTest(cls) {
        return new RegExp("(^|\\s)" + cls + "(?:$|\\s)\\s*");
    };
    var removeChildren = function removeChildren(e) {
        for(var count = e.childNodes.length; count > 0; --count){
            e.removeChild(e.firstChild);
        }
        return e;
    };
    var removeChildrenAndAdd = function removeChildrenAndAdd(parent, e) {
        return removeChildren(parent).appendChild(e);
    };
    var elt = function elt(tag, content, className, style) {
        var e = document.createElement(tag);
        if (className) {
            e.className = className;
        }
        if (style) {
            e.style.cssText = style;
        }
        if (typeof content == "string") {
            e.appendChild(document.createTextNode(content));
        } else if (content) {
            for(var i = 0; i < content.length; ++i){
                e.appendChild(content[i]);
            }
        }
        return e;
    };
    var eltP = // wrapper for elt, which removes the elt from the accessibility tree
    function eltP(tag, content, className, style) {
        var e = elt(tag, content, className, style);
        e.setAttribute("role", "presentation");
        return e;
    };
    var contains = function contains(parent, child) {
        if (child.nodeType == 3) {
            child = child.parentNode;
        }
        if (parent.contains) {
            return parent.contains(child);
        }
        do {
            if (child.nodeType == 11) {
                child = child.host;
            }
            if (child == parent) {
                return true;
            }
        }while (child = child.parentNode);
    };
    var activeElt = function activeElt() {
        // IE and Edge may throw an "Unspecified Error" when accessing document.activeElement.
        // IE < 10 will throw when accessed while the page is loading or in an iframe.
        // IE > 9 and Edge will throw when accessed in an iframe if document.body is unavailable.
        var activeElement;
        try {
            activeElement = document.activeElement;
        } catch (e) {
            activeElement = document.body || null;
        }
        while(activeElement && activeElement.shadowRoot && activeElement.shadowRoot.activeElement){
            activeElement = activeElement.shadowRoot.activeElement;
        }
        return activeElement;
    };
    var addClass = function addClass(node, cls) {
        var current = node.className;
        if (!classTest(cls).test(current)) {
            node.className += (current ? " " : "") + cls;
        }
    };
    var joinClasses = function joinClasses(a, b) {
        var as = a.split(" ");
        for(var i = 0; i < as.length; i++){
            if (as[i] && !classTest(as[i]).test(b)) {
                b += " " + as[i];
            }
        }
        return b;
    };
    var bind = function bind(f) {
        var args = Array.prototype.slice.call(arguments, 1);
        return function() {
            return f.apply(null, args);
        };
    };
    var copyObj = function copyObj(obj, target, overwrite) {
        if (!target) {
            target = {};
        }
        for(var prop in obj){
            if (obj.hasOwnProperty(prop) && (overwrite !== false || !target.hasOwnProperty(prop))) {
                target[prop] = obj[prop];
            }
        }
        return target;
    };
    var countColumn = // Counts the column offset in a string, taking tabs into account.
    // Used mostly to find indentation.
    function countColumn(string, end, tabSize, startIndex, startValue) {
        if (end == null) {
            end = string.search(/[^\s\u00a0]/);
            if (end == -1) {
                end = string.length;
            }
        }
        for(var i = startIndex || 0, n = startValue || 0;;){
            var nextTab = string.indexOf("	", i);
            if (nextTab < 0 || nextTab >= end) {
                return n + (end - i);
            }
            n += nextTab - i;
            n += tabSize - n % tabSize;
            i = nextTab + 1;
        }
    };
    var indexOf = function indexOf(array, elt) {
        for(var i = 0; i < array.length; ++i){
            if (array[i] == elt) {
                return i;
            }
        }
        return -1;
    };
    var findColumn = // The inverse of countColumn -- find the offset that corresponds to
    // a particular column.
    function findColumn(string, goal, tabSize) {
        for(var pos = 0, col = 0;;){
            var nextTab = string.indexOf("	", pos);
            if (nextTab == -1) {
                nextTab = string.length;
            }
            var skipped = nextTab - pos;
            if (nextTab == string.length || col + skipped >= goal) {
                return pos + Math.min(skipped, goal - col);
            }
            col += nextTab - pos;
            col += tabSize - col % tabSize;
            pos = nextTab + 1;
            if (col >= goal) {
                return pos;
            }
        }
    };
    var spaceStr = function spaceStr(n) {
        while(spaceStrs.length <= n){
            spaceStrs.push(lst(spaceStrs) + " ");
        }
        return spaceStrs[n];
    };
    var lst = function lst(arr) {
        return arr[arr.length - 1];
    };
    var map = function map(array, f) {
        var out = [];
        for(var i = 0; i < array.length; i++){
            out[i] = f(array[i], i);
        }
        return out;
    };
    var insertSorted = function insertSorted(array, value, score) {
        var pos = 0, priority = score(value);
        while(pos < array.length && score(array[pos]) <= priority){
            pos++;
        }
        array.splice(pos, 0, value);
    };
    var nothing = function nothing() {};
    var createObj = function createObj(base, props) {
        var inst;
        if (Object.create) {
            inst = Object.create(base);
        } else {
            nothing.prototype = base;
            inst = new nothing();
        }
        if (props) {
            copyObj(props, inst);
        }
        return inst;
    };
    var isWordCharBasic = function isWordCharBasic(ch) {
        return /\w/.test(ch) || ch > "\x80" && (ch.toUpperCase() != ch.toLowerCase() || nonASCIISingleCaseWordChar.test(ch));
    };
    var isWordChar = function isWordChar(ch, helper) {
        if (!helper) {
            return isWordCharBasic(ch);
        }
        if (helper.source.indexOf("\\w") > -1 && isWordCharBasic(ch)) {
            return true;
        }
        return helper.test(ch);
    };
    var isEmpty = function isEmpty(obj) {
        for(var n in obj){
            if (obj.hasOwnProperty(n) && obj[n]) {
                return false;
            }
        }
        return true;
    };
    var isExtendingChar = function isExtendingChar(ch) {
        return ch.charCodeAt(0) >= 768 && extendingChars.test(ch);
    };
    var skipExtendingChars = // Returns a number from the range [`0`; `str.length`] unless `pos` is outside that range.
    function skipExtendingChars(str, pos, dir) {
        while((dir < 0 ? pos > 0 : pos < str.length) && isExtendingChar(str.charAt(pos))){
            pos += dir;
        }
        return pos;
    };
    var findFirst = // Returns the value from the range [`from`; `to`] that satisfies
    // `pred` and is closest to `from`. Assumes that at least `to`
    // satisfies `pred`. Supports `from` being greater than `to`.
    function findFirst(pred, from, to) {
        // At any point we are certain `to` satisfies `pred`, don't know
        // whether `from` does.
        var dir = from > to ? -1 : 1;
        for(;;){
            if (from == to) {
                return from;
            }
            var midF = (from + to) / 2, mid = dir < 0 ? Math.ceil(midF) : Math.floor(midF);
            if (mid == from) {
                return pred(mid) ? from : to;
            }
            if (pred(mid)) {
                to = mid;
            } else {
                from = mid + dir;
            }
        }
    };
    var iterateBidiSections = // BIDI HELPERS
    function iterateBidiSections(order, from, to, f) {
        if (!order) {
            return f(from, to, "ltr", 0);
        }
        var found = false;
        for(var i = 0; i < order.length; ++i){
            var part = order[i];
            if (part.from < to && part.to > from || from == to && part.to == from) {
                f(Math.max(part.from, from), Math.min(part.to, to), part.level == 1 ? "rtl" : "ltr", i);
                found = true;
            }
        }
        if (!found) {
            f(from, to, "ltr");
        }
    };
    var getBidiPartAt = function getBidiPartAt(order, ch, sticky) {
        var found;
        bidiOther = null;
        for(var i = 0; i < order.length; ++i){
            var cur = order[i];
            if (cur.from < ch && cur.to > ch) {
                return i;
            }
            if (cur.to == ch) {
                if (cur.from != cur.to && sticky == "before") {
                    found = i;
                } else {
                    bidiOther = i;
                }
            }
            if (cur.from == ch) {
                if (cur.from != cur.to && sticky != "before") {
                    found = i;
                } else {
                    bidiOther = i;
                }
            }
        }
        return found != null ? found : bidiOther;
    };
    var getOrder = // Get the bidi ordering for the given line (and cache it). Returns
    // false for lines that are fully left-to-right, and an array of
    // BidiSpan objects otherwise.
    function getOrder(line, direction) {
        var order = line.order;
        if (order == null) {
            order = line.order = bidiOrdering(line.text, direction);
        }
        return order;
    };
    var getHandlers = function getHandlers(emitter, type) {
        return emitter._handlers && emitter._handlers[type] || noHandlers;
    };
    var off = function off(emitter, type, f) {
        if (emitter.removeEventListener) {
            emitter.removeEventListener(type, f, false);
        } else if (emitter.detachEvent) {
            emitter.detachEvent("on" + type, f);
        } else {
            var map = emitter._handlers, arr = map && map[type];
            if (arr) {
                var index = indexOf(arr, f);
                if (index > -1) {
                    map[type] = arr.slice(0, index).concat(arr.slice(index + 1));
                }
            }
        }
    };
    var signal = function signal(emitter, type /*, values...*/ ) {
        var handlers = getHandlers(emitter, type);
        if (!handlers.length) {
            return;
        }
        var args = Array.prototype.slice.call(arguments, 2);
        for(var i = 0; i < handlers.length; ++i){
            handlers[i].apply(null, args);
        }
    };
    var signalDOMEvent = // The DOM events that CodeMirror handles can be overridden by
    // registering a (non-DOM) handler on the editor for the event name,
    // and preventDefault-ing the event in that handler.
    function signalDOMEvent(cm, e, override) {
        if (typeof e == "string") {
            e = {
                type: e,
                preventDefault: function preventDefault() {
                    this.defaultPrevented = true;
                }
            };
        }
        signal(cm, override || e.type, cm, e);
        return e_defaultPrevented(e) || e.codemirrorIgnore;
    };
    var signalCursorActivity = function signalCursorActivity(cm) {
        var arr = cm._handlers && cm._handlers.cursorActivity;
        if (!arr) {
            return;
        }
        var set = cm.curOp.cursorActivityHandlers || (cm.curOp.cursorActivityHandlers = []);
        for(var i = 0; i < arr.length; ++i){
            if (indexOf(set, arr[i]) == -1) {
                set.push(arr[i]);
            }
        }
    };
    var hasHandler = function hasHandler(emitter, type) {
        return getHandlers(emitter, type).length > 0;
    };
    var eventMixin = // Add on and off methods to a constructor's prototype, to make
    // registering events on such objects more convenient.
    function eventMixin(ctor) {
        ctor.prototype.on = function(type, f) {
            on(this, type, f);
        };
        ctor.prototype.off = function(type, f) {
            off(this, type, f);
        };
    };
    var e_preventDefault = // Due to the fact that we still support jurassic IE versions, some
    // compatibility wrappers are needed.
    function e_preventDefault(e) {
        if (e.preventDefault) {
            e.preventDefault();
        } else {
            e.returnValue = false;
        }
    };
    var e_stopPropagation = function e_stopPropagation(e) {
        if (e.stopPropagation) {
            e.stopPropagation();
        } else {
            e.cancelBubble = true;
        }
    };
    var e_defaultPrevented = function e_defaultPrevented(e) {
        return e.defaultPrevented != null ? e.defaultPrevented : e.returnValue == false;
    };
    var e_stop = function e_stop(e) {
        e_preventDefault(e);
        e_stopPropagation(e);
    };
    var e_target = function e_target(e) {
        return e.target || e.srcElement;
    };
    var e_button = function e_button(e) {
        var b = e.which;
        if (b == null) {
            if (e.button & 1) {
                b = 1;
            } else if (e.button & 2) {
                b = 3;
            } else if (e.button & 4) {
                b = 2;
            }
        }
        if (mac && e.ctrlKey && b == 1) {
            b = 3;
        }
        return b;
    };
    var zeroWidthElement = function zeroWidthElement(measure) {
        if (zwspSupported == null) {
            var test = elt("span", "​");
            removeChildrenAndAdd(measure, elt("span", [
                test,
                document.createTextNode("x")
            ]));
            if (measure.firstChild.offsetHeight != 0) {
                zwspSupported = test.offsetWidth <= 1 && test.offsetHeight > 2 && !(ie && ie_version < 8);
            }
        }
        var node = zwspSupported ? elt("span", "​") : elt("span", "\xa0", null, "display: inline-block; width: 1px; margin-right: -1px");
        node.setAttribute("cm-text", "");
        return node;
    };
    var hasBadBidiRects = function hasBadBidiRects(measure) {
        if (badBidiRects != null) {
            return badBidiRects;
        }
        var txt = removeChildrenAndAdd(measure, document.createTextNode("AخA"));
        var r0 = range(txt, 0, 1).getBoundingClientRect();
        var r1 = range(txt, 1, 2).getBoundingClientRect();
        removeChildren(measure);
        if (!r0 || r0.left == r0.right) {
            return false;
        } // Safari returns null in some cases (#2780)
        return badBidiRects = r1.right - r0.right < 3;
    };
    var hasBadZoomedRects = function hasBadZoomedRects(measure) {
        if (badZoomedRects != null) {
            return badZoomedRects;
        }
        var node = removeChildrenAndAdd(measure, elt("span", "x"));
        var normal = node.getBoundingClientRect();
        var fromRange = range(node, 0, 1).getBoundingClientRect();
        return badZoomedRects = Math.abs(normal.left - fromRange.left) > 1;
    };
    var defineMode = // Extra arguments are stored as the mode's dependencies, which is
    // used by (legacy) mechanisms like loadmode.js to automatically
    // load a mode. (Preferred mechanism is the require/define calls.)
    function defineMode(name, mode) {
        if (arguments.length > 2) {
            mode.dependencies = Array.prototype.slice.call(arguments, 2);
        }
        modes[name] = mode;
    };
    var defineMIME = function defineMIME(mime, spec) {
        mimeModes[mime] = spec;
    };
    var extendMode = function extendMode(mode, properties) {
        var exts = modeExtensions.hasOwnProperty(mode) ? modeExtensions[mode] : modeExtensions[mode] = {};
        copyObj(properties, exts);
    };
    var copyState = function copyState(mode, state) {
        if (state === true) {
            return state;
        }
        if (mode.copyState) {
            return mode.copyState(state);
        }
        var nstate = {};
        for(var n in state){
            var val = state[n];
            if (_instanceof(val, Array)) {
                val = val.concat([]);
            }
            nstate[n] = val;
        }
        return nstate;
    };
    var innerMode = // Given a mode and a state (for that mode), find the inner mode and
    // state at the position that the state refers to.
    function innerMode(mode, state) {
        var info;
        while(mode.innerMode){
            info = mode.innerMode(state);
            if (!info || info.mode == mode) {
                break;
            }
            state = info.state;
            mode = info.mode;
        }
        return info || {
            mode: mode,
            state: state
        };
    };
    var startState = function startState(mode, a1, a2) {
        return mode.startState ? mode.startState(a1, a2) : true;
    };
    var getLine = // Find the line object corresponding to the given line number.
    function getLine(doc, n) {
        n -= doc.first;
        if (n < 0 || n >= doc.size) {
            throw new Error("There is no line " + (n + doc.first) + " in the document.");
        }
        var chunk = doc;
        while(!chunk.lines){
            for(var i = 0;; ++i){
                var child = chunk.children[i], sz = child.chunkSize();
                if (n < sz) {
                    chunk = child;
                    break;
                }
                n -= sz;
            }
        }
        return chunk.lines[n];
    };
    var getBetween = // Get the part of a document between two positions, as an array of
    // strings.
    function getBetween(doc, start, end) {
        var out = [], n = start.line;
        doc.iter(start.line, end.line + 1, function(line) {
            var text = line.text;
            if (n == end.line) {
                text = text.slice(0, end.ch);
            }
            if (n == start.line) {
                text = text.slice(start.ch);
            }
            out.push(text);
            ++n;
        });
        return out;
    };
    var getLines = // Get the lines between from and to, as array of strings.
    function getLines(doc, from, to) {
        var out = [];
        doc.iter(from, to, function(line) {
            out.push(line.text);
        }); // iter aborts when callback returns truthy value
        return out;
    };
    var updateLineHeight = // Update the height of a line, propagating the height change
    // upwards to parent nodes.
    function updateLineHeight(line, height) {
        var diff = height - line.height;
        if (diff) {
            for(var n = line; n; n = n.parent){
                n.height += diff;
            }
        }
    };
    var lineNo = // Given a line object, find its line number by walking up through
    // its parent links.
    function lineNo(line) {
        if (line.parent == null) {
            return null;
        }
        var cur = line.parent, no = indexOf(cur.lines, line);
        for(var chunk = cur.parent; chunk; cur = chunk, chunk = chunk.parent){
            for(var i = 0;; ++i){
                if (chunk.children[i] == cur) {
                    break;
                }
                no += chunk.children[i].chunkSize();
            }
        }
        return no + cur.first;
    };
    var lineAtHeight = // Find the line at the given vertical position, using the height
    // information in the document tree.
    function lineAtHeight(chunk, h) {
        var n = chunk.first;
        outer: do {
            for(var i$1 = 0; i$1 < chunk.children.length; ++i$1){
                var child = chunk.children[i$1], ch = child.height;
                if (h < ch) {
                    chunk = child;
                    continue outer;
                }
                h -= ch;
                n += child.chunkSize();
            }
            return n;
        }while (!chunk.lines);
        var i = 0;
        for(; i < chunk.lines.length; ++i){
            var line = chunk.lines[i], lh = line.height;
            if (h < lh) {
                break;
            }
            h -= lh;
        }
        return n + i;
    };
    var isLine = function isLine(doc, l) {
        return l >= doc.first && l < doc.first + doc.size;
    };
    var lineNumberFor = function lineNumberFor(options, i) {
        return String(options.lineNumberFormatter(i + options.firstLineNumber));
    };
    var cmp = // Compare two positions, return 0 if they are the same, a negative
    // number when a is less, and a positive number otherwise.
    function cmp(a, b) {
        return a.line - b.line || a.ch - b.ch;
    };
    var equalCursorPos = function equalCursorPos(a, b) {
        return a.sticky == b.sticky && cmp(a, b) == 0;
    };
    var copyPos = function copyPos(x) {
        return Pos(x.line, x.ch);
    };
    var maxPos = function maxPos(a, b) {
        return cmp(a, b) < 0 ? b : a;
    };
    var minPos = function minPos(a, b) {
        return cmp(a, b) < 0 ? a : b;
    };
    var clipLine = // Most of the external API clips given positions to make sure they
    // actually exist within the document.
    function clipLine(doc, n) {
        return Math.max(doc.first, Math.min(n, doc.first + doc.size - 1));
    };
    var clipPos = function clipPos(doc, pos) {
        if (pos.line < doc.first) {
            return Pos(doc.first, 0);
        }
        var last = doc.first + doc.size - 1;
        if (pos.line > last) {
            return Pos(last, getLine(doc, last).text.length);
        }
        return clipToLen(pos, getLine(doc, pos.line).text.length);
    };
    var clipToLen = function clipToLen(pos, linelen) {
        var ch = pos.ch;
        if (ch == null || ch > linelen) {
            return Pos(pos.line, linelen);
        } else if (ch < 0) {
            return Pos(pos.line, 0);
        } else {
            return pos;
        }
    };
    var clipPosArray = function clipPosArray(doc, array) {
        var out = [];
        for(var i = 0; i < array.length; i++){
            out[i] = clipPos(doc, array[i]);
        }
        return out;
    };
    var highlightLine = // Compute a style array (an array starting with a mode generation
    // -- for invalidation -- followed by pairs of end positions and
    // style strings), which is used to highlight the tokens on the
    // line.
    function highlightLine(cm, line, context, forceToEnd) {
        // A styles array always starts with a number identifying the
        // mode/overlays that it is based on (for easy invalidation).
        var st = [
            cm.state.modeGen
        ], lineClasses = {};
        // Compute the base array of styles
        runMode(cm, line.text, cm.doc.mode, context, function(end, style) {
            return st.push(end, style);
        }, lineClasses, forceToEnd);
        var state = context.state;
        // Run overlays, adjust style array.
        var loop = function loop(o) {
            context.baseTokens = st;
            var overlay = cm.state.overlays[o], i = 1, at = 0;
            context.state = true;
            runMode(cm, line.text, overlay.mode, context, function(end, style) {
                var start = i;
                // Ensure there's a token end at the current position, and that i points at it
                while(at < end){
                    var i_end = st[i];
                    if (i_end > end) {
                        st.splice(i, 1, end, st[i + 1], i_end);
                    }
                    i += 2;
                    at = Math.min(end, i_end);
                }
                if (!style) {
                    return;
                }
                if (overlay.opaque) {
                    st.splice(start, i - start, end, "overlay " + style);
                    i = start + 2;
                } else {
                    for(; start < i; start += 2){
                        var cur = st[start + 1];
                        st[start + 1] = (cur ? cur + " " : "") + "overlay " + style;
                    }
                }
            }, lineClasses);
            context.state = state;
            context.baseTokens = null;
            context.baseTokenPos = 1;
        };
        for(var o = 0; o < cm.state.overlays.length; ++o)loop(o);
        return {
            styles: st,
            classes: lineClasses.bgClass || lineClasses.textClass ? lineClasses : null
        };
    };
    var getLineStyles = function getLineStyles(cm, line, updateFrontier) {
        if (!line.styles || line.styles[0] != cm.state.modeGen) {
            var context = getContextBefore(cm, lineNo(line));
            var resetState = line.text.length > cm.options.maxHighlightLength && copyState(cm.doc.mode, context.state);
            var result = highlightLine(cm, line, context);
            if (resetState) {
                context.state = resetState;
            }
            line.stateAfter = context.save(!resetState);
            line.styles = result.styles;
            if (result.classes) {
                line.styleClasses = result.classes;
            } else if (line.styleClasses) {
                line.styleClasses = null;
            }
            if (updateFrontier === cm.doc.highlightFrontier) {
                cm.doc.modeFrontier = Math.max(cm.doc.modeFrontier, ++cm.doc.highlightFrontier);
            }
        }
        return line.styles;
    };
    var getContextBefore = function getContextBefore(cm, n, precise) {
        var doc = cm.doc, display = cm.display;
        if (!doc.mode.startState) {
            return new Context(doc, true, n);
        }
        var start = findStartLine(cm, n, precise);
        var saved = start > doc.first && getLine(doc, start - 1).stateAfter;
        var context = saved ? Context.fromSaved(doc, saved, start) : new Context(doc, startState(doc.mode), start);
        doc.iter(start, n, function(line) {
            processLine(cm, line.text, context);
            var pos = context.line;
            line.stateAfter = pos == n - 1 || pos % 5 == 0 || pos >= display.viewFrom && pos < display.viewTo ? context.save() : null;
            context.nextLine();
        });
        if (precise) {
            doc.modeFrontier = context.line;
        }
        return context;
    };
    var processLine = // Lightweight form of highlight -- proceed over this line and
    // update state, but don't save a style array. Used for lines that
    // aren't currently visible.
    function processLine(cm, text, context, startAt) {
        var mode = cm.doc.mode;
        var stream = new StringStream(text, cm.options.tabSize, context);
        stream.start = stream.pos = startAt || 0;
        if (text == "") {
            callBlankLine(mode, context.state);
        }
        while(!stream.eol()){
            readToken(mode, stream, context.state);
            stream.start = stream.pos;
        }
    };
    var callBlankLine = function callBlankLine(mode, state) {
        if (mode.blankLine) {
            return mode.blankLine(state);
        }
        if (!mode.innerMode) {
            return;
        }
        var inner = innerMode(mode, state);
        if (inner.mode.blankLine) {
            return inner.mode.blankLine(inner.state);
        }
    };
    var readToken = function readToken(mode, stream, state, inner) {
        for(var i = 0; i < 10; i++){
            if (inner) {
                inner[0] = innerMode(mode, state).mode;
            }
            var style = mode.token(stream, state);
            if (stream.pos > stream.start) {
                return style;
            }
        }
        throw new Error("Mode " + mode.name + " failed to advance stream.");
    };
    var takeToken = // Utility for getTokenAt and getLineTokens
    function takeToken(cm, pos, precise, asArray) {
        var doc = cm.doc, mode = doc.mode, style;
        pos = clipPos(doc, pos);
        var line = getLine(doc, pos.line), context = getContextBefore(cm, pos.line, precise);
        var stream = new StringStream(line.text, cm.options.tabSize, context), tokens;
        if (asArray) {
            tokens = [];
        }
        while((asArray || stream.pos < pos.ch) && !stream.eol()){
            stream.start = stream.pos;
            style = readToken(mode, stream, context.state);
            if (asArray) {
                tokens.push(new Token(stream, style, copyState(doc.mode, context.state)));
            }
        }
        return asArray ? tokens : new Token(stream, style, context.state);
    };
    var extractLineClasses = function extractLineClasses(type, output) {
        if (type) {
            for(;;){
                var lineClass = type.match(/(?:^|\s+)line-(background-)?(\S+)/);
                if (!lineClass) {
                    break;
                }
                type = type.slice(0, lineClass.index) + type.slice(lineClass.index + lineClass[0].length);
                var prop = lineClass[1] ? "bgClass" : "textClass";
                if (output[prop] == null) {
                    output[prop] = lineClass[2];
                } else if (!new RegExp("(?:^|\\s)" + lineClass[2] + "(?:$|\\s)").test(output[prop])) {
                    output[prop] += " " + lineClass[2];
                }
            }
        }
        return type;
    };
    var runMode = // Run the given mode's parser over a line, calling f for each token.
    function runMode(cm, text, mode, context, f, lineClasses, forceToEnd) {
        var flattenSpans = mode.flattenSpans;
        if (flattenSpans == null) {
            flattenSpans = cm.options.flattenSpans;
        }
        var curStart = 0, curStyle = null;
        var stream = new StringStream(text, cm.options.tabSize, context), style;
        var inner = cm.options.addModeClass && [
            null
        ];
        if (text == "") {
            extractLineClasses(callBlankLine(mode, context.state), lineClasses);
        }
        while(!stream.eol()){
            if (stream.pos > cm.options.maxHighlightLength) {
                flattenSpans = false;
                if (forceToEnd) {
                    processLine(cm, text, context, stream.pos);
                }
                stream.pos = text.length;
                style = null;
            } else {
                style = extractLineClasses(readToken(mode, stream, context.state, inner), lineClasses);
            }
            if (inner) {
                var mName = inner[0].name;
                if (mName) {
                    style = "m-" + (style ? mName + " " + style : mName);
                }
            }
            if (!flattenSpans || curStyle != style) {
                while(curStart < stream.start){
                    curStart = Math.min(stream.start, curStart + 5000);
                    f(curStart, curStyle);
                }
                curStyle = style;
            }
            stream.start = stream.pos;
        }
        while(curStart < stream.pos){
            // Webkit seems to refuse to render text nodes longer than 57444
            // characters, and returns inaccurate measurements in nodes
            // starting around 5000 chars.
            var pos = Math.min(stream.pos, curStart + 5000);
            f(pos, curStyle);
            curStart = pos;
        }
    };
    var findStartLine = // Finds the line to start with when starting a parse. Tries to
    // find a line with a stateAfter, so that it can start with a
    // valid state. If that fails, it returns the line with the
    // smallest indentation, which tends to need the least context to
    // parse correctly.
    function findStartLine(cm, n, precise) {
        var minindent, minline, doc = cm.doc;
        var lim = precise ? -1 : n - (cm.doc.mode.innerMode ? 1000 : 100);
        for(var search = n; search > lim; --search){
            if (search <= doc.first) {
                return doc.first;
            }
            var line = getLine(doc, search - 1), after = line.stateAfter;
            if (after && (!precise || search + (_instanceof(after, SavedContext) ? after.lookAhead : 0) <= doc.modeFrontier)) {
                return search;
            }
            var indented = countColumn(line.text, null, cm.options.tabSize);
            if (minline == null || minindent > indented) {
                minline = search - 1;
                minindent = indented;
            }
        }
        return minline;
    };
    var retreatFrontier = function retreatFrontier(doc, n) {
        doc.modeFrontier = Math.min(doc.modeFrontier, n);
        if (doc.highlightFrontier < n - 10) {
            return;
        }
        var start = doc.first;
        for(var line = n - 1; line > start; line--){
            var saved = getLine(doc, line).stateAfter;
            // change is on 3
            // state on line 1 looked ahead 2 -- so saw 3
            // test 1 + 2 < 3 should cover this
            if (saved && (!_instanceof(saved, SavedContext) || line + saved.lookAhead < n)) {
                start = line + 1;
                break;
            }
        }
        doc.highlightFrontier = Math.min(doc.highlightFrontier, start);
    };
    var seeReadOnlySpans = function seeReadOnlySpans() {
        sawReadOnlySpans = true;
    };
    var seeCollapsedSpans = function seeCollapsedSpans() {
        sawCollapsedSpans = true;
    };
    var MarkedSpan = // TEXTMARKER SPANS
    function MarkedSpan(marker, from, to) {
        this.marker = marker;
        this.from = from;
        this.to = to;
    };
    var getMarkedSpanFor = // Search an array of spans for a span matching the given marker.
    function getMarkedSpanFor(spans, marker) {
        if (spans) {
            for(var i = 0; i < spans.length; ++i){
                var span = spans[i];
                if (span.marker == marker) {
                    return span;
                }
            }
        }
    };
    var removeMarkedSpan = // Remove a span from an array, returning undefined if no spans are
    // left (we don't store arrays for lines without spans).
    function removeMarkedSpan(spans, span) {
        var r;
        for(var i = 0; i < spans.length; ++i){
            if (spans[i] != span) {
                (r || (r = [])).push(spans[i]);
            }
        }
        return r;
    };
    var addMarkedSpan = // Add a span to a line.
    function addMarkedSpan(line, span, op) {
        var inThisOp = op && window.WeakSet && (op.markedSpans || (op.markedSpans = new WeakSet));
        if (inThisOp && inThisOp.has(line.markedSpans)) {
            line.markedSpans.push(span);
        } else {
            line.markedSpans = line.markedSpans ? line.markedSpans.concat([
                span
            ]) : [
                span
            ];
            if (inThisOp) {
                inThisOp.add(line.markedSpans);
            }
        }
        span.marker.attachLine(line);
    };
    var markedSpansBefore = // Used for the algorithm that adjusts markers for a change in the
    // document. These functions cut an array of spans at a given
    // character position, returning an array of remaining chunks (or
    // undefined if nothing remains).
    function markedSpansBefore(old, startCh, isInsert) {
        var nw;
        if (old) {
            for(var i = 0; i < old.length; ++i){
                var span = old[i], marker = span.marker;
                var startsBefore = span.from == null || (marker.inclusiveLeft ? span.from <= startCh : span.from < startCh);
                if (startsBefore || span.from == startCh && marker.type == "bookmark" && (!isInsert || !span.marker.insertLeft)) {
                    var endsAfter = span.to == null || (marker.inclusiveRight ? span.to >= startCh : span.to > startCh);
                    (nw || (nw = [])).push(new MarkedSpan(marker, span.from, endsAfter ? null : span.to));
                }
            }
        }
        return nw;
    };
    var markedSpansAfter = function markedSpansAfter(old, endCh, isInsert) {
        var nw;
        if (old) {
            for(var i = 0; i < old.length; ++i){
                var span = old[i], marker = span.marker;
                var endsAfter = span.to == null || (marker.inclusiveRight ? span.to >= endCh : span.to > endCh);
                if (endsAfter || span.from == endCh && marker.type == "bookmark" && (!isInsert || span.marker.insertLeft)) {
                    var startsBefore = span.from == null || (marker.inclusiveLeft ? span.from <= endCh : span.from < endCh);
                    (nw || (nw = [])).push(new MarkedSpan(marker, startsBefore ? null : span.from - endCh, span.to == null ? null : span.to - endCh));
                }
            }
        }
        return nw;
    };
    var stretchSpansOverChange = // Given a change object, compute the new set of marker spans that
    // cover the line in which the change took place. Removes spans
    // entirely within the change, reconnects spans belonging to the
    // same marker that appear on both sides of the change, and cuts off
    // spans partially within the change. Returns an array of span
    // arrays with one element for each line in (after) the change.
    function stretchSpansOverChange(doc, change) {
        if (change.full) {
            return null;
        }
        var oldFirst = isLine(doc, change.from.line) && getLine(doc, change.from.line).markedSpans;
        var oldLast = isLine(doc, change.to.line) && getLine(doc, change.to.line).markedSpans;
        if (!oldFirst && !oldLast) {
            return null;
        }
        var startCh = change.from.ch, endCh = change.to.ch, isInsert = cmp(change.from, change.to) == 0;
        // Get the spans that 'stick out' on both sides
        var first = markedSpansBefore(oldFirst, startCh, isInsert);
        var last = markedSpansAfter(oldLast, endCh, isInsert);
        // Next, merge those two ends
        var sameLine = change.text.length == 1, offset = lst(change.text).length + (sameLine ? startCh : 0);
        if (first) {
            // Fix up .to properties of first
            for(var i = 0; i < first.length; ++i){
                var span = first[i];
                if (span.to == null) {
                    var found = getMarkedSpanFor(last, span.marker);
                    if (!found) {
                        span.to = startCh;
                    } else if (sameLine) {
                        span.to = found.to == null ? null : found.to + offset;
                    }
                }
            }
        }
        if (last) {
            // Fix up .from in last (or move them into first in case of sameLine)
            for(var i$1 = 0; i$1 < last.length; ++i$1){
                var span$1 = last[i$1];
                if (span$1.to != null) {
                    span$1.to += offset;
                }
                if (span$1.from == null) {
                    var found$1 = getMarkedSpanFor(first, span$1.marker);
                    if (!found$1) {
                        span$1.from = offset;
                        if (sameLine) {
                            (first || (first = [])).push(span$1);
                        }
                    }
                } else {
                    span$1.from += offset;
                    if (sameLine) {
                        (first || (first = [])).push(span$1);
                    }
                }
            }
        }
        // Make sure we didn't create any zero-length spans
        if (first) {
            first = clearEmptySpans(first);
        }
        if (last && last != first) {
            last = clearEmptySpans(last);
        }
        var newMarkers = [
            first
        ];
        if (!sameLine) {
            // Fill gap with whole-line-spans
            var gap = change.text.length - 2, gapMarkers;
            if (gap > 0 && first) {
                for(var i$2 = 0; i$2 < first.length; ++i$2){
                    if (first[i$2].to == null) {
                        (gapMarkers || (gapMarkers = [])).push(new MarkedSpan(first[i$2].marker, null, null));
                    }
                }
            }
            for(var i$3 = 0; i$3 < gap; ++i$3){
                newMarkers.push(gapMarkers);
            }
            newMarkers.push(last);
        }
        return newMarkers;
    };
    var clearEmptySpans = // Remove spans that are empty and don't have a clearWhenEmpty
    // option of false.
    function clearEmptySpans(spans) {
        for(var i = 0; i < spans.length; ++i){
            var span = spans[i];
            if (span.from != null && span.from == span.to && span.marker.clearWhenEmpty !== false) {
                spans.splice(i--, 1);
            }
        }
        if (!spans.length) {
            return null;
        }
        return spans;
    };
    var removeReadOnlyRanges = // Used to 'clip' out readOnly ranges when making a change.
    function removeReadOnlyRanges(doc, from, to) {
        var markers = null;
        doc.iter(from.line, to.line + 1, function(line) {
            if (line.markedSpans) {
                for(var i = 0; i < line.markedSpans.length; ++i){
                    var mark = line.markedSpans[i].marker;
                    if (mark.readOnly && (!markers || indexOf(markers, mark) == -1)) {
                        (markers || (markers = [])).push(mark);
                    }
                }
            }
        });
        if (!markers) {
            return null;
        }
        var parts = [
            {
                from: from,
                to: to
            }
        ];
        for(var i = 0; i < markers.length; ++i){
            var mk = markers[i], m = mk.find(0);
            for(var j = 0; j < parts.length; ++j){
                var p = parts[j];
                if (cmp(p.to, m.from) < 0 || cmp(p.from, m.to) > 0) {
                    continue;
                }
                var newParts = [
                    j,
                    1
                ], dfrom = cmp(p.from, m.from), dto = cmp(p.to, m.to);
                if (dfrom < 0 || !mk.inclusiveLeft && !dfrom) {
                    newParts.push({
                        from: p.from,
                        to: m.from
                    });
                }
                if (dto > 0 || !mk.inclusiveRight && !dto) {
                    newParts.push({
                        from: m.to,
                        to: p.to
                    });
                }
                parts.splice.apply(parts, newParts);
                j += newParts.length - 3;
            }
        }
        return parts;
    };
    var detachMarkedSpans = // Connect or disconnect spans from a line.
    function detachMarkedSpans(line) {
        var spans = line.markedSpans;
        if (!spans) {
            return;
        }
        for(var i = 0; i < spans.length; ++i){
            spans[i].marker.detachLine(line);
        }
        line.markedSpans = null;
    };
    var attachMarkedSpans = function attachMarkedSpans(line, spans) {
        if (!spans) {
            return;
        }
        for(var i = 0; i < spans.length; ++i){
            spans[i].marker.attachLine(line);
        }
        line.markedSpans = spans;
    };
    var extraLeft = // Helpers used when computing which overlapping collapsed span
    // counts as the larger one.
    function extraLeft(marker) {
        return marker.inclusiveLeft ? -1 : 0;
    };
    var extraRight = function extraRight(marker) {
        return marker.inclusiveRight ? 1 : 0;
    };
    var compareCollapsedMarkers = // Returns a number indicating which of two overlapping collapsed
    // spans is larger (and thus includes the other). Falls back to
    // comparing ids when the spans cover exactly the same range.
    function compareCollapsedMarkers(a, b) {
        var lenDiff = a.lines.length - b.lines.length;
        if (lenDiff != 0) {
            return lenDiff;
        }
        var aPos = a.find(), bPos = b.find();
        var fromCmp = cmp(aPos.from, bPos.from) || extraLeft(a) - extraLeft(b);
        if (fromCmp) {
            return -fromCmp;
        }
        var toCmp = cmp(aPos.to, bPos.to) || extraRight(a) - extraRight(b);
        if (toCmp) {
            return toCmp;
        }
        return b.id - a.id;
    };
    var collapsedSpanAtSide = // Find out whether a line ends or starts in a collapsed span. If
    // so, return the marker for that span.
    function collapsedSpanAtSide(line, start) {
        var sps = sawCollapsedSpans && line.markedSpans, found;
        if (sps) {
            for(var sp = void 0, i = 0; i < sps.length; ++i){
                sp = sps[i];
                if (sp.marker.collapsed && (start ? sp.from : sp.to) == null && (!found || compareCollapsedMarkers(found, sp.marker) < 0)) {
                    found = sp.marker;
                }
            }
        }
        return found;
    };
    var collapsedSpanAtStart = function collapsedSpanAtStart(line) {
        return collapsedSpanAtSide(line, true);
    };
    var collapsedSpanAtEnd = function collapsedSpanAtEnd(line) {
        return collapsedSpanAtSide(line, false);
    };
    var collapsedSpanAround = function collapsedSpanAround(line, ch) {
        var sps = sawCollapsedSpans && line.markedSpans, found;
        if (sps) {
            for(var i = 0; i < sps.length; ++i){
                var sp = sps[i];
                if (sp.marker.collapsed && (sp.from == null || sp.from < ch) && (sp.to == null || sp.to > ch) && (!found || compareCollapsedMarkers(found, sp.marker) < 0)) {
                    found = sp.marker;
                }
            }
        }
        return found;
    };
    var conflictingCollapsedRange = // Test whether there exists a collapsed span that partially
    // overlaps (covers the start or end, but not both) of a new span.
    // Such overlap is not allowed.
    function conflictingCollapsedRange(doc, lineNo, from, to, marker) {
        var line = getLine(doc, lineNo);
        var sps = sawCollapsedSpans && line.markedSpans;
        if (sps) {
            for(var i = 0; i < sps.length; ++i){
                var sp = sps[i];
                if (!sp.marker.collapsed) {
                    continue;
                }
                var found = sp.marker.find(0);
                var fromCmp = cmp(found.from, from) || extraLeft(sp.marker) - extraLeft(marker);
                var toCmp = cmp(found.to, to) || extraRight(sp.marker) - extraRight(marker);
                if (fromCmp >= 0 && toCmp <= 0 || fromCmp <= 0 && toCmp >= 0) {
                    continue;
                }
                if (fromCmp <= 0 && (sp.marker.inclusiveRight && marker.inclusiveLeft ? cmp(found.to, from) >= 0 : cmp(found.to, from) > 0) || fromCmp >= 0 && (sp.marker.inclusiveRight && marker.inclusiveLeft ? cmp(found.from, to) <= 0 : cmp(found.from, to) < 0)) {
                    return true;
                }
            }
        }
    };
    var visualLine = // A visual line is a line as drawn on the screen. Folding, for
    // example, can cause multiple logical lines to appear on the same
    // visual line. This finds the start of the visual line that the
    // given line is part of (usually that is the line itself).
    function visualLine(line) {
        var merged;
        while(merged = collapsedSpanAtStart(line)){
            line = merged.find(-1, true).line;
        }
        return line;
    };
    var visualLineEnd = function visualLineEnd(line) {
        var merged;
        while(merged = collapsedSpanAtEnd(line)){
            line = merged.find(1, true).line;
        }
        return line;
    };
    var visualLineContinued = // Returns an array of logical lines that continue the visual line
    // started by the argument, or undefined if there are no such lines.
    function visualLineContinued(line) {
        var merged, lines;
        while(merged = collapsedSpanAtEnd(line)){
            line = merged.find(1, true).line;
            (lines || (lines = [])).push(line);
        }
        return lines;
    };
    var visualLineNo = // Get the line number of the start of the visual line that the
    // given line number is part of.
    function visualLineNo(doc, lineN) {
        var line = getLine(doc, lineN), vis = visualLine(line);
        if (line == vis) {
            return lineN;
        }
        return lineNo(vis);
    };
    var visualLineEndNo = // Get the line number of the start of the next visual line after
    // the given line.
    function visualLineEndNo(doc, lineN) {
        if (lineN > doc.lastLine()) {
            return lineN;
        }
        var line = getLine(doc, lineN), merged;
        if (!lineIsHidden(doc, line)) {
            return lineN;
        }
        while(merged = collapsedSpanAtEnd(line)){
            line = merged.find(1, true).line;
        }
        return lineNo(line) + 1;
    };
    var lineIsHidden = // Compute whether a line is hidden. Lines count as hidden when they
    // are part of a visual line that starts with another line, or when
    // they are entirely covered by collapsed, non-widget span.
    function lineIsHidden(doc, line) {
        var sps = sawCollapsedSpans && line.markedSpans;
        if (sps) {
            for(var sp = void 0, i = 0; i < sps.length; ++i){
                sp = sps[i];
                if (!sp.marker.collapsed) {
                    continue;
                }
                if (sp.from == null) {
                    return true;
                }
                if (sp.marker.widgetNode) {
                    continue;
                }
                if (sp.from == 0 && sp.marker.inclusiveLeft && lineIsHiddenInner(doc, line, sp)) {
                    return true;
                }
            }
        }
    };
    var heightAtLine = // Find the height above the given line.
    function heightAtLine(lineObj) {
        lineObj = visualLine(lineObj);
        var h = 0, chunk = lineObj.parent;
        for(var i = 0; i < chunk.lines.length; ++i){
            var line = chunk.lines[i];
            if (line == lineObj) {
                break;
            } else {
                h += line.height;
            }
        }
        for(var p = chunk.parent; p; chunk = p, p = chunk.parent){
            for(var i$1 = 0; i$1 < p.children.length; ++i$1){
                var cur = p.children[i$1];
                if (cur == chunk) {
                    break;
                } else {
                    h += cur.height;
                }
            }
        }
        return h;
    };
    var lineLength = // Compute the character length of a line, taking into account
    // collapsed ranges (see markText) that might hide parts, and join
    // other lines onto it.
    function lineLength(line) {
        if (line.height == 0) {
            return 0;
        }
        var len = line.text.length, merged, cur = line;
        while(merged = collapsedSpanAtStart(cur)){
            var found = merged.find(0, true);
            cur = found.from.line;
            len += found.from.ch - found.to.ch;
        }
        cur = line;
        while(merged = collapsedSpanAtEnd(cur)){
            var found$1 = merged.find(0, true);
            len -= cur.text.length - found$1.from.ch;
            cur = found$1.to.line;
            len += cur.text.length - found$1.to.ch;
        }
        return len;
    };
    var findMaxLine = // Find the longest line in the document.
    function findMaxLine(cm) {
        var d = cm.display, doc = cm.doc;
        d.maxLine = getLine(doc, doc.first);
        d.maxLineLength = lineLength(d.maxLine);
        d.maxLineChanged = true;
        doc.iter(function(line) {
            var len = lineLength(line);
            if (len > d.maxLineLength) {
                d.maxLineLength = len;
                d.maxLine = line;
            }
        });
    };
    var updateLine = // Change the content (text, markers) of a line. Automatically
    // invalidates cached information and tries to re-estimate the
    // line's height.
    function updateLine(line, text, markedSpans, estimateHeight) {
        line.text = text;
        if (line.stateAfter) {
            line.stateAfter = null;
        }
        if (line.styles) {
            line.styles = null;
        }
        if (line.order != null) {
            line.order = null;
        }
        detachMarkedSpans(line);
        attachMarkedSpans(line, markedSpans);
        var estHeight = estimateHeight ? estimateHeight(line) : 1;
        if (estHeight != line.height) {
            updateLineHeight(line, estHeight);
        }
    };
    var cleanUpLine = // Detach a line from the document tree and its markers.
    function cleanUpLine(line) {
        line.parent = null;
        detachMarkedSpans(line);
    };
    var interpretTokenStyle = function interpretTokenStyle(style, options) {
        if (!style || /^\s*$/.test(style)) {
            return null;
        }
        var cache = options.addModeClass ? styleToClassCacheWithMode : styleToClassCache;
        return cache[style] || (cache[style] = style.replace(/\S+/g, "cm-$&"));
    };
    var buildLineContent = // Render the DOM representation of the text of a line. Also builds
    // up a 'line map', which points at the DOM nodes that represent
    // specific stretches of text, and is used by the measuring code.
    // The returned object contains the DOM node, this map, and
    // information about line-wide styles that were set by the mode.
    function buildLineContent(cm, lineView) {
        // The padding-right forces the element to have a 'border', which
        // is needed on Webkit to be able to get line-level bounding
        // rectangles for it (in measureChar).
        var content = eltP("span", null, null, webkit ? "padding-right: .1px" : null);
        var builder = {
            pre: eltP("pre", [
                content
            ], "CodeMirror-line"),
            content: content,
            col: 0,
            pos: 0,
            cm: cm,
            trailingSpace: false,
            splitSpaces: cm.getOption("lineWrapping")
        };
        lineView.measure = {};
        // Iterate over the logical lines that make up this visual line.
        for(var i = 0; i <= (lineView.rest ? lineView.rest.length : 0); i++){
            var line = i ? lineView.rest[i - 1] : lineView.line, order = void 0;
            builder.pos = 0;
            builder.addToken = buildToken;
            // Optionally wire in some hacks into the token-rendering
            // algorithm, to deal with browser quirks.
            if (hasBadBidiRects(cm.display.measure) && (order = getOrder(line, cm.doc.direction))) {
                builder.addToken = buildTokenBadBidi(builder.addToken, order);
            }
            builder.map = [];
            var allowFrontierUpdate = lineView != cm.display.externalMeasured && lineNo(line);
            insertLineContent(line, builder, getLineStyles(cm, line, allowFrontierUpdate));
            if (line.styleClasses) {
                if (line.styleClasses.bgClass) {
                    builder.bgClass = joinClasses(line.styleClasses.bgClass, builder.bgClass || "");
                }
                if (line.styleClasses.textClass) {
                    builder.textClass = joinClasses(line.styleClasses.textClass, builder.textClass || "");
                }
            }
            // Ensure at least a single node is present, for measuring.
            if (builder.map.length == 0) {
                builder.map.push(0, 0, builder.content.appendChild(zeroWidthElement(cm.display.measure)));
            }
            // Store the map and a cache object for the current logical line
            if (i == 0) {
                lineView.measure.map = builder.map;
                lineView.measure.cache = {};
            } else {
                (lineView.measure.maps || (lineView.measure.maps = [])).push(builder.map);
                (lineView.measure.caches || (lineView.measure.caches = [])).push({});
            }
        }
        // See issue #2901
        if (webkit) {
            var last = builder.content.lastChild;
            if (/\bcm-tab\b/.test(last.className) || last.querySelector && last.querySelector(".cm-tab")) {
                builder.content.className = "cm-tab-wrap-hack";
            }
        }
        signal(cm, "renderLine", cm, lineView.line, builder.pre);
        if (builder.pre.className) {
            builder.textClass = joinClasses(builder.pre.className, builder.textClass || "");
        }
        return builder;
    };
    var defaultSpecialCharPlaceholder = function defaultSpecialCharPlaceholder(ch) {
        var token = elt("span", "•", "cm-invalidchar");
        token.title = "\\u" + ch.charCodeAt(0).toString(16);
        token.setAttribute("aria-label", token.title);
        return token;
    };
    var buildToken = // Build up the DOM representation for a single token, and add it to
    // the line map. Takes care to render special characters separately.
    function buildToken(builder, text, style, startStyle, endStyle, css, attributes) {
        if (!text) {
            return;
        }
        var displayText = builder.splitSpaces ? splitSpaces(text, builder.trailingSpace) : text;
        var special = builder.cm.state.specialChars, mustWrap = false;
        var content;
        if (!special.test(text)) {
            builder.col += text.length;
            content = document.createTextNode(displayText);
            builder.map.push(builder.pos, builder.pos + text.length, content);
            if (ie && ie_version < 9) {
                mustWrap = true;
            }
            builder.pos += text.length;
        } else {
            content = document.createDocumentFragment();
            var pos = 0;
            while(true){
                special.lastIndex = pos;
                var m = special.exec(text);
                var skipped = m ? m.index - pos : text.length - pos;
                if (skipped) {
                    var txt = document.createTextNode(displayText.slice(pos, pos + skipped));
                    if (ie && ie_version < 9) {
                        content.appendChild(elt("span", [
                            txt
                        ]));
                    } else {
                        content.appendChild(txt);
                    }
                    builder.map.push(builder.pos, builder.pos + skipped, txt);
                    builder.col += skipped;
                    builder.pos += skipped;
                }
                if (!m) {
                    break;
                }
                pos += skipped + 1;
                var txt$1 = void 0;
                if (m[0] == "	") {
                    var tabSize = builder.cm.options.tabSize, tabWidth = tabSize - builder.col % tabSize;
                    txt$1 = content.appendChild(elt("span", spaceStr(tabWidth), "cm-tab"));
                    txt$1.setAttribute("role", "presentation");
                    txt$1.setAttribute("cm-text", "	");
                    builder.col += tabWidth;
                } else if (m[0] == "\r" || m[0] == "\n") {
                    txt$1 = content.appendChild(elt("span", m[0] == "\r" ? "␍" : "␤", "cm-invalidchar"));
                    txt$1.setAttribute("cm-text", m[0]);
                    builder.col += 1;
                } else {
                    txt$1 = builder.cm.options.specialCharPlaceholder(m[0]);
                    txt$1.setAttribute("cm-text", m[0]);
                    if (ie && ie_version < 9) {
                        content.appendChild(elt("span", [
                            txt$1
                        ]));
                    } else {
                        content.appendChild(txt$1);
                    }
                    builder.col += 1;
                }
                builder.map.push(builder.pos, builder.pos + 1, txt$1);
                builder.pos++;
            }
        }
        builder.trailingSpace = displayText.charCodeAt(text.length - 1) == 32;
        if (style || startStyle || endStyle || mustWrap || css || attributes) {
            var fullStyle = style || "";
            if (startStyle) {
                fullStyle += startStyle;
            }
            if (endStyle) {
                fullStyle += endStyle;
            }
            var token = elt("span", [
                content
            ], fullStyle, css);
            if (attributes) {
                for(var attr in attributes){
                    if (attributes.hasOwnProperty(attr) && attr != "style" && attr != "class") {
                        token.setAttribute(attr, attributes[attr]);
                    }
                }
            }
            return builder.content.appendChild(token);
        }
        builder.content.appendChild(content);
    };
    var splitSpaces = // Change some spaces to NBSP to prevent the browser from collapsing
    // trailing spaces at the end of a line when rendering text (issue #1362).
    function splitSpaces(text, trailingBefore) {
        if (text.length > 1 && !/  /.test(text)) {
            return text;
        }
        var spaceBefore = trailingBefore, result = "";
        for(var i = 0; i < text.length; i++){
            var ch = text.charAt(i);
            if (ch == " " && spaceBefore && (i == text.length - 1 || text.charCodeAt(i + 1) == 32)) {
                ch = "\xa0";
            }
            result += ch;
            spaceBefore = ch == " ";
        }
        return result;
    };
    var buildTokenBadBidi = // Work around nonsense dimensions being reported for stretches of
    // right-to-left text.
    function buildTokenBadBidi(inner, order) {
        return function(builder, text, style, startStyle, endStyle, css, attributes) {
            style = style ? style + " cm-force-border" : "cm-force-border";
            var start = builder.pos, end = start + text.length;
            for(;;){
                // Find the part that overlaps with the start of this text
                var part = void 0;
                for(var i = 0; i < order.length; i++){
                    part = order[i];
                    if (part.to > start && part.from <= start) {
                        break;
                    }
                }
                if (part.to >= end) {
                    return inner(builder, text, style, startStyle, endStyle, css, attributes);
                }
                inner(builder, text.slice(0, part.to - start), style, startStyle, null, css, attributes);
                startStyle = null;
                text = text.slice(part.to - start);
                start = part.to;
            }
        };
    };
    var buildCollapsedSpan = function buildCollapsedSpan(builder, size, marker, ignoreWidget) {
        var widget = !ignoreWidget && marker.widgetNode;
        if (widget) {
            builder.map.push(builder.pos, builder.pos + size, widget);
        }
        if (!ignoreWidget && builder.cm.display.input.needsContentAttribute) {
            if (!widget) {
                widget = builder.content.appendChild(document.createElement("span"));
            }
            widget.setAttribute("cm-marker", marker.id);
        }
        if (widget) {
            builder.cm.display.input.setUneditable(widget);
            builder.content.appendChild(widget);
        }
        builder.pos += size;
        builder.trailingSpace = false;
    };
    var insertLineContent = // Outputs a number of spans to make up a line, taking highlighting
    // and marked text into account.
    function insertLineContent(line, builder, styles) {
        var spans = line.markedSpans, allText = line.text, at = 0;
        if (!spans) {
            for(var i$1 = 1; i$1 < styles.length; i$1 += 2){
                builder.addToken(builder, allText.slice(at, at = styles[i$1]), interpretTokenStyle(styles[i$1 + 1], builder.cm.options));
            }
            return;
        }
        var len = allText.length, pos = 0, i = 1, text = "", style, css;
        var nextChange = 0, spanStyle, spanEndStyle, spanStartStyle, collapsed, attributes;
        for(;;){
            if (nextChange == pos) {
                spanStyle = spanEndStyle = spanStartStyle = css = "";
                attributes = null;
                collapsed = null;
                nextChange = Infinity;
                var foundBookmarks = [], endStyles = void 0;
                for(var j = 0; j < spans.length; ++j){
                    var sp = spans[j], m = sp.marker;
                    if (m.type == "bookmark" && sp.from == pos && m.widgetNode) {
                        foundBookmarks.push(m);
                    } else if (sp.from <= pos && (sp.to == null || sp.to > pos || m.collapsed && sp.to == pos && sp.from == pos)) {
                        if (sp.to != null && sp.to != pos && nextChange > sp.to) {
                            nextChange = sp.to;
                            spanEndStyle = "";
                        }
                        if (m.className) {
                            spanStyle += " " + m.className;
                        }
                        if (m.css) {
                            css = (css ? css + ";" : "") + m.css;
                        }
                        if (m.startStyle && sp.from == pos) {
                            spanStartStyle += " " + m.startStyle;
                        }
                        if (m.endStyle && sp.to == nextChange) {
                            (endStyles || (endStyles = [])).push(m.endStyle, sp.to);
                        }
                        // support for the old title property
                        // https://github.com/codemirror/CodeMirror/pull/5673
                        if (m.title) {
                            (attributes || (attributes = {})).title = m.title;
                        }
                        if (m.attributes) {
                            for(var attr in m.attributes){
                                (attributes || (attributes = {}))[attr] = m.attributes[attr];
                            }
                        }
                        if (m.collapsed && (!collapsed || compareCollapsedMarkers(collapsed.marker, m) < 0)) {
                            collapsed = sp;
                        }
                    } else if (sp.from > pos && nextChange > sp.from) {
                        nextChange = sp.from;
                    }
                }
                if (endStyles) {
                    for(var j$1 = 0; j$1 < endStyles.length; j$1 += 2){
                        if (endStyles[j$1 + 1] == nextChange) {
                            spanEndStyle += " " + endStyles[j$1];
                        }
                    }
                }
                if (!collapsed || collapsed.from == pos) {
                    for(var j$2 = 0; j$2 < foundBookmarks.length; ++j$2){
                        buildCollapsedSpan(builder, 0, foundBookmarks[j$2]);
                    }
                }
                if (collapsed && (collapsed.from || 0) == pos) {
                    buildCollapsedSpan(builder, (collapsed.to == null ? len + 1 : collapsed.to) - pos, collapsed.marker, collapsed.from == null);
                    if (collapsed.to == null) {
                        return;
                    }
                    if (collapsed.to == pos) {
                        collapsed = false;
                    }
                }
            }
            if (pos >= len) {
                break;
            }
            var upto = Math.min(len, nextChange);
            while(true){
                if (text) {
                    var end = pos + text.length;
                    if (!collapsed) {
                        var tokenText = end > upto ? text.slice(0, upto - pos) : text;
                        builder.addToken(builder, tokenText, style ? style + spanStyle : spanStyle, spanStartStyle, pos + tokenText.length == nextChange ? spanEndStyle : "", css, attributes);
                    }
                    if (end >= upto) {
                        text = text.slice(upto - pos);
                        pos = upto;
                        break;
                    }
                    pos = end;
                    spanStartStyle = "";
                }
                text = allText.slice(at, at = styles[i++]);
                style = interpretTokenStyle(styles[i++], builder.cm.options);
            }
        }
    };
    var LineView = // These objects are used to represent the visible (currently drawn)
    // part of the document. A LineView may correspond to multiple
    // logical lines, if those are connected by collapsed ranges.
    function LineView(doc, line, lineN) {
        // The starting line
        this.line = line;
        // Continuing lines, if any
        this.rest = visualLineContinued(line);
        // Number of logical lines in this visual line
        this.size = this.rest ? lineNo(lst(this.rest)) - lineN + 1 : 1;
        this.node = this.text = null;
        this.hidden = lineIsHidden(doc, line);
    };
    var buildViewArray = // Create a range of LineView objects for the given lines.
    function buildViewArray(cm, from, to) {
        var array = [], nextPos;
        for(var pos = from; pos < to; pos = nextPos){
            var view = new LineView(cm.doc, getLine(cm.doc, pos), pos);
            nextPos = pos + view.size;
            array.push(view);
        }
        return array;
    };
    var pushOperation = function pushOperation(op) {
        if (operationGroup) {
            operationGroup.ops.push(op);
        } else {
            op.ownsGroup = operationGroup = {
                ops: [
                    op
                ],
                delayedCallbacks: []
            };
        }
    };
    var fireCallbacksForOps = function fireCallbacksForOps(group) {
        // Calls delayed callbacks and cursorActivity handlers until no
        // new ones appear
        var callbacks = group.delayedCallbacks, i = 0;
        do {
            for(; i < callbacks.length; i++){
                callbacks[i].call(null);
            }
            for(var j = 0; j < group.ops.length; j++){
                var op = group.ops[j];
                if (op.cursorActivityHandlers) {
                    while(op.cursorActivityCalled < op.cursorActivityHandlers.length){
                        op.cursorActivityHandlers[op.cursorActivityCalled++].call(null, op.cm);
                    }
                }
            }
        }while (i < callbacks.length);
    };
    var finishOperation = function finishOperation(op, endCb) {
        var group = op.ownsGroup;
        if (!group) {
            return;
        }
        try {
            fireCallbacksForOps(group);
        } finally{
            operationGroup = null;
            endCb(group);
        }
    };
    var signalLater = // Often, we want to signal events at a point where we are in the
    // middle of some work, but don't want the handler to start calling
    // other methods on the editor, which might be in an inconsistent
    // state or simply not expect any other events to happen.
    // signalLater looks whether there are any handlers, and schedules
    // them to be executed when the last operation ends, or, if no
    // operation is active, when a timeout fires.
    function signalLater(emitter, type /*, values...*/ ) {
        var arr = getHandlers(emitter, type);
        if (!arr.length) {
            return;
        }
        var args = Array.prototype.slice.call(arguments, 2), list;
        if (operationGroup) {
            list = operationGroup.delayedCallbacks;
        } else if (orphanDelayedCallbacks) {
            list = orphanDelayedCallbacks;
        } else {
            list = orphanDelayedCallbacks = [];
            setTimeout(fireOrphanDelayed, 0);
        }
        var loop = function loop(i) {
            list.push(function() {
                return arr[i].apply(null, args);
            });
        };
        for(var i = 0; i < arr.length; ++i)loop(i);
    };
    var fireOrphanDelayed = function fireOrphanDelayed() {
        var delayed = orphanDelayedCallbacks;
        orphanDelayedCallbacks = null;
        for(var i = 0; i < delayed.length; ++i){
            delayed[i]();
        }
    };
    var updateLineForChanges = // When an aspect of a line changes, a string is added to
    // lineView.changes. This updates the relevant part of the line's
    // DOM structure.
    function updateLineForChanges(cm, lineView, lineN, dims) {
        for(var j = 0; j < lineView.changes.length; j++){
            var type = lineView.changes[j];
            if (type == "text") {
                updateLineText(cm, lineView);
            } else if (type == "gutter") {
                updateLineGutter(cm, lineView, lineN, dims);
            } else if (type == "class") {
                updateLineClasses(cm, lineView);
            } else if (type == "widget") {
                updateLineWidgets(cm, lineView, dims);
            }
        }
        lineView.changes = null;
    };
    var ensureLineWrapped = // Lines with gutter elements, widgets or a background class need to
    // be wrapped, and have the extra elements added to the wrapper div
    function ensureLineWrapped(lineView) {
        if (lineView.node == lineView.text) {
            lineView.node = elt("div", null, null, "position: relative");
            if (lineView.text.parentNode) {
                lineView.text.parentNode.replaceChild(lineView.node, lineView.text);
            }
            lineView.node.appendChild(lineView.text);
            if (ie && ie_version < 8) {
                lineView.node.style.zIndex = 2;
            }
        }
        return lineView.node;
    };
    var updateLineBackground = function updateLineBackground(cm, lineView) {
        var cls = lineView.bgClass ? lineView.bgClass + " " + (lineView.line.bgClass || "") : lineView.line.bgClass;
        if (cls) {
            cls += " CodeMirror-linebackground";
        }
        if (lineView.background) {
            if (cls) {
                lineView.background.className = cls;
            } else {
                lineView.background.parentNode.removeChild(lineView.background);
                lineView.background = null;
            }
        } else if (cls) {
            var wrap = ensureLineWrapped(lineView);
            lineView.background = wrap.insertBefore(elt("div", null, cls), wrap.firstChild);
            cm.display.input.setUneditable(lineView.background);
        }
    };
    var getLineContent = // Wrapper around buildLineContent which will reuse the structure
    // in display.externalMeasured when possible.
    function getLineContent(cm, lineView) {
        var ext = cm.display.externalMeasured;
        if (ext && ext.line == lineView.line) {
            cm.display.externalMeasured = null;
            lineView.measure = ext.measure;
            return ext.built;
        }
        return buildLineContent(cm, lineView);
    };
    var updateLineText = // Redraw the line's text. Interacts with the background and text
    // classes because the mode may output tokens that influence these
    // classes.
    function updateLineText(cm, lineView) {
        var cls = lineView.text.className;
        var built = getLineContent(cm, lineView);
        if (lineView.text == lineView.node) {
            lineView.node = built.pre;
        }
        lineView.text.parentNode.replaceChild(built.pre, lineView.text);
        lineView.text = built.pre;
        if (built.bgClass != lineView.bgClass || built.textClass != lineView.textClass) {
            lineView.bgClass = built.bgClass;
            lineView.textClass = built.textClass;
            updateLineClasses(cm, lineView);
        } else if (cls) {
            lineView.text.className = cls;
        }
    };
    var updateLineClasses = function updateLineClasses(cm, lineView) {
        updateLineBackground(cm, lineView);
        if (lineView.line.wrapClass) {
            ensureLineWrapped(lineView).className = lineView.line.wrapClass;
        } else if (lineView.node != lineView.text) {
            lineView.node.className = "";
        }
        var textClass = lineView.textClass ? lineView.textClass + " " + (lineView.line.textClass || "") : lineView.line.textClass;
        lineView.text.className = textClass || "";
    };
    var updateLineGutter = function updateLineGutter(cm, lineView, lineN, dims) {
        if (lineView.gutter) {
            lineView.node.removeChild(lineView.gutter);
            lineView.gutter = null;
        }
        if (lineView.gutterBackground) {
            lineView.node.removeChild(lineView.gutterBackground);
            lineView.gutterBackground = null;
        }
        if (lineView.line.gutterClass) {
            var wrap = ensureLineWrapped(lineView);
            lineView.gutterBackground = elt("div", null, "CodeMirror-gutter-background " + lineView.line.gutterClass, "left: " + (cm.options.fixedGutter ? dims.fixedPos : -dims.gutterTotalWidth) + "px; width: " + dims.gutterTotalWidth + "px");
            cm.display.input.setUneditable(lineView.gutterBackground);
            wrap.insertBefore(lineView.gutterBackground, lineView.text);
        }
        var markers = lineView.line.gutterMarkers;
        if (cm.options.lineNumbers || markers) {
            var wrap$1 = ensureLineWrapped(lineView);
            var gutterWrap = lineView.gutter = elt("div", null, "CodeMirror-gutter-wrapper", "left: " + (cm.options.fixedGutter ? dims.fixedPos : -dims.gutterTotalWidth) + "px");
            gutterWrap.setAttribute("aria-hidden", "true");
            cm.display.input.setUneditable(gutterWrap);
            wrap$1.insertBefore(gutterWrap, lineView.text);
            if (lineView.line.gutterClass) {
                gutterWrap.className += " " + lineView.line.gutterClass;
            }
            if (cm.options.lineNumbers && (!markers || !markers["CodeMirror-linenumbers"])) {
                lineView.lineNumber = gutterWrap.appendChild(elt("div", lineNumberFor(cm.options, lineN), "CodeMirror-linenumber CodeMirror-gutter-elt", "left: " + dims.gutterLeft["CodeMirror-linenumbers"] + "px; width: " + cm.display.lineNumInnerWidth + "px"));
            }
            if (markers) {
                for(var k = 0; k < cm.display.gutterSpecs.length; ++k){
                    var id = cm.display.gutterSpecs[k].className, found = markers.hasOwnProperty(id) && markers[id];
                    if (found) {
                        gutterWrap.appendChild(elt("div", [
                            found
                        ], "CodeMirror-gutter-elt", "left: " + dims.gutterLeft[id] + "px; width: " + dims.gutterWidth[id] + "px"));
                    }
                }
            }
        }
    };
    var updateLineWidgets = function updateLineWidgets(cm, lineView, dims) {
        if (lineView.alignable) {
            lineView.alignable = null;
        }
        var isWidget = classTest("CodeMirror-linewidget");
        for(var node = lineView.node.firstChild, next = void 0; node; node = next){
            next = node.nextSibling;
            if (isWidget.test(node.className)) {
                lineView.node.removeChild(node);
            }
        }
        insertLineWidgets(cm, lineView, dims);
    };
    var buildLineElement = // Build a line's DOM representation from scratch
    function buildLineElement(cm, lineView, lineN, dims) {
        var built = getLineContent(cm, lineView);
        lineView.text = lineView.node = built.pre;
        if (built.bgClass) {
            lineView.bgClass = built.bgClass;
        }
        if (built.textClass) {
            lineView.textClass = built.textClass;
        }
        updateLineClasses(cm, lineView);
        updateLineGutter(cm, lineView, lineN, dims);
        insertLineWidgets(cm, lineView, dims);
        return lineView.node;
    };
    var insertLineWidgets = // A lineView may contain multiple logical lines (when merged by
    // collapsed spans). The widgets for all of them need to be drawn.
    function insertLineWidgets(cm, lineView, dims) {
        insertLineWidgetsFor(cm, lineView.line, lineView, dims, true);
        if (lineView.rest) {
            for(var i = 0; i < lineView.rest.length; i++){
                insertLineWidgetsFor(cm, lineView.rest[i], lineView, dims, false);
            }
        }
    };
    var insertLineWidgetsFor = function insertLineWidgetsFor(cm, line, lineView, dims, allowAbove) {
        if (!line.widgets) {
            return;
        }
        var wrap = ensureLineWrapped(lineView);
        for(var i = 0, ws = line.widgets; i < ws.length; ++i){
            var widget = ws[i], node = elt("div", [
                widget.node
            ], "CodeMirror-linewidget" + (widget.className ? " " + widget.className : ""));
            if (!widget.handleMouseEvents) {
                node.setAttribute("cm-ignore-events", "true");
            }
            positionLineWidget(widget, node, lineView, dims);
            cm.display.input.setUneditable(node);
            if (allowAbove && widget.above) {
                wrap.insertBefore(node, lineView.gutter || lineView.text);
            } else {
                wrap.appendChild(node);
            }
            signalLater(widget, "redraw");
        }
    };
    var positionLineWidget = function positionLineWidget(widget, node, lineView, dims) {
        if (widget.noHScroll) {
            (lineView.alignable || (lineView.alignable = [])).push(node);
            var width = dims.wrapperWidth;
            node.style.left = dims.fixedPos + "px";
            if (!widget.coverGutter) {
                width -= dims.gutterTotalWidth;
                node.style.paddingLeft = dims.gutterTotalWidth + "px";
            }
            node.style.width = width + "px";
        }
        if (widget.coverGutter) {
            node.style.zIndex = 5;
            node.style.position = "relative";
            if (!widget.noHScroll) {
                node.style.marginLeft = -dims.gutterTotalWidth + "px";
            }
        }
    };
    var widgetHeight = function widgetHeight(widget) {
        if (widget.height != null) {
            return widget.height;
        }
        var cm = widget.doc.cm;
        if (!cm) {
            return 0;
        }
        if (!contains(document.body, widget.node)) {
            var parentStyle = "position: relative;";
            if (widget.coverGutter) {
                parentStyle += "margin-left: -" + cm.display.gutters.offsetWidth + "px;";
            }
            if (widget.noHScroll) {
                parentStyle += "width: " + cm.display.wrapper.clientWidth + "px;";
            }
            removeChildrenAndAdd(cm.display.measure, elt("div", [
                widget.node
            ], null, parentStyle));
        }
        return widget.height = widget.node.parentNode.offsetHeight;
    };
    var eventInWidget = // Return true when the given mouse event happened in a widget
    function eventInWidget(display, e) {
        for(var n = e_target(e); n != display.wrapper; n = n.parentNode){
            if (!n || n.nodeType == 1 && n.getAttribute("cm-ignore-events") == "true" || n.parentNode == display.sizer && n != display.mover) {
                return true;
            }
        }
    };
    var paddingTop = // POSITION MEASUREMENT
    function paddingTop(display) {
        return display.lineSpace.offsetTop;
    };
    var paddingVert = function paddingVert(display) {
        return display.mover.offsetHeight - display.lineSpace.offsetHeight;
    };
    var paddingH = function paddingH(display) {
        if (display.cachedPaddingH) {
            return display.cachedPaddingH;
        }
        var e = removeChildrenAndAdd(display.measure, elt("pre", "x", "CodeMirror-line-like"));
        var style = window.getComputedStyle ? window.getComputedStyle(e) : e.currentStyle;
        var data = {
            left: parseInt(style.paddingLeft),
            right: parseInt(style.paddingRight)
        };
        if (!isNaN(data.left) && !isNaN(data.right)) {
            display.cachedPaddingH = data;
        }
        return data;
    };
    var scrollGap = function scrollGap(cm) {
        return scrollerGap - cm.display.nativeBarWidth;
    };
    var displayWidth = function displayWidth(cm) {
        return cm.display.scroller.clientWidth - scrollGap(cm) - cm.display.barWidth;
    };
    var displayHeight = function displayHeight(cm) {
        return cm.display.scroller.clientHeight - scrollGap(cm) - cm.display.barHeight;
    };
    var ensureLineHeights = // Ensure the lineView.wrapping.heights array is populated. This is
    // an array of bottom offsets for the lines that make up a drawn
    // line. When lineWrapping is on, there might be more than one
    // height.
    function ensureLineHeights(cm, lineView, rect) {
        var wrapping = cm.options.lineWrapping;
        var curWidth = wrapping && displayWidth(cm);
        if (!lineView.measure.heights || wrapping && lineView.measure.width != curWidth) {
            var heights = lineView.measure.heights = [];
            if (wrapping) {
                lineView.measure.width = curWidth;
                var rects = lineView.text.firstChild.getClientRects();
                for(var i = 0; i < rects.length - 1; i++){
                    var cur = rects[i], next = rects[i + 1];
                    if (Math.abs(cur.bottom - next.bottom) > 2) {
                        heights.push((cur.bottom + next.top) / 2 - rect.top);
                    }
                }
            }
            heights.push(rect.bottom - rect.top);
        }
    };
    var mapFromLineView = // Find a line map (mapping character offsets to text nodes) and a
    // measurement cache for the given line number. (A line view might
    // contain multiple lines when collapsed ranges are present.)
    function mapFromLineView(lineView, line, lineN) {
        if (lineView.line == line) {
            return {
                map: lineView.measure.map,
                cache: lineView.measure.cache
            };
        }
        if (lineView.rest) {
            for(var i = 0; i < lineView.rest.length; i++){
                if (lineView.rest[i] == line) {
                    return {
                        map: lineView.measure.maps[i],
                        cache: lineView.measure.caches[i]
                    };
                }
            }
            for(var i$1 = 0; i$1 < lineView.rest.length; i$1++){
                if (lineNo(lineView.rest[i$1]) > lineN) {
                    return {
                        map: lineView.measure.maps[i$1],
                        cache: lineView.measure.caches[i$1],
                        before: true
                    };
                }
            }
        }
    };
    var updateExternalMeasurement = // Render a line into the hidden node display.externalMeasured. Used
    // when measurement is needed for a line that's not in the viewport.
    function updateExternalMeasurement(cm, line) {
        line = visualLine(line);
        var lineN = lineNo(line);
        var view = cm.display.externalMeasured = new LineView(cm.doc, line, lineN);
        view.lineN = lineN;
        var built = view.built = buildLineContent(cm, view);
        view.text = built.pre;
        removeChildrenAndAdd(cm.display.lineMeasure, built.pre);
        return view;
    };
    var measureChar = // Get a {top, bottom, left, right} box (in line-local coordinates)
    // for a given character.
    function measureChar(cm, line, ch, bias) {
        return measureCharPrepared(cm, prepareMeasureForLine(cm, line), ch, bias);
    };
    var findViewForLine = // Find a line view that corresponds to the given line number.
    function findViewForLine(cm, lineN) {
        if (lineN >= cm.display.viewFrom && lineN < cm.display.viewTo) {
            return cm.display.view[findViewIndex(cm, lineN)];
        }
        var ext = cm.display.externalMeasured;
        if (ext && lineN >= ext.lineN && lineN < ext.lineN + ext.size) {
            return ext;
        }
    };
    var prepareMeasureForLine = // Measurement can be split in two steps, the set-up work that
    // applies to the whole line, and the measurement of the actual
    // character. Functions like coordsChar, that need to do a lot of
    // measurements in a row, can thus ensure that the set-up work is
    // only done once.
    function prepareMeasureForLine(cm, line) {
        var lineN = lineNo(line);
        var view = findViewForLine(cm, lineN);
        if (view && !view.text) {
            view = null;
        } else if (view && view.changes) {
            updateLineForChanges(cm, view, lineN, getDimensions(cm));
            cm.curOp.forceUpdate = true;
        }
        if (!view) {
            view = updateExternalMeasurement(cm, line);
        }
        var info = mapFromLineView(view, line, lineN);
        return {
            line: line,
            view: view,
            rect: null,
            map: info.map,
            cache: info.cache,
            before: info.before,
            hasHeights: false
        };
    };
    var measureCharPrepared = // Given a prepared measurement object, measures the position of an
    // actual character (or fetches it from the cache).
    function measureCharPrepared(cm, prepared, ch, bias, varHeight) {
        if (prepared.before) {
            ch = -1;
        }
        var key = ch + (bias || ""), found;
        if (prepared.cache.hasOwnProperty(key)) {
            found = prepared.cache[key];
        } else {
            if (!prepared.rect) {
                prepared.rect = prepared.view.text.getBoundingClientRect();
            }
            if (!prepared.hasHeights) {
                ensureLineHeights(cm, prepared.view, prepared.rect);
                prepared.hasHeights = true;
            }
            found = measureCharInner(cm, prepared, ch, bias);
            if (!found.bogus) {
                prepared.cache[key] = found;
            }
        }
        return {
            left: found.left,
            right: found.right,
            top: varHeight ? found.rtop : found.top,
            bottom: varHeight ? found.rbottom : found.bottom
        };
    };
    var nodeAndOffsetInLineMap = function nodeAndOffsetInLineMap(map, ch, bias) {
        var node, start, end, collapse, mStart, mEnd;
        // First, search the line map for the text node corresponding to,
        // or closest to, the target character.
        for(var i = 0; i < map.length; i += 3){
            mStart = map[i];
            mEnd = map[i + 1];
            if (ch < mStart) {
                start = 0;
                end = 1;
                collapse = "left";
            } else if (ch < mEnd) {
                start = ch - mStart;
                end = start + 1;
            } else if (i == map.length - 3 || ch == mEnd && map[i + 3] > ch) {
                end = mEnd - mStart;
                start = end - 1;
                if (ch >= mEnd) {
                    collapse = "right";
                }
            }
            if (start != null) {
                node = map[i + 2];
                if (mStart == mEnd && bias == (node.insertLeft ? "left" : "right")) {
                    collapse = bias;
                }
                if (bias == "left" && start == 0) {
                    while(i && map[i - 2] == map[i - 3] && map[i - 1].insertLeft){
                        node = map[(i -= 3) + 2];
                        collapse = "left";
                    }
                }
                if (bias == "right" && start == mEnd - mStart) {
                    while(i < map.length - 3 && map[i + 3] == map[i + 4] && !map[i + 5].insertLeft){
                        node = map[(i += 3) + 2];
                        collapse = "right";
                    }
                }
                break;
            }
        }
        return {
            node: node,
            start: start,
            end: end,
            collapse: collapse,
            coverStart: mStart,
            coverEnd: mEnd
        };
    };
    var getUsefulRect = function getUsefulRect(rects, bias) {
        var rect = nullRect;
        if (bias == "left") {
            for(var i = 0; i < rects.length; i++){
                if ((rect = rects[i]).left != rect.right) {
                    break;
                }
            }
        } else {
            for(var i$1 = rects.length - 1; i$1 >= 0; i$1--){
                if ((rect = rects[i$1]).left != rect.right) {
                    break;
                }
            }
        }
        return rect;
    };
    var measureCharInner = function measureCharInner(cm, prepared, ch, bias) {
        var place = nodeAndOffsetInLineMap(prepared.map, ch, bias);
        var node = place.node, start = place.start, end = place.end, collapse = place.collapse;
        var rect;
        if (node.nodeType == 3) {
            for(var i$1 = 0; i$1 < 4; i$1++){
                while(start && isExtendingChar(prepared.line.text.charAt(place.coverStart + start))){
                    --start;
                }
                while(place.coverStart + end < place.coverEnd && isExtendingChar(prepared.line.text.charAt(place.coverStart + end))){
                    ++end;
                }
                if (ie && ie_version < 9 && start == 0 && end == place.coverEnd - place.coverStart) {
                    rect = node.parentNode.getBoundingClientRect();
                } else {
                    rect = getUsefulRect(range(node, start, end).getClientRects(), bias);
                }
                if (rect.left || rect.right || start == 0) {
                    break;
                }
                end = start;
                start = start - 1;
                collapse = "right";
            }
            if (ie && ie_version < 11) {
                rect = maybeUpdateRectForZooming(cm.display.measure, rect);
            }
        } else {
            if (start > 0) {
                collapse = bias = "right";
            }
            var rects;
            if (cm.options.lineWrapping && (rects = node.getClientRects()).length > 1) {
                rect = rects[bias == "right" ? rects.length - 1 : 0];
            } else {
                rect = node.getBoundingClientRect();
            }
        }
        if (ie && ie_version < 9 && !start && (!rect || !rect.left && !rect.right)) {
            var rSpan = node.parentNode.getClientRects()[0];
            if (rSpan) {
                rect = {
                    left: rSpan.left,
                    right: rSpan.left + charWidth(cm.display),
                    top: rSpan.top,
                    bottom: rSpan.bottom
                };
            } else {
                rect = nullRect;
            }
        }
        var rtop = rect.top - prepared.rect.top, rbot = rect.bottom - prepared.rect.top;
        var mid = (rtop + rbot) / 2;
        var heights = prepared.view.measure.heights;
        var i = 0;
        for(; i < heights.length - 1; i++){
            if (mid < heights[i]) {
                break;
            }
        }
        var top = i ? heights[i - 1] : 0, bot = heights[i];
        var result = {
            left: (collapse == "right" ? rect.right : rect.left) - prepared.rect.left,
            right: (collapse == "left" ? rect.left : rect.right) - prepared.rect.left,
            top: top,
            bottom: bot
        };
        if (!rect.left && !rect.right) {
            result.bogus = true;
        }
        if (!cm.options.singleCursorHeightPerLine) {
            result.rtop = rtop;
            result.rbottom = rbot;
        }
        return result;
    };
    var maybeUpdateRectForZooming = // Work around problem with bounding client rects on ranges being
    // returned incorrectly when zoomed on IE10 and below.
    function maybeUpdateRectForZooming(measure, rect) {
        if (!window.screen || screen.logicalXDPI == null || screen.logicalXDPI == screen.deviceXDPI || !hasBadZoomedRects(measure)) {
            return rect;
        }
        var scaleX = screen.logicalXDPI / screen.deviceXDPI;
        var scaleY = screen.logicalYDPI / screen.deviceYDPI;
        return {
            left: rect.left * scaleX,
            right: rect.right * scaleX,
            top: rect.top * scaleY,
            bottom: rect.bottom * scaleY
        };
    };
    var clearLineMeasurementCacheFor = function clearLineMeasurementCacheFor(lineView) {
        if (lineView.measure) {
            lineView.measure.cache = {};
            lineView.measure.heights = null;
            if (lineView.rest) {
                for(var i = 0; i < lineView.rest.length; i++){
                    lineView.measure.caches[i] = {};
                }
            }
        }
    };
    var clearLineMeasurementCache = function clearLineMeasurementCache(cm) {
        cm.display.externalMeasure = null;
        removeChildren(cm.display.lineMeasure);
        for(var i = 0; i < cm.display.view.length; i++){
            clearLineMeasurementCacheFor(cm.display.view[i]);
        }
    };
    var clearCaches = function clearCaches(cm) {
        clearLineMeasurementCache(cm);
        cm.display.cachedCharWidth = cm.display.cachedTextHeight = cm.display.cachedPaddingH = null;
        if (!cm.options.lineWrapping) {
            cm.display.maxLineChanged = true;
        }
        cm.display.lineNumChars = null;
    };
    var pageScrollX = function pageScrollX() {
        // Work around https://bugs.chromium.org/p/chromium/issues/detail?id=489206
        // which causes page_Offset and bounding client rects to use
        // different reference viewports and invalidate our calculations.
        if (chrome && android) {
            return -(document.body.getBoundingClientRect().left - parseInt(getComputedStyle(document.body).marginLeft));
        }
        return window.pageXOffset || (document.documentElement || document.body).scrollLeft;
    };
    var pageScrollY = function pageScrollY() {
        if (chrome && android) {
            return -(document.body.getBoundingClientRect().top - parseInt(getComputedStyle(document.body).marginTop));
        }
        return window.pageYOffset || (document.documentElement || document.body).scrollTop;
    };
    var widgetTopHeight = function widgetTopHeight(lineObj) {
        var height = 0;
        if (lineObj.widgets) {
            for(var i = 0; i < lineObj.widgets.length; ++i){
                if (lineObj.widgets[i].above) {
                    height += widgetHeight(lineObj.widgets[i]);
                }
            }
        }
        return height;
    };
    var intoCoordSystem = // Converts a {top, bottom, left, right} box from line-local
    // coordinates into another coordinate system. Context may be one of
    // "line", "div" (display.lineDiv), "local"./null (editor), "window",
    // or "page".
    function intoCoordSystem(cm, lineObj, rect, context, includeWidgets) {
        if (!includeWidgets) {
            var height = widgetTopHeight(lineObj);
            rect.top += height;
            rect.bottom += height;
        }
        if (context == "line") {
            return rect;
        }
        if (!context) {
            context = "local";
        }
        var yOff = heightAtLine(lineObj);
        if (context == "local") {
            yOff += paddingTop(cm.display);
        } else {
            yOff -= cm.display.viewOffset;
        }
        if (context == "page" || context == "window") {
            var lOff = cm.display.lineSpace.getBoundingClientRect();
            yOff += lOff.top + (context == "window" ? 0 : pageScrollY());
            var xOff = lOff.left + (context == "window" ? 0 : pageScrollX());
            rect.left += xOff;
            rect.right += xOff;
        }
        rect.top += yOff;
        rect.bottom += yOff;
        return rect;
    };
    var fromCoordSystem = // Coverts a box from "div" coords to another coordinate system.
    // Context may be "window", "page", "div", or "local"./null.
    function fromCoordSystem(cm, coords, context) {
        if (context == "div") {
            return coords;
        }
        var left = coords.left, top = coords.top;
        // First move into "page" coordinate system
        if (context == "page") {
            left -= pageScrollX();
            top -= pageScrollY();
        } else if (context == "local" || !context) {
            var localBox = cm.display.sizer.getBoundingClientRect();
            left += localBox.left;
            top += localBox.top;
        }
        var lineSpaceBox = cm.display.lineSpace.getBoundingClientRect();
        return {
            left: left - lineSpaceBox.left,
            top: top - lineSpaceBox.top
        };
    };
    var charCoords = function charCoords(cm, pos, context, lineObj, bias) {
        if (!lineObj) {
            lineObj = getLine(cm.doc, pos.line);
        }
        return intoCoordSystem(cm, lineObj, measureChar(cm, lineObj, pos.ch, bias), context);
    };
    var cursorCoords = // Returns a box for a given cursor position, which may have an
    // 'other' property containing the position of the secondary cursor
    // on a bidi boundary.
    // A cursor Pos(line, char, "before") is on the same visual line as `char - 1`
    // and after `char - 1` in writing order of `char - 1`
    // A cursor Pos(line, char, "after") is on the same visual line as `char`
    // and before `char` in writing order of `char`
    // Examples (upper-case letters are RTL, lower-case are LTR):
    //     Pos(0, 1, ...)
    //     before   after
    // ab     a|b     a|b
    // aB     a|B     aB|
    // Ab     |Ab     A|b
    // AB     B|A     B|A
    // Every position after the last character on a line is considered to stick
    // to the last character on the line.
    function cursorCoords(cm, pos, context, lineObj, preparedMeasure, varHeight) {
        lineObj = lineObj || getLine(cm.doc, pos.line);
        if (!preparedMeasure) {
            preparedMeasure = prepareMeasureForLine(cm, lineObj);
        }
        function get(ch, right) {
            var m = measureCharPrepared(cm, preparedMeasure, ch, right ? "right" : "left", varHeight);
            if (right) {
                m.left = m.right;
            } else {
                m.right = m.left;
            }
            return intoCoordSystem(cm, lineObj, m, context);
        }
        var order = getOrder(lineObj, cm.doc.direction), ch = pos.ch, sticky = pos.sticky;
        if (ch >= lineObj.text.length) {
            ch = lineObj.text.length;
            sticky = "before";
        } else if (ch <= 0) {
            ch = 0;
            sticky = "after";
        }
        if (!order) {
            return get(sticky == "before" ? ch - 1 : ch, sticky == "before");
        }
        function getBidi(ch, partPos, invert) {
            var part = order[partPos], right = part.level == 1;
            return get(invert ? ch - 1 : ch, right != invert);
        }
        var partPos = getBidiPartAt(order, ch, sticky);
        var other = bidiOther;
        var val = getBidi(ch, partPos, sticky == "before");
        if (other != null) {
            val.other = getBidi(ch, other, sticky != "before");
        }
        return val;
    };
    var estimateCoords = // Used to cheaply estimate the coordinates for a position. Used for
    // intermediate scroll updates.
    function estimateCoords(cm, pos) {
        var left = 0;
        pos = clipPos(cm.doc, pos);
        if (!cm.options.lineWrapping) {
            left = charWidth(cm.display) * pos.ch;
        }
        var lineObj = getLine(cm.doc, pos.line);
        var top = heightAtLine(lineObj) + paddingTop(cm.display);
        return {
            left: left,
            right: left,
            top: top,
            bottom: top + lineObj.height
        };
    };
    var PosWithInfo = // Positions returned by coordsChar contain some extra information.
    // xRel is the relative x position of the input coordinates compared
    // to the found position (so xRel > 0 means the coordinates are to
    // the right of the character position, for example). When outside
    // is true, that means the coordinates lie outside the line's
    // vertical range.
    function PosWithInfo(line, ch, sticky, outside, xRel) {
        var pos = Pos(line, ch, sticky);
        pos.xRel = xRel;
        if (outside) {
            pos.outside = outside;
        }
        return pos;
    };
    var coordsChar = // Compute the character position closest to the given coordinates.
    // Input must be lineSpace-local ("div" coordinate system).
    function coordsChar(cm, x, y) {
        var doc = cm.doc;
        y += cm.display.viewOffset;
        if (y < 0) {
            return PosWithInfo(doc.first, 0, null, -1, -1);
        }
        var lineN = lineAtHeight(doc, y), last = doc.first + doc.size - 1;
        if (lineN > last) {
            return PosWithInfo(doc.first + doc.size - 1, getLine(doc, last).text.length, null, 1, 1);
        }
        if (x < 0) {
            x = 0;
        }
        var lineObj = getLine(doc, lineN);
        for(;;){
            var found = coordsCharInner(cm, lineObj, lineN, x, y);
            var collapsed = collapsedSpanAround(lineObj, found.ch + (found.xRel > 0 || found.outside > 0 ? 1 : 0));
            if (!collapsed) {
                return found;
            }
            var rangeEnd = collapsed.find(1);
            if (rangeEnd.line == lineN) {
                return rangeEnd;
            }
            lineObj = getLine(doc, lineN = rangeEnd.line);
        }
    };
    var wrappedLineExtent = function wrappedLineExtent(cm, lineObj, preparedMeasure, y) {
        y -= widgetTopHeight(lineObj);
        var end = lineObj.text.length;
        var begin = findFirst(function(ch) {
            return measureCharPrepared(cm, preparedMeasure, ch - 1).bottom <= y;
        }, end, 0);
        end = findFirst(function(ch) {
            return measureCharPrepared(cm, preparedMeasure, ch).top > y;
        }, begin, end);
        return {
            begin: begin,
            end: end
        };
    };
    var wrappedLineExtentChar = function wrappedLineExtentChar(cm, lineObj, preparedMeasure, target) {
        if (!preparedMeasure) {
            preparedMeasure = prepareMeasureForLine(cm, lineObj);
        }
        var targetTop = intoCoordSystem(cm, lineObj, measureCharPrepared(cm, preparedMeasure, target), "line").top;
        return wrappedLineExtent(cm, lineObj, preparedMeasure, targetTop);
    };
    var boxIsAfter = // Returns true if the given side of a box is after the given
    // coordinates, in top-to-bottom, left-to-right order.
    function boxIsAfter(box, x, y, left) {
        return box.bottom <= y ? false : box.top > y ? true : (left ? box.left : box.right) > x;
    };
    var coordsCharInner = function coordsCharInner(cm, lineObj, lineNo, x, y) {
        // Move y into line-local coordinate space
        y -= heightAtLine(lineObj);
        var preparedMeasure = prepareMeasureForLine(cm, lineObj);
        // When directly calling `measureCharPrepared`, we have to adjust
        // for the widgets at this line.
        var widgetHeight = widgetTopHeight(lineObj);
        var begin = 0, end = lineObj.text.length, ltr = true;
        var order = getOrder(lineObj, cm.doc.direction);
        // If the line isn't plain left-to-right text, first figure out
        // which bidi section the coordinates fall into.
        if (order) {
            var part = (cm.options.lineWrapping ? coordsBidiPartWrapped : coordsBidiPart)(cm, lineObj, lineNo, preparedMeasure, order, x, y);
            ltr = part.level != 1;
            // The awkward -1 offsets are needed because findFirst (called
            // on these below) will treat its first bound as inclusive,
            // second as exclusive, but we want to actually address the
            // characters in the part's range
            begin = ltr ? part.from : part.to - 1;
            end = ltr ? part.to : part.from - 1;
        }
        // A binary search to find the first character whose bounding box
        // starts after the coordinates. If we run across any whose box wrap
        // the coordinates, store that.
        var chAround = null, boxAround = null;
        var ch = findFirst(function(ch) {
            var box = measureCharPrepared(cm, preparedMeasure, ch);
            box.top += widgetHeight;
            box.bottom += widgetHeight;
            if (!boxIsAfter(box, x, y, false)) {
                return false;
            }
            if (box.top <= y && box.left <= x) {
                chAround = ch;
                boxAround = box;
            }
            return true;
        }, begin, end);
        var baseX, sticky, outside = false;
        // If a box around the coordinates was found, use that
        if (boxAround) {
            // Distinguish coordinates nearer to the left or right side of the box
            var atLeft = x - boxAround.left < boxAround.right - x, atStart = atLeft == ltr;
            ch = chAround + (atStart ? 0 : 1);
            sticky = atStart ? "after" : "before";
            baseX = atLeft ? boxAround.left : boxAround.right;
        } else {
            // (Adjust for extended bound, if necessary.)
            if (!ltr && (ch == end || ch == begin)) {
                ch++;
            }
            // To determine which side to associate with, get the box to the
            // left of the character and compare it's vertical position to the
            // coordinates
            sticky = ch == 0 ? "after" : ch == lineObj.text.length ? "before" : measureCharPrepared(cm, preparedMeasure, ch - (ltr ? 1 : 0)).bottom + widgetHeight <= y == ltr ? "after" : "before";
            // Now get accurate coordinates for this place, in order to get a
            // base X position
            var coords = cursorCoords(cm, Pos(lineNo, ch, sticky), "line", lineObj, preparedMeasure);
            baseX = coords.left;
            outside = y < coords.top ? -1 : y >= coords.bottom ? 1 : 0;
        }
        ch = skipExtendingChars(lineObj.text, ch, 1);
        return PosWithInfo(lineNo, ch, sticky, outside, x - baseX);
    };
    var coordsBidiPart = function coordsBidiPart(cm, lineObj, lineNo, preparedMeasure, order, x, y) {
        // Bidi parts are sorted left-to-right, and in a non-line-wrapping
        // situation, we can take this ordering to correspond to the visual
        // ordering. This finds the first part whose end is after the given
        // coordinates.
        var index = findFirst(function(i) {
            var part = order[i], ltr = part.level != 1;
            return boxIsAfter(cursorCoords(cm, Pos(lineNo, ltr ? part.to : part.from, ltr ? "before" : "after"), "line", lineObj, preparedMeasure), x, y, true);
        }, 0, order.length - 1);
        var part = order[index];
        // If this isn't the first part, the part's start is also after
        // the coordinates, and the coordinates aren't on the same line as
        // that start, move one part back.
        if (index > 0) {
            var ltr = part.level != 1;
            var start = cursorCoords(cm, Pos(lineNo, ltr ? part.from : part.to, ltr ? "after" : "before"), "line", lineObj, preparedMeasure);
            if (boxIsAfter(start, x, y, true) && start.top > y) {
                part = order[index - 1];
            }
        }
        return part;
    };
    var coordsBidiPartWrapped = function coordsBidiPartWrapped(cm, lineObj, _lineNo, preparedMeasure, order, x, y) {
        // In a wrapped line, rtl text on wrapping boundaries can do things
        // that don't correspond to the ordering in our `order` array at
        // all, so a binary search doesn't work, and we want to return a
        // part that only spans one line so that the binary search in
        // coordsCharInner is safe. As such, we first find the extent of the
        // wrapped line, and then do a flat search in which we discard any
        // spans that aren't on the line.
        var ref = wrappedLineExtent(cm, lineObj, preparedMeasure, y);
        var begin = ref.begin;
        var end = ref.end;
        if (/\s/.test(lineObj.text.charAt(end - 1))) {
            end--;
        }
        var part = null, closestDist = null;
        for(var i = 0; i < order.length; i++){
            var p = order[i];
            if (p.from >= end || p.to <= begin) {
                continue;
            }
            var ltr = p.level != 1;
            var endX = measureCharPrepared(cm, preparedMeasure, ltr ? Math.min(end, p.to) - 1 : Math.max(begin, p.from)).right;
            // Weigh against spans ending before this, so that they are only
            // picked if nothing ends after
            var dist = endX < x ? x - endX + 1e9 : endX - x;
            if (!part || closestDist > dist) {
                part = p;
                closestDist = dist;
            }
        }
        if (!part) {
            part = order[order.length - 1];
        }
        // Clip the part to the wrapped line.
        if (part.from < begin) {
            part = {
                from: begin,
                to: part.to,
                level: part.level
            };
        }
        if (part.to > end) {
            part = {
                from: part.from,
                to: end,
                level: part.level
            };
        }
        return part;
    };
    var textHeight = // Compute the default text height.
    function textHeight(display) {
        if (display.cachedTextHeight != null) {
            return display.cachedTextHeight;
        }
        if (measureText == null) {
            measureText = elt("pre", null, "CodeMirror-line-like");
            // Measure a bunch of lines, for browsers that compute
            // fractional heights.
            for(var i = 0; i < 49; ++i){
                measureText.appendChild(document.createTextNode("x"));
                measureText.appendChild(elt("br"));
            }
            measureText.appendChild(document.createTextNode("x"));
        }
        removeChildrenAndAdd(display.measure, measureText);
        var height = measureText.offsetHeight / 50;
        if (height > 3) {
            display.cachedTextHeight = height;
        }
        removeChildren(display.measure);
        return height || 1;
    };
    var charWidth = // Compute the default character width.
    function charWidth(display) {
        if (display.cachedCharWidth != null) {
            return display.cachedCharWidth;
        }
        var anchor = elt("span", "xxxxxxxxxx");
        var pre = elt("pre", [
            anchor
        ], "CodeMirror-line-like");
        removeChildrenAndAdd(display.measure, pre);
        var rect = anchor.getBoundingClientRect(), width = (rect.right - rect.left) / 10;
        if (width > 2) {
            display.cachedCharWidth = width;
        }
        return width || 10;
    };
    var getDimensions = // Do a bulk-read of the DOM positions and sizes needed to draw the
    // view, so that we don't interleave reading and writing to the DOM.
    function getDimensions(cm) {
        var d = cm.display, left = {}, width = {};
        var gutterLeft = d.gutters.clientLeft;
        for(var n = d.gutters.firstChild, i = 0; n; n = n.nextSibling, ++i){
            var id = cm.display.gutterSpecs[i].className;
            left[id] = n.offsetLeft + n.clientLeft + gutterLeft;
            width[id] = n.clientWidth;
        }
        return {
            fixedPos: compensateForHScroll(d),
            gutterTotalWidth: d.gutters.offsetWidth,
            gutterLeft: left,
            gutterWidth: width,
            wrapperWidth: d.wrapper.clientWidth
        };
    };
    var compensateForHScroll = // Computes display.scroller.scrollLeft + display.gutters.offsetWidth,
    // but using getBoundingClientRect to get a sub-pixel-accurate
    // result.
    function compensateForHScroll(display) {
        return display.scroller.getBoundingClientRect().left - display.sizer.getBoundingClientRect().left;
    };
    var estimateHeight = // Returns a function that estimates the height of a line, to use as
    // first approximation until the line becomes visible (and is thus
    // properly measurable).
    function estimateHeight(cm) {
        var th = textHeight(cm.display), wrapping = cm.options.lineWrapping;
        var perLine = wrapping && Math.max(5, cm.display.scroller.clientWidth / charWidth(cm.display) - 3);
        return function(line) {
            if (lineIsHidden(cm.doc, line)) {
                return 0;
            }
            var widgetsHeight = 0;
            if (line.widgets) {
                for(var i = 0; i < line.widgets.length; i++){
                    if (line.widgets[i].height) {
                        widgetsHeight += line.widgets[i].height;
                    }
                }
            }
            if (wrapping) {
                return widgetsHeight + (Math.ceil(line.text.length / perLine) || 1) * th;
            } else {
                return widgetsHeight + th;
            }
        };
    };
    var estimateLineHeights = function estimateLineHeights(cm) {
        var doc = cm.doc, est = estimateHeight(cm);
        doc.iter(function(line) {
            var estHeight = est(line);
            if (estHeight != line.height) {
                updateLineHeight(line, estHeight);
            }
        });
    };
    var posFromMouse = // Given a mouse event, find the corresponding position. If liberal
    // is false, it checks whether a gutter or scrollbar was clicked,
    // and returns null if it was. forRect is used by rectangular
    // selections, and tries to estimate a character position even for
    // coordinates beyond the right of the text.
    function posFromMouse(cm, e, liberal, forRect) {
        var display = cm.display;
        if (!liberal && e_target(e).getAttribute("cm-not-content") == "true") {
            return null;
        }
        var x, y, space = display.lineSpace.getBoundingClientRect();
        // Fails unpredictably on IE[67] when mouse is dragged around quickly.
        try {
            x = e.clientX - space.left;
            y = e.clientY - space.top;
        } catch (e$1) {
            return null;
        }
        var coords = coordsChar(cm, x, y), line;
        if (forRect && coords.xRel > 0 && (line = getLine(cm.doc, coords.line).text).length == coords.ch) {
            var colDiff = countColumn(line, line.length, cm.options.tabSize) - line.length;
            coords = Pos(coords.line, Math.max(0, Math.round((x - paddingH(cm.display).left) / charWidth(cm.display)) - colDiff));
        }
        return coords;
    };
    var findViewIndex = // Find the view element corresponding to a given line. Return null
    // when the line isn't visible.
    function findViewIndex(cm, n) {
        if (n >= cm.display.viewTo) {
            return null;
        }
        n -= cm.display.viewFrom;
        if (n < 0) {
            return null;
        }
        var view = cm.display.view;
        for(var i = 0; i < view.length; i++){
            n -= view[i].size;
            if (n < 0) {
                return i;
            }
        }
    };
    var regChange = // Updates the display.view data structure for a given change to the
    // document. From and to are in pre-change coordinates. Lendiff is
    // the amount of lines added or subtracted by the change. This is
    // used for changes that span multiple lines, or change the way
    // lines are divided into visual lines. regLineChange (below)
    // registers single-line changes.
    function regChange(cm, from, to, lendiff) {
        if (from == null) {
            from = cm.doc.first;
        }
        if (to == null) {
            to = cm.doc.first + cm.doc.size;
        }
        if (!lendiff) {
            lendiff = 0;
        }
        var display = cm.display;
        if (lendiff && to < display.viewTo && (display.updateLineNumbers == null || display.updateLineNumbers > from)) {
            display.updateLineNumbers = from;
        }
        cm.curOp.viewChanged = true;
        if (from >= display.viewTo) {
            if (sawCollapsedSpans && visualLineNo(cm.doc, from) < display.viewTo) {
                resetView(cm);
            }
        } else if (to <= display.viewFrom) {
            if (sawCollapsedSpans && visualLineEndNo(cm.doc, to + lendiff) > display.viewFrom) {
                resetView(cm);
            } else {
                display.viewFrom += lendiff;
                display.viewTo += lendiff;
            }
        } else if (from <= display.viewFrom && to >= display.viewTo) {
            resetView(cm);
        } else if (from <= display.viewFrom) {
            var cut = viewCuttingPoint(cm, to, to + lendiff, 1);
            if (cut) {
                display.view = display.view.slice(cut.index);
                display.viewFrom = cut.lineN;
                display.viewTo += lendiff;
            } else {
                resetView(cm);
            }
        } else if (to >= display.viewTo) {
            var cut$1 = viewCuttingPoint(cm, from, from, -1);
            if (cut$1) {
                display.view = display.view.slice(0, cut$1.index);
                display.viewTo = cut$1.lineN;
            } else {
                resetView(cm);
            }
        } else {
            var cutTop = viewCuttingPoint(cm, from, from, -1);
            var cutBot = viewCuttingPoint(cm, to, to + lendiff, 1);
            if (cutTop && cutBot) {
                display.view = display.view.slice(0, cutTop.index).concat(buildViewArray(cm, cutTop.lineN, cutBot.lineN)).concat(display.view.slice(cutBot.index));
                display.viewTo += lendiff;
            } else {
                resetView(cm);
            }
        }
        var ext = display.externalMeasured;
        if (ext) {
            if (to < ext.lineN) {
                ext.lineN += lendiff;
            } else if (from < ext.lineN + ext.size) {
                display.externalMeasured = null;
            }
        }
    };
    var regLineChange = // Register a change to a single line. Type must be one of "text",
    // "gutter", "class", "widget"
    function regLineChange(cm, line, type) {
        cm.curOp.viewChanged = true;
        var display = cm.display, ext = cm.display.externalMeasured;
        if (ext && line >= ext.lineN && line < ext.lineN + ext.size) {
            display.externalMeasured = null;
        }
        if (line < display.viewFrom || line >= display.viewTo) {
            return;
        }
        var lineView = display.view[findViewIndex(cm, line)];
        if (lineView.node == null) {
            return;
        }
        var arr = lineView.changes || (lineView.changes = []);
        if (indexOf(arr, type) == -1) {
            arr.push(type);
        }
    };
    var resetView = // Clear the view.
    function resetView(cm) {
        cm.display.viewFrom = cm.display.viewTo = cm.doc.first;
        cm.display.view = [];
        cm.display.viewOffset = 0;
    };
    var viewCuttingPoint = function viewCuttingPoint(cm, oldN, newN, dir) {
        var index = findViewIndex(cm, oldN), diff, view = cm.display.view;
        if (!sawCollapsedSpans || newN == cm.doc.first + cm.doc.size) {
            return {
                index: index,
                lineN: newN
            };
        }
        var n = cm.display.viewFrom;
        for(var i = 0; i < index; i++){
            n += view[i].size;
        }
        if (n != oldN) {
            if (dir > 0) {
                if (index == view.length - 1) {
                    return null;
                }
                diff = n + view[index].size - oldN;
                index++;
            } else {
                diff = n - oldN;
            }
            oldN += diff;
            newN += diff;
        }
        while(visualLineNo(cm.doc, newN) != newN){
            if (index == (dir < 0 ? 0 : view.length - 1)) {
                return null;
            }
            newN += dir * view[index - (dir < 0 ? 1 : 0)].size;
            index += dir;
        }
        return {
            index: index,
            lineN: newN
        };
    };
    var adjustView = // Force the view to cover a given range, adding empty view element
    // or clipping off existing ones as needed.
    function adjustView(cm, from, to) {
        var display = cm.display, view = display.view;
        if (view.length == 0 || from >= display.viewTo || to <= display.viewFrom) {
            display.view = buildViewArray(cm, from, to);
            display.viewFrom = from;
        } else {
            if (display.viewFrom > from) {
                display.view = buildViewArray(cm, from, display.viewFrom).concat(display.view);
            } else if (display.viewFrom < from) {
                display.view = display.view.slice(findViewIndex(cm, from));
            }
            display.viewFrom = from;
            if (display.viewTo < to) {
                display.view = display.view.concat(buildViewArray(cm, display.viewTo, to));
            } else if (display.viewTo > to) {
                display.view = display.view.slice(0, findViewIndex(cm, to));
            }
        }
        display.viewTo = to;
    };
    var countDirtyView = // Count the number of lines in the view whose DOM representation is
    // out of date (or nonexistent).
    function countDirtyView(cm) {
        var view = cm.display.view, dirty = 0;
        for(var i = 0; i < view.length; i++){
            var lineView = view[i];
            if (!lineView.hidden && (!lineView.node || lineView.changes)) {
                ++dirty;
            }
        }
        return dirty;
    };
    var updateSelection = function updateSelection(cm) {
        cm.display.input.showSelection(cm.display.input.prepareSelection());
    };
    var prepareSelection = function prepareSelection(cm, primary) {
        if (primary === void 0) primary = true;
        var doc = cm.doc, result = {};
        var curFragment = result.cursors = document.createDocumentFragment();
        var selFragment = result.selection = document.createDocumentFragment();
        var customCursor = cm.options.$customCursor;
        if (customCursor) {
            primary = true;
        }
        for(var i = 0; i < doc.sel.ranges.length; i++){
            if (!primary && i == doc.sel.primIndex) {
                continue;
            }
            var range = doc.sel.ranges[i];
            if (range.from().line >= cm.display.viewTo || range.to().line < cm.display.viewFrom) {
                continue;
            }
            var collapsed = range.empty();
            if (customCursor) {
                var head = customCursor(cm, range);
                if (head) {
                    drawSelectionCursor(cm, head, curFragment);
                }
            } else if (collapsed || cm.options.showCursorWhenSelecting) {
                drawSelectionCursor(cm, range.head, curFragment);
            }
            if (!collapsed) {
                drawSelectionRange(cm, range, selFragment);
            }
        }
        return result;
    };
    var drawSelectionCursor = // Draws a cursor for the given range
    function drawSelectionCursor(cm, head, output) {
        var pos = cursorCoords(cm, head, "div", null, null, !cm.options.singleCursorHeightPerLine);
        var cursor = output.appendChild(elt("div", "\xa0", "CodeMirror-cursor"));
        cursor.style.left = pos.left + "px";
        cursor.style.top = pos.top + "px";
        cursor.style.height = Math.max(0, pos.bottom - pos.top) * cm.options.cursorHeight + "px";
        if (/\bcm-fat-cursor\b/.test(cm.getWrapperElement().className)) {
            var charPos = charCoords(cm, head, "div", null, null);
            var width = charPos.right - charPos.left;
            cursor.style.width = (width > 0 ? width : cm.defaultCharWidth()) + "px";
        }
        if (pos.other) {
            // Secondary cursor, shown when on a 'jump' in bi-directional text
            var otherCursor = output.appendChild(elt("div", "\xa0", "CodeMirror-cursor CodeMirror-secondarycursor"));
            otherCursor.style.display = "";
            otherCursor.style.left = pos.other.left + "px";
            otherCursor.style.top = pos.other.top + "px";
            otherCursor.style.height = (pos.other.bottom - pos.other.top) * .85 + "px";
        }
    };
    var cmpCoords = function cmpCoords(a, b) {
        return a.top - b.top || a.left - b.left;
    };
    var drawSelectionRange = // Draws the given range as a highlighted selection
    function drawSelectionRange(cm, range, output) {
        var display = cm.display, doc = cm.doc;
        var fragment = document.createDocumentFragment();
        var padding = paddingH(cm.display), leftSide = padding.left;
        var rightSide = Math.max(display.sizerWidth, displayWidth(cm) - display.sizer.offsetLeft) - padding.right;
        var docLTR = doc.direction == "ltr";
        function add(left, top, width, bottom) {
            if (top < 0) {
                top = 0;
            }
            top = Math.round(top);
            bottom = Math.round(bottom);
            fragment.appendChild(elt("div", null, "CodeMirror-selected", "position: absolute; left: " + left + "px;\n                             top: " + top + "px; width: " + (width == null ? rightSide - left : width) + "px;\n                             height: " + (bottom - top) + "px"));
        }
        function drawForLine(line, fromArg, toArg) {
            var lineObj = getLine(doc, line);
            var lineLen = lineObj.text.length;
            var start, end;
            function coords(ch, bias) {
                return charCoords(cm, Pos(line, ch), "div", lineObj, bias);
            }
            function wrapX(pos, dir, side) {
                var extent = wrappedLineExtentChar(cm, lineObj, null, pos);
                var prop = dir == "ltr" == (side == "after") ? "left" : "right";
                var ch = side == "after" ? extent.begin : extent.end - (/\s/.test(lineObj.text.charAt(extent.end - 1)) ? 2 : 1);
                return coords(ch, prop)[prop];
            }
            var order = getOrder(lineObj, doc.direction);
            iterateBidiSections(order, fromArg || 0, toArg == null ? lineLen : toArg, function(from, to, dir, i) {
                var ltr = dir == "ltr";
                var fromPos = coords(from, ltr ? "left" : "right");
                var toPos = coords(to - 1, ltr ? "right" : "left");
                var openStart = fromArg == null && from == 0, openEnd = toArg == null && to == lineLen;
                var first = i == 0, last = !order || i == order.length - 1;
                if (toPos.top - fromPos.top <= 3) {
                    var openLeft = (docLTR ? openStart : openEnd) && first;
                    var openRight = (docLTR ? openEnd : openStart) && last;
                    var left = openLeft ? leftSide : (ltr ? fromPos : toPos).left;
                    var right = openRight ? rightSide : (ltr ? toPos : fromPos).right;
                    add(left, fromPos.top, right - left, fromPos.bottom);
                } else {
                    var topLeft, topRight, botLeft, botRight;
                    if (ltr) {
                        topLeft = docLTR && openStart && first ? leftSide : fromPos.left;
                        topRight = docLTR ? rightSide : wrapX(from, dir, "before");
                        botLeft = docLTR ? leftSide : wrapX(to, dir, "after");
                        botRight = docLTR && openEnd && last ? rightSide : toPos.right;
                    } else {
                        topLeft = !docLTR ? leftSide : wrapX(from, dir, "before");
                        topRight = !docLTR && openStart && first ? rightSide : fromPos.right;
                        botLeft = !docLTR && openEnd && last ? leftSide : toPos.left;
                        botRight = !docLTR ? rightSide : wrapX(to, dir, "after");
                    }
                    add(topLeft, fromPos.top, topRight - topLeft, fromPos.bottom);
                    if (fromPos.bottom < toPos.top) {
                        add(leftSide, fromPos.bottom, null, toPos.top);
                    }
                    add(botLeft, toPos.top, botRight - botLeft, toPos.bottom);
                }
                if (!start || cmpCoords(fromPos, start) < 0) {
                    start = fromPos;
                }
                if (cmpCoords(toPos, start) < 0) {
                    start = toPos;
                }
                if (!end || cmpCoords(fromPos, end) < 0) {
                    end = fromPos;
                }
                if (cmpCoords(toPos, end) < 0) {
                    end = toPos;
                }
            });
            return {
                start: start,
                end: end
            };
        }
        var sFrom = range.from(), sTo = range.to();
        if (sFrom.line == sTo.line) {
            drawForLine(sFrom.line, sFrom.ch, sTo.ch);
        } else {
            var fromLine = getLine(doc, sFrom.line), toLine = getLine(doc, sTo.line);
            var singleVLine = visualLine(fromLine) == visualLine(toLine);
            var leftEnd = drawForLine(sFrom.line, sFrom.ch, singleVLine ? fromLine.text.length + 1 : null).end;
            var rightStart = drawForLine(sTo.line, singleVLine ? 0 : null, sTo.ch).start;
            if (singleVLine) {
                if (leftEnd.top < rightStart.top - 2) {
                    add(leftEnd.right, leftEnd.top, null, leftEnd.bottom);
                    add(leftSide, rightStart.top, rightStart.left, rightStart.bottom);
                } else {
                    add(leftEnd.right, leftEnd.top, rightStart.left - leftEnd.right, leftEnd.bottom);
                }
            }
            if (leftEnd.bottom < rightStart.top) {
                add(leftSide, leftEnd.bottom, null, rightStart.top);
            }
        }
        output.appendChild(fragment);
    };
    var restartBlink = // Cursor-blinking
    function restartBlink(cm) {
        if (!cm.state.focused) {
            return;
        }
        var display = cm.display;
        clearInterval(display.blinker);
        var on = true;
        display.cursorDiv.style.visibility = "";
        if (cm.options.cursorBlinkRate > 0) {
            display.blinker = setInterval(function() {
                if (!cm.hasFocus()) {
                    onBlur(cm);
                }
                display.cursorDiv.style.visibility = (on = !on) ? "" : "hidden";
            }, cm.options.cursorBlinkRate);
        } else if (cm.options.cursorBlinkRate < 0) {
            display.cursorDiv.style.visibility = "hidden";
        }
    };
    var ensureFocus = function ensureFocus(cm) {
        if (!cm.hasFocus()) {
            cm.display.input.focus();
            if (!cm.state.focused) {
                onFocus(cm);
            }
        }
    };
    var delayBlurEvent = function delayBlurEvent(cm) {
        cm.state.delayingBlurEvent = true;
        setTimeout(function() {
            if (cm.state.delayingBlurEvent) {
                cm.state.delayingBlurEvent = false;
                if (cm.state.focused) {
                    onBlur(cm);
                }
            }
        }, 100);
    };
    var onFocus = function onFocus(cm, e) {
        if (cm.state.delayingBlurEvent && !cm.state.draggingText) {
            cm.state.delayingBlurEvent = false;
        }
        if (cm.options.readOnly == "nocursor") {
            return;
        }
        if (!cm.state.focused) {
            signal(cm, "focus", cm, e);
            cm.state.focused = true;
            addClass(cm.display.wrapper, "CodeMirror-focused");
            // This test prevents this from firing when a context
            // menu is closed (since the input reset would kill the
            // select-all detection hack)
            if (!cm.curOp && cm.display.selForContextMenu != cm.doc.sel) {
                cm.display.input.reset();
                if (webkit) {
                    setTimeout(function() {
                        return cm.display.input.reset(true);
                    }, 20);
                } // Issue #1730
            }
            cm.display.input.receivedFocus();
        }
        restartBlink(cm);
    };
    var onBlur = function onBlur(cm, e) {
        if (cm.state.delayingBlurEvent) {
            return;
        }
        if (cm.state.focused) {
            signal(cm, "blur", cm, e);
            cm.state.focused = false;
            rmClass(cm.display.wrapper, "CodeMirror-focused");
        }
        clearInterval(cm.display.blinker);
        setTimeout(function() {
            if (!cm.state.focused) {
                cm.display.shift = false;
            }
        }, 150);
    };
    var updateHeightsInViewport = // Read the actual heights of the rendered lines, and update their
    // stored heights to match.
    function updateHeightsInViewport(cm) {
        var display = cm.display;
        var prevBottom = display.lineDiv.offsetTop;
        var viewTop = Math.max(0, display.scroller.getBoundingClientRect().top);
        var oldHeight = display.lineDiv.getBoundingClientRect().top;
        var mustScroll = 0;
        for(var i = 0; i < display.view.length; i++){
            var cur = display.view[i], wrapping = cm.options.lineWrapping;
            var height = void 0, width = 0;
            if (cur.hidden) {
                continue;
            }
            oldHeight += cur.line.height;
            if (ie && ie_version < 8) {
                var bot = cur.node.offsetTop + cur.node.offsetHeight;
                height = bot - prevBottom;
                prevBottom = bot;
            } else {
                var box = cur.node.getBoundingClientRect();
                height = box.bottom - box.top;
                // Check that lines don't extend past the right of the current
                // editor width
                if (!wrapping && cur.text.firstChild) {
                    width = cur.text.firstChild.getBoundingClientRect().right - box.left - 1;
                }
            }
            var diff = cur.line.height - height;
            if (diff > .005 || diff < -.005) {
                if (oldHeight < viewTop) {
                    mustScroll -= diff;
                }
                updateLineHeight(cur.line, height);
                updateWidgetHeight(cur.line);
                if (cur.rest) {
                    for(var j = 0; j < cur.rest.length; j++){
                        updateWidgetHeight(cur.rest[j]);
                    }
                }
            }
            if (width > cm.display.sizerWidth) {
                var chWidth = Math.ceil(width / charWidth(cm.display));
                if (chWidth > cm.display.maxLineLength) {
                    cm.display.maxLineLength = chWidth;
                    cm.display.maxLine = cur.line;
                    cm.display.maxLineChanged = true;
                }
            }
        }
        if (Math.abs(mustScroll) > 2) {
            display.scroller.scrollTop += mustScroll;
        }
    };
    var updateWidgetHeight = // Read and store the height of line widgets associated with the
    // given line.
    function updateWidgetHeight(line) {
        if (line.widgets) {
            for(var i = 0; i < line.widgets.length; ++i){
                var w = line.widgets[i], parent = w.node.parentNode;
                if (parent) {
                    w.height = parent.offsetHeight;
                }
            }
        }
    };
    var visibleLines = // Compute the lines that are visible in a given viewport (defaults
    // the the current scroll position). viewport may contain top,
    // height, and ensure (see op.scrollToPos) properties.
    function visibleLines(display, doc, viewport) {
        var top = viewport && viewport.top != null ? Math.max(0, viewport.top) : display.scroller.scrollTop;
        top = Math.floor(top - paddingTop(display));
        var bottom = viewport && viewport.bottom != null ? viewport.bottom : top + display.wrapper.clientHeight;
        var from = lineAtHeight(doc, top), to = lineAtHeight(doc, bottom);
        // Ensure is a {from: {line, ch}, to: {line, ch}} object, and
        // forces those lines into the viewport (if possible).
        if (viewport && viewport.ensure) {
            var ensureFrom = viewport.ensure.from.line, ensureTo = viewport.ensure.to.line;
            if (ensureFrom < from) {
                from = ensureFrom;
                to = lineAtHeight(doc, heightAtLine(getLine(doc, ensureFrom)) + display.wrapper.clientHeight);
            } else if (Math.min(ensureTo, doc.lastLine()) >= to) {
                from = lineAtHeight(doc, heightAtLine(getLine(doc, ensureTo)) - display.wrapper.clientHeight);
                to = ensureTo;
            }
        }
        return {
            from: from,
            to: Math.max(to, from + 1)
        };
    };
    var maybeScrollWindow = // SCROLLING THINGS INTO VIEW
    // If an editor sits on the top or bottom of the window, partially
    // scrolled out of view, this ensures that the cursor is visible.
    function maybeScrollWindow(cm, rect) {
        if (signalDOMEvent(cm, "scrollCursorIntoView")) {
            return;
        }
        var display = cm.display, box = display.sizer.getBoundingClientRect(), doScroll = null;
        if (rect.top + box.top < 0) {
            doScroll = true;
        } else if (rect.bottom + box.top > (window.innerHeight || document.documentElement.clientHeight)) {
            doScroll = false;
        }
        if (doScroll != null && !phantom) {
            var scrollNode = elt("div", "​", null, "position: absolute;\n                         top: " + (rect.top - display.viewOffset - paddingTop(cm.display)) + "px;\n                         height: " + (rect.bottom - rect.top + scrollGap(cm) + display.barHeight) + "px;\n                         left: " + rect.left + "px; width: " + Math.max(2, rect.right - rect.left) + "px;");
            cm.display.lineSpace.appendChild(scrollNode);
            scrollNode.scrollIntoView(doScroll);
            cm.display.lineSpace.removeChild(scrollNode);
        }
    };
    var scrollPosIntoView = // Scroll a given position into view (immediately), verifying that
    // it actually became visible (as line heights are accurately
    // measured, the position of something may 'drift' during drawing).
    function scrollPosIntoView(cm, pos, end, margin) {
        if (margin == null) {
            margin = 0;
        }
        var rect;
        if (!cm.options.lineWrapping && pos == end) {
            // Set pos and end to the cursor positions around the character pos sticks to
            // If pos.sticky == "before", that is around pos.ch - 1, otherwise around pos.ch
            // If pos == Pos(_, 0, "before"), pos and end are unchanged
            end = pos.sticky == "before" ? Pos(pos.line, pos.ch + 1, "before") : pos;
            pos = pos.ch ? Pos(pos.line, pos.sticky == "before" ? pos.ch - 1 : pos.ch, "after") : pos;
        }
        for(var limit = 0; limit < 5; limit++){
            var changed = false;
            var coords = cursorCoords(cm, pos);
            var endCoords = !end || end == pos ? coords : cursorCoords(cm, end);
            rect = {
                left: Math.min(coords.left, endCoords.left),
                top: Math.min(coords.top, endCoords.top) - margin,
                right: Math.max(coords.left, endCoords.left),
                bottom: Math.max(coords.bottom, endCoords.bottom) + margin
            };
            var scrollPos = calculateScrollPos(cm, rect);
            var startTop = cm.doc.scrollTop, startLeft = cm.doc.scrollLeft;
            if (scrollPos.scrollTop != null) {
                updateScrollTop(cm, scrollPos.scrollTop);
                if (Math.abs(cm.doc.scrollTop - startTop) > 1) {
                    changed = true;
                }
            }
            if (scrollPos.scrollLeft != null) {
                setScrollLeft(cm, scrollPos.scrollLeft);
                if (Math.abs(cm.doc.scrollLeft - startLeft) > 1) {
                    changed = true;
                }
            }
            if (!changed) {
                break;
            }
        }
        return rect;
    };
    var scrollIntoView = // Scroll a given set of coordinates into view (immediately).
    function scrollIntoView(cm, rect) {
        var scrollPos = calculateScrollPos(cm, rect);
        if (scrollPos.scrollTop != null) {
            updateScrollTop(cm, scrollPos.scrollTop);
        }
        if (scrollPos.scrollLeft != null) {
            setScrollLeft(cm, scrollPos.scrollLeft);
        }
    };
    var calculateScrollPos = // Calculate a new scroll position needed to scroll the given
    // rectangle into view. Returns an object with scrollTop and
    // scrollLeft properties. When these are undefined, the
    // vertical/horizontal position does not need to be adjusted.
    function calculateScrollPos(cm, rect) {
        var display = cm.display, snapMargin = textHeight(cm.display);
        if (rect.top < 0) {
            rect.top = 0;
        }
        var screentop = cm.curOp && cm.curOp.scrollTop != null ? cm.curOp.scrollTop : display.scroller.scrollTop;
        var screen1 = displayHeight(cm), result = {};
        if (rect.bottom - rect.top > screen1) {
            rect.bottom = rect.top + screen1;
        }
        var docBottom = cm.doc.height + paddingVert(display);
        var atTop = rect.top < snapMargin, atBottom = rect.bottom > docBottom - snapMargin;
        if (rect.top < screentop) {
            result.scrollTop = atTop ? 0 : rect.top;
        } else if (rect.bottom > screentop + screen1) {
            var newTop = Math.min(rect.top, (atBottom ? docBottom : rect.bottom) - screen1);
            if (newTop != screentop) {
                result.scrollTop = newTop;
            }
        }
        var gutterSpace = cm.options.fixedGutter ? 0 : display.gutters.offsetWidth;
        var screenleft = cm.curOp && cm.curOp.scrollLeft != null ? cm.curOp.scrollLeft : display.scroller.scrollLeft - gutterSpace;
        var screenw = displayWidth(cm) - display.gutters.offsetWidth;
        var tooWide = rect.right - rect.left > screenw;
        if (tooWide) {
            rect.right = rect.left + screenw;
        }
        if (rect.left < 10) {
            result.scrollLeft = 0;
        } else if (rect.left < screenleft) {
            result.scrollLeft = Math.max(0, rect.left + gutterSpace - (tooWide ? 0 : 10));
        } else if (rect.right > screenw + screenleft - 3) {
            result.scrollLeft = rect.right + (tooWide ? 0 : 10) - screenw;
        }
        return result;
    };
    var addToScrollTop = // Store a relative adjustment to the scroll position in the current
    // operation (to be applied when the operation finishes).
    function addToScrollTop(cm, top) {
        if (top == null) {
            return;
        }
        resolveScrollToPos(cm);
        cm.curOp.scrollTop = (cm.curOp.scrollTop == null ? cm.doc.scrollTop : cm.curOp.scrollTop) + top;
    };
    var ensureCursorVisible = // Make sure that at the end of the operation the current cursor is
    // shown.
    function ensureCursorVisible(cm) {
        resolveScrollToPos(cm);
        var cur = cm.getCursor();
        cm.curOp.scrollToPos = {
            from: cur,
            to: cur,
            margin: cm.options.cursorScrollMargin
        };
    };
    var scrollToCoords = function scrollToCoords(cm, x, y) {
        if (x != null || y != null) {
            resolveScrollToPos(cm);
        }
        if (x != null) {
            cm.curOp.scrollLeft = x;
        }
        if (y != null) {
            cm.curOp.scrollTop = y;
        }
    };
    var scrollToRange = function scrollToRange(cm, range) {
        resolveScrollToPos(cm);
        cm.curOp.scrollToPos = range;
    };
    var resolveScrollToPos = // When an operation has its scrollToPos property set, and another
    // scroll action is applied before the end of the operation, this
    // 'simulates' scrolling that position into view in a cheap way, so
    // that the effect of intermediate scroll commands is not ignored.
    function resolveScrollToPos(cm) {
        var range = cm.curOp.scrollToPos;
        if (range) {
            cm.curOp.scrollToPos = null;
            var from = estimateCoords(cm, range.from), to = estimateCoords(cm, range.to);
            scrollToCoordsRange(cm, from, to, range.margin);
        }
    };
    var scrollToCoordsRange = function scrollToCoordsRange(cm, from, to, margin) {
        var sPos = calculateScrollPos(cm, {
            left: Math.min(from.left, to.left),
            top: Math.min(from.top, to.top) - margin,
            right: Math.max(from.right, to.right),
            bottom: Math.max(from.bottom, to.bottom) + margin
        });
        scrollToCoords(cm, sPos.scrollLeft, sPos.scrollTop);
    };
    var updateScrollTop = // Sync the scrollable area and scrollbars, ensure the viewport
    // covers the visible area.
    function updateScrollTop(cm, val) {
        if (Math.abs(cm.doc.scrollTop - val) < 2) {
            return;
        }
        if (!gecko) {
            updateDisplaySimple(cm, {
                top: val
            });
        }
        setScrollTop(cm, val, true);
        if (gecko) {
            updateDisplaySimple(cm);
        }
        startWorker(cm, 100);
    };
    var setScrollTop = function setScrollTop(cm, val, forceScroll) {
        val = Math.max(0, Math.min(cm.display.scroller.scrollHeight - cm.display.scroller.clientHeight, val));
        if (cm.display.scroller.scrollTop == val && !forceScroll) {
            return;
        }
        cm.doc.scrollTop = val;
        cm.display.scrollbars.setScrollTop(val);
        if (cm.display.scroller.scrollTop != val) {
            cm.display.scroller.scrollTop = val;
        }
    };
    var setScrollLeft = // Sync scroller and scrollbar, ensure the gutter elements are
    // aligned.
    function setScrollLeft(cm, val, isScroller, forceScroll) {
        val = Math.max(0, Math.min(val, cm.display.scroller.scrollWidth - cm.display.scroller.clientWidth));
        if ((isScroller ? val == cm.doc.scrollLeft : Math.abs(cm.doc.scrollLeft - val) < 2) && !forceScroll) {
            return;
        }
        cm.doc.scrollLeft = val;
        alignHorizontally(cm);
        if (cm.display.scroller.scrollLeft != val) {
            cm.display.scroller.scrollLeft = val;
        }
        cm.display.scrollbars.setScrollLeft(val);
    };
    var measureForScrollbars = // SCROLLBARS
    // Prepare DOM reads needed to update the scrollbars. Done in one
    // shot to minimize update/measure roundtrips.
    function measureForScrollbars(cm) {
        var d = cm.display, gutterW = d.gutters.offsetWidth;
        var docH = Math.round(cm.doc.height + paddingVert(cm.display));
        return {
            clientHeight: d.scroller.clientHeight,
            viewHeight: d.wrapper.clientHeight,
            scrollWidth: d.scroller.scrollWidth,
            clientWidth: d.scroller.clientWidth,
            viewWidth: d.wrapper.clientWidth,
            barLeft: cm.options.fixedGutter ? gutterW : 0,
            docHeight: docH,
            scrollHeight: docH + scrollGap(cm) + d.barHeight,
            nativeBarWidth: d.nativeBarWidth,
            gutterWidth: gutterW
        };
    };
    var updateScrollbars = function updateScrollbars(cm, measure) {
        if (!measure) {
            measure = measureForScrollbars(cm);
        }
        var startWidth = cm.display.barWidth, startHeight = cm.display.barHeight;
        updateScrollbarsInner(cm, measure);
        for(var i = 0; i < 4 && startWidth != cm.display.barWidth || startHeight != cm.display.barHeight; i++){
            if (startWidth != cm.display.barWidth && cm.options.lineWrapping) {
                updateHeightsInViewport(cm);
            }
            updateScrollbarsInner(cm, measureForScrollbars(cm));
            startWidth = cm.display.barWidth;
            startHeight = cm.display.barHeight;
        }
    };
    var updateScrollbarsInner = // Re-synchronize the fake scrollbars with the actual size of the
    // content.
    function updateScrollbarsInner(cm, measure) {
        var d = cm.display;
        var sizes = d.scrollbars.update(measure);
        d.sizer.style.paddingRight = (d.barWidth = sizes.right) + "px";
        d.sizer.style.paddingBottom = (d.barHeight = sizes.bottom) + "px";
        d.heightForcer.style.borderBottom = sizes.bottom + "px solid transparent";
        if (sizes.right && sizes.bottom) {
            d.scrollbarFiller.style.display = "block";
            d.scrollbarFiller.style.height = sizes.bottom + "px";
            d.scrollbarFiller.style.width = sizes.right + "px";
        } else {
            d.scrollbarFiller.style.display = "";
        }
        if (sizes.bottom && cm.options.coverGutterNextToScrollbar && cm.options.fixedGutter) {
            d.gutterFiller.style.display = "block";
            d.gutterFiller.style.height = sizes.bottom + "px";
            d.gutterFiller.style.width = measure.gutterWidth + "px";
        } else {
            d.gutterFiller.style.display = "";
        }
    };
    var initScrollbars = function initScrollbars(cm) {
        if (cm.display.scrollbars) {
            cm.display.scrollbars.clear();
            if (cm.display.scrollbars.addClass) {
                rmClass(cm.display.wrapper, cm.display.scrollbars.addClass);
            }
        }
        cm.display.scrollbars = new scrollbarModel[cm.options.scrollbarStyle](function(node) {
            cm.display.wrapper.insertBefore(node, cm.display.scrollbarFiller);
            // Prevent clicks in the scrollbars from killing focus
            on(node, "mousedown", function() {
                if (cm.state.focused) {
                    setTimeout(function() {
                        return cm.display.input.focus();
                    }, 0);
                }
            });
            node.setAttribute("cm-not-content", "true");
        }, function(pos, axis) {
            if (axis == "horizontal") {
                setScrollLeft(cm, pos);
            } else {
                updateScrollTop(cm, pos);
            }
        }, cm);
        if (cm.display.scrollbars.addClass) {
            addClass(cm.display.wrapper, cm.display.scrollbars.addClass);
        }
    };
    var startOperation = // Start a new operation.
    function startOperation(cm) {
        cm.curOp = {
            cm: cm,
            viewChanged: false,
            startHeight: cm.doc.height,
            forceUpdate: false,
            updateInput: 0,
            typing: false,
            changeObjs: null,
            cursorActivityHandlers: null,
            cursorActivityCalled: 0,
            selectionChanged: false,
            updateMaxLine: false,
            scrollLeft: null,
            scrollTop: null,
            scrollToPos: null,
            focus: false,
            id: ++nextOpId,
            markArrays: null // Used by addMarkedSpan
        };
        pushOperation(cm.curOp);
    };
    var endOperation = // Finish an operation, updating the display and signalling delayed events
    function endOperation(cm) {
        var op = cm.curOp;
        if (op) {
            finishOperation(op, function(group) {
                for(var i = 0; i < group.ops.length; i++){
                    group.ops[i].cm.curOp = null;
                }
                endOperations(group);
            });
        }
    };
    var endOperations = // The DOM updates done when an operation finishes are batched so
    // that the minimum number of relayouts are required.
    function endOperations(group) {
        var ops = group.ops;
        for(var i = 0; i < ops.length; i++){
            endOperation_R1(ops[i]);
        }
        for(var i$1 = 0; i$1 < ops.length; i$1++){
            endOperation_W1(ops[i$1]);
        }
        for(var i$2 = 0; i$2 < ops.length; i$2++){
            endOperation_R2(ops[i$2]);
        }
        for(var i$3 = 0; i$3 < ops.length; i$3++){
            endOperation_W2(ops[i$3]);
        }
        for(var i$4 = 0; i$4 < ops.length; i$4++){
            endOperation_finish(ops[i$4]);
        }
    };
    var endOperation_R1 = function endOperation_R1(op) {
        var cm = op.cm, display = cm.display;
        maybeClipScrollbars(cm);
        if (op.updateMaxLine) {
            findMaxLine(cm);
        }
        op.mustUpdate = op.viewChanged || op.forceUpdate || op.scrollTop != null || op.scrollToPos && (op.scrollToPos.from.line < display.viewFrom || op.scrollToPos.to.line >= display.viewTo) || display.maxLineChanged && cm.options.lineWrapping;
        op.update = op.mustUpdate && new DisplayUpdate(cm, op.mustUpdate && {
            top: op.scrollTop,
            ensure: op.scrollToPos
        }, op.forceUpdate);
    };
    var endOperation_W1 = function endOperation_W1(op) {
        op.updatedDisplay = op.mustUpdate && updateDisplayIfNeeded(op.cm, op.update);
    };
    var endOperation_R2 = function endOperation_R2(op) {
        var cm = op.cm, display = cm.display;
        if (op.updatedDisplay) {
            updateHeightsInViewport(cm);
        }
        op.barMeasure = measureForScrollbars(cm);
        // If the max line changed since it was last measured, measure it,
        // and ensure the document's width matches it.
        // updateDisplay_W2 will use these properties to do the actual resizing
        if (display.maxLineChanged && !cm.options.lineWrapping) {
            op.adjustWidthTo = measureChar(cm, display.maxLine, display.maxLine.text.length).left + 3;
            cm.display.sizerWidth = op.adjustWidthTo;
            op.barMeasure.scrollWidth = Math.max(display.scroller.clientWidth, display.sizer.offsetLeft + op.adjustWidthTo + scrollGap(cm) + cm.display.barWidth);
            op.maxScrollLeft = Math.max(0, display.sizer.offsetLeft + op.adjustWidthTo - displayWidth(cm));
        }
        if (op.updatedDisplay || op.selectionChanged) {
            op.preparedSelection = display.input.prepareSelection();
        }
    };
    var endOperation_W2 = function endOperation_W2(op) {
        var cm = op.cm;
        if (op.adjustWidthTo != null) {
            cm.display.sizer.style.minWidth = op.adjustWidthTo + "px";
            if (op.maxScrollLeft < cm.doc.scrollLeft) {
                setScrollLeft(cm, Math.min(cm.display.scroller.scrollLeft, op.maxScrollLeft), true);
            }
            cm.display.maxLineChanged = false;
        }
        var takeFocus = op.focus && op.focus == activeElt();
        if (op.preparedSelection) {
            cm.display.input.showSelection(op.preparedSelection, takeFocus);
        }
        if (op.updatedDisplay || op.startHeight != cm.doc.height) {
            updateScrollbars(cm, op.barMeasure);
        }
        if (op.updatedDisplay) {
            setDocumentHeight(cm, op.barMeasure);
        }
        if (op.selectionChanged) {
            restartBlink(cm);
        }
        if (cm.state.focused && op.updateInput) {
            cm.display.input.reset(op.typing);
        }
        if (takeFocus) {
            ensureFocus(op.cm);
        }
    };
    var endOperation_finish = function endOperation_finish(op) {
        var cm = op.cm, display = cm.display, doc = cm.doc;
        if (op.updatedDisplay) {
            postUpdateDisplay(cm, op.update);
        }
        // Abort mouse wheel delta measurement, when scrolling explicitly
        if (display.wheelStartX != null && (op.scrollTop != null || op.scrollLeft != null || op.scrollToPos)) {
            display.wheelStartX = display.wheelStartY = null;
        }
        // Propagate the scroll position to the actual DOM scroller
        if (op.scrollTop != null) {
            setScrollTop(cm, op.scrollTop, op.forceScroll);
        }
        if (op.scrollLeft != null) {
            setScrollLeft(cm, op.scrollLeft, true, true);
        }
        // If we need to scroll a specific position into view, do so.
        if (op.scrollToPos) {
            var rect = scrollPosIntoView(cm, clipPos(doc, op.scrollToPos.from), clipPos(doc, op.scrollToPos.to), op.scrollToPos.margin);
            maybeScrollWindow(cm, rect);
        }
        // Fire events for markers that are hidden/unidden by editing or
        // undoing
        var hidden = op.maybeHiddenMarkers, unhidden = op.maybeUnhiddenMarkers;
        if (hidden) {
            for(var i = 0; i < hidden.length; ++i){
                if (!hidden[i].lines.length) {
                    signal(hidden[i], "hide");
                }
            }
        }
        if (unhidden) {
            for(var i$1 = 0; i$1 < unhidden.length; ++i$1){
                if (unhidden[i$1].lines.length) {
                    signal(unhidden[i$1], "unhide");
                }
            }
        }
        if (display.wrapper.offsetHeight) {
            doc.scrollTop = cm.display.scroller.scrollTop;
        }
        // Fire change events, and delayed event handlers
        if (op.changeObjs) {
            signal(cm, "changes", cm, op.changeObjs);
        }
        if (op.update) {
            op.update.finish();
        }
    };
    var runInOp = // Run the given function in an operation
    function runInOp(cm, f) {
        if (cm.curOp) {
            return f();
        }
        startOperation(cm);
        try {
            return f();
        } finally{
            endOperation(cm);
        }
    };
    var operation = // Wraps a function in an operation. Returns the wrapped function.
    function operation(cm, f) {
        return function() {
            if (cm.curOp) {
                return f.apply(cm, arguments);
            }
            startOperation(cm);
            try {
                return f.apply(cm, arguments);
            } finally{
                endOperation(cm);
            }
        };
    };
    var methodOp = // Used to add methods to editor and doc instances, wrapping them in
    // operations.
    function methodOp(f) {
        return function() {
            if (this.curOp) {
                return f.apply(this, arguments);
            }
            startOperation(this);
            try {
                return f.apply(this, arguments);
            } finally{
                endOperation(this);
            }
        };
    };
    var docMethodOp = function docMethodOp(f) {
        return function() {
            var cm = this.cm;
            if (!cm || cm.curOp) {
                return f.apply(this, arguments);
            }
            startOperation(cm);
            try {
                return f.apply(this, arguments);
            } finally{
                endOperation(cm);
            }
        };
    };
    var startWorker = // HIGHLIGHT WORKER
    function startWorker(cm, time) {
        if (cm.doc.highlightFrontier < cm.display.viewTo) {
            cm.state.highlight.set(time, bind(highlightWorker, cm));
        }
    };
    var highlightWorker = function highlightWorker(cm) {
        var doc = cm.doc;
        if (doc.highlightFrontier >= cm.display.viewTo) {
            return;
        }
        var end = +new Date + cm.options.workTime;
        var context = getContextBefore(cm, doc.highlightFrontier);
        var changedLines = [];
        doc.iter(context.line, Math.min(doc.first + doc.size, cm.display.viewTo + 500), function(line) {
            if (context.line >= cm.display.viewFrom) {
                var oldStyles = line.styles;
                var resetState = line.text.length > cm.options.maxHighlightLength ? copyState(doc.mode, context.state) : null;
                var highlighted = highlightLine(cm, line, context, true);
                if (resetState) {
                    context.state = resetState;
                }
                line.styles = highlighted.styles;
                var oldCls = line.styleClasses, newCls = highlighted.classes;
                if (newCls) {
                    line.styleClasses = newCls;
                } else if (oldCls) {
                    line.styleClasses = null;
                }
                var ischange = !oldStyles || oldStyles.length != line.styles.length || oldCls != newCls && (!oldCls || !newCls || oldCls.bgClass != newCls.bgClass || oldCls.textClass != newCls.textClass);
                for(var i = 0; !ischange && i < oldStyles.length; ++i){
                    ischange = oldStyles[i] != line.styles[i];
                }
                if (ischange) {
                    changedLines.push(context.line);
                }
                line.stateAfter = context.save();
                context.nextLine();
            } else {
                if (line.text.length <= cm.options.maxHighlightLength) {
                    processLine(cm, line.text, context);
                }
                line.stateAfter = context.line % 5 == 0 ? context.save() : null;
                context.nextLine();
            }
            if (+new Date > end) {
                startWorker(cm, cm.options.workDelay);
                return true;
            }
        });
        doc.highlightFrontier = context.line;
        doc.modeFrontier = Math.max(doc.modeFrontier, context.line);
        if (changedLines.length) {
            runInOp(cm, function() {
                for(var i = 0; i < changedLines.length; i++){
                    regLineChange(cm, changedLines[i], "text");
                }
            });
        }
    };
    var maybeClipScrollbars = function maybeClipScrollbars(cm) {
        var display = cm.display;
        if (!display.scrollbarsClipped && display.scroller.offsetWidth) {
            display.nativeBarWidth = display.scroller.offsetWidth - display.scroller.clientWidth;
            display.heightForcer.style.height = scrollGap(cm) + "px";
            display.sizer.style.marginBottom = -display.nativeBarWidth + "px";
            display.sizer.style.borderRightWidth = scrollGap(cm) + "px";
            display.scrollbarsClipped = true;
        }
    };
    var selectionSnapshot = function selectionSnapshot(cm) {
        if (cm.hasFocus()) {
            return null;
        }
        var active = activeElt();
        if (!active || !contains(cm.display.lineDiv, active)) {
            return null;
        }
        var result = {
            activeElt: active
        };
        if (window.getSelection) {
            var sel = window.getSelection();
            if (sel.anchorNode && sel.extend && contains(cm.display.lineDiv, sel.anchorNode)) {
                result.anchorNode = sel.anchorNode;
                result.anchorOffset = sel.anchorOffset;
                result.focusNode = sel.focusNode;
                result.focusOffset = sel.focusOffset;
            }
        }
        return result;
    };
    var restoreSelection = function restoreSelection(snapshot) {
        if (!snapshot || !snapshot.activeElt || snapshot.activeElt == activeElt()) {
            return;
        }
        snapshot.activeElt.focus();
        if (!/^(INPUT|TEXTAREA)$/.test(snapshot.activeElt.nodeName) && snapshot.anchorNode && contains(document.body, snapshot.anchorNode) && contains(document.body, snapshot.focusNode)) {
            var sel = window.getSelection(), range = document.createRange();
            range.setEnd(snapshot.anchorNode, snapshot.anchorOffset);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
            sel.extend(snapshot.focusNode, snapshot.focusOffset);
        }
    };
    var updateDisplayIfNeeded = // Does the actual updating of the line display. Bails out
    // (returning false) when there is nothing to be done and forced is
    // false.
    function updateDisplayIfNeeded(cm, update) {
        var display = cm.display, doc = cm.doc;
        if (update.editorIsHidden) {
            resetView(cm);
            return false;
        }
        // Bail out if the visible area is already rendered and nothing changed.
        if (!update.force && update.visible.from >= display.viewFrom && update.visible.to <= display.viewTo && (display.updateLineNumbers == null || display.updateLineNumbers >= display.viewTo) && display.renderedView == display.view && countDirtyView(cm) == 0) {
            return false;
        }
        if (maybeUpdateLineNumberWidth(cm)) {
            resetView(cm);
            update.dims = getDimensions(cm);
        }
        // Compute a suitable new viewport (from & to)
        var end = doc.first + doc.size;
        var from = Math.max(update.visible.from - cm.options.viewportMargin, doc.first);
        var to = Math.min(end, update.visible.to + cm.options.viewportMargin);
        if (display.viewFrom < from && from - display.viewFrom < 20) {
            from = Math.max(doc.first, display.viewFrom);
        }
        if (display.viewTo > to && display.viewTo - to < 20) {
            to = Math.min(end, display.viewTo);
        }
        if (sawCollapsedSpans) {
            from = visualLineNo(cm.doc, from);
            to = visualLineEndNo(cm.doc, to);
        }
        var different = from != display.viewFrom || to != display.viewTo || display.lastWrapHeight != update.wrapperHeight || display.lastWrapWidth != update.wrapperWidth;
        adjustView(cm, from, to);
        display.viewOffset = heightAtLine(getLine(cm.doc, display.viewFrom));
        // Position the mover div to align with the current scroll position
        cm.display.mover.style.top = display.viewOffset + "px";
        var toUpdate = countDirtyView(cm);
        if (!different && toUpdate == 0 && !update.force && display.renderedView == display.view && (display.updateLineNumbers == null || display.updateLineNumbers >= display.viewTo)) {
            return false;
        }
        // For big changes, we hide the enclosing element during the
        // update, since that speeds up the operations on most browsers.
        var selSnapshot = selectionSnapshot(cm);
        if (toUpdate > 4) {
            display.lineDiv.style.display = "none";
        }
        patchDisplay(cm, display.updateLineNumbers, update.dims);
        if (toUpdate > 4) {
            display.lineDiv.style.display = "";
        }
        display.renderedView = display.view;
        // There might have been a widget with a focused element that got
        // hidden or updated, if so re-focus it.
        restoreSelection(selSnapshot);
        // Prevent selection and cursors from interfering with the scroll
        // width and height.
        removeChildren(display.cursorDiv);
        removeChildren(display.selectionDiv);
        display.gutters.style.height = display.sizer.style.minHeight = 0;
        if (different) {
            display.lastWrapHeight = update.wrapperHeight;
            display.lastWrapWidth = update.wrapperWidth;
            startWorker(cm, 400);
        }
        display.updateLineNumbers = null;
        return true;
    };
    var postUpdateDisplay = function postUpdateDisplay(cm, update) {
        var viewport = update.viewport;
        for(var first = true;; first = false){
            if (!first || !cm.options.lineWrapping || update.oldDisplayWidth == displayWidth(cm)) {
                // Clip forced viewport to actual scrollable area.
                if (viewport && viewport.top != null) {
                    viewport = {
                        top: Math.min(cm.doc.height + paddingVert(cm.display) - displayHeight(cm), viewport.top)
                    };
                }
                // Updated line heights might result in the drawn area not
                // actually covering the viewport. Keep looping until it does.
                update.visible = visibleLines(cm.display, cm.doc, viewport);
                if (update.visible.from >= cm.display.viewFrom && update.visible.to <= cm.display.viewTo) {
                    break;
                }
            } else if (first) {
                update.visible = visibleLines(cm.display, cm.doc, viewport);
            }
            if (!updateDisplayIfNeeded(cm, update)) {
                break;
            }
            updateHeightsInViewport(cm);
            var barMeasure = measureForScrollbars(cm);
            updateSelection(cm);
            updateScrollbars(cm, barMeasure);
            setDocumentHeight(cm, barMeasure);
            update.force = false;
        }
        update.signal(cm, "update", cm);
        if (cm.display.viewFrom != cm.display.reportedViewFrom || cm.display.viewTo != cm.display.reportedViewTo) {
            update.signal(cm, "viewportChange", cm, cm.display.viewFrom, cm.display.viewTo);
            cm.display.reportedViewFrom = cm.display.viewFrom;
            cm.display.reportedViewTo = cm.display.viewTo;
        }
    };
    var updateDisplaySimple = function updateDisplaySimple(cm, viewport) {
        var update = new DisplayUpdate(cm, viewport);
        if (updateDisplayIfNeeded(cm, update)) {
            updateHeightsInViewport(cm);
            postUpdateDisplay(cm, update);
            var barMeasure = measureForScrollbars(cm);
            updateSelection(cm);
            updateScrollbars(cm, barMeasure);
            setDocumentHeight(cm, barMeasure);
            update.finish();
        }
    };
    var patchDisplay = // Sync the actual display DOM structure with display.view, removing
    // nodes for lines that are no longer in view, and creating the ones
    // that are not there yet, and updating the ones that are out of
    // date.
    function patchDisplay(cm, updateNumbersFrom, dims) {
        var display = cm.display, lineNumbers = cm.options.lineNumbers;
        var container = display.lineDiv, cur = container.firstChild;
        function rm(node) {
            var next = node.nextSibling;
            // Works around a throw-scroll bug in OS X Webkit
            if (webkit && mac && cm.display.currentWheelTarget == node) {
                node.style.display = "none";
            } else {
                node.parentNode.removeChild(node);
            }
            return next;
        }
        var view = display.view, lineN = display.viewFrom;
        // Loop over the elements in the view, syncing cur (the DOM nodes
        // in display.lineDiv) with the view as we go.
        for(var i = 0; i < view.length; i++){
            var lineView = view[i];
            if (lineView.hidden) ;
            else if (!lineView.node || lineView.node.parentNode != container) {
                var node = buildLineElement(cm, lineView, lineN, dims);
                container.insertBefore(node, cur);
            } else {
                while(cur != lineView.node){
                    cur = rm(cur);
                }
                var updateNumber = lineNumbers && updateNumbersFrom != null && updateNumbersFrom <= lineN && lineView.lineNumber;
                if (lineView.changes) {
                    if (indexOf(lineView.changes, "gutter") > -1) {
                        updateNumber = false;
                    }
                    updateLineForChanges(cm, lineView, lineN, dims);
                }
                if (updateNumber) {
                    removeChildren(lineView.lineNumber);
                    lineView.lineNumber.appendChild(document.createTextNode(lineNumberFor(cm.options, lineN)));
                }
                cur = lineView.node.nextSibling;
            }
            lineN += lineView.size;
        }
        while(cur){
            cur = rm(cur);
        }
    };
    var updateGutterSpace = function updateGutterSpace(display) {
        var width = display.gutters.offsetWidth;
        display.sizer.style.marginLeft = width + "px";
        // Send an event to consumers responding to changes in gutter width.
        signalLater(display, "gutterChanged", display);
    };
    var setDocumentHeight = function setDocumentHeight(cm, measure) {
        cm.display.sizer.style.minHeight = measure.docHeight + "px";
        cm.display.heightForcer.style.top = measure.docHeight + "px";
        cm.display.gutters.style.height = measure.docHeight + cm.display.barHeight + scrollGap(cm) + "px";
    };
    var alignHorizontally = // Re-align line numbers and gutter marks to compensate for
    // horizontal scrolling.
    function alignHorizontally(cm) {
        var display = cm.display, view = display.view;
        if (!display.alignWidgets && (!display.gutters.firstChild || !cm.options.fixedGutter)) {
            return;
        }
        var comp = compensateForHScroll(display) - display.scroller.scrollLeft + cm.doc.scrollLeft;
        var gutterW = display.gutters.offsetWidth, left = comp + "px";
        for(var i = 0; i < view.length; i++){
            if (!view[i].hidden) {
                if (cm.options.fixedGutter) {
                    if (view[i].gutter) {
                        view[i].gutter.style.left = left;
                    }
                    if (view[i].gutterBackground) {
                        view[i].gutterBackground.style.left = left;
                    }
                }
                var align = view[i].alignable;
                if (align) {
                    for(var j = 0; j < align.length; j++){
                        align[j].style.left = left;
                    }
                }
            }
        }
        if (cm.options.fixedGutter) {
            display.gutters.style.left = comp + gutterW + "px";
        }
    };
    var maybeUpdateLineNumberWidth = // Used to ensure that the line number gutter is still the right
    // size for the current document size. Returns true when an update
    // is needed.
    function maybeUpdateLineNumberWidth(cm) {
        if (!cm.options.lineNumbers) {
            return false;
        }
        var doc = cm.doc, last = lineNumberFor(cm.options, doc.first + doc.size - 1), display = cm.display;
        if (last.length != display.lineNumChars) {
            var test = display.measure.appendChild(elt("div", [
                elt("div", last)
            ], "CodeMirror-linenumber CodeMirror-gutter-elt"));
            var innerW = test.firstChild.offsetWidth, padding = test.offsetWidth - innerW;
            display.lineGutter.style.width = "";
            display.lineNumInnerWidth = Math.max(innerW, display.lineGutter.offsetWidth - padding) + 1;
            display.lineNumWidth = display.lineNumInnerWidth + padding;
            display.lineNumChars = display.lineNumInnerWidth ? last.length : -1;
            display.lineGutter.style.width = display.lineNumWidth + "px";
            updateGutterSpace(cm.display);
            return true;
        }
        return false;
    };
    var getGutters = function getGutters(gutters, lineNumbers) {
        var result = [], sawLineNumbers = false;
        for(var i = 0; i < gutters.length; i++){
            var name = gutters[i], style = null;
            if (typeof name != "string") {
                style = name.style;
                name = name.className;
            }
            if (name == "CodeMirror-linenumbers") {
                if (!lineNumbers) {
                    continue;
                } else {
                    sawLineNumbers = true;
                }
            }
            result.push({
                className: name,
                style: style
            });
        }
        if (lineNumbers && !sawLineNumbers) {
            result.push({
                className: "CodeMirror-linenumbers",
                style: null
            });
        }
        return result;
    };
    var renderGutters = // Rebuild the gutter elements, ensure the margin to the left of the
    // code matches their width.
    function renderGutters(display) {
        var gutters = display.gutters, specs = display.gutterSpecs;
        removeChildren(gutters);
        display.lineGutter = null;
        for(var i = 0; i < specs.length; ++i){
            var ref = specs[i];
            var className = ref.className;
            var style = ref.style;
            var gElt = gutters.appendChild(elt("div", null, "CodeMirror-gutter " + className));
            if (style) {
                gElt.style.cssText = style;
            }
            if (className == "CodeMirror-linenumbers") {
                display.lineGutter = gElt;
                gElt.style.width = (display.lineNumWidth || 1) + "px";
            }
        }
        gutters.style.display = specs.length ? "" : "none";
        updateGutterSpace(display);
    };
    var updateGutters = function updateGutters(cm) {
        renderGutters(cm.display);
        regChange(cm);
        alignHorizontally(cm);
    };
    var Display = // The display handles the DOM integration, both for input reading
    // and content drawing. It holds references to DOM nodes and
    // display-related state.
    function Display(place, doc, input, options) {
        var d = this;
        this.input = input;
        // Covers bottom-right square when both scrollbars are present.
        d.scrollbarFiller = elt("div", null, "CodeMirror-scrollbar-filler");
        d.scrollbarFiller.setAttribute("cm-not-content", "true");
        // Covers bottom of gutter when coverGutterNextToScrollbar is on
        // and h scrollbar is present.
        d.gutterFiller = elt("div", null, "CodeMirror-gutter-filler");
        d.gutterFiller.setAttribute("cm-not-content", "true");
        // Will contain the actual code, positioned to cover the viewport.
        d.lineDiv = eltP("div", null, "CodeMirror-code");
        // Elements are added to these to represent selection and cursors.
        d.selectionDiv = elt("div", null, null, "position: relative; z-index: 1");
        d.cursorDiv = elt("div", null, "CodeMirror-cursors");
        // A visibility: hidden element used to find the size of things.
        d.measure = elt("div", null, "CodeMirror-measure");
        // When lines outside of the viewport are measured, they are drawn in this.
        d.lineMeasure = elt("div", null, "CodeMirror-measure");
        // Wraps everything that needs to exist inside the vertically-padded coordinate system
        d.lineSpace = eltP("div", [
            d.measure,
            d.lineMeasure,
            d.selectionDiv,
            d.cursorDiv,
            d.lineDiv
        ], null, "position: relative; outline: none");
        var lines = eltP("div", [
            d.lineSpace
        ], "CodeMirror-lines");
        // Moved around its parent to cover visible view.
        d.mover = elt("div", [
            lines
        ], null, "position: relative");
        // Set to the height of the document, allowing scrolling.
        d.sizer = elt("div", [
            d.mover
        ], "CodeMirror-sizer");
        d.sizerWidth = null;
        // Behavior of elts with overflow: auto and padding is
        // inconsistent across browsers. This is used to ensure the
        // scrollable area is big enough.
        d.heightForcer = elt("div", null, null, "position: absolute; height: " + scrollerGap + "px; width: 1px;");
        // Will contain the gutters, if any.
        d.gutters = elt("div", null, "CodeMirror-gutters");
        d.lineGutter = null;
        // Actual scrollable element.
        d.scroller = elt("div", [
            d.sizer,
            d.heightForcer,
            d.gutters
        ], "CodeMirror-scroll");
        d.scroller.setAttribute("tabIndex", "-1");
        // The element in which the editor lives.
        d.wrapper = elt("div", [
            d.scrollbarFiller,
            d.gutterFiller,
            d.scroller
        ], "CodeMirror");
        // This attribute is respected by automatic translation systems such as Google Translate,
        // and may also be respected by tools used by human translators.
        d.wrapper.setAttribute("translate", "no");
        // Work around IE7 z-index bug (not perfect, hence IE7 not really being supported)
        if (ie && ie_version < 8) {
            d.gutters.style.zIndex = -1;
            d.scroller.style.paddingRight = 0;
        }
        if (!webkit && !(gecko && mobile)) {
            d.scroller.draggable = true;
        }
        if (place) {
            if (place.appendChild) {
                place.appendChild(d.wrapper);
            } else {
                place(d.wrapper);
            }
        }
        // Current rendered range (may be bigger than the view window).
        d.viewFrom = d.viewTo = doc.first;
        d.reportedViewFrom = d.reportedViewTo = doc.first;
        // Information about the rendered lines.
        d.view = [];
        d.renderedView = null;
        // Holds info about a single rendered line when it was rendered
        // for measurement, while not in view.
        d.externalMeasured = null;
        // Empty space (in pixels) above the view
        d.viewOffset = 0;
        d.lastWrapHeight = d.lastWrapWidth = 0;
        d.updateLineNumbers = null;
        d.nativeBarWidth = d.barHeight = d.barWidth = 0;
        d.scrollbarsClipped = false;
        // Used to only resize the line number gutter when necessary (when
        // the amount of lines crosses a boundary that makes its width change)
        d.lineNumWidth = d.lineNumInnerWidth = d.lineNumChars = null;
        // Set to true when a non-horizontal-scrolling line widget is
        // added. As an optimization, line widget aligning is skipped when
        // this is false.
        d.alignWidgets = false;
        d.cachedCharWidth = d.cachedTextHeight = d.cachedPaddingH = null;
        // Tracks the maximum line length so that the horizontal scrollbar
        // can be kept static when scrolling.
        d.maxLine = null;
        d.maxLineLength = 0;
        d.maxLineChanged = false;
        // Used for measuring wheel scrolling granularity
        d.wheelDX = d.wheelDY = d.wheelStartX = d.wheelStartY = null;
        // True when shift is held down.
        d.shift = false;
        // Used to track whether anything happened since the context menu
        // was opened.
        d.selForContextMenu = null;
        d.activeTouch = null;
        d.gutterSpecs = getGutters(options.gutters, options.lineNumbers);
        renderGutters(d);
        input.init(d);
    };
    var wheelEventDelta = function wheelEventDelta(e) {
        var dx = e.wheelDeltaX, dy = e.wheelDeltaY;
        if (dx == null && e.detail && e.axis == e.HORIZONTAL_AXIS) {
            dx = e.detail;
        }
        if (dy == null && e.detail && e.axis == e.VERTICAL_AXIS) {
            dy = e.detail;
        } else if (dy == null) {
            dy = e.wheelDelta;
        }
        return {
            x: dx,
            y: dy
        };
    };
    var wheelEventPixels = function wheelEventPixels(e) {
        var delta = wheelEventDelta(e);
        delta.x *= wheelPixelsPerUnit;
        delta.y *= wheelPixelsPerUnit;
        return delta;
    };
    var onScrollWheel = function onScrollWheel(cm, e) {
        var delta = wheelEventDelta(e), dx = delta.x, dy = delta.y;
        var pixelsPerUnit = wheelPixelsPerUnit;
        if (e.deltaMode === 0) {
            dx = e.deltaX;
            dy = e.deltaY;
            pixelsPerUnit = 1;
        }
        var display = cm.display, scroll = display.scroller;
        // Quit if there's nothing to scroll here
        var canScrollX = scroll.scrollWidth > scroll.clientWidth;
        var canScrollY = scroll.scrollHeight > scroll.clientHeight;
        if (!(dx && canScrollX || dy && canScrollY)) {
            return;
        }
        // Webkit browsers on OS X abort momentum scrolls when the target
        // of the scroll event is removed from the scrollable element.
        // This hack (see related code in patchDisplay) makes sure the
        // element is kept around.
        if (dy && mac && webkit) {
            outer: for(var cur = e.target, view = display.view; cur != scroll; cur = cur.parentNode){
                for(var i = 0; i < view.length; i++){
                    if (view[i].node == cur) {
                        cm.display.currentWheelTarget = cur;
                        break outer;
                    }
                }
            }
        }
        // On some browsers, horizontal scrolling will cause redraws to
        // happen before the gutter has been realigned, causing it to
        // wriggle around in a most unseemly way. When we have an
        // estimated pixels/delta value, we just handle horizontal
        // scrolling entirely here. It'll be slightly off from native, but
        // better than glitching out.
        if (dx && !gecko && !presto && pixelsPerUnit != null) {
            if (dy && canScrollY) {
                updateScrollTop(cm, Math.max(0, scroll.scrollTop + dy * pixelsPerUnit));
            }
            setScrollLeft(cm, Math.max(0, scroll.scrollLeft + dx * pixelsPerUnit));
            // Only prevent default scrolling if vertical scrolling is
            // actually possible. Otherwise, it causes vertical scroll
            // jitter on OSX trackpads when deltaX is small and deltaY
            // is large (issue #3579)
            if (!dy || dy && canScrollY) {
                e_preventDefault(e);
            }
            display.wheelStartX = null; // Abort measurement, if in progress
            return;
        }
        // 'Project' the visible viewport to cover the area that is being
        // scrolled into view (if we know enough to estimate it).
        if (dy && pixelsPerUnit != null) {
            var pixels = dy * pixelsPerUnit;
            var top = cm.doc.scrollTop, bot = top + display.wrapper.clientHeight;
            if (pixels < 0) {
                top = Math.max(0, top + pixels - 50);
            } else {
                bot = Math.min(cm.doc.height, bot + pixels + 50);
            }
            updateDisplaySimple(cm, {
                top: top,
                bottom: bot
            });
        }
        if (wheelSamples < 20 && e.deltaMode !== 0) {
            if (display.wheelStartX == null) {
                display.wheelStartX = scroll.scrollLeft;
                display.wheelStartY = scroll.scrollTop;
                display.wheelDX = dx;
                display.wheelDY = dy;
                setTimeout(function() {
                    if (display.wheelStartX == null) {
                        return;
                    }
                    var movedX = scroll.scrollLeft - display.wheelStartX;
                    var movedY = scroll.scrollTop - display.wheelStartY;
                    var sample = movedY && display.wheelDY && movedY / display.wheelDY || movedX && display.wheelDX && movedX / display.wheelDX;
                    display.wheelStartX = display.wheelStartY = null;
                    if (!sample) {
                        return;
                    }
                    wheelPixelsPerUnit = (wheelPixelsPerUnit * wheelSamples + sample) / (wheelSamples + 1);
                    ++wheelSamples;
                }, 200);
            } else {
                display.wheelDX += dx;
                display.wheelDY += dy;
            }
        }
    };
    var normalizeSelection = // Take an unsorted, potentially overlapping set of ranges, and
    // build a selection out of it. 'Consumes' ranges array (modifying
    // it).
    function normalizeSelection(cm, ranges, primIndex) {
        var mayTouch = cm && cm.options.selectionsMayTouch;
        var prim = ranges[primIndex];
        ranges.sort(function(a, b) {
            return cmp(a.from(), b.from());
        });
        primIndex = indexOf(ranges, prim);
        for(var i = 1; i < ranges.length; i++){
            var cur = ranges[i], prev = ranges[i - 1];
            var diff = cmp(prev.to(), cur.from());
            if (mayTouch && !cur.empty() ? diff > 0 : diff >= 0) {
                var from = minPos(prev.from(), cur.from()), to = maxPos(prev.to(), cur.to());
                var inv = prev.empty() ? cur.from() == cur.head : prev.from() == prev.head;
                if (i <= primIndex) {
                    --primIndex;
                }
                ranges.splice(--i, 2, new Range(inv ? to : from, inv ? from : to));
            }
        }
        return new Selection(ranges, primIndex);
    };
    var simpleSelection = function simpleSelection(anchor, head) {
        return new Selection([
            new Range(anchor, head || anchor)
        ], 0);
    };
    var changeEnd = // Compute the position of the end of a change (its 'to' property
    // refers to the pre-change end).
    function changeEnd(change) {
        if (!change.text) {
            return change.to;
        }
        return Pos(change.from.line + change.text.length - 1, lst(change.text).length + (change.text.length == 1 ? change.from.ch : 0));
    };
    var adjustForChange = // Adjust a position to refer to the post-change position of the
    // same text, or the end of the change if the change covers it.
    function adjustForChange(pos, change) {
        if (cmp(pos, change.from) < 0) {
            return pos;
        }
        if (cmp(pos, change.to) <= 0) {
            return changeEnd(change);
        }
        var line = pos.line + change.text.length - (change.to.line - change.from.line) - 1, ch = pos.ch;
        if (pos.line == change.to.line) {
            ch += changeEnd(change).ch - change.to.ch;
        }
        return Pos(line, ch);
    };
    var computeSelAfterChange = function computeSelAfterChange(doc, change) {
        var out = [];
        for(var i = 0; i < doc.sel.ranges.length; i++){
            var range = doc.sel.ranges[i];
            out.push(new Range(adjustForChange(range.anchor, change), adjustForChange(range.head, change)));
        }
        return normalizeSelection(doc.cm, out, doc.sel.primIndex);
    };
    var offsetPos = function offsetPos(pos, old, nw) {
        if (pos.line == old.line) {
            return Pos(nw.line, pos.ch - old.ch + nw.ch);
        } else {
            return Pos(nw.line + (pos.line - old.line), pos.ch);
        }
    };
    var computeReplacedSel = // Used by replaceSelections to allow moving the selection to the
    // start or around the replaced test. Hint may be "start" or "around".
    function computeReplacedSel(doc, changes, hint) {
        var out = [];
        var oldPrev = Pos(doc.first, 0), newPrev = oldPrev;
        for(var i = 0; i < changes.length; i++){
            var change = changes[i];
            var from = offsetPos(change.from, oldPrev, newPrev);
            var to = offsetPos(changeEnd(change), oldPrev, newPrev);
            oldPrev = change.to;
            newPrev = to;
            if (hint == "around") {
                var range = doc.sel.ranges[i], inv = cmp(range.head, range.anchor) < 0;
                out[i] = new Range(inv ? to : from, inv ? from : to);
            } else {
                out[i] = new Range(from, from);
            }
        }
        return new Selection(out, doc.sel.primIndex);
    };
    var loadMode = // Used to get the editor into a consistent state again when options change.
    function loadMode(cm) {
        cm.doc.mode = getMode(cm.options, cm.doc.modeOption);
        resetModeState(cm);
    };
    var resetModeState = function resetModeState(cm) {
        cm.doc.iter(function(line) {
            if (line.stateAfter) {
                line.stateAfter = null;
            }
            if (line.styles) {
                line.styles = null;
            }
        });
        cm.doc.modeFrontier = cm.doc.highlightFrontier = cm.doc.first;
        startWorker(cm, 100);
        cm.state.modeGen++;
        if (cm.curOp) {
            regChange(cm);
        }
    };
    var isWholeLineUpdate = // DOCUMENT DATA STRUCTURE
    // By default, updates that start and end at the beginning of a line
    // are treated specially, in order to make the association of line
    // widgets and marker elements with the text behave more intuitive.
    function isWholeLineUpdate(doc, change) {
        return change.from.ch == 0 && change.to.ch == 0 && lst(change.text) == "" && (!doc.cm || doc.cm.options.wholeLineUpdateBefore);
    };
    var updateDoc = // Perform a change on the document data structure.
    function updateDoc(doc, change, markedSpans, estimateHeight) {
        function spansFor(n) {
            return markedSpans ? markedSpans[n] : null;
        }
        function update(line, text, spans) {
            updateLine(line, text, spans, estimateHeight);
            signalLater(line, "change", line, change);
        }
        function linesFor(start, end) {
            var result = [];
            for(var i = start; i < end; ++i){
                result.push(new Line(text[i], spansFor(i), estimateHeight));
            }
            return result;
        }
        var from = change.from, to = change.to, text = change.text;
        var firstLine = getLine(doc, from.line), lastLine = getLine(doc, to.line);
        var lastText = lst(text), lastSpans = spansFor(text.length - 1), nlines = to.line - from.line;
        // Adjust the line structure
        if (change.full) {
            doc.insert(0, linesFor(0, text.length));
            doc.remove(text.length, doc.size - text.length);
        } else if (isWholeLineUpdate(doc, change)) {
            // This is a whole-line replace. Treated specially to make
            // sure line objects move the way they are supposed to.
            var added = linesFor(0, text.length - 1);
            update(lastLine, lastLine.text, lastSpans);
            if (nlines) {
                doc.remove(from.line, nlines);
            }
            if (added.length) {
                doc.insert(from.line, added);
            }
        } else if (firstLine == lastLine) {
            if (text.length == 1) {
                update(firstLine, firstLine.text.slice(0, from.ch) + lastText + firstLine.text.slice(to.ch), lastSpans);
            } else {
                var added$1 = linesFor(1, text.length - 1);
                added$1.push(new Line(lastText + firstLine.text.slice(to.ch), lastSpans, estimateHeight));
                update(firstLine, firstLine.text.slice(0, from.ch) + text[0], spansFor(0));
                doc.insert(from.line + 1, added$1);
            }
        } else if (text.length == 1) {
            update(firstLine, firstLine.text.slice(0, from.ch) + text[0] + lastLine.text.slice(to.ch), spansFor(0));
            doc.remove(from.line + 1, nlines);
        } else {
            update(firstLine, firstLine.text.slice(0, from.ch) + text[0], spansFor(0));
            update(lastLine, lastText + lastLine.text.slice(to.ch), lastSpans);
            var added$2 = linesFor(1, text.length - 1);
            if (nlines > 1) {
                doc.remove(from.line + 1, nlines - 1);
            }
            doc.insert(from.line + 1, added$2);
        }
        signalLater(doc, "change", doc, change);
    };
    var linkedDocs = // Call f for all linked documents.
    function linkedDocs(doc, f, sharedHistOnly) {
        function propagate(doc, skip, sharedHist) {
            if (doc.linked) {
                for(var i = 0; i < doc.linked.length; ++i){
                    var rel = doc.linked[i];
                    if (rel.doc == skip) {
                        continue;
                    }
                    var shared = sharedHist && rel.sharedHist;
                    if (sharedHistOnly && !shared) {
                        continue;
                    }
                    f(rel.doc, shared);
                    propagate(rel.doc, doc, shared);
                }
            }
        }
        propagate(doc, null, true);
    };
    var attachDoc = // Attach a document to an editor.
    function attachDoc(cm, doc) {
        if (doc.cm) {
            throw new Error("This document is already in use.");
        }
        cm.doc = doc;
        doc.cm = cm;
        estimateLineHeights(cm);
        loadMode(cm);
        setDirectionClass(cm);
        cm.options.direction = doc.direction;
        if (!cm.options.lineWrapping) {
            findMaxLine(cm);
        }
        cm.options.mode = doc.modeOption;
        regChange(cm);
    };
    var setDirectionClass = function setDirectionClass(cm) {
        (cm.doc.direction == "rtl" ? addClass : rmClass)(cm.display.lineDiv, "CodeMirror-rtl");
    };
    var directionChanged = function directionChanged(cm) {
        runInOp(cm, function() {
            setDirectionClass(cm);
            regChange(cm);
        });
    };
    var History = function History(prev) {
        // Arrays of change events and selections. Doing something adds an
        // event to done and clears undo. Undoing moves events from done
        // to undone, redoing moves them in the other direction.
        this.done = [];
        this.undone = [];
        this.undoDepth = prev ? prev.undoDepth : Infinity;
        // Used to track when changes can be merged into a single undo
        // event
        this.lastModTime = this.lastSelTime = 0;
        this.lastOp = this.lastSelOp = null;
        this.lastOrigin = this.lastSelOrigin = null;
        // Used by the isClean() method
        this.generation = this.maxGeneration = prev ? prev.maxGeneration : 1;
    };
    var historyChangeFromChange = // Create a history change event from an updateDoc-style change
    // object.
    function historyChangeFromChange(doc, change) {
        var histChange = {
            from: copyPos(change.from),
            to: changeEnd(change),
            text: getBetween(doc, change.from, change.to)
        };
        attachLocalSpans(doc, histChange, change.from.line, change.to.line + 1);
        linkedDocs(doc, function(doc) {
            return attachLocalSpans(doc, histChange, change.from.line, change.to.line + 1);
        }, true);
        return histChange;
    };
    var clearSelectionEvents = // Pop all selection events off the end of a history array. Stop at
    // a change event.
    function clearSelectionEvents(array) {
        while(array.length){
            var last = lst(array);
            if (last.ranges) {
                array.pop();
            } else {
                break;
            }
        }
    };
    var lastChangeEvent = // Find the top change event in the history. Pop off selection
    // events that are in the way.
    function lastChangeEvent(hist, force) {
        if (force) {
            clearSelectionEvents(hist.done);
            return lst(hist.done);
        } else if (hist.done.length && !lst(hist.done).ranges) {
            return lst(hist.done);
        } else if (hist.done.length > 1 && !hist.done[hist.done.length - 2].ranges) {
            hist.done.pop();
            return lst(hist.done);
        }
    };
    var addChangeToHistory = // Register a change in the history. Merges changes that are within
    // a single operation, or are close together with an origin that
    // allows merging (starting with "+") into a single event.
    function addChangeToHistory(doc, change, selAfter, opId) {
        var hist = doc.history;
        hist.undone.length = 0;
        var time = +new Date, cur;
        var last;
        if ((hist.lastOp == opId || hist.lastOrigin == change.origin && change.origin && (change.origin.charAt(0) == "+" && hist.lastModTime > time - (doc.cm ? doc.cm.options.historyEventDelay : 500) || change.origin.charAt(0) == "*")) && (cur = lastChangeEvent(hist, hist.lastOp == opId))) {
            // Merge this change into the last event
            last = lst(cur.changes);
            if (cmp(change.from, change.to) == 0 && cmp(change.from, last.to) == 0) {
                // Optimized case for simple insertion -- don't want to add
                // new changesets for every character typed
                last.to = changeEnd(change);
            } else {
                // Add new sub-event
                cur.changes.push(historyChangeFromChange(doc, change));
            }
        } else {
            // Can not be merged, start a new event.
            var before = lst(hist.done);
            if (!before || !before.ranges) {
                pushSelectionToHistory(doc.sel, hist.done);
            }
            cur = {
                changes: [
                    historyChangeFromChange(doc, change)
                ],
                generation: hist.generation
            };
            hist.done.push(cur);
            while(hist.done.length > hist.undoDepth){
                hist.done.shift();
                if (!hist.done[0].ranges) {
                    hist.done.shift();
                }
            }
        }
        hist.done.push(selAfter);
        hist.generation = ++hist.maxGeneration;
        hist.lastModTime = hist.lastSelTime = time;
        hist.lastOp = hist.lastSelOp = opId;
        hist.lastOrigin = hist.lastSelOrigin = change.origin;
        if (!last) {
            signal(doc, "historyAdded");
        }
    };
    var selectionEventCanBeMerged = function selectionEventCanBeMerged(doc, origin, prev, sel) {
        var ch = origin.charAt(0);
        return ch == "*" || ch == "+" && prev.ranges.length == sel.ranges.length && prev.somethingSelected() == sel.somethingSelected() && new Date - doc.history.lastSelTime <= (doc.cm ? doc.cm.options.historyEventDelay : 500);
    };
    var addSelectionToHistory = // Called whenever the selection changes, sets the new selection as
    // the pending selection in the history, and pushes the old pending
    // selection into the 'done' array when it was significantly
    // different (in number of selected ranges, emptiness, or time).
    function addSelectionToHistory(doc, sel, opId, options) {
        var hist = doc.history, origin = options && options.origin;
        // A new event is started when the previous origin does not match
        // the current, or the origins don't allow matching. Origins
        // starting with * are always merged, those starting with + are
        // merged when similar and close together in time.
        if (opId == hist.lastSelOp || origin && hist.lastSelOrigin == origin && (hist.lastModTime == hist.lastSelTime && hist.lastOrigin == origin || selectionEventCanBeMerged(doc, origin, lst(hist.done), sel))) {
            hist.done[hist.done.length - 1] = sel;
        } else {
            pushSelectionToHistory(sel, hist.done);
        }
        hist.lastSelTime = +new Date;
        hist.lastSelOrigin = origin;
        hist.lastSelOp = opId;
        if (options && options.clearRedo !== false) {
            clearSelectionEvents(hist.undone);
        }
    };
    var pushSelectionToHistory = function pushSelectionToHistory(sel, dest) {
        var top = lst(dest);
        if (!(top && top.ranges && top.equals(sel))) {
            dest.push(sel);
        }
    };
    var attachLocalSpans = // Used to store marked span information in the history.
    function attachLocalSpans(doc, change, from, to) {
        var existing = change["spans_" + doc.id], n = 0;
        doc.iter(Math.max(doc.first, from), Math.min(doc.first + doc.size, to), function(line) {
            if (line.markedSpans) {
                (existing || (existing = change["spans_" + doc.id] = {}))[n] = line.markedSpans;
            }
            ++n;
        });
    };
    var removeClearedSpans = // When un/re-doing restores text containing marked spans, those
    // that have been explicitly cleared should not be restored.
    function removeClearedSpans(spans) {
        if (!spans) {
            return null;
        }
        var out;
        for(var i = 0; i < spans.length; ++i){
            if (spans[i].marker.explicitlyCleared) {
                if (!out) {
                    out = spans.slice(0, i);
                }
            } else if (out) {
                out.push(spans[i]);
            }
        }
        return !out ? spans : out.length ? out : null;
    };
    var getOldSpans = // Retrieve and filter the old marked spans stored in a change event.
    function getOldSpans(doc, change) {
        var found = change["spans_" + doc.id];
        if (!found) {
            return null;
        }
        var nw = [];
        for(var i = 0; i < change.text.length; ++i){
            nw.push(removeClearedSpans(found[i]));
        }
        return nw;
    };
    var mergeOldSpans = // Used for un/re-doing changes from the history. Combines the
    // result of computing the existing spans with the set of spans that
    // existed in the history (so that deleting around a span and then
    // undoing brings back the span).
    function mergeOldSpans(doc, change) {
        var old = getOldSpans(doc, change);
        var stretched = stretchSpansOverChange(doc, change);
        if (!old) {
            return stretched;
        }
        if (!stretched) {
            return old;
        }
        for(var i = 0; i < old.length; ++i){
            var oldCur = old[i], stretchCur = stretched[i];
            if (oldCur && stretchCur) {
                spans: for(var j = 0; j < stretchCur.length; ++j){
                    var span = stretchCur[j];
                    for(var k = 0; k < oldCur.length; ++k){
                        if (oldCur[k].marker == span.marker) {
                            continue spans;
                        }
                    }
                    oldCur.push(span);
                }
            } else if (stretchCur) {
                old[i] = stretchCur;
            }
        }
        return old;
    };
    var copyHistoryArray = // Used both to provide a JSON-safe object in .getHistory, and, when
    // detaching a document, to split the history in two
    function copyHistoryArray(events, newGroup, instantiateSel) {
        var copy = [];
        for(var i = 0; i < events.length; ++i){
            var event = events[i];
            if (event.ranges) {
                copy.push(instantiateSel ? Selection.prototype.deepCopy.call(event) : event);
                continue;
            }
            var changes = event.changes, newChanges = [];
            copy.push({
                changes: newChanges
            });
            for(var j = 0; j < changes.length; ++j){
                var change = changes[j], m = void 0;
                newChanges.push({
                    from: change.from,
                    to: change.to,
                    text: change.text
                });
                if (newGroup) {
                    for(var prop in change){
                        if (m = prop.match(/^spans_(\d+)$/)) {
                            if (indexOf(newGroup, Number(m[1])) > -1) {
                                lst(newChanges)[prop] = change[prop];
                                delete change[prop];
                            }
                        }
                    }
                }
            }
        }
        return copy;
    };
    var extendRange = // The 'scroll' parameter given to many of these indicated whether
    // the new cursor position should be scrolled into view after
    // modifying the selection.
    // If shift is held or the extend flag is set, extends a range to
    // include a given position (and optionally a second position).
    // Otherwise, simply returns the range between the given positions.
    // Used for cursor motion and such.
    function extendRange(range, head, other, extend) {
        if (extend) {
            var anchor = range.anchor;
            if (other) {
                var posBefore = cmp(head, anchor) < 0;
                if (posBefore != cmp(other, anchor) < 0) {
                    anchor = head;
                    head = other;
                } else if (posBefore != cmp(head, other) < 0) {
                    head = other;
                }
            }
            return new Range(anchor, head);
        } else {
            return new Range(other || head, head);
        }
    };
    var extendSelection = // Extend the primary selection range, discard the rest.
    function extendSelection(doc, head, other, options, extend) {
        if (extend == null) {
            extend = doc.cm && (doc.cm.display.shift || doc.extend);
        }
        setSelection(doc, new Selection([
            extendRange(doc.sel.primary(), head, other, extend)
        ], 0), options);
    };
    var extendSelections = // Extend all selections (pos is an array of selections with length
    // equal the number of selections)
    function extendSelections(doc, heads, options) {
        var out = [];
        var extend = doc.cm && (doc.cm.display.shift || doc.extend);
        for(var i = 0; i < doc.sel.ranges.length; i++){
            out[i] = extendRange(doc.sel.ranges[i], heads[i], null, extend);
        }
        var newSel = normalizeSelection(doc.cm, out, doc.sel.primIndex);
        setSelection(doc, newSel, options);
    };
    var replaceOneSelection = // Updates a single range in the selection.
    function replaceOneSelection(doc, i, range, options) {
        var ranges = doc.sel.ranges.slice(0);
        ranges[i] = range;
        setSelection(doc, normalizeSelection(doc.cm, ranges, doc.sel.primIndex), options);
    };
    var setSimpleSelection = // Reset the selection to a single range.
    function setSimpleSelection(doc, anchor, head, options) {
        setSelection(doc, simpleSelection(anchor, head), options);
    };
    var filterSelectionChange = // Give beforeSelectionChange handlers a change to influence a
    // selection update.
    function filterSelectionChange(doc, sel, options) {
        var obj = {
            ranges: sel.ranges,
            update: function update(ranges) {
                this.ranges = [];
                for(var i = 0; i < ranges.length; i++){
                    this.ranges[i] = new Range(clipPos(doc, ranges[i].anchor), clipPos(doc, ranges[i].head));
                }
            },
            origin: options && options.origin
        };
        signal(doc, "beforeSelectionChange", doc, obj);
        if (doc.cm) {
            signal(doc.cm, "beforeSelectionChange", doc.cm, obj);
        }
        if (obj.ranges != sel.ranges) {
            return normalizeSelection(doc.cm, obj.ranges, obj.ranges.length - 1);
        } else {
            return sel;
        }
    };
    var setSelectionReplaceHistory = function setSelectionReplaceHistory(doc, sel, options) {
        var done = doc.history.done, last = lst(done);
        if (last && last.ranges) {
            done[done.length - 1] = sel;
            setSelectionNoUndo(doc, sel, options);
        } else {
            setSelection(doc, sel, options);
        }
    };
    var setSelection = // Set a new selection.
    function setSelection(doc, sel, options) {
        setSelectionNoUndo(doc, sel, options);
        addSelectionToHistory(doc, doc.sel, doc.cm ? doc.cm.curOp.id : NaN, options);
    };
    var setSelectionNoUndo = function setSelectionNoUndo(doc, sel, options) {
        if (hasHandler(doc, "beforeSelectionChange") || doc.cm && hasHandler(doc.cm, "beforeSelectionChange")) {
            sel = filterSelectionChange(doc, sel, options);
        }
        var bias = options && options.bias || (cmp(sel.primary().head, doc.sel.primary().head) < 0 ? -1 : 1);
        setSelectionInner(doc, skipAtomicInSelection(doc, sel, bias, true));
        if (!(options && options.scroll === false) && doc.cm && doc.cm.getOption("readOnly") != "nocursor") {
            ensureCursorVisible(doc.cm);
        }
    };
    var setSelectionInner = function setSelectionInner(doc, sel) {
        if (sel.equals(doc.sel)) {
            return;
        }
        doc.sel = sel;
        if (doc.cm) {
            doc.cm.curOp.updateInput = 1;
            doc.cm.curOp.selectionChanged = true;
            signalCursorActivity(doc.cm);
        }
        signalLater(doc, "cursorActivity", doc);
    };
    var reCheckSelection = // Verify that the selection does not partially select any atomic
    // marked ranges.
    function reCheckSelection(doc) {
        setSelectionInner(doc, skipAtomicInSelection(doc, doc.sel, null, false));
    };
    var skipAtomicInSelection = // Return a selection that does not partially select any atomic
    // ranges.
    function skipAtomicInSelection(doc, sel, bias, mayClear) {
        var out;
        for(var i = 0; i < sel.ranges.length; i++){
            var range = sel.ranges[i];
            var old = sel.ranges.length == doc.sel.ranges.length && doc.sel.ranges[i];
            var newAnchor = skipAtomic(doc, range.anchor, old && old.anchor, bias, mayClear);
            var newHead = skipAtomic(doc, range.head, old && old.head, bias, mayClear);
            if (out || newAnchor != range.anchor || newHead != range.head) {
                if (!out) {
                    out = sel.ranges.slice(0, i);
                }
                out[i] = new Range(newAnchor, newHead);
            }
        }
        return out ? normalizeSelection(doc.cm, out, sel.primIndex) : sel;
    };
    var skipAtomic = // Ensure a given position is not inside an atomic range.
    function skipAtomic(doc, pos, oldPos, bias, mayClear) {
        var dir = bias || 1;
        var found = skipAtomicInner(doc, pos, oldPos, dir, mayClear) || !mayClear && skipAtomicInner(doc, pos, oldPos, dir, true) || skipAtomicInner(doc, pos, oldPos, -dir, mayClear) || !mayClear && skipAtomicInner(doc, pos, oldPos, -dir, true);
        if (!found) {
            doc.cantEdit = true;
            return Pos(doc.first, 0);
        }
        return found;
    };
    var movePos = function movePos(doc, pos, dir, line) {
        if (dir < 0 && pos.ch == 0) {
            if (pos.line > doc.first) {
                return clipPos(doc, Pos(pos.line - 1));
            } else {
                return null;
            }
        } else if (dir > 0 && pos.ch == (line || getLine(doc, pos.line)).text.length) {
            if (pos.line < doc.first + doc.size - 1) {
                return Pos(pos.line + 1, 0);
            } else {
                return null;
            }
        } else {
            return new Pos(pos.line, pos.ch + dir);
        }
    };
    var selectAll = function selectAll(cm) {
        cm.setSelection(Pos(cm.firstLine(), 0), Pos(cm.lastLine()), sel_dontScroll);
    };
    var filterChange = // UPDATING
    // Allow "beforeChange" event handlers to influence a change
    function filterChange(doc, change, update) {
        var obj = {
            canceled: false,
            from: change.from,
            to: change.to,
            text: change.text,
            origin: change.origin,
            cancel: function cancel() {
                return obj.canceled = true;
            }
        };
        if (update) {
            obj.update = function(from, to, text, origin) {
                if (from) {
                    obj.from = clipPos(doc, from);
                }
                if (to) {
                    obj.to = clipPos(doc, to);
                }
                if (text) {
                    obj.text = text;
                }
                if (origin !== undefined) {
                    obj.origin = origin;
                }
            };
        }
        signal(doc, "beforeChange", doc, obj);
        if (doc.cm) {
            signal(doc.cm, "beforeChange", doc.cm, obj);
        }
        if (obj.canceled) {
            if (doc.cm) {
                doc.cm.curOp.updateInput = 2;
            }
            return null;
        }
        return {
            from: obj.from,
            to: obj.to,
            text: obj.text,
            origin: obj.origin
        };
    };
    var makeChangeInner = function makeChangeInner(doc, change) {
        if (change.text.length == 1 && change.text[0] == "" && cmp(change.from, change.to) == 0) {
            return;
        }
        var selAfter = computeSelAfterChange(doc, change);
        addChangeToHistory(doc, change, selAfter, doc.cm ? doc.cm.curOp.id : NaN);
        makeChangeSingleDoc(doc, change, selAfter, stretchSpansOverChange(doc, change));
        var rebased = [];
        linkedDocs(doc, function(doc, sharedHist) {
            if (!sharedHist && indexOf(rebased, doc.history) == -1) {
                rebaseHist(doc.history, change);
                rebased.push(doc.history);
            }
            makeChangeSingleDoc(doc, change, null, stretchSpansOverChange(doc, change));
        });
    };
    var makeChangeFromHistory = // Revert a change stored in a document's history.
    function makeChangeFromHistory(doc, type, allowSelectionOnly) {
        var suppress = doc.cm && doc.cm.state.suppressEdits;
        if (suppress && !allowSelectionOnly) {
            return;
        }
        var hist = doc.history, event, selAfter = doc.sel;
        var source = type == "undo" ? hist.done : hist.undone, dest = type == "undo" ? hist.undone : hist.done;
        // Verify that there is a useable event (so that ctrl-z won't
        // needlessly clear selection events)
        var i = 0;
        for(; i < source.length; i++){
            event = source[i];
            if (allowSelectionOnly ? event.ranges && !event.equals(doc.sel) : !event.ranges) {
                break;
            }
        }
        if (i == source.length) {
            return;
        }
        hist.lastOrigin = hist.lastSelOrigin = null;
        for(;;){
            event = source.pop();
            if (event.ranges) {
                pushSelectionToHistory(event, dest);
                if (allowSelectionOnly && !event.equals(doc.sel)) {
                    setSelection(doc, event, {
                        clearRedo: false
                    });
                    return;
                }
                selAfter = event;
            } else if (suppress) {
                source.push(event);
                return;
            } else {
                break;
            }
        }
        // Build up a reverse change object to add to the opposite history
        // stack (redo when undoing, and vice versa).
        var antiChanges = [];
        pushSelectionToHistory(selAfter, dest);
        dest.push({
            changes: antiChanges,
            generation: hist.generation
        });
        hist.generation = event.generation || ++hist.maxGeneration;
        var filter = hasHandler(doc, "beforeChange") || doc.cm && hasHandler(doc.cm, "beforeChange");
        var loop = function loop(i) {
            var change = event.changes[i];
            change.origin = type;
            if (filter && !filterChange(doc, change, false)) {
                source.length = 0;
                return {};
            }
            antiChanges.push(historyChangeFromChange(doc, change));
            var after = i ? computeSelAfterChange(doc, change) : lst(source);
            makeChangeSingleDoc(doc, change, after, mergeOldSpans(doc, change));
            if (!i && doc.cm) {
                doc.cm.scrollIntoView({
                    from: change.from,
                    to: changeEnd(change)
                });
            }
            var rebased = [];
            // Propagate to the linked documents
            linkedDocs(doc, function(doc, sharedHist) {
                if (!sharedHist && indexOf(rebased, doc.history) == -1) {
                    rebaseHist(doc.history, change);
                    rebased.push(doc.history);
                }
                makeChangeSingleDoc(doc, change, null, mergeOldSpans(doc, change));
            });
        };
        for(var i$1 = event.changes.length - 1; i$1 >= 0; --i$1){
            var returned = loop(i$1);
            if (returned) return returned.v;
        }
    };
    var shiftDoc = // Sub-views need their line numbers shifted when text is added
    // above or below them in the parent document.
    function shiftDoc(doc, distance) {
        if (distance == 0) {
            return;
        }
        doc.first += distance;
        doc.sel = new Selection(map(doc.sel.ranges, function(range) {
            return new Range(Pos(range.anchor.line + distance, range.anchor.ch), Pos(range.head.line + distance, range.head.ch));
        }), doc.sel.primIndex);
        if (doc.cm) {
            regChange(doc.cm, doc.first, doc.first - distance, distance);
            for(var d = doc.cm.display, l = d.viewFrom; l < d.viewTo; l++){
                regLineChange(doc.cm, l, "gutter");
            }
        }
    };
    var makeChangeSingleDocInEditor = // Handle the interaction of a change to a document with the editor
    // that this document is part of.
    function makeChangeSingleDocInEditor(cm, change, spans) {
        var doc = cm.doc, display = cm.display, from = change.from, to = change.to;
        var recomputeMaxLength = false, checkWidthStart = from.line;
        if (!cm.options.lineWrapping) {
            checkWidthStart = lineNo(visualLine(getLine(doc, from.line)));
            doc.iter(checkWidthStart, to.line + 1, function(line) {
                if (line == display.maxLine) {
                    recomputeMaxLength = true;
                    return true;
                }
            });
        }
        if (doc.sel.contains(change.from, change.to) > -1) {
            signalCursorActivity(cm);
        }
        updateDoc(doc, change, spans, estimateHeight(cm));
        if (!cm.options.lineWrapping) {
            doc.iter(checkWidthStart, from.line + change.text.length, function(line) {
                var len = lineLength(line);
                if (len > display.maxLineLength) {
                    display.maxLine = line;
                    display.maxLineLength = len;
                    display.maxLineChanged = true;
                    recomputeMaxLength = false;
                }
            });
            if (recomputeMaxLength) {
                cm.curOp.updateMaxLine = true;
            }
        }
        retreatFrontier(doc, from.line);
        startWorker(cm, 400);
        var lendiff = change.text.length - (to.line - from.line) - 1;
        // Remember that these lines changed, for updating the display
        if (change.full) {
            regChange(cm);
        } else if (from.line == to.line && change.text.length == 1 && !isWholeLineUpdate(cm.doc, change)) {
            regLineChange(cm, from.line, "text");
        } else {
            regChange(cm, from.line, to.line + 1, lendiff);
        }
        var changesHandler = hasHandler(cm, "changes"), changeHandler = hasHandler(cm, "change");
        if (changeHandler || changesHandler) {
            var obj = {
                from: from,
                to: to,
                text: change.text,
                removed: change.removed,
                origin: change.origin
            };
            if (changeHandler) {
                signalLater(cm, "change", cm, obj);
            }
            if (changesHandler) {
                (cm.curOp.changeObjs || (cm.curOp.changeObjs = [])).push(obj);
            }
        }
        cm.display.selForContextMenu = null;
    };
    var replaceRange = function replaceRange(doc, code, from, to, origin) {
        var assign;
        if (!to) {
            to = from;
        }
        if (cmp(to, from) < 0) {
            assign = [
                to,
                from
            ], from = assign[0], to = assign[1];
        }
        if (typeof code == "string") {
            code = doc.splitLines(code);
        }
        makeChange(doc, {
            from: from,
            to: to,
            text: code,
            origin: origin
        });
    };
    var rebaseHistSelSingle = // Rebasing/resetting history to deal with externally-sourced changes
    function rebaseHistSelSingle(pos, from, to, diff) {
        if (to < pos.line) {
            pos.line += diff;
        } else if (from < pos.line) {
            pos.line = from;
            pos.ch = 0;
        }
    };
    var rebaseHistArray = // Tries to rebase an array of history events given a change in the
    // document. If the change touches the same lines as the event, the
    // event, and everything 'behind' it, is discarded. If the change is
    // before the event, the event's positions are updated. Uses a
    // copy-on-write scheme for the positions, to avoid having to
    // reallocate them all on every rebase, but also avoid problems with
    // shared position objects being unsafely updated.
    function rebaseHistArray(array, from, to, diff) {
        for(var i = 0; i < array.length; ++i){
            var sub = array[i], ok = true;
            if (sub.ranges) {
                if (!sub.copied) {
                    sub = array[i] = sub.deepCopy();
                    sub.copied = true;
                }
                for(var j = 0; j < sub.ranges.length; j++){
                    rebaseHistSelSingle(sub.ranges[j].anchor, from, to, diff);
                    rebaseHistSelSingle(sub.ranges[j].head, from, to, diff);
                }
                continue;
            }
            for(var j$1 = 0; j$1 < sub.changes.length; ++j$1){
                var cur = sub.changes[j$1];
                if (to < cur.from.line) {
                    cur.from = Pos(cur.from.line + diff, cur.from.ch);
                    cur.to = Pos(cur.to.line + diff, cur.to.ch);
                } else if (from <= cur.to.line) {
                    ok = false;
                    break;
                }
            }
            if (!ok) {
                array.splice(0, i + 1);
                i = 0;
            }
        }
    };
    var rebaseHist = function rebaseHist(hist, change) {
        var from = change.from.line, to = change.to.line, diff = change.text.length - (to - from) - 1;
        rebaseHistArray(hist.done, from, to, diff);
        rebaseHistArray(hist.undone, from, to, diff);
    };
    var changeLine = // Utility for applying a change to a line by handle or number,
    // returning the number and optionally registering the line as
    // changed.
    function changeLine(doc, handle, changeType, op) {
        var no = handle, line = handle;
        if (typeof handle == "number") {
            line = getLine(doc, clipLine(doc, handle));
        } else {
            no = lineNo(handle);
        }
        if (no == null) {
            return null;
        }
        if (op(line, no) && doc.cm) {
            regLineChange(doc.cm, no, changeType);
        }
        return line;
    };
    var LeafChunk = // The document is represented as a BTree consisting of leaves, with
    // chunk of lines in them, and branches, with up to ten leaves or
    // other branch nodes below them. The top node is always a branch
    // node, and is the document object itself (meaning it has
    // additional methods and properties).
    //
    // All nodes have parent links. The tree is used both to go from
    // line numbers to line objects, and to go from objects to numbers.
    // It also indexes by height, and is used to convert between height
    // and line object, and to find the total height of the document.
    //
    // See also http://marijnhaverbeke.nl/blog/codemirror-line-tree.html
    function LeafChunk(lines) {
        this.lines = lines;
        this.parent = null;
        var height = 0;
        for(var i = 0; i < lines.length; ++i){
            lines[i].parent = this;
            height += lines[i].height;
        }
        this.height = height;
    };
    var BranchChunk = function BranchChunk(children) {
        this.children = children;
        var size = 0, height = 0;
        for(var i = 0; i < children.length; ++i){
            var ch = children[i];
            size += ch.chunkSize();
            height += ch.height;
            ch.parent = this;
        }
        this.size = size;
        this.height = height;
        this.parent = null;
    };
    var adjustScrollWhenAboveVisible = function adjustScrollWhenAboveVisible(cm, line, diff) {
        if (heightAtLine(line) < (cm.curOp && cm.curOp.scrollTop || cm.doc.scrollTop)) {
            addToScrollTop(cm, diff);
        }
    };
    var addLineWidget = function addLineWidget(doc, handle, node, options) {
        var widget = new LineWidget(doc, node, options);
        var cm = doc.cm;
        if (cm && widget.noHScroll) {
            cm.display.alignWidgets = true;
        }
        changeLine(doc, handle, "widget", function(line) {
            var widgets = line.widgets || (line.widgets = []);
            if (widget.insertAt == null) {
                widgets.push(widget);
            } else {
                widgets.splice(Math.min(widgets.length, Math.max(0, widget.insertAt)), 0, widget);
            }
            widget.line = line;
            if (cm && !lineIsHidden(doc, line)) {
                var aboveVisible = heightAtLine(line) < doc.scrollTop;
                updateLineHeight(line, line.height + widgetHeight(widget));
                if (aboveVisible) {
                    addToScrollTop(cm, widget.height);
                }
                cm.curOp.forceUpdate = true;
            }
            return true;
        });
        if (cm) {
            signalLater(cm, "lineWidgetAdded", cm, widget, typeof handle == "number" ? handle : lineNo(handle));
        }
        return widget;
    };
    var markTextShared = function markTextShared(doc, from, to, options, type) {
        options = copyObj(options);
        options.shared = false;
        var markers = [
            markText(doc, from, to, options, type)
        ], primary = markers[0];
        var widget = options.widgetNode;
        linkedDocs(doc, function(doc) {
            if (widget) {
                options.widgetNode = widget.cloneNode(true);
            }
            markers.push(markText(doc, clipPos(doc, from), clipPos(doc, to), options, type));
            for(var i = 0; i < doc.linked.length; ++i){
                if (doc.linked[i].isParent) {
                    return;
                }
            }
            primary = lst(markers);
        });
        return new SharedTextMarker(markers, primary);
    };
    var findSharedMarkers = function findSharedMarkers(doc) {
        return doc.findMarks(Pos(doc.first, 0), doc.clipPos(Pos(doc.lastLine())), function(m) {
            return m.parent;
        });
    };
    var copySharedMarkers = function copySharedMarkers(doc, markers) {
        for(var i = 0; i < markers.length; i++){
            var marker = markers[i], pos = marker.find();
            var mFrom = doc.clipPos(pos.from), mTo = doc.clipPos(pos.to);
            if (cmp(mFrom, mTo)) {
                var subMark = markText(doc, mFrom, mTo, marker.primary, marker.primary.type);
                marker.markers.push(subMark);
                subMark.parent = marker;
            }
        }
    };
    var detachSharedMarkers = function detachSharedMarkers(markers) {
        var loop = function loop(i) {
            var marker = markers[i], linked = [
                marker.primary.doc
            ];
            linkedDocs(marker.primary.doc, function(d) {
                return linked.push(d);
            });
            for(var j = 0; j < marker.markers.length; j++){
                var subMarker = marker.markers[j];
                if (indexOf(linked, subMarker.doc) == -1) {
                    subMarker.parent = null;
                    marker.markers.splice(j--, 1);
                }
            }
        };
        for(var i = 0; i < markers.length; i++)loop(i);
    };
    var onDrop = function onDrop(e) {
        var cm = this;
        clearDragCursor(cm);
        if (signalDOMEvent(cm, e) || eventInWidget(cm.display, e)) {
            return;
        }
        e_preventDefault(e);
        if (ie) {
            lastDrop = +new Date;
        }
        var pos = posFromMouse(cm, e, true), files = e.dataTransfer.files;
        if (!pos || cm.isReadOnly()) {
            return;
        }
        // Might be a file drop, in which case we simply extract the text
        // and insert it.
        if (files && files.length && window.FileReader && window.File) {
            var n = files.length, text = Array(n), read = 0;
            var markAsReadAndPasteIfAllFilesAreRead = function markAsReadAndPasteIfAllFilesAreRead() {
                if (++read == n) {
                    operation(cm, function() {
                        pos = clipPos(cm.doc, pos);
                        var change = {
                            from: pos,
                            to: pos,
                            text: cm.doc.splitLines(text.filter(function(t) {
                                return t != null;
                            }).join(cm.doc.lineSeparator())),
                            origin: "paste"
                        };
                        makeChange(cm.doc, change);
                        setSelectionReplaceHistory(cm.doc, simpleSelection(clipPos(cm.doc, pos), clipPos(cm.doc, changeEnd(change))));
                    })();
                }
            };
            var readTextFromFile = function readTextFromFile(file, i) {
                if (cm.options.allowDropFileTypes && indexOf(cm.options.allowDropFileTypes, file.type) == -1) {
                    markAsReadAndPasteIfAllFilesAreRead();
                    return;
                }
                var reader = new FileReader;
                reader.onerror = function() {
                    return markAsReadAndPasteIfAllFilesAreRead();
                };
                reader.onload = function() {
                    var content = reader.result;
                    if (/[\x00-\x08\x0e-\x1f]{2}/.test(content)) {
                        markAsReadAndPasteIfAllFilesAreRead();
                        return;
                    }
                    text[i] = content;
                    markAsReadAndPasteIfAllFilesAreRead();
                };
                reader.readAsText(file);
            };
            for(var i = 0; i < files.length; i++){
                readTextFromFile(files[i], i);
            }
        } else {
            // Don't do a replace if the drop happened inside of the selected text.
            if (cm.state.draggingText && cm.doc.sel.contains(pos) > -1) {
                cm.state.draggingText(e);
                // Ensure the editor is re-focused
                setTimeout(function() {
                    return cm.display.input.focus();
                }, 20);
                return;
            }
            try {
                var text$1 = e.dataTransfer.getData("Text");
                if (text$1) {
                    var selected;
                    if (cm.state.draggingText && !cm.state.draggingText.copy) {
                        selected = cm.listSelections();
                    }
                    setSelectionNoUndo(cm.doc, simpleSelection(pos, pos));
                    if (selected) {
                        for(var i$1 = 0; i$1 < selected.length; ++i$1){
                            replaceRange(cm.doc, "", selected[i$1].anchor, selected[i$1].head, "drag");
                        }
                    }
                    cm.replaceSelection(text$1, "around", "paste");
                    cm.display.input.focus();
                }
            } catch (e$1) {}
        }
    };
    var onDragStart = function onDragStart(cm, e) {
        if (ie && (!cm.state.draggingText || +new Date - lastDrop < 100)) {
            e_stop(e);
            return;
        }
        if (signalDOMEvent(cm, e) || eventInWidget(cm.display, e)) {
            return;
        }
        e.dataTransfer.setData("Text", cm.getSelection());
        e.dataTransfer.effectAllowed = "copyMove";
        // Use dummy image instead of default browsers image.
        // Recent Safari (~6.0.2) have a tendency to segfault when this happens, so we don't do it there.
        if (e.dataTransfer.setDragImage && !safari) {
            var img = elt("img", null, null, "position: fixed; left: 0; top: 0;");
            img.src = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
            if (presto) {
                img.width = img.height = 1;
                cm.display.wrapper.appendChild(img);
                // Force a relayout, or Opera won't use our image for some obscure reason
                img._top = img.offsetTop;
            }
            e.dataTransfer.setDragImage(img, 0, 0);
            if (presto) {
                img.parentNode.removeChild(img);
            }
        }
    };
    var onDragOver = function onDragOver(cm, e) {
        var pos = posFromMouse(cm, e);
        if (!pos) {
            return;
        }
        var frag = document.createDocumentFragment();
        drawSelectionCursor(cm, pos, frag);
        if (!cm.display.dragCursor) {
            cm.display.dragCursor = elt("div", null, "CodeMirror-cursors CodeMirror-dragcursors");
            cm.display.lineSpace.insertBefore(cm.display.dragCursor, cm.display.cursorDiv);
        }
        removeChildrenAndAdd(cm.display.dragCursor, frag);
    };
    var clearDragCursor = function clearDragCursor(cm) {
        if (cm.display.dragCursor) {
            cm.display.lineSpace.removeChild(cm.display.dragCursor);
            cm.display.dragCursor = null;
        }
    };
    var forEachCodeMirror = // These must be handled carefully, because naively registering a
    // handler for each editor will cause the editors to never be
    // garbage collected.
    function forEachCodeMirror(f) {
        if (!document.getElementsByClassName) {
            return;
        }
        var byClass = document.getElementsByClassName("CodeMirror"), editors = [];
        for(var i = 0; i < byClass.length; i++){
            var cm = byClass[i].CodeMirror;
            if (cm) {
                editors.push(cm);
            }
        }
        if (editors.length) {
            editors[0].operation(function() {
                for(var i = 0; i < editors.length; i++){
                    f(editors[i]);
                }
            });
        }
    };
    var ensureGlobalHandlers = function ensureGlobalHandlers() {
        if (globalsRegistered) {
            return;
        }
        registerGlobalHandlers();
        globalsRegistered = true;
    };
    var registerGlobalHandlers = function registerGlobalHandlers() {
        // When the window resizes, we need to refresh active editors.
        var resizeTimer;
        on(window, "resize", function() {
            if (resizeTimer == null) {
                resizeTimer = setTimeout(function() {
                    resizeTimer = null;
                    forEachCodeMirror(onResize);
                }, 100);
            }
        });
        // When the window loses focus, we want to show the editor as blurred
        on(window, "blur", function() {
            return forEachCodeMirror(onBlur);
        });
    };
    var onResize = // Called when the window resizes
    function onResize(cm) {
        var d = cm.display;
        // Might be a text scaling operation, clear size caches.
        d.cachedCharWidth = d.cachedTextHeight = d.cachedPaddingH = null;
        d.scrollbarsClipped = false;
        cm.setSize();
    };
    var normalizeKeyName = // KEYMAP DISPATCH
    function normalizeKeyName(name) {
        var parts = name.split(/-(?!$)/);
        name = parts[parts.length - 1];
        var alt, ctrl, shift, cmd;
        for(var i = 0; i < parts.length - 1; i++){
            var mod = parts[i];
            if (/^(cmd|meta|m)$/i.test(mod)) {
                cmd = true;
            } else if (/^a(lt)?$/i.test(mod)) {
                alt = true;
            } else if (/^(c|ctrl|control)$/i.test(mod)) {
                ctrl = true;
            } else if (/^s(hift)?$/i.test(mod)) {
                shift = true;
            } else {
                throw new Error("Unrecognized modifier name: " + mod);
            }
        }
        if (alt) {
            name = "Alt-" + name;
        }
        if (ctrl) {
            name = "Ctrl-" + name;
        }
        if (cmd) {
            name = "Cmd-" + name;
        }
        if (shift) {
            name = "Shift-" + name;
        }
        return name;
    };
    var normalizeKeyMap = // This is a kludge to keep keymaps mostly working as raw objects
    // (backwards compatibility) while at the same time support features
    // like normalization and multi-stroke key bindings. It compiles a
    // new normalized keymap, and then updates the old object to reflect
    // this.
    function normalizeKeyMap(keymap) {
        var copy = {};
        for(var keyname in keymap){
            if (keymap.hasOwnProperty(keyname)) {
                var value = keymap[keyname];
                if (/^(name|fallthrough|(de|at)tach)$/.test(keyname)) {
                    continue;
                }
                if (value == "...") {
                    delete keymap[keyname];
                    continue;
                }
                var keys = map(keyname.split(" "), normalizeKeyName);
                for(var i = 0; i < keys.length; i++){
                    var val = void 0, name = void 0;
                    if (i == keys.length - 1) {
                        name = keys.join(" ");
                        val = value;
                    } else {
                        name = keys.slice(0, i + 1).join(" ");
                        val = "...";
                    }
                    var prev = copy[name];
                    if (!prev) {
                        copy[name] = val;
                    } else if (prev != val) {
                        throw new Error("Inconsistent bindings for " + name);
                    }
                }
                delete keymap[keyname];
            }
        }
        for(var prop in copy){
            keymap[prop] = copy[prop];
        }
        return keymap;
    };
    var isModifierKey = // Modifier key presses don't count as 'real' key presses for the
    // purpose of keymap fallthrough.
    function isModifierKey(value) {
        var name = typeof value == "string" ? value : keyNames[value.keyCode];
        return name == "Ctrl" || name == "Alt" || name == "Shift" || name == "Mod";
    };
    var addModifierNames = function addModifierNames(name, event, noShift) {
        var base = name;
        if (event.altKey && base != "Alt") {
            name = "Alt-" + name;
        }
        if ((flipCtrlCmd ? event.metaKey : event.ctrlKey) && base != "Ctrl") {
            name = "Ctrl-" + name;
        }
        if ((flipCtrlCmd ? event.ctrlKey : event.metaKey) && base != "Mod") {
            name = "Cmd-" + name;
        }
        if (!noShift && event.shiftKey && base != "Shift") {
            name = "Shift-" + name;
        }
        return name;
    };
    var keyName = // Look up the name of a key as indicated by an event object.
    function keyName(event, noShift) {
        if (presto && event.keyCode == 34 && event["char"]) {
            return false;
        }
        var name = keyNames[event.keyCode];
        if (name == null || event.altGraphKey) {
            return false;
        }
        // Ctrl-ScrollLock has keyCode 3, same as Ctrl-Pause,
        // so we'll use event.code when available (Chrome 48+, FF 38+, Safari 10.1+)
        if (event.keyCode == 3 && event.code) {
            name = event.code;
        }
        return addModifierNames(name, event, noShift);
    };
    var getKeyMap = function getKeyMap(val) {
        return typeof val == "string" ? keyMap[val] : val;
    };
    var deleteNearSelection = // Helper for deleting text near the selection(s), used to implement
    // backspace, delete, and similar functionality.
    function deleteNearSelection(cm, compute) {
        var ranges = cm.doc.sel.ranges, kill = [];
        // Build up a set of ranges to kill first, merging overlapping
        // ranges.
        for(var i = 0; i < ranges.length; i++){
            var toKill = compute(ranges[i]);
            while(kill.length && cmp(toKill.from, lst(kill).to) <= 0){
                var replaced = kill.pop();
                if (cmp(replaced.from, toKill.from) < 0) {
                    toKill.from = replaced.from;
                    break;
                }
            }
            kill.push(toKill);
        }
        // Next, remove those actual ranges.
        runInOp(cm, function() {
            for(var i = kill.length - 1; i >= 0; i--){
                replaceRange(cm.doc, "", kill[i].from, kill[i].to, "+delete");
            }
            ensureCursorVisible(cm);
        });
    };
    var moveCharLogically = function moveCharLogically(line, ch, dir) {
        var target = skipExtendingChars(line.text, ch + dir, dir);
        return target < 0 || target > line.text.length ? null : target;
    };
    var moveLogically = function moveLogically(line, start, dir) {
        var ch = moveCharLogically(line, start.ch, dir);
        return ch == null ? null : new Pos(start.line, ch, dir < 0 ? "after" : "before");
    };
    var endOfLine = function endOfLine(visually, cm, lineObj, lineNo, dir) {
        if (visually) {
            if (cm.doc.direction == "rtl") {
                dir = -dir;
            }
            var order = getOrder(lineObj, cm.doc.direction);
            if (order) {
                var part = dir < 0 ? lst(order) : order[0];
                var moveInStorageOrder = dir < 0 == (part.level == 1);
                var sticky = moveInStorageOrder ? "after" : "before";
                var ch;
                // With a wrapped rtl chunk (possibly spanning multiple bidi parts),
                // it could be that the last bidi part is not on the last visual line,
                // since visual lines contain content order-consecutive chunks.
                // Thus, in rtl, we are looking for the first (content-order) character
                // in the rtl chunk that is on the last line (that is, the same line
                // as the last (content-order) character).
                if (part.level > 0 || cm.doc.direction == "rtl") {
                    var prep = prepareMeasureForLine(cm, lineObj);
                    ch = dir < 0 ? lineObj.text.length - 1 : 0;
                    var targetTop = measureCharPrepared(cm, prep, ch).top;
                    ch = findFirst(function(ch) {
                        return measureCharPrepared(cm, prep, ch).top == targetTop;
                    }, dir < 0 == (part.level == 1) ? part.from : part.to - 1, ch);
                    if (sticky == "before") {
                        ch = moveCharLogically(lineObj, ch, 1);
                    }
                } else {
                    ch = dir < 0 ? part.to : part.from;
                }
                return new Pos(lineNo, ch, sticky);
            }
        }
        return new Pos(lineNo, dir < 0 ? lineObj.text.length : 0, dir < 0 ? "before" : "after");
    };
    var moveVisually = function moveVisually(cm, line, start, dir) {
        var bidi = getOrder(line, cm.doc.direction);
        if (!bidi) {
            return moveLogically(line, start, dir);
        }
        if (start.ch >= line.text.length) {
            start.ch = line.text.length;
            start.sticky = "before";
        } else if (start.ch <= 0) {
            start.ch = 0;
            start.sticky = "after";
        }
        var partPos = getBidiPartAt(bidi, start.ch, start.sticky), part = bidi[partPos];
        if (cm.doc.direction == "ltr" && part.level % 2 == 0 && (dir > 0 ? part.to > start.ch : part.from < start.ch)) {
            // Case 1: We move within an ltr part in an ltr editor. Even with wrapped lines,
            // nothing interesting happens.
            return moveLogically(line, start, dir);
        }
        var mv = function mv(pos, dir) {
            return moveCharLogically(line, _instanceof(pos, Pos) ? pos.ch : pos, dir);
        };
        var prep;
        var getWrappedLineExtent = function getWrappedLineExtent(ch) {
            if (!cm.options.lineWrapping) {
                return {
                    begin: 0,
                    end: line.text.length
                };
            }
            prep = prep || prepareMeasureForLine(cm, line);
            return wrappedLineExtentChar(cm, line, prep, ch);
        };
        var wrappedLineExtent = getWrappedLineExtent(start.sticky == "before" ? mv(start, -1) : start.ch);
        if (cm.doc.direction == "rtl" || part.level == 1) {
            var moveInStorageOrder = part.level == 1 == dir < 0;
            var ch = mv(start, moveInStorageOrder ? 1 : -1);
            if (ch != null && (!moveInStorageOrder ? ch >= part.from && ch >= wrappedLineExtent.begin : ch <= part.to && ch <= wrappedLineExtent.end)) {
                // Case 2: We move within an rtl part or in an rtl editor on the same visual line
                var sticky = moveInStorageOrder ? "before" : "after";
                return new Pos(start.line, ch, sticky);
            }
        }
        // Case 3: Could not move within this bidi part in this visual line, so leave
        // the current bidi part
        var searchInVisualLine = function searchInVisualLine(partPos, dir, wrappedLineExtent) {
            var getRes = function getRes(ch, moveInStorageOrder) {
                return moveInStorageOrder ? new Pos(start.line, mv(ch, 1), "before") : new Pos(start.line, ch, "after");
            };
            for(; partPos >= 0 && partPos < bidi.length; partPos += dir){
                var part = bidi[partPos];
                var moveInStorageOrder = dir > 0 == (part.level != 1);
                var ch = moveInStorageOrder ? wrappedLineExtent.begin : mv(wrappedLineExtent.end, -1);
                if (part.from <= ch && ch < part.to) {
                    return getRes(ch, moveInStorageOrder);
                }
                ch = moveInStorageOrder ? part.from : mv(part.to, -1);
                if (wrappedLineExtent.begin <= ch && ch < wrappedLineExtent.end) {
                    return getRes(ch, moveInStorageOrder);
                }
            }
        };
        // Case 3a: Look for other bidi parts on the same visual line
        var res = searchInVisualLine(partPos + dir, dir, wrappedLineExtent);
        if (res) {
            return res;
        }
        // Case 3b: Look for other bidi parts on the next visual line
        var nextCh = dir > 0 ? wrappedLineExtent.end : mv(wrappedLineExtent.begin, -1);
        if (nextCh != null && !(dir > 0 && nextCh == line.text.length)) {
            res = searchInVisualLine(dir > 0 ? 0 : bidi.length - 1, dir, getWrappedLineExtent(nextCh));
            if (res) {
                return res;
            }
        }
        // Case 4: Nowhere to move
        return null;
    };
    var lineStart = function lineStart(cm, lineN) {
        var line = getLine(cm.doc, lineN);
        var visual = visualLine(line);
        if (visual != line) {
            lineN = lineNo(visual);
        }
        return endOfLine(true, cm, visual, lineN, 1);
    };
    var lineEnd = function lineEnd(cm, lineN) {
        var line = getLine(cm.doc, lineN);
        var visual = visualLineEnd(line);
        if (visual != line) {
            lineN = lineNo(visual);
        }
        return endOfLine(true, cm, line, lineN, -1);
    };
    var lineStartSmart = function lineStartSmart(cm, pos) {
        var start = lineStart(cm, pos.line);
        var line = getLine(cm.doc, start.line);
        var order = getOrder(line, cm.doc.direction);
        if (!order || order[0].level == 0) {
            var firstNonWS = Math.max(start.ch, line.text.search(/\S/));
            var inWS = pos.line == start.line && pos.ch <= firstNonWS && pos.ch;
            return Pos(start.line, inWS ? 0 : firstNonWS, start.sticky);
        }
        return start;
    };
    var doHandleBinding = // Run a handler that was bound to a key.
    function doHandleBinding(cm, bound, dropShift) {
        if (typeof bound == "string") {
            bound = commands[bound];
            if (!bound) {
                return false;
            }
        }
        // Ensure previous input has been read, so that the handler sees a
        // consistent view of the document
        cm.display.input.ensurePolled();
        var prevShift = cm.display.shift, done = false;
        try {
            if (cm.isReadOnly()) {
                cm.state.suppressEdits = true;
            }
            if (dropShift) {
                cm.display.shift = false;
            }
            done = bound(cm) != Pass;
        } finally{
            cm.display.shift = prevShift;
            cm.state.suppressEdits = false;
        }
        return done;
    };
    var lookupKeyForEditor = function lookupKeyForEditor(cm, name, handle) {
        for(var i = 0; i < cm.state.keyMaps.length; i++){
            var result = lookupKey(name, cm.state.keyMaps[i], handle, cm);
            if (result) {
                return result;
            }
        }
        return cm.options.extraKeys && lookupKey(name, cm.options.extraKeys, handle, cm) || lookupKey(name, cm.options.keyMap, handle, cm);
    };
    var dispatchKey = function dispatchKey(cm, name, e, handle) {
        var seq = cm.state.keySeq;
        if (seq) {
            if (isModifierKey(name)) {
                return "handled";
            }
            if (/\'$/.test(name)) {
                cm.state.keySeq = null;
            } else {
                stopSeq.set(50, function() {
                    if (cm.state.keySeq == seq) {
                        cm.state.keySeq = null;
                        cm.display.input.reset();
                    }
                });
            }
            if (dispatchKeyInner(cm, seq + " " + name, e, handle)) {
                return true;
            }
        }
        return dispatchKeyInner(cm, name, e, handle);
    };
    var dispatchKeyInner = function dispatchKeyInner(cm, name, e, handle) {
        var result = lookupKeyForEditor(cm, name, handle);
        if (result == "multi") {
            cm.state.keySeq = name;
        }
        if (result == "handled") {
            signalLater(cm, "keyHandled", cm, name, e);
        }
        if (result == "handled" || result == "multi") {
            e_preventDefault(e);
            restartBlink(cm);
        }
        return !!result;
    };
    var handleKeyBinding = // Handle a key from the keydown event.
    function handleKeyBinding(cm, e) {
        var name = keyName(e, true);
        if (!name) {
            return false;
        }
        if (e.shiftKey && !cm.state.keySeq) {
            // First try to resolve full name (including 'Shift-'). Failing
            // that, see if there is a cursor-motion command (starting with
            // 'go') bound to the keyname without 'Shift-'.
            return dispatchKey(cm, "Shift-" + name, e, function(b) {
                return doHandleBinding(cm, b, true);
            }) || dispatchKey(cm, name, e, function(b) {
                if (typeof b == "string" ? /^go[A-Z]/.test(b) : b.motion) {
                    return doHandleBinding(cm, b);
                }
            });
        } else {
            return dispatchKey(cm, name, e, function(b) {
                return doHandleBinding(cm, b);
            });
        }
    };
    var handleCharBinding = // Handle a key from the keypress event
    function handleCharBinding(cm, e, ch) {
        return dispatchKey(cm, "'" + ch + "'", e, function(b) {
            return doHandleBinding(cm, b, true);
        });
    };
    var onKeyDown = function onKeyDown(e) {
        var cm = this;
        if (e.target && e.target != cm.display.input.getField()) {
            return;
        }
        cm.curOp.focus = activeElt();
        if (signalDOMEvent(cm, e)) {
            return;
        }
        // IE does strange things with escape.
        if (ie && ie_version < 11 && e.keyCode == 27) {
            e.returnValue = false;
        }
        var code = e.keyCode;
        cm.display.shift = code == 16 || e.shiftKey;
        var handled = handleKeyBinding(cm, e);
        if (presto) {
            lastStoppedKey = handled ? code : null;
            // Opera has no cut event... we try to at least catch the key combo
            if (!handled && code == 88 && !hasCopyEvent && (mac ? e.metaKey : e.ctrlKey)) {
                cm.replaceSelection("", null, "cut");
            }
        }
        if (gecko && !mac && !handled && code == 46 && e.shiftKey && !e.ctrlKey && document.execCommand) {
            document.execCommand("cut");
        }
        // Turn mouse into crosshair when Alt is held on Mac.
        if (code == 18 && !/\bCodeMirror-crosshair\b/.test(cm.display.lineDiv.className)) {
            showCrossHair(cm);
        }
    };
    var showCrossHair = function showCrossHair(cm) {
        var lineDiv = cm.display.lineDiv;
        addClass(lineDiv, "CodeMirror-crosshair");
        function up(e) {
            if (e.keyCode == 18 || !e.altKey) {
                rmClass(lineDiv, "CodeMirror-crosshair");
                off(document, "keyup", up);
                off(document, "mouseover", up);
            }
        }
        on(document, "keyup", up);
        on(document, "mouseover", up);
    };
    var onKeyUp = function onKeyUp(e) {
        if (e.keyCode == 16) {
            this.doc.sel.shift = false;
        }
        signalDOMEvent(this, e);
    };
    var onKeyPress = function onKeyPress(e) {
        var cm = this;
        if (e.target && e.target != cm.display.input.getField()) {
            return;
        }
        if (eventInWidget(cm.display, e) || signalDOMEvent(cm, e) || e.ctrlKey && !e.altKey || mac && e.metaKey) {
            return;
        }
        var keyCode = e.keyCode, charCode = e.charCode;
        if (presto && keyCode == lastStoppedKey) {
            lastStoppedKey = null;
            e_preventDefault(e);
            return;
        }
        if (presto && (!e.which || e.which < 10) && handleKeyBinding(cm, e)) {
            return;
        }
        var ch = String.fromCharCode(charCode == null ? keyCode : charCode);
        // Some browsers fire keypress events for backspace
        if (ch == "\b") {
            return;
        }
        if (handleCharBinding(cm, e, ch)) {
            return;
        }
        cm.display.input.onKeyPress(e);
    };
    var clickRepeat = function clickRepeat(pos, button) {
        var now = +new Date;
        if (lastDoubleClick && lastDoubleClick.compare(now, pos, button)) {
            lastClick = lastDoubleClick = null;
            return "triple";
        } else if (lastClick && lastClick.compare(now, pos, button)) {
            lastDoubleClick = new PastClick(now, pos, button);
            lastClick = null;
            return "double";
        } else {
            lastClick = new PastClick(now, pos, button);
            lastDoubleClick = null;
            return "single";
        }
    };
    var onMouseDown = // A mouse down can be a single click, double click, triple click,
    // start of selection drag, start of text drag, new cursor
    // (ctrl-click), rectangle drag (alt-drag), or xwin
    // middle-click-paste. Or it might be a click on something we should
    // not interfere with, such as a scrollbar or widget.
    function onMouseDown(e) {
        var cm = this, display = cm.display;
        if (signalDOMEvent(cm, e) || display.activeTouch && display.input.supportsTouch()) {
            return;
        }
        display.input.ensurePolled();
        display.shift = e.shiftKey;
        if (eventInWidget(display, e)) {
            if (!webkit) {
                // Briefly turn off draggability, to allow widgets to do
                // normal dragging things.
                display.scroller.draggable = false;
                setTimeout(function() {
                    return display.scroller.draggable = true;
                }, 100);
            }
            return;
        }
        if (clickInGutter(cm, e)) {
            return;
        }
        var pos = posFromMouse(cm, e), button = e_button(e), repeat = pos ? clickRepeat(pos, button) : "single";
        window.focus();
        // #3261: make sure, that we're not starting a second selection
        if (button == 1 && cm.state.selectingText) {
            cm.state.selectingText(e);
        }
        if (pos && handleMappedButton(cm, button, pos, repeat, e)) {
            return;
        }
        if (button == 1) {
            if (pos) {
                leftButtonDown(cm, pos, repeat, e);
            } else if (e_target(e) == display.scroller) {
                e_preventDefault(e);
            }
        } else if (button == 2) {
            if (pos) {
                extendSelection(cm.doc, pos);
            }
            setTimeout(function() {
                return display.input.focus();
            }, 20);
        } else if (button == 3) {
            if (captureRightClick) {
                cm.display.input.onContextMenu(e);
            } else {
                delayBlurEvent(cm);
            }
        }
    };
    var handleMappedButton = function handleMappedButton(cm, button, pos, repeat, event) {
        var name = "Click";
        if (repeat == "double") {
            name = "Double" + name;
        } else if (repeat == "triple") {
            name = "Triple" + name;
        }
        name = (button == 1 ? "Left" : button == 2 ? "Middle" : "Right") + name;
        return dispatchKey(cm, addModifierNames(name, event), event, function(bound) {
            if (typeof bound == "string") {
                bound = commands[bound];
            }
            if (!bound) {
                return false;
            }
            var done = false;
            try {
                if (cm.isReadOnly()) {
                    cm.state.suppressEdits = true;
                }
                done = bound(cm, pos) != Pass;
            } finally{
                cm.state.suppressEdits = false;
            }
            return done;
        });
    };
    var configureMouse = function configureMouse(cm, repeat, event) {
        var option = cm.getOption("configureMouse");
        var value = option ? option(cm, repeat, event) : {};
        if (value.unit == null) {
            var rect = chromeOS ? event.shiftKey && event.metaKey : event.altKey;
            value.unit = rect ? "rectangle" : repeat == "single" ? "char" : repeat == "double" ? "word" : "line";
        }
        if (value.extend == null || cm.doc.extend) {
            value.extend = cm.doc.extend || event.shiftKey;
        }
        if (value.addNew == null) {
            value.addNew = mac ? event.metaKey : event.ctrlKey;
        }
        if (value.moveOnDrag == null) {
            value.moveOnDrag = !(mac ? event.altKey : event.ctrlKey);
        }
        return value;
    };
    var leftButtonDown = function leftButtonDown(cm, pos, repeat, event) {
        if (ie) {
            setTimeout(bind(ensureFocus, cm), 0);
        } else {
            cm.curOp.focus = activeElt();
        }
        var behavior = configureMouse(cm, repeat, event);
        var sel = cm.doc.sel, contained;
        if (cm.options.dragDrop && dragAndDrop && !cm.isReadOnly() && repeat == "single" && (contained = sel.contains(pos)) > -1 && (cmp((contained = sel.ranges[contained]).from(), pos) < 0 || pos.xRel > 0) && (cmp(contained.to(), pos) > 0 || pos.xRel < 0)) {
            leftButtonStartDrag(cm, event, pos, behavior);
        } else {
            leftButtonSelect(cm, event, pos, behavior);
        }
    };
    var leftButtonStartDrag = // Start a text drag. When it ends, see if any dragging actually
    // happen, and treat as a click if it didn't.
    function leftButtonStartDrag(cm, event, pos, behavior) {
        var display = cm.display, moved = false;
        var dragEnd = operation(cm, function(e) {
            if (webkit) {
                display.scroller.draggable = false;
            }
            cm.state.draggingText = false;
            if (cm.state.delayingBlurEvent) {
                if (cm.hasFocus()) {
                    cm.state.delayingBlurEvent = false;
                } else {
                    delayBlurEvent(cm);
                }
            }
            off(display.wrapper.ownerDocument, "mouseup", dragEnd);
            off(display.wrapper.ownerDocument, "mousemove", mouseMove);
            off(display.scroller, "dragstart", dragStart);
            off(display.scroller, "drop", dragEnd);
            if (!moved) {
                e_preventDefault(e);
                if (!behavior.addNew) {
                    extendSelection(cm.doc, pos, null, null, behavior.extend);
                }
                // Work around unexplainable focus problem in IE9 (#2127) and Chrome (#3081)
                if (webkit && !safari || ie && ie_version == 9) {
                    setTimeout(function() {
                        display.wrapper.ownerDocument.body.focus({
                            preventScroll: true
                        });
                        display.input.focus();
                    }, 20);
                } else {
                    display.input.focus();
                }
            }
        });
        var mouseMove = function mouseMove(e2) {
            moved = moved || Math.abs(event.clientX - e2.clientX) + Math.abs(event.clientY - e2.clientY) >= 10;
        };
        var dragStart = function dragStart() {
            return moved = true;
        };
        // Let the drag handler handle this.
        if (webkit) {
            display.scroller.draggable = true;
        }
        cm.state.draggingText = dragEnd;
        dragEnd.copy = !behavior.moveOnDrag;
        on(display.wrapper.ownerDocument, "mouseup", dragEnd);
        on(display.wrapper.ownerDocument, "mousemove", mouseMove);
        on(display.scroller, "dragstart", dragStart);
        on(display.scroller, "drop", dragEnd);
        cm.state.delayingBlurEvent = true;
        setTimeout(function() {
            return display.input.focus();
        }, 20);
        // IE's approach to draggable
        if (display.scroller.dragDrop) {
            display.scroller.dragDrop();
        }
    };
    var rangeForUnit = function rangeForUnit(cm, pos, unit) {
        if (unit == "char") {
            return new Range(pos, pos);
        }
        if (unit == "word") {
            return cm.findWordAt(pos);
        }
        if (unit == "line") {
            return new Range(Pos(pos.line, 0), clipPos(cm.doc, Pos(pos.line + 1, 0)));
        }
        var result = unit(cm, pos);
        return new Range(result.from, result.to);
    };
    var leftButtonSelect = // Normal selection, as opposed to text dragging.
    function leftButtonSelect(cm, event, start, behavior) {
        if (ie) {
            delayBlurEvent(cm);
        }
        var display = cm.display, doc = cm.doc;
        e_preventDefault(event);
        var ourRange, ourIndex, startSel = doc.sel, ranges = startSel.ranges;
        if (behavior.addNew && !behavior.extend) {
            ourIndex = doc.sel.contains(start);
            if (ourIndex > -1) {
                ourRange = ranges[ourIndex];
            } else {
                ourRange = new Range(start, start);
            }
        } else {
            ourRange = doc.sel.primary();
            ourIndex = doc.sel.primIndex;
        }
        if (behavior.unit == "rectangle") {
            if (!behavior.addNew) {
                ourRange = new Range(start, start);
            }
            start = posFromMouse(cm, event, true, true);
            ourIndex = -1;
        } else {
            var range = rangeForUnit(cm, start, behavior.unit);
            if (behavior.extend) {
                ourRange = extendRange(ourRange, range.anchor, range.head, behavior.extend);
            } else {
                ourRange = range;
            }
        }
        if (!behavior.addNew) {
            ourIndex = 0;
            setSelection(doc, new Selection([
                ourRange
            ], 0), sel_mouse);
            startSel = doc.sel;
        } else if (ourIndex == -1) {
            ourIndex = ranges.length;
            setSelection(doc, normalizeSelection(cm, ranges.concat([
                ourRange
            ]), ourIndex), {
                scroll: false,
                origin: "*mouse"
            });
        } else if (ranges.length > 1 && ranges[ourIndex].empty() && behavior.unit == "char" && !behavior.extend) {
            setSelection(doc, normalizeSelection(cm, ranges.slice(0, ourIndex).concat(ranges.slice(ourIndex + 1)), 0), {
                scroll: false,
                origin: "*mouse"
            });
            startSel = doc.sel;
        } else {
            replaceOneSelection(doc, ourIndex, ourRange, sel_mouse);
        }
        var lastPos = start;
        function extendTo(pos) {
            if (cmp(lastPos, pos) == 0) {
                return;
            }
            lastPos = pos;
            if (behavior.unit == "rectangle") {
                var ranges = [], tabSize = cm.options.tabSize;
                var startCol = countColumn(getLine(doc, start.line).text, start.ch, tabSize);
                var posCol = countColumn(getLine(doc, pos.line).text, pos.ch, tabSize);
                var left = Math.min(startCol, posCol), right = Math.max(startCol, posCol);
                for(var line = Math.min(start.line, pos.line), end = Math.min(cm.lastLine(), Math.max(start.line, pos.line)); line <= end; line++){
                    var text = getLine(doc, line).text, leftPos = findColumn(text, left, tabSize);
                    if (left == right) {
                        ranges.push(new Range(Pos(line, leftPos), Pos(line, leftPos)));
                    } else if (text.length > leftPos) {
                        ranges.push(new Range(Pos(line, leftPos), Pos(line, findColumn(text, right, tabSize))));
                    }
                }
                if (!ranges.length) {
                    ranges.push(new Range(start, start));
                }
                setSelection(doc, normalizeSelection(cm, startSel.ranges.slice(0, ourIndex).concat(ranges), ourIndex), {
                    origin: "*mouse",
                    scroll: false
                });
                cm.scrollIntoView(pos);
            } else {
                var oldRange = ourRange;
                var range = rangeForUnit(cm, pos, behavior.unit);
                var anchor = oldRange.anchor, head;
                if (cmp(range.anchor, anchor) > 0) {
                    head = range.head;
                    anchor = minPos(oldRange.from(), range.anchor);
                } else {
                    head = range.anchor;
                    anchor = maxPos(oldRange.to(), range.head);
                }
                var ranges$1 = startSel.ranges.slice(0);
                ranges$1[ourIndex] = bidiSimplify(cm, new Range(clipPos(doc, anchor), head));
                setSelection(doc, normalizeSelection(cm, ranges$1, ourIndex), sel_mouse);
            }
        }
        var editorSize = display.wrapper.getBoundingClientRect();
        // Used to ensure timeout re-tries don't fire when another extend
        // happened in the meantime (clearTimeout isn't reliable -- at
        // least on Chrome, the timeouts still happen even when cleared,
        // if the clear happens after their scheduled firing time).
        var counter = 0;
        function extend(e) {
            var curCount = ++counter;
            var cur = posFromMouse(cm, e, true, behavior.unit == "rectangle");
            if (!cur) {
                return;
            }
            if (cmp(cur, lastPos) != 0) {
                cm.curOp.focus = activeElt();
                extendTo(cur);
                var visible = visibleLines(display, doc);
                if (cur.line >= visible.to || cur.line < visible.from) {
                    setTimeout(operation(cm, function() {
                        if (counter == curCount) {
                            extend(e);
                        }
                    }), 150);
                }
            } else {
                var outside = e.clientY < editorSize.top ? -20 : e.clientY > editorSize.bottom ? 20 : 0;
                if (outside) {
                    setTimeout(operation(cm, function() {
                        if (counter != curCount) {
                            return;
                        }
                        display.scroller.scrollTop += outside;
                        extend(e);
                    }), 50);
                }
            }
        }
        function done(e) {
            cm.state.selectingText = false;
            counter = Infinity;
            // If e is null or undefined we interpret this as someone trying
            // to explicitly cancel the selection rather than the user
            // letting go of the mouse button.
            if (e) {
                e_preventDefault(e);
                display.input.focus();
            }
            off(display.wrapper.ownerDocument, "mousemove", move);
            off(display.wrapper.ownerDocument, "mouseup", up);
            doc.history.lastSelOrigin = null;
        }
        var move = operation(cm, function(e) {
            if (e.buttons === 0 || !e_button(e)) {
                done(e);
            } else {
                extend(e);
            }
        });
        var up = operation(cm, done);
        cm.state.selectingText = up;
        on(display.wrapper.ownerDocument, "mousemove", move);
        on(display.wrapper.ownerDocument, "mouseup", up);
    };
    var bidiSimplify = // Used when mouse-selecting to adjust the anchor to the proper side
    // of a bidi jump depending on the visual position of the head.
    function bidiSimplify(cm, range) {
        var anchor = range.anchor;
        var head = range.head;
        var anchorLine = getLine(cm.doc, anchor.line);
        if (cmp(anchor, head) == 0 && anchor.sticky == head.sticky) {
            return range;
        }
        var order = getOrder(anchorLine);
        if (!order) {
            return range;
        }
        var index = getBidiPartAt(order, anchor.ch, anchor.sticky), part = order[index];
        if (part.from != anchor.ch && part.to != anchor.ch) {
            return range;
        }
        var boundary = index + (part.from == anchor.ch == (part.level != 1) ? 0 : 1);
        if (boundary == 0 || boundary == order.length) {
            return range;
        }
        // Compute the relative visual position of the head compared to the
        // anchor (<0 is to the left, >0 to the right)
        var leftSide;
        if (head.line != anchor.line) {
            leftSide = (head.line - anchor.line) * (cm.doc.direction == "ltr" ? 1 : -1) > 0;
        } else {
            var headIndex = getBidiPartAt(order, head.ch, head.sticky);
            var dir = headIndex - index || (head.ch - anchor.ch) * (part.level == 1 ? -1 : 1);
            if (headIndex == boundary - 1 || headIndex == boundary) {
                leftSide = dir < 0;
            } else {
                leftSide = dir > 0;
            }
        }
        var usePart = order[boundary + (leftSide ? -1 : 0)];
        var from = leftSide == (usePart.level == 1);
        var ch = from ? usePart.from : usePart.to, sticky = from ? "after" : "before";
        return anchor.ch == ch && anchor.sticky == sticky ? range : new Range(new Pos(anchor.line, ch, sticky), head);
    };
    var gutterEvent = // Determines whether an event happened in the gutter, and fires the
    // handlers for the corresponding event.
    function gutterEvent(cm, e, type, prevent) {
        var mX, mY;
        if (e.touches) {
            mX = e.touches[0].clientX;
            mY = e.touches[0].clientY;
        } else {
            try {
                mX = e.clientX;
                mY = e.clientY;
            } catch (e$1) {
                return false;
            }
        }
        if (mX >= Math.floor(cm.display.gutters.getBoundingClientRect().right)) {
            return false;
        }
        if (prevent) {
            e_preventDefault(e);
        }
        var display = cm.display;
        var lineBox = display.lineDiv.getBoundingClientRect();
        if (mY > lineBox.bottom || !hasHandler(cm, type)) {
            return e_defaultPrevented(e);
        }
        mY -= lineBox.top - display.viewOffset;
        for(var i = 0; i < cm.display.gutterSpecs.length; ++i){
            var g = display.gutters.childNodes[i];
            if (g && g.getBoundingClientRect().right >= mX) {
                var line = lineAtHeight(cm.doc, mY);
                var gutter = cm.display.gutterSpecs[i];
                signal(cm, type, cm, line, gutter.className, e);
                return e_defaultPrevented(e);
            }
        }
    };
    var clickInGutter = function clickInGutter(cm, e) {
        return gutterEvent(cm, e, "gutterClick", true);
    };
    var onContextMenu = // CONTEXT MENU HANDLING
    // To make the context menu work, we need to briefly unhide the
    // textarea (making it as unobtrusive as possible) to let the
    // right-click take effect on it.
    function onContextMenu(cm, e) {
        if (eventInWidget(cm.display, e) || contextMenuInGutter(cm, e)) {
            return;
        }
        if (signalDOMEvent(cm, e, "contextmenu")) {
            return;
        }
        if (!captureRightClick) {
            cm.display.input.onContextMenu(e);
        }
    };
    var contextMenuInGutter = function contextMenuInGutter(cm, e) {
        if (!hasHandler(cm, "gutterContextMenu")) {
            return false;
        }
        return gutterEvent(cm, e, "gutterContextMenu", false);
    };
    var themeChanged = function themeChanged(cm) {
        cm.display.wrapper.className = cm.display.wrapper.className.replace(/\s*cm-s-\S+/g, "") + cm.options.theme.replace(/(^|\s)\s*/g, " cm-s-");
        clearCaches(cm);
    };
    var defineOptions = function defineOptions(CodeMirror) {
        var optionHandlers = CodeMirror.optionHandlers;
        function option(name, deflt, handle, notOnInit) {
            CodeMirror.defaults[name] = deflt;
            if (handle) {
                optionHandlers[name] = notOnInit ? function(cm, val, old) {
                    if (old != Init) {
                        handle(cm, val, old);
                    }
                } : handle;
            }
        }
        CodeMirror.defineOption = option;
        // Passed to option handlers when there is no old value.
        CodeMirror.Init = Init;
        // These two are, on init, called from the constructor because they
        // have to be initialized before the editor can start at all.
        option("value", "", function(cm, val) {
            return cm.setValue(val);
        }, true);
        option("mode", null, function(cm, val) {
            cm.doc.modeOption = val;
            loadMode(cm);
        }, true);
        option("indentUnit", 2, loadMode, true);
        option("indentWithTabs", false);
        option("smartIndent", true);
        option("tabSize", 4, function(cm) {
            resetModeState(cm);
            clearCaches(cm);
            regChange(cm);
        }, true);
        option("lineSeparator", null, function(cm, val) {
            cm.doc.lineSep = val;
            if (!val) {
                return;
            }
            var newBreaks = [], lineNo = cm.doc.first;
            cm.doc.iter(function(line) {
                for(var pos = 0;;){
                    var found = line.text.indexOf(val, pos);
                    if (found == -1) {
                        break;
                    }
                    pos = found + val.length;
                    newBreaks.push(Pos(lineNo, found));
                }
                lineNo++;
            });
            for(var i = newBreaks.length - 1; i >= 0; i--){
                replaceRange(cm.doc, val, newBreaks[i], Pos(newBreaks[i].line, newBreaks[i].ch + val.length));
            }
        });
        option("specialChars", /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u200b\u200e\u200f\u2028\u2029\ufeff\ufff9-\ufffc]/g, function(cm, val, old) {
            cm.state.specialChars = new RegExp(val.source + (val.test("	") ? "" : "|	"), "g");
            if (old != Init) {
                cm.refresh();
            }
        });
        option("specialCharPlaceholder", defaultSpecialCharPlaceholder, function(cm) {
            return cm.refresh();
        }, true);
        option("electricChars", true);
        option("inputStyle", mobile ? "contenteditable" : "textarea", function() {
            throw new Error("inputStyle can not (yet) be changed in a running editor") // FIXME
            ;
        }, true);
        option("spellcheck", false, function(cm, val) {
            return cm.getInputField().spellcheck = val;
        }, true);
        option("autocorrect", false, function(cm, val) {
            return cm.getInputField().autocorrect = val;
        }, true);
        option("autocapitalize", false, function(cm, val) {
            return cm.getInputField().autocapitalize = val;
        }, true);
        option("rtlMoveVisually", !windows);
        option("wholeLineUpdateBefore", true);
        option("theme", "default", function(cm) {
            themeChanged(cm);
            updateGutters(cm);
        }, true);
        option("keyMap", "default", function(cm, val, old) {
            var next = getKeyMap(val);
            var prev = old != Init && getKeyMap(old);
            if (prev && prev.detach) {
                prev.detach(cm, next);
            }
            if (next.attach) {
                next.attach(cm, prev || null);
            }
        });
        option("extraKeys", null);
        option("configureMouse", null);
        option("lineWrapping", false, wrappingChanged, true);
        option("gutters", [], function(cm, val) {
            cm.display.gutterSpecs = getGutters(val, cm.options.lineNumbers);
            updateGutters(cm);
        }, true);
        option("fixedGutter", true, function(cm, val) {
            cm.display.gutters.style.left = val ? compensateForHScroll(cm.display) + "px" : "0";
            cm.refresh();
        }, true);
        option("coverGutterNextToScrollbar", false, function(cm) {
            return updateScrollbars(cm);
        }, true);
        option("scrollbarStyle", "native", function(cm) {
            initScrollbars(cm);
            updateScrollbars(cm);
            cm.display.scrollbars.setScrollTop(cm.doc.scrollTop);
            cm.display.scrollbars.setScrollLeft(cm.doc.scrollLeft);
        }, true);
        option("lineNumbers", false, function(cm, val) {
            cm.display.gutterSpecs = getGutters(cm.options.gutters, val);
            updateGutters(cm);
        }, true);
        option("firstLineNumber", 1, updateGutters, true);
        option("lineNumberFormatter", function(integer) {
            return integer;
        }, updateGutters, true);
        option("showCursorWhenSelecting", false, updateSelection, true);
        option("resetSelectionOnContextMenu", true);
        option("lineWiseCopyCut", true);
        option("pasteLinesPerSelection", true);
        option("selectionsMayTouch", false);
        option("readOnly", false, function(cm, val) {
            if (val == "nocursor") {
                onBlur(cm);
                cm.display.input.blur();
            }
            cm.display.input.readOnlyChanged(val);
        });
        option("screenReaderLabel", null, function(cm, val) {
            val = val === "" ? null : val;
            cm.display.input.screenReaderLabelChanged(val);
        });
        option("disableInput", false, function(cm, val) {
            if (!val) {
                cm.display.input.reset();
            }
        }, true);
        option("dragDrop", true, dragDropChanged);
        option("allowDropFileTypes", null);
        option("cursorBlinkRate", 530);
        option("cursorScrollMargin", 0);
        option("cursorHeight", 1, updateSelection, true);
        option("singleCursorHeightPerLine", true, updateSelection, true);
        option("workTime", 100);
        option("workDelay", 100);
        option("flattenSpans", true, resetModeState, true);
        option("addModeClass", false, resetModeState, true);
        option("pollInterval", 100);
        option("undoDepth", 200, function(cm, val) {
            return cm.doc.history.undoDepth = val;
        });
        option("historyEventDelay", 1250);
        option("viewportMargin", 10, function(cm) {
            return cm.refresh();
        }, true);
        option("maxHighlightLength", 10000, resetModeState, true);
        option("moveInputWithCursor", true, function(cm, val) {
            if (!val) {
                cm.display.input.resetPosition();
            }
        });
        option("tabindex", null, function(cm, val) {
            return cm.display.input.getField().tabIndex = val || "";
        });
        option("autofocus", null);
        option("direction", "ltr", function(cm, val) {
            return cm.doc.setDirection(val);
        }, true);
        option("phrases", null);
    };
    var dragDropChanged = function dragDropChanged(cm, value, old) {
        var wasOn = old && old != Init;
        if (!value != !wasOn) {
            var funcs = cm.display.dragFunctions;
            var toggle = value ? on : off;
            toggle(cm.display.scroller, "dragstart", funcs.start);
            toggle(cm.display.scroller, "dragenter", funcs.enter);
            toggle(cm.display.scroller, "dragover", funcs.over);
            toggle(cm.display.scroller, "dragleave", funcs.leave);
            toggle(cm.display.scroller, "drop", funcs.drop);
        }
    };
    var wrappingChanged = function wrappingChanged(cm) {
        if (cm.options.lineWrapping) {
            addClass(cm.display.wrapper, "CodeMirror-wrap");
            cm.display.sizer.style.minWidth = "";
            cm.display.sizerWidth = null;
        } else {
            rmClass(cm.display.wrapper, "CodeMirror-wrap");
            findMaxLine(cm);
        }
        estimateLineHeights(cm);
        regChange(cm);
        clearCaches(cm);
        setTimeout(function() {
            return updateScrollbars(cm);
        }, 100);
    };
    var registerEventHandlers = // Attach the necessary event handlers when initializing the editor
    function registerEventHandlers(cm) {
        var d = cm.display;
        on(d.scroller, "mousedown", operation(cm, onMouseDown));
        // Older IE's will not fire a second mousedown for a double click
        if (ie && ie_version < 11) {
            on(d.scroller, "dblclick", operation(cm, function(e) {
                if (signalDOMEvent(cm, e)) {
                    return;
                }
                var pos = posFromMouse(cm, e);
                if (!pos || clickInGutter(cm, e) || eventInWidget(cm.display, e)) {
                    return;
                }
                e_preventDefault(e);
                var word = cm.findWordAt(pos);
                extendSelection(cm.doc, word.anchor, word.head);
            }));
        } else {
            on(d.scroller, "dblclick", function(e) {
                return signalDOMEvent(cm, e) || e_preventDefault(e);
            });
        }
        // Some browsers fire contextmenu *after* opening the menu, at
        // which point we can't mess with it anymore. Context menu is
        // handled in onMouseDown for these browsers.
        on(d.scroller, "contextmenu", function(e) {
            return onContextMenu(cm, e);
        });
        on(d.input.getField(), "contextmenu", function(e) {
            if (!d.scroller.contains(e.target)) {
                onContextMenu(cm, e);
            }
        });
        // Used to suppress mouse event handling when a touch happens
        var touchFinished, prevTouch = {
            end: 0
        };
        function finishTouch() {
            if (d.activeTouch) {
                touchFinished = setTimeout(function() {
                    return d.activeTouch = null;
                }, 1000);
                prevTouch = d.activeTouch;
                prevTouch.end = +new Date;
            }
        }
        function isMouseLikeTouchEvent(e) {
            if (e.touches.length != 1) {
                return false;
            }
            var touch = e.touches[0];
            return touch.radiusX <= 1 && touch.radiusY <= 1;
        }
        function farAway(touch, other) {
            if (other.left == null) {
                return true;
            }
            var dx = other.left - touch.left, dy = other.top - touch.top;
            return dx * dx + dy * dy > 20 * 20;
        }
        on(d.scroller, "touchstart", function(e) {
            if (!signalDOMEvent(cm, e) && !isMouseLikeTouchEvent(e) && !clickInGutter(cm, e)) {
                d.input.ensurePolled();
                clearTimeout(touchFinished);
                var now = +new Date;
                d.activeTouch = {
                    start: now,
                    moved: false,
                    prev: now - prevTouch.end <= 300 ? prevTouch : null
                };
                if (e.touches.length == 1) {
                    d.activeTouch.left = e.touches[0].pageX;
                    d.activeTouch.top = e.touches[0].pageY;
                }
            }
        });
        on(d.scroller, "touchmove", function() {
            if (d.activeTouch) {
                d.activeTouch.moved = true;
            }
        });
        on(d.scroller, "touchend", function(e) {
            var touch = d.activeTouch;
            if (touch && !eventInWidget(d, e) && touch.left != null && !touch.moved && new Date - touch.start < 300) {
                var pos = cm.coordsChar(d.activeTouch, "page"), range;
                if (!touch.prev || farAway(touch, touch.prev)) {
                    range = new Range(pos, pos);
                } else if (!touch.prev.prev || farAway(touch, touch.prev.prev)) {
                    range = cm.findWordAt(pos);
                } else {
                    range = new Range(Pos(pos.line, 0), clipPos(cm.doc, Pos(pos.line + 1, 0)));
                }
                cm.setSelection(range.anchor, range.head);
                cm.focus();
                e_preventDefault(e);
            }
            finishTouch();
        });
        on(d.scroller, "touchcancel", finishTouch);
        // Sync scrolling between fake scrollbars and real scrollable
        // area, ensure viewport is updated when scrolling.
        on(d.scroller, "scroll", function() {
            if (d.scroller.clientHeight) {
                updateScrollTop(cm, d.scroller.scrollTop);
                setScrollLeft(cm, d.scroller.scrollLeft, true);
                signal(cm, "scroll", cm);
            }
        });
        // Listen to wheel events in order to try and update the viewport on time.
        on(d.scroller, "mousewheel", function(e) {
            return onScrollWheel(cm, e);
        });
        on(d.scroller, "DOMMouseScroll", function(e) {
            return onScrollWheel(cm, e);
        });
        // Prevent wrapper from ever scrolling
        on(d.wrapper, "scroll", function() {
            return d.wrapper.scrollTop = d.wrapper.scrollLeft = 0;
        });
        d.dragFunctions = {
            enter: function enter(e) {
                if (!signalDOMEvent(cm, e)) {
                    e_stop(e);
                }
            },
            over: function over(e) {
                if (!signalDOMEvent(cm, e)) {
                    onDragOver(cm, e);
                    e_stop(e);
                }
            },
            start: function start(e) {
                return onDragStart(cm, e);
            },
            drop: operation(cm, onDrop),
            leave: function leave(e) {
                if (!signalDOMEvent(cm, e)) {
                    clearDragCursor(cm);
                }
            }
        };
        var inp = d.input.getField();
        on(inp, "keyup", function(e) {
            return onKeyUp.call(cm, e);
        });
        on(inp, "keydown", operation(cm, onKeyDown));
        on(inp, "keypress", operation(cm, onKeyPress));
        on(inp, "focus", function(e) {
            return onFocus(cm, e);
        });
        on(inp, "blur", function(e) {
            return onBlur(cm, e);
        });
    };
    var indentLine = // Indent the given line. The how parameter can be "smart",
    // "add"/null, "subtract", or "prev". When aggressive is false
    // (typically set to true for forced single-line indents), empty
    // lines are not indented, and places where the mode returns Pass
    // are left alone.
    function indentLine(cm, n, how, aggressive) {
        var doc = cm.doc, state;
        if (how == null) {
            how = "add";
        }
        if (how == "smart") {
            // Fall back to "prev" when the mode doesn't have an indentation
            // method.
            if (!doc.mode.indent) {
                how = "prev";
            } else {
                state = getContextBefore(cm, n).state;
            }
        }
        var tabSize = cm.options.tabSize;
        var line = getLine(doc, n), curSpace = countColumn(line.text, null, tabSize);
        if (line.stateAfter) {
            line.stateAfter = null;
        }
        var curSpaceString = line.text.match(/^\s*/)[0], indentation;
        if (!aggressive && !/\S/.test(line.text)) {
            indentation = 0;
            how = "not";
        } else if (how == "smart") {
            indentation = doc.mode.indent(state, line.text.slice(curSpaceString.length), line.text);
            if (indentation == Pass || indentation > 150) {
                if (!aggressive) {
                    return;
                }
                how = "prev";
            }
        }
        if (how == "prev") {
            if (n > doc.first) {
                indentation = countColumn(getLine(doc, n - 1).text, null, tabSize);
            } else {
                indentation = 0;
            }
        } else if (how == "add") {
            indentation = curSpace + cm.options.indentUnit;
        } else if (how == "subtract") {
            indentation = curSpace - cm.options.indentUnit;
        } else if (typeof how == "number") {
            indentation = curSpace + how;
        }
        indentation = Math.max(0, indentation);
        var indentString = "", pos = 0;
        if (cm.options.indentWithTabs) {
            for(var i = Math.floor(indentation / tabSize); i; --i){
                pos += tabSize;
                indentString += "	";
            }
        }
        if (pos < indentation) {
            indentString += spaceStr(indentation - pos);
        }
        if (indentString != curSpaceString) {
            replaceRange(doc, indentString, Pos(n, 0), Pos(n, curSpaceString.length), "+input");
            line.stateAfter = null;
            return true;
        } else {
            // Ensure that, if the cursor was in the whitespace at the start
            // of the line, it is moved to the end of that space.
            for(var i$1 = 0; i$1 < doc.sel.ranges.length; i$1++){
                var range = doc.sel.ranges[i$1];
                if (range.head.line == n && range.head.ch < curSpaceString.length) {
                    var pos$1 = Pos(n, curSpaceString.length);
                    replaceOneSelection(doc, i$1, new Range(pos$1, pos$1));
                    break;
                }
            }
        }
    };
    var setLastCopied = function setLastCopied(newLastCopied) {
        lastCopied = newLastCopied;
    };
    var applyTextInput = function applyTextInput(cm, inserted, deleted, sel, origin) {
        var doc = cm.doc;
        cm.display.shift = false;
        if (!sel) {
            sel = doc.sel;
        }
        var recent = +new Date - 200;
        var paste = origin == "paste" || cm.state.pasteIncoming > recent;
        var textLines = splitLinesAuto(inserted), multiPaste = null;
        // When pasting N lines into N selections, insert one line per selection
        if (paste && sel.ranges.length > 1) {
            if (lastCopied && lastCopied.text.join("\n") == inserted) {
                if (sel.ranges.length % lastCopied.text.length == 0) {
                    multiPaste = [];
                    for(var i = 0; i < lastCopied.text.length; i++){
                        multiPaste.push(doc.splitLines(lastCopied.text[i]));
                    }
                }
            } else if (textLines.length == sel.ranges.length && cm.options.pasteLinesPerSelection) {
                multiPaste = map(textLines, function(l) {
                    return [
                        l
                    ];
                });
            }
        }
        var updateInput = cm.curOp.updateInput;
        // Normal behavior is to insert the new text into every selection
        for(var i$1 = sel.ranges.length - 1; i$1 >= 0; i$1--){
            var range = sel.ranges[i$1];
            var from = range.from(), to = range.to();
            if (range.empty()) {
                if (deleted && deleted > 0) {
                    from = Pos(from.line, from.ch - deleted);
                } else if (cm.state.overwrite && !paste) {
                    to = Pos(to.line, Math.min(getLine(doc, to.line).text.length, to.ch + lst(textLines).length));
                } else if (paste && lastCopied && lastCopied.lineWise && lastCopied.text.join("\n") == textLines.join("\n")) {
                    from = to = Pos(from.line, 0);
                }
            }
            var changeEvent = {
                from: from,
                to: to,
                text: multiPaste ? multiPaste[i$1 % multiPaste.length] : textLines,
                origin: origin || (paste ? "paste" : cm.state.cutIncoming > recent ? "cut" : "+input")
            };
            makeChange(cm.doc, changeEvent);
            signalLater(cm, "inputRead", cm, changeEvent);
        }
        if (inserted && !paste) {
            triggerElectric(cm, inserted);
        }
        ensureCursorVisible(cm);
        if (cm.curOp.updateInput < 2) {
            cm.curOp.updateInput = updateInput;
        }
        cm.curOp.typing = true;
        cm.state.pasteIncoming = cm.state.cutIncoming = -1;
    };
    var handlePaste = function handlePaste(e, cm) {
        var pasted = e.clipboardData && e.clipboardData.getData("Text");
        if (pasted) {
            e.preventDefault();
            if (!cm.isReadOnly() && !cm.options.disableInput) {
                runInOp(cm, function() {
                    return applyTextInput(cm, pasted, 0, null, "paste");
                });
            }
            return true;
        }
    };
    var triggerElectric = function triggerElectric(cm, inserted) {
        // When an 'electric' character is inserted, immediately trigger a reindent
        if (!cm.options.electricChars || !cm.options.smartIndent) {
            return;
        }
        var sel = cm.doc.sel;
        for(var i = sel.ranges.length - 1; i >= 0; i--){
            var range = sel.ranges[i];
            if (range.head.ch > 100 || i && sel.ranges[i - 1].head.line == range.head.line) {
                continue;
            }
            var mode = cm.getModeAt(range.head);
            var indented = false;
            if (mode.electricChars) {
                for(var j = 0; j < mode.electricChars.length; j++){
                    if (inserted.indexOf(mode.electricChars.charAt(j)) > -1) {
                        indented = indentLine(cm, range.head.line, "smart");
                        break;
                    }
                }
            } else if (mode.electricInput) {
                if (mode.electricInput.test(getLine(cm.doc, range.head.line).text.slice(0, range.head.ch))) {
                    indented = indentLine(cm, range.head.line, "smart");
                }
            }
            if (indented) {
                signalLater(cm, "electricInput", cm, range.head.line);
            }
        }
    };
    var copyableRanges = function copyableRanges(cm) {
        var text = [], ranges = [];
        for(var i = 0; i < cm.doc.sel.ranges.length; i++){
            var line = cm.doc.sel.ranges[i].head.line;
            var lineRange = {
                anchor: Pos(line, 0),
                head: Pos(line + 1, 0)
            };
            ranges.push(lineRange);
            text.push(cm.getRange(lineRange.anchor, lineRange.head));
        }
        return {
            text: text,
            ranges: ranges
        };
    };
    var disableBrowserMagic = function disableBrowserMagic(field, spellcheck, autocorrect, autocapitalize) {
        field.setAttribute("autocorrect", autocorrect ? "" : "off");
        field.setAttribute("autocapitalize", autocapitalize ? "" : "off");
        field.setAttribute("spellcheck", !!spellcheck);
    };
    var hiddenTextarea = function hiddenTextarea() {
        var te = elt("textarea", null, null, "position: absolute; bottom: -1em; padding: 0; width: 1px; height: 1em; min-height: 1em; outline: none");
        var div = elt("div", [
            te
        ], null, "overflow: hidden; position: relative; width: 3px; height: 0px;");
        // The textarea is kept positioned near the cursor to prevent the
        // fact that it'll be scrolled into view on input from scrolling
        // our fake cursor out of view. On webkit, when wrap=off, paste is
        // very slow. So make the area wide instead.
        if (webkit) {
            te.style.width = "1000px";
        } else {
            te.setAttribute("wrap", "off");
        }
        // If border: 0; -- iOS fails to open keyboard (issue #1287)
        if (ios) {
            te.style.border = "1px solid black";
        }
        disableBrowserMagic(te);
        return div;
    };
    var addEditorMethods = // The publicly visible API. Note that methodOp(f) means
    // 'wrap f in an operation, performed on its `this` parameter'.
    // This is not the complete set of editor methods. Most of the
    // methods defined on the Doc type are also injected into
    // CodeMirror.prototype, for backwards compatibility and
    // convenience.
    function addEditorMethods(CodeMirror) {
        var optionHandlers = CodeMirror.optionHandlers;
        var helpers = CodeMirror.helpers = {};
        CodeMirror.prototype = {
            constructor: CodeMirror,
            focus: function focus() {
                window.focus();
                this.display.input.focus();
            },
            setOption: function setOption(option, value) {
                var options = this.options, old = options[option];
                if (options[option] == value && option != "mode") {
                    return;
                }
                options[option] = value;
                if (optionHandlers.hasOwnProperty(option)) {
                    operation(this, optionHandlers[option])(this, value, old);
                }
                signal(this, "optionChange", this, option);
            },
            getOption: function getOption(option) {
                return this.options[option];
            },
            getDoc: function getDoc() {
                return this.doc;
            },
            addKeyMap: function addKeyMap(map, bottom) {
                this.state.keyMaps[bottom ? "push" : "unshift"](getKeyMap(map));
            },
            removeKeyMap: function removeKeyMap(map) {
                var maps = this.state.keyMaps;
                for(var i = 0; i < maps.length; ++i){
                    if (maps[i] == map || maps[i].name == map) {
                        maps.splice(i, 1);
                        return true;
                    }
                }
            },
            addOverlay: methodOp(function(spec, options) {
                var mode = spec.token ? spec : CodeMirror.getMode(this.options, spec);
                if (mode.startState) {
                    throw new Error("Overlays may not be stateful.");
                }
                insertSorted(this.state.overlays, {
                    mode: mode,
                    modeSpec: spec,
                    opaque: options && options.opaque,
                    priority: options && options.priority || 0
                }, function(overlay) {
                    return overlay.priority;
                });
                this.state.modeGen++;
                regChange(this);
            }),
            removeOverlay: methodOp(function(spec) {
                var overlays = this.state.overlays;
                for(var i = 0; i < overlays.length; ++i){
                    var cur = overlays[i].modeSpec;
                    if (cur == spec || typeof spec == "string" && cur.name == spec) {
                        overlays.splice(i, 1);
                        this.state.modeGen++;
                        regChange(this);
                        return;
                    }
                }
            }),
            indentLine: methodOp(function(n, dir, aggressive) {
                if (typeof dir != "string" && typeof dir != "number") {
                    if (dir == null) {
                        dir = this.options.smartIndent ? "smart" : "prev";
                    } else {
                        dir = dir ? "add" : "subtract";
                    }
                }
                if (isLine(this.doc, n)) {
                    indentLine(this, n, dir, aggressive);
                }
            }),
            indentSelection: methodOp(function(how) {
                var ranges = this.doc.sel.ranges, end = -1;
                for(var i = 0; i < ranges.length; i++){
                    var range = ranges[i];
                    if (!range.empty()) {
                        var from = range.from(), to = range.to();
                        var start = Math.max(end, from.line);
                        end = Math.min(this.lastLine(), to.line - (to.ch ? 0 : 1)) + 1;
                        for(var j = start; j < end; ++j){
                            indentLine(this, j, how);
                        }
                        var newRanges = this.doc.sel.ranges;
                        if (from.ch == 0 && ranges.length == newRanges.length && newRanges[i].from().ch > 0) {
                            replaceOneSelection(this.doc, i, new Range(from, newRanges[i].to()), sel_dontScroll);
                        }
                    } else if (range.head.line > end) {
                        indentLine(this, range.head.line, how, true);
                        end = range.head.line;
                        if (i == this.doc.sel.primIndex) {
                            ensureCursorVisible(this);
                        }
                    }
                }
            }),
            // Fetch the parser token for a given character. Useful for hacks
            // that want to inspect the mode state (say, for completion).
            getTokenAt: function getTokenAt(pos, precise) {
                return takeToken(this, pos, precise);
            },
            getLineTokens: function getLineTokens(line, precise) {
                return takeToken(this, Pos(line), precise, true);
            },
            getTokenTypeAt: function getTokenTypeAt(pos) {
                pos = clipPos(this.doc, pos);
                var styles = getLineStyles(this, getLine(this.doc, pos.line));
                var before = 0, after = (styles.length - 1) / 2, ch = pos.ch;
                var type;
                if (ch == 0) {
                    type = styles[2];
                } else {
                    for(;;){
                        var mid = before + after >> 1;
                        if ((mid ? styles[mid * 2 - 1] : 0) >= ch) {
                            after = mid;
                        } else if (styles[mid * 2 + 1] < ch) {
                            before = mid + 1;
                        } else {
                            type = styles[mid * 2 + 2];
                            break;
                        }
                    }
                }
                var cut = type ? type.indexOf("overlay ") : -1;
                return cut < 0 ? type : cut == 0 ? null : type.slice(0, cut - 1);
            },
            getModeAt: function getModeAt(pos) {
                var mode = this.doc.mode;
                if (!mode.innerMode) {
                    return mode;
                }
                return CodeMirror.innerMode(mode, this.getTokenAt(pos).state).mode;
            },
            getHelper: function getHelper(pos, type) {
                return this.getHelpers(pos, type)[0];
            },
            getHelpers: function getHelpers(pos, type) {
                var found = [];
                if (!helpers.hasOwnProperty(type)) {
                    return found;
                }
                var help = helpers[type], mode = this.getModeAt(pos);
                if (typeof mode[type] == "string") {
                    if (help[mode[type]]) {
                        found.push(help[mode[type]]);
                    }
                } else if (mode[type]) {
                    for(var i = 0; i < mode[type].length; i++){
                        var val = help[mode[type][i]];
                        if (val) {
                            found.push(val);
                        }
                    }
                } else if (mode.helperType && help[mode.helperType]) {
                    found.push(help[mode.helperType]);
                } else if (help[mode.name]) {
                    found.push(help[mode.name]);
                }
                for(var i$1 = 0; i$1 < help._global.length; i$1++){
                    var cur = help._global[i$1];
                    if (cur.pred(mode, this) && indexOf(found, cur.val) == -1) {
                        found.push(cur.val);
                    }
                }
                return found;
            },
            getStateAfter: function getStateAfter(line, precise) {
                var doc = this.doc;
                line = clipLine(doc, line == null ? doc.first + doc.size - 1 : line);
                return getContextBefore(this, line + 1, precise).state;
            },
            cursorCoords: function cursorCoords1(start, mode) {
                var pos, range = this.doc.sel.primary();
                if (start == null) {
                    pos = range.head;
                } else if (typeof start == "object") {
                    pos = clipPos(this.doc, start);
                } else {
                    pos = start ? range.from() : range.to();
                }
                return cursorCoords(this, pos, mode || "page");
            },
            charCoords: function charCoords1(pos, mode) {
                return charCoords(this, clipPos(this.doc, pos), mode || "page");
            },
            coordsChar: function coordsChar1(coords, mode) {
                coords = fromCoordSystem(this, coords, mode || "page");
                return coordsChar(this, coords.left, coords.top);
            },
            lineAtHeight: function lineAtHeight1(height, mode) {
                height = fromCoordSystem(this, {
                    top: height,
                    left: 0
                }, mode || "page").top;
                return lineAtHeight(this.doc, height + this.display.viewOffset);
            },
            heightAtLine: function heightAtLine1(line, mode, includeWidgets) {
                var end = false, lineObj;
                if (typeof line == "number") {
                    var last = this.doc.first + this.doc.size - 1;
                    if (line < this.doc.first) {
                        line = this.doc.first;
                    } else if (line > last) {
                        line = last;
                        end = true;
                    }
                    lineObj = getLine(this.doc, line);
                } else {
                    lineObj = line;
                }
                return intoCoordSystem(this, lineObj, {
                    top: 0,
                    left: 0
                }, mode || "page", includeWidgets || end).top + (end ? this.doc.height - heightAtLine(lineObj) : 0);
            },
            defaultTextHeight: function defaultTextHeight() {
                return textHeight(this.display);
            },
            defaultCharWidth: function defaultCharWidth() {
                return charWidth(this.display);
            },
            getViewport: function getViewport() {
                return {
                    from: this.display.viewFrom,
                    to: this.display.viewTo
                };
            },
            addWidget: function addWidget(pos, node, scroll, vert, horiz) {
                var display = this.display;
                pos = cursorCoords(this, clipPos(this.doc, pos));
                var top = pos.bottom, left = pos.left;
                node.style.position = "absolute";
                node.setAttribute("cm-ignore-events", "true");
                this.display.input.setUneditable(node);
                display.sizer.appendChild(node);
                if (vert == "over") {
                    top = pos.top;
                } else if (vert == "above" || vert == "near") {
                    var vspace = Math.max(display.wrapper.clientHeight, this.doc.height), hspace = Math.max(display.sizer.clientWidth, display.lineSpace.clientWidth);
                    // Default to positioning above (if specified and possible); otherwise default to positioning below
                    if ((vert == "above" || pos.bottom + node.offsetHeight > vspace) && pos.top > node.offsetHeight) {
                        top = pos.top - node.offsetHeight;
                    } else if (pos.bottom + node.offsetHeight <= vspace) {
                        top = pos.bottom;
                    }
                    if (left + node.offsetWidth > hspace) {
                        left = hspace - node.offsetWidth;
                    }
                }
                node.style.top = top + "px";
                node.style.left = node.style.right = "";
                if (horiz == "right") {
                    left = display.sizer.clientWidth - node.offsetWidth;
                    node.style.right = "0px";
                } else {
                    if (horiz == "left") {
                        left = 0;
                    } else if (horiz == "middle") {
                        left = (display.sizer.clientWidth - node.offsetWidth) / 2;
                    }
                    node.style.left = left + "px";
                }
                if (scroll) {
                    scrollIntoView(this, {
                        left: left,
                        top: top,
                        right: left + node.offsetWidth,
                        bottom: top + node.offsetHeight
                    });
                }
            },
            triggerOnKeyDown: methodOp(onKeyDown),
            triggerOnKeyPress: methodOp(onKeyPress),
            triggerOnKeyUp: onKeyUp,
            triggerOnMouseDown: methodOp(onMouseDown),
            execCommand: function execCommand(cmd) {
                if (commands.hasOwnProperty(cmd)) {
                    return commands[cmd].call(null, this);
                }
            },
            triggerElectric: methodOp(function(text) {
                triggerElectric(this, text);
            }),
            findPosH: function findPosH1(from, amount, unit, visually) {
                var dir = 1;
                if (amount < 0) {
                    dir = -1;
                    amount = -amount;
                }
                var cur = clipPos(this.doc, from);
                for(var i = 0; i < amount; ++i){
                    cur = findPosH(this.doc, cur, dir, unit, visually);
                    if (cur.hitSide) {
                        break;
                    }
                }
                return cur;
            },
            moveH: methodOp(function(dir, unit) {
                var this$1 = this;
                this.extendSelectionsBy(function(range) {
                    if (this$1.display.shift || this$1.doc.extend || range.empty()) {
                        return findPosH(this$1.doc, range.head, dir, unit, this$1.options.rtlMoveVisually);
                    } else {
                        return dir < 0 ? range.from() : range.to();
                    }
                }, sel_move);
            }),
            deleteH: methodOp(function(dir, unit) {
                var sel = this.doc.sel, doc = this.doc;
                if (sel.somethingSelected()) {
                    doc.replaceSelection("", null, "+delete");
                } else {
                    deleteNearSelection(this, function(range) {
                        var other = findPosH(doc, range.head, dir, unit, false);
                        return dir < 0 ? {
                            from: other,
                            to: range.head
                        } : {
                            from: range.head,
                            to: other
                        };
                    });
                }
            }),
            findPosV: function findPosV1(from, amount, unit, goalColumn) {
                var dir = 1, x = goalColumn;
                if (amount < 0) {
                    dir = -1;
                    amount = -amount;
                }
                var cur = clipPos(this.doc, from);
                for(var i = 0; i < amount; ++i){
                    var coords = cursorCoords(this, cur, "div");
                    if (x == null) {
                        x = coords.left;
                    } else {
                        coords.left = x;
                    }
                    cur = findPosV(this, coords, dir, unit);
                    if (cur.hitSide) {
                        break;
                    }
                }
                return cur;
            },
            moveV: methodOp(function(dir, unit) {
                var this$1 = this;
                var doc = this.doc, goals = [];
                var collapse = !this.display.shift && !doc.extend && doc.sel.somethingSelected();
                doc.extendSelectionsBy(function(range) {
                    if (collapse) {
                        return dir < 0 ? range.from() : range.to();
                    }
                    var headPos = cursorCoords(this$1, range.head, "div");
                    if (range.goalColumn != null) {
                        headPos.left = range.goalColumn;
                    }
                    goals.push(headPos.left);
                    var pos = findPosV(this$1, headPos, dir, unit);
                    if (unit == "page" && range == doc.sel.primary()) {
                        addToScrollTop(this$1, charCoords(this$1, pos, "div").top - headPos.top);
                    }
                    return pos;
                }, sel_move);
                if (goals.length) {
                    for(var i = 0; i < doc.sel.ranges.length; i++){
                        doc.sel.ranges[i].goalColumn = goals[i];
                    }
                }
            }),
            // Find the word at the given position (as returned by coordsChar).
            findWordAt: function findWordAt(pos) {
                var doc = this.doc, line = getLine(doc, pos.line).text;
                var start = pos.ch, end = pos.ch;
                if (line) {
                    var helper = this.getHelper(pos, "wordChars");
                    if ((pos.sticky == "before" || end == line.length) && start) {
                        --start;
                    } else {
                        ++end;
                    }
                    var startChar = line.charAt(start);
                    var check = isWordChar(startChar, helper) ? function check(ch) {
                        return isWordChar(ch, helper);
                    } : /\s/.test(startChar) ? function(ch) {
                        return /\s/.test(ch);
                    } : function(ch) {
                        return !/\s/.test(ch) && !isWordChar(ch);
                    };
                    while(start > 0 && check(line.charAt(start - 1))){
                        --start;
                    }
                    while(end < line.length && check(line.charAt(end))){
                        ++end;
                    }
                }
                return new Range(Pos(pos.line, start), Pos(pos.line, end));
            },
            toggleOverwrite: function toggleOverwrite(value) {
                if (value != null && value == this.state.overwrite) {
                    return;
                }
                if (this.state.overwrite = !this.state.overwrite) {
                    addClass(this.display.cursorDiv, "CodeMirror-overwrite");
                } else {
                    rmClass(this.display.cursorDiv, "CodeMirror-overwrite");
                }
                signal(this, "overwriteToggle", this, this.state.overwrite);
            },
            hasFocus: function hasFocus() {
                return this.display.input.getField() == activeElt();
            },
            isReadOnly: function isReadOnly() {
                return !!(this.options.readOnly || this.doc.cantEdit);
            },
            scrollTo: methodOp(function(x, y) {
                scrollToCoords(this, x, y);
            }),
            getScrollInfo: function getScrollInfo() {
                var scroller = this.display.scroller;
                return {
                    left: scroller.scrollLeft,
                    top: scroller.scrollTop,
                    height: scroller.scrollHeight - scrollGap(this) - this.display.barHeight,
                    width: scroller.scrollWidth - scrollGap(this) - this.display.barWidth,
                    clientHeight: displayHeight(this),
                    clientWidth: displayWidth(this)
                };
            },
            scrollIntoView: methodOp(function(range, margin) {
                if (range == null) {
                    range = {
                        from: this.doc.sel.primary().head,
                        to: null
                    };
                    if (margin == null) {
                        margin = this.options.cursorScrollMargin;
                    }
                } else if (typeof range == "number") {
                    range = {
                        from: Pos(range, 0),
                        to: null
                    };
                } else if (range.from == null) {
                    range = {
                        from: range,
                        to: null
                    };
                }
                if (!range.to) {
                    range.to = range.from;
                }
                range.margin = margin || 0;
                if (range.from.line != null) {
                    scrollToRange(this, range);
                } else {
                    scrollToCoordsRange(this, range.from, range.to, range.margin);
                }
            }),
            setSize: methodOp(function(width, height) {
                var this$1 = this;
                var interpret = function interpret(val) {
                    return typeof val == "number" || /^\d+$/.test(String(val)) ? val + "px" : val;
                };
                if (width != null) {
                    this.display.wrapper.style.width = interpret(width);
                }
                if (height != null) {
                    this.display.wrapper.style.height = interpret(height);
                }
                if (this.options.lineWrapping) {
                    clearLineMeasurementCache(this);
                }
                var lineNo = this.display.viewFrom;
                this.doc.iter(lineNo, this.display.viewTo, function(line) {
                    if (line.widgets) {
                        for(var i = 0; i < line.widgets.length; i++){
                            if (line.widgets[i].noHScroll) {
                                regLineChange(this$1, lineNo, "widget");
                                break;
                            }
                        }
                    }
                    ++lineNo;
                });
                this.curOp.forceUpdate = true;
                signal(this, "refresh", this);
            }),
            operation: function operation(f) {
                return runInOp(this, f);
            },
            startOperation: function startOperation1() {
                return startOperation(this);
            },
            endOperation: function endOperation1() {
                return endOperation(this);
            },
            refresh: methodOp(function() {
                var oldHeight = this.display.cachedTextHeight;
                regChange(this);
                this.curOp.forceUpdate = true;
                clearCaches(this);
                scrollToCoords(this, this.doc.scrollLeft, this.doc.scrollTop);
                updateGutterSpace(this.display);
                if (oldHeight == null || Math.abs(oldHeight - textHeight(this.display)) > .5 || this.options.lineWrapping) {
                    estimateLineHeights(this);
                }
                signal(this, "refresh", this);
            }),
            swapDoc: methodOp(function(doc) {
                var old = this.doc;
                old.cm = null;
                // Cancel the current text selection if any (#5821)
                if (this.state.selectingText) {
                    this.state.selectingText();
                }
                attachDoc(this, doc);
                clearCaches(this);
                this.display.input.reset();
                scrollToCoords(this, doc.scrollLeft, doc.scrollTop);
                this.curOp.forceScroll = true;
                signalLater(this, "swapDoc", this, old);
                return old;
            }),
            phrase: function phrase(phraseText) {
                var phrases = this.options.phrases;
                return phrases && Object.prototype.hasOwnProperty.call(phrases, phraseText) ? phrases[phraseText] : phraseText;
            },
            getInputField: function getInputField() {
                return this.display.input.getField();
            },
            getWrapperElement: function getWrapperElement() {
                return this.display.wrapper;
            },
            getScrollerElement: function getScrollerElement() {
                return this.display.scroller;
            },
            getGutterElement: function getGutterElement() {
                return this.display.gutters;
            }
        };
        eventMixin(CodeMirror);
        CodeMirror.registerHelper = function(type, name, value) {
            if (!helpers.hasOwnProperty(type)) {
                helpers[type] = CodeMirror[type] = {
                    _global: []
                };
            }
            helpers[type][name] = value;
        };
        CodeMirror.registerGlobalHelper = function(type, name, predicate, value) {
            CodeMirror.registerHelper(type, name, value);
            helpers[type]._global.push({
                pred: predicate,
                val: value
            });
        };
    };
    var findPosH = // Used for horizontal relative motion. Dir is -1 or 1 (left or
    // right), unit can be "codepoint", "char", "column" (like char, but
    // doesn't cross line boundaries), "word" (across next word), or
    // "group" (to the start of next group of word or
    // non-word-non-whitespace chars). The visually param controls
    // whether, in right-to-left text, direction 1 means to move towards
    // the next index in the string, or towards the character to the right
    // of the current position. The resulting position will have a
    // hitSide=true property if it reached the end of the document.
    function findPosH(doc, pos, dir, unit, visually) {
        var oldPos = pos;
        var origDir = dir;
        var lineObj = getLine(doc, pos.line);
        var lineDir = visually && doc.direction == "rtl" ? -dir : dir;
        function findNextLine() {
            var l = pos.line + lineDir;
            if (l < doc.first || l >= doc.first + doc.size) {
                return false;
            }
            pos = new Pos(l, pos.ch, pos.sticky);
            return lineObj = getLine(doc, l);
        }
        function moveOnce(boundToLine) {
            var next;
            if (unit == "codepoint") {
                var ch = lineObj.text.charCodeAt(pos.ch + (dir > 0 ? 0 : -1));
                if (isNaN(ch)) {
                    next = null;
                } else {
                    var astral = dir > 0 ? ch >= 0xD800 && ch < 0xDC00 : ch >= 0xDC00 && ch < 0xDFFF;
                    next = new Pos(pos.line, Math.max(0, Math.min(lineObj.text.length, pos.ch + dir * (astral ? 2 : 1))), -dir);
                }
            } else if (visually) {
                next = moveVisually(doc.cm, lineObj, pos, dir);
            } else {
                next = moveLogically(lineObj, pos, dir);
            }
            if (next == null) {
                if (!boundToLine && findNextLine()) {
                    pos = endOfLine(visually, doc.cm, lineObj, pos.line, lineDir);
                } else {
                    return false;
                }
            } else {
                pos = next;
            }
            return true;
        }
        if (unit == "char" || unit == "codepoint") {
            moveOnce();
        } else if (unit == "column") {
            moveOnce(true);
        } else if (unit == "word" || unit == "group") {
            var sawType = null, group = unit == "group";
            var helper = doc.cm && doc.cm.getHelper(pos, "wordChars");
            for(var first = true;; first = false){
                if (dir < 0 && !moveOnce(!first)) {
                    break;
                }
                var cur = lineObj.text.charAt(pos.ch) || "\n";
                var type = isWordChar(cur, helper) ? "w" : group && cur == "\n" ? "n" : !group || /\s/.test(cur) ? null : "p";
                if (group && !first && !type) {
                    type = "s";
                }
                if (sawType && sawType != type) {
                    if (dir < 0) {
                        dir = 1;
                        moveOnce();
                        pos.sticky = "after";
                    }
                    break;
                }
                if (type) {
                    sawType = type;
                }
                if (dir > 0 && !moveOnce(!first)) {
                    break;
                }
            }
        }
        var result = skipAtomic(doc, pos, oldPos, origDir, true);
        if (equalCursorPos(oldPos, result)) {
            result.hitSide = true;
        }
        return result;
    };
    var findPosV = // For relative vertical movement. Dir may be -1 or 1. Unit can be
    // "page" or "line". The resulting position will have a hitSide=true
    // property if it reached the end of the document.
    function findPosV(cm, pos, dir, unit) {
        var doc = cm.doc, x = pos.left, y;
        if (unit == "page") {
            var pageSize = Math.min(cm.display.wrapper.clientHeight, window.innerHeight || document.documentElement.clientHeight);
            var moveAmount = Math.max(pageSize - .5 * textHeight(cm.display), 3);
            y = (dir > 0 ? pos.bottom : pos.top) + dir * moveAmount;
        } else if (unit == "line") {
            y = dir > 0 ? pos.bottom + 3 : pos.top - 3;
        }
        var target;
        for(;;){
            target = coordsChar(cm, x, y);
            if (!target.outside) {
                break;
            }
            if (dir < 0 ? y <= 0 : y >= doc.height) {
                target.hitSide = true;
                break;
            }
            y += dir * 5;
        }
        return target;
    };
    var posToDOM = function posToDOM(cm, pos) {
        var view = findViewForLine(cm, pos.line);
        if (!view || view.hidden) {
            return null;
        }
        var line = getLine(cm.doc, pos.line);
        var info = mapFromLineView(view, line, pos.line);
        var order = getOrder(line, cm.doc.direction), side = "left";
        if (order) {
            var partPos = getBidiPartAt(order, pos.ch);
            side = partPos % 2 ? "right" : "left";
        }
        var result = nodeAndOffsetInLineMap(info.map, pos.ch, side);
        result.offset = result.collapse == "right" ? result.end : result.start;
        return result;
    };
    var isInGutter = function isInGutter(node) {
        for(var scan = node; scan; scan = scan.parentNode){
            if (/CodeMirror-gutter-wrapper/.test(scan.className)) {
                return true;
            }
        }
        return false;
    };
    var badPos = function badPos(pos, bad) {
        if (bad) {
            pos.bad = true;
        }
        return pos;
    };
    var domTextBetween = function domTextBetween(cm, from, to, fromLine, toLine) {
        var text = "", closing = false, lineSep = cm.doc.lineSeparator(), extraLinebreak = false;
        function recognizeMarker(id) {
            return function(marker) {
                return marker.id == id;
            };
        }
        function close() {
            if (closing) {
                text += lineSep;
                if (extraLinebreak) {
                    text += lineSep;
                }
                closing = extraLinebreak = false;
            }
        }
        function addText(str) {
            if (str) {
                close();
                text += str;
            }
        }
        function walk(node) {
            if (node.nodeType == 1) {
                var cmText = node.getAttribute("cm-text");
                if (cmText) {
                    addText(cmText);
                    return;
                }
                var markerID = node.getAttribute("cm-marker"), range;
                if (markerID) {
                    var found = cm.findMarks(Pos(fromLine, 0), Pos(toLine + 1, 0), recognizeMarker(+markerID));
                    if (found.length && (range = found[0].find(0))) {
                        addText(getBetween(cm.doc, range.from, range.to).join(lineSep));
                    }
                    return;
                }
                if (node.getAttribute("contenteditable") == "false") {
                    return;
                }
                var isBlock = /^(pre|div|p|li|table|br)$/i.test(node.nodeName);
                if (!/^br$/i.test(node.nodeName) && node.textContent.length == 0) {
                    return;
                }
                if (isBlock) {
                    close();
                }
                for(var i = 0; i < node.childNodes.length; i++){
                    walk(node.childNodes[i]);
                }
                if (/^(pre|p)$/i.test(node.nodeName)) {
                    extraLinebreak = true;
                }
                if (isBlock) {
                    closing = true;
                }
            } else if (node.nodeType == 3) {
                addText(node.nodeValue.replace(/\u200b/g, "").replace(/\u00a0/g, " "));
            }
        }
        for(;;){
            walk(from);
            if (from == to) {
                break;
            }
            from = from.nextSibling;
            extraLinebreak = false;
        }
        return text;
    };
    var domToPos = function domToPos(cm, node, offset) {
        var lineNode;
        if (node == cm.display.lineDiv) {
            lineNode = cm.display.lineDiv.childNodes[offset];
            if (!lineNode) {
                return badPos(cm.clipPos(Pos(cm.display.viewTo - 1)), true);
            }
            node = null;
            offset = 0;
        } else {
            for(lineNode = node;; lineNode = lineNode.parentNode){
                if (!lineNode || lineNode == cm.display.lineDiv) {
                    return null;
                }
                if (lineNode.parentNode && lineNode.parentNode == cm.display.lineDiv) {
                    break;
                }
            }
        }
        for(var i = 0; i < cm.display.view.length; i++){
            var lineView = cm.display.view[i];
            if (lineView.node == lineNode) {
                return locateNodeInLineView(lineView, node, offset);
            }
        }
    };
    var locateNodeInLineView = function locateNodeInLineView(lineView, node, offset) {
        var wrapper = lineView.text.firstChild, bad = false;
        if (!node || !contains(wrapper, node)) {
            return badPos(Pos(lineNo(lineView.line), 0), true);
        }
        if (node == wrapper) {
            bad = true;
            node = wrapper.childNodes[offset];
            offset = 0;
            if (!node) {
                var line = lineView.rest ? lst(lineView.rest) : lineView.line;
                return badPos(Pos(lineNo(line), line.text.length), bad);
            }
        }
        var textNode = node.nodeType == 3 ? node : null, topNode = node;
        if (!textNode && node.childNodes.length == 1 && node.firstChild.nodeType == 3) {
            textNode = node.firstChild;
            if (offset) {
                offset = textNode.nodeValue.length;
            }
        }
        while(topNode.parentNode != wrapper){
            topNode = topNode.parentNode;
        }
        var measure = lineView.measure, maps = measure.maps;
        function find(textNode, topNode, offset) {
            for(var i = -1; i < (maps ? maps.length : 0); i++){
                var map = i < 0 ? measure.map : maps[i];
                for(var j = 0; j < map.length; j += 3){
                    var curNode = map[j + 2];
                    if (curNode == textNode || curNode == topNode) {
                        var line = lineNo(i < 0 ? lineView.line : lineView.rest[i]);
                        var ch = map[j] + offset;
                        if (offset < 0 || curNode != textNode) {
                            ch = map[j + (offset ? 1 : 0)];
                        }
                        return Pos(line, ch);
                    }
                }
            }
        }
        var found = find(textNode, topNode, offset);
        if (found) {
            return badPos(found, bad);
        }
        // FIXME this is all really shaky. might handle the few cases it needs to handle, but likely to cause problems
        for(var after = topNode.nextSibling, dist = textNode ? textNode.nodeValue.length - offset : 0; after; after = after.nextSibling){
            found = find(after, after.firstChild, 0);
            if (found) {
                return badPos(Pos(found.line, found.ch - dist), bad);
            } else {
                dist += after.textContent.length;
            }
        }
        for(var before = topNode.previousSibling, dist$1 = offset; before; before = before.previousSibling){
            found = find(before, before.firstChild, -1);
            if (found) {
                return badPos(Pos(found.line, found.ch + dist$1), bad);
            } else {
                dist$1 += before.textContent.length;
            }
        }
    };
    var fromTextArea = function fromTextArea(textarea, options) {
        options = options ? copyObj(options) : {};
        options.value = textarea.value;
        if (!options.tabindex && textarea.tabIndex) {
            options.tabindex = textarea.tabIndex;
        }
        if (!options.placeholder && textarea.placeholder) {
            options.placeholder = textarea.placeholder;
        }
        // Set autofocus to true if this textarea is focused, or if it has
        // autofocus and no other element is focused.
        if (options.autofocus == null) {
            var hasFocus = activeElt();
            options.autofocus = hasFocus == textarea || textarea.getAttribute("autofocus") != null && hasFocus == document.body;
        }
        function save() {
            textarea.value = cm.getValue();
        }
        var realSubmit;
        if (textarea.form) {
            on(textarea.form, "submit", save);
            // Deplorable hack to make the submit method do the right thing.
            if (!options.leaveSubmitMethodAlone) {
                var form = textarea.form;
                realSubmit = form.submit;
                try {
                    var wrappedSubmit = form.submit = function wrappedSubmit1() {
                        save();
                        form.submit = realSubmit;
                        form.submit();
                        form.submit = wrappedSubmit;
                    };
                } catch (e) {}
            }
        }
        options.finishInit = function(cm) {
            cm.save = save;
            cm.getTextArea = function() {
                return textarea;
            };
            cm.toTextArea = function() {
                cm.toTextArea = isNaN; // Prevent this from being ran twice
                save();
                textarea.parentNode.removeChild(cm.getWrapperElement());
                textarea.style.display = "";
                if (textarea.form) {
                    off(textarea.form, "submit", save);
                    if (!options.leaveSubmitMethodAlone && typeof textarea.form.submit == "function") {
                        textarea.form.submit = realSubmit;
                    }
                }
            };
        };
        textarea.style.display = "none";
        var cm = CodeMirror(function(node) {
            return textarea.parentNode.insertBefore(node, textarea.nextSibling);
        }, options);
        return cm;
    };
    var addLegacyProps = function addLegacyProps(CodeMirror) {
        CodeMirror.off = off;
        CodeMirror.on = on;
        CodeMirror.wheelEventPixels = wheelEventPixels;
        CodeMirror.Doc = Doc;
        CodeMirror.splitLines = splitLinesAuto;
        CodeMirror.countColumn = countColumn;
        CodeMirror.findColumn = findColumn;
        CodeMirror.isWordChar = isWordCharBasic;
        CodeMirror.Pass = Pass;
        CodeMirror.signal = signal;
        CodeMirror.Line = Line;
        CodeMirror.changeEnd = changeEnd;
        CodeMirror.scrollbarModel = scrollbarModel;
        CodeMirror.Pos = Pos;
        CodeMirror.cmpPos = cmp;
        CodeMirror.modes = modes;
        CodeMirror.mimeModes = mimeModes;
        CodeMirror.resolveMode = resolveMode;
        CodeMirror.getMode = getMode;
        CodeMirror.modeExtensions = modeExtensions;
        CodeMirror.extendMode = extendMode;
        CodeMirror.copyState = copyState;
        CodeMirror.startState = startState;
        CodeMirror.innerMode = innerMode;
        CodeMirror.commands = commands;
        CodeMirror.keyMap = keyMap;
        CodeMirror.keyName = keyName;
        CodeMirror.isModifierKey = isModifierKey;
        CodeMirror.lookupKey = lookupKey;
        CodeMirror.normalizeKeyMap = normalizeKeyMap;
        CodeMirror.StringStream = StringStream;
        CodeMirror.SharedTextMarker = SharedTextMarker;
        CodeMirror.TextMarker = TextMarker;
        CodeMirror.LineWidget = LineWidget;
        CodeMirror.e_preventDefault = e_preventDefault;
        CodeMirror.e_stopPropagation = e_stopPropagation;
        CodeMirror.e_stop = e_stop;
        CodeMirror.addClass = addClass;
        CodeMirror.contains = contains;
        CodeMirror.rmClass = rmClass;
        CodeMirror.keyNames = keyNames;
    };
    // Kludges for bugs and behavior differences that can't be feature
    // detected are enabled based on userAgent etc sniffing.
    var userAgent = navigator.userAgent;
    var platform = navigator.platform;
    var gecko = /gecko\/\d/i.test(userAgent);
    var ie_upto10 = /MSIE \d/.test(userAgent);
    var ie_11up = /Trident\/(?:[7-9]|\d{2,})\..*rv:(\d+)/.exec(userAgent);
    var edge = /Edge\/(\d+)/.exec(userAgent);
    var ie = ie_upto10 || ie_11up || edge;
    var ie_version = ie && (ie_upto10 ? document.documentMode || 6 : +(edge || ie_11up)[1]);
    var webkit = !edge && /WebKit\//.test(userAgent);
    var qtwebkit = webkit && /Qt\/\d+\.\d+/.test(userAgent);
    var chrome = !edge && /Chrome\//.test(userAgent);
    var presto = /Opera\//.test(userAgent);
    var safari = /Apple Computer/.test(navigator.vendor);
    var mac_geMountainLion = /Mac OS X 1\d\D([8-9]|\d\d)\D/.test(userAgent);
    var phantom = /PhantomJS/.test(userAgent);
    var ios = safari && (/Mobile\/\w+/.test(userAgent) || navigator.maxTouchPoints > 2);
    var android = /Android/.test(userAgent);
    // This is woefully incomplete. Suggestions for alternative methods welcome.
    var mobile = ios || android || /webOS|BlackBerry|Opera Mini|Opera Mobi|IEMobile/i.test(userAgent);
    var mac = ios || /Mac/.test(platform);
    var chromeOS = /\bCrOS\b/.test(userAgent);
    var windows = /win/i.test(platform);
    var presto_version = presto && userAgent.match(/Version\/(\d*\.\d*)/);
    if (presto_version) {
        presto_version = Number(presto_version[1]);
    }
    if (presto_version && presto_version >= 15) {
        presto = false;
        webkit = true;
    }
    // Some browsers use the wrong event properties to signal cmd/ctrl on OS X
    var flipCtrlCmd = mac && (qtwebkit || presto && (presto_version == null || presto_version < 12.11));
    var captureRightClick = gecko || ie && ie_version >= 9;
    var rmClass = function rmClass(node, cls) {
        var current = node.className;
        var match = classTest(cls).exec(current);
        if (match) {
            var after = current.slice(match.index + match[0].length);
            node.className = current.slice(0, match.index) + (after ? match[1] + after : "");
        }
    };
    var range;
    if (document.createRange) {
        range = function range(node, start, end, endNode) {
            var r = document.createRange();
            r.setEnd(endNode || node, end);
            r.setStart(node, start);
            return r;
        };
    } else {
        range = function range(node, start, end) {
            var r = document.body.createTextRange();
            try {
                r.moveToElementText(node.parentNode);
            } catch (e) {
                return r;
            }
            r.collapse(true);
            r.moveEnd("character", end);
            r.moveStart("character", start);
            return r;
        };
    }
    var selectInput = function selectInput(node) {
        node.select();
    };
    if (ios) {
        selectInput = function selectInput(node) {
            node.selectionStart = 0;
            node.selectionEnd = node.value.length;
        };
    } else if (ie) {
        selectInput = function selectInput(node) {
            try {
                node.select();
            } catch (_e) {}
        };
    }
    var Delayed = function Delayed() {
        this.id = null;
        this.f = null;
        this.time = 0;
        this.handler = bind(this.onTimeout, this);
    };
    Delayed.prototype.onTimeout = function(self1) {
        self1.id = 0;
        if (self1.time <= +new Date) {
            self1.f();
        } else {
            setTimeout(self1.handler, self1.time - +new Date);
        }
    };
    Delayed.prototype.set = function(ms, f) {
        this.f = f;
        var time = +new Date + ms;
        if (!this.id || time < this.time) {
            clearTimeout(this.id);
            this.id = setTimeout(this.handler, ms);
            this.time = time;
        }
    };
    // Number of pixels added to scroller and sizer to hide scrollbar
    var scrollerGap = 50;
    // Returned or thrown by various protocols to signal 'I'm not
    // handling this'.
    var Pass = {
        toString: function toString() {
            return "CodeMirror.Pass";
        }
    };
    // Reused option objects for setSelection & friends
    var sel_dontScroll = {
        scroll: false
    }, sel_mouse = {
        origin: "*mouse"
    }, sel_move = {
        origin: "+move"
    };
    var spaceStrs = [
        ""
    ];
    var nonASCIISingleCaseWordChar = /[\u00df\u0587\u0590-\u05f4\u0600-\u06ff\u3040-\u309f\u30a0-\u30ff\u3400-\u4db5\u4e00-\u9fcc\uac00-\ud7af]/;
    // Extending unicode characters. A series of a non-extending char +
    // any number of extending chars is treated as a single unit as far
    // as editing and measuring is concerned. This is not fully correct,
    // since some scripts/fonts/browsers also treat other configurations
    // of code points as a group.
    var extendingChars = /[\u0300-\u036f\u0483-\u0489\u0591-\u05bd\u05bf\u05c1\u05c2\u05c4\u05c5\u05c7\u0610-\u061a\u064b-\u065e\u0670\u06d6-\u06dc\u06de-\u06e4\u06e7\u06e8\u06ea-\u06ed\u0711\u0730-\u074a\u07a6-\u07b0\u07eb-\u07f3\u0816-\u0819\u081b-\u0823\u0825-\u0827\u0829-\u082d\u0900-\u0902\u093c\u0941-\u0948\u094d\u0951-\u0955\u0962\u0963\u0981\u09bc\u09be\u09c1-\u09c4\u09cd\u09d7\u09e2\u09e3\u0a01\u0a02\u0a3c\u0a41\u0a42\u0a47\u0a48\u0a4b-\u0a4d\u0a51\u0a70\u0a71\u0a75\u0a81\u0a82\u0abc\u0ac1-\u0ac5\u0ac7\u0ac8\u0acd\u0ae2\u0ae3\u0b01\u0b3c\u0b3e\u0b3f\u0b41-\u0b44\u0b4d\u0b56\u0b57\u0b62\u0b63\u0b82\u0bbe\u0bc0\u0bcd\u0bd7\u0c3e-\u0c40\u0c46-\u0c48\u0c4a-\u0c4d\u0c55\u0c56\u0c62\u0c63\u0cbc\u0cbf\u0cc2\u0cc6\u0ccc\u0ccd\u0cd5\u0cd6\u0ce2\u0ce3\u0d3e\u0d41-\u0d44\u0d4d\u0d57\u0d62\u0d63\u0dca\u0dcf\u0dd2-\u0dd4\u0dd6\u0ddf\u0e31\u0e34-\u0e3a\u0e47-\u0e4e\u0eb1\u0eb4-\u0eb9\u0ebb\u0ebc\u0ec8-\u0ecd\u0f18\u0f19\u0f35\u0f37\u0f39\u0f71-\u0f7e\u0f80-\u0f84\u0f86\u0f87\u0f90-\u0f97\u0f99-\u0fbc\u0fc6\u102d-\u1030\u1032-\u1037\u1039\u103a\u103d\u103e\u1058\u1059\u105e-\u1060\u1071-\u1074\u1082\u1085\u1086\u108d\u109d\u135f\u1712-\u1714\u1732-\u1734\u1752\u1753\u1772\u1773\u17b7-\u17bd\u17c6\u17c9-\u17d3\u17dd\u180b-\u180d\u18a9\u1920-\u1922\u1927\u1928\u1932\u1939-\u193b\u1a17\u1a18\u1a56\u1a58-\u1a5e\u1a60\u1a62\u1a65-\u1a6c\u1a73-\u1a7c\u1a7f\u1b00-\u1b03\u1b34\u1b36-\u1b3a\u1b3c\u1b42\u1b6b-\u1b73\u1b80\u1b81\u1ba2-\u1ba5\u1ba8\u1ba9\u1c2c-\u1c33\u1c36\u1c37\u1cd0-\u1cd2\u1cd4-\u1ce0\u1ce2-\u1ce8\u1ced\u1dc0-\u1de6\u1dfd-\u1dff\u200c\u200d\u20d0-\u20f0\u2cef-\u2cf1\u2de0-\u2dff\u302a-\u302f\u3099\u309a\ua66f-\ua672\ua67c\ua67d\ua6f0\ua6f1\ua802\ua806\ua80b\ua825\ua826\ua8c4\ua8e0-\ua8f1\ua926-\ua92d\ua947-\ua951\ua980-\ua982\ua9b3\ua9b6-\ua9b9\ua9bc\uaa29-\uaa2e\uaa31\uaa32\uaa35\uaa36\uaa43\uaa4c\uaab0\uaab2-\uaab4\uaab7\uaab8\uaabe\uaabf\uaac1\uabe5\uabe8\uabed\udc00-\udfff\ufb1e\ufe00-\ufe0f\ufe20-\ufe26\uff9e\uff9f]/;
    var bidiOther = null;
    // Bidirectional ordering algorithm
    // See http://unicode.org/reports/tr9/tr9-13.html for the algorithm
    // that this (partially) implements.
    // One-char codes used for character types:
    // L (L):   Left-to-Right
    // R (R):   Right-to-Left
    // r (AL):  Right-to-Left Arabic
    // 1 (EN):  European Number
    // + (ES):  European Number Separator
    // % (ET):  European Number Terminator
    // n (AN):  Arabic Number
    // , (CS):  Common Number Separator
    // m (NSM): Non-Spacing Mark
    // b (BN):  Boundary Neutral
    // s (B):   Paragraph Separator
    // t (S):   Segment Separator
    // w (WS):  Whitespace
    // N (ON):  Other Neutrals
    // Returns null if characters are ordered as they appear
    // (left-to-right), or an array of sections ({from, to, level}
    // objects) in the order in which they occur visually.
    var bidiOrdering = function() {
        var charType = function charType(code) {
            if (code <= 0xf7) {
                return lowTypes.charAt(code);
            } else if (0x590 <= code && code <= 0x5f4) {
                return "R";
            } else if (0x600 <= code && code <= 0x6f9) {
                return arabicTypes.charAt(code - 0x600);
            } else if (0x6ee <= code && code <= 0x8ac) {
                return "r";
            } else if (0x2000 <= code && code <= 0x200b) {
                return "w";
            } else if (code == 0x200c) {
                return "b";
            } else {
                return "L";
            }
        };
        var BidiSpan = function BidiSpan(level, from, to) {
            this.level = level;
            this.from = from;
            this.to = to;
        };
        // Character types for codepoints 0 to 0xff
        var lowTypes = "bbbbbbbbbtstwsbbbbbbbbbbbbbbssstwNN%%%NNNNNN,N,N1111111111NNNNNNNLLLLLLLLLLLLLLLLLLLLLLLLLLNNNNNNLLLLLLLLLLLLLLLLLLLLLLLLLLNNNNbbbbbbsbbbbbbbbbbbbbbbbbbbbbbbbbb,N%%%%NNNNLNNNNN%%11NLNNN1LNNNNNLLLLLLLLLLLLLLLLLLLLLLLNLLLLLLLLLLLLLLLLLLLLLLLLLLLLLLLN";
        // Character types for codepoints 0x600 to 0x6f9
        var arabicTypes = "nnnnnnNNr%%r,rNNmmmmmmmmmmmrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrmmmmmmmmmmmmmmmmmmmmmnnnnnnnnnn%nnrrrmrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrmmmmmmmnNmmmmmmrrmmNmmmmrr1111111111";
        var bidiRE = /[\u0590-\u05f4\u0600-\u06ff\u0700-\u08ac]/;
        var isNeutral = /[stwN]/, isStrong = /[LRr]/, countsAsLeft = /[Lb1n]/, countsAsNum = /[1n]/;
        return function(str, direction) {
            var outerType = direction == "ltr" ? "L" : "R";
            if (str.length == 0 || direction == "ltr" && !bidiRE.test(str)) {
                return false;
            }
            var len = str.length, types = [];
            for(var i = 0; i < len; ++i){
                types.push(charType(str.charCodeAt(i)));
            }
            // W1. Examine each non-spacing mark (NSM) in the level run, and
            // change the type of the NSM to the type of the previous
            // character. If the NSM is at the start of the level run, it will
            // get the type of sor.
            for(var i$1 = 0, prev = outerType; i$1 < len; ++i$1){
                var type = types[i$1];
                if (type == "m") {
                    types[i$1] = prev;
                } else {
                    prev = type;
                }
            }
            // W2. Search backwards from each instance of a European number
            // until the first strong type (R, L, AL, or sor) is found. If an
            // AL is found, change the type of the European number to Arabic
            // number.
            // W3. Change all ALs to R.
            for(var i$2 = 0, cur = outerType; i$2 < len; ++i$2){
                var type$1 = types[i$2];
                if (type$1 == "1" && cur == "r") {
                    types[i$2] = "n";
                } else if (isStrong.test(type$1)) {
                    cur = type$1;
                    if (type$1 == "r") {
                        types[i$2] = "R";
                    }
                }
            }
            // W4. A single European separator between two European numbers
            // changes to a European number. A single common separator between
            // two numbers of the same type changes to that type.
            for(var i$3 = 1, prev$1 = types[0]; i$3 < len - 1; ++i$3){
                var type$2 = types[i$3];
                if (type$2 == "+" && prev$1 == "1" && types[i$3 + 1] == "1") {
                    types[i$3] = "1";
                } else if (type$2 == "," && prev$1 == types[i$3 + 1] && (prev$1 == "1" || prev$1 == "n")) {
                    types[i$3] = prev$1;
                }
                prev$1 = type$2;
            }
            // W5. A sequence of European terminators adjacent to European
            // numbers changes to all European numbers.
            // W6. Otherwise, separators and terminators change to Other
            // Neutral.
            for(var i$4 = 0; i$4 < len; ++i$4){
                var type$3 = types[i$4];
                if (type$3 == ",") {
                    types[i$4] = "N";
                } else if (type$3 == "%") {
                    var end = void 0;
                    for(end = i$4 + 1; end < len && types[end] == "%"; ++end){}
                    var replace = i$4 && types[i$4 - 1] == "!" || end < len && types[end] == "1" ? "1" : "N";
                    for(var j = i$4; j < end; ++j){
                        types[j] = replace;
                    }
                    i$4 = end - 1;
                }
            }
            // W7. Search backwards from each instance of a European number
            // until the first strong type (R, L, or sor) is found. If an L is
            // found, then change the type of the European number to L.
            for(var i$5 = 0, cur$1 = outerType; i$5 < len; ++i$5){
                var type$4 = types[i$5];
                if (cur$1 == "L" && type$4 == "1") {
                    types[i$5] = "L";
                } else if (isStrong.test(type$4)) {
                    cur$1 = type$4;
                }
            }
            // N1. A sequence of neutrals takes the direction of the
            // surrounding strong text if the text on both sides has the same
            // direction. European and Arabic numbers act as if they were R in
            // terms of their influence on neutrals. Start-of-level-run (sor)
            // and end-of-level-run (eor) are used at level run boundaries.
            // N2. Any remaining neutrals take the embedding direction.
            for(var i$6 = 0; i$6 < len; ++i$6){
                if (isNeutral.test(types[i$6])) {
                    var end$1 = void 0;
                    for(end$1 = i$6 + 1; end$1 < len && isNeutral.test(types[end$1]); ++end$1){}
                    var before = (i$6 ? types[i$6 - 1] : outerType) == "L";
                    var after = (end$1 < len ? types[end$1] : outerType) == "L";
                    var replace$1 = before == after ? before ? "L" : "R" : outerType;
                    for(var j$1 = i$6; j$1 < end$1; ++j$1){
                        types[j$1] = replace$1;
                    }
                    i$6 = end$1 - 1;
                }
            }
            // Here we depart from the documented algorithm, in order to avoid
            // building up an actual levels array. Since there are only three
            // levels (0, 1, 2) in an implementation that doesn't take
            // explicit embedding into account, we can build up the order on
            // the fly, without following the level-based algorithm.
            var order = [], m;
            for(var i$7 = 0; i$7 < len;){
                if (countsAsLeft.test(types[i$7])) {
                    var start = i$7;
                    for(++i$7; i$7 < len && countsAsLeft.test(types[i$7]); ++i$7){}
                    order.push(new BidiSpan(0, start, i$7));
                } else {
                    var pos = i$7, at = order.length, isRTL = direction == "rtl" ? 1 : 0;
                    for(++i$7; i$7 < len && types[i$7] != "L"; ++i$7){}
                    for(var j$2 = pos; j$2 < i$7;){
                        if (countsAsNum.test(types[j$2])) {
                            if (pos < j$2) {
                                order.splice(at, 0, new BidiSpan(1, pos, j$2));
                                at += isRTL;
                            }
                            var nstart = j$2;
                            for(++j$2; j$2 < i$7 && countsAsNum.test(types[j$2]); ++j$2){}
                            order.splice(at, 0, new BidiSpan(2, nstart, j$2));
                            at += isRTL;
                            pos = j$2;
                        } else {
                            ++j$2;
                        }
                    }
                    if (pos < i$7) {
                        order.splice(at, 0, new BidiSpan(1, pos, i$7));
                    }
                }
            }
            if (direction == "ltr") {
                if (order[0].level == 1 && (m = str.match(/^\s+/))) {
                    order[0].from = m[0].length;
                    order.unshift(new BidiSpan(0, 0, m[0].length));
                }
                if (lst(order).level == 1 && (m = str.match(/\s+$/))) {
                    lst(order).to -= m[0].length;
                    order.push(new BidiSpan(0, len - m[0].length, len));
                }
            }
            return direction == "rtl" ? order.reverse() : order;
        };
    }();
    // EVENT HANDLING
    // Lightweight event framework. on/off also work on DOM nodes,
    // registering native DOM handlers.
    var noHandlers = [];
    var on = function on(emitter, type, f) {
        if (emitter.addEventListener) {
            emitter.addEventListener(type, f, false);
        } else if (emitter.attachEvent) {
            emitter.attachEvent("on" + type, f);
        } else {
            var map = emitter._handlers || (emitter._handlers = {});
            map[type] = (map[type] || noHandlers).concat(f);
        }
    };
    // Detect drag-and-drop
    var dragAndDrop = function() {
        // There is *some* kind of drag-and-drop support in IE6-8, but I
        // couldn't get it to work yet.
        if (ie && ie_version < 9) {
            return false;
        }
        var div = elt("div");
        return "draggable" in div || "dragDrop" in div;
    }();
    var zwspSupported;
    // Feature-detect IE's crummy client rect reporting for bidi text
    var badBidiRects;
    // See if "".split is the broken IE version, if so, provide an
    // alternative way to split lines.
    var splitLinesAuto = "\n\nb".split(/\n/).length != 3 ? function splitLinesAuto(string) {
        var pos = 0, result = [], l = string.length;
        while(pos <= l){
            var nl = string.indexOf("\n", pos);
            if (nl == -1) {
                nl = string.length;
            }
            var line = string.slice(pos, string.charAt(nl - 1) == "\r" ? nl - 1 : nl);
            var rt = line.indexOf("\r");
            if (rt != -1) {
                result.push(line.slice(0, rt));
                pos += rt + 1;
            } else {
                result.push(line);
                pos = nl + 1;
            }
        }
        return result;
    } : function(string) {
        return string.split(/\r\n?|\n/);
    };
    var hasSelection = window.getSelection ? function hasSelection(te) {
        try {
            return te.selectionStart != te.selectionEnd;
        } catch (e) {
            return false;
        }
    } : function(te) {
        var range;
        try {
            range = te.ownerDocument.selection.createRange();
        } catch (e) {}
        if (!range || range.parentElement() != te) {
            return false;
        }
        return range.compareEndPoints("StartToEnd", range) != 0;
    };
    var hasCopyEvent = function() {
        var e = elt("div");
        if ("oncopy" in e) {
            return true;
        }
        e.setAttribute("oncopy", "return;");
        return typeof e.oncopy == "function";
    }();
    var badZoomedRects = null;
    // Known modes, by name and by MIME
    var modes = {}, mimeModes = {};
    // Given a MIME type, a {name, ...options} config object, or a name
    // string, return a mode config object.
    function resolveMode(spec) {
        if (typeof spec == "string" && mimeModes.hasOwnProperty(spec)) {
            spec = mimeModes[spec];
        } else if (spec && typeof spec.name == "string" && mimeModes.hasOwnProperty(spec.name)) {
            var found = mimeModes[spec.name];
            if (typeof found == "string") {
                found = {
                    name: found
                };
            }
            spec = createObj(found, spec);
            spec.name = found.name;
        } else if (typeof spec == "string" && /^[\w\-]+\/[\w\-]+\+xml$/.test(spec)) {
            return resolveMode("application/xml");
        } else if (typeof spec == "string" && /^[\w\-]+\/[\w\-]+\+json$/.test(spec)) {
            return resolveMode("application/json");
        }
        if (typeof spec == "string") {
            return {
                name: spec
            };
        } else {
            return spec || {
                name: "null"
            };
        }
    }
    // Given a mode spec (anything that resolveMode accepts), find and
    // initialize an actual mode object.
    function getMode(options, spec) {
        spec = resolveMode(spec);
        var mfactory = modes[spec.name];
        if (!mfactory) {
            return getMode(options, "text/plain");
        }
        var modeObj = mfactory(options, spec);
        if (modeExtensions.hasOwnProperty(spec.name)) {
            var exts = modeExtensions[spec.name];
            for(var prop in exts){
                if (!exts.hasOwnProperty(prop)) {
                    continue;
                }
                if (modeObj.hasOwnProperty(prop)) {
                    modeObj["_" + prop] = modeObj[prop];
                }
                modeObj[prop] = exts[prop];
            }
        }
        modeObj.name = spec.name;
        if (spec.helperType) {
            modeObj.helperType = spec.helperType;
        }
        if (spec.modeProps) {
            for(var prop$1 in spec.modeProps){
                modeObj[prop$1] = spec.modeProps[prop$1];
            }
        }
        return modeObj;
    }
    // This can be used to attach properties to mode objects from
    // outside the actual mode definition.
    var modeExtensions = {};
    // STRING STREAM
    // Fed to the mode parsers, provides helper functions to make
    // parsers more succinct.
    var StringStream = function StringStream(string, tabSize, lineOracle) {
        this.pos = this.start = 0;
        this.string = string;
        this.tabSize = tabSize || 8;
        this.lastColumnPos = this.lastColumnValue = 0;
        this.lineStart = 0;
        this.lineOracle = lineOracle;
    };
    StringStream.prototype.eol = function() {
        return this.pos >= this.string.length;
    };
    StringStream.prototype.sol = function() {
        return this.pos == this.lineStart;
    };
    StringStream.prototype.peek = function() {
        return this.string.charAt(this.pos) || undefined;
    };
    StringStream.prototype.next = function() {
        if (this.pos < this.string.length) {
            return this.string.charAt(this.pos++);
        }
    };
    StringStream.prototype.eat = function(match) {
        var ch = this.string.charAt(this.pos);
        var ok;
        if (typeof match == "string") {
            ok = ch == match;
        } else {
            ok = ch && (match.test ? match.test(ch) : match(ch));
        }
        if (ok) {
            ++this.pos;
            return ch;
        }
    };
    StringStream.prototype.eatWhile = function(match) {
        var start = this.pos;
        while(this.eat(match)){}
        return this.pos > start;
    };
    StringStream.prototype.eatSpace = function() {
        var start = this.pos;
        while(/[\s\u00a0]/.test(this.string.charAt(this.pos))){
            ++this.pos;
        }
        return this.pos > start;
    };
    StringStream.prototype.skipToEnd = function() {
        this.pos = this.string.length;
    };
    StringStream.prototype.skipTo = function(ch) {
        var found = this.string.indexOf(ch, this.pos);
        if (found > -1) {
            this.pos = found;
            return true;
        }
    };
    StringStream.prototype.backUp = function(n) {
        this.pos -= n;
    };
    StringStream.prototype.column = function() {
        if (this.lastColumnPos < this.start) {
            this.lastColumnValue = countColumn(this.string, this.start, this.tabSize, this.lastColumnPos, this.lastColumnValue);
            this.lastColumnPos = this.start;
        }
        return this.lastColumnValue - (this.lineStart ? countColumn(this.string, this.lineStart, this.tabSize) : 0);
    };
    StringStream.prototype.indentation = function() {
        return countColumn(this.string, null, this.tabSize) - (this.lineStart ? countColumn(this.string, this.lineStart, this.tabSize) : 0);
    };
    StringStream.prototype.match = function(pattern, consume, caseInsensitive) {
        if (typeof pattern == "string") {
            var cased = function cased(str) {
                return caseInsensitive ? str.toLowerCase() : str;
            };
            var substr = this.string.substr(this.pos, pattern.length);
            if (cased(substr) == cased(pattern)) {
                if (consume !== false) {
                    this.pos += pattern.length;
                }
                return true;
            }
        } else {
            var match = this.string.slice(this.pos).match(pattern);
            if (match && match.index > 0) {
                return null;
            }
            if (match && consume !== false) {
                this.pos += match[0].length;
            }
            return match;
        }
    };
    StringStream.prototype.current = function() {
        return this.string.slice(this.start, this.pos);
    };
    StringStream.prototype.hideFirstChars = function(n, inner) {
        this.lineStart += n;
        try {
            return inner();
        } finally{
            this.lineStart -= n;
        }
    };
    StringStream.prototype.lookAhead = function(n) {
        var oracle = this.lineOracle;
        return oracle && oracle.lookAhead(n);
    };
    StringStream.prototype.baseToken = function() {
        var oracle = this.lineOracle;
        return oracle && oracle.baseToken(this.pos);
    };
    // A Pos instance represents a position within the text.
    function Pos(line, ch, sticky) {
        if (sticky === void 0) sticky = null;
        if (!_instanceof(this, Pos)) {
            return new Pos(line, ch, sticky);
        }
        this.line = line;
        this.ch = ch;
        this.sticky = sticky;
    }
    var SavedContext = function SavedContext(state, lookAhead) {
        this.state = state;
        this.lookAhead = lookAhead;
    };
    var Context = function Context(doc, state, line, lookAhead) {
        this.state = state;
        this.doc = doc;
        this.line = line;
        this.maxLookAhead = lookAhead || 0;
        this.baseTokens = null;
        this.baseTokenPos = 1;
    };
    Context.prototype.lookAhead = function(n) {
        var line = this.doc.getLine(this.line + n);
        if (line != null && n > this.maxLookAhead) {
            this.maxLookAhead = n;
        }
        return line;
    };
    Context.prototype.baseToken = function(n) {
        if (!this.baseTokens) {
            return null;
        }
        while(this.baseTokens[this.baseTokenPos] <= n){
            this.baseTokenPos += 2;
        }
        var type = this.baseTokens[this.baseTokenPos + 1];
        return {
            type: type && type.replace(/( |^)overlay .*/, ""),
            size: this.baseTokens[this.baseTokenPos] - n
        };
    };
    Context.prototype.nextLine = function() {
        this.line++;
        if (this.maxLookAhead > 0) {
            this.maxLookAhead--;
        }
    };
    Context.fromSaved = function(doc, saved, line) {
        if (_instanceof(saved, SavedContext)) {
            return new Context(doc, copyState(doc.mode, saved.state), line, saved.lookAhead);
        } else {
            return new Context(doc, copyState(doc.mode, saved), line);
        }
    };
    Context.prototype.save = function(copy) {
        var state = copy !== false ? copyState(this.doc.mode, this.state) : this.state;
        return this.maxLookAhead > 0 ? new SavedContext(state, this.maxLookAhead) : state;
    };
    var Token = function Token(stream, type, state) {
        this.start = stream.start;
        this.end = stream.pos;
        this.string = stream.current();
        this.type = type || null;
        this.state = state;
    };
    // Optimize some code when these features are not used.
    var sawReadOnlySpans = false, sawCollapsedSpans = false;
    function lineIsHiddenInner(doc, line, span) {
        if (span.to == null) {
            var end = span.marker.find(1, true);
            return lineIsHiddenInner(doc, end.line, getMarkedSpanFor(end.line.markedSpans, span.marker));
        }
        if (span.marker.inclusiveRight && span.to == line.text.length) {
            return true;
        }
        for(var sp = void 0, i = 0; i < line.markedSpans.length; ++i){
            sp = line.markedSpans[i];
            if (sp.marker.collapsed && !sp.marker.widgetNode && sp.from == span.to && (sp.to == null || sp.to != span.from) && (sp.marker.inclusiveLeft || span.marker.inclusiveRight) && lineIsHiddenInner(doc, line, sp)) {
                return true;
            }
        }
    }
    // LINE DATA STRUCTURE
    // Line objects. These hold state related to a line, including
    // highlighting info (the styles array).
    var Line = function Line(text, markedSpans, estimateHeight) {
        this.text = text;
        attachMarkedSpans(this, markedSpans);
        this.height = estimateHeight ? estimateHeight(this) : 1;
    };
    Line.prototype.lineNo = function() {
        return lineNo(this);
    };
    eventMixin(Line);
    // Convert a style as returned by a mode (either null, or a string
    // containing one or more styles) to a CSS style. This is cached,
    // and also looks for line-wide styles.
    var styleToClassCache = {}, styleToClassCacheWithMode = {};
    var operationGroup = null;
    var orphanDelayedCallbacks = null;
    var nullRect = {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    };
    var measureText;
    var NativeScrollbars = function NativeScrollbars(place, scroll, cm) {
        this.cm = cm;
        var vert = this.vert = elt("div", [
            elt("div", null, null, "min-width: 1px")
        ], "CodeMirror-vscrollbar");
        var horiz = this.horiz = elt("div", [
            elt("div", null, null, "height: 100%; min-height: 1px")
        ], "CodeMirror-hscrollbar");
        vert.tabIndex = horiz.tabIndex = -1;
        place(vert);
        place(horiz);
        on(vert, "scroll", function() {
            if (vert.clientHeight) {
                scroll(vert.scrollTop, "vertical");
            }
        });
        on(horiz, "scroll", function() {
            if (horiz.clientWidth) {
                scroll(horiz.scrollLeft, "horizontal");
            }
        });
        this.checkedZeroWidth = false;
        // Need to set a minimum width to see the scrollbar on IE7 (but must not set it on IE8).
        if (ie && ie_version < 8) {
            this.horiz.style.minHeight = this.vert.style.minWidth = "18px";
        }
    };
    NativeScrollbars.prototype.update = function(measure) {
        var needsH = measure.scrollWidth > measure.clientWidth + 1;
        var needsV = measure.scrollHeight > measure.clientHeight + 1;
        var sWidth = measure.nativeBarWidth;
        if (needsV) {
            this.vert.style.display = "block";
            this.vert.style.bottom = needsH ? sWidth + "px" : "0";
            var totalHeight = measure.viewHeight - (needsH ? sWidth : 0);
            // A bug in IE8 can cause this value to be negative, so guard it.
            this.vert.firstChild.style.height = Math.max(0, measure.scrollHeight - measure.clientHeight + totalHeight) + "px";
        } else {
            this.vert.scrollTop = 0;
            this.vert.style.display = "";
            this.vert.firstChild.style.height = "0";
        }
        if (needsH) {
            this.horiz.style.display = "block";
            this.horiz.style.right = needsV ? sWidth + "px" : "0";
            this.horiz.style.left = measure.barLeft + "px";
            var totalWidth = measure.viewWidth - measure.barLeft - (needsV ? sWidth : 0);
            this.horiz.firstChild.style.width = Math.max(0, measure.scrollWidth - measure.clientWidth + totalWidth) + "px";
        } else {
            this.horiz.style.display = "";
            this.horiz.firstChild.style.width = "0";
        }
        if (!this.checkedZeroWidth && measure.clientHeight > 0) {
            if (sWidth == 0) {
                this.zeroWidthHack();
            }
            this.checkedZeroWidth = true;
        }
        return {
            right: needsV ? sWidth : 0,
            bottom: needsH ? sWidth : 0
        };
    };
    NativeScrollbars.prototype.setScrollLeft = function(pos) {
        if (this.horiz.scrollLeft != pos) {
            this.horiz.scrollLeft = pos;
        }
        if (this.disableHoriz) {
            this.enableZeroWidthBar(this.horiz, this.disableHoriz, "horiz");
        }
    };
    NativeScrollbars.prototype.setScrollTop = function(pos) {
        if (this.vert.scrollTop != pos) {
            this.vert.scrollTop = pos;
        }
        if (this.disableVert) {
            this.enableZeroWidthBar(this.vert, this.disableVert, "vert");
        }
    };
    NativeScrollbars.prototype.zeroWidthHack = function() {
        var w = mac && !mac_geMountainLion ? "12px" : "18px";
        this.horiz.style.height = this.vert.style.width = w;
        this.horiz.style.pointerEvents = this.vert.style.pointerEvents = "none";
        this.disableHoriz = new Delayed;
        this.disableVert = new Delayed;
    };
    NativeScrollbars.prototype.enableZeroWidthBar = function(bar, delay, type) {
        bar.style.pointerEvents = "auto";
        function maybeDisable() {
            // To find out whether the scrollbar is still visible, we
            // check whether the element under the pixel in the bottom
            // right corner of the scrollbar box is the scrollbar box
            // itself (when the bar is still visible) or its filler child
            // (when the bar is hidden). If it is still visible, we keep
            // it enabled, if it's hidden, we disable pointer events.
            var box = bar.getBoundingClientRect();
            var elt = type == "vert" ? document.elementFromPoint(box.right - 1, (box.top + box.bottom) / 2) : document.elementFromPoint((box.right + box.left) / 2, box.bottom - 1);
            if (elt != bar) {
                bar.style.pointerEvents = "none";
            } else {
                delay.set(1000, maybeDisable);
            }
        }
        delay.set(1000, maybeDisable);
    };
    NativeScrollbars.prototype.clear = function() {
        var parent = this.horiz.parentNode;
        parent.removeChild(this.horiz);
        parent.removeChild(this.vert);
    };
    var NullScrollbars = function NullScrollbars() {};
    NullScrollbars.prototype.update = function() {
        return {
            bottom: 0,
            right: 0
        };
    };
    NullScrollbars.prototype.setScrollLeft = function() {};
    NullScrollbars.prototype.setScrollTop = function() {};
    NullScrollbars.prototype.clear = function() {};
    var scrollbarModel = {
        "native": NativeScrollbars,
        "null": NullScrollbars
    };
    // Operations are used to wrap a series of changes to the editor
    // state in such a way that each change won't have to update the
    // cursor and display (which would be awkward, slow, and
    // error-prone). Instead, display updates are batched and then all
    // combined and executed at once.
    var nextOpId = 0;
    // DISPLAY DRAWING
    var DisplayUpdate = function DisplayUpdate(cm, viewport, force) {
        var display = cm.display;
        this.viewport = viewport;
        // Store some values that we'll need later (but don't want to force a relayout for)
        this.visible = visibleLines(display, cm.doc, viewport);
        this.editorIsHidden = !display.wrapper.offsetWidth;
        this.wrapperHeight = display.wrapper.clientHeight;
        this.wrapperWidth = display.wrapper.clientWidth;
        this.oldDisplayWidth = displayWidth(cm);
        this.force = force;
        this.dims = getDimensions(cm);
        this.events = [];
    };
    DisplayUpdate.prototype.signal = function(emitter, type) {
        if (hasHandler(emitter, type)) {
            this.events.push(arguments);
        }
    };
    DisplayUpdate.prototype.finish = function() {
        for(var i = 0; i < this.events.length; i++){
            signal.apply(null, this.events[i]);
        }
    };
    // Since the delta values reported on mouse wheel events are
    // unstandardized between browsers and even browser versions, and
    // generally horribly unpredictable, this code starts by measuring
    // the scroll effect that the first few mouse wheel events have,
    // and, from that, detects the way it can convert deltas to pixel
    // offsets afterwards.
    //
    // The reason we want to know the amount a wheel event will scroll
    // is that it gives us a chance to update the display before the
    // actual scrolling happens, reducing flickering.
    var wheelSamples = 0, wheelPixelsPerUnit = null;
    // Fill in a browser-detected starting value on browsers where we
    // know one. These don't have to be accurate -- the result of them
    // being wrong would just be a slight flicker on the first wheel
    // scroll (if it is large enough).
    if (ie) {
        wheelPixelsPerUnit = -.53;
    } else if (gecko) {
        wheelPixelsPerUnit = 15;
    } else if (chrome) {
        wheelPixelsPerUnit = -.7;
    } else if (safari) {
        wheelPixelsPerUnit = -1 / 3;
    }
    // Selection objects are immutable. A new one is created every time
    // the selection changes. A selection is one or more non-overlapping
    // (and non-touching) ranges, sorted, and an integer that indicates
    // which one is the primary selection (the one that's scrolled into
    // view, that getCursor returns, etc).
    var Selection = function Selection(ranges, primIndex) {
        this.ranges = ranges;
        this.primIndex = primIndex;
    };
    Selection.prototype.primary = function() {
        return this.ranges[this.primIndex];
    };
    Selection.prototype.equals = function(other) {
        if (other == this) {
            return true;
        }
        if (other.primIndex != this.primIndex || other.ranges.length != this.ranges.length) {
            return false;
        }
        for(var i = 0; i < this.ranges.length; i++){
            var here = this.ranges[i], there = other.ranges[i];
            if (!equalCursorPos(here.anchor, there.anchor) || !equalCursorPos(here.head, there.head)) {
                return false;
            }
        }
        return true;
    };
    Selection.prototype.deepCopy = function() {
        var out = [];
        for(var i = 0; i < this.ranges.length; i++){
            out[i] = new Range(copyPos(this.ranges[i].anchor), copyPos(this.ranges[i].head));
        }
        return new Selection(out, this.primIndex);
    };
    Selection.prototype.somethingSelected = function() {
        for(var i = 0; i < this.ranges.length; i++){
            if (!this.ranges[i].empty()) {
                return true;
            }
        }
        return false;
    };
    Selection.prototype.contains = function(pos, end) {
        if (!end) {
            end = pos;
        }
        for(var i = 0; i < this.ranges.length; i++){
            var range = this.ranges[i];
            if (cmp(end, range.from()) >= 0 && cmp(pos, range.to()) <= 0) {
                return i;
            }
        }
        return -1;
    };
    var Range = function Range(anchor, head) {
        this.anchor = anchor;
        this.head = head;
    };
    Range.prototype.from = function() {
        return minPos(this.anchor, this.head);
    };
    Range.prototype.to = function() {
        return maxPos(this.anchor, this.head);
    };
    Range.prototype.empty = function() {
        return this.head.line == this.anchor.line && this.head.ch == this.anchor.ch;
    };
    function skipAtomicInner(doc, pos, oldPos, dir, mayClear) {
        var line = getLine(doc, pos.line);
        if (line.markedSpans) {
            for(var i = 0; i < line.markedSpans.length; ++i){
                var sp = line.markedSpans[i], m = sp.marker;
                // Determine if we should prevent the cursor being placed to the left/right of an atomic marker
                // Historically this was determined using the inclusiveLeft/Right option, but the new way to control it
                // is with selectLeft/Right
                var preventCursorLeft = "selectLeft" in m ? !m.selectLeft : m.inclusiveLeft;
                var preventCursorRight = "selectRight" in m ? !m.selectRight : m.inclusiveRight;
                if ((sp.from == null || (preventCursorLeft ? sp.from <= pos.ch : sp.from < pos.ch)) && (sp.to == null || (preventCursorRight ? sp.to >= pos.ch : sp.to > pos.ch))) {
                    if (mayClear) {
                        signal(m, "beforeCursorEnter");
                        if (m.explicitlyCleared) {
                            if (!line.markedSpans) {
                                break;
                            } else {
                                --i;
                                continue;
                            }
                        }
                    }
                    if (!m.atomic) {
                        continue;
                    }
                    if (oldPos) {
                        var near = m.find(dir < 0 ? 1 : -1), diff = void 0;
                        if (dir < 0 ? preventCursorRight : preventCursorLeft) {
                            near = movePos(doc, near, -dir, near && near.line == pos.line ? line : null);
                        }
                        if (near && near.line == pos.line && (diff = cmp(near, oldPos)) && (dir < 0 ? diff < 0 : diff > 0)) {
                            return skipAtomicInner(doc, near, pos, dir, mayClear);
                        }
                    }
                    var far = m.find(dir < 0 ? -1 : 1);
                    if (dir < 0 ? preventCursorLeft : preventCursorRight) {
                        far = movePos(doc, far, dir, far.line == pos.line ? line : null);
                    }
                    return far ? skipAtomicInner(doc, far, pos, dir, mayClear) : null;
                }
            }
        }
        return pos;
    }
    // Apply a change to a document, and add it to the document's
    // history, and propagating it to all linked documents.
    function makeChange(doc, change, ignoreReadOnly) {
        if (doc.cm) {
            if (!doc.cm.curOp) {
                return operation(doc.cm, makeChange)(doc, change, ignoreReadOnly);
            }
            if (doc.cm.state.suppressEdits) {
                return;
            }
        }
        if (hasHandler(doc, "beforeChange") || doc.cm && hasHandler(doc.cm, "beforeChange")) {
            change = filterChange(doc, change, true);
            if (!change) {
                return;
            }
        }
        // Possibly split or suppress the update based on the presence
        // of read-only spans in its range.
        var split = sawReadOnlySpans && !ignoreReadOnly && removeReadOnlyRanges(doc, change.from, change.to);
        if (split) {
            for(var i = split.length - 1; i >= 0; --i){
                makeChangeInner(doc, {
                    from: split[i].from,
                    to: split[i].to,
                    text: i ? [
                        ""
                    ] : change.text,
                    origin: change.origin
                });
            }
        } else {
            makeChangeInner(doc, change);
        }
    }
    // More lower-level change function, handling only a single document
    // (not linked ones).
    function makeChangeSingleDoc(doc, change, selAfter, spans) {
        if (doc.cm && !doc.cm.curOp) {
            return operation(doc.cm, makeChangeSingleDoc)(doc, change, selAfter, spans);
        }
        if (change.to.line < doc.first) {
            shiftDoc(doc, change.text.length - 1 - (change.to.line - change.from.line));
            return;
        }
        if (change.from.line > doc.lastLine()) {
            return;
        }
        // Clip the change to the size of this doc
        if (change.from.line < doc.first) {
            var shift = change.text.length - 1 - (doc.first - change.from.line);
            shiftDoc(doc, shift);
            change = {
                from: Pos(doc.first, 0),
                to: Pos(change.to.line + shift, change.to.ch),
                text: [
                    lst(change.text)
                ],
                origin: change.origin
            };
        }
        var last = doc.lastLine();
        if (change.to.line > last) {
            change = {
                from: change.from,
                to: Pos(last, getLine(doc, last).text.length),
                text: [
                    change.text[0]
                ],
                origin: change.origin
            };
        }
        change.removed = getBetween(doc, change.from, change.to);
        if (!selAfter) {
            selAfter = computeSelAfterChange(doc, change);
        }
        if (doc.cm) {
            makeChangeSingleDocInEditor(doc.cm, change, spans);
        } else {
            updateDoc(doc, change, spans);
        }
        setSelectionNoUndo(doc, selAfter, sel_dontScroll);
        if (doc.cantEdit && skipAtomic(doc, Pos(doc.firstLine(), 0))) {
            doc.cantEdit = false;
        }
    }
    LeafChunk.prototype = {
        chunkSize: function chunkSize() {
            return this.lines.length;
        },
        // Remove the n lines at offset 'at'.
        removeInner: function removeInner(at, n) {
            for(var i = at, e = at + n; i < e; ++i){
                var line = this.lines[i];
                this.height -= line.height;
                cleanUpLine(line);
                signalLater(line, "delete");
            }
            this.lines.splice(at, n);
        },
        // Helper used to collapse a small branch into a single leaf.
        collapse: function collapse(lines) {
            lines.push.apply(lines, this.lines);
        },
        // Insert the given array of lines at offset 'at', count them as
        // having the given height.
        insertInner: function insertInner(at, lines, height) {
            this.height += height;
            this.lines = this.lines.slice(0, at).concat(lines).concat(this.lines.slice(at));
            for(var i = 0; i < lines.length; ++i){
                lines[i].parent = this;
            }
        },
        // Used to iterate over a part of the tree.
        iterN: function iterN(at, n, op) {
            for(var e = at + n; at < e; ++at){
                if (op(this.lines[at])) {
                    return true;
                }
            }
        }
    };
    BranchChunk.prototype = {
        chunkSize: function chunkSize() {
            return this.size;
        },
        removeInner: function removeInner(at, n) {
            this.size -= n;
            for(var i = 0; i < this.children.length; ++i){
                var child = this.children[i], sz = child.chunkSize();
                if (at < sz) {
                    var rm = Math.min(n, sz - at), oldHeight = child.height;
                    child.removeInner(at, rm);
                    this.height -= oldHeight - child.height;
                    if (sz == rm) {
                        this.children.splice(i--, 1);
                        child.parent = null;
                    }
                    if ((n -= rm) == 0) {
                        break;
                    }
                    at = 0;
                } else {
                    at -= sz;
                }
            }
            // If the result is smaller than 25 lines, ensure that it is a
            // single leaf node.
            if (this.size - n < 25 && (this.children.length > 1 || !_instanceof(this.children[0], LeafChunk))) {
                var lines = [];
                this.collapse(lines);
                this.children = [
                    new LeafChunk(lines)
                ];
                this.children[0].parent = this;
            }
        },
        collapse: function collapse(lines) {
            for(var i = 0; i < this.children.length; ++i){
                this.children[i].collapse(lines);
            }
        },
        insertInner: function insertInner(at, lines, height) {
            this.size += lines.length;
            this.height += height;
            for(var i = 0; i < this.children.length; ++i){
                var child = this.children[i], sz = child.chunkSize();
                if (at <= sz) {
                    child.insertInner(at, lines, height);
                    if (child.lines && child.lines.length > 50) {
                        // To avoid memory thrashing when child.lines is huge (e.g. first view of a large file), it's never spliced.
                        // Instead, small slices are taken. They're taken in order because sequential memory accesses are fastest.
                        var remaining = child.lines.length % 25 + 25;
                        for(var pos = remaining; pos < child.lines.length;){
                            var leaf = new LeafChunk(child.lines.slice(pos, pos += 25));
                            child.height -= leaf.height;
                            this.children.splice(++i, 0, leaf);
                            leaf.parent = this;
                        }
                        child.lines = child.lines.slice(0, remaining);
                        this.maybeSpill();
                    }
                    break;
                }
                at -= sz;
            }
        },
        // When a node has grown, check whether it should be split.
        maybeSpill: function maybeSpill() {
            if (this.children.length <= 10) {
                return;
            }
            var me = this;
            do {
                var spilled = me.children.splice(me.children.length - 5, 5);
                var sibling = new BranchChunk(spilled);
                if (!me.parent) {
                    var copy = new BranchChunk(me.children);
                    copy.parent = me;
                    me.children = [
                        copy,
                        sibling
                    ];
                    me = copy;
                } else {
                    me.size -= sibling.size;
                    me.height -= sibling.height;
                    var myIndex = indexOf(me.parent.children, me);
                    me.parent.children.splice(myIndex + 1, 0, sibling);
                }
                sibling.parent = me.parent;
            }while (me.children.length > 10);
            me.parent.maybeSpill();
        },
        iterN: function iterN(at, n, op) {
            for(var i = 0; i < this.children.length; ++i){
                var child = this.children[i], sz = child.chunkSize();
                if (at < sz) {
                    var used = Math.min(n, sz - at);
                    if (child.iterN(at, used, op)) {
                        return true;
                    }
                    if ((n -= used) == 0) {
                        break;
                    }
                    at = 0;
                } else {
                    at -= sz;
                }
            }
        }
    };
    // Line widgets are block elements displayed above or below a line.
    var LineWidget = function LineWidget(doc, node, options) {
        if (options) {
            for(var opt in options){
                if (options.hasOwnProperty(opt)) {
                    this[opt] = options[opt];
                }
            }
        }
        this.doc = doc;
        this.node = node;
    };
    LineWidget.prototype.clear = function() {
        var cm = this.doc.cm, ws = this.line.widgets, line = this.line, no = lineNo(line);
        if (no == null || !ws) {
            return;
        }
        for(var i = 0; i < ws.length; ++i){
            if (ws[i] == this) {
                ws.splice(i--, 1);
            }
        }
        if (!ws.length) {
            line.widgets = null;
        }
        var height = widgetHeight(this);
        updateLineHeight(line, Math.max(0, line.height - height));
        if (cm) {
            runInOp(cm, function() {
                adjustScrollWhenAboveVisible(cm, line, -height);
                regLineChange(cm, no, "widget");
            });
            signalLater(cm, "lineWidgetCleared", cm, this, no);
        }
    };
    LineWidget.prototype.changed = function() {
        var this$1 = this;
        var oldH = this.height, cm = this.doc.cm, line = this.line;
        this.height = null;
        var diff = widgetHeight(this) - oldH;
        if (!diff) {
            return;
        }
        if (!lineIsHidden(this.doc, line)) {
            updateLineHeight(line, line.height + diff);
        }
        if (cm) {
            runInOp(cm, function() {
                cm.curOp.forceUpdate = true;
                adjustScrollWhenAboveVisible(cm, line, diff);
                signalLater(cm, "lineWidgetChanged", cm, this$1, lineNo(line));
            });
        }
    };
    eventMixin(LineWidget);
    // TEXTMARKERS
    // Created with markText and setBookmark methods. A TextMarker is a
    // handle that can be used to clear or find a marked position in the
    // document. Line objects hold arrays (markedSpans) containing
    // {from, to, marker} object pointing to such marker objects, and
    // indicating that such a marker is present on that line. Multiple
    // lines may point to the same marker when it spans across lines.
    // The spans will have null for their from/to properties when the
    // marker continues beyond the start/end of the line. Markers have
    // links back to the lines they currently touch.
    // Collapsed markers have unique ids, in order to be able to order
    // them, which is needed for uniquely determining an outer marker
    // when they overlap (they may nest, but not partially overlap).
    var nextMarkerId = 0;
    var TextMarker = function TextMarker(doc, type) {
        this.lines = [];
        this.type = type;
        this.doc = doc;
        this.id = ++nextMarkerId;
    };
    // Clear the marker.
    TextMarker.prototype.clear = function() {
        if (this.explicitlyCleared) {
            return;
        }
        var cm = this.doc.cm, withOp = cm && !cm.curOp;
        if (withOp) {
            startOperation(cm);
        }
        if (hasHandler(this, "clear")) {
            var found = this.find();
            if (found) {
                signalLater(this, "clear", found.from, found.to);
            }
        }
        var min = null, max = null;
        for(var i = 0; i < this.lines.length; ++i){
            var line = this.lines[i];
            var span = getMarkedSpanFor(line.markedSpans, this);
            if (cm && !this.collapsed) {
                regLineChange(cm, lineNo(line), "text");
            } else if (cm) {
                if (span.to != null) {
                    max = lineNo(line);
                }
                if (span.from != null) {
                    min = lineNo(line);
                }
            }
            line.markedSpans = removeMarkedSpan(line.markedSpans, span);
            if (span.from == null && this.collapsed && !lineIsHidden(this.doc, line) && cm) {
                updateLineHeight(line, textHeight(cm.display));
            }
        }
        if (cm && this.collapsed && !cm.options.lineWrapping) {
            for(var i$1 = 0; i$1 < this.lines.length; ++i$1){
                var visual = visualLine(this.lines[i$1]), len = lineLength(visual);
                if (len > cm.display.maxLineLength) {
                    cm.display.maxLine = visual;
                    cm.display.maxLineLength = len;
                    cm.display.maxLineChanged = true;
                }
            }
        }
        if (min != null && cm && this.collapsed) {
            regChange(cm, min, max + 1);
        }
        this.lines.length = 0;
        this.explicitlyCleared = true;
        if (this.atomic && this.doc.cantEdit) {
            this.doc.cantEdit = false;
            if (cm) {
                reCheckSelection(cm.doc);
            }
        }
        if (cm) {
            signalLater(cm, "markerCleared", cm, this, min, max);
        }
        if (withOp) {
            endOperation(cm);
        }
        if (this.parent) {
            this.parent.clear();
        }
    };
    // Find the position of the marker in the document. Returns a {from,
    // to} object by default. Side can be passed to get a specific side
    // -- 0 (both), -1 (left), or 1 (right). When lineObj is true, the
    // Pos objects returned contain a line object, rather than a line
    // number (used to prevent looking up the same line twice).
    TextMarker.prototype.find = function(side, lineObj) {
        if (side == null && this.type == "bookmark") {
            side = 1;
        }
        var from, to;
        for(var i = 0; i < this.lines.length; ++i){
            var line = this.lines[i];
            var span = getMarkedSpanFor(line.markedSpans, this);
            if (span.from != null) {
                from = Pos(lineObj ? line : lineNo(line), span.from);
                if (side == -1) {
                    return from;
                }
            }
            if (span.to != null) {
                to = Pos(lineObj ? line : lineNo(line), span.to);
                if (side == 1) {
                    return to;
                }
            }
        }
        return from && {
            from: from,
            to: to
        };
    };
    // Signals that the marker's widget changed, and surrounding layout
    // should be recomputed.
    TextMarker.prototype.changed = function() {
        var this$1 = this;
        var pos = this.find(-1, true), widget = this, cm = this.doc.cm;
        if (!pos || !cm) {
            return;
        }
        runInOp(cm, function() {
            var line = pos.line, lineN = lineNo(pos.line);
            var view = findViewForLine(cm, lineN);
            if (view) {
                clearLineMeasurementCacheFor(view);
                cm.curOp.selectionChanged = cm.curOp.forceUpdate = true;
            }
            cm.curOp.updateMaxLine = true;
            if (!lineIsHidden(widget.doc, line) && widget.height != null) {
                var oldHeight = widget.height;
                widget.height = null;
                var dHeight = widgetHeight(widget) - oldHeight;
                if (dHeight) {
                    updateLineHeight(line, line.height + dHeight);
                }
            }
            signalLater(cm, "markerChanged", cm, this$1);
        });
    };
    TextMarker.prototype.attachLine = function(line) {
        if (!this.lines.length && this.doc.cm) {
            var op = this.doc.cm.curOp;
            if (!op.maybeHiddenMarkers || indexOf(op.maybeHiddenMarkers, this) == -1) {
                (op.maybeUnhiddenMarkers || (op.maybeUnhiddenMarkers = [])).push(this);
            }
        }
        this.lines.push(line);
    };
    TextMarker.prototype.detachLine = function(line) {
        this.lines.splice(indexOf(this.lines, line), 1);
        if (!this.lines.length && this.doc.cm) {
            var op = this.doc.cm.curOp;
            (op.maybeHiddenMarkers || (op.maybeHiddenMarkers = [])).push(this);
        }
    };
    eventMixin(TextMarker);
    // Create a marker, wire it up to the right lines, and
    function markText(doc, from, to, options, type) {
        // Shared markers (across linked documents) are handled separately
        // (markTextShared will call out to this again, once per
        // document).
        if (options && options.shared) {
            return markTextShared(doc, from, to, options, type);
        }
        // Ensure we are in an operation.
        if (doc.cm && !doc.cm.curOp) {
            return operation(doc.cm, markText)(doc, from, to, options, type);
        }
        var marker = new TextMarker(doc, type), diff = cmp(from, to);
        if (options) {
            copyObj(options, marker, false);
        }
        // Don't connect empty markers unless clearWhenEmpty is false
        if (diff > 0 || diff == 0 && marker.clearWhenEmpty !== false) {
            return marker;
        }
        if (marker.replacedWith) {
            // Showing up as a widget implies collapsed (widget replaces text)
            marker.collapsed = true;
            marker.widgetNode = eltP("span", [
                marker.replacedWith
            ], "CodeMirror-widget");
            if (!options.handleMouseEvents) {
                marker.widgetNode.setAttribute("cm-ignore-events", "true");
            }
            if (options.insertLeft) {
                marker.widgetNode.insertLeft = true;
            }
        }
        if (marker.collapsed) {
            if (conflictingCollapsedRange(doc, from.line, from, to, marker) || from.line != to.line && conflictingCollapsedRange(doc, to.line, from, to, marker)) {
                throw new Error("Inserting collapsed marker partially overlapping an existing one");
            }
            seeCollapsedSpans();
        }
        if (marker.addToHistory) {
            addChangeToHistory(doc, {
                from: from,
                to: to,
                origin: "markText"
            }, doc.sel, NaN);
        }
        var curLine = from.line, cm = doc.cm, updateMaxLine;
        doc.iter(curLine, to.line + 1, function(line) {
            if (cm && marker.collapsed && !cm.options.lineWrapping && visualLine(line) == cm.display.maxLine) {
                updateMaxLine = true;
            }
            if (marker.collapsed && curLine != from.line) {
                updateLineHeight(line, 0);
            }
            addMarkedSpan(line, new MarkedSpan(marker, curLine == from.line ? from.ch : null, curLine == to.line ? to.ch : null), doc.cm && doc.cm.curOp);
            ++curLine;
        });
        // lineIsHidden depends on the presence of the spans, so needs a second pass
        if (marker.collapsed) {
            doc.iter(from.line, to.line + 1, function(line) {
                if (lineIsHidden(doc, line)) {
                    updateLineHeight(line, 0);
                }
            });
        }
        if (marker.clearOnEnter) {
            on(marker, "beforeCursorEnter", function() {
                return marker.clear();
            });
        }
        if (marker.readOnly) {
            seeReadOnlySpans();
            if (doc.history.done.length || doc.history.undone.length) {
                doc.clearHistory();
            }
        }
        if (marker.collapsed) {
            marker.id = ++nextMarkerId;
            marker.atomic = true;
        }
        if (cm) {
            // Sync editor state
            if (updateMaxLine) {
                cm.curOp.updateMaxLine = true;
            }
            if (marker.collapsed) {
                regChange(cm, from.line, to.line + 1);
            } else if (marker.className || marker.startStyle || marker.endStyle || marker.css || marker.attributes || marker.title) {
                for(var i = from.line; i <= to.line; i++){
                    regLineChange(cm, i, "text");
                }
            }
            if (marker.atomic) {
                reCheckSelection(cm.doc);
            }
            signalLater(cm, "markerAdded", cm, marker);
        }
        return marker;
    }
    // SHARED TEXTMARKERS
    // A shared marker spans multiple linked documents. It is
    // implemented as a meta-marker-object controlling multiple normal
    // markers.
    var SharedTextMarker = function SharedTextMarker(markers, primary) {
        this.markers = markers;
        this.primary = primary;
        for(var i = 0; i < markers.length; ++i){
            markers[i].parent = this;
        }
    };
    SharedTextMarker.prototype.clear = function() {
        if (this.explicitlyCleared) {
            return;
        }
        this.explicitlyCleared = true;
        for(var i = 0; i < this.markers.length; ++i){
            this.markers[i].clear();
        }
        signalLater(this, "clear");
    };
    SharedTextMarker.prototype.find = function(side, lineObj) {
        return this.primary.find(side, lineObj);
    };
    eventMixin(SharedTextMarker);
    var nextDocId = 0;
    var Doc = function Doc1(text, mode, firstLine, lineSep, direction) {
        if (!_instanceof(this, Doc)) {
            return new Doc(text, mode, firstLine, lineSep, direction);
        }
        if (firstLine == null) {
            firstLine = 0;
        }
        BranchChunk.call(this, [
            new LeafChunk([
                new Line("", null)
            ])
        ]);
        this.first = firstLine;
        this.scrollTop = this.scrollLeft = 0;
        this.cantEdit = false;
        this.cleanGeneration = 1;
        this.modeFrontier = this.highlightFrontier = firstLine;
        var start = Pos(firstLine, 0);
        this.sel = simpleSelection(start);
        this.history = new History(null);
        this.id = ++nextDocId;
        this.modeOption = mode;
        this.lineSep = lineSep;
        this.direction = direction == "rtl" ? "rtl" : "ltr";
        this.extend = false;
        if (typeof text == "string") {
            text = this.splitLines(text);
        }
        updateDoc(this, {
            from: start,
            to: start,
            text: text
        });
        setSelection(this, simpleSelection(start), sel_dontScroll);
    };
    Doc.prototype = createObj(BranchChunk.prototype, {
        constructor: Doc,
        // Iterate over the document. Supports two forms -- with only one
        // argument, it calls that for each line in the document. With
        // three, it iterates over the range given by the first two (with
        // the second being non-inclusive).
        iter: function iter(from, to, op) {
            if (op) {
                this.iterN(from - this.first, to - from, op);
            } else {
                this.iterN(this.first, this.first + this.size, from);
            }
        },
        // Non-public interface for adding and removing lines.
        insert: function insert(at, lines) {
            var height = 0;
            for(var i = 0; i < lines.length; ++i){
                height += lines[i].height;
            }
            this.insertInner(at - this.first, lines, height);
        },
        remove: function remove(at, n) {
            this.removeInner(at - this.first, n);
        },
        // From here, the methods are part of the public interface. Most
        // are also available from CodeMirror (editor) instances.
        getValue: function getValue(lineSep) {
            var lines = getLines(this, this.first, this.first + this.size);
            if (lineSep === false) {
                return lines;
            }
            return lines.join(lineSep || this.lineSeparator());
        },
        setValue: docMethodOp(function(code) {
            var top = Pos(this.first, 0), last = this.first + this.size - 1;
            makeChange(this, {
                from: top,
                to: Pos(last, getLine(this, last).text.length),
                text: this.splitLines(code),
                origin: "setValue",
                full: true
            }, true);
            if (this.cm) {
                scrollToCoords(this.cm, 0, 0);
            }
            setSelection(this, simpleSelection(top), sel_dontScroll);
        }),
        replaceRange: function replaceRange1(code, from, to, origin) {
            from = clipPos(this, from);
            to = to ? clipPos(this, to) : from;
            replaceRange(this, code, from, to, origin);
        },
        getRange: function getRange(from, to, lineSep) {
            var lines = getBetween(this, clipPos(this, from), clipPos(this, to));
            if (lineSep === false) {
                return lines;
            }
            if (lineSep === "") {
                return lines.join("");
            }
            return lines.join(lineSep || this.lineSeparator());
        },
        getLine: function getLine(line) {
            var l = this.getLineHandle(line);
            return l && l.text;
        },
        getLineHandle: function getLineHandle(line) {
            if (isLine(this, line)) {
                return getLine(this, line);
            }
        },
        getLineNumber: function getLineNumber(line) {
            return lineNo(line);
        },
        getLineHandleVisualStart: function getLineHandleVisualStart(line) {
            if (typeof line == "number") {
                line = getLine(this, line);
            }
            return visualLine(line);
        },
        lineCount: function lineCount() {
            return this.size;
        },
        firstLine: function firstLine() {
            return this.first;
        },
        lastLine: function lastLine() {
            return this.first + this.size - 1;
        },
        clipPos: function clipPos1(pos) {
            return clipPos(this, pos);
        },
        getCursor: function getCursor(start) {
            var range = this.sel.primary(), pos;
            if (start == null || start == "head") {
                pos = range.head;
            } else if (start == "anchor") {
                pos = range.anchor;
            } else if (start == "end" || start == "to" || start === false) {
                pos = range.to();
            } else {
                pos = range.from();
            }
            return pos;
        },
        listSelections: function listSelections() {
            return this.sel.ranges;
        },
        somethingSelected: function somethingSelected() {
            return this.sel.somethingSelected();
        },
        setCursor: docMethodOp(function(line, ch, options) {
            setSimpleSelection(this, clipPos(this, typeof line == "number" ? Pos(line, ch || 0) : line), null, options);
        }),
        setSelection: docMethodOp(function(anchor, head, options) {
            setSimpleSelection(this, clipPos(this, anchor), clipPos(this, head || anchor), options);
        }),
        extendSelection: docMethodOp(function(head, other, options) {
            extendSelection(this, clipPos(this, head), other && clipPos(this, other), options);
        }),
        extendSelections: docMethodOp(function(heads, options) {
            extendSelections(this, clipPosArray(this, heads), options);
        }),
        extendSelectionsBy: docMethodOp(function(f, options) {
            var heads = map(this.sel.ranges, f);
            extendSelections(this, clipPosArray(this, heads), options);
        }),
        setSelections: docMethodOp(function(ranges, primary, options) {
            if (!ranges.length) {
                return;
            }
            var out = [];
            for(var i = 0; i < ranges.length; i++){
                out[i] = new Range(clipPos(this, ranges[i].anchor), clipPos(this, ranges[i].head || ranges[i].anchor));
            }
            if (primary == null) {
                primary = Math.min(ranges.length - 1, this.sel.primIndex);
            }
            setSelection(this, normalizeSelection(this.cm, out, primary), options);
        }),
        addSelection: docMethodOp(function(anchor, head, options) {
            var ranges = this.sel.ranges.slice(0);
            ranges.push(new Range(clipPos(this, anchor), clipPos(this, head || anchor)));
            setSelection(this, normalizeSelection(this.cm, ranges, ranges.length - 1), options);
        }),
        getSelection: function getSelection(lineSep) {
            var ranges = this.sel.ranges, lines;
            for(var i = 0; i < ranges.length; i++){
                var sel = getBetween(this, ranges[i].from(), ranges[i].to());
                lines = lines ? lines.concat(sel) : sel;
            }
            if (lineSep === false) {
                return lines;
            } else {
                return lines.join(lineSep || this.lineSeparator());
            }
        },
        getSelections: function getSelections(lineSep) {
            var parts = [], ranges = this.sel.ranges;
            for(var i = 0; i < ranges.length; i++){
                var sel = getBetween(this, ranges[i].from(), ranges[i].to());
                if (lineSep !== false) {
                    sel = sel.join(lineSep || this.lineSeparator());
                }
                parts[i] = sel;
            }
            return parts;
        },
        replaceSelection: function replaceSelection(code, collapse, origin) {
            var dup = [];
            for(var i = 0; i < this.sel.ranges.length; i++){
                dup[i] = code;
            }
            this.replaceSelections(dup, collapse, origin || "+input");
        },
        replaceSelections: docMethodOp(function(code, collapse, origin) {
            var changes = [], sel = this.sel;
            for(var i = 0; i < sel.ranges.length; i++){
                var range = sel.ranges[i];
                changes[i] = {
                    from: range.from(),
                    to: range.to(),
                    text: this.splitLines(code[i]),
                    origin: origin
                };
            }
            var newSel = collapse && collapse != "end" && computeReplacedSel(this, changes, collapse);
            for(var i$1 = changes.length - 1; i$1 >= 0; i$1--){
                makeChange(this, changes[i$1]);
            }
            if (newSel) {
                setSelectionReplaceHistory(this, newSel);
            } else if (this.cm) {
                ensureCursorVisible(this.cm);
            }
        }),
        undo: docMethodOp(function() {
            makeChangeFromHistory(this, "undo");
        }),
        redo: docMethodOp(function() {
            makeChangeFromHistory(this, "redo");
        }),
        undoSelection: docMethodOp(function() {
            makeChangeFromHistory(this, "undo", true);
        }),
        redoSelection: docMethodOp(function() {
            makeChangeFromHistory(this, "redo", true);
        }),
        setExtending: function setExtending(val) {
            this.extend = val;
        },
        getExtending: function getExtending() {
            return this.extend;
        },
        historySize: function historySize() {
            var hist = this.history, done = 0, undone = 0;
            for(var i = 0; i < hist.done.length; i++){
                if (!hist.done[i].ranges) {
                    ++done;
                }
            }
            for(var i$1 = 0; i$1 < hist.undone.length; i$1++){
                if (!hist.undone[i$1].ranges) {
                    ++undone;
                }
            }
            return {
                undo: done,
                redo: undone
            };
        },
        clearHistory: function clearHistory() {
            var this$1 = this;
            this.history = new History(this.history);
            linkedDocs(this, function(doc) {
                return doc.history = this$1.history;
            }, true);
        },
        markClean: function markClean() {
            this.cleanGeneration = this.changeGeneration(true);
        },
        changeGeneration: function changeGeneration(forceSplit) {
            if (forceSplit) {
                this.history.lastOp = this.history.lastSelOp = this.history.lastOrigin = null;
            }
            return this.history.generation;
        },
        isClean: function isClean(gen) {
            return this.history.generation == (gen || this.cleanGeneration);
        },
        getHistory: function getHistory() {
            return {
                done: copyHistoryArray(this.history.done),
                undone: copyHistoryArray(this.history.undone)
            };
        },
        setHistory: function setHistory(histData) {
            var hist = this.history = new History(this.history);
            hist.done = copyHistoryArray(histData.done.slice(0), null, true);
            hist.undone = copyHistoryArray(histData.undone.slice(0), null, true);
        },
        setGutterMarker: docMethodOp(function(line, gutterID, value) {
            return changeLine(this, line, "gutter", function(line) {
                var markers = line.gutterMarkers || (line.gutterMarkers = {});
                markers[gutterID] = value;
                if (!value && isEmpty(markers)) {
                    line.gutterMarkers = null;
                }
                return true;
            });
        }),
        clearGutter: docMethodOp(function(gutterID) {
            var this$1 = this;
            this.iter(function(line) {
                if (line.gutterMarkers && line.gutterMarkers[gutterID]) {
                    changeLine(this$1, line, "gutter", function() {
                        line.gutterMarkers[gutterID] = null;
                        if (isEmpty(line.gutterMarkers)) {
                            line.gutterMarkers = null;
                        }
                        return true;
                    });
                }
            });
        }),
        lineInfo: function lineInfo(line) {
            var n;
            if (typeof line == "number") {
                if (!isLine(this, line)) {
                    return null;
                }
                n = line;
                line = getLine(this, line);
                if (!line) {
                    return null;
                }
            } else {
                n = lineNo(line);
                if (n == null) {
                    return null;
                }
            }
            return {
                line: n,
                handle: line,
                text: line.text,
                gutterMarkers: line.gutterMarkers,
                textClass: line.textClass,
                bgClass: line.bgClass,
                wrapClass: line.wrapClass,
                widgets: line.widgets
            };
        },
        addLineClass: docMethodOp(function(handle, where, cls) {
            return changeLine(this, handle, where == "gutter" ? "gutter" : "class", function(line) {
                var prop = where == "text" ? "textClass" : where == "background" ? "bgClass" : where == "gutter" ? "gutterClass" : "wrapClass";
                if (!line[prop]) {
                    line[prop] = cls;
                } else if (classTest(cls).test(line[prop])) {
                    return false;
                } else {
                    line[prop] += " " + cls;
                }
                return true;
            });
        }),
        removeLineClass: docMethodOp(function(handle, where, cls) {
            return changeLine(this, handle, where == "gutter" ? "gutter" : "class", function(line) {
                var prop = where == "text" ? "textClass" : where == "background" ? "bgClass" : where == "gutter" ? "gutterClass" : "wrapClass";
                var cur = line[prop];
                if (!cur) {
                    return false;
                } else if (cls == null) {
                    line[prop] = null;
                } else {
                    var found = cur.match(classTest(cls));
                    if (!found) {
                        return false;
                    }
                    var end = found.index + found[0].length;
                    line[prop] = cur.slice(0, found.index) + (!found.index || end == cur.length ? "" : " ") + cur.slice(end) || null;
                }
                return true;
            });
        }),
        addLineWidget: docMethodOp(function(handle, node, options) {
            return addLineWidget(this, handle, node, options);
        }),
        removeLineWidget: function removeLineWidget(widget) {
            widget.clear();
        },
        markText: function markText1(from, to, options) {
            return markText(this, clipPos(this, from), clipPos(this, to), options, options && options.type || "range");
        },
        setBookmark: function setBookmark(pos, options) {
            var realOpts = {
                replacedWith: options && (options.nodeType == null ? options.widget : options),
                insertLeft: options && options.insertLeft,
                clearWhenEmpty: false,
                shared: options && options.shared,
                handleMouseEvents: options && options.handleMouseEvents
            };
            pos = clipPos(this, pos);
            return markText(this, pos, pos, realOpts, "bookmark");
        },
        findMarksAt: function findMarksAt(pos) {
            pos = clipPos(this, pos);
            var markers = [], spans = getLine(this, pos.line).markedSpans;
            if (spans) {
                for(var i = 0; i < spans.length; ++i){
                    var span = spans[i];
                    if ((span.from == null || span.from <= pos.ch) && (span.to == null || span.to >= pos.ch)) {
                        markers.push(span.marker.parent || span.marker);
                    }
                }
            }
            return markers;
        },
        findMarks: function findMarks(from, to, filter) {
            from = clipPos(this, from);
            to = clipPos(this, to);
            var found = [], lineNo = from.line;
            this.iter(from.line, to.line + 1, function(line) {
                var spans = line.markedSpans;
                if (spans) {
                    for(var i = 0; i < spans.length; i++){
                        var span = spans[i];
                        if (!(span.to != null && lineNo == from.line && from.ch >= span.to || span.from == null && lineNo != from.line || span.from != null && lineNo == to.line && span.from >= to.ch) && (!filter || filter(span.marker))) {
                            found.push(span.marker.parent || span.marker);
                        }
                    }
                }
                ++lineNo;
            });
            return found;
        },
        getAllMarks: function getAllMarks() {
            var markers = [];
            this.iter(function(line) {
                var sps = line.markedSpans;
                if (sps) {
                    for(var i = 0; i < sps.length; ++i){
                        if (sps[i].from != null) {
                            markers.push(sps[i].marker);
                        }
                    }
                }
            });
            return markers;
        },
        posFromIndex: function posFromIndex(off) {
            var ch, lineNo = this.first, sepSize = this.lineSeparator().length;
            this.iter(function(line) {
                var sz = line.text.length + sepSize;
                if (sz > off) {
                    ch = off;
                    return true;
                }
                off -= sz;
                ++lineNo;
            });
            return clipPos(this, Pos(lineNo, ch));
        },
        indexFromPos: function indexFromPos(coords) {
            coords = clipPos(this, coords);
            var index = coords.ch;
            if (coords.line < this.first || coords.ch < 0) {
                return 0;
            }
            var sepSize = this.lineSeparator().length;
            this.iter(this.first, coords.line, function(line) {
                index += line.text.length + sepSize;
            });
            return index;
        },
        copy: function copy(copyHistory) {
            var doc = new Doc(getLines(this, this.first, this.first + this.size), this.modeOption, this.first, this.lineSep, this.direction);
            doc.scrollTop = this.scrollTop;
            doc.scrollLeft = this.scrollLeft;
            doc.sel = this.sel;
            doc.extend = false;
            if (copyHistory) {
                doc.history.undoDepth = this.history.undoDepth;
                doc.setHistory(this.getHistory());
            }
            return doc;
        },
        linkedDoc: function linkedDoc(options) {
            if (!options) {
                options = {};
            }
            var from = this.first, to = this.first + this.size;
            if (options.from != null && options.from > from) {
                from = options.from;
            }
            if (options.to != null && options.to < to) {
                to = options.to;
            }
            var copy = new Doc(getLines(this, from, to), options.mode || this.modeOption, from, this.lineSep, this.direction);
            if (options.sharedHist) {
                copy.history = this.history;
            }
            (this.linked || (this.linked = [])).push({
                doc: copy,
                sharedHist: options.sharedHist
            });
            copy.linked = [
                {
                    doc: this,
                    isParent: true,
                    sharedHist: options.sharedHist
                }
            ];
            copySharedMarkers(copy, findSharedMarkers(this));
            return copy;
        },
        unlinkDoc: function unlinkDoc(other) {
            if (_instanceof(other, CodeMirror)) {
                other = other.doc;
            }
            if (this.linked) {
                for(var i = 0; i < this.linked.length; ++i){
                    var link = this.linked[i];
                    if (link.doc != other) {
                        continue;
                    }
                    this.linked.splice(i, 1);
                    other.unlinkDoc(this);
                    detachSharedMarkers(findSharedMarkers(this));
                    break;
                }
            }
            // If the histories were shared, split them again
            if (other.history == this.history) {
                var splitIds = [
                    other.id
                ];
                linkedDocs(other, function(doc) {
                    return splitIds.push(doc.id);
                }, true);
                other.history = new History(null);
                other.history.done = copyHistoryArray(this.history.done, splitIds);
                other.history.undone = copyHistoryArray(this.history.undone, splitIds);
            }
        },
        iterLinkedDocs: function iterLinkedDocs(f) {
            linkedDocs(this, f);
        },
        getMode: function getMode() {
            return this.mode;
        },
        getEditor: function getEditor() {
            return this.cm;
        },
        splitLines: function splitLines(str) {
            if (this.lineSep) {
                return str.split(this.lineSep);
            }
            return splitLinesAuto(str);
        },
        lineSeparator: function lineSeparator() {
            return this.lineSep || "\n";
        },
        setDirection: docMethodOp(function(dir) {
            if (dir != "rtl") {
                dir = "ltr";
            }
            if (dir == this.direction) {
                return;
            }
            this.direction = dir;
            this.iter(function(line) {
                return line.order = null;
            });
            if (this.cm) {
                directionChanged(this.cm);
            }
        })
    });
    // Public alias.
    Doc.prototype.eachLine = Doc.prototype.iter;
    // Kludge to work around strange IE behavior where it'll sometimes
    // re-fire a series of drag-related events right after the drop (#1551)
    var lastDrop = 0;
    var globalsRegistered = false;
    var keyNames = {
        3: "Pause",
        8: "Backspace",
        9: "Tab",
        13: "Enter",
        16: "Shift",
        17: "Ctrl",
        18: "Alt",
        19: "Pause",
        20: "CapsLock",
        27: "Esc",
        32: "Space",
        33: "PageUp",
        34: "PageDown",
        35: "End",
        36: "Home",
        37: "Left",
        38: "Up",
        39: "Right",
        40: "Down",
        44: "PrintScrn",
        45: "Insert",
        46: "Delete",
        59: ";",
        61: "=",
        91: "Mod",
        92: "Mod",
        93: "Mod",
        106: "*",
        107: "=",
        109: "-",
        110: ".",
        111: "/",
        145: "ScrollLock",
        173: "-",
        186: ";",
        187: "=",
        188: ",",
        189: "-",
        190: ".",
        191: "/",
        192: "`",
        219: "[",
        220: "\\",
        221: "]",
        222: "'",
        224: "Mod",
        63232: "Up",
        63233: "Down",
        63234: "Left",
        63235: "Right",
        63272: "Delete",
        63273: "Home",
        63275: "End",
        63276: "PageUp",
        63277: "PageDown",
        63302: "Insert"
    };
    // Number keys
    for(var i = 0; i < 10; i++){
        keyNames[i + 48] = keyNames[i + 96] = String(i);
    }
    // Alphabetic keys
    for(var i$1 = 65; i$1 <= 90; i$1++){
        keyNames[i$1] = String.fromCharCode(i$1);
    }
    // Function keys
    for(var i$2 = 1; i$2 <= 12; i$2++){
        keyNames[i$2 + 111] = keyNames[i$2 + 63235] = "F" + i$2;
    }
    var keyMap = {};
    keyMap.basic = {
        "Left": "goCharLeft",
        "Right": "goCharRight",
        "Up": "goLineUp",
        "Down": "goLineDown",
        "End": "goLineEnd",
        "Home": "goLineStartSmart",
        "PageUp": "goPageUp",
        "PageDown": "goPageDown",
        "Delete": "delCharAfter",
        "Backspace": "delCharBefore",
        "Shift-Backspace": "delCharBefore",
        "Tab": "defaultTab",
        "Shift-Tab": "indentAuto",
        "Enter": "newlineAndIndent",
        "Insert": "toggleOverwrite",
        "Esc": "singleSelection"
    };
    // Note that the save and find-related commands aren't defined by
    // default. User code or addons can define them. Unknown commands
    // are simply ignored.
    keyMap.pcDefault = {
        "Ctrl-A": "selectAll",
        "Ctrl-D": "deleteLine",
        "Ctrl-Z": "undo",
        "Shift-Ctrl-Z": "redo",
        "Ctrl-Y": "redo",
        "Ctrl-Home": "goDocStart",
        "Ctrl-End": "goDocEnd",
        "Ctrl-Up": "goLineUp",
        "Ctrl-Down": "goLineDown",
        "Ctrl-Left": "goGroupLeft",
        "Ctrl-Right": "goGroupRight",
        "Alt-Left": "goLineStart",
        "Alt-Right": "goLineEnd",
        "Ctrl-Backspace": "delGroupBefore",
        "Ctrl-Delete": "delGroupAfter",
        "Ctrl-S": "save",
        "Ctrl-F": "find",
        "Ctrl-G": "findNext",
        "Shift-Ctrl-G": "findPrev",
        "Shift-Ctrl-F": "replace",
        "Shift-Ctrl-R": "replaceAll",
        "Ctrl-[": "indentLess",
        "Ctrl-]": "indentMore",
        "Ctrl-U": "undoSelection",
        "Shift-Ctrl-U": "redoSelection",
        "Alt-U": "redoSelection",
        "fallthrough": "basic"
    };
    // Very basic readline/emacs-style bindings, which are standard on Mac.
    keyMap.emacsy = {
        "Ctrl-F": "goCharRight",
        "Ctrl-B": "goCharLeft",
        "Ctrl-P": "goLineUp",
        "Ctrl-N": "goLineDown",
        "Ctrl-A": "goLineStart",
        "Ctrl-E": "goLineEnd",
        "Ctrl-V": "goPageDown",
        "Shift-Ctrl-V": "goPageUp",
        "Ctrl-D": "delCharAfter",
        "Ctrl-H": "delCharBefore",
        "Alt-Backspace": "delWordBefore",
        "Ctrl-K": "killLine",
        "Ctrl-T": "transposeChars",
        "Ctrl-O": "openLine"
    };
    keyMap.macDefault = {
        "Cmd-A": "selectAll",
        "Cmd-D": "deleteLine",
        "Cmd-Z": "undo",
        "Shift-Cmd-Z": "redo",
        "Cmd-Y": "redo",
        "Cmd-Home": "goDocStart",
        "Cmd-Up": "goDocStart",
        "Cmd-End": "goDocEnd",
        "Cmd-Down": "goDocEnd",
        "Alt-Left": "goGroupLeft",
        "Alt-Right": "goGroupRight",
        "Cmd-Left": "goLineLeft",
        "Cmd-Right": "goLineRight",
        "Alt-Backspace": "delGroupBefore",
        "Ctrl-Alt-Backspace": "delGroupAfter",
        "Alt-Delete": "delGroupAfter",
        "Cmd-S": "save",
        "Cmd-F": "find",
        "Cmd-G": "findNext",
        "Shift-Cmd-G": "findPrev",
        "Cmd-Alt-F": "replace",
        "Shift-Cmd-Alt-F": "replaceAll",
        "Cmd-[": "indentLess",
        "Cmd-]": "indentMore",
        "Cmd-Backspace": "delWrappedLineLeft",
        "Cmd-Delete": "delWrappedLineRight",
        "Cmd-U": "undoSelection",
        "Shift-Cmd-U": "redoSelection",
        "Ctrl-Up": "goDocStart",
        "Ctrl-Down": "goDocEnd",
        "fallthrough": [
            "basic",
            "emacsy"
        ]
    };
    keyMap["default"] = mac ? keyMap.macDefault : keyMap.pcDefault;
    function lookupKey(key, map, handle, context) {
        map = getKeyMap(map);
        var found = map.call ? map.call(key, context) : map[key];
        if (found === false) {
            return "nothing";
        }
        if (found === "...") {
            return "multi";
        }
        if (found != null && handle(found)) {
            return "handled";
        }
        if (map.fallthrough) {
            if (Object.prototype.toString.call(map.fallthrough) != "[object Array]") {
                return lookupKey(key, map.fallthrough, handle, context);
            }
            for(var i = 0; i < map.fallthrough.length; i++){
                var result = lookupKey(key, map.fallthrough[i], handle, context);
                if (result) {
                    return result;
                }
            }
        }
    }
    // Commands are parameter-less actions that can be performed on an
    // editor, mostly used for keybindings.
    var commands = {
        selectAll: selectAll,
        singleSelection: function singleSelection(cm) {
            return cm.setSelection(cm.getCursor("anchor"), cm.getCursor("head"), sel_dontScroll);
        },
        killLine: function killLine(cm) {
            return deleteNearSelection(cm, function(range) {
                if (range.empty()) {
                    var len = getLine(cm.doc, range.head.line).text.length;
                    if (range.head.ch == len && range.head.line < cm.lastLine()) {
                        return {
                            from: range.head,
                            to: Pos(range.head.line + 1, 0)
                        };
                    } else {
                        return {
                            from: range.head,
                            to: Pos(range.head.line, len)
                        };
                    }
                } else {
                    return {
                        from: range.from(),
                        to: range.to()
                    };
                }
            });
        },
        deleteLine: function deleteLine(cm) {
            return deleteNearSelection(cm, function(range) {
                return {
                    from: Pos(range.from().line, 0),
                    to: clipPos(cm.doc, Pos(range.to().line + 1, 0))
                };
            });
        },
        delLineLeft: function delLineLeft(cm) {
            return deleteNearSelection(cm, function(range) {
                return {
                    from: Pos(range.from().line, 0),
                    to: range.from()
                };
            });
        },
        delWrappedLineLeft: function delWrappedLineLeft(cm) {
            return deleteNearSelection(cm, function(range) {
                var top = cm.charCoords(range.head, "div").top + 5;
                var leftPos = cm.coordsChar({
                    left: 0,
                    top: top
                }, "div");
                return {
                    from: leftPos,
                    to: range.from()
                };
            });
        },
        delWrappedLineRight: function delWrappedLineRight(cm) {
            return deleteNearSelection(cm, function(range) {
                var top = cm.charCoords(range.head, "div").top + 5;
                var rightPos = cm.coordsChar({
                    left: cm.display.lineDiv.offsetWidth + 100,
                    top: top
                }, "div");
                return {
                    from: range.from(),
                    to: rightPos
                };
            });
        },
        undo: function undo(cm) {
            return cm.undo();
        },
        redo: function redo(cm) {
            return cm.redo();
        },
        undoSelection: function undoSelection(cm) {
            return cm.undoSelection();
        },
        redoSelection: function redoSelection(cm) {
            return cm.redoSelection();
        },
        goDocStart: function goDocStart(cm) {
            return cm.extendSelection(Pos(cm.firstLine(), 0));
        },
        goDocEnd: function goDocEnd(cm) {
            return cm.extendSelection(Pos(cm.lastLine()));
        },
        goLineStart: function goLineStart(cm) {
            return cm.extendSelectionsBy(function(range) {
                return lineStart(cm, range.head.line);
            }, {
                origin: "+move",
                bias: 1
            });
        },
        goLineStartSmart: function goLineStartSmart(cm) {
            return cm.extendSelectionsBy(function(range) {
                return lineStartSmart(cm, range.head);
            }, {
                origin: "+move",
                bias: 1
            });
        },
        goLineEnd: function goLineEnd(cm) {
            return cm.extendSelectionsBy(function(range) {
                return lineEnd(cm, range.head.line);
            }, {
                origin: "+move",
                bias: -1
            });
        },
        goLineRight: function goLineRight(cm) {
            return cm.extendSelectionsBy(function(range) {
                var top = cm.cursorCoords(range.head, "div").top + 5;
                return cm.coordsChar({
                    left: cm.display.lineDiv.offsetWidth + 100,
                    top: top
                }, "div");
            }, sel_move);
        },
        goLineLeft: function goLineLeft(cm) {
            return cm.extendSelectionsBy(function(range) {
                var top = cm.cursorCoords(range.head, "div").top + 5;
                return cm.coordsChar({
                    left: 0,
                    top: top
                }, "div");
            }, sel_move);
        },
        goLineLeftSmart: function goLineLeftSmart(cm) {
            return cm.extendSelectionsBy(function(range) {
                var top = cm.cursorCoords(range.head, "div").top + 5;
                var pos = cm.coordsChar({
                    left: 0,
                    top: top
                }, "div");
                if (pos.ch < cm.getLine(pos.line).search(/\S/)) {
                    return lineStartSmart(cm, range.head);
                }
                return pos;
            }, sel_move);
        },
        goLineUp: function goLineUp(cm) {
            return cm.moveV(-1, "line");
        },
        goLineDown: function goLineDown(cm) {
            return cm.moveV(1, "line");
        },
        goPageUp: function goPageUp(cm) {
            return cm.moveV(-1, "page");
        },
        goPageDown: function goPageDown(cm) {
            return cm.moveV(1, "page");
        },
        goCharLeft: function goCharLeft(cm) {
            return cm.moveH(-1, "char");
        },
        goCharRight: function goCharRight(cm) {
            return cm.moveH(1, "char");
        },
        goColumnLeft: function goColumnLeft(cm) {
            return cm.moveH(-1, "column");
        },
        goColumnRight: function goColumnRight(cm) {
            return cm.moveH(1, "column");
        },
        goWordLeft: function goWordLeft(cm) {
            return cm.moveH(-1, "word");
        },
        goGroupRight: function goGroupRight(cm) {
            return cm.moveH(1, "group");
        },
        goGroupLeft: function goGroupLeft(cm) {
            return cm.moveH(-1, "group");
        },
        goWordRight: function goWordRight(cm) {
            return cm.moveH(1, "word");
        },
        delCharBefore: function delCharBefore(cm) {
            return cm.deleteH(-1, "codepoint");
        },
        delCharAfter: function delCharAfter(cm) {
            return cm.deleteH(1, "char");
        },
        delWordBefore: function delWordBefore(cm) {
            return cm.deleteH(-1, "word");
        },
        delWordAfter: function delWordAfter(cm) {
            return cm.deleteH(1, "word");
        },
        delGroupBefore: function delGroupBefore(cm) {
            return cm.deleteH(-1, "group");
        },
        delGroupAfter: function delGroupAfter(cm) {
            return cm.deleteH(1, "group");
        },
        indentAuto: function indentAuto(cm) {
            return cm.indentSelection("smart");
        },
        indentMore: function indentMore(cm) {
            return cm.indentSelection("add");
        },
        indentLess: function indentLess(cm) {
            return cm.indentSelection("subtract");
        },
        insertTab: function insertTab(cm) {
            return cm.replaceSelection("	");
        },
        insertSoftTab: function insertSoftTab(cm) {
            var spaces = [], ranges = cm.listSelections(), tabSize = cm.options.tabSize;
            for(var i = 0; i < ranges.length; i++){
                var pos = ranges[i].from();
                var col = countColumn(cm.getLine(pos.line), pos.ch, tabSize);
                spaces.push(spaceStr(tabSize - col % tabSize));
            }
            cm.replaceSelections(spaces);
        },
        defaultTab: function defaultTab(cm) {
            if (cm.somethingSelected()) {
                cm.indentSelection("add");
            } else {
                cm.execCommand("insertTab");
            }
        },
        // Swap the two chars left and right of each selection's head.
        // Move cursor behind the two swapped characters afterwards.
        //
        // Doesn't consider line feeds a character.
        // Doesn't scan more than one line above to find a character.
        // Doesn't do anything on an empty line.
        // Doesn't do anything with non-empty selections.
        transposeChars: function transposeChars(cm) {
            return runInOp(cm, function() {
                var ranges = cm.listSelections(), newSel = [];
                for(var i = 0; i < ranges.length; i++){
                    if (!ranges[i].empty()) {
                        continue;
                    }
                    var cur = ranges[i].head, line = getLine(cm.doc, cur.line).text;
                    if (line) {
                        if (cur.ch == line.length) {
                            cur = new Pos(cur.line, cur.ch - 1);
                        }
                        if (cur.ch > 0) {
                            cur = new Pos(cur.line, cur.ch + 1);
                            cm.replaceRange(line.charAt(cur.ch - 1) + line.charAt(cur.ch - 2), Pos(cur.line, cur.ch - 2), cur, "+transpose");
                        } else if (cur.line > cm.doc.first) {
                            var prev = getLine(cm.doc, cur.line - 1).text;
                            if (prev) {
                                cur = new Pos(cur.line, 1);
                                cm.replaceRange(line.charAt(0) + cm.doc.lineSeparator() + prev.charAt(prev.length - 1), Pos(cur.line - 1, prev.length - 1), cur, "+transpose");
                            }
                        }
                    }
                    newSel.push(new Range(cur, cur));
                }
                cm.setSelections(newSel);
            });
        },
        newlineAndIndent: function newlineAndIndent(cm) {
            return runInOp(cm, function() {
                var sels = cm.listSelections();
                for(var i = sels.length - 1; i >= 0; i--){
                    cm.replaceRange(cm.doc.lineSeparator(), sels[i].anchor, sels[i].head, "+input");
                }
                sels = cm.listSelections();
                for(var i$1 = 0; i$1 < sels.length; i$1++){
                    cm.indentLine(sels[i$1].from().line, null, true);
                }
                ensureCursorVisible(cm);
            });
        },
        openLine: function openLine(cm) {
            return cm.replaceSelection("\n", "start");
        },
        toggleOverwrite: function toggleOverwrite(cm) {
            return cm.toggleOverwrite();
        }
    };
    // Note that, despite the name, this function is also used to check
    // for bound mouse clicks.
    var stopSeq = new Delayed;
    var lastStoppedKey = null;
    var DOUBLECLICK_DELAY = 400;
    var PastClick = function PastClick(time, pos, button) {
        this.time = time;
        this.pos = pos;
        this.button = button;
    };
    PastClick.prototype.compare = function(time, pos, button) {
        return this.time + DOUBLECLICK_DELAY > time && cmp(pos, this.pos) == 0 && button == this.button;
    };
    var lastClick, lastDoubleClick;
    var Init = {
        toString: function toString() {
            return "CodeMirror.Init";
        }
    };
    var defaults = {};
    var optionHandlers = {};
    // A CodeMirror instance represents an editor. This is the object
    // that user code is usually dealing with.
    function CodeMirror(place, options) {
        var this$1 = this;
        if (!_instanceof(this, CodeMirror)) {
            return new CodeMirror(place, options);
        }
        this.options = options = options ? copyObj(options) : {};
        // Determine effective options based on given values and defaults.
        copyObj(defaults, options, false);
        var doc = options.value;
        if (typeof doc == "string") {
            doc = new Doc(doc, options.mode, null, options.lineSeparator, options.direction);
        } else if (options.mode) {
            doc.modeOption = options.mode;
        }
        this.doc = doc;
        var input = new CodeMirror.inputStyles[options.inputStyle](this);
        var display = this.display = new Display(place, doc, input, options);
        display.wrapper.CodeMirror = this;
        themeChanged(this);
        if (options.lineWrapping) {
            this.display.wrapper.className += " CodeMirror-wrap";
        }
        initScrollbars(this);
        this.state = {
            keyMaps: [],
            overlays: [],
            modeGen: 0,
            overwrite: false,
            delayingBlurEvent: false,
            focused: false,
            suppressEdits: false,
            pasteIncoming: -1,
            cutIncoming: -1,
            selectingText: false,
            draggingText: false,
            highlight: new Delayed(),
            keySeq: null,
            specialChars: null
        };
        if (options.autofocus && !mobile) {
            display.input.focus();
        }
        // Override magic textarea content restore that IE sometimes does
        // on our hidden textarea on reload
        if (ie && ie_version < 11) {
            setTimeout(function() {
                return this$1.display.input.reset(true);
            }, 20);
        }
        registerEventHandlers(this);
        ensureGlobalHandlers();
        startOperation(this);
        this.curOp.forceUpdate = true;
        attachDoc(this, doc);
        if (options.autofocus && !mobile || this.hasFocus()) {
            setTimeout(function() {
                if (this$1.hasFocus() && !this$1.state.focused) {
                    onFocus(this$1);
                }
            }, 20);
        } else {
            onBlur(this);
        }
        for(var opt in optionHandlers){
            if (optionHandlers.hasOwnProperty(opt)) {
                optionHandlers[opt](this, options[opt], Init);
            }
        }
        maybeUpdateLineNumberWidth(this);
        if (options.finishInit) {
            options.finishInit(this);
        }
        for(var i = 0; i < initHooks.length; ++i){
            initHooks[i](this);
        }
        endOperation(this);
        // Suppress optimizelegibility in Webkit, since it breaks text
        // measuring on line wrapping boundaries.
        if (webkit && options.lineWrapping && getComputedStyle(display.lineDiv).textRendering == "optimizelegibility") {
            display.lineDiv.style.textRendering = "auto";
        }
    }
    // The default configuration options.
    CodeMirror.defaults = defaults;
    // Functions to run when options are changed.
    CodeMirror.optionHandlers = optionHandlers;
    var initHooks = [];
    CodeMirror.defineInitHook = function(f) {
        return initHooks.push(f);
    };
    // This will be set to a {lineWise: bool, text: [string]} object, so
    // that, when pasting, we know what kind of selections the copied
    // text was made out of.
    var lastCopied = null;
    // CONTENTEDITABLE INPUT STYLE
    var ContentEditableInput = function ContentEditableInput(cm) {
        this.cm = cm;
        this.lastAnchorNode = this.lastAnchorOffset = this.lastFocusNode = this.lastFocusOffset = null;
        this.polling = new Delayed();
        this.composing = null;
        this.gracePeriod = false;
        this.readDOMTimeout = null;
    };
    ContentEditableInput.prototype.init = function(display) {
        var belongsToInput = function belongsToInput(e) {
            for(var t = e.target; t; t = t.parentNode){
                if (t == div) {
                    return true;
                }
                if (/\bCodeMirror-(?:line)?widget\b/.test(t.className)) {
                    break;
                }
            }
            return false;
        };
        var onCopyCut = function onCopyCut(e) {
            if (!belongsToInput(e) || signalDOMEvent(cm, e)) {
                return;
            }
            if (cm.somethingSelected()) {
                setLastCopied({
                    lineWise: false,
                    text: cm.getSelections()
                });
                if (e.type == "cut") {
                    cm.replaceSelection("", null, "cut");
                }
            } else if (!cm.options.lineWiseCopyCut) {
                return;
            } else {
                var ranges = copyableRanges(cm);
                setLastCopied({
                    lineWise: true,
                    text: ranges.text
                });
                if (e.type == "cut") {
                    cm.operation(function() {
                        cm.setSelections(ranges.ranges, 0, sel_dontScroll);
                        cm.replaceSelection("", null, "cut");
                    });
                }
            }
            if (e.clipboardData) {
                e.clipboardData.clearData();
                var content = lastCopied.text.join("\n");
                // iOS exposes the clipboard API, but seems to discard content inserted into it
                e.clipboardData.setData("Text", content);
                if (e.clipboardData.getData("Text") == content) {
                    e.preventDefault();
                    return;
                }
            }
            // Old-fashioned briefly-focus-a-textarea hack
            var kludge = hiddenTextarea(), te = kludge.firstChild;
            cm.display.lineSpace.insertBefore(kludge, cm.display.lineSpace.firstChild);
            te.value = lastCopied.text.join("\n");
            var hadFocus = activeElt();
            selectInput(te);
            setTimeout(function() {
                cm.display.lineSpace.removeChild(kludge);
                hadFocus.focus();
                if (hadFocus == div) {
                    input.showPrimarySelection();
                }
            }, 50);
        };
        var this$1 = this;
        var input = this, cm = input.cm;
        var div = input.div = display.lineDiv;
        div.contentEditable = true;
        disableBrowserMagic(div, cm.options.spellcheck, cm.options.autocorrect, cm.options.autocapitalize);
        on(div, "paste", function(e) {
            if (!belongsToInput(e) || signalDOMEvent(cm, e) || handlePaste(e, cm)) {
                return;
            }
            // IE doesn't fire input events, so we schedule a read for the pasted content in this way
            if (ie_version <= 11) {
                setTimeout(operation(cm, function() {
                    return this$1.updateFromDOM();
                }), 20);
            }
        });
        on(div, "compositionstart", function(e) {
            this$1.composing = {
                data: e.data,
                done: false
            };
        });
        on(div, "compositionupdate", function(e) {
            if (!this$1.composing) {
                this$1.composing = {
                    data: e.data,
                    done: false
                };
            }
        });
        on(div, "compositionend", function(e) {
            if (this$1.composing) {
                if (e.data != this$1.composing.data) {
                    this$1.readFromDOMSoon();
                }
                this$1.composing.done = true;
            }
        });
        on(div, "touchstart", function() {
            return input.forceCompositionEnd();
        });
        on(div, "input", function() {
            if (!this$1.composing) {
                this$1.readFromDOMSoon();
            }
        });
        on(div, "copy", onCopyCut);
        on(div, "cut", onCopyCut);
    };
    ContentEditableInput.prototype.screenReaderLabelChanged = function(label) {
        // Label for screenreaders, accessibility
        if (label) {
            this.div.setAttribute("aria-label", label);
        } else {
            this.div.removeAttribute("aria-label");
        }
    };
    ContentEditableInput.prototype.prepareSelection = function() {
        var result = prepareSelection(this.cm, false);
        result.focus = activeElt() == this.div;
        return result;
    };
    ContentEditableInput.prototype.showSelection = function(info, takeFocus) {
        if (!info || !this.cm.display.view.length) {
            return;
        }
        if (info.focus || takeFocus) {
            this.showPrimarySelection();
        }
        this.showMultipleSelections(info);
    };
    ContentEditableInput.prototype.getSelection = function() {
        return this.cm.display.wrapper.ownerDocument.getSelection();
    };
    ContentEditableInput.prototype.showPrimarySelection = function() {
        var sel = this.getSelection(), cm = this.cm, prim = cm.doc.sel.primary();
        var from = prim.from(), to = prim.to();
        if (cm.display.viewTo == cm.display.viewFrom || from.line >= cm.display.viewTo || to.line < cm.display.viewFrom) {
            sel.removeAllRanges();
            return;
        }
        var curAnchor = domToPos(cm, sel.anchorNode, sel.anchorOffset);
        var curFocus = domToPos(cm, sel.focusNode, sel.focusOffset);
        if (curAnchor && !curAnchor.bad && curFocus && !curFocus.bad && cmp(minPos(curAnchor, curFocus), from) == 0 && cmp(maxPos(curAnchor, curFocus), to) == 0) {
            return;
        }
        var view = cm.display.view;
        var start = from.line >= cm.display.viewFrom && posToDOM(cm, from) || {
            node: view[0].measure.map[2],
            offset: 0
        };
        var end = to.line < cm.display.viewTo && posToDOM(cm, to);
        if (!end) {
            var measure = view[view.length - 1].measure;
            var map = measure.maps ? measure.maps[measure.maps.length - 1] : measure.map;
            end = {
                node: map[map.length - 1],
                offset: map[map.length - 2] - map[map.length - 3]
            };
        }
        if (!start || !end) {
            sel.removeAllRanges();
            return;
        }
        var old = sel.rangeCount && sel.getRangeAt(0), rng;
        try {
            rng = range(start.node, start.offset, end.offset, end.node);
        } catch (e) {} // Our model of the DOM might be outdated, in which case the range we try to set can be impossible
        if (rng) {
            if (!gecko && cm.state.focused) {
                sel.collapse(start.node, start.offset);
                if (!rng.collapsed) {
                    sel.removeAllRanges();
                    sel.addRange(rng);
                }
            } else {
                sel.removeAllRanges();
                sel.addRange(rng);
            }
            if (old && sel.anchorNode == null) {
                sel.addRange(old);
            } else if (gecko) {
                this.startGracePeriod();
            }
        }
        this.rememberSelection();
    };
    ContentEditableInput.prototype.startGracePeriod = function() {
        var this$1 = this;
        clearTimeout(this.gracePeriod);
        this.gracePeriod = setTimeout(function() {
            this$1.gracePeriod = false;
            if (this$1.selectionChanged()) {
                this$1.cm.operation(function() {
                    return this$1.cm.curOp.selectionChanged = true;
                });
            }
        }, 20);
    };
    ContentEditableInput.prototype.showMultipleSelections = function(info) {
        removeChildrenAndAdd(this.cm.display.cursorDiv, info.cursors);
        removeChildrenAndAdd(this.cm.display.selectionDiv, info.selection);
    };
    ContentEditableInput.prototype.rememberSelection = function() {
        var sel = this.getSelection();
        this.lastAnchorNode = sel.anchorNode;
        this.lastAnchorOffset = sel.anchorOffset;
        this.lastFocusNode = sel.focusNode;
        this.lastFocusOffset = sel.focusOffset;
    };
    ContentEditableInput.prototype.selectionInEditor = function() {
        var sel = this.getSelection();
        if (!sel.rangeCount) {
            return false;
        }
        var node = sel.getRangeAt(0).commonAncestorContainer;
        return contains(this.div, node);
    };
    ContentEditableInput.prototype.focus = function() {
        if (this.cm.options.readOnly != "nocursor") {
            if (!this.selectionInEditor() || activeElt() != this.div) {
                this.showSelection(this.prepareSelection(), true);
            }
            this.div.focus();
        }
    };
    ContentEditableInput.prototype.blur = function() {
        this.div.blur();
    };
    ContentEditableInput.prototype.getField = function() {
        return this.div;
    };
    ContentEditableInput.prototype.supportsTouch = function() {
        return true;
    };
    ContentEditableInput.prototype.receivedFocus = function() {
        var this$1 = this;
        var input = this;
        if (this.selectionInEditor()) {
            setTimeout(function() {
                return this$1.pollSelection();
            }, 20);
        } else {
            runInOp(this.cm, function() {
                return input.cm.curOp.selectionChanged = true;
            });
        }
        function poll() {
            if (input.cm.state.focused) {
                input.pollSelection();
                input.polling.set(input.cm.options.pollInterval, poll);
            }
        }
        this.polling.set(this.cm.options.pollInterval, poll);
    };
    ContentEditableInput.prototype.selectionChanged = function() {
        var sel = this.getSelection();
        return sel.anchorNode != this.lastAnchorNode || sel.anchorOffset != this.lastAnchorOffset || sel.focusNode != this.lastFocusNode || sel.focusOffset != this.lastFocusOffset;
    };
    ContentEditableInput.prototype.pollSelection = function() {
        if (this.readDOMTimeout != null || this.gracePeriod || !this.selectionChanged()) {
            return;
        }
        var sel = this.getSelection(), cm = this.cm;
        // On Android Chrome (version 56, at least), backspacing into an
        // uneditable block element will put the cursor in that element,
        // and then, because it's not editable, hide the virtual keyboard.
        // Because Android doesn't allow us to actually detect backspace
        // presses in a sane way, this code checks for when that happens
        // and simulates a backspace press in this case.
        if (android && chrome && this.cm.display.gutterSpecs.length && isInGutter(sel.anchorNode)) {
            this.cm.triggerOnKeyDown({
                type: "keydown",
                keyCode: 8,
                preventDefault: Math.abs
            });
            this.blur();
            this.focus();
            return;
        }
        if (this.composing) {
            return;
        }
        this.rememberSelection();
        var anchor = domToPos(cm, sel.anchorNode, sel.anchorOffset);
        var head = domToPos(cm, sel.focusNode, sel.focusOffset);
        if (anchor && head) {
            runInOp(cm, function() {
                setSelection(cm.doc, simpleSelection(anchor, head), sel_dontScroll);
                if (anchor.bad || head.bad) {
                    cm.curOp.selectionChanged = true;
                }
            });
        }
    };
    ContentEditableInput.prototype.pollContent = function() {
        if (this.readDOMTimeout != null) {
            clearTimeout(this.readDOMTimeout);
            this.readDOMTimeout = null;
        }
        var cm = this.cm, display = cm.display, sel = cm.doc.sel.primary();
        var from = sel.from(), to = sel.to();
        if (from.ch == 0 && from.line > cm.firstLine()) {
            from = Pos(from.line - 1, getLine(cm.doc, from.line - 1).length);
        }
        if (to.ch == getLine(cm.doc, to.line).text.length && to.line < cm.lastLine()) {
            to = Pos(to.line + 1, 0);
        }
        if (from.line < display.viewFrom || to.line > display.viewTo - 1) {
            return false;
        }
        var fromIndex, fromLine, fromNode;
        if (from.line == display.viewFrom || (fromIndex = findViewIndex(cm, from.line)) == 0) {
            fromLine = lineNo(display.view[0].line);
            fromNode = display.view[0].node;
        } else {
            fromLine = lineNo(display.view[fromIndex].line);
            fromNode = display.view[fromIndex - 1].node.nextSibling;
        }
        var toIndex = findViewIndex(cm, to.line);
        var toLine, toNode;
        if (toIndex == display.view.length - 1) {
            toLine = display.viewTo - 1;
            toNode = display.lineDiv.lastChild;
        } else {
            toLine = lineNo(display.view[toIndex + 1].line) - 1;
            toNode = display.view[toIndex + 1].node.previousSibling;
        }
        if (!fromNode) {
            return false;
        }
        var newText = cm.doc.splitLines(domTextBetween(cm, fromNode, toNode, fromLine, toLine));
        var oldText = getBetween(cm.doc, Pos(fromLine, 0), Pos(toLine, getLine(cm.doc, toLine).text.length));
        while(newText.length > 1 && oldText.length > 1){
            if (lst(newText) == lst(oldText)) {
                newText.pop();
                oldText.pop();
                toLine--;
            } else if (newText[0] == oldText[0]) {
                newText.shift();
                oldText.shift();
                fromLine++;
            } else {
                break;
            }
        }
        var cutFront = 0, cutEnd = 0;
        var newTop = newText[0], oldTop = oldText[0], maxCutFront = Math.min(newTop.length, oldTop.length);
        while(cutFront < maxCutFront && newTop.charCodeAt(cutFront) == oldTop.charCodeAt(cutFront)){
            ++cutFront;
        }
        var newBot = lst(newText), oldBot = lst(oldText);
        var maxCutEnd = Math.min(newBot.length - (newText.length == 1 ? cutFront : 0), oldBot.length - (oldText.length == 1 ? cutFront : 0));
        while(cutEnd < maxCutEnd && newBot.charCodeAt(newBot.length - cutEnd - 1) == oldBot.charCodeAt(oldBot.length - cutEnd - 1)){
            ++cutEnd;
        }
        // Try to move start of change to start of selection if ambiguous
        if (newText.length == 1 && oldText.length == 1 && fromLine == from.line) {
            while(cutFront && cutFront > from.ch && newBot.charCodeAt(newBot.length - cutEnd - 1) == oldBot.charCodeAt(oldBot.length - cutEnd - 1)){
                cutFront--;
                cutEnd++;
            }
        }
        newText[newText.length - 1] = newBot.slice(0, newBot.length - cutEnd).replace(/^\u200b+/, "");
        newText[0] = newText[0].slice(cutFront).replace(/\u200b+$/, "");
        var chFrom = Pos(fromLine, cutFront);
        var chTo = Pos(toLine, oldText.length ? lst(oldText).length - cutEnd : 0);
        if (newText.length > 1 || newText[0] || cmp(chFrom, chTo)) {
            replaceRange(cm.doc, newText, chFrom, chTo, "+input");
            return true;
        }
    };
    ContentEditableInput.prototype.ensurePolled = function() {
        this.forceCompositionEnd();
    };
    ContentEditableInput.prototype.reset = function() {
        this.forceCompositionEnd();
    };
    ContentEditableInput.prototype.forceCompositionEnd = function() {
        if (!this.composing) {
            return;
        }
        clearTimeout(this.readDOMTimeout);
        this.composing = null;
        this.updateFromDOM();
        this.div.blur();
        this.div.focus();
    };
    ContentEditableInput.prototype.readFromDOMSoon = function() {
        var this$1 = this;
        if (this.readDOMTimeout != null) {
            return;
        }
        this.readDOMTimeout = setTimeout(function() {
            this$1.readDOMTimeout = null;
            if (this$1.composing) {
                if (this$1.composing.done) {
                    this$1.composing = null;
                } else {
                    return;
                }
            }
            this$1.updateFromDOM();
        }, 80);
    };
    ContentEditableInput.prototype.updateFromDOM = function() {
        var this$1 = this;
        if (this.cm.isReadOnly() || !this.pollContent()) {
            runInOp(this.cm, function() {
                return regChange(this$1.cm);
            });
        }
    };
    ContentEditableInput.prototype.setUneditable = function(node) {
        node.contentEditable = "false";
    };
    ContentEditableInput.prototype.onKeyPress = function(e) {
        if (e.charCode == 0 || this.composing) {
            return;
        }
        e.preventDefault();
        if (!this.cm.isReadOnly()) {
            operation(this.cm, applyTextInput)(this.cm, String.fromCharCode(e.charCode == null ? e.keyCode : e.charCode), 0);
        }
    };
    ContentEditableInput.prototype.readOnlyChanged = function(val) {
        this.div.contentEditable = String(val != "nocursor");
    };
    ContentEditableInput.prototype.onContextMenu = function() {};
    ContentEditableInput.prototype.resetPosition = function() {};
    ContentEditableInput.prototype.needsContentAttribute = true;
    // TEXTAREA INPUT STYLE
    var TextareaInput = function TextareaInput(cm) {
        this.cm = cm;
        // See input.poll and input.reset
        this.prevInput = "";
        // Flag that indicates whether we expect input to appear real soon
        // now (after some event like 'keypress' or 'input') and are
        // polling intensively.
        this.pollingFast = false;
        // Self-resetting timeout for the poller
        this.polling = new Delayed();
        // Used to work around IE issue with selection being forgotten when focus moves away from textarea
        this.hasSelection = false;
        this.composing = null;
    };
    TextareaInput.prototype.init = function(display) {
        var prepareCopyCut = function prepareCopyCut(e) {
            if (signalDOMEvent(cm, e)) {
                return;
            }
            if (cm.somethingSelected()) {
                setLastCopied({
                    lineWise: false,
                    text: cm.getSelections()
                });
            } else if (!cm.options.lineWiseCopyCut) {
                return;
            } else {
                var ranges = copyableRanges(cm);
                setLastCopied({
                    lineWise: true,
                    text: ranges.text
                });
                if (e.type == "cut") {
                    cm.setSelections(ranges.ranges, null, sel_dontScroll);
                } else {
                    input.prevInput = "";
                    te.value = ranges.text.join("\n");
                    selectInput(te);
                }
            }
            if (e.type == "cut") {
                cm.state.cutIncoming = +new Date;
            }
        };
        var this$1 = this;
        var input = this, cm = this.cm;
        this.createField(display);
        var te = this.textarea;
        display.wrapper.insertBefore(this.wrapper, display.wrapper.firstChild);
        // Needed to hide big blue blinking cursor on Mobile Safari (doesn't seem to work in iOS 8 anymore)
        if (ios) {
            te.style.width = "0px";
        }
        on(te, "input", function() {
            if (ie && ie_version >= 9 && this$1.hasSelection) {
                this$1.hasSelection = null;
            }
            input.poll();
        });
        on(te, "paste", function(e) {
            if (signalDOMEvent(cm, e) || handlePaste(e, cm)) {
                return;
            }
            cm.state.pasteIncoming = +new Date;
            input.fastPoll();
        });
        on(te, "cut", prepareCopyCut);
        on(te, "copy", prepareCopyCut);
        on(display.scroller, "paste", function(e) {
            if (eventInWidget(display, e) || signalDOMEvent(cm, e)) {
                return;
            }
            if (!te.dispatchEvent) {
                cm.state.pasteIncoming = +new Date;
                input.focus();
                return;
            }
            // Pass the `paste` event to the textarea so it's handled by its event listener.
            var event = new Event("paste");
            event.clipboardData = e.clipboardData;
            te.dispatchEvent(event);
        });
        // Prevent normal selection in the editor (we handle our own)
        on(display.lineSpace, "selectstart", function(e) {
            if (!eventInWidget(display, e)) {
                e_preventDefault(e);
            }
        });
        on(te, "compositionstart", function() {
            var start = cm.getCursor("from");
            if (input.composing) {
                input.composing.range.clear();
            }
            input.composing = {
                start: start,
                range: cm.markText(start, cm.getCursor("to"), {
                    className: "CodeMirror-composing"
                })
            };
        });
        on(te, "compositionend", function() {
            if (input.composing) {
                input.poll();
                input.composing.range.clear();
                input.composing = null;
            }
        });
    };
    TextareaInput.prototype.createField = function(_display) {
        // Wraps and hides input textarea
        this.wrapper = hiddenTextarea();
        // The semihidden textarea that is focused when the editor is
        // focused, and receives input.
        this.textarea = this.wrapper.firstChild;
    };
    TextareaInput.prototype.screenReaderLabelChanged = function(label) {
        // Label for screenreaders, accessibility
        if (label) {
            this.textarea.setAttribute("aria-label", label);
        } else {
            this.textarea.removeAttribute("aria-label");
        }
    };
    TextareaInput.prototype.prepareSelection = function() {
        // Redraw the selection and/or cursor
        var cm = this.cm, display = cm.display, doc = cm.doc;
        var result = prepareSelection(cm);
        // Move the hidden textarea near the cursor to prevent scrolling artifacts
        if (cm.options.moveInputWithCursor) {
            var headPos = cursorCoords(cm, doc.sel.primary().head, "div");
            var wrapOff = display.wrapper.getBoundingClientRect(), lineOff = display.lineDiv.getBoundingClientRect();
            result.teTop = Math.max(0, Math.min(display.wrapper.clientHeight - 10, headPos.top + lineOff.top - wrapOff.top));
            result.teLeft = Math.max(0, Math.min(display.wrapper.clientWidth - 10, headPos.left + lineOff.left - wrapOff.left));
        }
        return result;
    };
    TextareaInput.prototype.showSelection = function(drawn) {
        var cm = this.cm, display = cm.display;
        removeChildrenAndAdd(display.cursorDiv, drawn.cursors);
        removeChildrenAndAdd(display.selectionDiv, drawn.selection);
        if (drawn.teTop != null) {
            this.wrapper.style.top = drawn.teTop + "px";
            this.wrapper.style.left = drawn.teLeft + "px";
        }
    };
    // Reset the input to correspond to the selection (or to be empty,
    // when not typing and nothing is selected)
    TextareaInput.prototype.reset = function(typing) {
        if (this.contextMenuPending || this.composing) {
            return;
        }
        var cm = this.cm;
        if (cm.somethingSelected()) {
            this.prevInput = "";
            var content = cm.getSelection();
            this.textarea.value = content;
            if (cm.state.focused) {
                selectInput(this.textarea);
            }
            if (ie && ie_version >= 9) {
                this.hasSelection = content;
            }
        } else if (!typing) {
            this.prevInput = this.textarea.value = "";
            if (ie && ie_version >= 9) {
                this.hasSelection = null;
            }
        }
    };
    TextareaInput.prototype.getField = function() {
        return this.textarea;
    };
    TextareaInput.prototype.supportsTouch = function() {
        return false;
    };
    TextareaInput.prototype.focus = function() {
        if (this.cm.options.readOnly != "nocursor" && (!mobile || activeElt() != this.textarea)) {
            try {
                this.textarea.focus();
            } catch (e) {} // IE8 will throw if the textarea is display: none or not in DOM
        }
    };
    TextareaInput.prototype.blur = function() {
        this.textarea.blur();
    };
    TextareaInput.prototype.resetPosition = function() {
        this.wrapper.style.top = this.wrapper.style.left = 0;
    };
    TextareaInput.prototype.receivedFocus = function() {
        this.slowPoll();
    };
    // Poll for input changes, using the normal rate of polling. This
    // runs as long as the editor is focused.
    TextareaInput.prototype.slowPoll = function() {
        var this$1 = this;
        if (this.pollingFast) {
            return;
        }
        this.polling.set(this.cm.options.pollInterval, function() {
            this$1.poll();
            if (this$1.cm.state.focused) {
                this$1.slowPoll();
            }
        });
    };
    // When an event has just come in that is likely to add or change
    // something in the input textarea, we poll faster, to ensure that
    // the change appears on the screen quickly.
    TextareaInput.prototype.fastPoll = function() {
        var missed = false, input = this;
        input.pollingFast = true;
        function p() {
            var changed = input.poll();
            if (!changed && !missed) {
                missed = true;
                input.polling.set(60, p);
            } else {
                input.pollingFast = false;
                input.slowPoll();
            }
        }
        input.polling.set(20, p);
    };
    // Read input from the textarea, and update the document to match.
    // When something is selected, it is present in the textarea, and
    // selected (unless it is huge, in which case a placeholder is
    // used). When nothing is selected, the cursor sits after previously
    // seen text (can be empty), which is stored in prevInput (we must
    // not reset the textarea when typing, because that breaks IME).
    TextareaInput.prototype.poll = function() {
        var this$1 = this;
        var cm = this.cm, input = this.textarea, prevInput = this.prevInput;
        // Since this is called a *lot*, try to bail out as cheaply as
        // possible when it is clear that nothing happened. hasSelection
        // will be the case when there is a lot of text in the textarea,
        // in which case reading its value would be expensive.
        if (this.contextMenuPending || !cm.state.focused || hasSelection(input) && !prevInput && !this.composing || cm.isReadOnly() || cm.options.disableInput || cm.state.keySeq) {
            return false;
        }
        var text = input.value;
        // If nothing changed, bail.
        if (text == prevInput && !cm.somethingSelected()) {
            return false;
        }
        // Work around nonsensical selection resetting in IE9/10, and
        // inexplicable appearance of private area unicode characters on
        // some key combos in Mac (#2689).
        if (ie && ie_version >= 9 && this.hasSelection === text || mac && /[\uf700-\uf7ff]/.test(text)) {
            cm.display.input.reset();
            return false;
        }
        if (cm.doc.sel == cm.display.selForContextMenu) {
            var first = text.charCodeAt(0);
            if (first == 0x200b && !prevInput) {
                prevInput = "​";
            }
            if (first == 0x21da) {
                this.reset();
                return this.cm.execCommand("undo");
            }
        }
        // Find the part of the input that is actually new
        var same = 0, l = Math.min(prevInput.length, text.length);
        while(same < l && prevInput.charCodeAt(same) == text.charCodeAt(same)){
            ++same;
        }
        runInOp(cm, function() {
            applyTextInput(cm, text.slice(same), prevInput.length - same, null, this$1.composing ? "*compose" : null);
            // Don't leave long text in the textarea, since it makes further polling slow
            if (text.length > 1000 || text.indexOf("\n") > -1) {
                input.value = this$1.prevInput = "";
            } else {
                this$1.prevInput = text;
            }
            if (this$1.composing) {
                this$1.composing.range.clear();
                this$1.composing.range = cm.markText(this$1.composing.start, cm.getCursor("to"), {
                    className: "CodeMirror-composing"
                });
            }
        });
        return true;
    };
    TextareaInput.prototype.ensurePolled = function() {
        if (this.pollingFast && this.poll()) {
            this.pollingFast = false;
        }
    };
    TextareaInput.prototype.onKeyPress = function() {
        if (ie && ie_version >= 9) {
            this.hasSelection = null;
        }
        this.fastPoll();
    };
    TextareaInput.prototype.onContextMenu = function(e) {
        var prepareSelectAllHack = // Select-all will be greyed out if there's nothing to select, so
        // this adds a zero-width space so that we can later check whether
        // it got selected.
        function prepareSelectAllHack() {
            if (te.selectionStart != null) {
                var selected = cm.somethingSelected();
                var extval = "​" + (selected ? te.value : "");
                te.value = "⇚"; // Used to catch context-menu undo
                te.value = extval;
                input.prevInput = selected ? "" : "​";
                te.selectionStart = 1;
                te.selectionEnd = extval.length;
                // Re-set this, in case some other handler touched the
                // selection in the meantime.
                display.selForContextMenu = cm.doc.sel;
            }
        };
        var input = this, cm = input.cm, display = cm.display, te = input.textarea;
        if (input.contextMenuPending) {
            input.contextMenuPending();
        }
        var pos = posFromMouse(cm, e), scrollPos = display.scroller.scrollTop;
        if (!pos || presto) {
            return;
        } // Opera is difficult.
        // Reset the current text selection only if the click is done outside of the selection
        // and 'resetSelectionOnContextMenu' option is true.
        var reset = cm.options.resetSelectionOnContextMenu;
        if (reset && cm.doc.sel.contains(pos) == -1) {
            operation(cm, setSelection)(cm.doc, simpleSelection(pos), sel_dontScroll);
        }
        var oldCSS = te.style.cssText, oldWrapperCSS = input.wrapper.style.cssText;
        var wrapperBox = input.wrapper.offsetParent.getBoundingClientRect();
        input.wrapper.style.cssText = "position: static";
        te.style.cssText = "position: absolute; width: 30px; height: 30px;\n      top: " + (e.clientY - wrapperBox.top - 5) + "px; left: " + (e.clientX - wrapperBox.left - 5) + "px;\n      z-index: 1000; background: " + (ie ? "rgba(255, 255, 255, .05)" : "transparent") + ";\n      outline: none; border-width: 0; outline: none; overflow: hidden; opacity: .05; filter: alpha(opacity=5);";
        var oldScrollY;
        if (webkit) {
            oldScrollY = window.scrollY;
        } // Work around Chrome issue (#2712)
        display.input.focus();
        if (webkit) {
            window.scrollTo(null, oldScrollY);
        }
        display.input.reset();
        // Adds "Select all" to context menu in FF
        if (!cm.somethingSelected()) {
            te.value = input.prevInput = " ";
        }
        input.contextMenuPending = rehide;
        display.selForContextMenu = cm.doc.sel;
        clearTimeout(display.detectingSelectAll);
        function rehide() {
            if (input.contextMenuPending != rehide) {
                return;
            }
            input.contextMenuPending = false;
            input.wrapper.style.cssText = oldWrapperCSS;
            te.style.cssText = oldCSS;
            if (ie && ie_version < 9) {
                display.scrollbars.setScrollTop(display.scroller.scrollTop = scrollPos);
            }
            // Try to detect the user choosing select-all
            if (te.selectionStart != null) {
                if (!ie || ie && ie_version < 9) {
                    prepareSelectAllHack();
                }
                var i = 0, poll = function poll1() {
                    if (display.selForContextMenu == cm.doc.sel && te.selectionStart == 0 && te.selectionEnd > 0 && input.prevInput == "​") {
                        operation(cm, selectAll)(cm);
                    } else if (i++ < 10) {
                        display.detectingSelectAll = setTimeout(poll, 500);
                    } else {
                        display.selForContextMenu = null;
                        display.input.reset();
                    }
                };
                display.detectingSelectAll = setTimeout(poll, 200);
            }
        }
        if (ie && ie_version >= 9) {
            prepareSelectAllHack();
        }
        if (captureRightClick) {
            e_stop(e);
            var mouseup = function mouseup1() {
                off(window, "mouseup", mouseup);
                setTimeout(rehide, 20);
            };
            on(window, "mouseup", mouseup);
        } else {
            setTimeout(rehide, 50);
        }
    };
    TextareaInput.prototype.readOnlyChanged = function(val) {
        if (!val) {
            this.reset();
        }
        this.textarea.disabled = val == "nocursor";
        this.textarea.readOnly = !!val;
    };
    TextareaInput.prototype.setUneditable = function() {};
    TextareaInput.prototype.needsContentAttribute = false;
    // EDITOR CONSTRUCTOR
    defineOptions(CodeMirror);
    addEditorMethods(CodeMirror);
    // Set up methods on CodeMirror's prototype to redirect to the editor's document.
    var dontDelegate = "iter insert remove copy getEditor constructor".split(" ");
    for(var prop in Doc.prototype){
        if (Doc.prototype.hasOwnProperty(prop) && indexOf(dontDelegate, prop) < 0) {
            CodeMirror.prototype[prop] = function(method) {
                return function() {
                    return method.apply(this.doc, arguments);
                };
            }(Doc.prototype[prop]);
        }
    }
    eventMixin(Doc);
    CodeMirror.inputStyles = {
        "textarea": TextareaInput,
        "contenteditable": ContentEditableInput
    };
    // Extra arguments are stored as the mode's dependencies, which is
    // used by (legacy) mechanisms like loadmode.js to automatically
    // load a mode. (Preferred mechanism is the require/define calls.)
    CodeMirror.defineMode = function(name /*, mode, …*/ ) {
        if (!CodeMirror.defaults.mode && name != "null") {
            CodeMirror.defaults.mode = name;
        }
        defineMode.apply(this, arguments);
    };
    CodeMirror.defineMIME = defineMIME;
    // Minimal default mode.
    CodeMirror.defineMode("null", function() {
        return {
            token: function token(stream) {
                return stream.skipToEnd();
            }
        };
    });
    CodeMirror.defineMIME("text/plain", "null");
    // EXTENSIONS
    CodeMirror.defineExtension = function(name, func) {
        CodeMirror.prototype[name] = func;
    };
    CodeMirror.defineDocExtension = function(name, func) {
        Doc.prototype[name] = func;
    };
    CodeMirror.fromTextArea = fromTextArea;
    addLegacyProps(CodeMirror);
    CodeMirror.version = "5.64.0";
    return CodeMirror;
});
});

var activeLine = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var clearActiveLines = function clearActiveLines(cm) {
        for(var i = 0; i < cm.state.activeLines.length; i++){
            cm.removeLineClass(cm.state.activeLines[i], "wrap", WRAP_CLASS);
            cm.removeLineClass(cm.state.activeLines[i], "background", BACK_CLASS);
            cm.removeLineClass(cm.state.activeLines[i], "gutter", GUTT_CLASS);
        }
    };
    var sameArray = function sameArray(a, b) {
        if (a.length != b.length) return false;
        for(var i = 0; i < a.length; i++)if (a[i] != b[i]) return false;
        return true;
    };
    var updateActiveLines = function updateActiveLines(cm, ranges) {
        var active = [];
        for(var i = 0; i < ranges.length; i++){
            var range = ranges[i];
            var option = cm.getOption("styleActiveLine");
            if (typeof option == "object" && option.nonEmpty ? range.anchor.line != range.head.line : !range.empty()) continue;
            var line = cm.getLineHandleVisualStart(range.head.line);
            if (active[active.length - 1] != line) active.push(line);
        }
        if (sameArray(cm.state.activeLines, active)) return;
        cm.operation(function() {
            clearActiveLines(cm);
            for(var i = 0; i < active.length; i++){
                cm.addLineClass(active[i], "wrap", WRAP_CLASS);
                cm.addLineClass(active[i], "background", BACK_CLASS);
                cm.addLineClass(active[i], "gutter", GUTT_CLASS);
            }
            cm.state.activeLines = active;
        });
    };
    var selectionChange = function selectionChange(cm, sel) {
        updateActiveLines(cm, sel.ranges);
    };
    var WRAP_CLASS = "CodeMirror-activeline";
    var BACK_CLASS = "CodeMirror-activeline-background";
    var GUTT_CLASS = "CodeMirror-activeline-gutter";
    CodeMirror1.defineOption("styleActiveLine", false, function(cm, val, old) {
        var prev = old == CodeMirror1.Init ? false : old;
        if (val == prev) return;
        if (prev) {
            cm.off("beforeSelectionChange", selectionChange);
            clearActiveLines(cm);
            delete cm.state.activeLines;
        }
        if (val) {
            cm.state.activeLines = [];
            updateActiveLines(cm, cm.listSelections());
            cm.on("beforeSelectionChange", selectionChange);
        }
    });
});
});

var matchbrackets = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var bracketRegex = function bracketRegex(config) {
        return config && config.bracketRegex || /[(){}[\]]/;
    };
    var findMatchingBracket = function findMatchingBracket(cm, where, config) {
        var line = cm.getLineHandle(where.line), pos = where.ch - 1;
        var afterCursor = config && config.afterCursor;
        if (afterCursor == null) afterCursor = /(^| )cm-fat-cursor($| )/.test(cm.getWrapperElement().className);
        var re = bracketRegex(config);
        // A cursor is defined as between two characters, but in in vim command mode
        // (i.e. not insert mode), the cursor is visually represented as a
        // highlighted box on top of the 2nd character. Otherwise, we allow matches
        // from before or after the cursor.
        var match = !afterCursor && pos >= 0 && re.test(line.text.charAt(pos)) && matching[line.text.charAt(pos)] || re.test(line.text.charAt(pos + 1)) && matching[line.text.charAt(++pos)];
        if (!match) return null;
        var dir = match.charAt(1) == ">" ? 1 : -1;
        if (config && config.strict && dir > 0 != (pos == where.ch)) return null;
        var style = cm.getTokenTypeAt(Pos(where.line, pos + 1));
        var found = scanForBracket(cm, Pos(where.line, pos + (dir > 0 ? 1 : 0)), dir, style, config);
        if (found == null) return null;
        return {
            from: Pos(where.line, pos),
            to: found && found.pos,
            match: found && found.ch == match.charAt(0),
            forward: dir > 0
        };
    };
    var scanForBracket = // bracketRegex is used to specify which type of bracket to scan
    // should be a regexp, e.g. /[[\]]/
    //
    // Note: If "where" is on an open bracket, then this bracket is ignored.
    //
    // Returns false when no bracket was found, null when it reached
    // maxScanLines and gave up
    function scanForBracket(cm, where, dir, style, config) {
        var maxScanLen = config && config.maxScanLineLength || 10000;
        var maxScanLines = config && config.maxScanLines || 1000;
        var stack = [];
        var re = bracketRegex(config);
        var lineEnd = dir > 0 ? Math.min(where.line + maxScanLines, cm.lastLine() + 1) : Math.max(cm.firstLine() - 1, where.line - maxScanLines);
        for(var lineNo = where.line; lineNo != lineEnd; lineNo += dir){
            var line = cm.getLine(lineNo);
            if (!line) continue;
            var pos = dir > 0 ? 0 : line.length - 1, end = dir > 0 ? line.length : -1;
            if (line.length > maxScanLen) continue;
            if (lineNo == where.line) pos = where.ch - (dir < 0 ? 1 : 0);
            for(; pos != end; pos += dir){
                var ch = line.charAt(pos);
                if (re.test(ch) && (style === undefined || (cm.getTokenTypeAt(Pos(lineNo, pos + 1)) || "") == (style || ""))) {
                    var match = matching[ch];
                    if (match && match.charAt(1) == ">" == dir > 0) stack.push(ch);
                    else if (!stack.length) return {
                        pos: Pos(lineNo, pos),
                        ch: ch
                    };
                    else stack.pop();
                }
            }
        }
        return lineNo - dir == (dir > 0 ? cm.lastLine() : cm.firstLine()) ? false : null;
    };
    var matchBrackets = function matchBrackets(cm, autoclear, config) {
        // Disable brace matching in long lines, since it'll cause hugely slow updates
        var maxHighlightLen = cm.state.matchBrackets.maxHighlightLineLength || 1000, highlightNonMatching = config && config.highlightNonMatching;
        var marks = [], ranges = cm.listSelections();
        for(var i = 0; i < ranges.length; i++){
            var match = ranges[i].empty() && findMatchingBracket(cm, ranges[i].head, config);
            if (match && (match.match || highlightNonMatching !== false) && cm.getLine(match.from.line).length <= maxHighlightLen) {
                var style = match.match ? "CodeMirror-matchingbracket" : "CodeMirror-nonmatchingbracket";
                marks.push(cm.markText(match.from, Pos(match.from.line, match.from.ch + 1), {
                    className: style
                }));
                if (match.to && cm.getLine(match.to.line).length <= maxHighlightLen) marks.push(cm.markText(match.to, Pos(match.to.line, match.to.ch + 1), {
                    className: style
                }));
            }
        }
        if (marks.length) {
            // Kludge to work around the IE bug from issue #1193, where text
            // input stops going to the textarea whenever this fires.
            if (ie_lt8 && cm.state.focused) cm.focus();
            var clear = function clear() {
                cm.operation(function() {
                    for(var i = 0; i < marks.length; i++)marks[i].clear();
                });
            };
            if (autoclear) setTimeout(clear, 800);
            else return clear;
        }
    };
    var doMatchBrackets = function doMatchBrackets(cm) {
        cm.operation(function() {
            if (cm.state.matchBrackets.currentlyHighlighted) {
                cm.state.matchBrackets.currentlyHighlighted();
                cm.state.matchBrackets.currentlyHighlighted = null;
            }
            cm.state.matchBrackets.currentlyHighlighted = matchBrackets(cm, false, cm.state.matchBrackets);
        });
    };
    var clearHighlighted = function clearHighlighted(cm) {
        if (cm.state.matchBrackets && cm.state.matchBrackets.currentlyHighlighted) {
            cm.state.matchBrackets.currentlyHighlighted();
            cm.state.matchBrackets.currentlyHighlighted = null;
        }
    };
    var ie_lt8 = /MSIE \d/.test(navigator.userAgent) && (document.documentMode == null || document.documentMode < 8);
    var Pos = CodeMirror1.Pos;
    var matching = {
        "(": ")>",
        ")": "(<",
        "[": "]>",
        "]": "[<",
        "{": "}>",
        "}": "{<",
        "<": ">>",
        ">": "<<"
    };
    CodeMirror1.defineOption("matchBrackets", false, function(cm, val, old) {
        if (old && old != CodeMirror1.Init) {
            cm.off("cursorActivity", doMatchBrackets);
            cm.off("focus", doMatchBrackets);
            cm.off("blur", clearHighlighted);
            clearHighlighted(cm);
        }
        if (val) {
            cm.state.matchBrackets = typeof val == "object" ? val : {};
            cm.on("cursorActivity", doMatchBrackets);
            cm.on("focus", doMatchBrackets);
            cm.on("blur", clearHighlighted);
        }
    });
    CodeMirror1.defineExtension("matchBrackets", function() {
        matchBrackets(this, true);
    });
    CodeMirror1.defineExtension("findMatchingBracket", function(pos, config, oldConfig) {
        // Backwards-compatibility kludge
        if (oldConfig || typeof config == "boolean") {
            if (!oldConfig) {
                config = config ? {
                    strict: true
                } : null;
            } else {
                oldConfig.strict = config;
                config = oldConfig;
            }
        }
        return findMatchingBracket(this, pos, config);
    });
    CodeMirror1.defineExtension("scanForBracket", function(pos, dir, style, config) {
        return scanForBracket(this, pos, dir, style, config);
    });
});
});

var css_248z$1 = '.CodeMirror{font-family:monospace;height:300px;color:#000;direction:ltr}.CodeMirror-lines{padding:4px 0}.CodeMirror pre.CodeMirror-line,.CodeMirror pre.CodeMirror-line-like{padding:0 4px}.CodeMirror-gutter-filler,.CodeMirror-scrollbar-filler{background-color:#fff}.CodeMirror-gutters{border-right:1px solid #ddd;background-color:#f7f7f7;white-space:nowrap}.CodeMirror-linenumber{padding:0 3px 0 5px;min-width:20px;text-align:right;color:#999;white-space:nowrap}.CodeMirror-guttermarker{color:#000}.CodeMirror-guttermarker-subtle{color:#999}.CodeMirror-cursor{border-left:1px solid #000;border-right:none;width:0}.CodeMirror div.CodeMirror-secondarycursor{border-left:1px solid silver}.cm-fat-cursor .CodeMirror-cursor{width:auto;border:0!important;background:#7e7}.cm-fat-cursor div.CodeMirror-cursors{z-index:1}.cm-fat-cursor .CodeMirror-line::-moz-selection, .cm-fat-cursor .CodeMirror-line > span::-moz-selection, .cm-fat-cursor .CodeMirror-line > span > span::-moz-selection{background:transparent}.cm-fat-cursor .CodeMirror-line::selection,.cm-fat-cursor .CodeMirror-line>span::selection,.cm-fat-cursor .CodeMirror-line>span>span::selection{background:transparent}.cm-fat-cursor .CodeMirror-line::-moz-selection,.cm-fat-cursor .CodeMirror-line>span::-moz-selection,.cm-fat-cursor .CodeMirror-line>span>span::-moz-selection{background:transparent}.cm-fat-cursor{caret-color:transparent}@-webkit-keyframes blink{50%{background-color:transparent}}@keyframes blink{50%{background-color:transparent}}.cm-tab{display:inline-block;text-decoration:inherit}.CodeMirror-rulers{position:absolute;left:0;right:0;top:-50px;bottom:0;overflow:hidden}.CodeMirror-ruler{border-left:1px solid #ccc;top:0;bottom:0;position:absolute}.cm-s-default .cm-header{color:#00f}.cm-s-default .cm-quote{color:#090}.cm-negative{color:#d44}.cm-positive{color:#292}.cm-header,.cm-strong{font-weight:700}.cm-em{font-style:italic}.cm-link{text-decoration:underline}.cm-strikethrough{text-decoration:line-through}.cm-s-default .cm-keyword{color:#708}.cm-s-default .cm-atom{color:#219}.cm-s-default .cm-number{color:#164}.cm-s-default .cm-def{color:#00f}.cm-s-default .cm-variable-2{color:#05a}.cm-s-default .cm-type,.cm-s-default .cm-variable-3{color:#085}.cm-s-default .cm-comment{color:#a50}.cm-s-default .cm-string{color:#a11}.cm-s-default .cm-string-2{color:#f50}.cm-s-default .cm-meta,.cm-s-default .cm-qualifier{color:#555}.cm-s-default .cm-builtin{color:#30a}.cm-s-default .cm-bracket{color:#997}.cm-s-default .cm-tag{color:#170}.cm-s-default .cm-attribute{color:#00c}.cm-s-default .cm-hr{color:#999}.cm-s-default .cm-link{color:#00c}.cm-invalidchar,.cm-s-default .cm-error{color:red}.CodeMirror-composing{border-bottom:2px solid}div.CodeMirror span.CodeMirror-matchingbracket{color:#0b0}div.CodeMirror span.CodeMirror-nonmatchingbracket{color:#a22}.CodeMirror-matchingtag{background:rgba(255,150,0,.3)}.CodeMirror-activeline-background{background:#e8f2ff}.CodeMirror{position:relative;overflow:hidden;background:#fff}.CodeMirror-scroll{overflow:scroll!important;margin-bottom:-50px;margin-right:-50px;padding-bottom:50px;height:100%;outline:none;position:relative}.CodeMirror-sizer{position:relative;border-right:50px solid transparent}.CodeMirror-gutter-filler,.CodeMirror-hscrollbar,.CodeMirror-scrollbar-filler,.CodeMirror-vscrollbar{position:absolute;z-index:6;display:none;outline:none}.CodeMirror-vscrollbar{right:0;top:0;overflow-x:hidden;overflow-y:scroll}.CodeMirror-hscrollbar{bottom:0;left:0;overflow-y:hidden;overflow-x:scroll}.CodeMirror-scrollbar-filler{right:0;bottom:0}.CodeMirror-gutter-filler{left:0;bottom:0}.CodeMirror-gutters{position:absolute;left:0;top:0;min-height:100%;z-index:3}.CodeMirror-gutter{white-space:normal;height:100%;display:inline-block;vertical-align:top;margin-bottom:-50px}.CodeMirror-gutter-wrapper{position:absolute;z-index:4;background:none!important;border:none!important}.CodeMirror-gutter-background{position:absolute;top:0;bottom:0;z-index:4}.CodeMirror-gutter-elt{position:absolute;cursor:default;z-index:4}.CodeMirror-gutter-wrapper ::-moz-selection{background-color:transparent}.CodeMirror-gutter-wrapper ::selection{background-color:transparent}.CodeMirror-gutter-wrapper ::-moz-selection{background-color:transparent}.CodeMirror-lines{cursor:text;min-height:1px}.CodeMirror pre.CodeMirror-line,.CodeMirror pre.CodeMirror-line-like{border-radius:0;border-width:0;background:transparent;font-family:inherit;font-size:inherit;margin:0;white-space:pre;word-wrap:normal;line-height:inherit;color:inherit;z-index:2;position:relative;overflow:visible;-webkit-tap-highlight-color:transparent;font-variant-ligatures:contextual}.CodeMirror-wrap pre.CodeMirror-line,.CodeMirror-wrap pre.CodeMirror-line-like{word-wrap:break-word;white-space:pre-wrap;word-break:normal}.CodeMirror-linebackground{position:absolute;left:0;right:0;top:0;bottom:0;z-index:0}.CodeMirror-linewidget{position:relative;z-index:2;padding:.1px}.CodeMirror-rtl pre{direction:rtl}.CodeMirror-code{outline:none}.CodeMirror-gutter,.CodeMirror-gutters,.CodeMirror-linenumber,.CodeMirror-scroll,.CodeMirror-sizer{box-sizing:content-box}.CodeMirror-measure{position:absolute;width:100%;height:0;overflow:hidden;visibility:hidden}.CodeMirror-cursor{position:absolute;pointer-events:none}.CodeMirror-measure pre{position:static}div.CodeMirror-cursors{visibility:hidden;position:relative;z-index:3}.CodeMirror-focused div.CodeMirror-cursors,div.CodeMirror-dragcursors{visibility:visible}.CodeMirror-selected{background:#d9d9d9}.CodeMirror-focused .CodeMirror-selected{background:#d7d4f0}.CodeMirror-crosshair{cursor:crosshair}.CodeMirror-line::-moz-selection, .CodeMirror-line > span::-moz-selection, .CodeMirror-line > span > span::-moz-selection{background:#d7d4f0}.CodeMirror-line::selection,.CodeMirror-line>span::selection,.CodeMirror-line>span>span::selection{background:#d7d4f0}.CodeMirror-line::-moz-selection,.CodeMirror-line>span::-moz-selection,.CodeMirror-line>span>span::-moz-selection{background:#d7d4f0}.cm-searching{background-color:#ffa;background-color:rgba(255,255,0,.4)}.cm-force-border{padding-right:.1px}@media print{.CodeMirror div.CodeMirror-cursors{visibility:hidden}}.cm-tab-wrap-hack:after{content:""}span.CodeMirror-selectedtext{background:none}';
styleInject(css_248z$1);

var clike = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var Context = function Context(indented, column, type, info, align, prev) {
        this.indented = indented;
        this.column = column;
        this.type = type;
        this.info = info;
        this.align = align;
        this.prev = prev;
    };
    var pushContext = function pushContext(state, col, type, info) {
        var indent = state.indented;
        if (state.context && state.context.type == "statement" && type != "statement") indent = state.context.indented;
        return state.context = new Context(indent, col, type, info, null, state.context);
    };
    var popContext = function popContext(state) {
        var t = state.context.type;
        if (t == ")" || t == "]" || t == "}") state.indented = state.context.indented;
        return state.context = state.context.prev;
    };
    var typeBefore = function typeBefore(stream, state, pos) {
        if (state.prevToken == "variable" || state.prevToken == "type") return true;
        if (/\S(?:[^- ]>|[*\]])\s*$|\*$/.test(stream.string.slice(0, pos))) return true;
        if (state.typeAtEndOfLine && stream.column() == stream.indentation()) return true;
    };
    var isTopScope = function isTopScope(context) {
        for(;;){
            if (!context || context.type == "top") return true;
            if (context.type == "}" && context.prev.info != "namespace") return false;
            context = context.prev;
        }
    };
    var words = function words(str) {
        var obj = {}, words = str.split(" ");
        for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
        return obj;
    };
    var contains = function contains(words, word) {
        if (typeof words === "function") {
            return words(word);
        } else {
            return words.propertyIsEnumerable(word);
        }
    };
    var cTypes = // Returns true if identifier is a "C" type.
    // C type is defined as those that are reserved by the compiler (basicTypes),
    // and those that end in _t (Reserved by POSIX for types)
    // http://www.gnu.org/software/libc/manual/html_node/Reserved-Names.html
    function cTypes(identifier) {
        return contains(basicCTypes, identifier) || /.+_t$/.test(identifier);
    };
    var objCTypes = // Returns true if identifier is a "Objective C" type.
    function objCTypes(identifier) {
        return cTypes(identifier) || contains(basicObjCTypes, identifier);
    };
    var pointerHook = function pointerHook(_stream, state) {
        if (state.prevToken == "type") return "type";
        return false;
    };
    var cIsReservedIdentifier = // For C and C++ (and ObjC): identifiers starting with __
    // or _ followed by a capital letter are reserved for the compiler.
    function cIsReservedIdentifier(token) {
        if (!token || token.length < 2) return false;
        if (token[0] != "_") return false;
        return token[1] == "_" || token[1] !== token[1].toLowerCase();
    };
    var cpp14Literal = function cpp14Literal(stream) {
        stream.eatWhile(/[\w\.']/);
        return "number";
    };
    var cpp11StringHook = function cpp11StringHook(stream, state) {
        stream.backUp(1);
        // Raw strings.
        if (stream.match(/^(?:R|u8R|uR|UR|LR)/)) {
            var match = stream.match(/^"([^\s\\()]{0,16})\(/);
            if (!match) {
                return false;
            }
            state.cpp11RawStringDelim = match[1];
            state.tokenize = tokenRawString;
            return tokenRawString(stream, state);
        }
        // Unicode strings/chars.
        if (stream.match(/^(?:u8|u|U|L)/)) {
            if (stream.match(/^["']/, /* eat */ false)) {
                return "string";
            }
            return false;
        }
        // Ignore this hook.
        stream.next();
        return false;
    };
    var cppLooksLikeConstructor = function cppLooksLikeConstructor(word) {
        var lastTwo = /(\w+)::~?(\w+)$/.exec(word);
        return lastTwo && lastTwo[1] == lastTwo[2];
    };
    var tokenAtString = // C#-style strings where "" escapes a quote.
    function tokenAtString(stream, state) {
        var next;
        while((next = stream.next()) != null){
            if (next == '"' && !stream.eat('"')) {
                state.tokenize = null;
                break;
            }
        }
        return "string";
    };
    var tokenRawString = // C++11 raw string literal is <prefix>"<delim>( anything )<delim>", where
    // <delim> can be a string up to 16 characters long.
    function tokenRawString(stream, state) {
        // Escape characters that have special regex meanings.
        var delim = state.cpp11RawStringDelim.replace(/[^\w\s]/g, "\\$&");
        var match = stream.match(new RegExp(".*?\\)" + delim + '"'));
        if (match) state.tokenize = null;
        else stream.skipToEnd();
        return "string";
    };
    var def = function def(mimes, mode) {
        if (typeof mimes == "string") mimes = [
            mimes
        ];
        var words = [];
        function add(obj) {
            if (obj) {
                for(var prop in obj)if (obj.hasOwnProperty(prop)) words.push(prop);
            }
        }
        add(mode.keywords);
        add(mode.types);
        add(mode.builtin);
        add(mode.atoms);
        if (words.length) {
            mode.helperType = mimes[0];
            CodeMirror1.registerHelper("hintWords", mimes[0], words);
        }
        for(var i = 0; i < mimes.length; ++i)CodeMirror1.defineMIME(mimes[i], mode);
    };
    var tokenTripleString = function tokenTripleString(stream, state) {
        var escaped = false;
        while(!stream.eol()){
            if (!escaped && stream.match('"""')) {
                state.tokenize = null;
                break;
            }
            escaped = stream.next() == "\\" && !escaped;
        }
        return "string";
    };
    var tokenKotlinString = function tokenKotlinString(tripleString) {
        return function(stream, state) {
            var escaped = false, next, end = false;
            while(!stream.eol()){
                if (!tripleString && !escaped && stream.match('"')) {
                    end = true;
                    break;
                }
                if (tripleString && stream.match('"""')) {
                    end = true;
                    break;
                }
                next = stream.next();
                if (!escaped && next == "$" && stream.match("{")) stream.skipTo("}");
                escaped = !escaped && next == "\\" && !tripleString;
            }
            if (end || !tripleString) state.tokenize = null;
            return "string";
        };
    };
    CodeMirror1.defineMode("clike", function(config, parserConfig) {
        var tokenBase = function tokenBase(stream, state) {
            var ch = stream.next();
            if (hooks[ch]) {
                var result = hooks[ch](stream, state);
                if (result !== false) return result;
            }
            if (ch == '"' || ch == "'") {
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            }
            if (numberStart.test(ch)) {
                stream.backUp(1);
                if (stream.match(number)) return "number";
                stream.next();
            }
            if (isPunctuationChar.test(ch)) {
                curPunc = ch;
                return null;
            }
            if (ch == "/") {
                if (stream.eat("*")) {
                    state.tokenize = tokenComment;
                    return tokenComment(stream, state);
                }
                if (stream.eat("/")) {
                    stream.skipToEnd();
                    return "comment";
                }
            }
            if (isOperatorChar.test(ch)) {
                while(!stream.match(/^\/[\/*]/, false) && stream.eat(isOperatorChar)){}
                return "operator";
            }
            stream.eatWhile(isIdentifierChar);
            if (namespaceSeparator) while(stream.match(namespaceSeparator))stream.eatWhile(isIdentifierChar);
            var cur = stream.current();
            if (contains(keywords, cur)) {
                if (contains(blockKeywords, cur)) curPunc = "newstatement";
                if (contains(defKeywords, cur)) isDefKeyword = true;
                return "keyword";
            }
            if (contains(types, cur)) return "type";
            if (contains(builtin, cur) || isReservedIdentifier && isReservedIdentifier(cur)) {
                if (contains(blockKeywords, cur)) curPunc = "newstatement";
                return "builtin";
            }
            if (contains(atoms, cur)) return "atom";
            return "variable";
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, next, end = false;
                while((next = stream.next()) != null){
                    if (next == quote && !escaped) {
                        end = true;
                        break;
                    }
                    escaped = !escaped && next == "\\";
                }
                if (end || !(escaped || multiLineStrings)) state.tokenize = null;
                return "string";
            };
        };
        var tokenComment = function tokenComment(stream, state) {
            var maybeEnd = false, ch;
            while(ch = stream.next()){
                if (ch == "/" && maybeEnd) {
                    state.tokenize = null;
                    break;
                }
                maybeEnd = ch == "*";
            }
            return "comment";
        };
        var maybeEOL = function maybeEOL(stream, state) {
            if (parserConfig.typeFirstDefinitions && stream.eol() && isTopScope(state.context)) state.typeAtEndOfLine = typeBefore(stream, state, stream.pos);
        };
        var indentUnit = config.indentUnit, statementIndentUnit = parserConfig.statementIndentUnit || indentUnit, dontAlignCalls = parserConfig.dontAlignCalls, keywords = parserConfig.keywords || {}, types = parserConfig.types || {}, builtin = parserConfig.builtin || {}, blockKeywords = parserConfig.blockKeywords || {}, defKeywords = parserConfig.defKeywords || {}, atoms = parserConfig.atoms || {}, hooks = parserConfig.hooks || {}, multiLineStrings = parserConfig.multiLineStrings, indentStatements = parserConfig.indentStatements !== false, indentSwitch = parserConfig.indentSwitch !== false, namespaceSeparator = parserConfig.namespaceSeparator, isPunctuationChar = parserConfig.isPunctuationChar || /[\[\]{}\(\),;\:\.]/, numberStart = parserConfig.numberStart || /[\d\.]/, number = parserConfig.number || /^(?:0x[a-f\d]+|0b[01]+|(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?)(u|ll?|l|f)?/i, isOperatorChar = parserConfig.isOperatorChar || /[+\-*&%=<>!?|\/]/, isIdentifierChar = parserConfig.isIdentifierChar || /[\w\$_\xa1-\uffff]/, // An optional function that takes a {string} token and returns true if it
        // should be treated as a builtin.
        isReservedIdentifier = parserConfig.isReservedIdentifier || false;
        var curPunc, isDefKeyword;
        // Interface
        return {
            startState: function startState(basecolumn) {
                return {
                    tokenize: null,
                    context: new Context((basecolumn || 0) - indentUnit, 0, "top", null, false),
                    indented: 0,
                    startOfLine: true,
                    prevToken: null
                };
            },
            token: function token(stream, state) {
                var ctx = state.context;
                if (stream.sol()) {
                    if (ctx.align == null) ctx.align = false;
                    state.indented = stream.indentation();
                    state.startOfLine = true;
                }
                if (stream.eatSpace()) {
                    maybeEOL(stream, state);
                    return null;
                }
                curPunc = isDefKeyword = null;
                var style = (state.tokenize || tokenBase)(stream, state);
                if (style == "comment" || style == "meta") return style;
                if (ctx.align == null) ctx.align = true;
                if (curPunc == ";" || curPunc == ":" || curPunc == "," && stream.match(/^\s*(?:\/\/.*)?$/, false)) while(state.context.type == "statement")popContext(state);
                else if (curPunc == "{") pushContext(state, stream.column(), "}");
                else if (curPunc == "[") pushContext(state, stream.column(), "]");
                else if (curPunc == "(") pushContext(state, stream.column(), ")");
                else if (curPunc == "}") {
                    while(ctx.type == "statement")ctx = popContext(state);
                    if (ctx.type == "}") ctx = popContext(state);
                    while(ctx.type == "statement")ctx = popContext(state);
                } else if (curPunc == ctx.type) popContext(state);
                else if (indentStatements && ((ctx.type == "}" || ctx.type == "top") && curPunc != ";" || ctx.type == "statement" && curPunc == "newstatement")) {
                    pushContext(state, stream.column(), "statement", stream.current());
                }
                if (style == "variable" && (state.prevToken == "def" || parserConfig.typeFirstDefinitions && typeBefore(stream, state, stream.start) && isTopScope(state.context) && stream.match(/^\s*\(/, false))) style = "def";
                if (hooks.token) {
                    var result = hooks.token(stream, state, style);
                    if (result !== undefined) style = result;
                }
                if (style == "def" && parserConfig.styleDefs === false) style = "variable";
                state.startOfLine = false;
                state.prevToken = isDefKeyword ? "def" : style || curPunc;
                maybeEOL(stream, state);
                return style;
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize != tokenBase && state.tokenize != null || state.typeAtEndOfLine) return CodeMirror1.Pass;
                var ctx = state.context, firstChar = textAfter && textAfter.charAt(0);
                var closing = firstChar == ctx.type;
                if (ctx.type == "statement" && firstChar == "}") ctx = ctx.prev;
                if (parserConfig.dontIndentStatements) while(ctx.type == "statement" && parserConfig.dontIndentStatements.test(ctx.info))ctx = ctx.prev;
                if (hooks.indent) {
                    var hook = hooks.indent(state, ctx, textAfter, indentUnit);
                    if (typeof hook == "number") return hook;
                }
                var switchBlock = ctx.prev && ctx.prev.info == "switch";
                if (parserConfig.allmanIndentation && /[{(]/.test(firstChar)) {
                    while(ctx.type != "top" && ctx.type != "}")ctx = ctx.prev;
                    return ctx.indented;
                }
                if (ctx.type == "statement") return ctx.indented + (firstChar == "{" ? 0 : statementIndentUnit);
                if (ctx.align && (!dontAlignCalls || ctx.type != ")")) return ctx.column + (closing ? 0 : 1);
                if (ctx.type == ")" && !closing) return ctx.indented + statementIndentUnit;
                return ctx.indented + (closing ? 0 : indentUnit) + (!closing && switchBlock && !/^(?:case|default)\b/.test(textAfter) ? indentUnit : 0);
            },
            electricInput: indentSwitch ? /^\s*(?:case .*?:|default:|\{\}?|\})$/ : /^\s*[{}]$/,
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            blockCommentContinue: " * ",
            lineComment: "//",
            fold: "brace"
        };
    });
    var cKeywords = "auto if break case register continue return default do sizeof " + "static else struct switch extern typedef union for goto while enum const " + "volatile inline restrict asm fortran";
    // Keywords from https://en.cppreference.com/w/cpp/keyword includes C++20.
    var cppKeywords = "alignas alignof and and_eq audit axiom bitand bitor catch " + "class compl concept constexpr const_cast decltype delete dynamic_cast " + "explicit export final friend import module mutable namespace new noexcept " + "not not_eq operator or or_eq override private protected public " + "reinterpret_cast requires static_assert static_cast template this " + "thread_local throw try typeid typename using virtual xor xor_eq";
    var objCKeywords = "bycopy byref in inout oneway out self super atomic nonatomic retain copy " + "readwrite readonly strong weak assign typeof nullable nonnull null_resettable _cmd " + "@interface @implementation @end @protocol @encode @property @synthesize @dynamic @class " + "@public @package @private @protected @required @optional @try @catch @finally @import " + "@selector @encode @defs @synchronized @autoreleasepool @compatibility_alias @available";
    var objCBuiltins = "FOUNDATION_EXPORT FOUNDATION_EXTERN NS_INLINE NS_FORMAT_FUNCTION " + " NS_RETURNS_RETAINEDNS_ERROR_ENUM NS_RETURNS_NOT_RETAINED NS_RETURNS_INNER_POINTER " + "NS_DESIGNATED_INITIALIZER NS_ENUM NS_OPTIONS NS_REQUIRES_NIL_TERMINATION " + "NS_ASSUME_NONNULL_BEGIN NS_ASSUME_NONNULL_END NS_SWIFT_NAME NS_REFINED_FOR_SWIFT";
    // Do not use this. Use the cTypes function below. This is global just to avoid
    // excessive calls when cTypes is being called multiple times during a parse.
    var basicCTypes = words("int long char short double float unsigned signed " + "void bool");
    // Do not use this. Use the objCTypes function below. This is global just to avoid
    // excessive calls when objCTypes is being called multiple times during a parse.
    var basicObjCTypes = words("SEL instancetype id Class Protocol BOOL");
    var cBlockKeywords = "case do else for if switch while struct enum union";
    var cDefKeywords = "struct enum union";
    function cppHook(stream, state) {
        if (!state.startOfLine) return false;
        for(var ch, next = null; ch = stream.peek();){
            if (ch == "\\" && stream.match(/^.$/)) {
                next = cppHook;
                break;
            } else if (ch == "/" && stream.match(/^\/[\/\*]/, false)) {
                break;
            }
            stream.next();
        }
        state.tokenize = next;
        return "meta";
    }
    def([
        "text/x-csrc",
        "text/x-c",
        "text/x-chdr"
    ], {
        name: "clike",
        keywords: words(cKeywords),
        types: cTypes,
        blockKeywords: words(cBlockKeywords),
        defKeywords: words(cDefKeywords),
        typeFirstDefinitions: true,
        atoms: words("NULL true false"),
        isReservedIdentifier: cIsReservedIdentifier,
        hooks: {
            "#": cppHook,
            "*": pointerHook
        },
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def([
        "text/x-c++src",
        "text/x-c++hdr"
    ], {
        name: "clike",
        keywords: words(cKeywords + " " + cppKeywords),
        types: cTypes,
        blockKeywords: words(cBlockKeywords + " class try catch"),
        defKeywords: words(cDefKeywords + " class namespace"),
        typeFirstDefinitions: true,
        atoms: words("true false NULL nullptr"),
        dontIndentStatements: /^template$/,
        isIdentifierChar: /[\w\$_~\xa1-\uffff]/,
        isReservedIdentifier: cIsReservedIdentifier,
        hooks: {
            "#": cppHook,
            "*": pointerHook,
            "u": cpp11StringHook,
            "U": cpp11StringHook,
            "L": cpp11StringHook,
            "R": cpp11StringHook,
            "0": cpp14Literal,
            "1": cpp14Literal,
            "2": cpp14Literal,
            "3": cpp14Literal,
            "4": cpp14Literal,
            "5": cpp14Literal,
            "6": cpp14Literal,
            "7": cpp14Literal,
            "8": cpp14Literal,
            "9": cpp14Literal,
            token: function token(stream, state, style) {
                if (style == "variable" && stream.peek() == "(" && (state.prevToken == ";" || state.prevToken == null || state.prevToken == "}") && cppLooksLikeConstructor(stream.current())) return "def";
            }
        },
        namespaceSeparator: "::",
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def("text/x-java", {
        name: "clike",
        keywords: words("abstract assert break case catch class const continue default " + "do else enum extends final finally for goto if implements import " + "instanceof interface native new package private protected public " + "return static strictfp super switch synchronized this throw throws transient " + "try volatile while @interface"),
        types: words("var byte short int long float double boolean char void Boolean Byte Character Double Float " + "Integer Long Number Object Short String StringBuffer StringBuilder Void"),
        blockKeywords: words("catch class do else finally for if switch try while"),
        defKeywords: words("class interface enum @interface"),
        typeFirstDefinitions: true,
        atoms: words("true false null"),
        number: /^(?:0x[a-f\d_]+|0b[01_]+|(?:[\d_]+\.?\d*|\.\d+)(?:e[-+]?[\d_]+)?)(u|ll?|l|f)?/i,
        hooks: {
            "@": function(stream) {
                // Don't match the @interface keyword.
                if (stream.match("interface", false)) return false;
                stream.eatWhile(/[\w\$_]/);
                return "meta";
            }
        },
        modeProps: {
            fold: [
                "brace",
                "import"
            ]
        }
    });
    def("text/x-csharp", {
        name: "clike",
        keywords: words("abstract as async await base break case catch checked class const continue" + " default delegate do else enum event explicit extern finally fixed for" + " foreach goto if implicit in interface internal is lock namespace new" + " operator out override params private protected public readonly ref return sealed" + " sizeof stackalloc static struct switch this throw try typeof unchecked" + " unsafe using virtual void volatile while add alias ascending descending dynamic from get" + " global group into join let orderby partial remove select set value var yield"),
        types: words("Action Boolean Byte Char DateTime DateTimeOffset Decimal Double Func" + " Guid Int16 Int32 Int64 Object SByte Single String Task TimeSpan UInt16 UInt32" + " UInt64 bool byte char decimal double short int long object" + " sbyte float string ushort uint ulong"),
        blockKeywords: words("catch class do else finally for foreach if struct switch try while"),
        defKeywords: words("class interface namespace struct var"),
        typeFirstDefinitions: true,
        atoms: words("true false null"),
        hooks: {
            "@": function(stream, state) {
                if (stream.eat('"')) {
                    state.tokenize = tokenAtString;
                    return tokenAtString(stream, state);
                }
                stream.eatWhile(/[\w\$_]/);
                return "meta";
            }
        }
    });
    function tokenNestedComment(depth) {
        return function(stream, state) {
            var ch;
            while(ch = stream.next()){
                if (ch == "*" && stream.eat("/")) {
                    if (depth == 1) {
                        state.tokenize = null;
                        break;
                    } else {
                        state.tokenize = tokenNestedComment(depth - 1);
                        return state.tokenize(stream, state);
                    }
                } else if (ch == "/" && stream.eat("*")) {
                    state.tokenize = tokenNestedComment(depth + 1);
                    return state.tokenize(stream, state);
                }
            }
            return "comment";
        };
    }
    def("text/x-scala", {
        name: "clike",
        keywords: words(/* scala */ "abstract case catch class def do else extends final finally for forSome if " + "implicit import lazy match new null object override package private protected return " + "sealed super this throw trait try type val var while with yield _ " + /* package scala */ "assert assume require print println printf readLine readBoolean readByte readShort " + "readChar readInt readLong readFloat readDouble"),
        types: words("AnyVal App Application Array BufferedIterator BigDecimal BigInt Char Console Either " + "Enumeration Equiv Error Exception Fractional Function IndexedSeq Int Integral Iterable " + "Iterator List Map Numeric Nil NotNull Option Ordered Ordering PartialFunction PartialOrdering " + "Product Proxy Range Responder Seq Serializable Set Specializable Stream StringBuilder " + "StringContext Symbol Throwable Traversable TraversableOnce Tuple Unit Vector " + /* package java.lang */ "Boolean Byte Character CharSequence Class ClassLoader Cloneable Comparable " + "Compiler Double Exception Float Integer Long Math Number Object Package Pair Process " + "Runtime Runnable SecurityManager Short StackTraceElement StrictMath String " + "StringBuffer System Thread ThreadGroup ThreadLocal Throwable Triple Void"),
        multiLineStrings: true,
        blockKeywords: words("catch class enum do else finally for forSome if match switch try while"),
        defKeywords: words("class enum def object package trait type val var"),
        atoms: words("true false null"),
        indentStatements: false,
        indentSwitch: false,
        isOperatorChar: /[+\-*&%=<>!?|\/#:@]/,
        hooks: {
            "@": function(stream) {
                stream.eatWhile(/[\w\$_]/);
                return "meta";
            },
            '"': function(stream, state) {
                if (!stream.match('""')) return false;
                state.tokenize = tokenTripleString;
                return state.tokenize(stream, state);
            },
            "'": function(stream) {
                stream.eatWhile(/[\w\$_\xa1-\uffff]/);
                return "atom";
            },
            "=": function(stream, state) {
                var cx = state.context;
                if (cx.type == "}" && cx.align && stream.eat(">")) {
                    state.context = new Context(cx.indented, cx.column, cx.type, cx.info, null, cx.prev);
                    return "operator";
                } else {
                    return false;
                }
            },
            "/": function(stream, state) {
                if (!stream.eat("*")) return false;
                state.tokenize = tokenNestedComment(1);
                return state.tokenize(stream, state);
            }
        },
        modeProps: {
            closeBrackets: {
                pairs: '()[]{}""',
                triples: '"'
            }
        }
    });
    def("text/x-kotlin", {
        name: "clike",
        keywords: words(/*keywords*/ "package as typealias class interface this super val operator " + "var fun for is in This throw return annotation " + "break continue object if else while do try when !in !is as? " + /*soft keywords*/ "file import where by get set abstract enum open inner override private public internal " + "protected catch finally out final vararg reified dynamic companion constructor init " + "sealed field property receiver param sparam lateinit data inline noinline tailrec " + "external annotation crossinline const operator infix suspend actual expect setparam value"),
        types: words(/* package java.lang */ "Boolean Byte Character CharSequence Class ClassLoader Cloneable Comparable " + "Compiler Double Exception Float Integer Long Math Number Object Package Pair Process " + "Runtime Runnable SecurityManager Short StackTraceElement StrictMath String " + "StringBuffer System Thread ThreadGroup ThreadLocal Throwable Triple Void Annotation Any BooleanArray " + "ByteArray Char CharArray DeprecationLevel DoubleArray Enum FloatArray Function Int IntArray Lazy " + "LazyThreadSafetyMode LongArray Nothing ShortArray Unit"),
        intendSwitch: false,
        indentStatements: false,
        multiLineStrings: true,
        number: /^(?:0x[a-f\d_]+|0b[01_]+|(?:[\d_]+(\.\d+)?|\.\d+)(?:e[-+]?[\d_]+)?)(u|ll?|l|f)?/i,
        blockKeywords: words("catch class do else finally for if where try while enum"),
        defKeywords: words("class val var object interface fun"),
        atoms: words("true false null this"),
        hooks: {
            "@": function(stream) {
                stream.eatWhile(/[\w\$_]/);
                return "meta";
            },
            "*": function(_stream, state) {
                return state.prevToken == "." ? "variable" : "operator";
            },
            '"': function(stream, state) {
                state.tokenize = tokenKotlinString(stream.match('""'));
                return state.tokenize(stream, state);
            },
            "/": function(stream, state) {
                if (!stream.eat("*")) return false;
                state.tokenize = tokenNestedComment(1);
                return state.tokenize(stream, state);
            },
            indent: function indent(state, ctx, textAfter, indentUnit) {
                var firstChar = textAfter && textAfter.charAt(0);
                if ((state.prevToken == "}" || state.prevToken == ")") && textAfter == "") return state.indented;
                if (state.prevToken == "operator" && textAfter != "}" && state.context.type != "}" || state.prevToken == "variable" && firstChar == "." || (state.prevToken == "}" || state.prevToken == ")") && firstChar == ".") return indentUnit * 2 + ctx.indented;
                if (ctx.align && ctx.type == "}") return ctx.indented + (state.context.type == (textAfter || "").charAt(0) ? 0 : indentUnit);
            }
        },
        modeProps: {
            closeBrackets: {
                triples: '"'
            }
        }
    });
    def([
        "x-shader/x-vertex",
        "x-shader/x-fragment"
    ], {
        name: "clike",
        keywords: words("sampler1D sampler2D sampler3D samplerCube " + "sampler1DShadow sampler2DShadow " + "const attribute uniform varying " + "break continue discard return " + "for while do if else struct " + "in out inout"),
        types: words("float int bool void " + "vec2 vec3 vec4 ivec2 ivec3 ivec4 bvec2 bvec3 bvec4 " + "mat2 mat3 mat4"),
        blockKeywords: words("for while do if else struct"),
        builtin: words("radians degrees sin cos tan asin acos atan " + "pow exp log exp2 sqrt inversesqrt " + "abs sign floor ceil fract mod min max clamp mix step smoothstep " + "length distance dot cross normalize ftransform faceforward " + "reflect refract matrixCompMult " + "lessThan lessThanEqual greaterThan greaterThanEqual " + "equal notEqual any all not " + "texture1D texture1DProj texture1DLod texture1DProjLod " + "texture2D texture2DProj texture2DLod texture2DProjLod " + "texture3D texture3DProj texture3DLod texture3DProjLod " + "textureCube textureCubeLod " + "shadow1D shadow2D shadow1DProj shadow2DProj " + "shadow1DLod shadow2DLod shadow1DProjLod shadow2DProjLod " + "dFdx dFdy fwidth " + "noise1 noise2 noise3 noise4"),
        atoms: words("true false " + "gl_FragColor gl_SecondaryColor gl_Normal gl_Vertex " + "gl_MultiTexCoord0 gl_MultiTexCoord1 gl_MultiTexCoord2 gl_MultiTexCoord3 " + "gl_MultiTexCoord4 gl_MultiTexCoord5 gl_MultiTexCoord6 gl_MultiTexCoord7 " + "gl_FogCoord gl_PointCoord " + "gl_Position gl_PointSize gl_ClipVertex " + "gl_FrontColor gl_BackColor gl_FrontSecondaryColor gl_BackSecondaryColor " + "gl_TexCoord gl_FogFragCoord " + "gl_FragCoord gl_FrontFacing " + "gl_FragData gl_FragDepth " + "gl_ModelViewMatrix gl_ProjectionMatrix gl_ModelViewProjectionMatrix " + "gl_TextureMatrix gl_NormalMatrix gl_ModelViewMatrixInverse " + "gl_ProjectionMatrixInverse gl_ModelViewProjectionMatrixInverse " + "gl_TextureMatrixTranspose gl_ModelViewMatrixInverseTranspose " + "gl_ProjectionMatrixInverseTranspose " + "gl_ModelViewProjectionMatrixInverseTranspose " + "gl_TextureMatrixInverseTranspose " + "gl_NormalScale gl_DepthRange gl_ClipPlane " + "gl_Point gl_FrontMaterial gl_BackMaterial gl_LightSource gl_LightModel " + "gl_FrontLightModelProduct gl_BackLightModelProduct " + "gl_TextureColor gl_EyePlaneS gl_EyePlaneT gl_EyePlaneR gl_EyePlaneQ " + "gl_FogParameters " + "gl_MaxLights gl_MaxClipPlanes gl_MaxTextureUnits gl_MaxTextureCoords " + "gl_MaxVertexAttribs gl_MaxVertexUniformComponents gl_MaxVaryingFloats " + "gl_MaxVertexTextureImageUnits gl_MaxTextureImageUnits " + "gl_MaxFragmentUniformComponents gl_MaxCombineTextureImageUnits " + "gl_MaxDrawBuffers"),
        indentSwitch: false,
        hooks: {
            "#": cppHook
        },
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def("text/x-nesc", {
        name: "clike",
        keywords: words(cKeywords + " as atomic async call command component components configuration event generic " + "implementation includes interface module new norace nx_struct nx_union post provides " + "signal task uses abstract extends"),
        types: cTypes,
        blockKeywords: words(cBlockKeywords),
        atoms: words("null true false"),
        hooks: {
            "#": cppHook
        },
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def("text/x-objectivec", {
        name: "clike",
        keywords: words(cKeywords + " " + objCKeywords),
        types: objCTypes,
        builtin: words(objCBuiltins),
        blockKeywords: words(cBlockKeywords + " @synthesize @try @catch @finally @autoreleasepool @synchronized"),
        defKeywords: words(cDefKeywords + " @interface @implementation @protocol @class"),
        dontIndentStatements: /^@.*$/,
        typeFirstDefinitions: true,
        atoms: words("YES NO NULL Nil nil true false nullptr"),
        isReservedIdentifier: cIsReservedIdentifier,
        hooks: {
            "#": cppHook,
            "*": pointerHook
        },
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def("text/x-objectivec++", {
        name: "clike",
        keywords: words(cKeywords + " " + objCKeywords + " " + cppKeywords),
        types: objCTypes,
        builtin: words(objCBuiltins),
        blockKeywords: words(cBlockKeywords + " @synthesize @try @catch @finally @autoreleasepool @synchronized class try catch"),
        defKeywords: words(cDefKeywords + " @interface @implementation @protocol @class class namespace"),
        dontIndentStatements: /^@.*$|^template$/,
        typeFirstDefinitions: true,
        atoms: words("YES NO NULL Nil nil true false nullptr"),
        isReservedIdentifier: cIsReservedIdentifier,
        hooks: {
            "#": cppHook,
            "*": pointerHook,
            "u": cpp11StringHook,
            "U": cpp11StringHook,
            "L": cpp11StringHook,
            "R": cpp11StringHook,
            "0": cpp14Literal,
            "1": cpp14Literal,
            "2": cpp14Literal,
            "3": cpp14Literal,
            "4": cpp14Literal,
            "5": cpp14Literal,
            "6": cpp14Literal,
            "7": cpp14Literal,
            "8": cpp14Literal,
            "9": cpp14Literal,
            token: function token(stream, state, style) {
                if (style == "variable" && stream.peek() == "(" && (state.prevToken == ";" || state.prevToken == null || state.prevToken == "}") && cppLooksLikeConstructor(stream.current())) return "def";
            }
        },
        namespaceSeparator: "::",
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    def("text/x-squirrel", {
        name: "clike",
        keywords: words("base break clone continue const default delete enum extends function in class" + " foreach local resume return this throw typeof yield constructor instanceof static"),
        types: cTypes,
        blockKeywords: words("case catch class else for foreach if switch try while"),
        defKeywords: words("function local class"),
        typeFirstDefinitions: true,
        atoms: words("true false null"),
        hooks: {
            "#": cppHook
        },
        modeProps: {
            fold: [
                "brace",
                "include"
            ]
        }
    });
    // Ceylon Strings need to deal with interpolation
    var stringTokenizer = null;
    function tokenCeylonString(type) {
        return function(stream, state) {
            var escaped = false, next, end = false;
            while(!stream.eol()){
                if (!escaped && stream.match('"') && (type == "single" || stream.match('""'))) {
                    end = true;
                    break;
                }
                if (!escaped && stream.match("``")) {
                    stringTokenizer = tokenCeylonString(type);
                    end = true;
                    break;
                }
                next = stream.next();
                escaped = type == "single" && !escaped && next == "\\";
            }
            if (end) state.tokenize = null;
            return "string";
        };
    }
    def("text/x-ceylon", {
        name: "clike",
        keywords: words("abstracts alias assembly assert assign break case catch class continue dynamic else" + " exists extends finally for function given if import in interface is let module new" + " nonempty object of out outer package return satisfies super switch then this throw" + " try value void while"),
        types: function types(word) {
            // In Ceylon all identifiers that start with an uppercase are types
            var first = word.charAt(0);
            return first === first.toUpperCase() && first !== first.toLowerCase();
        },
        blockKeywords: words("case catch class dynamic else finally for function if interface module new object switch try while"),
        defKeywords: words("class dynamic function interface module object package value"),
        builtin: words("abstract actual aliased annotation by default deprecated doc final formal late license" + " native optional sealed see serializable shared suppressWarnings tagged throws variable"),
        isPunctuationChar: /[\[\]{}\(\),;\:\.`]/,
        isOperatorChar: /[+\-*&%=<>!?|^~:\/]/,
        numberStart: /[\d#$]/,
        number: /^(?:#[\da-fA-F_]+|\$[01_]+|[\d_]+[kMGTPmunpf]?|[\d_]+\.[\d_]+(?:[eE][-+]?\d+|[kMGTPmunpf]|)|)/i,
        multiLineStrings: true,
        typeFirstDefinitions: true,
        atoms: words("true false null larger smaller equal empty finished"),
        indentSwitch: false,
        styleDefs: false,
        hooks: {
            "@": function(stream) {
                stream.eatWhile(/[\w\$_]/);
                return "meta";
            },
            '"': function(stream, state) {
                state.tokenize = tokenCeylonString(stream.match('""') ? "triple" : "single");
                return state.tokenize(stream, state);
            },
            "`": function(stream, state) {
                if (!stringTokenizer || !stream.match("`")) return false;
                state.tokenize = stringTokenizer;
                stringTokenizer = null;
                return state.tokenize(stream, state);
            },
            "'": function(stream) {
                stream.eatWhile(/[\w\$_\xa1-\uffff]/);
                return "atom";
            },
            token: function token(_stream, state, style) {
                if ((style == "variable" || style == "type") && state.prevToken == ".") {
                    return "variable-2";
                }
            }
        },
        modeProps: {
            fold: [
                "brace",
                "import"
            ],
            closeBrackets: {
                triples: '"'
            }
        }
    });
});
});

var css = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var keySet = function keySet(array) {
        var keys = {};
        for(var i = 0; i < array.length; ++i){
            keys[array[i].toLowerCase()] = true;
        }
        return keys;
    };
    var tokenCComment = function tokenCComment(stream, state) {
        var maybeEnd = false, ch;
        while((ch = stream.next()) != null){
            if (maybeEnd && ch == "/") {
                state.tokenize = null;
                break;
            }
            maybeEnd = ch == "*";
        }
        return [
            "comment",
            "comment"
        ];
    };
    CodeMirror1.defineMode("css", function(config, parserConfig) {
        var ret = function ret(style, tp) {
            type = tp;
            return style;
        };
        var tokenBase = // Tokenizers
        function tokenBase(stream, state) {
            var ch = stream.next();
            if (tokenHooks[ch]) {
                var result = tokenHooks[ch](stream, state);
                if (result !== false) return result;
            }
            if (ch == "@") {
                stream.eatWhile(/[\w\\\-]/);
                return ret("def", stream.current());
            } else if (ch == "=" || (ch == "~" || ch == "|") && stream.eat("=")) {
                return ret(null, "compare");
            } else if (ch == '"' || ch == "'") {
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            } else if (ch == "#") {
                stream.eatWhile(/[\w\\\-]/);
                return ret("atom", "hash");
            } else if (ch == "!") {
                stream.match(/^\s*\w*/);
                return ret("keyword", "important");
            } else if (/\d/.test(ch) || ch == "." && stream.eat(/\d/)) {
                stream.eatWhile(/[\w.%]/);
                return ret("number", "unit");
            } else if (ch === "-") {
                if (/[\d.]/.test(stream.peek())) {
                    stream.eatWhile(/[\w.%]/);
                    return ret("number", "unit");
                } else if (stream.match(/^-[\w\\\-]*/)) {
                    stream.eatWhile(/[\w\\\-]/);
                    if (stream.match(/^\s*:/, false)) return ret("variable-2", "variable-definition");
                    return ret("variable-2", "variable");
                } else if (stream.match(/^\w+-/)) {
                    return ret("meta", "meta");
                }
            } else if (/[,+>*\/]/.test(ch)) {
                return ret(null, "select-op");
            } else if (ch == "." && stream.match(/^-?[_a-z][_a-z0-9-]*/i)) {
                return ret("qualifier", "qualifier");
            } else if (/[:;{}\[\]\(\)]/.test(ch)) {
                return ret(null, ch);
            } else if (stream.match(/^[\w-.]+(?=\()/)) {
                if (/^(url(-prefix)?|domain|regexp)$/i.test(stream.current())) {
                    state.tokenize = tokenParenthesized;
                }
                return ret("variable callee", "variable");
            } else if (/[\w\\\-]/.test(ch)) {
                stream.eatWhile(/[\w\\\-]/);
                return ret("property", "word");
            } else {
                return ret(null, null);
            }
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, ch;
                while((ch = stream.next()) != null){
                    if (ch == quote && !escaped) {
                        if (quote == ")") stream.backUp(1);
                        break;
                    }
                    escaped = !escaped && ch == "\\";
                }
                if (ch == quote || !escaped && quote != ")") state.tokenize = null;
                return ret("string", "string");
            };
        };
        var tokenParenthesized = function tokenParenthesized(stream, state) {
            stream.next(); // Must be '('
            if (!stream.match(/^\s*[\"\')]/, false)) state.tokenize = tokenString(")");
            else state.tokenize = null;
            return ret(null, "(");
        };
        var Context = // Context management
        function Context(type, indent, prev) {
            this.type = type;
            this.indent = indent;
            this.prev = prev;
        };
        var pushContext = function pushContext(state, stream, type, indent) {
            state.context = new Context(type, stream.indentation() + (indent === false ? 0 : indentUnit), state.context);
            return type;
        };
        var popContext = function popContext(state) {
            if (state.context.prev) state.context = state.context.prev;
            return state.context.type;
        };
        var pass = function pass(type, stream, state) {
            return states[state.context.type](type, stream, state);
        };
        var popAndPass = function popAndPass(type, stream, state, n) {
            for(var i = n || 1; i > 0; i--)state.context = state.context.prev;
            return pass(type, stream, state);
        };
        var wordAsValue = // Parser
        function wordAsValue(stream) {
            var word = stream.current().toLowerCase();
            if (valueKeywords.hasOwnProperty(word)) override = "atom";
            else if (colorKeywords.hasOwnProperty(word)) override = "keyword";
            else override = "variable";
        };
        var inline = parserConfig.inline;
        if (!parserConfig.propertyKeywords) parserConfig = CodeMirror1.resolveMode("text/css");
        var indentUnit = config.indentUnit, tokenHooks = parserConfig.tokenHooks, documentTypes = parserConfig.documentTypes || {}, mediaTypes = parserConfig.mediaTypes || {}, mediaFeatures = parserConfig.mediaFeatures || {}, mediaValueKeywords = parserConfig.mediaValueKeywords || {}, propertyKeywords = parserConfig.propertyKeywords || {}, nonStandardPropertyKeywords = parserConfig.nonStandardPropertyKeywords || {}, fontProperties = parserConfig.fontProperties || {}, counterDescriptors = parserConfig.counterDescriptors || {}, colorKeywords = parserConfig.colorKeywords || {}, valueKeywords = parserConfig.valueKeywords || {}, allowNested = parserConfig.allowNested, lineComment = parserConfig.lineComment, supportsAtComponent = parserConfig.supportsAtComponent === true, highlightNonStandardPropertyKeywords = config.highlightNonStandardPropertyKeywords !== false;
        var type, override;
        var states = {};
        states.top = function(type, stream, state) {
            if (type == "{") {
                return pushContext(state, stream, "block");
            } else if (type == "}" && state.context.prev) {
                return popContext(state);
            } else if (supportsAtComponent && /@component/i.test(type)) {
                return pushContext(state, stream, "atComponentBlock");
            } else if (/^@(-moz-)?document$/i.test(type)) {
                return pushContext(state, stream, "documentTypes");
            } else if (/^@(media|supports|(-moz-)?document|import)$/i.test(type)) {
                return pushContext(state, stream, "atBlock");
            } else if (/^@(font-face|counter-style)/i.test(type)) {
                state.stateArg = type;
                return "restricted_atBlock_before";
            } else if (/^@(-(moz|ms|o|webkit)-)?keyframes$/i.test(type)) {
                return "keyframes";
            } else if (type && type.charAt(0) == "@") {
                return pushContext(state, stream, "at");
            } else if (type == "hash") {
                override = "builtin";
            } else if (type == "word") {
                override = "tag";
            } else if (type == "variable-definition") {
                return "maybeprop";
            } else if (type == "interpolation") {
                return pushContext(state, stream, "interpolation");
            } else if (type == ":") {
                return "pseudo";
            } else if (allowNested && type == "(") {
                return pushContext(state, stream, "parens");
            }
            return state.context.type;
        };
        states.block = function(type, stream, state) {
            if (type == "word") {
                var word = stream.current().toLowerCase();
                if (propertyKeywords.hasOwnProperty(word)) {
                    override = "property";
                    return "maybeprop";
                } else if (nonStandardPropertyKeywords.hasOwnProperty(word)) {
                    override = highlightNonStandardPropertyKeywords ? "string-2" : "property";
                    return "maybeprop";
                } else if (allowNested) {
                    override = stream.match(/^\s*:(?:\s|$)/, false) ? "property" : "tag";
                    return "block";
                } else {
                    override += " error";
                    return "maybeprop";
                }
            } else if (type == "meta") {
                return "block";
            } else if (!allowNested && (type == "hash" || type == "qualifier")) {
                override = "error";
                return "block";
            } else {
                return states.top(type, stream, state);
            }
        };
        states.maybeprop = function(type, stream, state) {
            if (type == ":") return pushContext(state, stream, "prop");
            return pass(type, stream, state);
        };
        states.prop = function(type, stream, state) {
            if (type == ";") return popContext(state);
            if (type == "{" && allowNested) return pushContext(state, stream, "propBlock");
            if (type == "}" || type == "{") return popAndPass(type, stream, state);
            if (type == "(") return pushContext(state, stream, "parens");
            if (type == "hash" && !/^#([0-9a-fA-f]{3,4}|[0-9a-fA-f]{6}|[0-9a-fA-f]{8})$/.test(stream.current())) {
                override += " error";
            } else if (type == "word") {
                wordAsValue(stream);
            } else if (type == "interpolation") {
                return pushContext(state, stream, "interpolation");
            }
            return "prop";
        };
        states.propBlock = function(type, _stream, state) {
            if (type == "}") return popContext(state);
            if (type == "word") {
                override = "property";
                return "maybeprop";
            }
            return state.context.type;
        };
        states.parens = function(type, stream, state) {
            if (type == "{" || type == "}") return popAndPass(type, stream, state);
            if (type == ")") return popContext(state);
            if (type == "(") return pushContext(state, stream, "parens");
            if (type == "interpolation") return pushContext(state, stream, "interpolation");
            if (type == "word") wordAsValue(stream);
            return "parens";
        };
        states.pseudo = function(type, stream, state) {
            if (type == "meta") return "pseudo";
            if (type == "word") {
                override = "variable-3";
                return state.context.type;
            }
            return pass(type, stream, state);
        };
        states.documentTypes = function(type, stream, state) {
            if (type == "word" && documentTypes.hasOwnProperty(stream.current())) {
                override = "tag";
                return state.context.type;
            } else {
                return states.atBlock(type, stream, state);
            }
        };
        states.atBlock = function(type, stream, state) {
            if (type == "(") return pushContext(state, stream, "atBlock_parens");
            if (type == "}" || type == ";") return popAndPass(type, stream, state);
            if (type == "{") return popContext(state) && pushContext(state, stream, allowNested ? "block" : "top");
            if (type == "interpolation") return pushContext(state, stream, "interpolation");
            if (type == "word") {
                var word = stream.current().toLowerCase();
                if (word == "only" || word == "not" || word == "and" || word == "or") override = "keyword";
                else if (mediaTypes.hasOwnProperty(word)) override = "attribute";
                else if (mediaFeatures.hasOwnProperty(word)) override = "property";
                else if (mediaValueKeywords.hasOwnProperty(word)) override = "keyword";
                else if (propertyKeywords.hasOwnProperty(word)) override = "property";
                else if (nonStandardPropertyKeywords.hasOwnProperty(word)) override = highlightNonStandardPropertyKeywords ? "string-2" : "property";
                else if (valueKeywords.hasOwnProperty(word)) override = "atom";
                else if (colorKeywords.hasOwnProperty(word)) override = "keyword";
                else override = "error";
            }
            return state.context.type;
        };
        states.atComponentBlock = function(type, stream, state) {
            if (type == "}") return popAndPass(type, stream, state);
            if (type == "{") return popContext(state) && pushContext(state, stream, allowNested ? "block" : "top", false);
            if (type == "word") override = "error";
            return state.context.type;
        };
        states.atBlock_parens = function(type, stream, state) {
            if (type == ")") return popContext(state);
            if (type == "{" || type == "}") return popAndPass(type, stream, state, 2);
            return states.atBlock(type, stream, state);
        };
        states.restricted_atBlock_before = function(type, stream, state) {
            if (type == "{") return pushContext(state, stream, "restricted_atBlock");
            if (type == "word" && state.stateArg == "@counter-style") {
                override = "variable";
                return "restricted_atBlock_before";
            }
            return pass(type, stream, state);
        };
        states.restricted_atBlock = function(type, stream, state) {
            if (type == "}") {
                state.stateArg = null;
                return popContext(state);
            }
            if (type == "word") {
                if (state.stateArg == "@font-face" && !fontProperties.hasOwnProperty(stream.current().toLowerCase()) || state.stateArg == "@counter-style" && !counterDescriptors.hasOwnProperty(stream.current().toLowerCase())) override = "error";
                else override = "property";
                return "maybeprop";
            }
            return "restricted_atBlock";
        };
        states.keyframes = function(type, stream, state) {
            if (type == "word") {
                override = "variable";
                return "keyframes";
            }
            if (type == "{") return pushContext(state, stream, "top");
            return pass(type, stream, state);
        };
        states.at = function(type, stream, state) {
            if (type == ";") return popContext(state);
            if (type == "{" || type == "}") return popAndPass(type, stream, state);
            if (type == "word") override = "tag";
            else if (type == "hash") override = "builtin";
            return "at";
        };
        states.interpolation = function(type, stream, state) {
            if (type == "}") return popContext(state);
            if (type == "{" || type == ";") return popAndPass(type, stream, state);
            if (type == "word") override = "variable";
            else if (type != "variable" && type != "(" && type != ")") override = "error";
            return "interpolation";
        };
        return {
            startState: function startState(base) {
                return {
                    tokenize: null,
                    state: inline ? "block" : "top",
                    stateArg: null,
                    context: new Context(inline ? "block" : "top", base || 0, null)
                };
            },
            token: function token(stream, state) {
                if (!state.tokenize && stream.eatSpace()) return null;
                var style = (state.tokenize || tokenBase)(stream, state);
                if (style && typeof style == "object") {
                    type = style[1];
                    style = style[0];
                }
                override = style;
                if (type != "comment") state.state = states[state.state](type, stream, state);
                return override;
            },
            indent: function indent(state, textAfter) {
                var cx = state.context, ch = textAfter && textAfter.charAt(0);
                var indent = cx.indent;
                if (cx.type == "prop" && (ch == "}" || ch == ")")) cx = cx.prev;
                if (cx.prev) {
                    if (ch == "}" && (cx.type == "block" || cx.type == "top" || cx.type == "interpolation" || cx.type == "restricted_atBlock")) {
                        // Resume indentation from parent context.
                        cx = cx.prev;
                        indent = cx.indent;
                    } else if (ch == ")" && (cx.type == "parens" || cx.type == "atBlock_parens") || ch == "{" && (cx.type == "at" || cx.type == "atBlock")) {
                        // Dedent relative to current context.
                        indent = Math.max(0, cx.indent - indentUnit);
                    }
                }
                return indent;
            },
            electricChars: "}",
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            blockCommentContinue: " * ",
            lineComment: lineComment,
            fold: "brace"
        };
    });
    var documentTypes_ = [
        "domain",
        "regexp",
        "url",
        "url-prefix"
    ], documentTypes = keySet(documentTypes_);
    var mediaTypes_ = [
        "all",
        "aural",
        "braille",
        "handheld",
        "print",
        "projection",
        "screen",
        "tty",
        "tv",
        "embossed"
    ], mediaTypes = keySet(mediaTypes_);
    var mediaFeatures_ = [
        "width",
        "min-width",
        "max-width",
        "height",
        "min-height",
        "max-height",
        "device-width",
        "min-device-width",
        "max-device-width",
        "device-height",
        "min-device-height",
        "max-device-height",
        "aspect-ratio",
        "min-aspect-ratio",
        "max-aspect-ratio",
        "device-aspect-ratio",
        "min-device-aspect-ratio",
        "max-device-aspect-ratio",
        "color",
        "min-color",
        "max-color",
        "color-index",
        "min-color-index",
        "max-color-index",
        "monochrome",
        "min-monochrome",
        "max-monochrome",
        "resolution",
        "min-resolution",
        "max-resolution",
        "scan",
        "grid",
        "orientation",
        "device-pixel-ratio",
        "min-device-pixel-ratio",
        "max-device-pixel-ratio",
        "pointer",
        "any-pointer",
        "hover",
        "any-hover",
        "prefers-color-scheme",
        "dynamic-range",
        "video-dynamic-range"
    ], mediaFeatures = keySet(mediaFeatures_);
    var mediaValueKeywords_ = [
        "landscape",
        "portrait",
        "none",
        "coarse",
        "fine",
        "on-demand",
        "hover",
        "interlace",
        "progressive",
        "dark",
        "light",
        "standard",
        "high"
    ], mediaValueKeywords = keySet(mediaValueKeywords_);
    var propertyKeywords_ = [
        "align-content",
        "align-items",
        "align-self",
        "alignment-adjust",
        "alignment-baseline",
        "all",
        "anchor-point",
        "animation",
        "animation-delay",
        "animation-direction",
        "animation-duration",
        "animation-fill-mode",
        "animation-iteration-count",
        "animation-name",
        "animation-play-state",
        "animation-timing-function",
        "appearance",
        "azimuth",
        "backdrop-filter",
        "backface-visibility",
        "background",
        "background-attachment",
        "background-blend-mode",
        "background-clip",
        "background-color",
        "background-image",
        "background-origin",
        "background-position",
        "background-position-x",
        "background-position-y",
        "background-repeat",
        "background-size",
        "baseline-shift",
        "binding",
        "bleed",
        "block-size",
        "bookmark-label",
        "bookmark-level",
        "bookmark-state",
        "bookmark-target",
        "border",
        "border-bottom",
        "border-bottom-color",
        "border-bottom-left-radius",
        "border-bottom-right-radius",
        "border-bottom-style",
        "border-bottom-width",
        "border-collapse",
        "border-color",
        "border-image",
        "border-image-outset",
        "border-image-repeat",
        "border-image-slice",
        "border-image-source",
        "border-image-width",
        "border-left",
        "border-left-color",
        "border-left-style",
        "border-left-width",
        "border-radius",
        "border-right",
        "border-right-color",
        "border-right-style",
        "border-right-width",
        "border-spacing",
        "border-style",
        "border-top",
        "border-top-color",
        "border-top-left-radius",
        "border-top-right-radius",
        "border-top-style",
        "border-top-width",
        "border-width",
        "bottom",
        "box-decoration-break",
        "box-shadow",
        "box-sizing",
        "break-after",
        "break-before",
        "break-inside",
        "caption-side",
        "caret-color",
        "clear",
        "clip",
        "color",
        "color-profile",
        "column-count",
        "column-fill",
        "column-gap",
        "column-rule",
        "column-rule-color",
        "column-rule-style",
        "column-rule-width",
        "column-span",
        "column-width",
        "columns",
        "contain",
        "content",
        "counter-increment",
        "counter-reset",
        "crop",
        "cue",
        "cue-after",
        "cue-before",
        "cursor",
        "direction",
        "display",
        "dominant-baseline",
        "drop-initial-after-adjust",
        "drop-initial-after-align",
        "drop-initial-before-adjust",
        "drop-initial-before-align",
        "drop-initial-size",
        "drop-initial-value",
        "elevation",
        "empty-cells",
        "fit",
        "fit-content",
        "fit-position",
        "flex",
        "flex-basis",
        "flex-direction",
        "flex-flow",
        "flex-grow",
        "flex-shrink",
        "flex-wrap",
        "float",
        "float-offset",
        "flow-from",
        "flow-into",
        "font",
        "font-family",
        "font-feature-settings",
        "font-kerning",
        "font-language-override",
        "font-optical-sizing",
        "font-size",
        "font-size-adjust",
        "font-stretch",
        "font-style",
        "font-synthesis",
        "font-variant",
        "font-variant-alternates",
        "font-variant-caps",
        "font-variant-east-asian",
        "font-variant-ligatures",
        "font-variant-numeric",
        "font-variant-position",
        "font-variation-settings",
        "font-weight",
        "gap",
        "grid",
        "grid-area",
        "grid-auto-columns",
        "grid-auto-flow",
        "grid-auto-rows",
        "grid-column",
        "grid-column-end",
        "grid-column-gap",
        "grid-column-start",
        "grid-gap",
        "grid-row",
        "grid-row-end",
        "grid-row-gap",
        "grid-row-start",
        "grid-template",
        "grid-template-areas",
        "grid-template-columns",
        "grid-template-rows",
        "hanging-punctuation",
        "height",
        "hyphens",
        "icon",
        "image-orientation",
        "image-rendering",
        "image-resolution",
        "inline-box-align",
        "inset",
        "inset-block",
        "inset-block-end",
        "inset-block-start",
        "inset-inline",
        "inset-inline-end",
        "inset-inline-start",
        "isolation",
        "justify-content",
        "justify-items",
        "justify-self",
        "left",
        "letter-spacing",
        "line-break",
        "line-height",
        "line-height-step",
        "line-stacking",
        "line-stacking-ruby",
        "line-stacking-shift",
        "line-stacking-strategy",
        "list-style",
        "list-style-image",
        "list-style-position",
        "list-style-type",
        "margin",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "marks",
        "marquee-direction",
        "marquee-loop",
        "marquee-play-count",
        "marquee-speed",
        "marquee-style",
        "mask-clip",
        "mask-composite",
        "mask-image",
        "mask-mode",
        "mask-origin",
        "mask-position",
        "mask-repeat",
        "mask-size",
        "mask-type",
        "max-block-size",
        "max-height",
        "max-inline-size",
        "max-width",
        "min-block-size",
        "min-height",
        "min-inline-size",
        "min-width",
        "mix-blend-mode",
        "move-to",
        "nav-down",
        "nav-index",
        "nav-left",
        "nav-right",
        "nav-up",
        "object-fit",
        "object-position",
        "offset",
        "offset-anchor",
        "offset-distance",
        "offset-path",
        "offset-position",
        "offset-rotate",
        "opacity",
        "order",
        "orphans",
        "outline",
        "outline-color",
        "outline-offset",
        "outline-style",
        "outline-width",
        "overflow",
        "overflow-style",
        "overflow-wrap",
        "overflow-x",
        "overflow-y",
        "padding",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "page",
        "page-break-after",
        "page-break-before",
        "page-break-inside",
        "page-policy",
        "pause",
        "pause-after",
        "pause-before",
        "perspective",
        "perspective-origin",
        "pitch",
        "pitch-range",
        "place-content",
        "place-items",
        "place-self",
        "play-during",
        "position",
        "presentation-level",
        "punctuation-trim",
        "quotes",
        "region-break-after",
        "region-break-before",
        "region-break-inside",
        "region-fragment",
        "rendering-intent",
        "resize",
        "rest",
        "rest-after",
        "rest-before",
        "richness",
        "right",
        "rotate",
        "rotation",
        "rotation-point",
        "row-gap",
        "ruby-align",
        "ruby-overhang",
        "ruby-position",
        "ruby-span",
        "scale",
        "scroll-behavior",
        "scroll-margin",
        "scroll-margin-block",
        "scroll-margin-block-end",
        "scroll-margin-block-start",
        "scroll-margin-bottom",
        "scroll-margin-inline",
        "scroll-margin-inline-end",
        "scroll-margin-inline-start",
        "scroll-margin-left",
        "scroll-margin-right",
        "scroll-margin-top",
        "scroll-padding",
        "scroll-padding-block",
        "scroll-padding-block-end",
        "scroll-padding-block-start",
        "scroll-padding-bottom",
        "scroll-padding-inline",
        "scroll-padding-inline-end",
        "scroll-padding-inline-start",
        "scroll-padding-left",
        "scroll-padding-right",
        "scroll-padding-top",
        "scroll-snap-align",
        "scroll-snap-type",
        "shape-image-threshold",
        "shape-inside",
        "shape-margin",
        "shape-outside",
        "size",
        "speak",
        "speak-as",
        "speak-header",
        "speak-numeral",
        "speak-punctuation",
        "speech-rate",
        "stress",
        "string-set",
        "tab-size",
        "table-layout",
        "target",
        "target-name",
        "target-new",
        "target-position",
        "text-align",
        "text-align-last",
        "text-combine-upright",
        "text-decoration",
        "text-decoration-color",
        "text-decoration-line",
        "text-decoration-skip",
        "text-decoration-skip-ink",
        "text-decoration-style",
        "text-emphasis",
        "text-emphasis-color",
        "text-emphasis-position",
        "text-emphasis-style",
        "text-height",
        "text-indent",
        "text-justify",
        "text-orientation",
        "text-outline",
        "text-overflow",
        "text-rendering",
        "text-shadow",
        "text-size-adjust",
        "text-space-collapse",
        "text-transform",
        "text-underline-position",
        "text-wrap",
        "top",
        "touch-action",
        "transform",
        "transform-origin",
        "transform-style",
        "transition",
        "transition-delay",
        "transition-duration",
        "transition-property",
        "transition-timing-function",
        "translate",
        "unicode-bidi",
        "user-select",
        "vertical-align",
        "visibility",
        "voice-balance",
        "voice-duration",
        "voice-family",
        "voice-pitch",
        "voice-range",
        "voice-rate",
        "voice-stress",
        "voice-volume",
        "volume",
        "white-space",
        "widows",
        "width",
        "will-change",
        "word-break",
        "word-spacing",
        "word-wrap",
        "writing-mode",
        "z-index",
        // SVG-specific
        "clip-path",
        "clip-rule",
        "mask",
        "enable-background",
        "filter",
        "flood-color",
        "flood-opacity",
        "lighting-color",
        "stop-color",
        "stop-opacity",
        "pointer-events",
        "color-interpolation",
        "color-interpolation-filters",
        "color-rendering",
        "fill",
        "fill-opacity",
        "fill-rule",
        "image-rendering",
        "marker",
        "marker-end",
        "marker-mid",
        "marker-start",
        "paint-order",
        "shape-rendering",
        "stroke",
        "stroke-dasharray",
        "stroke-dashoffset",
        "stroke-linecap",
        "stroke-linejoin",
        "stroke-miterlimit",
        "stroke-opacity",
        "stroke-width",
        "text-rendering",
        "baseline-shift",
        "dominant-baseline",
        "glyph-orientation-horizontal",
        "glyph-orientation-vertical",
        "text-anchor",
        "writing-mode"
    ], propertyKeywords = keySet(propertyKeywords_);
    var nonStandardPropertyKeywords_ = [
        "accent-color",
        "aspect-ratio",
        "border-block",
        "border-block-color",
        "border-block-end",
        "border-block-end-color",
        "border-block-end-style",
        "border-block-end-width",
        "border-block-start",
        "border-block-start-color",
        "border-block-start-style",
        "border-block-start-width",
        "border-block-style",
        "border-block-width",
        "border-inline",
        "border-inline-color",
        "border-inline-end",
        "border-inline-end-color",
        "border-inline-end-style",
        "border-inline-end-width",
        "border-inline-start",
        "border-inline-start-color",
        "border-inline-start-style",
        "border-inline-start-width",
        "border-inline-style",
        "border-inline-width",
        "content-visibility",
        "margin-block",
        "margin-block-end",
        "margin-block-start",
        "margin-inline",
        "margin-inline-end",
        "margin-inline-start",
        "overflow-anchor",
        "overscroll-behavior",
        "padding-block",
        "padding-block-end",
        "padding-block-start",
        "padding-inline",
        "padding-inline-end",
        "padding-inline-start",
        "scroll-snap-stop",
        "scrollbar-3d-light-color",
        "scrollbar-arrow-color",
        "scrollbar-base-color",
        "scrollbar-dark-shadow-color",
        "scrollbar-face-color",
        "scrollbar-highlight-color",
        "scrollbar-shadow-color",
        "scrollbar-track-color",
        "searchfield-cancel-button",
        "searchfield-decoration",
        "searchfield-results-button",
        "searchfield-results-decoration",
        "shape-inside",
        "zoom"
    ], nonStandardPropertyKeywords = keySet(nonStandardPropertyKeywords_);
    var fontProperties_ = [
        "font-display",
        "font-family",
        "src",
        "unicode-range",
        "font-variant",
        "font-feature-settings",
        "font-stretch",
        "font-weight",
        "font-style"
    ], fontProperties = keySet(fontProperties_);
    var counterDescriptors_ = [
        "additive-symbols",
        "fallback",
        "negative",
        "pad",
        "prefix",
        "range",
        "speak-as",
        "suffix",
        "symbols",
        "system"
    ], counterDescriptors = keySet(counterDescriptors_);
    var colorKeywords_ = [
        "aliceblue",
        "antiquewhite",
        "aqua",
        "aquamarine",
        "azure",
        "beige",
        "bisque",
        "black",
        "blanchedalmond",
        "blue",
        "blueviolet",
        "brown",
        "burlywood",
        "cadetblue",
        "chartreuse",
        "chocolate",
        "coral",
        "cornflowerblue",
        "cornsilk",
        "crimson",
        "cyan",
        "darkblue",
        "darkcyan",
        "darkgoldenrod",
        "darkgray",
        "darkgreen",
        "darkgrey",
        "darkkhaki",
        "darkmagenta",
        "darkolivegreen",
        "darkorange",
        "darkorchid",
        "darkred",
        "darksalmon",
        "darkseagreen",
        "darkslateblue",
        "darkslategray",
        "darkslategrey",
        "darkturquoise",
        "darkviolet",
        "deeppink",
        "deepskyblue",
        "dimgray",
        "dimgrey",
        "dodgerblue",
        "firebrick",
        "floralwhite",
        "forestgreen",
        "fuchsia",
        "gainsboro",
        "ghostwhite",
        "gold",
        "goldenrod",
        "gray",
        "grey",
        "green",
        "greenyellow",
        "honeydew",
        "hotpink",
        "indianred",
        "indigo",
        "ivory",
        "khaki",
        "lavender",
        "lavenderblush",
        "lawngreen",
        "lemonchiffon",
        "lightblue",
        "lightcoral",
        "lightcyan",
        "lightgoldenrodyellow",
        "lightgray",
        "lightgreen",
        "lightgrey",
        "lightpink",
        "lightsalmon",
        "lightseagreen",
        "lightskyblue",
        "lightslategray",
        "lightslategrey",
        "lightsteelblue",
        "lightyellow",
        "lime",
        "limegreen",
        "linen",
        "magenta",
        "maroon",
        "mediumaquamarine",
        "mediumblue",
        "mediumorchid",
        "mediumpurple",
        "mediumseagreen",
        "mediumslateblue",
        "mediumspringgreen",
        "mediumturquoise",
        "mediumvioletred",
        "midnightblue",
        "mintcream",
        "mistyrose",
        "moccasin",
        "navajowhite",
        "navy",
        "oldlace",
        "olive",
        "olivedrab",
        "orange",
        "orangered",
        "orchid",
        "palegoldenrod",
        "palegreen",
        "paleturquoise",
        "palevioletred",
        "papayawhip",
        "peachpuff",
        "peru",
        "pink",
        "plum",
        "powderblue",
        "purple",
        "rebeccapurple",
        "red",
        "rosybrown",
        "royalblue",
        "saddlebrown",
        "salmon",
        "sandybrown",
        "seagreen",
        "seashell",
        "sienna",
        "silver",
        "skyblue",
        "slateblue",
        "slategray",
        "slategrey",
        "snow",
        "springgreen",
        "steelblue",
        "tan",
        "teal",
        "thistle",
        "tomato",
        "turquoise",
        "violet",
        "wheat",
        "white",
        "whitesmoke",
        "yellow",
        "yellowgreen"
    ], colorKeywords = keySet(colorKeywords_);
    var valueKeywords_ = [
        "above",
        "absolute",
        "activeborder",
        "additive",
        "activecaption",
        "afar",
        "after-white-space",
        "ahead",
        "alias",
        "all",
        "all-scroll",
        "alphabetic",
        "alternate",
        "always",
        "amharic",
        "amharic-abegede",
        "antialiased",
        "appworkspace",
        "arabic-indic",
        "armenian",
        "asterisks",
        "attr",
        "auto",
        "auto-flow",
        "avoid",
        "avoid-column",
        "avoid-page",
        "avoid-region",
        "axis-pan",
        "background",
        "backwards",
        "baseline",
        "below",
        "bidi-override",
        "binary",
        "bengali",
        "blink",
        "block",
        "block-axis",
        "blur",
        "bold",
        "bolder",
        "border",
        "border-box",
        "both",
        "bottom",
        "break",
        "break-all",
        "break-word",
        "brightness",
        "bullets",
        "button",
        "button-bevel",
        "buttonface",
        "buttonhighlight",
        "buttonshadow",
        "buttontext",
        "calc",
        "cambodian",
        "capitalize",
        "caps-lock-indicator",
        "caption",
        "captiontext",
        "caret",
        "cell",
        "center",
        "checkbox",
        "circle",
        "cjk-decimal",
        "cjk-earthly-branch",
        "cjk-heavenly-stem",
        "cjk-ideographic",
        "clear",
        "clip",
        "close-quote",
        "col-resize",
        "collapse",
        "color",
        "color-burn",
        "color-dodge",
        "column",
        "column-reverse",
        "compact",
        "condensed",
        "contain",
        "content",
        "contents",
        "content-box",
        "context-menu",
        "continuous",
        "contrast",
        "copy",
        "counter",
        "counters",
        "cover",
        "crop",
        "cross",
        "crosshair",
        "cubic-bezier",
        "currentcolor",
        "cursive",
        "cyclic",
        "darken",
        "dashed",
        "decimal",
        "decimal-leading-zero",
        "default",
        "default-button",
        "dense",
        "destination-atop",
        "destination-in",
        "destination-out",
        "destination-over",
        "devanagari",
        "difference",
        "disc",
        "discard",
        "disclosure-closed",
        "disclosure-open",
        "document",
        "dot-dash",
        "dot-dot-dash",
        "dotted",
        "double",
        "down",
        "drop-shadow",
        "e-resize",
        "ease",
        "ease-in",
        "ease-in-out",
        "ease-out",
        "element",
        "ellipse",
        "ellipsis",
        "embed",
        "end",
        "ethiopic",
        "ethiopic-abegede",
        "ethiopic-abegede-am-et",
        "ethiopic-abegede-gez",
        "ethiopic-abegede-ti-er",
        "ethiopic-abegede-ti-et",
        "ethiopic-halehame-aa-er",
        "ethiopic-halehame-aa-et",
        "ethiopic-halehame-am-et",
        "ethiopic-halehame-gez",
        "ethiopic-halehame-om-et",
        "ethiopic-halehame-sid-et",
        "ethiopic-halehame-so-et",
        "ethiopic-halehame-ti-er",
        "ethiopic-halehame-ti-et",
        "ethiopic-halehame-tig",
        "ethiopic-numeric",
        "ew-resize",
        "exclusion",
        "expanded",
        "extends",
        "extra-condensed",
        "extra-expanded",
        "fantasy",
        "fast",
        "fill",
        "fill-box",
        "fixed",
        "flat",
        "flex",
        "flex-end",
        "flex-start",
        "footnotes",
        "forwards",
        "from",
        "geometricPrecision",
        "georgian",
        "grayscale",
        "graytext",
        "grid",
        "groove",
        "gujarati",
        "gurmukhi",
        "hand",
        "hangul",
        "hangul-consonant",
        "hard-light",
        "hebrew",
        "help",
        "hidden",
        "hide",
        "higher",
        "highlight",
        "highlighttext",
        "hiragana",
        "hiragana-iroha",
        "horizontal",
        "hsl",
        "hsla",
        "hue",
        "hue-rotate",
        "icon",
        "ignore",
        "inactiveborder",
        "inactivecaption",
        "inactivecaptiontext",
        "infinite",
        "infobackground",
        "infotext",
        "inherit",
        "initial",
        "inline",
        "inline-axis",
        "inline-block",
        "inline-flex",
        "inline-grid",
        "inline-table",
        "inset",
        "inside",
        "intrinsic",
        "invert",
        "italic",
        "japanese-formal",
        "japanese-informal",
        "justify",
        "kannada",
        "katakana",
        "katakana-iroha",
        "keep-all",
        "khmer",
        "korean-hangul-formal",
        "korean-hanja-formal",
        "korean-hanja-informal",
        "landscape",
        "lao",
        "large",
        "larger",
        "left",
        "level",
        "lighter",
        "lighten",
        "line-through",
        "linear",
        "linear-gradient",
        "lines",
        "list-item",
        "listbox",
        "listitem",
        "local",
        "logical",
        "loud",
        "lower",
        "lower-alpha",
        "lower-armenian",
        "lower-greek",
        "lower-hexadecimal",
        "lower-latin",
        "lower-norwegian",
        "lower-roman",
        "lowercase",
        "ltr",
        "luminosity",
        "malayalam",
        "manipulation",
        "match",
        "matrix",
        "matrix3d",
        "media-controls-background",
        "media-current-time-display",
        "media-fullscreen-button",
        "media-mute-button",
        "media-play-button",
        "media-return-to-realtime-button",
        "media-rewind-button",
        "media-seek-back-button",
        "media-seek-forward-button",
        "media-slider",
        "media-sliderthumb",
        "media-time-remaining-display",
        "media-volume-slider",
        "media-volume-slider-container",
        "media-volume-sliderthumb",
        "medium",
        "menu",
        "menulist",
        "menulist-button",
        "menulist-text",
        "menulist-textfield",
        "menutext",
        "message-box",
        "middle",
        "min-intrinsic",
        "mix",
        "mongolian",
        "monospace",
        "move",
        "multiple",
        "multiple_mask_images",
        "multiply",
        "myanmar",
        "n-resize",
        "narrower",
        "ne-resize",
        "nesw-resize",
        "no-close-quote",
        "no-drop",
        "no-open-quote",
        "no-repeat",
        "none",
        "normal",
        "not-allowed",
        "nowrap",
        "ns-resize",
        "numbers",
        "numeric",
        "nw-resize",
        "nwse-resize",
        "oblique",
        "octal",
        "opacity",
        "open-quote",
        "optimizeLegibility",
        "optimizeSpeed",
        "oriya",
        "oromo",
        "outset",
        "outside",
        "outside-shape",
        "overlay",
        "overline",
        "padding",
        "padding-box",
        "painted",
        "page",
        "paused",
        "persian",
        "perspective",
        "pinch-zoom",
        "plus-darker",
        "plus-lighter",
        "pointer",
        "polygon",
        "portrait",
        "pre",
        "pre-line",
        "pre-wrap",
        "preserve-3d",
        "progress",
        "push-button",
        "radial-gradient",
        "radio",
        "read-only",
        "read-write",
        "read-write-plaintext-only",
        "rectangle",
        "region",
        "relative",
        "repeat",
        "repeating-linear-gradient",
        "repeating-radial-gradient",
        "repeat-x",
        "repeat-y",
        "reset",
        "reverse",
        "rgb",
        "rgba",
        "ridge",
        "right",
        "rotate",
        "rotate3d",
        "rotateX",
        "rotateY",
        "rotateZ",
        "round",
        "row",
        "row-resize",
        "row-reverse",
        "rtl",
        "run-in",
        "running",
        "s-resize",
        "sans-serif",
        "saturate",
        "saturation",
        "scale",
        "scale3d",
        "scaleX",
        "scaleY",
        "scaleZ",
        "screen",
        "scroll",
        "scrollbar",
        "scroll-position",
        "se-resize",
        "searchfield",
        "searchfield-cancel-button",
        "searchfield-decoration",
        "searchfield-results-button",
        "searchfield-results-decoration",
        "self-start",
        "self-end",
        "semi-condensed",
        "semi-expanded",
        "separate",
        "sepia",
        "serif",
        "show",
        "sidama",
        "simp-chinese-formal",
        "simp-chinese-informal",
        "single",
        "skew",
        "skewX",
        "skewY",
        "skip-white-space",
        "slide",
        "slider-horizontal",
        "slider-vertical",
        "sliderthumb-horizontal",
        "sliderthumb-vertical",
        "slow",
        "small",
        "small-caps",
        "small-caption",
        "smaller",
        "soft-light",
        "solid",
        "somali",
        "source-atop",
        "source-in",
        "source-out",
        "source-over",
        "space",
        "space-around",
        "space-between",
        "space-evenly",
        "spell-out",
        "square",
        "square-button",
        "start",
        "static",
        "status-bar",
        "stretch",
        "stroke",
        "stroke-box",
        "sub",
        "subpixel-antialiased",
        "svg_masks",
        "super",
        "sw-resize",
        "symbolic",
        "symbols",
        "system-ui",
        "table",
        "table-caption",
        "table-cell",
        "table-column",
        "table-column-group",
        "table-footer-group",
        "table-header-group",
        "table-row",
        "table-row-group",
        "tamil",
        "telugu",
        "text",
        "text-bottom",
        "text-top",
        "textarea",
        "textfield",
        "thai",
        "thick",
        "thin",
        "threeddarkshadow",
        "threedface",
        "threedhighlight",
        "threedlightshadow",
        "threedshadow",
        "tibetan",
        "tigre",
        "tigrinya-er",
        "tigrinya-er-abegede",
        "tigrinya-et",
        "tigrinya-et-abegede",
        "to",
        "top",
        "trad-chinese-formal",
        "trad-chinese-informal",
        "transform",
        "translate",
        "translate3d",
        "translateX",
        "translateY",
        "translateZ",
        "transparent",
        "ultra-condensed",
        "ultra-expanded",
        "underline",
        "unidirectional-pan",
        "unset",
        "up",
        "upper-alpha",
        "upper-armenian",
        "upper-greek",
        "upper-hexadecimal",
        "upper-latin",
        "upper-norwegian",
        "upper-roman",
        "uppercase",
        "urdu",
        "url",
        "var",
        "vertical",
        "vertical-text",
        "view-box",
        "visible",
        "visibleFill",
        "visiblePainted",
        "visibleStroke",
        "visual",
        "w-resize",
        "wait",
        "wave",
        "wider",
        "window",
        "windowframe",
        "windowtext",
        "words",
        "wrap",
        "wrap-reverse",
        "x-large",
        "x-small",
        "xor",
        "xx-large",
        "xx-small"
    ], valueKeywords = keySet(valueKeywords_);
    var allWords = documentTypes_.concat(mediaTypes_).concat(mediaFeatures_).concat(mediaValueKeywords_).concat(propertyKeywords_).concat(nonStandardPropertyKeywords_).concat(colorKeywords_).concat(valueKeywords_);
    CodeMirror1.registerHelper("hintWords", "css", allWords);
    CodeMirror1.defineMIME("text/css", {
        documentTypes: documentTypes,
        mediaTypes: mediaTypes,
        mediaFeatures: mediaFeatures,
        mediaValueKeywords: mediaValueKeywords,
        propertyKeywords: propertyKeywords,
        nonStandardPropertyKeywords: nonStandardPropertyKeywords,
        fontProperties: fontProperties,
        counterDescriptors: counterDescriptors,
        colorKeywords: colorKeywords,
        valueKeywords: valueKeywords,
        tokenHooks: {
            "/": function(stream, state) {
                if (!stream.eat("*")) return false;
                state.tokenize = tokenCComment;
                return tokenCComment(stream, state);
            }
        },
        name: "css"
    });
    CodeMirror1.defineMIME("text/x-scss", {
        mediaTypes: mediaTypes,
        mediaFeatures: mediaFeatures,
        mediaValueKeywords: mediaValueKeywords,
        propertyKeywords: propertyKeywords,
        nonStandardPropertyKeywords: nonStandardPropertyKeywords,
        colorKeywords: colorKeywords,
        valueKeywords: valueKeywords,
        fontProperties: fontProperties,
        allowNested: true,
        lineComment: "//",
        tokenHooks: {
            "/": function(stream, state) {
                if (stream.eat("/")) {
                    stream.skipToEnd();
                    return [
                        "comment",
                        "comment"
                    ];
                } else if (stream.eat("*")) {
                    state.tokenize = tokenCComment;
                    return tokenCComment(stream, state);
                } else {
                    return [
                        "operator",
                        "operator"
                    ];
                }
            },
            ":": function(stream) {
                if (stream.match(/^\s*\{/, false)) return [
                    null,
                    null
                ];
                return false;
            },
            "$": function(stream) {
                stream.match(/^[\w-]+/);
                if (stream.match(/^\s*:/, false)) return [
                    "variable-2",
                    "variable-definition"
                ];
                return [
                    "variable-2",
                    "variable"
                ];
            },
            "#": function(stream) {
                if (!stream.eat("{")) return false;
                return [
                    null,
                    "interpolation"
                ];
            }
        },
        name: "css",
        helperType: "scss"
    });
    CodeMirror1.defineMIME("text/x-less", {
        mediaTypes: mediaTypes,
        mediaFeatures: mediaFeatures,
        mediaValueKeywords: mediaValueKeywords,
        propertyKeywords: propertyKeywords,
        nonStandardPropertyKeywords: nonStandardPropertyKeywords,
        colorKeywords: colorKeywords,
        valueKeywords: valueKeywords,
        fontProperties: fontProperties,
        allowNested: true,
        lineComment: "//",
        tokenHooks: {
            "/": function(stream, state) {
                if (stream.eat("/")) {
                    stream.skipToEnd();
                    return [
                        "comment",
                        "comment"
                    ];
                } else if (stream.eat("*")) {
                    state.tokenize = tokenCComment;
                    return tokenCComment(stream, state);
                } else {
                    return [
                        "operator",
                        "operator"
                    ];
                }
            },
            "@": function(stream) {
                if (stream.eat("{")) return [
                    null,
                    "interpolation"
                ];
                if (stream.match(/^(charset|document|font-face|import|(-(moz|ms|o|webkit)-)?keyframes|media|namespace|page|supports)\b/i, false)) return false;
                stream.eatWhile(/[\w\\\-]/);
                if (stream.match(/^\s*:/, false)) return [
                    "variable-2",
                    "variable-definition"
                ];
                return [
                    "variable-2",
                    "variable"
                ];
            },
            "&": function() {
                return [
                    "atom",
                    "atom"
                ];
            }
        },
        name: "css",
        helperType: "less"
    });
    CodeMirror1.defineMIME("text/x-gss", {
        documentTypes: documentTypes,
        mediaTypes: mediaTypes,
        mediaFeatures: mediaFeatures,
        propertyKeywords: propertyKeywords,
        nonStandardPropertyKeywords: nonStandardPropertyKeywords,
        fontProperties: fontProperties,
        counterDescriptors: counterDescriptors,
        colorKeywords: colorKeywords,
        valueKeywords: valueKeywords,
        supportsAtComponent: true,
        tokenHooks: {
            "/": function(stream, state) {
                if (!stream.eat("*")) return false;
                state.tokenize = tokenCComment;
                return tokenCComment(stream, state);
            }
        },
        name: "css",
        helperType: "gss"
    });
});
});

var dart = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, clike);
})(function(CodeMirror1) {
    var set = function set(words) {
        var obj = {};
        for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
        return obj;
    };
    var pushInterpolationStack = function pushInterpolationStack(state) {
        (state.interpolationStack || (state.interpolationStack = [])).push(state.tokenize);
    };
    var popInterpolationStack = function popInterpolationStack(state) {
        return (state.interpolationStack || (state.interpolationStack = [])).pop();
    };
    var sizeInterpolationStack = function sizeInterpolationStack(state) {
        return state.interpolationStack ? state.interpolationStack.length : 0;
    };
    var tokenString = function tokenString(quote, stream, state, raw) {
        var tripleQuoted = false;
        if (stream.eat(quote)) {
            if (stream.eat(quote)) tripleQuoted = true;
            else return "string"; //empty string
        }
        function tokenStringHelper(stream, state) {
            var escaped = false;
            while(!stream.eol()){
                if (!raw && !escaped && stream.peek() == "$") {
                    pushInterpolationStack(state);
                    state.tokenize = tokenInterpolation;
                    return "string";
                }
                var next = stream.next();
                if (next == quote && !escaped && (!tripleQuoted || stream.match(quote + quote))) {
                    state.tokenize = null;
                    break;
                }
                escaped = !raw && !escaped && next == "\\";
            }
            return "string";
        }
        state.tokenize = tokenStringHelper;
        return tokenStringHelper(stream, state);
    };
    var tokenInterpolation = function tokenInterpolation(stream, state) {
        stream.eat("$");
        if (stream.eat("{")) {
            // let clike handle the content of ${...},
            // we take over again when "}" appears (see hooks).
            state.tokenize = null;
        } else {
            state.tokenize = tokenInterpolationIdentifier;
        }
        return null;
    };
    var tokenInterpolationIdentifier = function tokenInterpolationIdentifier(stream, state) {
        stream.eatWhile(/[\w_]/);
        state.tokenize = popInterpolationStack(state);
        return "variable";
    };
    var keywords = ("this super static final const abstract class extends external factory " + "implements mixin get native set typedef with enum throw rethrow " + "assert break case continue default in return new deferred async await covariant " + "try catch finally do else for if switch while import library export " + "part of show hide is as extension on yield late required").split(" ");
    var blockKeywords = "try catch finally do else for if switch while".split(" ");
    var atoms = "true false null".split(" ");
    var builtins = "void bool num int double dynamic var String Null Never".split(" ");
    CodeMirror1.defineMIME("application/dart", {
        name: "clike",
        keywords: set(keywords),
        blockKeywords: set(blockKeywords),
        builtin: set(builtins),
        atoms: set(atoms),
        hooks: {
            "@": function(stream) {
                stream.eatWhile(/[\w\$_\.]/);
                return "meta";
            },
            // custom string handling to deal with triple-quoted strings and string interpolation
            "'": function(stream, state) {
                return tokenString("'", stream, state, false);
            },
            '"': function(stream, state) {
                return tokenString('"', stream, state, false);
            },
            "r": function(stream, state) {
                var peek = stream.peek();
                if (peek == "'" || peek == '"') {
                    return tokenString(stream.next(), stream, state, true);
                }
                return false;
            },
            "}": function(_stream, state) {
                // "}" is end of interpolation, if interpolation stack is non-empty
                if (sizeInterpolationStack(state) > 0) {
                    state.tokenize = popInterpolationStack(state);
                    return null;
                }
                return false;
            },
            "/": function(stream, state) {
                if (!stream.eat("*")) return false;
                state.tokenize = tokenNestedComment(1);
                return state.tokenize(stream, state);
            },
            token: function token(stream, _, style) {
                if (style == "variable") {
                    // Assume uppercase symbols are classes using variable-2
                    var isUpper = RegExp("^[_$]*[A-Z][a-zA-Z0-9_$]*$", "g");
                    if (isUpper.test(stream.current())) {
                        return "variable-2";
                    }
                }
            }
        }
    });
    function tokenNestedComment(depth) {
        return function(stream, state) {
            var ch;
            while(ch = stream.next()){
                if (ch == "*" && stream.eat("/")) {
                    if (depth == 1) {
                        state.tokenize = null;
                        break;
                    } else {
                        state.tokenize = tokenNestedComment(depth - 1);
                        return state.tokenize(stream, state);
                    }
                } else if (ch == "/" && stream.eat("*")) {
                    state.tokenize = tokenNestedComment(depth + 1);
                    return state.tokenize(stream, state);
                }
            }
            return "comment";
        };
    }
    CodeMirror1.registerHelper("hintWords", "application/dart", keywords.concat(atoms).concat(builtins));
    // This is needed to make loading through meta.js work.
    CodeMirror1.defineMode("dart", function(conf) {
        return CodeMirror1.getMode(conf, "application/dart");
    }, "clike");
});
});

var elm = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: http://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("elm", function() {
        var switchState = function switchState(source, setState, f) {
            setState(f);
            return f(source, setState);
        };
        var normal = function normal() {
            return function(source, setState) {
                if (source.eatWhile(spacesRE)) {
                    return null;
                }
                var char = source.next();
                if (specialRE.test(char)) {
                    return char === "{" && source.eat("-") ? switchState(source, setState, chompMultiComment(1)) : char === "[" && source.match("glsl|") ? switchState(source, setState, chompGlsl) : "builtin";
                }
                if (char === "'") {
                    return switchState(source, setState, chompChar);
                }
                if (char === '"') {
                    return source.eat('"') ? source.eat('"') ? switchState(source, setState, chompMultiString) : "string" : switchState(source, setState, chompSingleString);
                }
                if (upperRE.test(char)) {
                    source.eatWhile(innerRE);
                    return "variable-2";
                }
                if (lowerRE.test(char)) {
                    var isDef = source.pos === 1;
                    source.eatWhile(innerRE);
                    return isDef ? "def" : "variable";
                }
                if (digitRE.test(char)) {
                    if (char === "0") {
                        if (source.eat(/[xX]/)) {
                            source.eatWhile(hexRE); // should require at least 1
                            return "number";
                        }
                    } else {
                        source.eatWhile(digitRE);
                    }
                    if (source.eat(".")) {
                        source.eatWhile(digitRE); // should require at least 1
                    }
                    if (source.eat(/[eE]/)) {
                        source.eat(/[-+]/);
                        source.eatWhile(digitRE); // should require at least 1
                    }
                    return "number";
                }
                if (symbolRE.test(char)) {
                    if (char === "-" && source.eat("-")) {
                        source.skipToEnd();
                        return "comment";
                    }
                    source.eatWhile(symbolRE);
                    return "keyword";
                }
                if (char === "_") {
                    return "keyword";
                }
                return "error";
            };
        };
        var chompMultiString = function chompMultiString(source, setState) {
            while(!source.eol()){
                var char = source.next();
                if (char === '"' && source.eat('"') && source.eat('"')) {
                    setState(normal());
                    return "string";
                }
            }
            return "string";
        };
        var chompSingleString = function chompSingleString(source, setState) {
            while(source.skipTo('\\"')){
                source.next();
                source.next();
            }
            if (source.skipTo('"')) {
                source.next();
                setState(normal());
                return "string";
            }
            source.skipToEnd();
            setState(normal());
            return "error";
        };
        var chompChar = function chompChar(source, setState) {
            while(source.skipTo("\\'")){
                source.next();
                source.next();
            }
            if (source.skipTo("'")) {
                source.next();
                setState(normal());
                return "string";
            }
            source.skipToEnd();
            setState(normal());
            return "error";
        };
        var chompGlsl = function chompGlsl(source, setState) {
            while(!source.eol()){
                var char = source.next();
                if (char === "|" && source.eat("]")) {
                    setState(normal());
                    return "string";
                }
            }
            return "string";
        };
        var lowerRE = /[a-z]/;
        var upperRE = /[A-Z]/;
        var innerRE = /[a-zA-Z0-9_]/;
        var digitRE = /[0-9]/;
        var hexRE = /[0-9A-Fa-f]/;
        var symbolRE = /[-&*+.\\/<>=?^|:]/;
        var specialRE = /[(),[\]{}]/;
        var spacesRE = /[ \v\f]/; // newlines are handled in tokenizer
        function chompMultiComment(nest) {
            if (nest == 0) {
                return normal();
            }
            return function(source, setState) {
                while(!source.eol()){
                    var char = source.next();
                    if (char == "{" && source.eat("-")) {
                        ++nest;
                    } else if (char == "-" && source.eat("}")) {
                        --nest;
                        if (nest === 0) {
                            setState(normal());
                            return "comment";
                        }
                    }
                }
                setState(chompMultiComment(nest));
                return "comment";
            };
        }
        var wellKnownWords = {
            case: 1,
            of: 1,
            as: 1,
            if: 1,
            then: 1,
            else: 1,
            let: 1,
            in: 1,
            type: 1,
            alias: 1,
            module: 1,
            where: 1,
            import: 1,
            exposing: 1,
            port: 1
        };
        return {
            startState: function startState() {
                return {
                    f: normal()
                };
            },
            copyState: function copyState(s) {
                return {
                    f: s.f
                };
            },
            token: function token(stream, state) {
                var type = state.f(stream, function(s) {
                    state.f = s;
                });
                var word = stream.current();
                return wellKnownWords.hasOwnProperty(word) ? "keyword" : type;
            }
        };
    });
    CodeMirror1.defineMIME("text/x-elm", "elm");
});
});

var go = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("go", function(config) {
        var tokenBase = function tokenBase(stream, state) {
            var ch = stream.next();
            if (ch == '"' || ch == "'" || ch == "`") {
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            }
            if (/[\d\.]/.test(ch)) {
                if (ch == ".") {
                    stream.match(/^[0-9]+([eE][\-+]?[0-9]+)?/);
                } else if (ch == "0") {
                    stream.match(/^[xX][0-9a-fA-F]+/) || stream.match(/^0[0-7]+/);
                } else {
                    stream.match(/^[0-9]*\.?[0-9]*([eE][\-+]?[0-9]+)?/);
                }
                return "number";
            }
            if (/[\[\]{}\(\),;\:\.]/.test(ch)) {
                curPunc = ch;
                return null;
            }
            if (ch == "/") {
                if (stream.eat("*")) {
                    state.tokenize = tokenComment;
                    return tokenComment(stream, state);
                }
                if (stream.eat("/")) {
                    stream.skipToEnd();
                    return "comment";
                }
            }
            if (isOperatorChar.test(ch)) {
                stream.eatWhile(isOperatorChar);
                return "operator";
            }
            stream.eatWhile(/[\w\$_\xa1-\uffff]/);
            var cur = stream.current();
            if (keywords.propertyIsEnumerable(cur)) {
                if (cur == "case" || cur == "default") curPunc = "case";
                return "keyword";
            }
            if (atoms.propertyIsEnumerable(cur)) return "atom";
            return "variable";
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, next, end = false;
                while((next = stream.next()) != null){
                    if (next == quote && !escaped) {
                        end = true;
                        break;
                    }
                    escaped = !escaped && quote != "`" && next == "\\";
                }
                if (end || !(escaped || quote == "`")) state.tokenize = tokenBase;
                return "string";
            };
        };
        var tokenComment = function tokenComment(stream, state) {
            var maybeEnd = false, ch;
            while(ch = stream.next()){
                if (ch == "/" && maybeEnd) {
                    state.tokenize = tokenBase;
                    break;
                }
                maybeEnd = ch == "*";
            }
            return "comment";
        };
        var Context = function Context(indented, column, type, align, prev) {
            this.indented = indented;
            this.column = column;
            this.type = type;
            this.align = align;
            this.prev = prev;
        };
        var pushContext = function pushContext(state, col, type) {
            return state.context = new Context(state.indented, col, type, null, state.context);
        };
        var popContext = function popContext(state) {
            if (!state.context.prev) return;
            var t = state.context.type;
            if (t == ")" || t == "]" || t == "}") state.indented = state.context.indented;
            return state.context = state.context.prev;
        };
        var indentUnit = config.indentUnit;
        var keywords = {
            "break": true,
            "case": true,
            "chan": true,
            "const": true,
            "continue": true,
            "default": true,
            "defer": true,
            "else": true,
            "fallthrough": true,
            "for": true,
            "func": true,
            "go": true,
            "goto": true,
            "if": true,
            "import": true,
            "interface": true,
            "map": true,
            "package": true,
            "range": true,
            "return": true,
            "select": true,
            "struct": true,
            "switch": true,
            "type": true,
            "var": true,
            "bool": true,
            "byte": true,
            "complex64": true,
            "complex128": true,
            "float32": true,
            "float64": true,
            "int8": true,
            "int16": true,
            "int32": true,
            "int64": true,
            "string": true,
            "uint8": true,
            "uint16": true,
            "uint32": true,
            "uint64": true,
            "int": true,
            "uint": true,
            "uintptr": true,
            "error": true,
            "rune": true
        };
        var atoms = {
            "true": true,
            "false": true,
            "iota": true,
            "nil": true,
            "append": true,
            "cap": true,
            "close": true,
            "complex": true,
            "copy": true,
            "delete": true,
            "imag": true,
            "len": true,
            "make": true,
            "new": true,
            "panic": true,
            "print": true,
            "println": true,
            "real": true,
            "recover": true
        };
        var isOperatorChar = /[+\-*&^%:=<>!|\/]/;
        var curPunc;
        // Interface
        return {
            startState: function startState(basecolumn) {
                return {
                    tokenize: null,
                    context: new Context((basecolumn || 0) - indentUnit, 0, "top", false),
                    indented: 0,
                    startOfLine: true
                };
            },
            token: function token(stream, state) {
                var ctx = state.context;
                if (stream.sol()) {
                    if (ctx.align == null) ctx.align = false;
                    state.indented = stream.indentation();
                    state.startOfLine = true;
                    if (ctx.type == "case") ctx.type = "}";
                }
                if (stream.eatSpace()) return null;
                curPunc = null;
                var style = (state.tokenize || tokenBase)(stream, state);
                if (style == "comment") return style;
                if (ctx.align == null) ctx.align = true;
                if (curPunc == "{") pushContext(state, stream.column(), "}");
                else if (curPunc == "[") pushContext(state, stream.column(), "]");
                else if (curPunc == "(") pushContext(state, stream.column(), ")");
                else if (curPunc == "case") ctx.type = "case";
                else if (curPunc == "}" && ctx.type == "}") popContext(state);
                else if (curPunc == ctx.type) popContext(state);
                state.startOfLine = false;
                return style;
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize != tokenBase && state.tokenize != null) return CodeMirror1.Pass;
                var ctx = state.context, firstChar = textAfter && textAfter.charAt(0);
                if (ctx.type == "case" && /^(?:case|default)\b/.test(textAfter)) {
                    state.context.type = "}";
                    return ctx.indented;
                }
                var closing = firstChar == ctx.type;
                if (ctx.align) return ctx.column + (closing ? 0 : 1);
                else return ctx.indented + (closing ? 0 : indentUnit);
            },
            electricChars: "{}):",
            closeBrackets: "()[]{}''\"\"``",
            fold: "brace",
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            lineComment: "//"
        };
    });
    CodeMirror1.defineMIME("text/x-go", "go");
});
});

var groovy = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("groovy", function(config) {
        var words = function words(str) {
            var obj = {}, words = str.split(" ");
            for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
            return obj;
        };
        var tokenBase = function tokenBase(stream, state) {
            var ch = stream.next();
            if (ch == '"' || ch == "'") {
                return startString(ch, stream, state);
            }
            if (/[\[\]{}\(\),;\:\.]/.test(ch)) {
                curPunc = ch;
                return null;
            }
            if (/\d/.test(ch)) {
                stream.eatWhile(/[\w\.]/);
                if (stream.eat(/eE/)) {
                    stream.eat(/\+\-/);
                    stream.eatWhile(/\d/);
                }
                return "number";
            }
            if (ch == "/") {
                if (stream.eat("*")) {
                    state.tokenize.push(tokenComment);
                    return tokenComment(stream, state);
                }
                if (stream.eat("/")) {
                    stream.skipToEnd();
                    return "comment";
                }
                if (expectExpression(state.lastToken, false)) {
                    return startString(ch, stream, state);
                }
            }
            if (ch == "-" && stream.eat(">")) {
                curPunc = "->";
                return null;
            }
            if (/[+\-*&%=<>!?|\/~]/.test(ch)) {
                stream.eatWhile(/[+\-*&%=<>|~]/);
                return "operator";
            }
            stream.eatWhile(/[\w\$_]/);
            if (ch == "@") {
                stream.eatWhile(/[\w\$_\.]/);
                return "meta";
            }
            if (state.lastToken == ".") return "property";
            if (stream.eat(":")) {
                curPunc = "proplabel";
                return "property";
            }
            var cur = stream.current();
            if (atoms.propertyIsEnumerable(cur)) {
                return "atom";
            }
            if (keywords.propertyIsEnumerable(cur)) {
                if (blockKeywords.propertyIsEnumerable(cur)) curPunc = "newstatement";
                else if (standaloneKeywords.propertyIsEnumerable(cur)) curPunc = "standalone";
                return "keyword";
            }
            return "variable";
        };
        var startString = function startString(quote, stream, state) {
            var tripleQuoted = false;
            if (quote != "/" && stream.eat(quote)) {
                if (stream.eat(quote)) tripleQuoted = true;
                else return "string";
            }
            function t(stream, state) {
                var escaped = false, next, end = !tripleQuoted;
                while((next = stream.next()) != null){
                    if (next == quote && !escaped) {
                        if (!tripleQuoted) {
                            break;
                        }
                        if (stream.match(quote + quote)) {
                            end = true;
                            break;
                        }
                    }
                    if (quote == '"' && next == "$" && !escaped && stream.eat("{")) {
                        state.tokenize.push(tokenBaseUntilBrace());
                        return "string";
                    }
                    escaped = !escaped && next == "\\";
                }
                if (end) state.tokenize.pop();
                return "string";
            }
            state.tokenize.push(t);
            return t(stream, state);
        };
        var tokenBaseUntilBrace = function tokenBaseUntilBrace() {
            var depth = 1;
            function t(stream, state) {
                if (stream.peek() == "}") {
                    depth--;
                    if (depth == 0) {
                        state.tokenize.pop();
                        return state.tokenize[state.tokenize.length - 1](stream, state);
                    }
                } else if (stream.peek() == "{") {
                    depth++;
                }
                return tokenBase(stream, state);
            }
            t.isBase = true;
            return t;
        };
        var tokenComment = function tokenComment(stream, state) {
            var maybeEnd = false, ch;
            while(ch = stream.next()){
                if (ch == "/" && maybeEnd) {
                    state.tokenize.pop();
                    break;
                }
                maybeEnd = ch == "*";
            }
            return "comment";
        };
        var expectExpression = function expectExpression(last, newline) {
            return !last || last == "operator" || last == "->" || /[\.\[\{\(,;:]/.test(last) || last == "newstatement" || last == "keyword" || last == "proplabel" || last == "standalone" && !newline;
        };
        var Context = function Context(indented, column, type, align, prev) {
            this.indented = indented;
            this.column = column;
            this.type = type;
            this.align = align;
            this.prev = prev;
        };
        var pushContext = function pushContext(state, col, type) {
            return state.context = new Context(state.indented, col, type, null, state.context);
        };
        var popContext = function popContext(state) {
            var t = state.context.type;
            if (t == ")" || t == "]" || t == "}") state.indented = state.context.indented;
            return state.context = state.context.prev;
        };
        var keywords = words("abstract as assert boolean break byte case catch char class const continue def default " + "do double else enum extends final finally float for goto if implements import in " + "instanceof int interface long native new package private protected public return " + "short static strictfp super switch synchronized threadsafe throw throws trait transient " + "try void volatile while");
        var blockKeywords = words("catch class def do else enum finally for if interface switch trait try while");
        var standaloneKeywords = words("return break continue");
        var atoms = words("null true false this");
        var curPunc;
        tokenBase.isBase = true;
        // Interface
        return {
            startState: function startState(basecolumn) {
                return {
                    tokenize: [
                        tokenBase
                    ],
                    context: new Context((basecolumn || 0) - config.indentUnit, 0, "top", false),
                    indented: 0,
                    startOfLine: true,
                    lastToken: null
                };
            },
            token: function token(stream, state) {
                var ctx = state.context;
                if (stream.sol()) {
                    if (ctx.align == null) ctx.align = false;
                    state.indented = stream.indentation();
                    state.startOfLine = true;
                    // Automatic semicolon insertion
                    if (ctx.type == "statement" && !expectExpression(state.lastToken, true)) {
                        popContext(state);
                        ctx = state.context;
                    }
                }
                if (stream.eatSpace()) return null;
                curPunc = null;
                var style = state.tokenize[state.tokenize.length - 1](stream, state);
                if (style == "comment") return style;
                if (ctx.align == null) ctx.align = true;
                if ((curPunc == ";" || curPunc == ":") && ctx.type == "statement") popContext(state);
                else if (curPunc == "->" && ctx.type == "statement" && ctx.prev.type == "}") {
                    popContext(state);
                    state.context.align = false;
                } else if (curPunc == "{") pushContext(state, stream.column(), "}");
                else if (curPunc == "[") pushContext(state, stream.column(), "]");
                else if (curPunc == "(") pushContext(state, stream.column(), ")");
                else if (curPunc == "}") {
                    while(ctx.type == "statement")ctx = popContext(state);
                    if (ctx.type == "}") ctx = popContext(state);
                    while(ctx.type == "statement")ctx = popContext(state);
                } else if (curPunc == ctx.type) popContext(state);
                else if (ctx.type == "}" || ctx.type == "top" || ctx.type == "statement" && curPunc == "newstatement") pushContext(state, stream.column(), "statement");
                state.startOfLine = false;
                state.lastToken = curPunc || style;
                return style;
            },
            indent: function indent(state, textAfter) {
                if (!state.tokenize[state.tokenize.length - 1].isBase) return CodeMirror1.Pass;
                var firstChar = textAfter && textAfter.charAt(0), ctx = state.context;
                if (ctx.type == "statement" && !expectExpression(state.lastToken, true)) ctx = ctx.prev;
                var closing = firstChar == ctx.type;
                if (ctx.type == "statement") return ctx.indented + (firstChar == "{" ? 0 : config.indentUnit);
                else if (ctx.align) return ctx.column + (closing ? 0 : 1);
                else return ctx.indented + (closing ? 0 : config.indentUnit);
            },
            electricChars: "{}",
            closeBrackets: {
                triples: "'\""
            },
            fold: "brace",
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            lineComment: "//"
        };
    });
    CodeMirror1.defineMIME("text/x-groovy", "groovy");
});
});

var xml = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var htmlConfig = {
        autoSelfClosers: {
            "area": true,
            "base": true,
            "br": true,
            "col": true,
            "command": true,
            "embed": true,
            "frame": true,
            "hr": true,
            "img": true,
            "input": true,
            "keygen": true,
            "link": true,
            "meta": true,
            "param": true,
            "source": true,
            "track": true,
            "wbr": true,
            "menuitem": true
        },
        implicitlyClosed: {
            "dd": true,
            "li": true,
            "optgroup": true,
            "option": true,
            "p": true,
            "rp": true,
            "rt": true,
            "tbody": true,
            "td": true,
            "tfoot": true,
            "th": true,
            "tr": true
        },
        contextGrabbers: {
            "dd": {
                "dd": true,
                "dt": true
            },
            "dt": {
                "dd": true,
                "dt": true
            },
            "li": {
                "li": true
            },
            "option": {
                "option": true,
                "optgroup": true
            },
            "optgroup": {
                "optgroup": true
            },
            "p": {
                "address": true,
                "article": true,
                "aside": true,
                "blockquote": true,
                "dir": true,
                "div": true,
                "dl": true,
                "fieldset": true,
                "footer": true,
                "form": true,
                "h1": true,
                "h2": true,
                "h3": true,
                "h4": true,
                "h5": true,
                "h6": true,
                "header": true,
                "hgroup": true,
                "hr": true,
                "menu": true,
                "nav": true,
                "ol": true,
                "p": true,
                "pre": true,
                "section": true,
                "table": true,
                "ul": true
            },
            "rp": {
                "rp": true,
                "rt": true
            },
            "rt": {
                "rp": true,
                "rt": true
            },
            "tbody": {
                "tbody": true,
                "tfoot": true
            },
            "td": {
                "td": true,
                "th": true
            },
            "tfoot": {
                "tbody": true
            },
            "th": {
                "td": true,
                "th": true
            },
            "thead": {
                "tbody": true,
                "tfoot": true
            },
            "tr": {
                "tr": true
            }
        },
        doNotIndent: {
            "pre": true
        },
        allowUnquoted: true,
        allowMissing: true,
        caseFold: true
    };
    var xmlConfig = {
        autoSelfClosers: {},
        implicitlyClosed: {},
        contextGrabbers: {},
        doNotIndent: {},
        allowUnquoted: false,
        allowMissing: false,
        allowMissingTagName: false,
        caseFold: false
    };
    CodeMirror1.defineMode("xml", function(editorConf, config_) {
        var inText = function inText(stream, state) {
            function chain(parser) {
                state.tokenize = parser;
                return parser(stream, state);
            }
            var ch = stream.next();
            if (ch == "<") {
                if (stream.eat("!")) {
                    if (stream.eat("[")) {
                        if (stream.match("CDATA[")) return chain(inBlock("atom", "]]>"));
                        else return null;
                    } else if (stream.match("--")) {
                        return chain(inBlock("comment", "-->"));
                    } else if (stream.match("DOCTYPE", true, true)) {
                        stream.eatWhile(/[\w\._\-]/);
                        return chain(doctype(1));
                    } else {
                        return null;
                    }
                } else if (stream.eat("?")) {
                    stream.eatWhile(/[\w\._\-]/);
                    state.tokenize = inBlock("meta", "?>");
                    return "meta";
                } else {
                    type = stream.eat("/") ? "closeTag" : "openTag";
                    state.tokenize = inTag;
                    return "tag bracket";
                }
            } else if (ch == "&") {
                var ok;
                if (stream.eat("#")) {
                    if (stream.eat("x")) {
                        ok = stream.eatWhile(/[a-fA-F\d]/) && stream.eat(";");
                    } else {
                        ok = stream.eatWhile(/[\d]/) && stream.eat(";");
                    }
                } else {
                    ok = stream.eatWhile(/[\w\.\-:]/) && stream.eat(";");
                }
                return ok ? "atom" : "error";
            } else {
                stream.eatWhile(/[^&<]/);
                return null;
            }
        };
        var inTag = function inTag(stream, state) {
            var ch = stream.next();
            if (ch == ">" || ch == "/" && stream.eat(">")) {
                state.tokenize = inText;
                type = ch == ">" ? "endTag" : "selfcloseTag";
                return "tag bracket";
            } else if (ch == "=") {
                type = "equals";
                return null;
            } else if (ch == "<") {
                state.tokenize = inText;
                state.state = baseState;
                state.tagName = state.tagStart = null;
                var next = state.tokenize(stream, state);
                return next ? next + " tag error" : "tag error";
            } else if (/[\'\"]/.test(ch)) {
                state.tokenize = inAttribute(ch);
                state.stringStartCol = stream.column();
                return state.tokenize(stream, state);
            } else {
                stream.match(/^[^\s\u00a0=<>\"\']*[^\s\u00a0=<>\"\'\/]/);
                return "word";
            }
        };
        var inAttribute = function inAttribute(quote) {
            var closure = function closure(stream, state) {
                while(!stream.eol()){
                    if (stream.next() == quote) {
                        state.tokenize = inTag;
                        break;
                    }
                }
                return "string";
            };
            closure.isInAttribute = true;
            return closure;
        };
        var inBlock = function inBlock(style, terminator) {
            return function(stream, state) {
                while(!stream.eol()){
                    if (stream.match(terminator)) {
                        state.tokenize = inText;
                        break;
                    }
                    stream.next();
                }
                return style;
            };
        };
        var lower = function lower(tagName) {
            return tagName && tagName.toLowerCase();
        };
        var Context = function Context(state, tagName, startOfLine) {
            this.prev = state.context;
            this.tagName = tagName || "";
            this.indent = state.indented;
            this.startOfLine = startOfLine;
            if (config.doNotIndent.hasOwnProperty(tagName) || state.context && state.context.noIndent) this.noIndent = true;
        };
        var popContext = function popContext(state) {
            if (state.context) state.context = state.context.prev;
        };
        var maybePopContext = function maybePopContext(state, nextTagName) {
            var parentTagName;
            while(true){
                if (!state.context) {
                    return;
                }
                parentTagName = state.context.tagName;
                if (!config.contextGrabbers.hasOwnProperty(lower(parentTagName)) || !config.contextGrabbers[lower(parentTagName)].hasOwnProperty(lower(nextTagName))) {
                    return;
                }
                popContext(state);
            }
        };
        var closeTagNameState = function closeTagNameState(type, stream, state) {
            if (type == "word") {
                var tagName = stream.current();
                if (state.context && state.context.tagName != tagName && config.implicitlyClosed.hasOwnProperty(lower(state.context.tagName))) popContext(state);
                if (state.context && state.context.tagName == tagName || config.matchClosing === false) {
                    setStyle = "tag";
                    return closeState;
                } else {
                    setStyle = "tag error";
                    return closeStateErr;
                }
            } else if (config.allowMissingTagName && type == "endTag") {
                setStyle = "tag bracket";
                return closeState(type, stream, state);
            } else {
                setStyle = "error";
                return closeStateErr;
            }
        };
        var closeStateErr = function closeStateErr(type, stream, state) {
            setStyle = "error";
            return closeState(type, stream, state);
        };
        var attrEqState = function attrEqState(type, stream, state) {
            if (type == "equals") return attrValueState;
            if (!config.allowMissing) setStyle = "error";
            return attrState(type, stream, state);
        };
        var attrValueState = function attrValueState(type, stream, state) {
            if (type == "string") return attrContinuedState;
            if (type == "word" && config.allowUnquoted) {
                setStyle = "string";
                return attrState;
            }
            setStyle = "error";
            return attrState(type, stream, state);
        };
        var indentUnit = editorConf.indentUnit;
        var config = {};
        var defaults = config_.htmlMode ? htmlConfig : xmlConfig;
        for(var prop in defaults)config[prop] = defaults[prop];
        for(var prop in config_)config[prop] = config_[prop];
        // Return variables for tokenizers
        var type, setStyle;
        inText.isInText = true;
        function doctype(depth) {
            return function(stream, state) {
                var ch;
                while((ch = stream.next()) != null){
                    if (ch == "<") {
                        state.tokenize = doctype(depth + 1);
                        return state.tokenize(stream, state);
                    } else if (ch == ">") {
                        if (depth == 1) {
                            state.tokenize = inText;
                            break;
                        } else {
                            state.tokenize = doctype(depth - 1);
                            return state.tokenize(stream, state);
                        }
                    }
                }
                return "meta";
            };
        }
        function baseState(type, stream, state) {
            if (type == "openTag") {
                state.tagStart = stream.column();
                return tagNameState;
            } else if (type == "closeTag") {
                return closeTagNameState;
            } else {
                return baseState;
            }
        }
        function tagNameState(type, stream, state) {
            if (type == "word") {
                state.tagName = stream.current();
                setStyle = "tag";
                return attrState;
            } else if (config.allowMissingTagName && type == "endTag") {
                setStyle = "tag bracket";
                return attrState(type, stream, state);
            } else {
                setStyle = "error";
                return tagNameState;
            }
        }
        function closeState(type, _stream, state) {
            if (type != "endTag") {
                setStyle = "error";
                return closeState;
            }
            popContext(state);
            return baseState;
        }
        function attrState(type, _stream, state) {
            if (type == "word") {
                setStyle = "attribute";
                return attrEqState;
            } else if (type == "endTag" || type == "selfcloseTag") {
                var tagName = state.tagName, tagStart = state.tagStart;
                state.tagName = state.tagStart = null;
                if (type == "selfcloseTag" || config.autoSelfClosers.hasOwnProperty(lower(tagName))) {
                    maybePopContext(state, tagName);
                } else {
                    maybePopContext(state, tagName);
                    state.context = new Context(state, tagName, tagStart == state.indented);
                }
                return baseState;
            }
            setStyle = "error";
            return attrState;
        }
        function attrContinuedState(type, stream, state) {
            if (type == "string") return attrContinuedState;
            return attrState(type, stream, state);
        }
        return {
            startState: function startState(baseIndent) {
                var state = {
                    tokenize: inText,
                    state: baseState,
                    indented: baseIndent || 0,
                    tagName: null,
                    tagStart: null,
                    context: null
                };
                if (baseIndent != null) state.baseIndent = baseIndent;
                return state;
            },
            token: function token(stream, state) {
                if (!state.tagName && stream.sol()) state.indented = stream.indentation();
                if (stream.eatSpace()) return null;
                type = null;
                var style = state.tokenize(stream, state);
                if ((style || type) && style != "comment") {
                    setStyle = null;
                    state.state = state.state(type || style, stream, state);
                    if (setStyle) style = setStyle == "error" ? style + " error" : setStyle;
                }
                return style;
            },
            indent: function indent(state, textAfter, fullLine) {
                var context = state.context;
                // Indent multi-line strings (e.g. css).
                if (state.tokenize.isInAttribute) {
                    if (state.tagStart == state.indented) return state.stringStartCol + 1;
                    else return state.indented + indentUnit;
                }
                if (context && context.noIndent) return CodeMirror1.Pass;
                if (state.tokenize != inTag && state.tokenize != inText) return fullLine ? fullLine.match(/^(\s*)/)[0].length : 0;
                // Indent the starts of attribute names.
                if (state.tagName) {
                    if (config.multilineTagIndentPastTag !== false) return state.tagStart + state.tagName.length + 2;
                    else return state.tagStart + indentUnit * (config.multilineTagIndentFactor || 1);
                }
                if (config.alignCDATA && /<!\[CDATA\[/.test(textAfter)) return 0;
                var tagAfter = textAfter && /^<(\/)?([\w_:\.-]*)/.exec(textAfter);
                if (tagAfter && tagAfter[1]) {
                    while(context){
                        if (context.tagName == tagAfter[2]) {
                            context = context.prev;
                            break;
                        } else if (config.implicitlyClosed.hasOwnProperty(lower(context.tagName))) {
                            context = context.prev;
                        } else {
                            break;
                        }
                    }
                } else if (tagAfter) {
                    while(context){
                        var grabbers = config.contextGrabbers[lower(context.tagName)];
                        if (grabbers && grabbers.hasOwnProperty(lower(tagAfter[2]))) context = context.prev;
                        else break;
                    }
                }
                while(context && context.prev && !context.startOfLine)context = context.prev;
                if (context) return context.indent + indentUnit;
                else return state.baseIndent || 0;
            },
            electricInput: /<\/[\s\w:]+>$/,
            blockCommentStart: "<!--",
            blockCommentEnd: "-->",
            configuration: config.htmlMode ? "html" : "xml",
            helperType: config.htmlMode ? "html" : "xml",
            skipAttribute: function skipAttribute(state) {
                if (state.state == attrValueState) state.state = attrState;
            },
            xmlCurrentTag: function xmlCurrentTag(state) {
                return state.tagName ? {
                    name: state.tagName,
                    close: state.type == "closeTag"
                } : null;
            },
            xmlCurrentContext: function xmlCurrentContext(state) {
                var context = [];
                for(var cx = state.context; cx; cx = cx.prev)context.push(cx.tagName);
                return context.reverse();
            }
        };
    });
    CodeMirror1.defineMIME("text/xml", "xml");
    CodeMirror1.defineMIME("application/xml", "xml");
    if (!CodeMirror1.mimeModes.hasOwnProperty("text/html")) CodeMirror1.defineMIME("text/html", {
        name: "xml",
        htmlMode: true
    });
});
});

var javascript = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("javascript", function(config, parserConfig) {
        var readRegexp = function readRegexp(stream) {
            var escaped = false, next, inSet = false;
            while((next = stream.next()) != null){
                if (!escaped) {
                    if (next == "/" && !inSet) return;
                    if (next == "[") inSet = true;
                    else if (inSet && next == "]") inSet = false;
                }
                escaped = !escaped && next == "\\";
            }
        };
        var ret = function ret(tp, style, cont) {
            type = tp;
            content = cont;
            return style;
        };
        var tokenBase = function tokenBase(stream, state) {
            var ch = stream.next();
            if (ch == '"' || ch == "'") {
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            } else if (ch == "." && stream.match(/^\d[\d_]*(?:[eE][+\-]?[\d_]+)?/)) {
                return ret("number", "number");
            } else if (ch == "." && stream.match("..")) {
                return ret("spread", "meta");
            } else if (/[\[\]{}\(\),;\:\.]/.test(ch)) {
                return ret(ch);
            } else if (ch == "=" && stream.eat(">")) {
                return ret("=>", "operator");
            } else if (ch == "0" && stream.match(/^(?:x[\dA-Fa-f_]+|o[0-7_]+|b[01_]+)n?/)) {
                return ret("number", "number");
            } else if (/\d/.test(ch)) {
                stream.match(/^[\d_]*(?:n|(?:\.[\d_]*)?(?:[eE][+\-]?[\d_]+)?)?/);
                return ret("number", "number");
            } else if (ch == "/") {
                if (stream.eat("*")) {
                    state.tokenize = tokenComment;
                    return tokenComment(stream, state);
                } else if (stream.eat("/")) {
                    stream.skipToEnd();
                    return ret("comment", "comment");
                } else if (expressionAllowed(stream, state, 1)) {
                    readRegexp(stream);
                    stream.match(/^\b(([gimyus])(?![gimyus]*\2))+\b/);
                    return ret("regexp", "string-2");
                } else {
                    stream.eat("=");
                    return ret("operator", "operator", stream.current());
                }
            } else if (ch == "`") {
                state.tokenize = tokenQuasi;
                return tokenQuasi(stream, state);
            } else if (ch == "#" && stream.peek() == "!") {
                stream.skipToEnd();
                return ret("meta", "meta");
            } else if (ch == "#" && stream.eatWhile(wordRE)) {
                return ret("variable", "property");
            } else if (ch == "<" && stream.match("!--") || ch == "-" && stream.match("->") && !/\S/.test(stream.string.slice(0, stream.start))) {
                stream.skipToEnd();
                return ret("comment", "comment");
            } else if (isOperatorChar.test(ch)) {
                if (ch != ">" || !state.lexical || state.lexical.type != ">") {
                    if (stream.eat("=")) {
                        if (ch == "!" || ch == "=") stream.eat("=");
                    } else if (/[<>*+\-|&?]/.test(ch)) {
                        stream.eat(ch);
                        if (ch == ">") stream.eat(ch);
                    }
                }
                if (ch == "?" && stream.eat(".")) return ret(".");
                return ret("operator", "operator", stream.current());
            } else if (wordRE.test(ch)) {
                stream.eatWhile(wordRE);
                var word = stream.current();
                if (state.lastType != ".") {
                    if (keywords.propertyIsEnumerable(word)) {
                        var kw = keywords[word];
                        return ret(kw.type, kw.style, word);
                    }
                    if (word == "async" && stream.match(/^(\s|\/\*([^*]|\*(?!\/))*?\*\/)*[\[\(\w]/, false)) return ret("async", "keyword", word);
                }
                return ret("variable", "variable", word);
            }
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, next;
                if (jsonldMode && stream.peek() == "@" && stream.match(isJsonldKeyword)) {
                    state.tokenize = tokenBase;
                    return ret("jsonld-keyword", "meta");
                }
                while((next = stream.next()) != null){
                    if (next == quote && !escaped) break;
                    escaped = !escaped && next == "\\";
                }
                if (!escaped) state.tokenize = tokenBase;
                return ret("string", "string");
            };
        };
        var tokenComment = function tokenComment(stream, state) {
            var maybeEnd = false, ch;
            while(ch = stream.next()){
                if (ch == "/" && maybeEnd) {
                    state.tokenize = tokenBase;
                    break;
                }
                maybeEnd = ch == "*";
            }
            return ret("comment", "comment");
        };
        var tokenQuasi = function tokenQuasi(stream, state) {
            var escaped = false, next;
            while((next = stream.next()) != null){
                if (!escaped && (next == "`" || next == "$" && stream.eat("{"))) {
                    state.tokenize = tokenBase;
                    break;
                }
                escaped = !escaped && next == "\\";
            }
            return ret("quasi", "string-2", stream.current());
        };
        var findFatArrow = // This is a crude lookahead trick to try and notice that we're
        // parsing the argument patterns for a fat-arrow function before we
        // actually hit the arrow token. It only works if the arrow is on
        // the same line as the arguments and there's no strange noise
        // (comments) in between. Fallback is to only notice when we hit the
        // arrow, and not declare the arguments as locals for the arrow
        // body.
        function findFatArrow(stream, state) {
            if (state.fatArrowAt) state.fatArrowAt = null;
            var arrow = stream.string.indexOf("=>", stream.start);
            if (arrow < 0) return;
            if (isTS) {
                var m = /:\s*(?:\w+(?:<[^>]*>|\[\])?|\{[^}]*\})\s*$/.exec(stream.string.slice(stream.start, arrow));
                if (m) arrow = m.index;
            }
            var depth = 0, sawSomething = false;
            for(var pos = arrow - 1; pos >= 0; --pos){
                var ch = stream.string.charAt(pos);
                var bracket = brackets.indexOf(ch);
                if (bracket >= 0 && bracket < 3) {
                    if (!depth) {
                        ++pos;
                        break;
                    }
                    if (--depth == 0) {
                        if (ch == "(") sawSomething = true;
                        break;
                    }
                } else if (bracket >= 3 && bracket < 6) {
                    ++depth;
                } else if (wordRE.test(ch)) {
                    sawSomething = true;
                } else if (/["'\/`]/.test(ch)) {
                    for(;; --pos){
                        if (pos == 0) return;
                        var next = stream.string.charAt(pos - 1);
                        if (next == ch && stream.string.charAt(pos - 2) != "\\") {
                            pos--;
                            break;
                        }
                    }
                } else if (sawSomething && !depth) {
                    ++pos;
                    break;
                }
            }
            if (sawSomething && !depth) state.fatArrowAt = pos;
        };
        var JSLexical = function JSLexical(indented, column, type, align, prev, info) {
            this.indented = indented;
            this.column = column;
            this.type = type;
            this.prev = prev;
            this.info = info;
            if (align != null) this.align = align;
        };
        var inScope = function inScope(state, varname) {
            if (!trackScope) return false;
            for(var v = state.localVars; v; v = v.next)if (v.name == varname) return true;
            for(var cx = state.context; cx; cx = cx.prev){
                for(var v = cx.vars; v; v = v.next)if (v.name == varname) return true;
            }
        };
        var parseJS = function parseJS(state, style, type, content, stream) {
            var cc = state.cc;
            // Communicate our context to the combinators.
            // (Less wasteful than consing up a hundred closures on every call.)
            cx.state = state;
            cx.stream = stream;
            cx.marked = null, cx.cc = cc;
            cx.style = style;
            if (!state.lexical.hasOwnProperty("align")) state.lexical.align = true;
            while(true){
                var combinator = cc.length ? cc.pop() : jsonMode ? expression : statement;
                if (combinator(type, content)) {
                    while(cc.length && cc[cc.length - 1].lex)cc.pop()();
                    if (cx.marked) return cx.marked;
                    if (type == "variable" && inScope(state, content)) return "variable-2";
                    return style;
                }
            }
        };
        var pass = function pass() {
            for(var i = arguments.length - 1; i >= 0; i--)cx.cc.push(arguments[i]);
        };
        var cont = function cont() {
            pass.apply(null, arguments);
            return true;
        };
        var inList = function inList(name, list) {
            for(var v = list; v; v = v.next)if (v.name == name) return true;
            return false;
        };
        var register = function register(varname) {
            var state = cx.state;
            cx.marked = "def";
            if (!trackScope) return;
            if (state.context) {
                if (state.lexical.info == "var" && state.context && state.context.block) {
                    // FIXME function decls are also not block scoped
                    var newContext = registerVarScoped(varname, state.context);
                    if (newContext != null) {
                        state.context = newContext;
                        return;
                    }
                } else if (!inList(varname, state.localVars)) {
                    state.localVars = new Var(varname, state.localVars);
                    return;
                }
            }
            // Fall through means this is global
            if (parserConfig.globalVars && !inList(varname, state.globalVars)) state.globalVars = new Var(varname, state.globalVars);
        };
        var isModifier = function isModifier(name) {
            return name == "public" || name == "private" || name == "protected" || name == "abstract" || name == "readonly";
        };
        var Context = // Combinators
        function Context(prev, vars, block) {
            this.prev = prev;
            this.vars = vars;
            this.block = block;
        };
        var Var = function Var(name, next) {
            this.name = name;
            this.next = next;
        };
        var pushcontext = function pushcontext() {
            cx.state.context = new Context(cx.state.context, cx.state.localVars, false);
            cx.state.localVars = defaultVars;
        };
        var pushblockcontext = function pushblockcontext() {
            cx.state.context = new Context(cx.state.context, cx.state.localVars, true);
            cx.state.localVars = null;
        };
        var popcontext = function popcontext() {
            cx.state.localVars = cx.state.context.vars;
            cx.state.context = cx.state.context.prev;
        };
        var pushlex = function pushlex(type, info) {
            var result = function result() {
                var state = cx.state, indent = state.indented;
                if (state.lexical.type == "stat") indent = state.lexical.indented;
                else for(var outer = state.lexical; outer && outer.type == ")" && outer.align; outer = outer.prev)indent = outer.indented;
                state.lexical = new JSLexical(indent, cx.stream.column(), type, null, state.lexical, info);
            };
            result.lex = true;
            return result;
        };
        var poplex = function poplex() {
            var state = cx.state;
            if (state.lexical.prev) {
                if (state.lexical.type == ")") state.indented = state.lexical.indented;
                state.lexical = state.lexical.prev;
            }
        };
        var expect = function expect(wanted) {
            function exp(type) {
                if (type == wanted) return cont();
                else if (wanted == ";" || type == "}" || type == ")" || type == "]") return pass();
                else return cont(exp);
            }
            return exp;
        };
        var maybeCatchBinding = function maybeCatchBinding(type) {
            if (type == "(") return cont(funarg, expect(")"));
        };
        var expression = function expression(type, value) {
            return expressionInner(type, value, false);
        };
        var expressionNoComma = function expressionNoComma(type, value) {
            return expressionInner(type, value, true);
        };
        var parenExpr = function parenExpr(type) {
            if (type != "(") return pass();
            return cont(pushlex(")"), maybeexpression, expect(")"), poplex);
        };
        var expressionInner = function expressionInner(type, value, noComma) {
            if (cx.state.fatArrowAt == cx.stream.start) {
                var body = noComma ? arrowBodyNoComma : arrowBody;
                if (type == "(") return cont(pushcontext, pushlex(")"), commasep(funarg, ")"), poplex, expect("=>"), body, popcontext);
                else if (type == "variable") return pass(pushcontext, pattern, expect("=>"), body, popcontext);
            }
            var maybeop = noComma ? maybeoperatorNoComma : maybeoperatorComma;
            if (atomicTypes.hasOwnProperty(type)) return cont(maybeop);
            if (type == "function") return cont(functiondef, maybeop);
            if (type == "class" || isTS && value == "interface") {
                cx.marked = "keyword";
                return cont(pushlex("form"), classExpression, poplex);
            }
            if (type == "keyword c" || type == "async") return cont(noComma ? expressionNoComma : expression);
            if (type == "(") return cont(pushlex(")"), maybeexpression, expect(")"), poplex, maybeop);
            if (type == "operator" || type == "spread") return cont(noComma ? expressionNoComma : expression);
            if (type == "[") return cont(pushlex("]"), arrayLiteral, poplex, maybeop);
            if (type == "{") return contCommasep(objprop, "}", null, maybeop);
            if (type == "quasi") return pass(quasi, maybeop);
            if (type == "new") return cont(maybeTarget(noComma));
            return cont();
        };
        var maybeexpression = function maybeexpression(type) {
            if (type.match(/[;\}\)\],]/)) return pass();
            return pass(expression);
        };
        var maybeoperatorComma = function maybeoperatorComma(type, value) {
            if (type == ",") return cont(maybeexpression);
            return maybeoperatorNoComma(type, value, false);
        };
        var continueQuasi = function continueQuasi(type) {
            if (type == "}") {
                cx.marked = "string-2";
                cx.state.tokenize = tokenQuasi;
                return cont(quasi);
            }
        };
        var arrowBody = function arrowBody(type) {
            findFatArrow(cx.stream, cx.state);
            return pass(type == "{" ? statement : expression);
        };
        var arrowBodyNoComma = function arrowBodyNoComma(type) {
            findFatArrow(cx.stream, cx.state);
            return pass(type == "{" ? statement : expressionNoComma);
        };
        var maybeTarget = function maybeTarget(noComma) {
            return function(type) {
                if (type == ".") return cont(noComma ? targetNoComma : target);
                else if (type == "variable" && isTS) return cont(maybeTypeArgs, noComma ? maybeoperatorNoComma : maybeoperatorComma);
                else return pass(noComma ? expressionNoComma : expression);
            };
        };
        var target = function target(_, value) {
            if (value == "target") {
                cx.marked = "keyword";
                return cont(maybeoperatorComma);
            }
        };
        var targetNoComma = function targetNoComma(_, value) {
            if (value == "target") {
                cx.marked = "keyword";
                return cont(maybeoperatorNoComma);
            }
        };
        var maybelabel = function maybelabel(type) {
            if (type == ":") return cont(poplex, statement);
            return pass(maybeoperatorComma, expect(";"), poplex);
        };
        var property = function property(type) {
            if (type == "variable") {
                cx.marked = "property";
                return cont();
            }
        };
        var getterSetter = function getterSetter(type) {
            if (type != "variable") return pass(afterprop);
            cx.marked = "property";
            return cont(functiondef);
        };
        var afterprop = function afterprop(type) {
            if (type == ":") return cont(expressionNoComma);
            if (type == "(") return pass(functiondef);
        };
        var commasep = function commasep(what, end, sep) {
            function proceed(type, value) {
                if (sep ? sep.indexOf(type) > -1 : type == ",") {
                    var lex = cx.state.lexical;
                    if (lex.info == "call") lex.pos = (lex.pos || 0) + 1;
                    return cont(function(type, value) {
                        if (type == end || value == end) return pass();
                        return pass(what);
                    }, proceed);
                }
                if (type == end || value == end) return cont();
                if (sep && sep.indexOf(";") > -1) return pass(what);
                return cont(expect(end));
            }
            return function(type, value) {
                if (type == end || value == end) return cont();
                return pass(what, proceed);
            };
        };
        var contCommasep = function contCommasep(what, end, info) {
            for(var i = 3; i < arguments.length; i++)cx.cc.push(arguments[i]);
            return cont(pushlex(end, info), commasep(what, end), poplex);
        };
        var maybetypeOrIn = function maybetypeOrIn(type, value) {
            if (isTS && (type == ":" || value == "in")) return cont(typeexpr);
        };
        var mayberettype = function mayberettype(type) {
            if (isTS && type == ":") {
                if (cx.stream.match(/^\s*\w+\s+is\b/, false)) return cont(expression, isKW, typeexpr);
                else return cont(typeexpr);
            }
        };
        var isKW = function isKW(_, value) {
            if (value == "is") {
                cx.marked = "keyword";
                return cont();
            }
        };
        var maybeReturnType = function maybeReturnType(type) {
            if (type == "=>") return cont(typeexpr);
        };
        var continueQuasiType = function continueQuasiType(type) {
            if (type == "}") {
                cx.marked = "string-2";
                cx.state.tokenize = tokenQuasi;
                return cont(quasiType);
            }
        };
        var maybeTypeArgs = function maybeTypeArgs(_, value) {
            if (value == "<") return cont(pushlex(">"), commasep(typeexpr, ">"), poplex, afterType);
        };
        var typeparam = function typeparam() {
            return pass(typeexpr, maybeTypeDefault);
        };
        var maybeTypeDefault = function maybeTypeDefault(_, value) {
            if (value == "=") return cont(typeexpr);
        };
        var vardef = function vardef(_, value) {
            if (value == "enum") {
                cx.marked = "keyword";
                return cont(enumdef);
            }
            return pass(pattern, maybetype, maybeAssign, vardefCont);
        };
        var eltpattern = function eltpattern() {
            return pass(pattern, maybeAssign);
        };
        var maybeAssign = function maybeAssign(_type, value) {
            if (value == "=") return cont(expressionNoComma);
        };
        var vardefCont = function vardefCont(type) {
            if (type == ",") return cont(vardef);
        };
        var maybeelse = function maybeelse(type, value) {
            if (type == "keyword b" && value == "else") return cont(pushlex("form", "else"), statement, poplex);
        };
        var forspec1 = function forspec1(type) {
            if (type == "var") return cont(vardef, forspec2);
            if (type == "variable") return cont(forspec2);
            return pass(forspec2);
        };
        var classExpression = function classExpression(type, value) {
            // Class expressions may have an optional name.
            if (type == "variable") return className(type, value);
            return classNameAfter(type, value);
        };
        var className = function className(type, value) {
            if (type == "variable") {
                register(value);
                return cont(classNameAfter);
            }
        };
        var afterExport = function afterExport(type, value) {
            if (value == "*") {
                cx.marked = "keyword";
                return cont(maybeFrom, expect(";"));
            }
            if (value == "default") {
                cx.marked = "keyword";
                return cont(expression, expect(";"));
            }
            if (type == "{") return cont(commasep(exportField, "}"), maybeFrom, expect(";"));
            return pass(statement);
        };
        var afterImport = function afterImport(type) {
            if (type == "string") return cont();
            if (type == "(") return pass(expression);
            if (type == ".") return pass(maybeoperatorComma);
            return pass(importSpec, maybeMoreImports, maybeFrom);
        };
        var maybeAs = function maybeAs(_type, value) {
            if (value == "as") {
                cx.marked = "keyword";
                return cont(importSpec);
            }
        };
        var maybeFrom = function maybeFrom(_type, value) {
            if (value == "from") {
                cx.marked = "keyword";
                return cont(expression);
            }
        };
        var arrayLiteral = function arrayLiteral(type) {
            if (type == "]") return cont();
            return pass(commasep(expressionNoComma, "]"));
        };
        var enumdef = function enumdef() {
            return pass(pushlex("form"), pattern, expect("{"), pushlex("}"), commasep(enummember, "}"), poplex, poplex);
        };
        var enummember = function enummember() {
            return pass(pattern, maybeAssign);
        };
        var isContinuedStatement = function isContinuedStatement(state, textAfter) {
            return state.lastType == "operator" || state.lastType == "," || isOperatorChar.test(textAfter.charAt(0)) || /[,.]/.test(textAfter.charAt(0));
        };
        var expressionAllowed = function expressionAllowed(stream, state, backUp) {
            return state.tokenize == tokenBase && /^(?:operator|sof|keyword [bcd]|case|new|export|default|spread|[\[{}\(,;:]|=>)$/.test(state.lastType) || state.lastType == "quasi" && /\{\s*$/.test(stream.string.slice(0, stream.pos - (backUp || 0)));
        };
        var indentUnit = config.indentUnit;
        var statementIndent = parserConfig.statementIndent;
        var jsonldMode = parserConfig.jsonld;
        var jsonMode = parserConfig.json || jsonldMode;
        var trackScope = parserConfig.trackScope !== false;
        var isTS = parserConfig.typescript;
        var wordRE = parserConfig.wordCharacters || /[\w$\xa1-\uffff]/;
        // Tokenizer
        var keywords = function() {
            var kw = function kw(type) {
                return {
                    type: type,
                    style: "keyword"
                };
            };
            var A = kw("keyword a"), B = kw("keyword b"), C = kw("keyword c"), D = kw("keyword d");
            var operator = kw("operator"), atom = {
                type: "atom",
                style: "atom"
            };
            return {
                "if": kw("if"),
                "while": A,
                "with": A,
                "else": B,
                "do": B,
                "try": B,
                "finally": B,
                "return": D,
                "break": D,
                "continue": D,
                "new": kw("new"),
                "delete": C,
                "void": C,
                "throw": C,
                "debugger": kw("debugger"),
                "var": kw("var"),
                "const": kw("var"),
                "let": kw("var"),
                "function": kw("function"),
                "catch": kw("catch"),
                "for": kw("for"),
                "switch": kw("switch"),
                "case": kw("case"),
                "default": kw("default"),
                "in": operator,
                "typeof": operator,
                "instanceof": operator,
                "true": atom,
                "false": atom,
                "null": atom,
                "undefined": atom,
                "NaN": atom,
                "Infinity": atom,
                "this": kw("this"),
                "class": kw("class"),
                "super": kw("atom"),
                "yield": C,
                "export": kw("export"),
                "import": kw("import"),
                "extends": C,
                "await": C
            };
        }();
        var isOperatorChar = /[+\-*&%=<>!?|~^@]/;
        var isJsonldKeyword = /^@(context|id|value|language|type|container|list|set|reverse|index|base|vocab|graph)"/;
        // Used as scratch variables to communicate multiple values without
        // consing up tons of objects.
        var type, content;
        var brackets = "([{}])";
        // Parser
        var atomicTypes = {
            "atom": true,
            "number": true,
            "variable": true,
            "string": true,
            "regexp": true,
            "this": true,
            "import": true,
            "jsonld-keyword": true
        };
        // Combinator utils
        var cx = {
            state: null,
            column: null,
            marked: null,
            cc: null
        };
        function registerVarScoped(varname, context) {
            if (!context) {
                return null;
            } else if (context.block) {
                var inner = registerVarScoped(varname, context.prev);
                if (!inner) return null;
                if (inner == context.prev) return context;
                return new Context(inner, context.vars, true);
            } else if (inList(varname, context.vars)) {
                return context;
            } else {
                return new Context(context.prev, new Var(varname, context.vars), false);
            }
        }
        var defaultVars = new Var("this", new Var("arguments", null));
        popcontext.lex = true;
        poplex.lex = true;
        function statement(type, value) {
            if (type == "var") return cont(pushlex("vardef", value), vardef, expect(";"), poplex);
            if (type == "keyword a") return cont(pushlex("form"), parenExpr, statement, poplex);
            if (type == "keyword b") return cont(pushlex("form"), statement, poplex);
            if (type == "keyword d") return cx.stream.match(/^\s*$/, false) ? cont() : cont(pushlex("stat"), maybeexpression, expect(";"), poplex);
            if (type == "debugger") return cont(expect(";"));
            if (type == "{") return cont(pushlex("}"), pushblockcontext, block, poplex, popcontext);
            if (type == ";") return cont();
            if (type == "if") {
                if (cx.state.lexical.info == "else" && cx.state.cc[cx.state.cc.length - 1] == poplex) cx.state.cc.pop()();
                return cont(pushlex("form"), parenExpr, statement, poplex, maybeelse);
            }
            if (type == "function") return cont(functiondef);
            if (type == "for") return cont(pushlex("form"), pushblockcontext, forspec, statement, popcontext, poplex);
            if (type == "class" || isTS && value == "interface") {
                cx.marked = "keyword";
                return cont(pushlex("form", type == "class" ? type : value), className, poplex);
            }
            if (type == "variable") {
                if (isTS && value == "declare") {
                    cx.marked = "keyword";
                    return cont(statement);
                } else if (isTS && (value == "module" || value == "enum" || value == "type") && cx.stream.match(/^\s*\w/, false)) {
                    cx.marked = "keyword";
                    if (value == "enum") return cont(enumdef);
                    else if (value == "type") return cont(typename, expect("operator"), typeexpr, expect(";"));
                    else return cont(pushlex("form"), pattern, expect("{"), pushlex("}"), block, poplex, poplex);
                } else if (isTS && value == "namespace") {
                    cx.marked = "keyword";
                    return cont(pushlex("form"), expression, statement, poplex);
                } else if (isTS && value == "abstract") {
                    cx.marked = "keyword";
                    return cont(statement);
                } else {
                    return cont(pushlex("stat"), maybelabel);
                }
            }
            if (type == "switch") return cont(pushlex("form"), parenExpr, expect("{"), pushlex("}", "switch"), pushblockcontext, block, poplex, poplex, popcontext);
            if (type == "case") return cont(expression, expect(":"));
            if (type == "default") return cont(expect(":"));
            if (type == "catch") return cont(pushlex("form"), pushcontext, maybeCatchBinding, statement, poplex, popcontext);
            if (type == "export") return cont(pushlex("stat"), afterExport, poplex);
            if (type == "import") return cont(pushlex("stat"), afterImport, poplex);
            if (type == "async") return cont(statement);
            if (value == "@") return cont(expression, statement);
            return pass(pushlex("stat"), expression, expect(";"), poplex);
        }
        function maybeoperatorNoComma(type, value, noComma) {
            var me = noComma == false ? maybeoperatorComma : maybeoperatorNoComma;
            var expr = noComma == false ? expression : expressionNoComma;
            if (type == "=>") return cont(pushcontext, noComma ? arrowBodyNoComma : arrowBody, popcontext);
            if (type == "operator") {
                if (/\+\+|--/.test(value) || isTS && value == "!") return cont(me);
                if (isTS && value == "<" && cx.stream.match(/^([^<>]|<[^<>]*>)*>\s*\(/, false)) return cont(pushlex(">"), commasep(typeexpr, ">"), poplex, me);
                if (value == "?") return cont(expression, expect(":"), expr);
                return cont(expr);
            }
            if (type == "quasi") {
                return pass(quasi, me);
            }
            if (type == ";") return;
            if (type == "(") return contCommasep(expressionNoComma, ")", "call", me);
            if (type == ".") return cont(property, me);
            if (type == "[") return cont(pushlex("]"), maybeexpression, expect("]"), poplex, me);
            if (isTS && value == "as") {
                cx.marked = "keyword";
                return cont(typeexpr, me);
            }
            if (type == "regexp") {
                cx.state.lastType = cx.marked = "operator";
                cx.stream.backUp(cx.stream.pos - cx.stream.start - 1);
                return cont(expr);
            }
        }
        function quasi(type, value) {
            if (type != "quasi") return pass();
            if (value.slice(value.length - 2) != "${") return cont(quasi);
            return cont(maybeexpression, continueQuasi);
        }
        function objprop(type, value) {
            if (type == "async") {
                cx.marked = "property";
                return cont(objprop);
            } else if (type == "variable" || cx.style == "keyword") {
                cx.marked = "property";
                if (value == "get" || value == "set") return cont(getterSetter);
                var m // Work around fat-arrow-detection complication for detecting typescript typed arrow params
                ;
                if (isTS && cx.state.fatArrowAt == cx.stream.start && (m = cx.stream.match(/^\s*:\s*/, false))) cx.state.fatArrowAt = cx.stream.pos + m[0].length;
                return cont(afterprop);
            } else if (type == "number" || type == "string") {
                cx.marked = jsonldMode ? "property" : cx.style + " property";
                return cont(afterprop);
            } else if (type == "jsonld-keyword") {
                return cont(afterprop);
            } else if (isTS && isModifier(value)) {
                cx.marked = "keyword";
                return cont(objprop);
            } else if (type == "[") {
                return cont(expression, maybetype, expect("]"), afterprop);
            } else if (type == "spread") {
                return cont(expressionNoComma, afterprop);
            } else if (value == "*") {
                cx.marked = "keyword";
                return cont(objprop);
            } else if (type == ":") {
                return pass(afterprop);
            }
        }
        function block(type) {
            if (type == "}") return cont();
            return pass(statement, block);
        }
        function maybetype(type, value) {
            if (isTS) {
                if (type == ":") return cont(typeexpr);
                if (value == "?") return cont(maybetype);
            }
        }
        function typeexpr(type, value) {
            if (value == "keyof" || value == "typeof" || value == "infer" || value == "readonly") {
                cx.marked = "keyword";
                return cont(value == "typeof" ? expressionNoComma : typeexpr);
            }
            if (type == "variable" || value == "void") {
                cx.marked = "type";
                return cont(afterType);
            }
            if (value == "|" || value == "&") return cont(typeexpr);
            if (type == "string" || type == "number" || type == "atom") return cont(afterType);
            if (type == "[") return cont(pushlex("]"), commasep(typeexpr, "]", ","), poplex, afterType);
            if (type == "{") return cont(pushlex("}"), typeprops, poplex, afterType);
            if (type == "(") return cont(commasep(typearg, ")"), maybeReturnType, afterType);
            if (type == "<") return cont(commasep(typeexpr, ">"), typeexpr);
            if (type == "quasi") {
                return pass(quasiType, afterType);
            }
        }
        function typeprops(type) {
            if (type.match(/[\}\)\]]/)) return cont();
            if (type == "," || type == ";") return cont(typeprops);
            return pass(typeprop, typeprops);
        }
        function typeprop(type, value) {
            if (type == "variable" || cx.style == "keyword") {
                cx.marked = "property";
                return cont(typeprop);
            } else if (value == "?" || type == "number" || type == "string") {
                return cont(typeprop);
            } else if (type == ":") {
                return cont(typeexpr);
            } else if (type == "[") {
                return cont(expect("variable"), maybetypeOrIn, expect("]"), typeprop);
            } else if (type == "(") {
                return pass(functiondecl, typeprop);
            } else if (!type.match(/[;\}\)\],]/)) {
                return cont();
            }
        }
        function quasiType(type, value) {
            if (type != "quasi") return pass();
            if (value.slice(value.length - 2) != "${") return cont(quasiType);
            return cont(typeexpr, continueQuasiType);
        }
        function typearg(type, value) {
            if (type == "variable" && cx.stream.match(/^\s*[?:]/, false) || value == "?") return cont(typearg);
            if (type == ":") return cont(typeexpr);
            if (type == "spread") return cont(typearg);
            return pass(typeexpr);
        }
        function afterType(type, value) {
            if (value == "<") return cont(pushlex(">"), commasep(typeexpr, ">"), poplex, afterType);
            if (value == "|" || type == "." || value == "&") return cont(typeexpr);
            if (type == "[") return cont(typeexpr, expect("]"), afterType);
            if (value == "extends" || value == "implements") {
                cx.marked = "keyword";
                return cont(typeexpr);
            }
            if (value == "?") return cont(typeexpr, expect(":"), typeexpr);
        }
        function pattern(type, value) {
            if (isTS && isModifier(value)) {
                cx.marked = "keyword";
                return cont(pattern);
            }
            if (type == "variable") {
                register(value);
                return cont();
            }
            if (type == "spread") return cont(pattern);
            if (type == "[") return contCommasep(eltpattern, "]");
            if (type == "{") return contCommasep(proppattern, "}");
        }
        function proppattern(type, value) {
            if (type == "variable" && !cx.stream.match(/^\s*:/, false)) {
                register(value);
                return cont(maybeAssign);
            }
            if (type == "variable") cx.marked = "property";
            if (type == "spread") return cont(pattern);
            if (type == "}") return pass();
            if (type == "[") return cont(expression, expect("]"), expect(":"), proppattern);
            return cont(expect(":"), pattern, maybeAssign);
        }
        function forspec(type, value) {
            if (value == "await") return cont(forspec);
            if (type == "(") return cont(pushlex(")"), forspec1, poplex);
        }
        function forspec2(type, value) {
            if (type == ")") return cont();
            if (type == ";") return cont(forspec2);
            if (value == "in" || value == "of") {
                cx.marked = "keyword";
                return cont(expression, forspec2);
            }
            return pass(expression, forspec2);
        }
        function functiondef(type, value) {
            if (value == "*") {
                cx.marked = "keyword";
                return cont(functiondef);
            }
            if (type == "variable") {
                register(value);
                return cont(functiondef);
            }
            if (type == "(") return cont(pushcontext, pushlex(")"), commasep(funarg, ")"), poplex, mayberettype, statement, popcontext);
            if (isTS && value == "<") return cont(pushlex(">"), commasep(typeparam, ">"), poplex, functiondef);
        }
        function functiondecl(type, value) {
            if (value == "*") {
                cx.marked = "keyword";
                return cont(functiondecl);
            }
            if (type == "variable") {
                register(value);
                return cont(functiondecl);
            }
            if (type == "(") return cont(pushcontext, pushlex(")"), commasep(funarg, ")"), poplex, mayberettype, popcontext);
            if (isTS && value == "<") return cont(pushlex(">"), commasep(typeparam, ">"), poplex, functiondecl);
        }
        function typename(type, value) {
            if (type == "keyword" || type == "variable") {
                cx.marked = "type";
                return cont(typename);
            } else if (value == "<") {
                return cont(pushlex(">"), commasep(typeparam, ">"), poplex);
            }
        }
        function funarg(type, value) {
            if (value == "@") cont(expression, funarg);
            if (type == "spread") return cont(funarg);
            if (isTS && isModifier(value)) {
                cx.marked = "keyword";
                return cont(funarg);
            }
            if (isTS && type == "this") return cont(maybetype, maybeAssign);
            return pass(pattern, maybetype, maybeAssign);
        }
        function classNameAfter(type, value) {
            if (value == "<") return cont(pushlex(">"), commasep(typeparam, ">"), poplex, classNameAfter);
            if (value == "extends" || value == "implements" || isTS && type == ",") {
                if (value == "implements") cx.marked = "keyword";
                return cont(isTS ? typeexpr : expression, classNameAfter);
            }
            if (type == "{") return cont(pushlex("}"), classBody, poplex);
        }
        function classBody(type, value) {
            if (type == "async" || type == "variable" && (value == "static" || value == "get" || value == "set" || isTS && isModifier(value)) && cx.stream.match(/^\s+[\w$\xa1-\uffff]/, false)) {
                cx.marked = "keyword";
                return cont(classBody);
            }
            if (type == "variable" || cx.style == "keyword") {
                cx.marked = "property";
                return cont(classfield, classBody);
            }
            if (type == "number" || type == "string") return cont(classfield, classBody);
            if (type == "[") return cont(expression, maybetype, expect("]"), classfield, classBody);
            if (value == "*") {
                cx.marked = "keyword";
                return cont(classBody);
            }
            if (isTS && type == "(") return pass(functiondecl, classBody);
            if (type == ";" || type == ",") return cont(classBody);
            if (type == "}") return cont();
            if (value == "@") return cont(expression, classBody);
        }
        function classfield(type, value) {
            if (value == "!") return cont(classfield);
            if (value == "?") return cont(classfield);
            if (type == ":") return cont(typeexpr, maybeAssign);
            if (value == "=") return cont(expressionNoComma);
            var context = cx.state.lexical.prev, isInterface = context && context.info == "interface";
            return pass(isInterface ? functiondecl : functiondef);
        }
        function exportField(type, value) {
            if (value == "as") {
                cx.marked = "keyword";
                return cont(expect("variable"));
            }
            if (type == "variable") return pass(expressionNoComma, exportField);
        }
        function importSpec(type, value) {
            if (type == "{") return contCommasep(importSpec, "}");
            if (type == "variable") register(value);
            if (value == "*") cx.marked = "keyword";
            return cont(maybeAs);
        }
        function maybeMoreImports(type) {
            if (type == ",") return cont(importSpec, maybeMoreImports);
        }
        // Interface
        return {
            startState: function startState(basecolumn) {
                var state = {
                    tokenize: tokenBase,
                    lastType: "sof",
                    cc: [],
                    lexical: new JSLexical((basecolumn || 0) - indentUnit, 0, "block", false),
                    localVars: parserConfig.localVars,
                    context: parserConfig.localVars && new Context(null, null, false),
                    indented: basecolumn || 0
                };
                if (parserConfig.globalVars && typeof parserConfig.globalVars == "object") state.globalVars = parserConfig.globalVars;
                return state;
            },
            token: function token(stream, state) {
                if (stream.sol()) {
                    if (!state.lexical.hasOwnProperty("align")) state.lexical.align = false;
                    state.indented = stream.indentation();
                    findFatArrow(stream, state);
                }
                if (state.tokenize != tokenComment && stream.eatSpace()) return null;
                var style = state.tokenize(stream, state);
                if (type == "comment") return style;
                state.lastType = type == "operator" && (content == "++" || content == "--") ? "incdec" : type;
                return parseJS(state, style, type, content, stream);
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize == tokenComment || state.tokenize == tokenQuasi) return CodeMirror1.Pass;
                if (state.tokenize != tokenBase) return 0;
                var firstChar = textAfter && textAfter.charAt(0), lexical = state.lexical, top;
                // Kludge to prevent 'maybelse' from blocking lexical scope pops
                if (!/^\s*else\b/.test(textAfter)) for(var i = state.cc.length - 1; i >= 0; --i){
                    var c = state.cc[i];
                    if (c == poplex) lexical = lexical.prev;
                    else if (c != maybeelse && c != popcontext) break;
                }
                while((lexical.type == "stat" || lexical.type == "form") && (firstChar == "}" || (top = state.cc[state.cc.length - 1]) && (top == maybeoperatorComma || top == maybeoperatorNoComma) && !/^[,\.=+\-*:?[\(]/.test(textAfter)))lexical = lexical.prev;
                if (statementIndent && lexical.type == ")" && lexical.prev.type == "stat") lexical = lexical.prev;
                var type = lexical.type, closing = firstChar == type;
                if (type == "vardef") return lexical.indented + (state.lastType == "operator" || state.lastType == "," ? lexical.info.length + 1 : 0);
                else if (type == "form" && firstChar == "{") return lexical.indented;
                else if (type == "form") return lexical.indented + indentUnit;
                else if (type == "stat") return lexical.indented + (isContinuedStatement(state, textAfter) ? statementIndent || indentUnit : 0);
                else if (lexical.info == "switch" && !closing && parserConfig.doubleIndentSwitch != false) return lexical.indented + (/^(?:case|default)\b/.test(textAfter) ? indentUnit : 2 * indentUnit);
                else if (lexical.align) return lexical.column + (closing ? 0 : 1);
                else return lexical.indented + (closing ? 0 : indentUnit);
            },
            electricInput: /^\s*(?:case .*?:|default:|\{|\})$/,
            blockCommentStart: jsonMode ? null : "/*",
            blockCommentEnd: jsonMode ? null : "*/",
            blockCommentContinue: jsonMode ? null : " * ",
            lineComment: jsonMode ? null : "//",
            fold: "brace",
            closeBrackets: "()[]{}''\"\"``",
            helperType: jsonMode ? "json" : "javascript",
            jsonldMode: jsonldMode,
            jsonMode: jsonMode,
            expressionAllowed: expressionAllowed,
            skipExpression: function skipExpression(state) {
                parseJS(state, "atom", "atom", "true", new CodeMirror1.StringStream("", 2, null));
            }
        };
    });
    CodeMirror1.registerHelper("wordChars", "javascript", /[\w$]/);
    CodeMirror1.defineMIME("text/javascript", "javascript");
    CodeMirror1.defineMIME("text/ecmascript", "javascript");
    CodeMirror1.defineMIME("application/javascript", "javascript");
    CodeMirror1.defineMIME("application/x-javascript", "javascript");
    CodeMirror1.defineMIME("application/ecmascript", "javascript");
    CodeMirror1.defineMIME("application/json", {
        name: "javascript",
        json: true
    });
    CodeMirror1.defineMIME("application/x-json", {
        name: "javascript",
        json: true
    });
    CodeMirror1.defineMIME("application/manifest+json", {
        name: "javascript",
        json: true
    });
    CodeMirror1.defineMIME("application/ld+json", {
        name: "javascript",
        jsonld: true
    });
    CodeMirror1.defineMIME("text/typescript", {
        name: "javascript",
        typescript: true
    });
    CodeMirror1.defineMIME("application/typescript", {
        name: "javascript",
        typescript: true
    });
});
});

var htmlmixed = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, xml, javascript, css);
})(function(CodeMirror1) {
    var maybeBackup = function maybeBackup(stream, pat, style) {
        var cur = stream.current(), close = cur.search(pat);
        if (close > -1) {
            stream.backUp(cur.length - close);
        } else if (cur.match(/<\/?$/)) {
            stream.backUp(cur.length);
            if (!stream.match(pat, false)) stream.match(cur);
        }
        return style;
    };
    var getAttrRegexp = function getAttrRegexp(attr) {
        var regexp = attrRegexpCache[attr];
        if (regexp) return regexp;
        return attrRegexpCache[attr] = new RegExp("\\s+" + attr + "\\s*=\\s*('|\")?([^'\"]+)('|\")?\\s*");
    };
    var getAttrValue = function getAttrValue(text, attr) {
        var match = text.match(getAttrRegexp(attr));
        return match ? /^\s*(.*?)\s*$/.exec(match[2])[1] : "";
    };
    var getTagRegexp = function getTagRegexp(tagName, anchored) {
        return new RegExp((anchored ? "^" : "") + "</s*" + tagName + "s*>", "i");
    };
    var addTags = function addTags(from, to) {
        for(var tag in from){
            var dest = to[tag] || (to[tag] = []);
            var source = from[tag];
            for(var i = source.length - 1; i >= 0; i--)dest.unshift(source[i]);
        }
    };
    var findMatchingMode = function findMatchingMode(tagInfo, tagText) {
        for(var i = 0; i < tagInfo.length; i++){
            var spec = tagInfo[i];
            if (!spec[0] || spec[1].test(getAttrValue(tagText, spec[0]))) return spec[2];
        }
    };
    var defaultTags = {
        script: [
            [
                "lang",
                /(javascript|babel)/i,
                "javascript"
            ],
            [
                "type",
                /^(?:text|application)\/(?:x-)?(?:java|ecma)script$|^module$|^$/i,
                "javascript"
            ],
            [
                "type",
                /./,
                "text/plain"
            ],
            [
                null,
                null,
                "javascript"
            ]
        ],
        style: [
            [
                "lang",
                /^css$/i,
                "css"
            ],
            [
                "type",
                /^(text\/)?(x-)?(stylesheet|css)$/i,
                "css"
            ],
            [
                "type",
                /./,
                "text/plain"
            ],
            [
                null,
                null,
                "css"
            ]
        ]
    };
    var attrRegexpCache = {};
    CodeMirror1.defineMode("htmlmixed", function(config, parserConfig) {
        var htmlMode = CodeMirror1.getMode(config, {
            name: "xml",
            htmlMode: true,
            multilineTagIndentFactor: parserConfig.multilineTagIndentFactor,
            multilineTagIndentPastTag: parserConfig.multilineTagIndentPastTag,
            allowMissingTagName: parserConfig.allowMissingTagName
        });
        var tags = {};
        var configTags = parserConfig && parserConfig.tags, configScript = parserConfig && parserConfig.scriptTypes;
        addTags(defaultTags, tags);
        if (configTags) addTags(configTags, tags);
        if (configScript) for(var i = configScript.length - 1; i >= 0; i--)tags.script.unshift([
            "type",
            configScript[i].matches,
            configScript[i].mode
        ]);
        function html(stream, state) {
            var style = htmlMode.token(stream, state.htmlState), tag = /\btag\b/.test(style), tagName;
            if (tag && !/[<>\s\/]/.test(stream.current()) && (tagName = state.htmlState.tagName && state.htmlState.tagName.toLowerCase()) && tags.hasOwnProperty(tagName)) {
                state.inTag = tagName + " ";
            } else if (state.inTag && tag && />$/.test(stream.current())) {
                var inTag = /^([\S]+) (.*)/.exec(state.inTag);
                state.inTag = null;
                var modeSpec = stream.current() == ">" && findMatchingMode(tags[inTag[1]], inTag[2]);
                var mode = CodeMirror1.getMode(config, modeSpec);
                var endTagA = getTagRegexp(inTag[1], true), endTag = getTagRegexp(inTag[1], false);
                state.token = function(stream, state) {
                    if (stream.match(endTagA, false)) {
                        state.token = html;
                        state.localState = state.localMode = null;
                        return null;
                    }
                    return maybeBackup(stream, endTag, state.localMode.token(stream, state.localState));
                };
                state.localMode = mode;
                state.localState = CodeMirror1.startState(mode, htmlMode.indent(state.htmlState, "", ""));
            } else if (state.inTag) {
                state.inTag += stream.current();
                if (stream.eol()) state.inTag += " ";
            }
            return style;
        }
        return {
            startState: function startState() {
                var state = CodeMirror1.startState(htmlMode);
                return {
                    token: html,
                    inTag: null,
                    localMode: null,
                    localState: null,
                    htmlState: state
                };
            },
            copyState: function copyState(state) {
                var local;
                if (state.localState) {
                    local = CodeMirror1.copyState(state.localMode, state.localState);
                }
                return {
                    token: state.token,
                    inTag: state.inTag,
                    localMode: state.localMode,
                    localState: local,
                    htmlState: CodeMirror1.copyState(htmlMode, state.htmlState)
                };
            },
            token: function token(stream, state) {
                return state.token(stream, state);
            },
            indent: function indent(state, textAfter, line) {
                if (!state.localMode || /^\s*<\//.test(textAfter)) return htmlMode.indent(state.htmlState, textAfter, line);
                else if (state.localMode.indent) return state.localMode.indent(state.localState, textAfter, line);
                else return CodeMirror1.Pass;
            },
            innerMode: function innerMode(state) {
                return {
                    state: state.localState || state.htmlState,
                    mode: state.localMode || htmlMode
                };
            }
        };
    }, "xml", "javascript", "css");
    CodeMirror1.defineMIME("text/html", "htmlmixed");
});
});

var http = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("http", function() {
        var failFirstLine = function failFirstLine(stream, state) {
            stream.skipToEnd();
            state.cur = header;
            return "error";
        };
        var start = function start(stream, state) {
            if (stream.match(/^HTTP\/\d\.\d/)) {
                state.cur = responseStatusCode;
                return "keyword";
            } else if (stream.match(/^[A-Z]+/) && /[ \t]/.test(stream.peek())) {
                state.cur = requestPath;
                return "keyword";
            } else {
                return failFirstLine(stream, state);
            }
        };
        var responseStatusCode = function responseStatusCode(stream, state) {
            var code = stream.match(/^\d+/);
            if (!code) return failFirstLine(stream, state);
            state.cur = responseStatusText;
            var status = Number(code[0]);
            if (status >= 100 && status < 200) {
                return "positive informational";
            } else if (status >= 200 && status < 300) {
                return "positive success";
            } else if (status >= 300 && status < 400) {
                return "positive redirect";
            } else if (status >= 400 && status < 500) {
                return "negative client-error";
            } else if (status >= 500 && status < 600) {
                return "negative server-error";
            } else {
                return "error";
            }
        };
        var responseStatusText = function responseStatusText(stream, state) {
            stream.skipToEnd();
            state.cur = header;
            return null;
        };
        var requestPath = function requestPath(stream, state) {
            stream.eatWhile(/\S/);
            state.cur = requestProtocol;
            return "string-2";
        };
        var requestProtocol = function requestProtocol(stream, state) {
            if (stream.match(/^HTTP\/\d\.\d$/)) {
                state.cur = header;
                return "keyword";
            } else {
                return failFirstLine(stream, state);
            }
        };
        var header = function header(stream) {
            if (stream.sol() && !stream.eat(/[ \t]/)) {
                if (stream.match(/^.*?:/)) {
                    return "atom";
                } else {
                    stream.skipToEnd();
                    return "error";
                }
            } else {
                stream.skipToEnd();
                return "string";
            }
        };
        var body = function body(stream) {
            stream.skipToEnd();
            return null;
        };
        return {
            token: function token(stream, state) {
                var cur = state.cur;
                if (cur != header && cur != body && stream.eatSpace()) return null;
                return cur(stream, state);
            },
            blankLine: function blankLine(state) {
                state.cur = body;
            },
            startState: function startState() {
                return {
                    cur: start
                };
            }
        };
    });
    CodeMirror1.defineMIME("message/http", "http");
});
});

var jsx = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, xml, javascript);
})(function(CodeMirror1) {
    var Context = // Depth means the amount of open braces in JS context, in XML
    // context 0 means not in tag, 1 means in tag, and 2 means in tag
    // and js block comment.
    function Context(state, mode, depth, prev) {
        this.state = state;
        this.mode = mode;
        this.depth = depth;
        this.prev = prev;
    };
    function copyContext(context) {
        return new Context(CodeMirror1.copyState(context.mode, context.state), context.mode, context.depth, context.prev && copyContext(context.prev));
    }
    CodeMirror1.defineMode("jsx", function(config, modeConfig) {
        var flatXMLIndent = function flatXMLIndent(state) {
            var tagName = state.tagName;
            state.tagName = null;
            var result = xmlMode.indent(state, "", "");
            state.tagName = tagName;
            return result;
        };
        var token = function token(stream, state) {
            if (state.context.mode == xmlMode) return xmlToken(stream, state, state.context);
            else return jsToken(stream, state, state.context);
        };
        var xmlToken = function xmlToken(stream, state, cx) {
            if (cx.depth == 2) {
                if (stream.match(/^.*?\*\//)) cx.depth = 1;
                else stream.skipToEnd();
                return "comment";
            }
            if (stream.peek() == "{") {
                xmlMode.skipAttribute(cx.state);
                var indent = flatXMLIndent(cx.state), xmlContext = cx.state.context;
                // If JS starts on same line as tag
                if (xmlContext && stream.match(/^[^>]*>\s*$/, false)) {
                    while(xmlContext.prev && !xmlContext.startOfLine)xmlContext = xmlContext.prev;
                    // If tag starts the line, use XML indentation level
                    if (xmlContext.startOfLine) indent -= config.indentUnit;
                    else if (cx.prev.state.lexical) indent = cx.prev.state.lexical.indented;
                // Else if inside of tag
                } else if (cx.depth == 1) {
                    indent += config.indentUnit;
                }
                state.context = new Context(CodeMirror1.startState(jsMode, indent), jsMode, 0, state.context);
                return null;
            }
            if (cx.depth == 1) {
                if (stream.peek() == "<") {
                    xmlMode.skipAttribute(cx.state);
                    state.context = new Context(CodeMirror1.startState(xmlMode, flatXMLIndent(cx.state)), xmlMode, 0, state.context);
                    return null;
                } else if (stream.match("//")) {
                    stream.skipToEnd();
                    return "comment";
                } else if (stream.match("/*")) {
                    cx.depth = 2;
                    return token(stream, state);
                }
            }
            var style = xmlMode.token(stream, cx.state), cur = stream.current(), stop;
            if (/\btag\b/.test(style)) {
                if (/>$/.test(cur)) {
                    if (cx.state.context) cx.depth = 0;
                    else state.context = state.context.prev;
                } else if (/^</.test(cur)) {
                    cx.depth = 1;
                }
            } else if (!style && (stop = cur.indexOf("{")) > -1) {
                stream.backUp(cur.length - stop);
            }
            return style;
        };
        var jsToken = function jsToken(stream, state, cx) {
            if (stream.peek() == "<" && jsMode.expressionAllowed(stream, cx.state)) {
                state.context = new Context(CodeMirror1.startState(xmlMode, jsMode.indent(cx.state, "", "")), xmlMode, 0, state.context);
                jsMode.skipExpression(cx.state);
                return null;
            }
            var style = jsMode.token(stream, cx.state);
            if (!style && cx.depth != null) {
                var cur = stream.current();
                if (cur == "{") {
                    cx.depth++;
                } else if (cur == "}") {
                    if (--cx.depth == 0) state.context = state.context.prev;
                }
            }
            return style;
        };
        var xmlMode = CodeMirror1.getMode(config, {
            name: "xml",
            allowMissing: true,
            multilineTagIndentPastTag: false,
            allowMissingTagName: true
        });
        var jsMode = CodeMirror1.getMode(config, modeConfig && modeConfig.base || "javascript");
        return {
            startState: function startState() {
                return {
                    context: new Context(CodeMirror1.startState(jsMode), jsMode)
                };
            },
            copyState: function copyState(state) {
                return {
                    context: copyContext(state.context)
                };
            },
            token: token,
            indent: function indent(state, textAfter, fullLine) {
                return state.context.mode.indent(state.context.state, textAfter, fullLine);
            },
            innerMode: function innerMode(state) {
                return state.context;
            }
        };
    }, "xml", "javascript");
    CodeMirror1.defineMIME("text/jsx", "jsx");
    CodeMirror1.defineMIME("text/typescript-jsx", {
        name: "jsx",
        base: {
            name: "javascript",
            typescript: true
        }
    });
});
});

var stex = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
/*
 * Author: Constantin Jucovschi (c.jucovschi@jacobs-university.de)
 * Licence: MIT
 */ (function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("stex", function(_config, parserConfig) {
        var pushCommand = function pushCommand(state, command) {
            state.cmdState.push(command);
        };
        var peekCommand = function peekCommand(state) {
            if (state.cmdState.length > 0) {
                return state.cmdState[state.cmdState.length - 1];
            } else {
                return null;
            }
        };
        var popCommand = function popCommand(state) {
            var plug = state.cmdState.pop();
            if (plug) {
                plug.closeBracket();
            }
        };
        var getMostPowerful = // returns the non-default plugin closest to the end of the list
        function getMostPowerful(state) {
            var context = state.cmdState;
            for(var i = context.length - 1; i >= 0; i--){
                var plug = context[i];
                if (plug.name == "DEFAULT") {
                    continue;
                }
                return plug;
            }
            return {
                styleIdentifier: function styleIdentifier() {
                    return null;
                }
            };
        };
        var addPluginPattern = function addPluginPattern(pluginName, cmdStyle, styles) {
            return function() {
                this.name = pluginName;
                this.bracketNo = 0;
                this.style = cmdStyle;
                this.styles = styles;
                this.argument = null; // \begin and \end have arguments that follow. These are stored in the plugin
                this.styleIdentifier = function() {
                    return this.styles[this.bracketNo - 1] || null;
                };
                this.openBracket = function() {
                    this.bracketNo++;
                    return "bracket";
                };
                this.closeBracket = function() {};
            };
        };
        var setState = function setState(state, f) {
            state.f = f;
        };
        var normal = // called when in a normal (no environment) context
        function normal(source, state) {
            var plug;
            // Do we look like '\command' ?  If so, attempt to apply the plugin 'command'
            if (source.match(/^\\[a-zA-Z@]+/)) {
                var cmdName = source.current().slice(1);
                plug = plugins.hasOwnProperty(cmdName) ? plugins[cmdName] : plugins["DEFAULT"];
                plug = new plug();
                pushCommand(state, plug);
                setState(state, beginParams);
                return plug.style;
            }
            // escape characters
            if (source.match(/^\\[$&%#{}_]/)) {
                return "tag";
            }
            // white space control characters
            if (source.match(/^\\[,;!\/\\]/)) {
                return "tag";
            }
            // find if we're starting various math modes
            if (source.match("\\[")) {
                setState(state, function(source, state) {
                    return inMathMode(source, state, "\\]");
                });
                return "keyword";
            }
            if (source.match("\\(")) {
                setState(state, function(source, state) {
                    return inMathMode(source, state, "\\)");
                });
                return "keyword";
            }
            if (source.match("$$")) {
                setState(state, function(source, state) {
                    return inMathMode(source, state, "$$");
                });
                return "keyword";
            }
            if (source.match("$")) {
                setState(state, function(source, state) {
                    return inMathMode(source, state, "$");
                });
                return "keyword";
            }
            var ch = source.next();
            if (ch == "%") {
                source.skipToEnd();
                return "comment";
            } else if (ch == "}" || ch == "]") {
                plug = peekCommand(state);
                if (plug) {
                    plug.closeBracket(ch);
                    setState(state, beginParams);
                } else {
                    return "error";
                }
                return "bracket";
            } else if (ch == "{" || ch == "[") {
                plug = plugins["DEFAULT"];
                plug = new plug();
                pushCommand(state, plug);
                return "bracket";
            } else if (/\d/.test(ch)) {
                source.eatWhile(/[\w.%]/);
                return "atom";
            } else {
                source.eatWhile(/[\w\-_]/);
                plug = getMostPowerful(state);
                if (plug.name == "begin") {
                    plug.argument = source.current();
                }
                return plug.styleIdentifier();
            }
        };
        var inMathMode = function inMathMode(source, state, endModeSeq) {
            if (source.eatSpace()) {
                return null;
            }
            if (endModeSeq && source.match(endModeSeq)) {
                setState(state, normal);
                return "keyword";
            }
            if (source.match(/^\\[a-zA-Z@]+/)) {
                return "tag";
            }
            if (source.match(/^[a-zA-Z]+/)) {
                return "variable-2";
            }
            // escape characters
            if (source.match(/^\\[$&%#{}_]/)) {
                return "tag";
            }
            // white space control characters
            if (source.match(/^\\[,;!\/]/)) {
                return "tag";
            }
            // special math-mode characters
            if (source.match(/^[\^_&]/)) {
                return "tag";
            }
            // non-special characters
            if (source.match(/^[+\-<>|=,\/@!*:;'"`~#?]/)) {
                return null;
            }
            if (source.match(/^(\d+\.\d*|\d*\.\d+|\d+)/)) {
                return "number";
            }
            var ch = source.next();
            if (ch == "{" || ch == "}" || ch == "[" || ch == "]" || ch == "(" || ch == ")") {
                return "bracket";
            }
            if (ch == "%") {
                source.skipToEnd();
                return "comment";
            }
            return "error";
        };
        var beginParams = function beginParams(source, state) {
            var ch = source.peek(), lastPlug;
            if (ch == "{" || ch == "[") {
                lastPlug = peekCommand(state);
                lastPlug.openBracket(ch);
                source.eat(ch);
                setState(state, normal);
                return "bracket";
            }
            if (/[ \t\r]/.test(ch)) {
                source.eat(ch);
                return null;
            }
            setState(state, normal);
            popCommand(state);
            return normal(source, state);
        };
        var plugins = {};
        plugins["importmodule"] = addPluginPattern("importmodule", "tag", [
            "string",
            "builtin"
        ]);
        plugins["documentclass"] = addPluginPattern("documentclass", "tag", [
            "",
            "atom"
        ]);
        plugins["usepackage"] = addPluginPattern("usepackage", "tag", [
            "atom"
        ]);
        plugins["begin"] = addPluginPattern("begin", "tag", [
            "atom"
        ]);
        plugins["end"] = addPluginPattern("end", "tag", [
            "atom"
        ]);
        plugins["label"] = addPluginPattern("label", "tag", [
            "atom"
        ]);
        plugins["ref"] = addPluginPattern("ref", "tag", [
            "atom"
        ]);
        plugins["eqref"] = addPluginPattern("eqref", "tag", [
            "atom"
        ]);
        plugins["cite"] = addPluginPattern("cite", "tag", [
            "atom"
        ]);
        plugins["bibitem"] = addPluginPattern("bibitem", "tag", [
            "atom"
        ]);
        plugins["Bibitem"] = addPluginPattern("Bibitem", "tag", [
            "atom"
        ]);
        plugins["RBibitem"] = addPluginPattern("RBibitem", "tag", [
            "atom"
        ]);
        plugins["DEFAULT"] = function() {
            this.name = "DEFAULT";
            this.style = "tag";
            this.styleIdentifier = this.openBracket = this.closeBracket = function() {};
        };
        return {
            startState: function startState() {
                var f = parserConfig.inMathMode ? function f(source, state) {
                    return inMathMode(source, state);
                } : normal;
                return {
                    cmdState: [],
                    f: f
                };
            },
            copyState: function copyState(s) {
                return {
                    cmdState: s.cmdState.slice(),
                    f: s.f
                };
            },
            token: function token(stream, state) {
                return state.f(stream, state);
            },
            blankLine: function blankLine(state) {
                state.f = normal;
                state.cmdState.length = 0;
            },
            lineComment: "%"
        };
    });
    CodeMirror1.defineMIME("text/x-stex", "stex");
    CodeMirror1.defineMIME("text/x-latex", "stex");
});
});

var lua = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// LUA mode. Ported to CodeMirror 2 from Franciszek Wawrzak's
// CodeMirror 1 mode.
// highlights keywords, strings, comments (no leveling supported! ("[==[")), tokens, basic indenting
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("lua", function(config, parserConfig) {
        var prefixRE = function prefixRE(words) {
            return new RegExp("^(?:" + words.join("|") + ")", "i");
        };
        var wordRE = function wordRE(words) {
            return new RegExp("^(?:" + words.join("|") + ")$", "i");
        };
        var readBracket = function readBracket(stream) {
            var level = 0;
            while(stream.eat("="))++level;
            stream.eat("[");
            return level;
        };
        var normal = function normal(stream, state) {
            var ch = stream.next();
            if (ch == "-" && stream.eat("-")) {
                if (stream.eat("[") && stream.eat("[")) return (state.cur = bracketed(readBracket(stream), "comment"))(stream, state);
                stream.skipToEnd();
                return "comment";
            }
            if (ch == '"' || ch == "'") return (state.cur = string(ch))(stream, state);
            if (ch == "[" && /[\[=]/.test(stream.peek())) return (state.cur = bracketed(readBracket(stream), "string"))(stream, state);
            if (/\d/.test(ch)) {
                stream.eatWhile(/[\w.%]/);
                return "number";
            }
            if (/[\w_]/.test(ch)) {
                stream.eatWhile(/[\w\\\-_.]/);
                return "variable";
            }
            return null;
        };
        var bracketed = function bracketed(level, style) {
            return function(stream, state) {
                var curlev = null, ch;
                while((ch = stream.next()) != null){
                    if (curlev == null) {
                        if (ch == "]") curlev = 0;
                    } else if (ch == "=") ++curlev;
                    else if (ch == "]" && curlev == level) {
                        state.cur = normal;
                        break;
                    } else curlev = null;
                }
                return style;
            };
        };
        var string = function string(quote) {
            return function(stream, state) {
                var escaped = false, ch;
                while((ch = stream.next()) != null){
                    if (ch == quote && !escaped) break;
                    escaped = !escaped && ch == "\\";
                }
                if (!escaped) state.cur = normal;
                return "string";
            };
        };
        var indentUnit = config.indentUnit;
        var specials = wordRE(parserConfig.specials || []);
        // long list of standard functions from lua manual
        var builtins = wordRE([
            "_G",
            "_VERSION",
            "assert",
            "collectgarbage",
            "dofile",
            "error",
            "getfenv",
            "getmetatable",
            "ipairs",
            "load",
            "loadfile",
            "loadstring",
            "module",
            "next",
            "pairs",
            "pcall",
            "print",
            "rawequal",
            "rawget",
            "rawset",
            "require",
            "select",
            "setfenv",
            "setmetatable",
            "tonumber",
            "tostring",
            "type",
            "unpack",
            "xpcall",
            "coroutine.create",
            "coroutine.resume",
            "coroutine.running",
            "coroutine.status",
            "coroutine.wrap",
            "coroutine.yield",
            "debug.debug",
            "debug.getfenv",
            "debug.gethook",
            "debug.getinfo",
            "debug.getlocal",
            "debug.getmetatable",
            "debug.getregistry",
            "debug.getupvalue",
            "debug.setfenv",
            "debug.sethook",
            "debug.setlocal",
            "debug.setmetatable",
            "debug.setupvalue",
            "debug.traceback",
            "close",
            "flush",
            "lines",
            "read",
            "seek",
            "setvbuf",
            "write",
            "io.close",
            "io.flush",
            "io.input",
            "io.lines",
            "io.open",
            "io.output",
            "io.popen",
            "io.read",
            "io.stderr",
            "io.stdin",
            "io.stdout",
            "io.tmpfile",
            "io.type",
            "io.write",
            "math.abs",
            "math.acos",
            "math.asin",
            "math.atan",
            "math.atan2",
            "math.ceil",
            "math.cos",
            "math.cosh",
            "math.deg",
            "math.exp",
            "math.floor",
            "math.fmod",
            "math.frexp",
            "math.huge",
            "math.ldexp",
            "math.log",
            "math.log10",
            "math.max",
            "math.min",
            "math.modf",
            "math.pi",
            "math.pow",
            "math.rad",
            "math.random",
            "math.randomseed",
            "math.sin",
            "math.sinh",
            "math.sqrt",
            "math.tan",
            "math.tanh",
            "os.clock",
            "os.date",
            "os.difftime",
            "os.execute",
            "os.exit",
            "os.getenv",
            "os.remove",
            "os.rename",
            "os.setlocale",
            "os.time",
            "os.tmpname",
            "package.cpath",
            "package.loaded",
            "package.loaders",
            "package.loadlib",
            "package.path",
            "package.preload",
            "package.seeall",
            "string.byte",
            "string.char",
            "string.dump",
            "string.find",
            "string.format",
            "string.gmatch",
            "string.gsub",
            "string.len",
            "string.lower",
            "string.match",
            "string.rep",
            "string.reverse",
            "string.sub",
            "string.upper",
            "table.concat",
            "table.insert",
            "table.maxn",
            "table.remove",
            "table.sort"
        ]);
        var keywords = wordRE([
            "and",
            "break",
            "elseif",
            "false",
            "nil",
            "not",
            "or",
            "return",
            "true",
            "function",
            "end",
            "if",
            "then",
            "else",
            "do",
            "while",
            "repeat",
            "until",
            "for",
            "in",
            "local"
        ]);
        var indentTokens = wordRE([
            "function",
            "if",
            "repeat",
            "do",
            "\\(",
            "{"
        ]);
        var dedentTokens = wordRE([
            "end",
            "until",
            "\\)",
            "}"
        ]);
        var dedentPartial = prefixRE([
            "end",
            "until",
            "\\)",
            "}",
            "else",
            "elseif"
        ]);
        return {
            startState: function startState(basecol) {
                return {
                    basecol: basecol || 0,
                    indentDepth: 0,
                    cur: normal
                };
            },
            token: function token(stream, state) {
                if (stream.eatSpace()) return null;
                var style = state.cur(stream, state);
                var word = stream.current();
                if (style == "variable") {
                    if (keywords.test(word)) style = "keyword";
                    else if (builtins.test(word)) style = "builtin";
                    else if (specials.test(word)) style = "variable-2";
                }
                if (style != "comment" && style != "string") {
                    if (indentTokens.test(word)) ++state.indentDepth;
                    else if (dedentTokens.test(word)) --state.indentDepth;
                }
                return style;
            },
            indent: function indent(state, textAfter) {
                var closing = dedentPartial.test(textAfter);
                return state.basecol + indentUnit * (state.indentDepth - (closing ? 1 : 0));
            },
            electricInput: /^\s*(?:end|until|else|\)|\})$/,
            lineComment: "--",
            blockCommentStart: "--[[",
            blockCommentEnd: "]]"
        };
    });
    CodeMirror1.defineMIME("text/x-lua", "lua");
});
});

var meta = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.modeInfo = [
        {
            name: "APL",
            mime: "text/apl",
            mode: "apl",
            ext: [
                "dyalog",
                "apl"
            ]
        },
        {
            name: "PGP",
            mimes: [
                "application/pgp",
                "application/pgp-encrypted",
                "application/pgp-keys",
                "application/pgp-signature"
            ],
            mode: "asciiarmor",
            ext: [
                "asc",
                "pgp",
                "sig"
            ]
        },
        {
            name: "ASN.1",
            mime: "text/x-ttcn-asn",
            mode: "asn.1",
            ext: [
                "asn",
                "asn1"
            ]
        },
        {
            name: "Asterisk",
            mime: "text/x-asterisk",
            mode: "asterisk",
            file: /^extensions\.conf$/i
        },
        {
            name: "Brainfuck",
            mime: "text/x-brainfuck",
            mode: "brainfuck",
            ext: [
                "b",
                "bf"
            ]
        },
        {
            name: "C",
            mime: "text/x-csrc",
            mode: "clike",
            ext: [
                "c",
                "h",
                "ino"
            ]
        },
        {
            name: "C++",
            mime: "text/x-c++src",
            mode: "clike",
            ext: [
                "cpp",
                "c++",
                "cc",
                "cxx",
                "hpp",
                "h++",
                "hh",
                "hxx"
            ],
            alias: [
                "cpp"
            ]
        },
        {
            name: "Cobol",
            mime: "text/x-cobol",
            mode: "cobol",
            ext: [
                "cob",
                "cpy",
                "cbl"
            ]
        },
        {
            name: "C#",
            mime: "text/x-csharp",
            mode: "clike",
            ext: [
                "cs"
            ],
            alias: [
                "csharp",
                "cs"
            ]
        },
        {
            name: "Clojure",
            mime: "text/x-clojure",
            mode: "clojure",
            ext: [
                "clj",
                "cljc",
                "cljx"
            ]
        },
        {
            name: "ClojureScript",
            mime: "text/x-clojurescript",
            mode: "clojure",
            ext: [
                "cljs"
            ]
        },
        {
            name: "Closure Stylesheets (GSS)",
            mime: "text/x-gss",
            mode: "css",
            ext: [
                "gss"
            ]
        },
        {
            name: "CMake",
            mime: "text/x-cmake",
            mode: "cmake",
            ext: [
                "cmake",
                "cmake.in"
            ],
            file: /^CMakeLists\.txt$/
        },
        {
            name: "CoffeeScript",
            mimes: [
                "application/vnd.coffeescript",
                "text/coffeescript",
                "text/x-coffeescript"
            ],
            mode: "coffeescript",
            ext: [
                "coffee"
            ],
            alias: [
                "coffee",
                "coffee-script"
            ]
        },
        {
            name: "Common Lisp",
            mime: "text/x-common-lisp",
            mode: "commonlisp",
            ext: [
                "cl",
                "lisp",
                "el"
            ],
            alias: [
                "lisp"
            ]
        },
        {
            name: "Cypher",
            mime: "application/x-cypher-query",
            mode: "cypher",
            ext: [
                "cyp",
                "cypher"
            ]
        },
        {
            name: "Cython",
            mime: "text/x-cython",
            mode: "python",
            ext: [
                "pyx",
                "pxd",
                "pxi"
            ]
        },
        {
            name: "Crystal",
            mime: "text/x-crystal",
            mode: "crystal",
            ext: [
                "cr"
            ]
        },
        {
            name: "CSS",
            mime: "text/css",
            mode: "css",
            ext: [
                "css"
            ]
        },
        {
            name: "CQL",
            mime: "text/x-cassandra",
            mode: "sql",
            ext: [
                "cql"
            ]
        },
        {
            name: "D",
            mime: "text/x-d",
            mode: "d",
            ext: [
                "d"
            ]
        },
        {
            name: "Dart",
            mimes: [
                "application/dart",
                "text/x-dart"
            ],
            mode: "dart",
            ext: [
                "dart"
            ]
        },
        {
            name: "diff",
            mime: "text/x-diff",
            mode: "diff",
            ext: [
                "diff",
                "patch"
            ]
        },
        {
            name: "Django",
            mime: "text/x-django",
            mode: "django"
        },
        {
            name: "Dockerfile",
            mime: "text/x-dockerfile",
            mode: "dockerfile",
            file: /^Dockerfile$/
        },
        {
            name: "DTD",
            mime: "application/xml-dtd",
            mode: "dtd",
            ext: [
                "dtd"
            ]
        },
        {
            name: "Dylan",
            mime: "text/x-dylan",
            mode: "dylan",
            ext: [
                "dylan",
                "dyl",
                "intr"
            ]
        },
        {
            name: "EBNF",
            mime: "text/x-ebnf",
            mode: "ebnf"
        },
        {
            name: "ECL",
            mime: "text/x-ecl",
            mode: "ecl",
            ext: [
                "ecl"
            ]
        },
        {
            name: "edn",
            mime: "application/edn",
            mode: "clojure",
            ext: [
                "edn"
            ]
        },
        {
            name: "Eiffel",
            mime: "text/x-eiffel",
            mode: "eiffel",
            ext: [
                "e"
            ]
        },
        {
            name: "Elm",
            mime: "text/x-elm",
            mode: "elm",
            ext: [
                "elm"
            ]
        },
        {
            name: "Embedded JavaScript",
            mime: "application/x-ejs",
            mode: "htmlembedded",
            ext: [
                "ejs"
            ]
        },
        {
            name: "Embedded Ruby",
            mime: "application/x-erb",
            mode: "htmlembedded",
            ext: [
                "erb"
            ]
        },
        {
            name: "Erlang",
            mime: "text/x-erlang",
            mode: "erlang",
            ext: [
                "erl"
            ]
        },
        {
            name: "Esper",
            mime: "text/x-esper",
            mode: "sql"
        },
        {
            name: "Factor",
            mime: "text/x-factor",
            mode: "factor",
            ext: [
                "factor"
            ]
        },
        {
            name: "FCL",
            mime: "text/x-fcl",
            mode: "fcl"
        },
        {
            name: "Forth",
            mime: "text/x-forth",
            mode: "forth",
            ext: [
                "forth",
                "fth",
                "4th"
            ]
        },
        {
            name: "Fortran",
            mime: "text/x-fortran",
            mode: "fortran",
            ext: [
                "f",
                "for",
                "f77",
                "f90",
                "f95"
            ]
        },
        {
            name: "F#",
            mime: "text/x-fsharp",
            mode: "mllike",
            ext: [
                "fs"
            ],
            alias: [
                "fsharp"
            ]
        },
        {
            name: "Gas",
            mime: "text/x-gas",
            mode: "gas",
            ext: [
                "s"
            ]
        },
        {
            name: "Gherkin",
            mime: "text/x-feature",
            mode: "gherkin",
            ext: [
                "feature"
            ]
        },
        {
            name: "GitHub Flavored Markdown",
            mime: "text/x-gfm",
            mode: "gfm",
            file: /^(readme|contributing|history)\.md$/i
        },
        {
            name: "Go",
            mime: "text/x-go",
            mode: "go",
            ext: [
                "go"
            ]
        },
        {
            name: "Groovy",
            mime: "text/x-groovy",
            mode: "groovy",
            ext: [
                "groovy",
                "gradle"
            ],
            file: /^Jenkinsfile$/
        },
        {
            name: "HAML",
            mime: "text/x-haml",
            mode: "haml",
            ext: [
                "haml"
            ]
        },
        {
            name: "Haskell",
            mime: "text/x-haskell",
            mode: "haskell",
            ext: [
                "hs"
            ]
        },
        {
            name: "Haskell (Literate)",
            mime: "text/x-literate-haskell",
            mode: "haskell-literate",
            ext: [
                "lhs"
            ]
        },
        {
            name: "Haxe",
            mime: "text/x-haxe",
            mode: "haxe",
            ext: [
                "hx"
            ]
        },
        {
            name: "HXML",
            mime: "text/x-hxml",
            mode: "haxe",
            ext: [
                "hxml"
            ]
        },
        {
            name: "ASP.NET",
            mime: "application/x-aspx",
            mode: "htmlembedded",
            ext: [
                "aspx"
            ],
            alias: [
                "asp",
                "aspx"
            ]
        },
        {
            name: "HTML",
            mime: "text/html",
            mode: "htmlmixed",
            ext: [
                "html",
                "htm",
                "handlebars",
                "hbs"
            ],
            alias: [
                "xhtml"
            ]
        },
        {
            name: "HTTP",
            mime: "message/http",
            mode: "http"
        },
        {
            name: "IDL",
            mime: "text/x-idl",
            mode: "idl",
            ext: [
                "pro"
            ]
        },
        {
            name: "Pug",
            mime: "text/x-pug",
            mode: "pug",
            ext: [
                "jade",
                "pug"
            ],
            alias: [
                "jade"
            ]
        },
        {
            name: "Java",
            mime: "text/x-java",
            mode: "clike",
            ext: [
                "java"
            ]
        },
        {
            name: "Java Server Pages",
            mime: "application/x-jsp",
            mode: "htmlembedded",
            ext: [
                "jsp"
            ],
            alias: [
                "jsp"
            ]
        },
        {
            name: "JavaScript",
            mimes: [
                "text/javascript",
                "text/ecmascript",
                "application/javascript",
                "application/x-javascript",
                "application/ecmascript"
            ],
            mode: "javascript",
            ext: [
                "js"
            ],
            alias: [
                "ecmascript",
                "js",
                "node"
            ]
        },
        {
            name: "JSON",
            mimes: [
                "application/json",
                "application/x-json"
            ],
            mode: "javascript",
            ext: [
                "json",
                "map"
            ],
            alias: [
                "json5"
            ]
        },
        {
            name: "JSON-LD",
            mime: "application/ld+json",
            mode: "javascript",
            ext: [
                "jsonld"
            ],
            alias: [
                "jsonld"
            ]
        },
        {
            name: "JSX",
            mime: "text/jsx",
            mode: "jsx",
            ext: [
                "jsx"
            ]
        },
        {
            name: "Jinja2",
            mime: "text/jinja2",
            mode: "jinja2",
            ext: [
                "j2",
                "jinja",
                "jinja2"
            ]
        },
        {
            name: "Julia",
            mime: "text/x-julia",
            mode: "julia",
            ext: [
                "jl"
            ],
            alias: [
                "jl"
            ]
        },
        {
            name: "Kotlin",
            mime: "text/x-kotlin",
            mode: "clike",
            ext: [
                "kt"
            ]
        },
        {
            name: "LESS",
            mime: "text/x-less",
            mode: "css",
            ext: [
                "less"
            ]
        },
        {
            name: "LiveScript",
            mime: "text/x-livescript",
            mode: "livescript",
            ext: [
                "ls"
            ],
            alias: [
                "ls"
            ]
        },
        {
            name: "Lua",
            mime: "text/x-lua",
            mode: "lua",
            ext: [
                "lua"
            ]
        },
        {
            name: "Markdown",
            mime: "text/x-markdown",
            mode: "markdown",
            ext: [
                "markdown",
                "md",
                "mkd"
            ]
        },
        {
            name: "mIRC",
            mime: "text/mirc",
            mode: "mirc"
        },
        {
            name: "MariaDB SQL",
            mime: "text/x-mariadb",
            mode: "sql"
        },
        {
            name: "Mathematica",
            mime: "text/x-mathematica",
            mode: "mathematica",
            ext: [
                "m",
                "nb",
                "wl",
                "wls"
            ]
        },
        {
            name: "Modelica",
            mime: "text/x-modelica",
            mode: "modelica",
            ext: [
                "mo"
            ]
        },
        {
            name: "MUMPS",
            mime: "text/x-mumps",
            mode: "mumps",
            ext: [
                "mps"
            ]
        },
        {
            name: "MS SQL",
            mime: "text/x-mssql",
            mode: "sql"
        },
        {
            name: "mbox",
            mime: "application/mbox",
            mode: "mbox",
            ext: [
                "mbox"
            ]
        },
        {
            name: "MySQL",
            mime: "text/x-mysql",
            mode: "sql"
        },
        {
            name: "Nginx",
            mime: "text/x-nginx-conf",
            mode: "nginx",
            file: /nginx.*\.conf$/i
        },
        {
            name: "NSIS",
            mime: "text/x-nsis",
            mode: "nsis",
            ext: [
                "nsh",
                "nsi"
            ]
        },
        {
            name: "NTriples",
            mimes: [
                "application/n-triples",
                "application/n-quads",
                "text/n-triples"
            ],
            mode: "ntriples",
            ext: [
                "nt",
                "nq"
            ]
        },
        {
            name: "Objective-C",
            mime: "text/x-objectivec",
            mode: "clike",
            ext: [
                "m"
            ],
            alias: [
                "objective-c",
                "objc"
            ]
        },
        {
            name: "Objective-C++",
            mime: "text/x-objectivec++",
            mode: "clike",
            ext: [
                "mm"
            ],
            alias: [
                "objective-c++",
                "objc++"
            ]
        },
        {
            name: "OCaml",
            mime: "text/x-ocaml",
            mode: "mllike",
            ext: [
                "ml",
                "mli",
                "mll",
                "mly"
            ]
        },
        {
            name: "Octave",
            mime: "text/x-octave",
            mode: "octave",
            ext: [
                "m"
            ]
        },
        {
            name: "Oz",
            mime: "text/x-oz",
            mode: "oz",
            ext: [
                "oz"
            ]
        },
        {
            name: "Pascal",
            mime: "text/x-pascal",
            mode: "pascal",
            ext: [
                "p",
                "pas"
            ]
        },
        {
            name: "PEG.js",
            mime: "null",
            mode: "pegjs",
            ext: [
                "jsonld"
            ]
        },
        {
            name: "Perl",
            mime: "text/x-perl",
            mode: "perl",
            ext: [
                "pl",
                "pm"
            ]
        },
        {
            name: "PHP",
            mimes: [
                "text/x-php",
                "application/x-httpd-php",
                "application/x-httpd-php-open"
            ],
            mode: "php",
            ext: [
                "php",
                "php3",
                "php4",
                "php5",
                "php7",
                "phtml"
            ]
        },
        {
            name: "Pig",
            mime: "text/x-pig",
            mode: "pig",
            ext: [
                "pig"
            ]
        },
        {
            name: "Plain Text",
            mime: "text/plain",
            mode: "null",
            ext: [
                "txt",
                "text",
                "conf",
                "def",
                "list",
                "log"
            ]
        },
        {
            name: "PLSQL",
            mime: "text/x-plsql",
            mode: "sql",
            ext: [
                "pls"
            ]
        },
        {
            name: "PostgreSQL",
            mime: "text/x-pgsql",
            mode: "sql"
        },
        {
            name: "PowerShell",
            mime: "application/x-powershell",
            mode: "powershell",
            ext: [
                "ps1",
                "psd1",
                "psm1"
            ]
        },
        {
            name: "Properties files",
            mime: "text/x-properties",
            mode: "properties",
            ext: [
                "properties",
                "ini",
                "in"
            ],
            alias: [
                "ini",
                "properties"
            ]
        },
        {
            name: "ProtoBuf",
            mime: "text/x-protobuf",
            mode: "protobuf",
            ext: [
                "proto"
            ]
        },
        {
            name: "Python",
            mime: "text/x-python",
            mode: "python",
            ext: [
                "BUILD",
                "bzl",
                "py",
                "pyw"
            ],
            file: /^(BUCK|BUILD)$/
        },
        {
            name: "Puppet",
            mime: "text/x-puppet",
            mode: "puppet",
            ext: [
                "pp"
            ]
        },
        {
            name: "Q",
            mime: "text/x-q",
            mode: "q",
            ext: [
                "q"
            ]
        },
        {
            name: "R",
            mime: "text/x-rsrc",
            mode: "r",
            ext: [
                "r",
                "R"
            ],
            alias: [
                "rscript"
            ]
        },
        {
            name: "reStructuredText",
            mime: "text/x-rst",
            mode: "rst",
            ext: [
                "rst"
            ],
            alias: [
                "rst"
            ]
        },
        {
            name: "RPM Changes",
            mime: "text/x-rpm-changes",
            mode: "rpm"
        },
        {
            name: "RPM Spec",
            mime: "text/x-rpm-spec",
            mode: "rpm",
            ext: [
                "spec"
            ]
        },
        {
            name: "Ruby",
            mime: "text/x-ruby",
            mode: "ruby",
            ext: [
                "rb"
            ],
            alias: [
                "jruby",
                "macruby",
                "rake",
                "rb",
                "rbx"
            ]
        },
        {
            name: "Rust",
            mime: "text/x-rustsrc",
            mode: "rust",
            ext: [
                "rs"
            ]
        },
        {
            name: "SAS",
            mime: "text/x-sas",
            mode: "sas",
            ext: [
                "sas"
            ]
        },
        {
            name: "Sass",
            mime: "text/x-sass",
            mode: "sass",
            ext: [
                "sass"
            ]
        },
        {
            name: "Scala",
            mime: "text/x-scala",
            mode: "clike",
            ext: [
                "scala"
            ]
        },
        {
            name: "Scheme",
            mime: "text/x-scheme",
            mode: "scheme",
            ext: [
                "scm",
                "ss"
            ]
        },
        {
            name: "SCSS",
            mime: "text/x-scss",
            mode: "css",
            ext: [
                "scss"
            ]
        },
        {
            name: "Shell",
            mimes: [
                "text/x-sh",
                "application/x-sh"
            ],
            mode: "shell",
            ext: [
                "sh",
                "ksh",
                "bash"
            ],
            alias: [
                "bash",
                "sh",
                "zsh"
            ],
            file: /^PKGBUILD$/
        },
        {
            name: "Sieve",
            mime: "application/sieve",
            mode: "sieve",
            ext: [
                "siv",
                "sieve"
            ]
        },
        {
            name: "Slim",
            mimes: [
                "text/x-slim",
                "application/x-slim"
            ],
            mode: "slim",
            ext: [
                "slim"
            ]
        },
        {
            name: "Smalltalk",
            mime: "text/x-stsrc",
            mode: "smalltalk",
            ext: [
                "st"
            ]
        },
        {
            name: "Smarty",
            mime: "text/x-smarty",
            mode: "smarty",
            ext: [
                "tpl"
            ]
        },
        {
            name: "Solr",
            mime: "text/x-solr",
            mode: "solr"
        },
        {
            name: "SML",
            mime: "text/x-sml",
            mode: "mllike",
            ext: [
                "sml",
                "sig",
                "fun",
                "smackspec"
            ]
        },
        {
            name: "Soy",
            mime: "text/x-soy",
            mode: "soy",
            ext: [
                "soy"
            ],
            alias: [
                "closure template"
            ]
        },
        {
            name: "SPARQL",
            mime: "application/sparql-query",
            mode: "sparql",
            ext: [
                "rq",
                "sparql"
            ],
            alias: [
                "sparul"
            ]
        },
        {
            name: "Spreadsheet",
            mime: "text/x-spreadsheet",
            mode: "spreadsheet",
            alias: [
                "excel",
                "formula"
            ]
        },
        {
            name: "SQL",
            mime: "text/x-sql",
            mode: "sql",
            ext: [
                "sql"
            ]
        },
        {
            name: "SQLite",
            mime: "text/x-sqlite",
            mode: "sql"
        },
        {
            name: "Squirrel",
            mime: "text/x-squirrel",
            mode: "clike",
            ext: [
                "nut"
            ]
        },
        {
            name: "Stylus",
            mime: "text/x-styl",
            mode: "stylus",
            ext: [
                "styl"
            ]
        },
        {
            name: "Swift",
            mime: "text/x-swift",
            mode: "swift",
            ext: [
                "swift"
            ]
        },
        {
            name: "sTeX",
            mime: "text/x-stex",
            mode: "stex"
        },
        {
            name: "LaTeX",
            mime: "text/x-latex",
            mode: "stex",
            ext: [
                "text",
                "ltx",
                "tex"
            ],
            alias: [
                "tex"
            ]
        },
        {
            name: "SystemVerilog",
            mime: "text/x-systemverilog",
            mode: "verilog",
            ext: [
                "v",
                "sv",
                "svh"
            ]
        },
        {
            name: "Tcl",
            mime: "text/x-tcl",
            mode: "tcl",
            ext: [
                "tcl"
            ]
        },
        {
            name: "Textile",
            mime: "text/x-textile",
            mode: "textile",
            ext: [
                "textile"
            ]
        },
        {
            name: "TiddlyWiki",
            mime: "text/x-tiddlywiki",
            mode: "tiddlywiki"
        },
        {
            name: "Tiki wiki",
            mime: "text/tiki",
            mode: "tiki"
        },
        {
            name: "TOML",
            mime: "text/x-toml",
            mode: "toml",
            ext: [
                "toml"
            ]
        },
        {
            name: "Tornado",
            mime: "text/x-tornado",
            mode: "tornado"
        },
        {
            name: "troff",
            mime: "text/troff",
            mode: "troff",
            ext: [
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9"
            ]
        },
        {
            name: "TTCN",
            mime: "text/x-ttcn",
            mode: "ttcn",
            ext: [
                "ttcn",
                "ttcn3",
                "ttcnpp"
            ]
        },
        {
            name: "TTCN_CFG",
            mime: "text/x-ttcn-cfg",
            mode: "ttcn-cfg",
            ext: [
                "cfg"
            ]
        },
        {
            name: "Turtle",
            mime: "text/turtle",
            mode: "turtle",
            ext: [
                "ttl"
            ]
        },
        {
            name: "TypeScript",
            mime: "application/typescript",
            mode: "javascript",
            ext: [
                "ts"
            ],
            alias: [
                "ts"
            ]
        },
        {
            name: "TypeScript-JSX",
            mime: "text/typescript-jsx",
            mode: "jsx",
            ext: [
                "tsx"
            ],
            alias: [
                "tsx"
            ]
        },
        {
            name: "Twig",
            mime: "text/x-twig",
            mode: "twig"
        },
        {
            name: "Web IDL",
            mime: "text/x-webidl",
            mode: "webidl",
            ext: [
                "webidl"
            ]
        },
        {
            name: "VB.NET",
            mime: "text/x-vb",
            mode: "vb",
            ext: [
                "vb"
            ]
        },
        {
            name: "VBScript",
            mime: "text/vbscript",
            mode: "vbscript",
            ext: [
                "vbs"
            ]
        },
        {
            name: "Velocity",
            mime: "text/velocity",
            mode: "velocity",
            ext: [
                "vtl"
            ]
        },
        {
            name: "Verilog",
            mime: "text/x-verilog",
            mode: "verilog",
            ext: [
                "v"
            ]
        },
        {
            name: "VHDL",
            mime: "text/x-vhdl",
            mode: "vhdl",
            ext: [
                "vhd",
                "vhdl"
            ]
        },
        {
            name: "Vue.js Component",
            mimes: [
                "script/x-vue",
                "text/x-vue"
            ],
            mode: "vue",
            ext: [
                "vue"
            ]
        },
        {
            name: "XML",
            mimes: [
                "application/xml",
                "text/xml"
            ],
            mode: "xml",
            ext: [
                "xml",
                "xsl",
                "xsd",
                "svg"
            ],
            alias: [
                "rss",
                "wsdl",
                "xsd"
            ]
        },
        {
            name: "XQuery",
            mime: "application/xquery",
            mode: "xquery",
            ext: [
                "xy",
                "xquery"
            ]
        },
        {
            name: "Yacas",
            mime: "text/x-yacas",
            mode: "yacas",
            ext: [
                "ys"
            ]
        },
        {
            name: "YAML",
            mimes: [
                "text/x-yaml",
                "text/yaml"
            ],
            mode: "yaml",
            ext: [
                "yaml",
                "yml"
            ],
            alias: [
                "yml"
            ]
        },
        {
            name: "Z80",
            mime: "text/x-z80",
            mode: "z80",
            ext: [
                "z80"
            ]
        },
        {
            name: "mscgen",
            mime: "text/x-mscgen",
            mode: "mscgen",
            ext: [
                "mscgen",
                "mscin",
                "msc"
            ]
        },
        {
            name: "xu",
            mime: "text/x-xu",
            mode: "mscgen",
            ext: [
                "xu"
            ]
        },
        {
            name: "msgenny",
            mime: "text/x-msgenny",
            mode: "mscgen",
            ext: [
                "msgenny"
            ]
        },
        {
            name: "WebAssembly",
            mime: "text/webassembly",
            mode: "wast",
            ext: [
                "wat",
                "wast"
            ]
        }
    ];
    // Ensure all modes have a mime property for backwards compatibility
    for(var i = 0; i < CodeMirror1.modeInfo.length; i++){
        var info = CodeMirror1.modeInfo[i];
        if (info.mimes) info.mime = info.mimes[0];
    }
    CodeMirror1.findModeByMIME = function(mime) {
        mime = mime.toLowerCase();
        for(var i = 0; i < CodeMirror1.modeInfo.length; i++){
            var info = CodeMirror1.modeInfo[i];
            if (info.mime == mime) return info;
            if (info.mimes) {
                for(var j = 0; j < info.mimes.length; j++)if (info.mimes[j] == mime) return info;
            }
        }
        if (/\+xml$/.test(mime)) return CodeMirror1.findModeByMIME("application/xml");
        if (/\+json$/.test(mime)) return CodeMirror1.findModeByMIME("application/json");
    };
    CodeMirror1.findModeByExtension = function(ext) {
        ext = ext.toLowerCase();
        for(var i = 0; i < CodeMirror1.modeInfo.length; i++){
            var info = CodeMirror1.modeInfo[i];
            if (info.ext) {
                for(var j = 0; j < info.ext.length; j++)if (info.ext[j] == ext) return info;
            }
        }
    };
    CodeMirror1.findModeByFileName = function(filename) {
        for(var i = 0; i < CodeMirror1.modeInfo.length; i++){
            var info = CodeMirror1.modeInfo[i];
            if (info.file && info.file.test(filename)) return info;
        }
        var dot = filename.lastIndexOf(".");
        var ext = dot > -1 && filename.substring(dot + 1, filename.length);
        if (ext) return CodeMirror1.findModeByExtension(ext);
    };
    CodeMirror1.findModeByName = function(name) {
        name = name.toLowerCase();
        for(var i = 0; i < CodeMirror1.modeInfo.length; i++){
            var info = CodeMirror1.modeInfo[i];
            if (info.name.toLowerCase() == name) return info;
            if (info.alias) {
                for(var j = 0; j < info.alias.length; j++)if (info.alias[j].toLowerCase() == name) return info;
            }
        }
    };
});
});

var markdown = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, xml, meta);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("markdown", function(cmCfg, modeCfg) {
        var getMode = function getMode(name) {
            if (CodeMirror1.findModeByName) {
                var found = CodeMirror1.findModeByName(name);
                if (found) name = found.mime || found.mimes[0];
            }
            var mode = CodeMirror1.getMode(cmCfg, name);
            return mode.name == "null" ? null : mode;
        };
        var switchInline = function switchInline(stream, state, f) {
            state.f = state.inline = f;
            return f(stream, state);
        };
        var switchBlock = function switchBlock(stream, state, f) {
            state.f = state.block = f;
            return f(stream, state);
        };
        var lineIsEmpty = function lineIsEmpty(line) {
            return !line || !/\S/.test(line.string);
        };
        var blankLine = // Blocks
        function blankLine(state) {
            // Reset linkTitle state
            state.linkTitle = false;
            state.linkHref = false;
            state.linkText = false;
            // Reset EM state
            state.em = false;
            // Reset STRONG state
            state.strong = false;
            // Reset strikethrough state
            state.strikethrough = false;
            // Reset state.quote
            state.quote = 0;
            // Reset state.indentedCode
            state.indentedCode = false;
            if (state.f == htmlBlock) {
                var exit = htmlModeMissing;
                if (!exit) {
                    var inner = CodeMirror1.innerMode(htmlMode, state.htmlState);
                    exit = inner.mode.name == "xml" && inner.state.tagStart === null && !inner.state.context && inner.state.tokenize.isInText;
                }
                if (exit) {
                    state.f = inlineNormal;
                    state.block = blockNormal;
                    state.htmlState = null;
                }
            }
            // Reset state.trailingSpace
            state.trailingSpace = 0;
            state.trailingSpaceNewLine = false;
            // Mark this line as blank
            state.prevLine = state.thisLine;
            state.thisLine = {
                stream: null
            };
            return null;
        };
        var blockNormal = function blockNormal(stream, state) {
            var firstTokenOnLine = stream.column() === state.indentation;
            var prevLineLineIsEmpty = lineIsEmpty(state.prevLine.stream);
            var prevLineIsIndentedCode = state.indentedCode;
            var prevLineIsHr = state.prevLine.hr;
            var prevLineIsList = state.list !== false;
            var maxNonCodeIndentation = (state.listStack[state.listStack.length - 1] || 0) + 3;
            state.indentedCode = false;
            var lineIndentation = state.indentation;
            // compute once per line (on first token)
            if (state.indentationDiff === null) {
                state.indentationDiff = state.indentation;
                if (prevLineIsList) {
                    state.list = null;
                    // While this list item's marker's indentation is less than the deepest
                    //  list item's content's indentation,pop the deepest list item
                    //  indentation off the stack, and update block indentation state
                    while(lineIndentation < state.listStack[state.listStack.length - 1]){
                        state.listStack.pop();
                        if (state.listStack.length) {
                            state.indentation = state.listStack[state.listStack.length - 1];
                        // less than the first list's indent -> the line is no longer a list
                        } else {
                            state.list = false;
                        }
                    }
                    if (state.list !== false) {
                        state.indentationDiff = lineIndentation - state.listStack[state.listStack.length - 1];
                    }
                }
            }
            // not comprehensive (currently only for setext detection purposes)
            var allowsInlineContinuation = !prevLineLineIsEmpty && !prevLineIsHr && !state.prevLine.header && (!prevLineIsList || !prevLineIsIndentedCode) && !state.prevLine.fencedCodeEnd;
            var isHr = (state.list === false || prevLineIsHr || prevLineLineIsEmpty) && state.indentation <= maxNonCodeIndentation && stream.match(hrRE);
            var match = null;
            if (state.indentationDiff >= 4 && (prevLineIsIndentedCode || state.prevLine.fencedCodeEnd || state.prevLine.header || prevLineLineIsEmpty)) {
                stream.skipToEnd();
                state.indentedCode = true;
                return tokenTypes.code;
            } else if (stream.eatSpace()) {
                return null;
            } else if (firstTokenOnLine && state.indentation <= maxNonCodeIndentation && (match = stream.match(atxHeaderRE)) && match[1].length <= 6) {
                state.quote = 0;
                state.header = match[1].length;
                state.thisLine.header = true;
                if (modeCfg.highlightFormatting) state.formatting = "header";
                state.f = state.inline;
                return getType(state);
            } else if (state.indentation <= maxNonCodeIndentation && stream.eat(">")) {
                state.quote = firstTokenOnLine ? 1 : state.quote + 1;
                if (modeCfg.highlightFormatting) state.formatting = "quote";
                stream.eatSpace();
                return getType(state);
            } else if (!isHr && !state.setext && firstTokenOnLine && state.indentation <= maxNonCodeIndentation && (match = stream.match(listRE))) {
                var listType = match[1] ? "ol" : "ul";
                state.indentation = lineIndentation + stream.current().length;
                state.list = true;
                state.quote = 0;
                // Add this list item's content's indentation to the stack
                state.listStack.push(state.indentation);
                // Reset inline styles which shouldn't propagate across list items
                state.em = false;
                state.strong = false;
                state.code = false;
                state.strikethrough = false;
                if (modeCfg.taskLists && stream.match(taskListRE, false)) {
                    state.taskList = true;
                }
                state.f = state.inline;
                if (modeCfg.highlightFormatting) state.formatting = [
                    "list",
                    "list-" + listType
                ];
                return getType(state);
            } else if (firstTokenOnLine && state.indentation <= maxNonCodeIndentation && (match = stream.match(fencedCodeRE, true))) {
                state.quote = 0;
                state.fencedEndRE = new RegExp(match[1] + "+ *$");
                // try switching mode
                state.localMode = modeCfg.fencedCodeBlockHighlighting && getMode(match[2] || modeCfg.fencedCodeBlockDefaultMode);
                if (state.localMode) state.localState = CodeMirror1.startState(state.localMode);
                state.f = state.block = local;
                if (modeCfg.highlightFormatting) state.formatting = "code-block";
                state.code = -1;
                return getType(state);
            // SETEXT has lowest block-scope precedence after HR, so check it after
            //  the others (code, blockquote, list...)
            } else if (// if setext set, indicates line after ---/===
            state.setext || // line before ---/===
            (!allowsInlineContinuation || !prevLineIsList) && !state.quote && state.list === false && !state.code && !isHr && !linkDefRE.test(stream.string) && (match = stream.lookAhead(1)) && (match = match.match(setextHeaderRE))) {
                if (!state.setext) {
                    state.header = match[0].charAt(0) == "=" ? 1 : 2;
                    state.setext = state.header;
                } else {
                    state.header = state.setext;
                    // has no effect on type so we can reset it now
                    state.setext = 0;
                    stream.skipToEnd();
                    if (modeCfg.highlightFormatting) state.formatting = "header";
                }
                state.thisLine.header = true;
                state.f = state.inline;
                return getType(state);
            } else if (isHr) {
                stream.skipToEnd();
                state.hr = true;
                state.thisLine.hr = true;
                return tokenTypes.hr;
            } else if (stream.peek() === "[") {
                return switchInline(stream, state, footnoteLink);
            }
            return switchInline(stream, state, state.inline);
        };
        var htmlBlock = function htmlBlock(stream, state) {
            var style = htmlMode.token(stream, state.htmlState);
            if (!htmlModeMissing) {
                var inner = CodeMirror1.innerMode(htmlMode, state.htmlState);
                if (inner.mode.name == "xml" && inner.state.tagStart === null && !inner.state.context && inner.state.tokenize.isInText || state.md_inside && stream.current().indexOf(">") > -1) {
                    state.f = inlineNormal;
                    state.block = blockNormal;
                    state.htmlState = null;
                }
            }
            return style;
        };
        var local = function local(stream, state) {
            var currListInd = state.listStack[state.listStack.length - 1] || 0;
            var hasExitedList = state.indentation < currListInd;
            var maxFencedEndInd = currListInd + 3;
            if (state.fencedEndRE && state.indentation <= maxFencedEndInd && (hasExitedList || stream.match(state.fencedEndRE))) {
                if (modeCfg.highlightFormatting) state.formatting = "code-block";
                var returnType;
                if (!hasExitedList) returnType = getType(state);
                state.localMode = state.localState = null;
                state.block = blockNormal;
                state.f = inlineNormal;
                state.fencedEndRE = null;
                state.code = 0;
                state.thisLine.fencedCodeEnd = true;
                if (hasExitedList) return switchBlock(stream, state, state.block);
                return returnType;
            } else if (state.localMode) {
                return state.localMode.token(stream, state.localState);
            } else {
                stream.skipToEnd();
                return tokenTypes.code;
            }
        };
        var getType = // Inline
        function getType(state) {
            var styles = [];
            if (state.formatting) {
                styles.push(tokenTypes.formatting);
                if (typeof state.formatting === "string") state.formatting = [
                    state.formatting
                ];
                for(var i = 0; i < state.formatting.length; i++){
                    styles.push(tokenTypes.formatting + "-" + state.formatting[i]);
                    if (state.formatting[i] === "header") {
                        styles.push(tokenTypes.formatting + "-" + state.formatting[i] + "-" + state.header);
                    }
                    // Add `formatting-quote` and `formatting-quote-#` for blockquotes
                    // Add `error` instead if the maximum blockquote nesting depth is passed
                    if (state.formatting[i] === "quote") {
                        if (!modeCfg.maxBlockquoteDepth || modeCfg.maxBlockquoteDepth >= state.quote) {
                            styles.push(tokenTypes.formatting + "-" + state.formatting[i] + "-" + state.quote);
                        } else {
                            styles.push("error");
                        }
                    }
                }
            }
            if (state.taskOpen) {
                styles.push("meta");
                return styles.length ? styles.join(" ") : null;
            }
            if (state.taskClosed) {
                styles.push("property");
                return styles.length ? styles.join(" ") : null;
            }
            if (state.linkHref) {
                styles.push(tokenTypes.linkHref, "url");
            } else {
                if (state.strong) {
                    styles.push(tokenTypes.strong);
                }
                if (state.em) {
                    styles.push(tokenTypes.em);
                }
                if (state.strikethrough) {
                    styles.push(tokenTypes.strikethrough);
                }
                if (state.emoji) {
                    styles.push(tokenTypes.emoji);
                }
                if (state.linkText) {
                    styles.push(tokenTypes.linkText);
                }
                if (state.code) {
                    styles.push(tokenTypes.code);
                }
                if (state.image) {
                    styles.push(tokenTypes.image);
                }
                if (state.imageAltText) {
                    styles.push(tokenTypes.imageAltText, "link");
                }
                if (state.imageMarker) {
                    styles.push(tokenTypes.imageMarker);
                }
            }
            if (state.header) {
                styles.push(tokenTypes.header, tokenTypes.header + "-" + state.header);
            }
            if (state.quote) {
                styles.push(tokenTypes.quote);
                // Add `quote-#` where the maximum for `#` is modeCfg.maxBlockquoteDepth
                if (!modeCfg.maxBlockquoteDepth || modeCfg.maxBlockquoteDepth >= state.quote) {
                    styles.push(tokenTypes.quote + "-" + state.quote);
                } else {
                    styles.push(tokenTypes.quote + "-" + modeCfg.maxBlockquoteDepth);
                }
            }
            if (state.list !== false) {
                var listMod = (state.listStack.length - 1) % 3;
                if (!listMod) {
                    styles.push(tokenTypes.list1);
                } else if (listMod === 1) {
                    styles.push(tokenTypes.list2);
                } else {
                    styles.push(tokenTypes.list3);
                }
            }
            if (state.trailingSpaceNewLine) {
                styles.push("trailing-space-new-line");
            } else if (state.trailingSpace) {
                styles.push("trailing-space-" + (state.trailingSpace % 2 ? "a" : "b"));
            }
            return styles.length ? styles.join(" ") : null;
        };
        var handleText = function handleText(stream, state) {
            if (stream.match(textRE, true)) {
                return getType(state);
            }
            return undefined;
        };
        var linkInline = function linkInline(stream, state) {
            var ch = stream.next();
            if (ch === ">") {
                state.f = state.inline = inlineNormal;
                if (modeCfg.highlightFormatting) state.formatting = "link";
                var type = getType(state);
                if (type) {
                    type += " ";
                } else {
                    type = "";
                }
                return type + tokenTypes.linkInline;
            }
            stream.match(/^[^>]+/, true);
            return tokenTypes.linkInline;
        };
        var linkHref = function linkHref(stream, state) {
            // Check if space, and return NULL if so (to avoid marking the space)
            if (stream.eatSpace()) {
                return null;
            }
            var ch = stream.next();
            if (ch === "(" || ch === "[") {
                state.f = state.inline = getLinkHrefInside(ch === "(" ? ")" : "]");
                if (modeCfg.highlightFormatting) state.formatting = "link-string";
                state.linkHref = true;
                return getType(state);
            }
            return "error";
        };
        var getLinkHrefInside = function getLinkHrefInside(endChar) {
            return function(stream, state) {
                var ch = stream.next();
                if (ch === endChar) {
                    state.f = state.inline = inlineNormal;
                    if (modeCfg.highlightFormatting) state.formatting = "link-string";
                    var returnState = getType(state);
                    state.linkHref = false;
                    return returnState;
                }
                stream.match(linkRE[endChar]);
                state.linkHref = true;
                return getType(state);
            };
        };
        var footnoteLink = function footnoteLink(stream, state) {
            if (stream.match(/^([^\]\\]|\\.)*\]:/, false)) {
                state.f = footnoteLinkInside;
                stream.next(); // Consume [
                if (modeCfg.highlightFormatting) state.formatting = "link";
                state.linkText = true;
                return getType(state);
            }
            return switchInline(stream, state, inlineNormal);
        };
        var footnoteLinkInside = function footnoteLinkInside(stream, state) {
            if (stream.match("]:", true)) {
                state.f = state.inline = footnoteUrl;
                if (modeCfg.highlightFormatting) state.formatting = "link";
                var returnType = getType(state);
                state.linkText = false;
                return returnType;
            }
            stream.match(/^([^\]\\]|\\.)+/, true);
            return tokenTypes.linkText;
        };
        var footnoteUrl = function footnoteUrl(stream, state) {
            // Check if space, and return NULL if so (to avoid marking the space)
            if (stream.eatSpace()) {
                return null;
            }
            // Match URL
            stream.match(/^[^\s]+/, true);
            // Check for link title
            if (stream.peek() === undefined) {
                state.linkTitle = true;
            } else {
                stream.match(/^(?:\s+(?:"(?:[^"\\]|\\.)+"|'(?:[^'\\]|\\.)+'|\((?:[^)\\]|\\.)+\)))?/, true);
            }
            state.f = state.inline = inlineNormal;
            return tokenTypes.linkHref + " url";
        };
        var htmlMode = CodeMirror1.getMode(cmCfg, "text/html");
        var htmlModeMissing = htmlMode.name == "null";
        // Should characters that affect highlighting be highlighted separate?
        // Does not include characters that will be output (such as `1.` and `-` for lists)
        if (modeCfg.highlightFormatting === undefined) modeCfg.highlightFormatting = false;
        // Maximum number of nested blockquotes. Set to 0 for infinite nesting.
        // Excess `>` will emit `error` token.
        if (modeCfg.maxBlockquoteDepth === undefined) modeCfg.maxBlockquoteDepth = 0;
        // Turn on task lists? ("- [ ] " and "- [x] ")
        if (modeCfg.taskLists === undefined) modeCfg.taskLists = false;
        // Turn on strikethrough syntax
        if (modeCfg.strikethrough === undefined) modeCfg.strikethrough = false;
        if (modeCfg.emoji === undefined) modeCfg.emoji = false;
        if (modeCfg.fencedCodeBlockHighlighting === undefined) modeCfg.fencedCodeBlockHighlighting = true;
        if (modeCfg.fencedCodeBlockDefaultMode === undefined) modeCfg.fencedCodeBlockDefaultMode = "text/plain";
        if (modeCfg.xml === undefined) modeCfg.xml = true;
        // Allow token types to be overridden by user-provided token types.
        if (modeCfg.tokenTypeOverrides === undefined) modeCfg.tokenTypeOverrides = {};
        var tokenTypes = {
            header: "header",
            code: "comment",
            quote: "quote",
            list1: "variable-2",
            list2: "variable-3",
            list3: "keyword",
            hr: "hr",
            image: "image",
            imageAltText: "image-alt-text",
            imageMarker: "image-marker",
            formatting: "formatting",
            linkInline: "link",
            linkEmail: "link",
            linkText: "link",
            linkHref: "string",
            em: "em",
            strong: "strong",
            strikethrough: "strikethrough",
            emoji: "builtin"
        };
        for(var tokenType in tokenTypes){
            if (tokenTypes.hasOwnProperty(tokenType) && modeCfg.tokenTypeOverrides[tokenType]) {
                tokenTypes[tokenType] = modeCfg.tokenTypeOverrides[tokenType];
            }
        }
        var hrRE = /^([*\-_])(?:\s*\1){2,}\s*$/, listRE = /^(?:[*\-+]|^[0-9]+([.)]))\s+/, taskListRE = /^\[(x| )\](?=\s)/i // Must follow listRE
        , atxHeaderRE = modeCfg.allowAtxHeaderWithoutSpace ? /^(#+)/ : /^(#+)(?: |$)/, setextHeaderRE = /^ {0,3}(?:\={1,}|-{2,})\s*$/, textRE = /^[^#!\[\]*_\\<>` "'(~:]+/, fencedCodeRE = /^(~~~+|```+)[ \t]*([\w\/+#-]*)[^\n`]*$/, linkDefRE = /^\s*\[[^\]]+?\]:.*$/ // naive link-definition
        , punctuation = /[!"#$%&'()*+,\-.\/:;<=>?@\[\\\]^_`{|}~\xA1\xA7\xAB\xB6\xB7\xBB\xBF\u037E\u0387\u055A-\u055F\u0589\u058A\u05BE\u05C0\u05C3\u05C6\u05F3\u05F4\u0609\u060A\u060C\u060D\u061B\u061E\u061F\u066A-\u066D\u06D4\u0700-\u070D\u07F7-\u07F9\u0830-\u083E\u085E\u0964\u0965\u0970\u0AF0\u0DF4\u0E4F\u0E5A\u0E5B\u0F04-\u0F12\u0F14\u0F3A-\u0F3D\u0F85\u0FD0-\u0FD4\u0FD9\u0FDA\u104A-\u104F\u10FB\u1360-\u1368\u1400\u166D\u166E\u169B\u169C\u16EB-\u16ED\u1735\u1736\u17D4-\u17D6\u17D8-\u17DA\u1800-\u180A\u1944\u1945\u1A1E\u1A1F\u1AA0-\u1AA6\u1AA8-\u1AAD\u1B5A-\u1B60\u1BFC-\u1BFF\u1C3B-\u1C3F\u1C7E\u1C7F\u1CC0-\u1CC7\u1CD3\u2010-\u2027\u2030-\u2043\u2045-\u2051\u2053-\u205E\u207D\u207E\u208D\u208E\u2308-\u230B\u2329\u232A\u2768-\u2775\u27C5\u27C6\u27E6-\u27EF\u2983-\u2998\u29D8-\u29DB\u29FC\u29FD\u2CF9-\u2CFC\u2CFE\u2CFF\u2D70\u2E00-\u2E2E\u2E30-\u2E42\u3001-\u3003\u3008-\u3011\u3014-\u301F\u3030\u303D\u30A0\u30FB\uA4FE\uA4FF\uA60D-\uA60F\uA673\uA67E\uA6F2-\uA6F7\uA874-\uA877\uA8CE\uA8CF\uA8F8-\uA8FA\uA8FC\uA92E\uA92F\uA95F\uA9C1-\uA9CD\uA9DE\uA9DF\uAA5C-\uAA5F\uAADE\uAADF\uAAF0\uAAF1\uABEB\uFD3E\uFD3F\uFE10-\uFE19\uFE30-\uFE52\uFE54-\uFE61\uFE63\uFE68\uFE6A\uFE6B\uFF01-\uFF03\uFF05-\uFF0A\uFF0C-\uFF0F\uFF1A\uFF1B\uFF1F\uFF20\uFF3B-\uFF3D\uFF3F\uFF5B\uFF5D\uFF5F-\uFF65]|\uD800[\uDD00-\uDD02\uDF9F\uDFD0]|\uD801\uDD6F|\uD802[\uDC57\uDD1F\uDD3F\uDE50-\uDE58\uDE7F\uDEF0-\uDEF6\uDF39-\uDF3F\uDF99-\uDF9C]|\uD804[\uDC47-\uDC4D\uDCBB\uDCBC\uDCBE-\uDCC1\uDD40-\uDD43\uDD74\uDD75\uDDC5-\uDDC9\uDDCD\uDDDB\uDDDD-\uDDDF\uDE38-\uDE3D\uDEA9]|\uD805[\uDCC6\uDDC1-\uDDD7\uDE41-\uDE43\uDF3C-\uDF3E]|\uD809[\uDC70-\uDC74]|\uD81A[\uDE6E\uDE6F\uDEF5\uDF37-\uDF3B\uDF44]|\uD82F\uDC9F|\uD836[\uDE87-\uDE8B]/, expandedTab = "    " // CommonMark specifies tab as 4 spaces
        ;
        function inlineNormal(stream, state) {
            var style = state.text(stream, state);
            if (typeof style !== "undefined") return style;
            if (state.list) {
                state.list = null;
                return getType(state);
            }
            if (state.taskList) {
                var taskOpen = stream.match(taskListRE, true)[1] === " ";
                if (taskOpen) state.taskOpen = true;
                else state.taskClosed = true;
                if (modeCfg.highlightFormatting) state.formatting = "task";
                state.taskList = false;
                return getType(state);
            }
            state.taskOpen = false;
            state.taskClosed = false;
            if (state.header && stream.match(/^#+$/, true)) {
                if (modeCfg.highlightFormatting) state.formatting = "header";
                return getType(state);
            }
            var ch = stream.next();
            // Matches link titles present on next line
            if (state.linkTitle) {
                state.linkTitle = false;
                var matchCh = ch;
                if (ch === "(") {
                    matchCh = ")";
                }
                matchCh = (matchCh + "").replace(/([.?*+^\[\]\\(){}|-])/g, "\\$1");
                var regex = "^\\s*(?:[^" + matchCh + "\\\\]+|\\\\\\\\|\\\\.)" + matchCh;
                if (stream.match(new RegExp(regex), true)) {
                    return tokenTypes.linkHref;
                }
            }
            // If this block is changed, it may need to be updated in GFM mode
            if (ch === "`") {
                var previousFormatting = state.formatting;
                if (modeCfg.highlightFormatting) state.formatting = "code";
                stream.eatWhile("`");
                var count = stream.current().length;
                if (state.code == 0 && (!state.quote || count == 1)) {
                    state.code = count;
                    return getType(state);
                } else if (count == state.code) {
                    var t = getType(state);
                    state.code = 0;
                    return t;
                } else {
                    state.formatting = previousFormatting;
                    return getType(state);
                }
            } else if (state.code) {
                return getType(state);
            }
            if (ch === "\\") {
                stream.next();
                if (modeCfg.highlightFormatting) {
                    var type = getType(state);
                    var formattingEscape = tokenTypes.formatting + "-escape";
                    return type ? type + " " + formattingEscape : formattingEscape;
                }
            }
            if (ch === "!" && stream.match(/\[[^\]]*\] ?(?:\(|\[)/, false)) {
                state.imageMarker = true;
                state.image = true;
                if (modeCfg.highlightFormatting) state.formatting = "image";
                return getType(state);
            }
            if (ch === "[" && state.imageMarker && stream.match(/[^\]]*\](\(.*?\)| ?\[.*?\])/, false)) {
                state.imageMarker = false;
                state.imageAltText = true;
                if (modeCfg.highlightFormatting) state.formatting = "image";
                return getType(state);
            }
            if (ch === "]" && state.imageAltText) {
                if (modeCfg.highlightFormatting) state.formatting = "image";
                var type = getType(state);
                state.imageAltText = false;
                state.image = false;
                state.inline = state.f = linkHref;
                return type;
            }
            if (ch === "[" && !state.image) {
                if (state.linkText && stream.match(/^.*?\]/)) return getType(state);
                state.linkText = true;
                if (modeCfg.highlightFormatting) state.formatting = "link";
                return getType(state);
            }
            if (ch === "]" && state.linkText) {
                if (modeCfg.highlightFormatting) state.formatting = "link";
                var type = getType(state);
                state.linkText = false;
                state.inline = state.f = stream.match(/\(.*?\)| ?\[.*?\]/, false) ? linkHref : inlineNormal;
                return type;
            }
            if (ch === "<" && stream.match(/^(https?|ftps?):\/\/(?:[^\\>]|\\.)+>/, false)) {
                state.f = state.inline = linkInline;
                if (modeCfg.highlightFormatting) state.formatting = "link";
                var type = getType(state);
                if (type) {
                    type += " ";
                } else {
                    type = "";
                }
                return type + tokenTypes.linkInline;
            }
            if (ch === "<" && stream.match(/^[^> \\]+@(?:[^\\>]|\\.)+>/, false)) {
                state.f = state.inline = linkInline;
                if (modeCfg.highlightFormatting) state.formatting = "link";
                var type = getType(state);
                if (type) {
                    type += " ";
                } else {
                    type = "";
                }
                return type + tokenTypes.linkEmail;
            }
            if (modeCfg.xml && ch === "<" && stream.match(/^(!--|\?|!\[CDATA\[|[a-z][a-z0-9-]*(?:\s+[a-z_:.\-]+(?:\s*=\s*[^>]+)?)*\s*(?:>|$))/i, false)) {
                var end = stream.string.indexOf(">", stream.pos);
                if (end != -1) {
                    var atts = stream.string.substring(stream.start, end);
                    if (/markdown\s*=\s*('|"){0,1}1('|"){0,1}/.test(atts)) state.md_inside = true;
                }
                stream.backUp(1);
                state.htmlState = CodeMirror1.startState(htmlMode);
                return switchBlock(stream, state, htmlBlock);
            }
            if (modeCfg.xml && ch === "<" && stream.match(/^\/\w*?>/)) {
                state.md_inside = false;
                return "tag";
            } else if (ch === "*" || ch === "_") {
                var len = 1, before = stream.pos == 1 ? " " : stream.string.charAt(stream.pos - 2);
                while(len < 3 && stream.eat(ch))len++;
                var after = stream.peek() || " ";
                // See http://spec.commonmark.org/0.27/#emphasis-and-strong-emphasis
                var leftFlanking = !/\s/.test(after) && (!punctuation.test(after) || /\s/.test(before) || punctuation.test(before));
                var rightFlanking = !/\s/.test(before) && (!punctuation.test(before) || /\s/.test(after) || punctuation.test(after));
                var setEm = null, setStrong = null;
                if (len % 2) {
                    if (!state.em && leftFlanking && (ch === "*" || !rightFlanking || punctuation.test(before))) setEm = true;
                    else if (state.em == ch && rightFlanking && (ch === "*" || !leftFlanking || punctuation.test(after))) setEm = false;
                }
                if (len > 1) {
                    if (!state.strong && leftFlanking && (ch === "*" || !rightFlanking || punctuation.test(before))) setStrong = true;
                    else if (state.strong == ch && rightFlanking && (ch === "*" || !leftFlanking || punctuation.test(after))) setStrong = false;
                }
                if (setStrong != null || setEm != null) {
                    if (modeCfg.highlightFormatting) state.formatting = setEm == null ? "strong" : setStrong == null ? "em" : "strong em";
                    if (setEm === true) state.em = ch;
                    if (setStrong === true) state.strong = ch;
                    var t = getType(state);
                    if (setEm === false) state.em = false;
                    if (setStrong === false) state.strong = false;
                    return t;
                }
            } else if (ch === " ") {
                if (stream.eat("*") || stream.eat("_")) {
                    if (stream.peek() === " ") {
                        return getType(state);
                    } else {
                        stream.backUp(1);
                    }
                }
            }
            if (modeCfg.strikethrough) {
                if (ch === "~" && stream.eatWhile(ch)) {
                    if (state.strikethrough) {
                        if (modeCfg.highlightFormatting) state.formatting = "strikethrough";
                        var t = getType(state);
                        state.strikethrough = false;
                        return t;
                    } else if (stream.match(/^[^\s]/, false)) {
                        state.strikethrough = true;
                        if (modeCfg.highlightFormatting) state.formatting = "strikethrough";
                        return getType(state);
                    }
                } else if (ch === " ") {
                    if (stream.match("~~", true)) {
                        if (stream.peek() === " ") {
                            return getType(state);
                        } else {
                            stream.backUp(2);
                        }
                    }
                }
            }
            if (modeCfg.emoji && ch === ":" && stream.match(/^(?:[a-z_\d+][a-z_\d+-]*|\-[a-z_\d+][a-z_\d+-]*):/)) {
                state.emoji = true;
                if (modeCfg.highlightFormatting) state.formatting = "emoji";
                var retType = getType(state);
                state.emoji = false;
                return retType;
            }
            if (ch === " ") {
                if (stream.match(/^ +$/, false)) {
                    state.trailingSpace++;
                } else if (state.trailingSpace) {
                    state.trailingSpaceNewLine = true;
                }
            }
            return getType(state);
        }
        var linkRE = {
            ")": /^(?:[^\\\(\)]|\\.|\((?:[^\\\(\)]|\\.)*\))*?(?=\))/,
            "]": /^(?:[^\\\[\]]|\\.|\[(?:[^\\\[\]]|\\.)*\])*?(?=\])/
        };
        var mode = {
            startState: function startState() {
                return {
                    f: blockNormal,
                    prevLine: {
                        stream: null
                    },
                    thisLine: {
                        stream: null
                    },
                    block: blockNormal,
                    htmlState: null,
                    indentation: 0,
                    inline: inlineNormal,
                    text: handleText,
                    formatting: false,
                    linkText: false,
                    linkHref: false,
                    linkTitle: false,
                    code: 0,
                    em: false,
                    strong: false,
                    header: 0,
                    setext: 0,
                    hr: false,
                    taskList: false,
                    list: false,
                    listStack: [],
                    quote: 0,
                    trailingSpace: 0,
                    trailingSpaceNewLine: false,
                    strikethrough: false,
                    emoji: false,
                    fencedEndRE: null
                };
            },
            copyState: function copyState(s) {
                return {
                    f: s.f,
                    prevLine: s.prevLine,
                    thisLine: s.thisLine,
                    block: s.block,
                    htmlState: s.htmlState && CodeMirror1.copyState(htmlMode, s.htmlState),
                    indentation: s.indentation,
                    localMode: s.localMode,
                    localState: s.localMode ? CodeMirror1.copyState(s.localMode, s.localState) : null,
                    inline: s.inline,
                    text: s.text,
                    formatting: false,
                    linkText: s.linkText,
                    linkTitle: s.linkTitle,
                    linkHref: s.linkHref,
                    code: s.code,
                    em: s.em,
                    strong: s.strong,
                    strikethrough: s.strikethrough,
                    emoji: s.emoji,
                    header: s.header,
                    setext: s.setext,
                    hr: s.hr,
                    taskList: s.taskList,
                    list: s.list,
                    listStack: s.listStack.slice(0),
                    quote: s.quote,
                    indentedCode: s.indentedCode,
                    trailingSpace: s.trailingSpace,
                    trailingSpaceNewLine: s.trailingSpaceNewLine,
                    md_inside: s.md_inside,
                    fencedEndRE: s.fencedEndRE
                };
            },
            token: function token(stream, state) {
                // Reset state.formatting
                state.formatting = false;
                if (stream != state.thisLine.stream) {
                    state.header = 0;
                    state.hr = false;
                    if (stream.match(/^\s*$/, true)) {
                        blankLine(state);
                        return null;
                    }
                    state.prevLine = state.thisLine;
                    state.thisLine = {
                        stream: stream
                    };
                    // Reset state.taskList
                    state.taskList = false;
                    // Reset state.trailingSpace
                    state.trailingSpace = 0;
                    state.trailingSpaceNewLine = false;
                    if (!state.localState) {
                        state.f = state.block;
                        if (state.f != htmlBlock) {
                            var indentation = stream.match(/^\s*/, true)[0].replace(/\t/g, expandedTab).length;
                            state.indentation = indentation;
                            state.indentationDiff = null;
                            if (indentation > 0) return null;
                        }
                    }
                }
                return state.f(stream, state);
            },
            innerMode: function innerMode(state) {
                if (state.block == htmlBlock) return {
                    state: state.htmlState,
                    mode: htmlMode
                };
                if (state.localState) return {
                    state: state.localState,
                    mode: state.localMode
                };
                return {
                    state: state,
                    mode: mode
                };
            },
            indent: function indent(state, textAfter, line) {
                if (state.block == htmlBlock && htmlMode.indent) return htmlMode.indent(state.htmlState, textAfter, line);
                if (state.localState && state.localMode.indent) return state.localMode.indent(state.localState, textAfter, line);
                return CodeMirror1.Pass;
            },
            blankLine: blankLine,
            getType: getType,
            blockCommentStart: "<!--",
            blockCommentEnd: "-->",
            closeBrackets: "()[]{}''\"\"``",
            fold: "markdown"
        };
        return mode;
    }, "xml");
    CodeMirror1.defineMIME("text/markdown", "markdown");
    CodeMirror1.defineMIME("text/x-markdown", "markdown");
});
});

var nginx = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("nginx", function(config) {
        var words = function words(str) {
            var obj = {}, words = str.split(" ");
            for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
            return obj;
        };
        var ret = function ret(style, tp) {
            type = tp;
            return style;
        };
        var tokenBase = function tokenBase(stream, state) {
            stream.eatWhile(/[\w\$_]/);
            var cur = stream.current();
            if (keywords.propertyIsEnumerable(cur)) {
                return "keyword";
            } else if (keywords_block.propertyIsEnumerable(cur)) {
                return "variable-2";
            } else if (keywords_important.propertyIsEnumerable(cur)) {
                return "string-2";
            }
            /**/ var ch = stream.next();
            if (ch == "@") {
                stream.eatWhile(/[\w\\\-]/);
                return ret("meta", stream.current());
            } else if (ch == "/" && stream.eat("*")) {
                state.tokenize = tokenCComment;
                return tokenCComment(stream, state);
            } else if (ch == "<" && stream.eat("!")) {
                state.tokenize = tokenSGMLComment;
                return tokenSGMLComment(stream, state);
            } else if (ch == "=") ret(null, "compare");
            else if ((ch == "~" || ch == "|") && stream.eat("=")) return ret(null, "compare");
            else if (ch == '"' || ch == "'") {
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            } else if (ch == "#") {
                stream.skipToEnd();
                return ret("comment", "comment");
            } else if (ch == "!") {
                stream.match(/^\s*\w*/);
                return ret("keyword", "important");
            } else if (/\d/.test(ch)) {
                stream.eatWhile(/[\w.%]/);
                return ret("number", "unit");
            } else if (/[,.+>*\/]/.test(ch)) {
                return ret(null, "select-op");
            } else if (/[;{}:\[\]]/.test(ch)) {
                return ret(null, ch);
            } else {
                stream.eatWhile(/[\w\\\-]/);
                return ret("variable", "variable");
            }
        };
        var tokenCComment = function tokenCComment(stream, state) {
            var maybeEnd = false, ch;
            while((ch = stream.next()) != null){
                if (maybeEnd && ch == "/") {
                    state.tokenize = tokenBase;
                    break;
                }
                maybeEnd = ch == "*";
            }
            return ret("comment", "comment");
        };
        var tokenSGMLComment = function tokenSGMLComment(stream, state) {
            var dashes = 0, ch;
            while((ch = stream.next()) != null){
                if (dashes >= 2 && ch == ">") {
                    state.tokenize = tokenBase;
                    break;
                }
                dashes = ch == "-" ? dashes + 1 : 0;
            }
            return ret("comment", "comment");
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, ch;
                while((ch = stream.next()) != null){
                    if (ch == quote && !escaped) break;
                    escaped = !escaped && ch == "\\";
                }
                if (!escaped) state.tokenize = tokenBase;
                return ret("string", "string");
            };
        };
        var keywords = words(/* ngxDirectiveControl */ "break return rewrite set" + /* ngxDirective */ " accept_mutex accept_mutex_delay access_log add_after_body add_before_body add_header addition_types aio alias allow ancient_browser ancient_browser_value auth_basic auth_basic_user_file auth_http auth_http_header auth_http_timeout autoindex autoindex_exact_size autoindex_localtime charset charset_types client_body_buffer_size client_body_in_file_only client_body_in_single_buffer client_body_temp_path client_body_timeout client_header_buffer_size client_header_timeout client_max_body_size connection_pool_size create_full_put_path daemon dav_access dav_methods debug_connection debug_points default_type degradation degrade deny devpoll_changes devpoll_events directio directio_alignment empty_gif env epoll_events error_log eventport_events expires fastcgi_bind fastcgi_buffer_size fastcgi_buffers fastcgi_busy_buffers_size fastcgi_cache fastcgi_cache_key fastcgi_cache_methods fastcgi_cache_min_uses fastcgi_cache_path fastcgi_cache_use_stale fastcgi_cache_valid fastcgi_catch_stderr fastcgi_connect_timeout fastcgi_hide_header fastcgi_ignore_client_abort fastcgi_ignore_headers fastcgi_index fastcgi_intercept_errors fastcgi_max_temp_file_size fastcgi_next_upstream fastcgi_param fastcgi_pass_header fastcgi_pass_request_body fastcgi_pass_request_headers fastcgi_read_timeout fastcgi_send_lowat fastcgi_send_timeout fastcgi_split_path_info fastcgi_store fastcgi_store_access fastcgi_temp_file_write_size fastcgi_temp_path fastcgi_upstream_fail_timeout fastcgi_upstream_max_fails flv geoip_city geoip_country google_perftools_profiles gzip gzip_buffers gzip_comp_level gzip_disable gzip_hash gzip_http_version gzip_min_length gzip_no_buffer gzip_proxied gzip_static gzip_types gzip_vary gzip_window if_modified_since ignore_invalid_headers image_filter image_filter_buffer image_filter_jpeg_quality image_filter_transparency imap_auth imap_capabilities imap_client_buffer index ip_hash keepalive_requests keepalive_timeout kqueue_changes kqueue_events large_client_header_buffers limit_conn limit_conn_log_level limit_rate limit_rate_after limit_req limit_req_log_level limit_req_zone limit_zone lingering_time lingering_timeout lock_file log_format log_not_found log_subrequest map_hash_bucket_size map_hash_max_size master_process memcached_bind memcached_buffer_size memcached_connect_timeout memcached_next_upstream memcached_read_timeout memcached_send_timeout memcached_upstream_fail_timeout memcached_upstream_max_fails merge_slashes min_delete_depth modern_browser modern_browser_value msie_padding msie_refresh multi_accept open_file_cache open_file_cache_errors open_file_cache_events open_file_cache_min_uses open_file_cache_valid open_log_file_cache output_buffers override_charset perl perl_modules perl_require perl_set pid pop3_auth pop3_capabilities port_in_redirect postpone_gzipping postpone_output protocol proxy proxy_bind proxy_buffer proxy_buffer_size proxy_buffering proxy_buffers proxy_busy_buffers_size proxy_cache proxy_cache_key proxy_cache_methods proxy_cache_min_uses proxy_cache_path proxy_cache_use_stale proxy_cache_valid proxy_connect_timeout proxy_headers_hash_bucket_size proxy_headers_hash_max_size proxy_hide_header proxy_ignore_client_abort proxy_ignore_headers proxy_intercept_errors proxy_max_temp_file_size proxy_method proxy_next_upstream proxy_pass_error_message proxy_pass_header proxy_pass_request_body proxy_pass_request_headers proxy_read_timeout proxy_redirect proxy_send_lowat proxy_send_timeout proxy_set_body proxy_set_header proxy_ssl_session_reuse proxy_store proxy_store_access proxy_temp_file_write_size proxy_temp_path proxy_timeout proxy_upstream_fail_timeout proxy_upstream_max_fails random_index read_ahead real_ip_header recursive_error_pages request_pool_size reset_timedout_connection resolver resolver_timeout rewrite_log rtsig_overflow_events rtsig_overflow_test rtsig_overflow_threshold rtsig_signo satisfy secure_link_secret send_lowat send_timeout sendfile sendfile_max_chunk server_name_in_redirect server_names_hash_bucket_size server_names_hash_max_size server_tokens set_real_ip_from smtp_auth smtp_capabilities smtp_client_buffer smtp_greeting_delay so_keepalive source_charset ssi ssi_ignore_recycled_buffers ssi_min_file_chunk ssi_silent_errors ssi_types ssi_value_length ssl ssl_certificate ssl_certificate_key ssl_ciphers ssl_client_certificate ssl_crl ssl_dhparam ssl_engine ssl_prefer_server_ciphers ssl_protocols ssl_session_cache ssl_session_timeout ssl_verify_client ssl_verify_depth starttls stub_status sub_filter sub_filter_once sub_filter_types tcp_nodelay tcp_nopush thread_stack_size timeout timer_resolution types_hash_bucket_size types_hash_max_size underscores_in_headers uninitialized_variable_warn use user userid userid_domain userid_expires userid_mark userid_name userid_p3p userid_path userid_service valid_referers variables_hash_bucket_size variables_hash_max_size worker_connections worker_cpu_affinity worker_priority worker_processes worker_rlimit_core worker_rlimit_nofile worker_rlimit_sigpending worker_threads working_directory xclient xml_entities xslt_stylesheet xslt_typesdrew@li229-23");
        var keywords_block = words(/* ngxDirectiveBlock */ "http mail events server types location upstream charset_map limit_except if geo map");
        var keywords_important = words(/* ngxDirectiveImportant */ "include root server server_name listen internal proxy_pass memcached_pass fastcgi_pass try_files");
        var indentUnit = config.indentUnit, type;
        return {
            startState: function startState(base) {
                return {
                    tokenize: tokenBase,
                    baseIndent: base || 0,
                    stack: []
                };
            },
            token: function token(stream, state) {
                if (stream.eatSpace()) return null;
                type = null;
                var style = state.tokenize(stream, state);
                var context = state.stack[state.stack.length - 1];
                if (type == "hash" && context == "rule") style = "atom";
                else if (style == "variable") {
                    if (context == "rule") style = "number";
                    else if (!context || context == "@media{") style = "tag";
                }
                if (context == "rule" && /^[\{\};]$/.test(type)) state.stack.pop();
                if (type == "{") {
                    if (context == "@media") state.stack[state.stack.length - 1] = "@media{";
                    else state.stack.push("{");
                } else if (type == "}") state.stack.pop();
                else if (type == "@media") state.stack.push("@media");
                else if (context == "{" && type != "comment") state.stack.push("rule");
                return style;
            },
            indent: function indent(state, textAfter) {
                var n = state.stack.length;
                if (/^\}/.test(textAfter)) n -= state.stack[state.stack.length - 1] == "rule" ? 2 : 1;
                return state.baseIndent + n * indentUnit;
            },
            electricChars: "}"
        };
    });
    CodeMirror1.defineMIME("text/x-nginx-conf", "nginx");
});
});

var perl = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// CodeMirror2 mode/perl/perl.js (text/x-perl) beta 0.10 (2011-11-08)
// This is a part of CodeMirror from https://github.com/sabaca/CodeMirror_mode_perl (mail@sabaca.com)
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var look = // it's like "peek", but need for look-ahead or look-behind if index < 0
    function look(stream, c) {
        return stream.string.charAt(stream.pos + (c || 0));
    };
    var prefix = // return a part of prefix of current stream from current position
    function prefix(stream, c) {
        if (c) {
            var x = stream.pos - c;
            return stream.string.substr(x >= 0 ? x : 0, c);
        } else {
            return stream.string.substr(0, stream.pos - 1);
        }
    };
    var suffix = // return a part of suffix of current stream from current position
    function suffix(stream, c) {
        var y = stream.string.length;
        var x = y - stream.pos + 1;
        return stream.string.substr(stream.pos, c && c < y ? c : x);
    };
    var eatSuffix = // eating and vomiting a part of stream from current position
    function eatSuffix(stream, c) {
        var x = stream.pos + c;
        var y;
        if (x <= 0) stream.pos = 0;
        else if (x >= (y = stream.string.length - 1)) stream.pos = y;
        else stream.pos = x;
    };
    CodeMirror1.defineMode("perl", function() {
        var tokenChain = function tokenChain(stream, state, chain, style, tail) {
            state.chain = null; //                                                          12   3tail
            state.style = null;
            state.tail = null;
            state.tokenize = function(stream, state) {
                var e = false, c, i = 0;
                while(c = stream.next()){
                    if (c === chain[i] && !e) {
                        if (chain[++i] !== undefined) {
                            state.chain = chain[i];
                            state.style = style;
                            state.tail = tail;
                        } else if (tail) stream.eatWhile(tail);
                        state.tokenize = tokenPerl;
                        return style;
                    }
                    e = !e && c == "\\";
                }
                return style;
            };
            return state.tokenize(stream, state);
        };
        var tokenSOMETHING = function tokenSOMETHING(stream, state, string) {
            state.tokenize = function(stream, state) {
                if (stream.string == string) state.tokenize = tokenPerl;
                stream.skipToEnd();
                return "string";
            };
            return state.tokenize(stream, state);
        };
        var tokenPerl = function tokenPerl(stream, state) {
            if (stream.eatSpace()) return null;
            if (state.chain) return tokenChain(stream, state, state.chain, state.style, state.tail);
            if (stream.match(/^\-?[\d\.]/, false)) {
                if (stream.match(/^(\-?(\d*\.\d+(e[+-]?\d+)?|\d+\.\d*)|0x[\da-fA-F]+|0b[01]+|\d+(e[+-]?\d+)?)/)) return "number";
            }
            if (stream.match(/^<<(?=[_a-zA-Z])/)) {
                stream.eatWhile(/\w/);
                return tokenSOMETHING(stream, state, stream.current().substr(2));
            }
            if (stream.sol() && stream.match(/^\=item(?!\w)/)) {
                return tokenSOMETHING(stream, state, "=cut");
            }
            var ch = stream.next();
            if (ch == '"' || ch == "'") {
                if (prefix(stream, 3) == "<<" + ch) {
                    var p = stream.pos;
                    stream.eatWhile(/\w/);
                    var n = stream.current().substr(1);
                    if (n && stream.eat(ch)) return tokenSOMETHING(stream, state, n);
                    stream.pos = p;
                }
                return tokenChain(stream, state, [
                    ch
                ], "string");
            }
            if (ch == "q") {
                var c = look(stream, -2);
                if (!(c && /\w/.test(c))) {
                    c = look(stream, 0);
                    if (c == "x") {
                        c = look(stream, 1);
                        if (c == "(") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ")"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "[") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "]"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "{") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "}"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "<") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ">"
                            ], RXstyle, RXmodifiers);
                        }
                        if (/[\^'"!~\/]/.test(c)) {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                stream.eat(c)
                            ], RXstyle, RXmodifiers);
                        }
                    } else if (c == "q") {
                        c = look(stream, 1);
                        if (c == "(") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ")"
                            ], "string");
                        }
                        if (c == "[") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "]"
                            ], "string");
                        }
                        if (c == "{") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "}"
                            ], "string");
                        }
                        if (c == "<") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ">"
                            ], "string");
                        }
                        if (/[\^'"!~\/]/.test(c)) {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                stream.eat(c)
                            ], "string");
                        }
                    } else if (c == "w") {
                        c = look(stream, 1);
                        if (c == "(") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ")"
                            ], "bracket");
                        }
                        if (c == "[") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "]"
                            ], "bracket");
                        }
                        if (c == "{") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "}"
                            ], "bracket");
                        }
                        if (c == "<") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ">"
                            ], "bracket");
                        }
                        if (/[\^'"!~\/]/.test(c)) {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                stream.eat(c)
                            ], "bracket");
                        }
                    } else if (c == "r") {
                        c = look(stream, 1);
                        if (c == "(") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ")"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "[") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "]"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "{") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                "}"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "<") {
                            eatSuffix(stream, 2);
                            return tokenChain(stream, state, [
                                ">"
                            ], RXstyle, RXmodifiers);
                        }
                        if (/[\^'"!~\/]/.test(c)) {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                stream.eat(c)
                            ], RXstyle, RXmodifiers);
                        }
                    } else if (/[\^'"!~\/(\[{<]/.test(c)) {
                        if (c == "(") {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                ")"
                            ], "string");
                        }
                        if (c == "[") {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                "]"
                            ], "string");
                        }
                        if (c == "{") {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                "}"
                            ], "string");
                        }
                        if (c == "<") {
                            eatSuffix(stream, 1);
                            return tokenChain(stream, state, [
                                ">"
                            ], "string");
                        }
                        if (/[\^'"!~\/]/.test(c)) {
                            return tokenChain(stream, state, [
                                stream.eat(c)
                            ], "string");
                        }
                    }
                }
            }
            if (ch == "m") {
                var c = look(stream, -2);
                if (!(c && /\w/.test(c))) {
                    c = stream.eat(/[(\[{<\^'"!~\/]/);
                    if (c) {
                        if (/[\^'"!~\/]/.test(c)) {
                            return tokenChain(stream, state, [
                                c
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "(") {
                            return tokenChain(stream, state, [
                                ")"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "[") {
                            return tokenChain(stream, state, [
                                "]"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "{") {
                            return tokenChain(stream, state, [
                                "}"
                            ], RXstyle, RXmodifiers);
                        }
                        if (c == "<") {
                            return tokenChain(stream, state, [
                                ">"
                            ], RXstyle, RXmodifiers);
                        }
                    }
                }
            }
            if (ch == "s") {
                var c = /[\/>\]})\w]/.test(look(stream, -2));
                if (!c) {
                    c = stream.eat(/[(\[{<\^'"!~\/]/);
                    if (c) {
                        if (c == "[") return tokenChain(stream, state, [
                            "]",
                            "]"
                        ], RXstyle, RXmodifiers);
                        if (c == "{") return tokenChain(stream, state, [
                            "}",
                            "}"
                        ], RXstyle, RXmodifiers);
                        if (c == "<") return tokenChain(stream, state, [
                            ">",
                            ">"
                        ], RXstyle, RXmodifiers);
                        if (c == "(") return tokenChain(stream, state, [
                            ")",
                            ")"
                        ], RXstyle, RXmodifiers);
                        return tokenChain(stream, state, [
                            c,
                            c
                        ], RXstyle, RXmodifiers);
                    }
                }
            }
            if (ch == "y") {
                var c = /[\/>\]})\w]/.test(look(stream, -2));
                if (!c) {
                    c = stream.eat(/[(\[{<\^'"!~\/]/);
                    if (c) {
                        if (c == "[") return tokenChain(stream, state, [
                            "]",
                            "]"
                        ], RXstyle, RXmodifiers);
                        if (c == "{") return tokenChain(stream, state, [
                            "}",
                            "}"
                        ], RXstyle, RXmodifiers);
                        if (c == "<") return tokenChain(stream, state, [
                            ">",
                            ">"
                        ], RXstyle, RXmodifiers);
                        if (c == "(") return tokenChain(stream, state, [
                            ")",
                            ")"
                        ], RXstyle, RXmodifiers);
                        return tokenChain(stream, state, [
                            c,
                            c
                        ], RXstyle, RXmodifiers);
                    }
                }
            }
            if (ch == "t") {
                var c = /[\/>\]})\w]/.test(look(stream, -2));
                if (!c) {
                    c = stream.eat("r");
                    if (c) {
                        c = stream.eat(/[(\[{<\^'"!~\/]/);
                        if (c) {
                            if (c == "[") return tokenChain(stream, state, [
                                "]",
                                "]"
                            ], RXstyle, RXmodifiers);
                            if (c == "{") return tokenChain(stream, state, [
                                "}",
                                "}"
                            ], RXstyle, RXmodifiers);
                            if (c == "<") return tokenChain(stream, state, [
                                ">",
                                ">"
                            ], RXstyle, RXmodifiers);
                            if (c == "(") return tokenChain(stream, state, [
                                ")",
                                ")"
                            ], RXstyle, RXmodifiers);
                            return tokenChain(stream, state, [
                                c,
                                c
                            ], RXstyle, RXmodifiers);
                        }
                    }
                }
            }
            if (ch == "`") {
                return tokenChain(stream, state, [
                    ch
                ], "variable-2");
            }
            if (ch == "/") {
                if (!/~\s*$/.test(prefix(stream))) return "operator";
                else return tokenChain(stream, state, [
                    ch
                ], RXstyle, RXmodifiers);
            }
            if (ch == "$") {
                var p = stream.pos;
                if (stream.eatWhile(/\d/) || stream.eat("{") && stream.eatWhile(/\d/) && stream.eat("}")) return "variable-2";
                else stream.pos = p;
            }
            if (/[$@%]/.test(ch)) {
                var p = stream.pos;
                if (stream.eat("^") && stream.eat(/[A-Z]/) || !/[@$%&]/.test(look(stream, -2)) && stream.eat(/[=|\\\-#?@;:&`~\^!\[\]*'"$+.,\/<>()]/)) {
                    var c = stream.current();
                    if (PERL[c]) return "variable-2";
                }
                stream.pos = p;
            }
            if (/[$@%&]/.test(ch)) {
                if (stream.eatWhile(/[\w$]/) || stream.eat("{") && stream.eatWhile(/[\w$]/) && stream.eat("}")) {
                    var c = stream.current();
                    if (PERL[c]) return "variable-2";
                    else return "variable";
                }
            }
            if (ch == "#") {
                if (look(stream, -2) != "$") {
                    stream.skipToEnd();
                    return "comment";
                }
            }
            if (/[:+\-\^*$&%@=<>!?|\/~\.]/.test(ch)) {
                var p = stream.pos;
                stream.eatWhile(/[:+\-\^*$&%@=<>!?|\/~\.]/);
                if (PERL[stream.current()]) return "operator";
                else stream.pos = p;
            }
            if (ch == "_") {
                if (stream.pos == 1) {
                    if (suffix(stream, 6) == "_END__") {
                        return tokenChain(stream, state, [
                            "\0"
                        ], "comment");
                    } else if (suffix(stream, 7) == "_DATA__") {
                        return tokenChain(stream, state, [
                            "\0"
                        ], "variable-2");
                    } else if (suffix(stream, 7) == "_C__") {
                        return tokenChain(stream, state, [
                            "\0"
                        ], "string");
                    }
                }
            }
            if (/\w/.test(ch)) {
                var p = stream.pos;
                if (look(stream, -2) == "{" && (look(stream, 0) == "}" || stream.eatWhile(/\w/) && look(stream, 0) == "}")) return "string";
                else stream.pos = p;
            }
            if (/[A-Z]/.test(ch)) {
                var l = look(stream, -2);
                var p = stream.pos;
                stream.eatWhile(/[A-Z_]/);
                if (/[\da-z]/.test(look(stream, 0))) {
                    stream.pos = p;
                } else {
                    var c = PERL[stream.current()];
                    if (!c) return "meta";
                    if (c[1]) c = c[0];
                    if (l != ":") {
                        if (c == 1) return "keyword";
                        else if (c == 2) return "def";
                        else if (c == 3) return "atom";
                        else if (c == 4) return "operator";
                        else if (c == 5) return "variable-2";
                        else return "meta";
                    } else return "meta";
                }
            }
            if (/[a-zA-Z_]/.test(ch)) {
                var l = look(stream, -2);
                stream.eatWhile(/\w/);
                var c = PERL[stream.current()];
                if (!c) return "meta";
                if (c[1]) c = c[0];
                if (l != ":") {
                    if (c == 1) return "keyword";
                    else if (c == 2) return "def";
                    else if (c == 3) return "atom";
                    else if (c == 4) return "operator";
                    else if (c == 5) return "variable-2";
                    else return "meta";
                } else return "meta";
            }
            return null;
        };
        // http://perldoc.perl.org
        var PERL = {
            //   1 - keyword
            //   2 - def
            //   3 - atom
            //   4 - operator
            //   5 - variable-2 (predefined)
            //   [x,y] - x=1,2,3; y=must be defined if x{...}
            //      PERL operators
            "->": 4,
            "++": 4,
            "--": 4,
            "**": 4,
            //   ! ~ \ and unary + and -
            "=~": 4,
            "!~": 4,
            "*": 4,
            "/": 4,
            "%": 4,
            "x": 4,
            "+": 4,
            "-": 4,
            ".": 4,
            "<<": 4,
            ">>": 4,
            //   named unary operators
            "<": 4,
            ">": 4,
            "<=": 4,
            ">=": 4,
            "lt": 4,
            "gt": 4,
            "le": 4,
            "ge": 4,
            "==": 4,
            "!=": 4,
            "<=>": 4,
            "eq": 4,
            "ne": 4,
            "cmp": 4,
            "~~": 4,
            "&": 4,
            "|": 4,
            "^": 4,
            "&&": 4,
            "||": 4,
            "//": 4,
            "..": 4,
            "...": 4,
            "?": 4,
            ":": 4,
            "=": 4,
            "+=": 4,
            "-=": 4,
            "*=": 4,
            ",": 4,
            "=>": 4,
            "::": 4,
            //   list operators (rightward)
            "not": 4,
            "and": 4,
            "or": 4,
            "xor": 4,
            //      PERL predefined variables (I know, what this is a paranoid idea, but may be needed for people, who learn PERL, and for me as well, ...and may be for you?;)
            "BEGIN": [
                5,
                1
            ],
            "END": [
                5,
                1
            ],
            "PRINT": [
                5,
                1
            ],
            "PRINTF": [
                5,
                1
            ],
            "GETC": [
                5,
                1
            ],
            "READ": [
                5,
                1
            ],
            "READLINE": [
                5,
                1
            ],
            "DESTROY": [
                5,
                1
            ],
            "TIE": [
                5,
                1
            ],
            "TIEHANDLE": [
                5,
                1
            ],
            "UNTIE": [
                5,
                1
            ],
            "STDIN": 5,
            "STDIN_TOP": 5,
            "STDOUT": 5,
            "STDOUT_TOP": 5,
            "STDERR": 5,
            "STDERR_TOP": 5,
            "$ARG": 5,
            "$_": 5,
            "@ARG": 5,
            "@_": 5,
            "$LIST_SEPARATOR": 5,
            '$"': 5,
            "$PROCESS_ID": 5,
            "$PID": 5,
            "$$": 5,
            "$REAL_GROUP_ID": 5,
            "$GID": 5,
            "$(": 5,
            "$EFFECTIVE_GROUP_ID": 5,
            "$EGID": 5,
            "$)": 5,
            "$PROGRAM_NAME": 5,
            "$0": 5,
            "$SUBSCRIPT_SEPARATOR": 5,
            "$SUBSEP": 5,
            "$;": 5,
            "$REAL_USER_ID": 5,
            "$UID": 5,
            "$<": 5,
            "$EFFECTIVE_USER_ID": 5,
            "$EUID": 5,
            "$>": 5,
            "$a": 5,
            "$b": 5,
            "$COMPILING": 5,
            "$^C": 5,
            "$DEBUGGING": 5,
            "$^D": 5,
            "${^ENCODING}": 5,
            "$ENV": 5,
            "%ENV": 5,
            "$SYSTEM_FD_MAX": 5,
            "$^F": 5,
            "@F": 5,
            "${^GLOBAL_PHASE}": 5,
            "$^H": 5,
            "%^H": 5,
            "@INC": 5,
            "%INC": 5,
            "$INPLACE_EDIT": 5,
            "$^I": 5,
            "$^M": 5,
            "$OSNAME": 5,
            "$^O": 5,
            "${^OPEN}": 5,
            "$PERLDB": 5,
            "$^P": 5,
            "$SIG": 5,
            "%SIG": 5,
            "$BASETIME": 5,
            "$^T": 5,
            "${^TAINT}": 5,
            "${^UNICODE}": 5,
            "${^UTF8CACHE}": 5,
            "${^UTF8LOCALE}": 5,
            "$PERL_VERSION": 5,
            "$^V": 5,
            "${^WIN32_SLOPPY_STAT}": 5,
            "$EXECUTABLE_NAME": 5,
            "$^X": 5,
            "$1": 5,
            "$MATCH": 5,
            "$&": 5,
            "${^MATCH}": 5,
            "$PREMATCH": 5,
            "$`": 5,
            "${^PREMATCH}": 5,
            "$POSTMATCH": 5,
            "$'": 5,
            "${^POSTMATCH}": 5,
            "$LAST_PAREN_MATCH": 5,
            "$+": 5,
            "$LAST_SUBMATCH_RESULT": 5,
            "$^N": 5,
            "@LAST_MATCH_END": 5,
            "@+": 5,
            "%LAST_PAREN_MATCH": 5,
            "%+": 5,
            "@LAST_MATCH_START": 5,
            "@-": 5,
            "%LAST_MATCH_START": 5,
            "%-": 5,
            "$LAST_REGEXP_CODE_RESULT": 5,
            "$^R": 5,
            "${^RE_DEBUG_FLAGS}": 5,
            "${^RE_TRIE_MAXBUF}": 5,
            "$ARGV": 5,
            "@ARGV": 5,
            "ARGV": 5,
            "ARGVOUT": 5,
            "$OUTPUT_FIELD_SEPARATOR": 5,
            "$OFS": 5,
            "$,": 5,
            "$INPUT_LINE_NUMBER": 5,
            "$NR": 5,
            "$.": 5,
            "$INPUT_RECORD_SEPARATOR": 5,
            "$RS": 5,
            "$/": 5,
            "$OUTPUT_RECORD_SEPARATOR": 5,
            "$ORS": 5,
            "$\\": 5,
            "$OUTPUT_AUTOFLUSH": 5,
            "$|": 5,
            "$ACCUMULATOR": 5,
            "$^A": 5,
            "$FORMAT_FORMFEED": 5,
            "$^L": 5,
            "$FORMAT_PAGE_NUMBER": 5,
            "$%": 5,
            "$FORMAT_LINES_LEFT": 5,
            "$-": 5,
            "$FORMAT_LINE_BREAK_CHARACTERS": 5,
            "$:": 5,
            "$FORMAT_LINES_PER_PAGE": 5,
            "$=": 5,
            "$FORMAT_TOP_NAME": 5,
            "$^": 5,
            "$FORMAT_NAME": 5,
            "$~": 5,
            "${^CHILD_ERROR_NATIVE}": 5,
            "$EXTENDED_OS_ERROR": 5,
            "$^E": 5,
            "$EXCEPTIONS_BEING_CAUGHT": 5,
            "$^S": 5,
            "$WARNING": 5,
            "$^W": 5,
            "${^WARNING_BITS}": 5,
            "$OS_ERROR": 5,
            "$ERRNO": 5,
            "$!": 5,
            "%OS_ERROR": 5,
            "%ERRNO": 5,
            "%!": 5,
            "$CHILD_ERROR": 5,
            "$?": 5,
            "$EVAL_ERROR": 5,
            "$@": 5,
            "$OFMT": 5,
            "$#": 5,
            "$*": 5,
            "$ARRAY_BASE": 5,
            "$[": 5,
            "$OLD_PERL_VERSION": 5,
            "$]": 5,
            //      PERL blocks
            "if": [
                1,
                1
            ],
            elsif: [
                1,
                1
            ],
            "else": [
                1,
                1
            ],
            "while": [
                1,
                1
            ],
            unless: [
                1,
                1
            ],
            "for": [
                1,
                1
            ],
            foreach: [
                1,
                1
            ],
            //      PERL functions
            "abs": 1,
            accept: 1,
            alarm: 1,
            "atan2": 1,
            bind: 1,
            binmode: 1,
            bless: 1,
            bootstrap: 1,
            "break": 1,
            caller: 1,
            chdir: 1,
            chmod: 1,
            chomp: 1,
            chop: 1,
            chown: 1,
            chr: 1,
            chroot: 1,
            close: 1,
            closedir: 1,
            connect: 1,
            "continue": [
                1,
                1
            ],
            "cos": 1,
            crypt: 1,
            dbmclose: 1,
            dbmopen: 1,
            "default": 1,
            defined: 1,
            "delete": 1,
            die: 1,
            "do": 1,
            dump: 1,
            each: 1,
            endgrent: 1,
            endhostent: 1,
            endnetent: 1,
            endprotoent: 1,
            endpwent: 1,
            endservent: 1,
            eof: 1,
            "eval": 1,
            "exec": 1,
            exists: 1,
            exit: 1,
            "exp": 1,
            fcntl: 1,
            fileno: 1,
            flock: 1,
            fork: 1,
            format: 1,
            formline: 1,
            getc: 1,
            getgrent: 1,
            getgrgid: 1,
            getgrnam: 1,
            gethostbyaddr: 1,
            gethostbyname: 1,
            gethostent: 1,
            getlogin: 1,
            getnetbyaddr: 1,
            getnetbyname: 1,
            getnetent: 1,
            getpeername: 1,
            getpgrp: 1,
            getppid: 1,
            getpriority: 1,
            getprotobyname: 1,
            getprotobynumber: 1,
            getprotoent: 1,
            getpwent: 1,
            getpwnam: 1,
            getpwuid: 1,
            getservbyname: 1,
            getservbyport: 1,
            getservent: 1,
            getsockname: 1,
            getsockopt: 1,
            given: 1,
            glob: 1,
            gmtime: 1,
            "goto": 1,
            grep: 1,
            hex: 1,
            "import": 1,
            index: 1,
            "int": 1,
            ioctl: 1,
            "join": 1,
            keys: 1,
            kill: 1,
            last: 1,
            lc: 1,
            lcfirst: 1,
            length: 1,
            "link": 1,
            listen: 1,
            local: 2,
            localtime: 1,
            lock: 1,
            "log": 1,
            lstat: 1,
            m: null,
            map: 1,
            mkdir: 1,
            msgctl: 1,
            msgget: 1,
            msgrcv: 1,
            msgsnd: 1,
            my: 2,
            "new": 1,
            next: 1,
            no: 1,
            oct: 1,
            open: 1,
            opendir: 1,
            ord: 1,
            our: 2,
            pack: 1,
            "package": 1,
            pipe: 1,
            pop: 1,
            pos: 1,
            print: 1,
            printf: 1,
            prototype: 1,
            push: 1,
            q: null,
            qq: null,
            qr: null,
            quotemeta: null,
            qw: null,
            qx: null,
            rand: 1,
            read: 1,
            readdir: 1,
            readline: 1,
            readlink: 1,
            readpipe: 1,
            recv: 1,
            redo: 1,
            ref: 1,
            rename: 1,
            require: 1,
            reset: 1,
            "return": 1,
            reverse: 1,
            rewinddir: 1,
            rindex: 1,
            rmdir: 1,
            s: null,
            say: 1,
            scalar: 1,
            seek: 1,
            seekdir: 1,
            select: 1,
            semctl: 1,
            semget: 1,
            semop: 1,
            send: 1,
            setgrent: 1,
            sethostent: 1,
            setnetent: 1,
            setpgrp: 1,
            setpriority: 1,
            setprotoent: 1,
            setpwent: 1,
            setservent: 1,
            setsockopt: 1,
            shift: 1,
            shmctl: 1,
            shmget: 1,
            shmread: 1,
            shmwrite: 1,
            shutdown: 1,
            "sin": 1,
            sleep: 1,
            socket: 1,
            socketpair: 1,
            "sort": 1,
            splice: 1,
            "split": 1,
            sprintf: 1,
            "sqrt": 1,
            srand: 1,
            stat: 1,
            state: 1,
            study: 1,
            "sub": 1,
            "substr": 1,
            symlink: 1,
            syscall: 1,
            sysopen: 1,
            sysread: 1,
            sysseek: 1,
            system: 1,
            syswrite: 1,
            tell: 1,
            telldir: 1,
            tie: 1,
            tied: 1,
            time: 1,
            times: 1,
            tr: null,
            truncate: 1,
            uc: 1,
            ucfirst: 1,
            umask: 1,
            undef: 1,
            unlink: 1,
            unpack: 1,
            unshift: 1,
            untie: 1,
            use: 1,
            utime: 1,
            values: 1,
            vec: 1,
            wait: 1,
            waitpid: 1,
            wantarray: 1,
            warn: 1,
            when: 1,
            write: 1,
            y: null
        }; // - transliterate a string
        var RXstyle = "string-2";
        var RXmodifiers = /[goseximacplud]/; // NOTE: "m", "s", "y" and "tr" need to correct real modifiers for each regexp type
        return {
            startState: function startState() {
                return {
                    tokenize: tokenPerl,
                    chain: null,
                    style: null,
                    tail: null
                };
            },
            token: function token(stream, state) {
                return (state.tokenize || tokenPerl)(stream, state);
            },
            lineComment: "#"
        };
    });
    CodeMirror1.registerHelper("wordChars", "perl", /[\w$]/);
    CodeMirror1.defineMIME("text/x-perl", "perl");
});
});

var php = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, htmlmixed, clike);
})(function(CodeMirror1) {
    var keywords = function keywords(str) {
        var obj = {}, words = str.split(" ");
        for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
        return obj;
    };
    var phpString = function phpString(closing, escapes) {
        return function(stream, state) {
            return phpString_(stream, state, closing, escapes);
        };
    };
    var phpString_ = function phpString_(stream, state, closing, escapes) {
        // "Complex" syntax
        if (escapes !== false && stream.match("${", false) || stream.match("{$", false)) {
            state.tokenize = null;
            return "string";
        }
        // Simple syntax
        if (escapes !== false && stream.match(/^\$[a-zA-Z_][a-zA-Z0-9_]*/)) {
            // After the variable name there may appear array or object operator.
            if (stream.match("[", false)) {
                // Match array operator
                state.tokenize = matchSequence([
                    [
                        [
                            "[",
                            null
                        ]
                    ],
                    [
                        [
                            /\d[\w\.]*/,
                            "number"
                        ],
                        [
                            /\$[a-zA-Z_][a-zA-Z0-9_]*/,
                            "variable-2"
                        ],
                        [
                            /[\w\$]+/,
                            "variable"
                        ]
                    ],
                    [
                        [
                            "]",
                            null
                        ]
                    ]
                ], closing, escapes);
            }
            if (stream.match(/^->\w/, false)) {
                // Match object operator
                state.tokenize = matchSequence([
                    [
                        [
                            "->",
                            null
                        ]
                    ],
                    [
                        [
                            /[\w]+/,
                            "variable"
                        ]
                    ]
                ], closing, escapes);
            }
            return "variable-2";
        }
        var escaped = false;
        // Normal string
        while(!stream.eol() && (escaped || escapes === false || !stream.match("{$", false) && !stream.match(/^(\$[a-zA-Z_][a-zA-Z0-9_]*|\$\{)/, false))){
            if (!escaped && stream.match(closing)) {
                state.tokenize = null;
                state.tokStack.pop();
                state.tokStack.pop();
                break;
            }
            escaped = stream.next() == "\\" && !escaped;
        }
        return "string";
    };
    // Helper for phpString
    function matchSequence(list, end, escapes) {
        if (list.length == 0) return phpString(end);
        return function(stream, state) {
            var patterns = list[0];
            for(var i = 0; i < patterns.length; i++)if (stream.match(patterns[i][0])) {
                state.tokenize = matchSequence(list.slice(1), end);
                return patterns[i][1];
            }
            state.tokenize = phpString(end, escapes);
            return "string";
        };
    }
    var phpKeywords = "abstract and array as break case catch class clone const continue declare default " + "do else elseif enddeclare endfor endforeach endif endswitch endwhile enum extends final " + "for foreach function global goto if implements interface instanceof namespace " + "new or private protected public static switch throw trait try use var while xor " + "die echo empty exit eval include include_once isset list require require_once return " + "print unset __halt_compiler self static parent yield insteadof finally readonly match";
    var phpAtoms = "true false null TRUE FALSE NULL __CLASS__ __DIR__ __FILE__ __LINE__ __METHOD__ __FUNCTION__ __NAMESPACE__ __TRAIT__";
    var phpBuiltin = "func_num_args func_get_arg func_get_args strlen strcmp strncmp strcasecmp strncasecmp each error_reporting define defined trigger_error user_error set_error_handler restore_error_handler get_declared_classes get_loaded_extensions extension_loaded get_extension_funcs debug_backtrace constant bin2hex hex2bin sleep usleep time mktime gmmktime strftime gmstrftime strtotime date gmdate getdate localtime checkdate flush wordwrap htmlspecialchars htmlentities html_entity_decode md5 md5_file crc32 getimagesize image_type_to_mime_type phpinfo phpversion phpcredits strnatcmp strnatcasecmp substr_count strspn strcspn strtok strtoupper strtolower strpos strrpos strrev hebrev hebrevc nl2br basename dirname pathinfo stripslashes stripcslashes strstr stristr strrchr str_shuffle str_word_count strcoll substr substr_replace quotemeta ucfirst ucwords strtr addslashes addcslashes rtrim str_replace str_repeat count_chars chunk_split trim ltrim strip_tags similar_text explode implode setlocale localeconv parse_str str_pad chop strchr sprintf printf vprintf vsprintf sscanf fscanf parse_url urlencode urldecode rawurlencode rawurldecode readlink linkinfo link unlink exec system escapeshellcmd escapeshellarg passthru shell_exec proc_open proc_close rand srand getrandmax mt_rand mt_srand mt_getrandmax base64_decode base64_encode abs ceil floor round is_finite is_nan is_infinite bindec hexdec octdec decbin decoct dechex base_convert number_format fmod ip2long long2ip getenv putenv getopt microtime gettimeofday getrusage uniqid quoted_printable_decode set_time_limit get_cfg_var magic_quotes_runtime set_magic_quotes_runtime get_magic_quotes_gpc get_magic_quotes_runtime import_request_variables error_log serialize unserialize memory_get_usage memory_get_peak_usage var_dump var_export debug_zval_dump print_r highlight_file show_source highlight_string ini_get ini_get_all ini_set ini_alter ini_restore get_include_path set_include_path restore_include_path setcookie header headers_sent connection_aborted connection_status ignore_user_abort parse_ini_file is_uploaded_file move_uploaded_file intval floatval doubleval strval gettype settype is_null is_resource is_bool is_long is_float is_int is_integer is_double is_real is_numeric is_string is_array is_object is_scalar ereg ereg_replace eregi eregi_replace split spliti join sql_regcase dl pclose popen readfile rewind rmdir umask fclose feof fgetc fgets fgetss fread fopen fpassthru ftruncate fstat fseek ftell fflush fwrite fputs mkdir rename copy tempnam tmpfile file file_get_contents file_put_contents stream_select stream_context_create stream_context_set_params stream_context_set_option stream_context_get_options stream_filter_prepend stream_filter_append fgetcsv flock get_meta_tags stream_set_write_buffer set_file_buffer set_socket_blocking stream_set_blocking socket_set_blocking stream_get_meta_data stream_register_wrapper stream_wrapper_register stream_set_timeout socket_set_timeout socket_get_status realpath fnmatch fsockopen pfsockopen pack unpack get_browser crypt opendir closedir chdir getcwd rewinddir readdir dir glob fileatime filectime filegroup fileinode filemtime fileowner fileperms filesize filetype file_exists is_writable is_writeable is_readable is_executable is_file is_dir is_link stat lstat chown touch clearstatcache mail ob_start ob_flush ob_clean ob_end_flush ob_end_clean ob_get_flush ob_get_clean ob_get_length ob_get_level ob_get_status ob_get_contents ob_implicit_flush ob_list_handlers ksort krsort natsort natcasesort asort arsort sort rsort usort uasort uksort shuffle array_walk count end prev next reset current key min max in_array array_search extract compact array_fill range array_multisort array_push array_pop array_shift array_unshift array_splice array_slice array_merge array_merge_recursive array_keys array_values array_count_values array_reverse array_reduce array_pad array_flip array_change_key_case array_rand array_unique array_intersect array_intersect_assoc array_diff array_diff_assoc array_sum array_filter array_map array_chunk array_key_exists array_intersect_key array_combine array_column pos sizeof key_exists assert assert_options version_compare ftok str_rot13 aggregate session_name session_module_name session_save_path session_id session_regenerate_id session_decode session_register session_unregister session_is_registered session_encode session_start session_destroy session_unset session_set_save_handler session_cache_limiter session_cache_expire session_set_cookie_params session_get_cookie_params session_write_close preg_match preg_match_all preg_replace preg_replace_callback preg_split preg_quote preg_grep overload ctype_alnum ctype_alpha ctype_cntrl ctype_digit ctype_lower ctype_graph ctype_print ctype_punct ctype_space ctype_upper ctype_xdigit virtual apache_request_headers apache_note apache_lookup_uri apache_child_terminate apache_setenv apache_response_headers apache_get_version getallheaders mysql_connect mysql_pconnect mysql_close mysql_select_db mysql_create_db mysql_drop_db mysql_query mysql_unbuffered_query mysql_db_query mysql_list_dbs mysql_list_tables mysql_list_fields mysql_list_processes mysql_error mysql_errno mysql_affected_rows mysql_insert_id mysql_result mysql_num_rows mysql_num_fields mysql_fetch_row mysql_fetch_array mysql_fetch_assoc mysql_fetch_object mysql_data_seek mysql_fetch_lengths mysql_fetch_field mysql_field_seek mysql_free_result mysql_field_name mysql_field_table mysql_field_len mysql_field_type mysql_field_flags mysql_escape_string mysql_real_escape_string mysql_stat mysql_thread_id mysql_client_encoding mysql_get_client_info mysql_get_host_info mysql_get_proto_info mysql_get_server_info mysql_info mysql mysql_fieldname mysql_fieldtable mysql_fieldlen mysql_fieldtype mysql_fieldflags mysql_selectdb mysql_createdb mysql_dropdb mysql_freeresult mysql_numfields mysql_numrows mysql_listdbs mysql_listtables mysql_listfields mysql_db_name mysql_dbname mysql_tablename mysql_table_name pg_connect pg_pconnect pg_close pg_connection_status pg_connection_busy pg_connection_reset pg_host pg_dbname pg_port pg_tty pg_options pg_ping pg_query pg_send_query pg_cancel_query pg_fetch_result pg_fetch_row pg_fetch_assoc pg_fetch_array pg_fetch_object pg_fetch_all pg_affected_rows pg_get_result pg_result_seek pg_result_status pg_free_result pg_last_oid pg_num_rows pg_num_fields pg_field_name pg_field_num pg_field_size pg_field_type pg_field_prtlen pg_field_is_null pg_get_notify pg_get_pid pg_result_error pg_last_error pg_last_notice pg_put_line pg_end_copy pg_copy_to pg_copy_from pg_trace pg_untrace pg_lo_create pg_lo_unlink pg_lo_open pg_lo_close pg_lo_read pg_lo_write pg_lo_read_all pg_lo_import pg_lo_export pg_lo_seek pg_lo_tell pg_escape_string pg_escape_bytea pg_unescape_bytea pg_client_encoding pg_set_client_encoding pg_meta_data pg_convert pg_insert pg_update pg_delete pg_select pg_exec pg_getlastoid pg_cmdtuples pg_errormessage pg_numrows pg_numfields pg_fieldname pg_fieldsize pg_fieldtype pg_fieldnum pg_fieldprtlen pg_fieldisnull pg_freeresult pg_result pg_loreadall pg_locreate pg_lounlink pg_loopen pg_loclose pg_loread pg_lowrite pg_loimport pg_loexport http_response_code get_declared_traits getimagesizefromstring socket_import_stream stream_set_chunk_size trait_exists header_register_callback class_uses session_status session_register_shutdown echo print global static exit array empty eval isset unset die include require include_once require_once json_decode json_encode json_last_error json_last_error_msg curl_close curl_copy_handle curl_errno curl_error curl_escape curl_exec curl_file_create curl_getinfo curl_init curl_multi_add_handle curl_multi_close curl_multi_exec curl_multi_getcontent curl_multi_info_read curl_multi_init curl_multi_remove_handle curl_multi_select curl_multi_setopt curl_multi_strerror curl_pause curl_reset curl_setopt_array curl_setopt curl_share_close curl_share_init curl_share_setopt curl_strerror curl_unescape curl_version mysqli_affected_rows mysqli_autocommit mysqli_change_user mysqli_character_set_name mysqli_close mysqli_commit mysqli_connect_errno mysqli_connect_error mysqli_connect mysqli_data_seek mysqli_debug mysqli_dump_debug_info mysqli_errno mysqli_error_list mysqli_error mysqli_fetch_all mysqli_fetch_array mysqli_fetch_assoc mysqli_fetch_field_direct mysqli_fetch_field mysqli_fetch_fields mysqli_fetch_lengths mysqli_fetch_object mysqli_fetch_row mysqli_field_count mysqli_field_seek mysqli_field_tell mysqli_free_result mysqli_get_charset mysqli_get_client_info mysqli_get_client_stats mysqli_get_client_version mysqli_get_connection_stats mysqli_get_host_info mysqli_get_proto_info mysqli_get_server_info mysqli_get_server_version mysqli_info mysqli_init mysqli_insert_id mysqli_kill mysqli_more_results mysqli_multi_query mysqli_next_result mysqli_num_fields mysqli_num_rows mysqli_options mysqli_ping mysqli_prepare mysqli_query mysqli_real_connect mysqli_real_escape_string mysqli_real_query mysqli_reap_async_query mysqli_refresh mysqli_rollback mysqli_select_db mysqli_set_charset mysqli_set_local_infile_default mysqli_set_local_infile_handler mysqli_sqlstate mysqli_ssl_set mysqli_stat mysqli_stmt_init mysqli_store_result mysqli_thread_id mysqli_thread_safe mysqli_use_result mysqli_warning_count";
    CodeMirror1.registerHelper("hintWords", "php", [
        phpKeywords,
        phpAtoms,
        phpBuiltin
    ].join(" ").split(" "));
    CodeMirror1.registerHelper("wordChars", "php", /[\w$]/);
    var phpConfig = {
        name: "clike",
        helperType: "php",
        keywords: keywords(phpKeywords),
        blockKeywords: keywords("catch do else elseif for foreach if switch try while finally"),
        defKeywords: keywords("class enum function interface namespace trait"),
        atoms: keywords(phpAtoms),
        builtin: keywords(phpBuiltin),
        multiLineStrings: true,
        hooks: {
            "$": function(stream) {
                stream.eatWhile(/[\w\$_]/);
                return "variable-2";
            },
            "<": function(stream, state) {
                var before;
                if (before = stream.match(/^<<\s*/)) {
                    var quoted = stream.eat(/['"]/);
                    stream.eatWhile(/[\w\.]/);
                    var delim = stream.current().slice(before[0].length + (quoted ? 2 : 1));
                    if (quoted) stream.eat(quoted);
                    if (delim) {
                        (state.tokStack || (state.tokStack = [])).push(delim, 0);
                        state.tokenize = phpString(delim, quoted != "'");
                        return "string";
                    }
                }
                return false;
            },
            "#": function(stream) {
                while(!stream.eol() && !stream.match("?>", false))stream.next();
                return "comment";
            },
            "/": function(stream) {
                if (stream.eat("/")) {
                    while(!stream.eol() && !stream.match("?>", false))stream.next();
                    return "comment";
                }
                return false;
            },
            '"': function(_stream, state) {
                (state.tokStack || (state.tokStack = [])).push('"', 0);
                state.tokenize = phpString('"');
                return "string";
            },
            "{": function(_stream, state) {
                if (state.tokStack && state.tokStack.length) state.tokStack[state.tokStack.length - 1]++;
                return false;
            },
            "}": function(_stream, state) {
                if (state.tokStack && state.tokStack.length > 0 && !--state.tokStack[state.tokStack.length - 1]) {
                    state.tokenize = phpString(state.tokStack[state.tokStack.length - 2]);
                }
                return false;
            }
        }
    };
    CodeMirror1.defineMode("php", function(config, parserConfig) {
        var dispatch = function dispatch(stream, state) {
            var isPHP = state.curMode == phpMode;
            if (stream.sol() && state.pending && state.pending != '"' && state.pending != "'") state.pending = null;
            if (!isPHP) {
                if (stream.match(/^<\?\w*/)) {
                    state.curMode = phpMode;
                    if (!state.php) state.php = CodeMirror1.startState(phpMode, htmlMode.indent(state.html, "", ""));
                    state.curState = state.php;
                    return "meta";
                }
                if (state.pending == '"' || state.pending == "'") {
                    while(!stream.eol() && stream.next() != state.pending){}
                    var style = "string";
                } else if (state.pending && stream.pos < state.pending.end) {
                    stream.pos = state.pending.end;
                    var style = state.pending.style;
                } else {
                    var style = htmlMode.token(stream, state.curState);
                }
                if (state.pending) state.pending = null;
                var cur = stream.current(), openPHP = cur.search(/<\?/), m;
                if (openPHP != -1) {
                    if (style == "string" && (m = cur.match(/[\'\"]$/)) && !/\?>/.test(cur)) state.pending = m[0];
                    else state.pending = {
                        end: stream.pos,
                        style: style
                    };
                    stream.backUp(cur.length - openPHP);
                }
                return style;
            } else if (isPHP && state.php.tokenize == null && stream.match("?>")) {
                state.curMode = htmlMode;
                state.curState = state.html;
                if (!state.php.context.prev) state.php = null;
                return "meta";
            } else {
                return phpMode.token(stream, state.curState);
            }
        };
        var htmlMode = CodeMirror1.getMode(config, parserConfig && parserConfig.htmlMode || "text/html");
        var phpMode = CodeMirror1.getMode(config, phpConfig);
        return {
            startState: function startState() {
                var html = CodeMirror1.startState(htmlMode);
                var php = parserConfig.startOpen ? CodeMirror1.startState(phpMode) : null;
                return {
                    html: html,
                    php: php,
                    curMode: parserConfig.startOpen ? phpMode : htmlMode,
                    curState: parserConfig.startOpen ? php : html,
                    pending: null
                };
            },
            copyState: function copyState(state) {
                var html = state.html, htmlNew = CodeMirror1.copyState(htmlMode, html), php = state.php, phpNew = php && CodeMirror1.copyState(phpMode, php), cur;
                if (state.curMode == htmlMode) cur = htmlNew;
                else cur = phpNew;
                return {
                    html: htmlNew,
                    php: phpNew,
                    curMode: state.curMode,
                    curState: cur,
                    pending: state.pending
                };
            },
            token: dispatch,
            indent: function indent(state, textAfter, line) {
                if (state.curMode != phpMode && /^\s*<\//.test(textAfter) || state.curMode == phpMode && /^\?>/.test(textAfter)) return htmlMode.indent(state.html, textAfter, line);
                return state.curMode.indent(state.curState, textAfter, line);
            },
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            lineComment: "//",
            innerMode: function innerMode(state) {
                return {
                    state: state.curState,
                    mode: state.curMode
                };
            }
        };
    }, "htmlmixed", "clike");
    CodeMirror1.defineMIME("application/x-httpd-php", "php");
    CodeMirror1.defineMIME("application/x-httpd-php-open", {
        name: "php",
        startOpen: true
    });
    CodeMirror1.defineMIME("text/x-php", phpConfig);
});
});

var powershell = createCommonjsModule(function (module, exports) {
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror) {
    CodeMirror.defineMode("powershell", function() {
        var buildRegexp = function buildRegexp(patterns, options) {
            options = options || {};
            var prefix = options.prefix !== undefined ? options.prefix : "^";
            var suffix = options.suffix !== undefined ? options.suffix : "\\b";
            for(var i = 0; i < patterns.length; i++){
                if (_instanceof(patterns[i], RegExp)) {
                    patterns[i] = patterns[i].source;
                } else {
                    patterns[i] = patterns[i].replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
                }
            }
            return new RegExp(prefix + "(" + patterns.join("|") + ")" + suffix, "i");
        };
        var tokenBase = // tokenizers
        function tokenBase(stream, state) {
            // Handle Comments
            //var ch = stream.peek();
            var parent = state.returnStack[state.returnStack.length - 1];
            if (parent && parent.shouldReturnFrom(state)) {
                state.tokenize = parent.tokenize;
                state.returnStack.pop();
                return state.tokenize(stream, state);
            }
            if (stream.eatSpace()) {
                return null;
            }
            if (stream.eat("(")) {
                state.bracketNesting += 1;
                return "punctuation";
            }
            if (stream.eat(")")) {
                state.bracketNesting -= 1;
                return "punctuation";
            }
            for(var key in grammar){
                if (stream.match(grammar[key])) {
                    return key;
                }
            }
            var ch = stream.next();
            // single-quote string
            if (ch === "'") {
                return tokenSingleQuoteString(stream, state);
            }
            if (ch === "$") {
                return tokenVariable(stream, state);
            }
            // double-quote string
            if (ch === '"') {
                return tokenDoubleQuoteString(stream, state);
            }
            if (ch === "<" && stream.eat("#")) {
                state.tokenize = tokenComment;
                return tokenComment(stream, state);
            }
            if (ch === "#") {
                stream.skipToEnd();
                return "comment";
            }
            if (ch === "@") {
                var quoteMatch = stream.eat(/["']/);
                if (quoteMatch && stream.eol()) {
                    state.tokenize = tokenMultiString;
                    state.startQuote = quoteMatch[0];
                    return tokenMultiString(stream, state);
                } else if (stream.eol()) {
                    return "error";
                } else if (stream.peek().match(/[({]/)) {
                    return "punctuation";
                } else if (stream.peek().match(varNames)) {
                    // splatted variable
                    return tokenVariable(stream, state);
                }
            }
            return "error";
        };
        var tokenSingleQuoteString = function tokenSingleQuoteString(stream, state) {
            var ch;
            while((ch = stream.peek()) != null){
                stream.next();
                if (ch === "'" && !stream.eat("'")) {
                    state.tokenize = tokenBase;
                    return "string";
                }
            }
            return "error";
        };
        var tokenDoubleQuoteString = function tokenDoubleQuoteString(stream, state) {
            var ch;
            while((ch = stream.peek()) != null){
                if (ch === "$") {
                    state.tokenize = tokenStringInterpolation;
                    return "string";
                }
                stream.next();
                if (ch === "`") {
                    stream.next();
                    continue;
                }
                if (ch === '"' && !stream.eat('"')) {
                    state.tokenize = tokenBase;
                    return "string";
                }
            }
            return "error";
        };
        var tokenStringInterpolation = function tokenStringInterpolation(stream, state) {
            return tokenInterpolation(stream, state, tokenDoubleQuoteString);
        };
        var tokenMultiStringReturn = function tokenMultiStringReturn(stream, state) {
            state.tokenize = tokenMultiString;
            state.startQuote = '"';
            return tokenMultiString(stream, state);
        };
        var tokenHereStringInterpolation = function tokenHereStringInterpolation(stream, state) {
            return tokenInterpolation(stream, state, tokenMultiStringReturn);
        };
        var tokenInterpolation = function tokenInterpolation(stream, state, parentTokenize) {
            if (stream.match("$(")) {
                var savedBracketNesting = state.bracketNesting;
                state.returnStack.push({
                    /*jshint loopfunc:true */ shouldReturnFrom: function shouldReturnFrom(state) {
                        return state.bracketNesting === savedBracketNesting;
                    },
                    tokenize: parentTokenize
                });
                state.tokenize = tokenBase;
                state.bracketNesting += 1;
                return "punctuation";
            } else {
                stream.next();
                state.returnStack.push({
                    shouldReturnFrom: function shouldReturnFrom() {
                        return true;
                    },
                    tokenize: parentTokenize
                });
                state.tokenize = tokenVariable;
                return state.tokenize(stream, state);
            }
        };
        var tokenComment = function tokenComment(stream, state) {
            var maybeEnd = false, ch;
            while((ch = stream.next()) != null){
                if (maybeEnd && ch == ">") {
                    state.tokenize = tokenBase;
                    break;
                }
                maybeEnd = ch === "#";
            }
            return "comment";
        };
        var tokenVariable = function tokenVariable(stream, state) {
            var ch = stream.peek();
            if (stream.eat("{")) {
                state.tokenize = tokenVariableWithBraces;
                return tokenVariableWithBraces(stream, state);
            } else if (ch != undefined && ch.match(varNames)) {
                stream.eatWhile(varNames);
                state.tokenize = tokenBase;
                return "variable-2";
            } else {
                state.tokenize = tokenBase;
                return "error";
            }
        };
        var tokenVariableWithBraces = function tokenVariableWithBraces(stream, state) {
            var ch;
            while((ch = stream.next()) != null){
                if (ch === "}") {
                    state.tokenize = tokenBase;
                    break;
                }
            }
            return "variable-2";
        };
        var tokenMultiString = function tokenMultiString(stream, state) {
            var quote = state.startQuote;
            if (stream.sol() && stream.match(new RegExp(quote + "@"))) {
                state.tokenize = tokenBase;
            } else if (quote === '"') {
                while(!stream.eol()){
                    var ch = stream.peek();
                    if (ch === "$") {
                        state.tokenize = tokenHereStringInterpolation;
                        return "string";
                    }
                    stream.next();
                    if (ch === "`") {
                        stream.next();
                    }
                }
            } else {
                stream.skipToEnd();
            }
            return "string";
        };
        var notCharacterOrDash = "(?=[^A-Za-z\\d\\-_]|$)";
        var varNames = /[\w\-:]/;
        var keywords = buildRegexp([
            /begin|break|catch|continue|data|default|do|dynamicparam/,
            /else|elseif|end|exit|filter|finally|for|foreach|from|function|if|in/,
            /param|process|return|switch|throw|trap|try|until|where|while/
        ], {
            suffix: notCharacterOrDash
        });
        var punctuation = /[\[\]{},;`\\\.]|@[({]/;
        var wordOperators = buildRegexp([
            "f",
            /b?not/,
            /[ic]?split/,
            "join",
            /is(not)?/,
            "as",
            /[ic]?(eq|ne|[gl][te])/,
            /[ic]?(not)?(like|match|contains)/,
            /[ic]?replace/,
            /b?(and|or|xor)/
        ], {
            prefix: "-"
        });
        var symbolOperators = /[+\-*\/%]=|\+\+|--|\.\.|[+\-*&^%:=!|\/]|<(?!#)|(?!#)>/;
        var operators = buildRegexp([
            wordOperators,
            symbolOperators
        ], {
            suffix: ""
        });
        var numbers = /^((0x[\da-f]+)|((\d+\.\d+|\d\.|\.\d+|\d+)(e[\+\-]?\d+)?))[ld]?([kmgtp]b)?/i;
        var identifiers = /^[A-Za-z\_][A-Za-z\-\_\d]*\b/;
        var symbolBuiltins = /[A-Z]:|%|\?/i;
        var namedBuiltins = buildRegexp([
            /Add-(Computer|Content|History|Member|PSSnapin|Type)/,
            /Checkpoint-Computer/,
            /Clear-(Content|EventLog|History|Host|Item(Property)?|Variable)/,
            /Compare-Object/,
            /Complete-Transaction/,
            /Connect-PSSession/,
            /ConvertFrom-(Csv|Json|SecureString|StringData)/,
            /Convert-Path/,
            /ConvertTo-(Csv|Html|Json|SecureString|Xml)/,
            /Copy-Item(Property)?/,
            /Debug-Process/,
            /Disable-(ComputerRestore|PSBreakpoint|PSRemoting|PSSessionConfiguration)/,
            /Disconnect-PSSession/,
            /Enable-(ComputerRestore|PSBreakpoint|PSRemoting|PSSessionConfiguration)/,
            /(Enter|Exit)-PSSession/,
            /Export-(Alias|Clixml|Console|Counter|Csv|FormatData|ModuleMember|PSSession)/,
            /ForEach-Object/,
            /Format-(Custom|List|Table|Wide)/,
            new RegExp("Get-(Acl|Alias|AuthenticodeSignature|ChildItem|Command|ComputerRestorePoint|Content|ControlPanelItem|Counter|Credential" + "|Culture|Date|Event|EventLog|EventSubscriber|ExecutionPolicy|FormatData|Help|History|Host|HotFix|Item|ItemProperty|Job" + "|Location|Member|Module|PfxCertificate|Process|PSBreakpoint|PSCallStack|PSDrive|PSProvider|PSSession|PSSessionConfiguration" + "|PSSnapin|Random|Service|TraceSource|Transaction|TypeData|UICulture|Unique|Variable|Verb|WinEvent|WmiObject)"),
            /Group-Object/,
            /Import-(Alias|Clixml|Counter|Csv|LocalizedData|Module|PSSession)/,
            /ImportSystemModules/,
            /Invoke-(Command|Expression|History|Item|RestMethod|WebRequest|WmiMethod)/,
            /Join-Path/,
            /Limit-EventLog/,
            /Measure-(Command|Object)/,
            /Move-Item(Property)?/,
            new RegExp("New-(Alias|Event|EventLog|Item(Property)?|Module|ModuleManifest|Object|PSDrive|PSSession|PSSessionConfigurationFile" + "|PSSessionOption|PSTransportOption|Service|TimeSpan|Variable|WebServiceProxy|WinEvent)"),
            /Out-(Default|File|GridView|Host|Null|Printer|String)/,
            /Pause/,
            /(Pop|Push)-Location/,
            /Read-Host/,
            /Receive-(Job|PSSession)/,
            /Register-(EngineEvent|ObjectEvent|PSSessionConfiguration|WmiEvent)/,
            /Remove-(Computer|Event|EventLog|Item(Property)?|Job|Module|PSBreakpoint|PSDrive|PSSession|PSSnapin|TypeData|Variable|WmiObject)/,
            /Rename-(Computer|Item(Property)?)/,
            /Reset-ComputerMachinePassword/,
            /Resolve-Path/,
            /Restart-(Computer|Service)/,
            /Restore-Computer/,
            /Resume-(Job|Service)/,
            /Save-Help/,
            /Select-(Object|String|Xml)/,
            /Send-MailMessage/,
            new RegExp("Set-(Acl|Alias|AuthenticodeSignature|Content|Date|ExecutionPolicy|Item(Property)?|Location|PSBreakpoint|PSDebug" + "|PSSessionConfiguration|Service|StrictMode|TraceSource|Variable|WmiInstance)"),
            /Show-(Command|ControlPanelItem|EventLog)/,
            /Sort-Object/,
            /Split-Path/,
            /Start-(Job|Process|Service|Sleep|Transaction|Transcript)/,
            /Stop-(Computer|Job|Process|Service|Transcript)/,
            /Suspend-(Job|Service)/,
            /TabExpansion2/,
            /Tee-Object/,
            /Test-(ComputerSecureChannel|Connection|ModuleManifest|Path|PSSessionConfigurationFile)/,
            /Trace-Command/,
            /Unblock-File/,
            /Undo-Transaction/,
            /Unregister-(Event|PSSessionConfiguration)/,
            /Update-(FormatData|Help|List|TypeData)/,
            /Use-Transaction/,
            /Wait-(Event|Job|Process)/,
            /Where-Object/,
            /Write-(Debug|Error|EventLog|Host|Output|Progress|Verbose|Warning)/,
            /cd|help|mkdir|more|oss|prompt/,
            /ac|asnp|cat|cd|chdir|clc|clear|clhy|cli|clp|cls|clv|cnsn|compare|copy|cp|cpi|cpp|cvpa|dbp|del|diff|dir|dnsn|ebp/,
            /echo|epal|epcsv|epsn|erase|etsn|exsn|fc|fl|foreach|ft|fw|gal|gbp|gc|gci|gcm|gcs|gdr|ghy|gi|gjb|gl|gm|gmo|gp|gps/,
            /group|gsn|gsnp|gsv|gu|gv|gwmi|h|history|icm|iex|ihy|ii|ipal|ipcsv|ipmo|ipsn|irm|ise|iwmi|iwr|kill|lp|ls|man|md/,
            /measure|mi|mount|move|mp|mv|nal|ndr|ni|nmo|npssc|nsn|nv|ogv|oh|popd|ps|pushd|pwd|r|rbp|rcjb|rcsn|rd|rdr|ren|ri/,
            /rjb|rm|rmdir|rmo|rni|rnp|rp|rsn|rsnp|rujb|rv|rvpa|rwmi|sajb|sal|saps|sasv|sbp|sc|select|set|shcm|si|sl|sleep|sls/,
            /sort|sp|spjb|spps|spsv|start|sujb|sv|swmi|tee|trcm|type|where|wjb|write/
        ], {
            prefix: "",
            suffix: ""
        });
        var variableBuiltins = buildRegexp([
            /[$?^_]|Args|ConfirmPreference|ConsoleFileName|DebugPreference|Error|ErrorActionPreference|ErrorView|ExecutionContext/,
            /FormatEnumerationLimit|Home|Host|Input|MaximumAliasCount|MaximumDriveCount|MaximumErrorCount|MaximumFunctionCount/,
            /MaximumHistoryCount|MaximumVariableCount|MyInvocation|NestedPromptLevel|OutputEncoding|Pid|Profile|ProgressPreference/,
            /PSBoundParameters|PSCommandPath|PSCulture|PSDefaultParameterValues|PSEmailServer|PSHome|PSScriptRoot|PSSessionApplicationName/,
            /PSSessionConfigurationName|PSSessionOption|PSUICulture|PSVersionTable|Pwd|ShellId|StackTrace|VerbosePreference/,
            /WarningPreference|WhatIfPreference/,
            /Event|EventArgs|EventSubscriber|Sender/,
            /Matches|Ofs|ForEach|LastExitCode|PSCmdlet|PSItem|PSSenderInfo|This/,
            /true|false|null/
        ], {
            prefix: "\\$",
            suffix: ""
        });
        var builtins = buildRegexp([
            symbolBuiltins,
            namedBuiltins,
            variableBuiltins
        ], {
            suffix: notCharacterOrDash
        });
        var grammar = {
            keyword: keywords,
            number: numbers,
            operator: operators,
            builtin: builtins,
            punctuation: punctuation,
            identifier: identifiers
        };
        var external = {
            startState: function startState() {
                return {
                    returnStack: [],
                    bracketNesting: 0,
                    tokenize: tokenBase
                };
            },
            token: function token(stream, state) {
                return state.tokenize(stream, state);
            },
            blockCommentStart: "<#",
            blockCommentEnd: "#>",
            lineComment: "#",
            fold: "brace"
        };
        return external;
    });
    CodeMirror.defineMIME("application/x-powershell", "powershell");
});
});

var python = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var wordRegexp = function wordRegexp(words) {
        return new RegExp("^((" + words.join(")|(") + "))\\b");
    };
    var top = function top(state) {
        return state.scopes[state.scopes.length - 1];
    };
    var wordOperators = wordRegexp([
        "and",
        "or",
        "not",
        "is"
    ]);
    var commonKeywords = [
        "as",
        "assert",
        "break",
        "class",
        "continue",
        "def",
        "del",
        "elif",
        "else",
        "except",
        "finally",
        "for",
        "from",
        "global",
        "if",
        "import",
        "lambda",
        "pass",
        "raise",
        "return",
        "try",
        "while",
        "with",
        "yield",
        "in"
    ];
    var commonBuiltins = [
        "abs",
        "all",
        "any",
        "bin",
        "bool",
        "bytearray",
        "callable",
        "chr",
        "classmethod",
        "compile",
        "complex",
        "delattr",
        "dict",
        "dir",
        "divmod",
        "enumerate",
        "eval",
        "filter",
        "float",
        "format",
        "frozenset",
        "getattr",
        "globals",
        "hasattr",
        "hash",
        "help",
        "hex",
        "id",
        "input",
        "int",
        "isinstance",
        "issubclass",
        "iter",
        "len",
        "list",
        "locals",
        "map",
        "max",
        "memoryview",
        "min",
        "next",
        "object",
        "oct",
        "open",
        "ord",
        "pow",
        "property",
        "range",
        "repr",
        "reversed",
        "round",
        "set",
        "setattr",
        "slice",
        "sorted",
        "staticmethod",
        "str",
        "sum",
        "super",
        "tuple",
        "type",
        "vars",
        "zip",
        "__import__",
        "NotImplemented",
        "Ellipsis",
        "__debug__"
    ];
    CodeMirror1.registerHelper("hintWords", "python", commonKeywords.concat(commonBuiltins));
    CodeMirror1.defineMode("python", function(conf, parserConf) {
        var tokenBase = // tokenizers
        function tokenBase(stream, state) {
            var sol = stream.sol() && state.lastToken != "\\";
            if (sol) state.indent = stream.indentation();
            // Handle scope changes
            if (sol && top(state).type == "py") {
                var scopeOffset = top(state).offset;
                if (stream.eatSpace()) {
                    var lineOffset = stream.indentation();
                    if (lineOffset > scopeOffset) pushPyScope(state);
                    else if (lineOffset < scopeOffset && dedent(stream, state) && stream.peek() != "#") state.errorToken = true;
                    return null;
                } else {
                    var style = tokenBaseInner(stream, state);
                    if (scopeOffset > 0 && dedent(stream, state)) style += " " + ERRORCLASS;
                    return style;
                }
            }
            return tokenBaseInner(stream, state);
        };
        var tokenBaseInner = function tokenBaseInner(stream, state, inFormat) {
            if (stream.eatSpace()) return null;
            // Handle Comments
            if (!inFormat && stream.match(/^#.*/)) return "comment";
            // Handle Number Literals
            if (stream.match(/^[0-9\.]/, false)) {
                var floatLiteral = false;
                // Floats
                if (stream.match(/^[\d_]*\.\d+(e[\+\-]?\d+)?/i)) {
                    floatLiteral = true;
                }
                if (stream.match(/^[\d_]+\.\d*/)) {
                    floatLiteral = true;
                }
                if (stream.match(/^\.\d+/)) {
                    floatLiteral = true;
                }
                if (floatLiteral) {
                    // Float literals may be "imaginary"
                    stream.eat(/J/i);
                    return "number";
                }
                // Integers
                var intLiteral = false;
                // Hex
                if (stream.match(/^0x[0-9a-f_]+/i)) intLiteral = true;
                // Binary
                if (stream.match(/^0b[01_]+/i)) intLiteral = true;
                // Octal
                if (stream.match(/^0o[0-7_]+/i)) intLiteral = true;
                // Decimal
                if (stream.match(/^[1-9][\d_]*(e[\+\-]?[\d_]+)?/)) {
                    // Decimal literals may be "imaginary"
                    stream.eat(/J/i);
                    // TODO - Can you have imaginary longs?
                    intLiteral = true;
                }
                // Zero by itself with no other piece of number.
                if (stream.match(/^0(?![\dx])/i)) intLiteral = true;
                if (intLiteral) {
                    // Integer literals may be "long"
                    stream.eat(/L/i);
                    return "number";
                }
            }
            // Handle Strings
            if (stream.match(stringPrefixes)) {
                var isFmtString = stream.current().toLowerCase().indexOf("f") !== -1;
                if (!isFmtString) {
                    state.tokenize = tokenStringFactory(stream.current(), state.tokenize);
                    return state.tokenize(stream, state);
                } else {
                    state.tokenize = formatStringFactory(stream.current(), state.tokenize);
                    return state.tokenize(stream, state);
                }
            }
            for(var i = 0; i < operators.length; i++)if (stream.match(operators[i])) return "operator";
            if (stream.match(delimiters)) return "punctuation";
            if (state.lastToken == "." && stream.match(identifiers)) return "property";
            if (stream.match(keywords) || stream.match(wordOperators)) return "keyword";
            if (stream.match(builtins)) return "builtin";
            if (stream.match(/^(self|cls)\b/)) return "variable-2";
            if (stream.match(identifiers)) {
                if (state.lastToken == "def" || state.lastToken == "class") return "def";
                return "variable";
            }
            // Handle non-detected items
            stream.next();
            return inFormat ? null : ERRORCLASS;
        };
        var formatStringFactory = function formatStringFactory(delimiter, tokenOuter) {
            while("rubf".indexOf(delimiter.charAt(0).toLowerCase()) >= 0)delimiter = delimiter.substr(1);
            var singleline = delimiter.length == 1;
            var OUTCLASS = "string";
            function tokenNestedExpr(depth) {
                return function(stream, state) {
                    var inner = tokenBaseInner(stream, state, true);
                    if (inner == "punctuation") {
                        if (stream.current() == "{") {
                            state.tokenize = tokenNestedExpr(depth + 1);
                        } else if (stream.current() == "}") {
                            if (depth > 1) state.tokenize = tokenNestedExpr(depth - 1);
                            else state.tokenize = tokenString;
                        }
                    }
                    return inner;
                };
            }
            function tokenString(stream, state) {
                while(!stream.eol()){
                    stream.eatWhile(/[^'"\{\}\\]/);
                    if (stream.eat("\\")) {
                        stream.next();
                        if (singleline && stream.eol()) return OUTCLASS;
                    } else if (stream.match(delimiter)) {
                        state.tokenize = tokenOuter;
                        return OUTCLASS;
                    } else if (stream.match("{{")) {
                        // ignore {{ in f-str
                        return OUTCLASS;
                    } else if (stream.match("{", false)) {
                        // switch to nested mode
                        state.tokenize = tokenNestedExpr(0);
                        if (stream.current()) return OUTCLASS;
                        else return state.tokenize(stream, state);
                    } else if (stream.match("}}")) {
                        return OUTCLASS;
                    } else if (stream.match("}")) {
                        // single } in f-string is an error
                        return ERRORCLASS;
                    } else {
                        stream.eat(/['"]/);
                    }
                }
                if (singleline) {
                    if (parserConf.singleLineStringErrors) return ERRORCLASS;
                    else state.tokenize = tokenOuter;
                }
                return OUTCLASS;
            }
            tokenString.isString = true;
            return tokenString;
        };
        var tokenStringFactory = function tokenStringFactory(delimiter, tokenOuter) {
            while("rubf".indexOf(delimiter.charAt(0).toLowerCase()) >= 0)delimiter = delimiter.substr(1);
            var singleline = delimiter.length == 1;
            var OUTCLASS = "string";
            function tokenString(stream, state) {
                while(!stream.eol()){
                    stream.eatWhile(/[^'"\\]/);
                    if (stream.eat("\\")) {
                        stream.next();
                        if (singleline && stream.eol()) return OUTCLASS;
                    } else if (stream.match(delimiter)) {
                        state.tokenize = tokenOuter;
                        return OUTCLASS;
                    } else {
                        stream.eat(/['"]/);
                    }
                }
                if (singleline) {
                    if (parserConf.singleLineStringErrors) return ERRORCLASS;
                    else state.tokenize = tokenOuter;
                }
                return OUTCLASS;
            }
            tokenString.isString = true;
            return tokenString;
        };
        var pushPyScope = function pushPyScope(state) {
            while(top(state).type != "py")state.scopes.pop();
            state.scopes.push({
                offset: top(state).offset + conf.indentUnit,
                type: "py",
                align: null
            });
        };
        var pushBracketScope = function pushBracketScope(stream, state, type) {
            var align = stream.match(/^[\s\[\{\(]*(?:#|$)/, false) ? null : stream.column() + 1;
            state.scopes.push({
                offset: state.indent + hangingIndent,
                type: type,
                align: align
            });
        };
        var dedent = function dedent(stream, state) {
            var indented = stream.indentation();
            while(state.scopes.length > 1 && top(state).offset > indented){
                if (top(state).type != "py") return true;
                state.scopes.pop();
            }
            return top(state).offset != indented;
        };
        var tokenLexer = function tokenLexer(stream, state) {
            if (stream.sol()) {
                state.beginningOfLine = true;
                state.dedent = false;
            }
            var style = state.tokenize(stream, state);
            var current = stream.current();
            // Handle decorators
            if (state.beginningOfLine && current == "@") return stream.match(identifiers, false) ? "meta" : py3 ? "operator" : ERRORCLASS;
            if (/\S/.test(current)) state.beginningOfLine = false;
            if ((style == "variable" || style == "builtin") && state.lastToken == "meta") style = "meta";
            // Handle scope changes.
            if (current == "pass" || current == "return") state.dedent = true;
            if (current == "lambda") state.lambda = true;
            if (current == ":" && !state.lambda && top(state).type == "py" && stream.match(/^\s*(?:#|$)/, false)) pushPyScope(state);
            if (current.length == 1 && !/string|comment/.test(style)) {
                var delimiter_index = "[({".indexOf(current);
                if (delimiter_index != -1) pushBracketScope(stream, state, "])}".slice(delimiter_index, delimiter_index + 1));
                delimiter_index = "])}".indexOf(current);
                if (delimiter_index != -1) {
                    if (top(state).type == current) state.indent = state.scopes.pop().offset - hangingIndent;
                    else return ERRORCLASS;
                }
            }
            if (state.dedent && stream.eol() && top(state).type == "py" && state.scopes.length > 1) state.scopes.pop();
            return style;
        };
        var ERRORCLASS = "error";
        var delimiters = parserConf.delimiters || parserConf.singleDelimiters || /^[\(\)\[\]\{\}@,:`=;\.\\]/;
        //               (Backwards-compatibility with old, cumbersome config system)
        var operators = [
            parserConf.singleOperators,
            parserConf.doubleOperators,
            parserConf.doubleDelimiters,
            parserConf.tripleDelimiters,
            parserConf.operators || /^([-+*/%\/&|^]=?|[<>=]+|\/\/=?|\*\*=?|!=|[~!@]|\.\.\.)/
        ];
        for(var i = 0; i < operators.length; i++)if (!operators[i]) operators.splice(i--, 1);
        var hangingIndent = parserConf.hangingIndent || conf.indentUnit;
        var myKeywords = commonKeywords, myBuiltins = commonBuiltins;
        if (parserConf.extra_keywords != undefined) myKeywords = myKeywords.concat(parserConf.extra_keywords);
        if (parserConf.extra_builtins != undefined) myBuiltins = myBuiltins.concat(parserConf.extra_builtins);
        var py3 = !(parserConf.version && Number(parserConf.version) < 3);
        if (py3) {
            // since http://legacy.python.org/dev/peps/pep-0465/ @ is also an operator
            var identifiers = parserConf.identifiers || /^[_A-Za-z\u00A1-\uFFFF][_A-Za-z0-9\u00A1-\uFFFF]*/;
            myKeywords = myKeywords.concat([
                "nonlocal",
                "False",
                "True",
                "None",
                "async",
                "await"
            ]);
            myBuiltins = myBuiltins.concat([
                "ascii",
                "bytes",
                "exec",
                "print"
            ]);
            var stringPrefixes = new RegExp("^(([rbuf]|(br)|(rb)|(fr)|(rf))?('{3}|\"{3}|['\"]))", "i");
        } else {
            var identifiers = parserConf.identifiers || /^[_A-Za-z][_A-Za-z0-9]*/;
            myKeywords = myKeywords.concat([
                "exec",
                "print"
            ]);
            myBuiltins = myBuiltins.concat([
                "apply",
                "basestring",
                "buffer",
                "cmp",
                "coerce",
                "execfile",
                "file",
                "intern",
                "long",
                "raw_input",
                "reduce",
                "reload",
                "unichr",
                "unicode",
                "xrange",
                "False",
                "True",
                "None"
            ]);
            var stringPrefixes = new RegExp("^(([rubf]|(ur)|(br))?('{3}|\"{3}|['\"]))", "i");
        }
        var keywords = wordRegexp(myKeywords);
        var builtins = wordRegexp(myBuiltins);
        var external = {
            startState: function startState(basecolumn) {
                return {
                    tokenize: tokenBase,
                    scopes: [
                        {
                            offset: basecolumn || 0,
                            type: "py",
                            align: null
                        }
                    ],
                    indent: basecolumn || 0,
                    lastToken: null,
                    lambda: false,
                    dedent: 0
                };
            },
            token: function token(stream, state) {
                var addErr = state.errorToken;
                if (addErr) state.errorToken = false;
                var style = tokenLexer(stream, state);
                if (style && style != "comment") state.lastToken = style == "keyword" || style == "punctuation" ? stream.current() : style;
                if (style == "punctuation") style = null;
                if (stream.eol() && state.lambda) state.lambda = false;
                return addErr ? style + " " + ERRORCLASS : style;
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize != tokenBase) return state.tokenize.isString ? CodeMirror1.Pass : 0;
                var scope = top(state);
                var closing = scope.type == textAfter.charAt(0) || scope.type == "py" && !state.dedent && /^(else:|elif |except |finally:)/.test(textAfter);
                if (scope.align != null) return scope.align - (closing ? 1 : 0);
                else return scope.offset - (closing ? hangingIndent : 0);
            },
            electricInput: /^\s*([\}\]\)]|else:|elif |except |finally:)$/,
            closeBrackets: {
                triples: "'\""
            },
            lineComment: "#",
            fold: "indent"
        };
        return external;
    });
    CodeMirror1.defineMIME("text/x-python", "python");
    var words = function words(str) {
        return str.split(" ");
    };
    CodeMirror1.defineMIME("text/x-cython", {
        name: "python",
        extra_keywords: words("by cdef cimport cpdef ctypedef enum except " + "extern gil include nogil property public " + "readonly struct union DEF IF ELIF ELSE")
    });
});
});

var r = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.registerHelper("wordChars", "r", /[\w.]/);
    CodeMirror1.defineMode("r", function(config) {
        var wordObj = function wordObj(words) {
            var res = {};
            for(var i = 0; i < words.length; ++i)res[words[i]] = true;
            return res;
        };
        var tokenBase = function tokenBase(stream, state) {
            curPunc = null;
            var ch = stream.next();
            if (ch == "#") {
                stream.skipToEnd();
                return "comment";
            } else if (ch == "0" && stream.eat("x")) {
                stream.eatWhile(/[\da-f]/i);
                return "number";
            } else if (ch == "." && stream.eat(/\d/)) {
                stream.match(/\d*(?:e[+\-]?\d+)?/);
                return "number";
            } else if (/\d/.test(ch)) {
                stream.match(/\d*(?:\.\d+)?(?:e[+\-]\d+)?L?/);
                return "number";
            } else if (ch == "'" || ch == '"') {
                state.tokenize = tokenString(ch);
                return "string";
            } else if (ch == "`") {
                stream.match(/[^`]+`/);
                return "variable-3";
            } else if (ch == "." && stream.match(/.(?:[.]|\d+)/)) {
                return "keyword";
            } else if (/[a-zA-Z\.]/.test(ch)) {
                stream.eatWhile(/[\w\.]/);
                var word = stream.current();
                if (atoms.propertyIsEnumerable(word)) return "atom";
                if (keywords.propertyIsEnumerable(word)) {
                    // Block keywords start new blocks, except 'else if', which only starts
                    // one new block for the 'if', no block for the 'else'.
                    if (blockkeywords.propertyIsEnumerable(word) && !stream.match(/\s*if(\s+|$)/, false)) curPunc = "block";
                    return "keyword";
                }
                if (builtins.propertyIsEnumerable(word)) return "builtin";
                return "variable";
            } else if (ch == "%") {
                if (stream.skipTo("%")) stream.next();
                return "operator variable-2";
            } else if (ch == "<" && stream.eat("-") || ch == "<" && stream.match("<-") || ch == "-" && stream.match(/>>?/)) {
                return "operator arrow";
            } else if (ch == "=" && state.ctx.argList) {
                return "arg-is";
            } else if (opChars.test(ch)) {
                if (ch == "$") return "operator dollar";
                stream.eatWhile(opChars);
                return "operator";
            } else if (/[\(\){}\[\];]/.test(ch)) {
                curPunc = ch;
                if (ch == ";") return "semi";
                return null;
            } else {
                return null;
            }
        };
        var tokenString = function tokenString(quote) {
            return function(stream, state) {
                if (stream.eat("\\")) {
                    var ch = stream.next();
                    if (ch == "x") stream.match(/^[a-f0-9]{2}/i);
                    else if ((ch == "u" || ch == "U") && stream.eat("{") && stream.skipTo("}")) stream.next();
                    else if (ch == "u") stream.match(/^[a-f0-9]{4}/i);
                    else if (ch == "U") stream.match(/^[a-f0-9]{8}/i);
                    else if (/[0-7]/.test(ch)) stream.match(/^[0-7]{1,2}/);
                    return "string-2";
                } else {
                    var next;
                    while((next = stream.next()) != null){
                        if (next == quote) {
                            state.tokenize = tokenBase;
                            break;
                        }
                        if (next == "\\") {
                            stream.backUp(1);
                            break;
                        }
                    }
                    return "string";
                }
            };
        };
        var push = function push(state, type, stream) {
            state.ctx = {
                type: type,
                indent: state.indent,
                flags: 0,
                column: stream.column(),
                prev: state.ctx
            };
        };
        var setFlag = function setFlag(state, flag) {
            var ctx = state.ctx;
            state.ctx = {
                type: ctx.type,
                indent: ctx.indent,
                flags: ctx.flags | flag,
                column: ctx.column,
                prev: ctx.prev
            };
        };
        var pop = function pop(state) {
            state.indent = state.ctx.indent;
            state.ctx = state.ctx.prev;
        };
        var commonAtoms = [
            "NULL",
            "NA",
            "Inf",
            "NaN",
            "NA_integer_",
            "NA_real_",
            "NA_complex_",
            "NA_character_",
            "TRUE",
            "FALSE"
        ];
        var commonBuiltins = [
            "list",
            "quote",
            "bquote",
            "eval",
            "return",
            "call",
            "parse",
            "deparse"
        ];
        var commonKeywords = [
            "if",
            "else",
            "repeat",
            "while",
            "function",
            "for",
            "in",
            "next",
            "break"
        ];
        var commonBlockKeywords = [
            "if",
            "else",
            "repeat",
            "while",
            "function",
            "for"
        ];
        CodeMirror1.registerHelper("hintWords", "r", commonAtoms.concat(commonBuiltins, commonKeywords));
        var atoms = wordObj(commonAtoms);
        var builtins = wordObj(commonBuiltins);
        var keywords = wordObj(commonKeywords);
        var blockkeywords = wordObj(commonBlockKeywords);
        var opChars = /[+\-*\/^<>=!&|~$:]/;
        var curPunc;
        var ALIGN_YES = 1, ALIGN_NO = 2, BRACELESS = 4;
        return {
            startState: function startState() {
                return {
                    tokenize: tokenBase,
                    ctx: {
                        type: "top",
                        indent: -config.indentUnit,
                        flags: ALIGN_NO
                    },
                    indent: 0,
                    afterIdent: false
                };
            },
            token: function token(stream, state) {
                if (stream.sol()) {
                    if ((state.ctx.flags & 3) == 0) state.ctx.flags |= ALIGN_NO;
                    if (state.ctx.flags & BRACELESS) pop(state);
                    state.indent = stream.indentation();
                }
                if (stream.eatSpace()) return null;
                var style = state.tokenize(stream, state);
                if (style != "comment" && (state.ctx.flags & ALIGN_NO) == 0) setFlag(state, ALIGN_YES);
                if ((curPunc == ";" || curPunc == "{" || curPunc == "}") && state.ctx.type == "block") pop(state);
                if (curPunc == "{") push(state, "}", stream);
                else if (curPunc == "(") {
                    push(state, ")", stream);
                    if (state.afterIdent) state.ctx.argList = true;
                } else if (curPunc == "[") push(state, "]", stream);
                else if (curPunc == "block") push(state, "block", stream);
                else if (curPunc == state.ctx.type) pop(state);
                else if (state.ctx.type == "block" && style != "comment") setFlag(state, BRACELESS);
                state.afterIdent = style == "variable" || style == "keyword";
                return style;
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize != tokenBase) return 0;
                var firstChar = textAfter && textAfter.charAt(0), ctx = state.ctx, closing = firstChar == ctx.type;
                if (ctx.flags & BRACELESS) ctx = ctx.prev;
                if (ctx.type == "block") return ctx.indent + (firstChar == "{" ? 0 : config.indentUnit);
                else if (ctx.flags & ALIGN_YES) return ctx.column + (closing ? 0 : 1);
                else return ctx.indent + (closing ? 0 : config.indentUnit);
            },
            lineComment: "#"
        };
    });
    CodeMirror1.defineMIME("text/x-rsrc", "r");
});
});

var ruby = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var wordObj = function wordObj(words) {
        var o = {};
        for(var i = 0, e = words.length; i < e; ++i)o[words[i]] = true;
        return o;
    };
    var keywordList = [
        "alias",
        "and",
        "BEGIN",
        "begin",
        "break",
        "case",
        "class",
        "def",
        "defined?",
        "do",
        "else",
        "elsif",
        "END",
        "end",
        "ensure",
        "false",
        "for",
        "if",
        "in",
        "module",
        "next",
        "not",
        "or",
        "redo",
        "rescue",
        "retry",
        "return",
        "self",
        "super",
        "then",
        "true",
        "undef",
        "unless",
        "until",
        "when",
        "while",
        "yield",
        "nil",
        "raise",
        "throw",
        "catch",
        "fail",
        "loop",
        "callcc",
        "caller",
        "lambda",
        "proc",
        "public",
        "protected",
        "private",
        "require",
        "load",
        "require_relative",
        "extend",
        "autoload",
        "__END__",
        "__FILE__",
        "__LINE__",
        "__dir__"
    ], keywords = wordObj(keywordList);
    var indentWords = wordObj([
        "def",
        "class",
        "case",
        "for",
        "while",
        "until",
        "module",
        "then",
        "catch",
        "loop",
        "proc",
        "begin"
    ]);
    var dedentWords = wordObj([
        "end",
        "until"
    ]);
    var opening = {
        "[": "]",
        "{": "}",
        "(": ")"
    };
    var closing = {
        "]": "[",
        "}": "{",
        ")": "("
    };
    CodeMirror1.defineMode("ruby", function(config) {
        var chain = function chain(newtok, stream, state) {
            state.tokenize.push(newtok);
            return newtok(stream, state);
        };
        var tokenBase = function tokenBase(stream, state) {
            if (stream.sol() && stream.match("=begin") && stream.eol()) {
                state.tokenize.push(readBlockComment);
                return "comment";
            }
            if (stream.eatSpace()) return null;
            var ch = stream.next(), m;
            if (ch == "`" || ch == "'" || ch == '"') {
                return chain(readQuoted(ch, "string", ch == '"' || ch == "`"), stream, state);
            } else if (ch == "/") {
                if (regexpAhead(stream)) return chain(readQuoted(ch, "string-2", true), stream, state);
                else return "operator";
            } else if (ch == "%") {
                var style = "string", embed = true;
                if (stream.eat("s")) style = "atom";
                else if (stream.eat(/[WQ]/)) style = "string";
                else if (stream.eat(/[r]/)) style = "string-2";
                else if (stream.eat(/[wxq]/)) {
                    style = "string";
                    embed = false;
                }
                var delim = stream.eat(/[^\w\s=]/);
                if (!delim) return "operator";
                if (opening.propertyIsEnumerable(delim)) delim = opening[delim];
                return chain(readQuoted(delim, style, embed, true), stream, state);
            } else if (ch == "#") {
                stream.skipToEnd();
                return "comment";
            } else if (ch == "<" && (m = stream.match(/^<([-~])[\`\"\']?([a-zA-Z_?]\w*)[\`\"\']?(?:;|$)/))) {
                return chain(readHereDoc(m[2], m[1]), stream, state);
            } else if (ch == "0") {
                if (stream.eat("x")) stream.eatWhile(/[\da-fA-F]/);
                else if (stream.eat("b")) stream.eatWhile(/[01]/);
                else stream.eatWhile(/[0-7]/);
                return "number";
            } else if (/\d/.test(ch)) {
                stream.match(/^[\d_]*(?:\.[\d_]+)?(?:[eE][+\-]?[\d_]+)?/);
                return "number";
            } else if (ch == "?") {
                while(stream.match(/^\\[CM]-/)){}
                if (stream.eat("\\")) stream.eatWhile(/\w/);
                else stream.next();
                return "string";
            } else if (ch == ":") {
                if (stream.eat("'")) return chain(readQuoted("'", "atom", false), stream, state);
                if (stream.eat('"')) return chain(readQuoted('"', "atom", true), stream, state);
                // :> :>> :< :<< are valid symbols
                if (stream.eat(/[\<\>]/)) {
                    stream.eat(/[\<\>]/);
                    return "atom";
                }
                // :+ :- :/ :* :| :& :! are valid symbols
                if (stream.eat(/[\+\-\*\/\&\|\:\!]/)) {
                    return "atom";
                }
                // Symbols can't start by a digit
                if (stream.eat(/[a-zA-Z$@_\xa1-\uffff]/)) {
                    stream.eatWhile(/[\w$\xa1-\uffff]/);
                    // Only one ? ! = is allowed and only as the last character
                    stream.eat(/[\?\!\=]/);
                    return "atom";
                }
                return "operator";
            } else if (ch == "@" && stream.match(/^@?[a-zA-Z_\xa1-\uffff]/)) {
                stream.eat("@");
                stream.eatWhile(/[\w\xa1-\uffff]/);
                return "variable-2";
            } else if (ch == "$") {
                if (stream.eat(/[a-zA-Z_]/)) {
                    stream.eatWhile(/[\w]/);
                } else if (stream.eat(/\d/)) {
                    stream.eat(/\d/);
                } else {
                    stream.next(); // Must be a special global like $: or $!
                }
                return "variable-3";
            } else if (/[a-zA-Z_\xa1-\uffff]/.test(ch)) {
                stream.eatWhile(/[\w\xa1-\uffff]/);
                stream.eat(/[\?\!]/);
                if (stream.eat(":")) return "atom";
                return "ident";
            } else if (ch == "|" && (state.varList || state.lastTok == "{" || state.lastTok == "do")) {
                curPunc = "|";
                return null;
            } else if (/[\(\)\[\]{}\\;]/.test(ch)) {
                curPunc = ch;
                return null;
            } else if (ch == "-" && stream.eat(">")) {
                return "arrow";
            } else if (/[=+\-\/*:\.^%<>~|]/.test(ch)) {
                var more = stream.eatWhile(/[=+\-\/*:\.^%<>~|]/);
                if (ch == "." && !more) curPunc = ".";
                return "operator";
            } else {
                return null;
            }
        };
        var regexpAhead = function regexpAhead(stream) {
            var start = stream.pos, depth = 0, next, found = false, escaped = false;
            while((next = stream.next()) != null){
                if (!escaped) {
                    if ("[{(".indexOf(next) > -1) {
                        depth++;
                    } else if ("]})".indexOf(next) > -1) {
                        depth--;
                        if (depth < 0) break;
                    } else if (next == "/" && depth == 0) {
                        found = true;
                        break;
                    }
                    escaped = next == "\\";
                } else {
                    escaped = false;
                }
            }
            stream.backUp(stream.pos - start);
            return found;
        };
        var tokenBaseOnce = function tokenBaseOnce() {
            var alreadyCalled = false;
            return function(stream, state) {
                if (alreadyCalled) {
                    state.tokenize.pop();
                    return state.tokenize[state.tokenize.length - 1](stream, state);
                }
                alreadyCalled = true;
                return tokenBase(stream, state);
            };
        };
        var readQuoted = function readQuoted(quote, style, embed, unescaped) {
            return function(stream, state) {
                var escaped = false, ch;
                if (state.context.type === "read-quoted-paused") {
                    state.context = state.context.prev;
                    stream.eat("}");
                }
                while((ch = stream.next()) != null){
                    if (ch == quote && (unescaped || !escaped)) {
                        state.tokenize.pop();
                        break;
                    }
                    if (embed && ch == "#" && !escaped) {
                        if (stream.eat("{")) {
                            if (quote == "}") {
                                state.context = {
                                    prev: state.context,
                                    type: "read-quoted-paused"
                                };
                            }
                            state.tokenize.push(tokenBaseUntilBrace());
                            break;
                        } else if (/[@\$]/.test(stream.peek())) {
                            state.tokenize.push(tokenBaseOnce());
                            break;
                        }
                    }
                    escaped = !escaped && ch == "\\";
                }
                return style;
            };
        };
        var readHereDoc = function readHereDoc(phrase, mayIndent) {
            return function(stream, state) {
                if (mayIndent) stream.eatSpace();
                if (stream.match(phrase)) state.tokenize.pop();
                else stream.skipToEnd();
                return "string";
            };
        };
        var readBlockComment = function readBlockComment(stream, state) {
            if (stream.sol() && stream.match("=end") && stream.eol()) state.tokenize.pop();
            stream.skipToEnd();
            return "comment";
        };
        var curPunc;
        function tokenBaseUntilBrace(depth) {
            if (!depth) depth = 1;
            return function(stream, state) {
                if (stream.peek() == "}") {
                    if (depth == 1) {
                        state.tokenize.pop();
                        return state.tokenize[state.tokenize.length - 1](stream, state);
                    } else {
                        state.tokenize[state.tokenize.length - 1] = tokenBaseUntilBrace(depth - 1);
                    }
                } else if (stream.peek() == "{") {
                    state.tokenize[state.tokenize.length - 1] = tokenBaseUntilBrace(depth + 1);
                }
                return tokenBase(stream, state);
            };
        }
        return {
            startState: function startState() {
                return {
                    tokenize: [
                        tokenBase
                    ],
                    indented: 0,
                    context: {
                        type: "top",
                        indented: -config.indentUnit
                    },
                    continuedLine: false,
                    lastTok: null,
                    varList: false
                };
            },
            token: function token(stream, state) {
                curPunc = null;
                if (stream.sol()) state.indented = stream.indentation();
                var style = state.tokenize[state.tokenize.length - 1](stream, state), kwtype;
                var thisTok = curPunc;
                if (style == "ident") {
                    var word = stream.current();
                    style = state.lastTok == "." ? "property" : keywords.propertyIsEnumerable(stream.current()) ? "keyword" : /^[A-Z]/.test(word) ? "tag" : state.lastTok == "def" || state.lastTok == "class" || state.varList ? "def" : "variable";
                    if (style == "keyword") {
                        thisTok = word;
                        if (indentWords.propertyIsEnumerable(word)) kwtype = "indent";
                        else if (dedentWords.propertyIsEnumerable(word)) kwtype = "dedent";
                        else if ((word == "if" || word == "unless") && stream.column() == stream.indentation()) kwtype = "indent";
                        else if (word == "do" && state.context.indented < state.indented) kwtype = "indent";
                    }
                }
                if (curPunc || style && style != "comment") state.lastTok = thisTok;
                if (curPunc == "|") state.varList = !state.varList;
                if (kwtype == "indent" || /[\(\[\{]/.test(curPunc)) state.context = {
                    prev: state.context,
                    type: curPunc || style,
                    indented: state.indented
                };
                else if ((kwtype == "dedent" || /[\)\]\}]/.test(curPunc)) && state.context.prev) state.context = state.context.prev;
                if (stream.eol()) state.continuedLine = curPunc == "\\" || style == "operator";
                return style;
            },
            indent: function indent(state, textAfter) {
                if (state.tokenize[state.tokenize.length - 1] != tokenBase) return CodeMirror1.Pass;
                var firstChar = textAfter && textAfter.charAt(0);
                var ct = state.context;
                var closed = ct.type == closing[firstChar] || ct.type == "keyword" && /^(?:end|until|else|elsif|when|rescue)\b/.test(textAfter);
                return ct.indented + (closed ? 0 : config.indentUnit) + (state.continuedLine ? config.indentUnit : 0);
            },
            electricInput: /^\s*(?:end|rescue|elsif|else|\})$/,
            lineComment: "#",
            fold: "indent"
        };
    });
    CodeMirror1.defineMIME("text/x-ruby", "ruby");
    CodeMirror1.registerHelper("hintWords", "ruby", keywordList);
});
});

var sass = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, css);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("sass", function(config) {
        var tokenRegexp = function tokenRegexp(words) {
            return new RegExp("^" + words.join("|"));
        };
        var isEndLine = function isEndLine(stream) {
            return !stream.peek() || stream.match(/\s+$/, false);
        };
        var urlTokens = function urlTokens(stream, state) {
            var ch = stream.peek();
            if (ch === ")") {
                stream.next();
                state.tokenizer = tokenBase;
                return "operator";
            } else if (ch === "(") {
                stream.next();
                stream.eatSpace();
                return "operator";
            } else if (ch === "'" || ch === '"') {
                state.tokenizer = buildStringTokenizer(stream.next());
                return "string";
            } else {
                state.tokenizer = buildStringTokenizer(")", false);
                return "string";
            }
        };
        var comment = function comment(indentation, multiLine) {
            return function(stream, state) {
                if (stream.sol() && stream.indentation() <= indentation) {
                    state.tokenizer = tokenBase;
                    return tokenBase(stream, state);
                }
                if (multiLine && stream.skipTo("*/")) {
                    stream.next();
                    stream.next();
                    state.tokenizer = tokenBase;
                } else {
                    stream.skipToEnd();
                }
                return "comment";
            };
        };
        var buildStringTokenizer = function buildStringTokenizer(quote, greedy) {
            if (greedy == null) {
                greedy = true;
            }
            function stringTokenizer(stream, state) {
                var nextChar = stream.next();
                var peekChar = stream.peek();
                var previousChar = stream.string.charAt(stream.pos - 2);
                var endingString = nextChar !== "\\" && peekChar === quote || nextChar === quote && previousChar !== "\\";
                if (endingString) {
                    if (nextChar !== quote && greedy) {
                        stream.next();
                    }
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    state.tokenizer = tokenBase;
                    return "string";
                } else if (nextChar === "#" && peekChar === "{") {
                    state.tokenizer = buildInterpolationTokenizer(stringTokenizer);
                    stream.next();
                    return "operator";
                } else {
                    return "string";
                }
            }
            return stringTokenizer;
        };
        var buildInterpolationTokenizer = function buildInterpolationTokenizer(currentTokenizer) {
            return function(stream, state) {
                if (stream.peek() === "}") {
                    stream.next();
                    state.tokenizer = currentTokenizer;
                    return "operator";
                } else {
                    return tokenBase(stream, state);
                }
            };
        };
        var indent = function indent(state) {
            if (state.indentCount == 0) {
                state.indentCount++;
                var lastScopeOffset = state.scopes[0].offset;
                var currentOffset = lastScopeOffset + config.indentUnit;
                state.scopes.unshift({
                    offset: currentOffset
                });
            }
        };
        var dedent = function dedent(state) {
            if (state.scopes.length == 1) return;
            state.scopes.shift();
        };
        var tokenLexer = function tokenLexer(stream, state) {
            if (stream.sol()) state.indentCount = 0;
            var style = state.tokenizer(stream, state);
            var current = stream.current();
            if (current === "@return" || current === "}") {
                dedent(state);
            }
            if (style !== null) {
                var startOfToken = stream.pos - current.length;
                var withCurrentIndent = startOfToken + config.indentUnit * state.indentCount;
                var newScopes = [];
                for(var i = 0; i < state.scopes.length; i++){
                    var scope = state.scopes[i];
                    if (scope.offset <= withCurrentIndent) newScopes.push(scope);
                }
                state.scopes = newScopes;
            }
            return style;
        };
        var cssMode = CodeMirror1.mimeModes["text/css"];
        var propertyKeywords = cssMode.propertyKeywords || {}, colorKeywords = cssMode.colorKeywords || {}, valueKeywords = cssMode.valueKeywords || {}, fontProperties = cssMode.fontProperties || {};
        var keywords = [
            "true",
            "false",
            "null",
            "auto"
        ];
        var keywordsRegexp = new RegExp("^" + keywords.join("|"));
        var operators = [
            "\\(",
            "\\)",
            "=",
            ">",
            "<",
            "==",
            ">=",
            "<=",
            "\\+",
            "-",
            "\\!=",
            "/",
            "\\*",
            "%",
            "and",
            "or",
            "not",
            ";",
            "\\{",
            "\\}",
            ":"
        ];
        var opRegexp = tokenRegexp(operators);
        var pseudoElementsRegexp = /^::?[a-zA-Z_][\w\-]*/;
        var word;
        function tokenBase(stream, state) {
            var ch = stream.peek();
            // Comment
            if (stream.match("/*")) {
                state.tokenizer = comment(stream.indentation(), true);
                return state.tokenizer(stream, state);
            }
            if (stream.match("//")) {
                state.tokenizer = comment(stream.indentation(), false);
                return state.tokenizer(stream, state);
            }
            // Interpolation
            if (stream.match("#{")) {
                state.tokenizer = buildInterpolationTokenizer(tokenBase);
                return "operator";
            }
            // Strings
            if (ch === '"' || ch === "'") {
                stream.next();
                state.tokenizer = buildStringTokenizer(ch);
                return "string";
            }
            if (!state.cursorHalf) {
                // first half i.e. before : for key-value pairs
                // including selectors
                if (ch === "-") {
                    if (stream.match(/^-\w+-/)) {
                        return "meta";
                    }
                }
                if (ch === ".") {
                    stream.next();
                    if (stream.match(/^[\w-]+/)) {
                        indent(state);
                        return "qualifier";
                    } else if (stream.peek() === "#") {
                        indent(state);
                        return "tag";
                    }
                }
                if (ch === "#") {
                    stream.next();
                    // ID selectors
                    if (stream.match(/^[\w-]+/)) {
                        indent(state);
                        return "builtin";
                    }
                    if (stream.peek() === "#") {
                        indent(state);
                        return "tag";
                    }
                }
                // Variables
                if (ch === "$") {
                    stream.next();
                    stream.eatWhile(/[\w-]/);
                    return "variable-2";
                }
                // Numbers
                if (stream.match(/^-?[0-9\.]+/)) return "number";
                // Units
                if (stream.match(/^(px|em|in)\b/)) return "unit";
                if (stream.match(keywordsRegexp)) return "keyword";
                if (stream.match(/^url/) && stream.peek() === "(") {
                    state.tokenizer = urlTokens;
                    return "atom";
                }
                if (ch === "=") {
                    // Match shortcut mixin definition
                    if (stream.match(/^=[\w-]+/)) {
                        indent(state);
                        return "meta";
                    }
                }
                if (ch === "+") {
                    // Match shortcut mixin definition
                    if (stream.match(/^\+[\w-]+/)) {
                        return "variable-3";
                    }
                }
                if (ch === "@") {
                    if (stream.match("@extend")) {
                        if (!stream.match(/\s*[\w]/)) dedent(state);
                    }
                }
                // Indent Directives
                if (stream.match(/^@(else if|if|media|else|for|each|while|mixin|function)/)) {
                    indent(state);
                    return "def";
                }
                // Other Directives
                if (ch === "@") {
                    stream.next();
                    stream.eatWhile(/[\w-]/);
                    return "def";
                }
                if (stream.eatWhile(/[\w-]/)) {
                    if (stream.match(/ *: *[\w-\+\$#!\("']/, false)) {
                        word = stream.current().toLowerCase();
                        var prop = state.prevProp + "-" + word;
                        if (propertyKeywords.hasOwnProperty(prop)) {
                            return "property";
                        } else if (propertyKeywords.hasOwnProperty(word)) {
                            state.prevProp = word;
                            return "property";
                        } else if (fontProperties.hasOwnProperty(word)) {
                            return "property";
                        }
                        return "tag";
                    } else if (stream.match(/ *:/, false)) {
                        indent(state);
                        state.cursorHalf = 1;
                        state.prevProp = stream.current().toLowerCase();
                        return "property";
                    } else if (stream.match(/ *,/, false)) {
                        return "tag";
                    } else {
                        indent(state);
                        return "tag";
                    }
                }
                if (ch === ":") {
                    if (stream.match(pseudoElementsRegexp)) {
                        return "variable-3";
                    }
                    stream.next();
                    state.cursorHalf = 1;
                    return "operator";
                }
            } else {
                if (ch === "#") {
                    stream.next();
                    // Hex numbers
                    if (stream.match(/[0-9a-fA-F]{6}|[0-9a-fA-F]{3}/)) {
                        if (isEndLine(stream)) {
                            state.cursorHalf = 0;
                        }
                        return "number";
                    }
                }
                // Numbers
                if (stream.match(/^-?[0-9\.]+/)) {
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "number";
                }
                // Units
                if (stream.match(/^(px|em|in)\b/)) {
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "unit";
                }
                if (stream.match(keywordsRegexp)) {
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "keyword";
                }
                if (stream.match(/^url/) && stream.peek() === "(") {
                    state.tokenizer = urlTokens;
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "atom";
                }
                // Variables
                if (ch === "$") {
                    stream.next();
                    stream.eatWhile(/[\w-]/);
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "variable-2";
                }
                // bang character for !important, !default, etc.
                if (ch === "!") {
                    stream.next();
                    state.cursorHalf = 0;
                    return stream.match(/^[\w]+/) ? "keyword" : "operator";
                }
                if (stream.match(opRegexp)) {
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    return "operator";
                }
                // attributes
                if (stream.eatWhile(/[\w-]/)) {
                    if (isEndLine(stream)) {
                        state.cursorHalf = 0;
                    }
                    word = stream.current().toLowerCase();
                    if (valueKeywords.hasOwnProperty(word)) {
                        return "atom";
                    } else if (colorKeywords.hasOwnProperty(word)) {
                        return "keyword";
                    } else if (propertyKeywords.hasOwnProperty(word)) {
                        state.prevProp = stream.current().toLowerCase();
                        return "property";
                    } else {
                        return "tag";
                    }
                }
                //stream.eatSpace();
                if (isEndLine(stream)) {
                    state.cursorHalf = 0;
                    return null;
                }
            } // else ends here
            if (stream.match(opRegexp)) return "operator";
            // If we haven't returned by now, we move 1 character
            // and return an error
            stream.next();
            return null;
        }
        return {
            startState: function startState() {
                return {
                    tokenizer: tokenBase,
                    scopes: [
                        {
                            offset: 0,
                            type: "sass"
                        }
                    ],
                    indentCount: 0,
                    cursorHalf: 0,
                    // or before (0) colon (well... more or less)
                    definedVars: [],
                    definedMixins: []
                };
            },
            token: function token(stream, state) {
                var style = tokenLexer(stream, state);
                state.lastToken = {
                    style: style,
                    content: stream.current()
                };
                return style;
            },
            indent: function indent(state) {
                return state.scopes[0].offset;
            },
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            lineComment: "//",
            fold: "indent"
        };
    }, "css");
    CodeMirror1.defineMIME("text/x-sass", "sass");
});
});

var shell = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("shell", function() {
        var define1 = function define1(style, dict) {
            for(var i = 0; i < dict.length; i++){
                words[dict[i]] = style;
            }
        };
        var tokenBase = function tokenBase(stream, state) {
            if (stream.eatSpace()) return null;
            var sol = stream.sol();
            var ch = stream.next();
            if (ch === "\\") {
                stream.next();
                return null;
            }
            if (ch === "'" || ch === '"' || ch === "`") {
                state.tokens.unshift(tokenString(ch, ch === "`" ? "quote" : "string"));
                return tokenize(stream, state);
            }
            if (ch === "#") {
                if (sol && stream.eat("!")) {
                    stream.skipToEnd();
                    return "meta"; // 'comment'?
                }
                stream.skipToEnd();
                return "comment";
            }
            if (ch === "$") {
                state.tokens.unshift(tokenDollar);
                return tokenize(stream, state);
            }
            if (ch === "+" || ch === "=") {
                return "operator";
            }
            if (ch === "-") {
                stream.eat("-");
                stream.eatWhile(/\w/);
                return "attribute";
            }
            if (ch == "<") {
                if (stream.match("<<")) return "operator";
                var heredoc = stream.match(/^<-?\s*['"]?([^'"]*)['"]?/);
                if (heredoc) {
                    state.tokens.unshift(tokenHeredoc(heredoc[1]));
                    return "string-2";
                }
            }
            if (/\d/.test(ch)) {
                stream.eatWhile(/\d/);
                if (stream.eol() || !/\w/.test(stream.peek())) {
                    return "number";
                }
            }
            stream.eatWhile(/[\w-]/);
            var cur = stream.current();
            if (stream.peek() === "=" && /\w+/.test(cur)) return "def";
            return words.hasOwnProperty(cur) ? words[cur] : null;
        };
        var tokenStringStart = function tokenStringStart(quote, style) {
            return function(stream, state) {
                state.tokens[0] = tokenString(quote, style);
                stream.next();
                return tokenize(stream, state);
            };
        };
        var tokenHeredoc = function tokenHeredoc(delim) {
            return function(stream, state) {
                if (stream.sol() && stream.string == delim) state.tokens.shift();
                stream.skipToEnd();
                return "string-2";
            };
        };
        var tokenize = function tokenize(stream, state) {
            return (state.tokens[0] || tokenBase)(stream, state);
        };
        var words = {};
        var commonAtoms = [
            "true",
            "false"
        ];
        var commonKeywords = [
            "if",
            "then",
            "do",
            "else",
            "elif",
            "while",
            "until",
            "for",
            "in",
            "esac",
            "fi",
            "fin",
            "fil",
            "done",
            "exit",
            "set",
            "unset",
            "export",
            "function"
        ];
        var commonCommands = [
            "ab",
            "awk",
            "bash",
            "beep",
            "cat",
            "cc",
            "cd",
            "chown",
            "chmod",
            "chroot",
            "clear",
            "cp",
            "curl",
            "cut",
            "diff",
            "echo",
            "find",
            "gawk",
            "gcc",
            "get",
            "git",
            "grep",
            "hg",
            "kill",
            "killall",
            "ln",
            "ls",
            "make",
            "mkdir",
            "openssl",
            "mv",
            "nc",
            "nl",
            "node",
            "npm",
            "ping",
            "ps",
            "restart",
            "rm",
            "rmdir",
            "sed",
            "service",
            "sh",
            "shopt",
            "shred",
            "source",
            "sort",
            "sleep",
            "ssh",
            "start",
            "stop",
            "su",
            "sudo",
            "svn",
            "tee",
            "telnet",
            "top",
            "touch",
            "vi",
            "vim",
            "wall",
            "wc",
            "wget",
            "who",
            "write",
            "yes",
            "zsh"
        ];
        CodeMirror1.registerHelper("hintWords", "shell", commonAtoms.concat(commonKeywords, commonCommands));
        define1("atom", commonAtoms);
        define1("keyword", commonKeywords);
        define1("builtin", commonCommands);
        function tokenString(quote, style) {
            var close = quote == "(" ? ")" : quote == "{" ? "}" : quote;
            return function(stream, state) {
                var next, escaped = false;
                while((next = stream.next()) != null){
                    if (next === close && !escaped) {
                        state.tokens.shift();
                        break;
                    } else if (next === "$" && !escaped && quote !== "'" && stream.peek() != close) {
                        escaped = true;
                        stream.backUp(1);
                        state.tokens.unshift(tokenDollar);
                        break;
                    } else if (!escaped && quote !== close && next === quote) {
                        state.tokens.unshift(tokenString(quote, style));
                        return tokenize(stream, state);
                    } else if (!escaped && /['"]/.test(next) && !/['"]/.test(quote)) {
                        state.tokens.unshift(tokenStringStart(next, "string"));
                        stream.backUp(1);
                        break;
                    }
                    escaped = !escaped && next === "\\";
                }
                return style;
            };
        }
        var tokenDollar = function tokenDollar(stream, state) {
            if (state.tokens.length > 1) stream.eat("$");
            var ch = stream.next();
            if (/['"({]/.test(ch)) {
                state.tokens[0] = tokenString(ch, ch == "(" ? "quote" : ch == "{" ? "def" : "string");
                return tokenize(stream, state);
            }
            if (!/\d/.test(ch)) stream.eatWhile(/\w/);
            state.tokens.shift();
            return "def";
        };
        return {
            startState: function startState() {
                return {
                    tokens: []
                };
            },
            token: function token(stream, state) {
                return tokenize(stream, state);
            },
            closeBrackets: "()[]{}''\"\"``",
            lineComment: "#",
            fold: "brace"
        };
    });
    CodeMirror1.defineMIME("text/x-sh", "shell");
    // Apache uses a slightly different Media Type for Shell scripts
    // http://svn.apache.org/repos/asf/httpd/httpd/trunk/docs/conf/mime.types
    CodeMirror1.defineMIME("application/x-sh", "shell");
});
});

var sql = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var hookIdentifier = // `identifier`
    function hookIdentifier(stream) {
        // MySQL/MariaDB identifiers
        // ref: http://dev.mysql.com/doc/refman/5.6/en/identifier-qualifiers.html
        var ch;
        while((ch = stream.next()) != null){
            if (ch == "`" && !stream.eat("`")) return "variable-2";
        }
        stream.backUp(stream.current().length - 1);
        return stream.eatWhile(/\w/) ? "variable-2" : null;
    };
    var hookIdentifierDoublequote = // "identifier"
    function hookIdentifierDoublequote(stream) {
        // Standard SQL /SQLite identifiers
        // ref: http://web.archive.org/web/20160813185132/http://savage.net.au/SQL/sql-99.bnf.html#delimited%20identifier
        // ref: http://sqlite.org/lang_keywords.html
        var ch;
        while((ch = stream.next()) != null){
            if (ch == '"' && !stream.eat('"')) return "variable-2";
        }
        stream.backUp(stream.current().length - 1);
        return stream.eatWhile(/\w/) ? "variable-2" : null;
    };
    var hookVar = // variable token
    function hookVar(stream) {
        // variables
        // @@prefix.varName @varName
        // varName can be quoted with ` or ' or "
        // ref: http://dev.mysql.com/doc/refman/5.5/en/user-variables.html
        if (stream.eat("@")) {
            stream.match("session.");
            stream.match("local.");
            stream.match("global.");
        }
        if (stream.eat("'")) {
            stream.match(/^.*'/);
            return "variable-2";
        } else if (stream.eat('"')) {
            stream.match(/^.*"/);
            return "variable-2";
        } else if (stream.eat("`")) {
            stream.match(/^.*`/);
            return "variable-2";
        } else if (stream.match(/^[0-9a-zA-Z$\.\_]+/)) {
            return "variable-2";
        }
        return null;
    };
    var hookClient = // short client keyword token
    function hookClient(stream) {
        // \N means NULL
        // ref: http://dev.mysql.com/doc/refman/5.5/en/null-values.html
        if (stream.eat("N")) {
            return "atom";
        }
        // \g, etc
        // ref: http://dev.mysql.com/doc/refman/5.5/en/mysql-commands.html
        return stream.match(/^[a-zA-Z.#!?]/) ? "variable-2" : null;
    };
    var set = // turn a space-separated list into an array
    function set(str) {
        var obj = {}, words = str.split(" ");
        for(var i = 0; i < words.length; ++i)obj[words[i]] = true;
        return obj;
    };
    CodeMirror1.defineMode("sql", function(config, parserConfig) {
        var tokenBase = function tokenBase(stream, state) {
            var ch = stream.next();
            // call hooks from the mime type
            if (hooks[ch]) {
                var result = hooks[ch](stream, state);
                if (result !== false) return result;
            }
            if (support.hexNumber && (ch == "0" && stream.match(/^[xX][0-9a-fA-F]+/) || (ch == "x" || ch == "X") && stream.match(/^'[0-9a-fA-F]+'/))) {
                // hex
                // ref: http://dev.mysql.com/doc/refman/5.5/en/hexadecimal-literals.html
                return "number";
            } else if (support.binaryNumber && ((ch == "b" || ch == "B") && stream.match(/^'[01]+'/) || ch == "0" && stream.match(/^b[01]+/))) {
                // bitstring
                // ref: http://dev.mysql.com/doc/refman/5.5/en/bit-field-literals.html
                return "number";
            } else if (ch.charCodeAt(0) > 47 && ch.charCodeAt(0) < 58) {
                // numbers
                // ref: http://dev.mysql.com/doc/refman/5.5/en/number-literals.html
                stream.match(/^[0-9]*(\.[0-9]+)?([eE][-+]?[0-9]+)?/);
                support.decimallessFloat && stream.match(/^\.(?!\.)/);
                return "number";
            } else if (ch == "?" && (stream.eatSpace() || stream.eol() || stream.eat(";"))) {
                // placeholders
                return "variable-3";
            } else if (ch == "'" || ch == '"' && support.doubleQuote) {
                // strings
                // ref: http://dev.mysql.com/doc/refman/5.5/en/string-literals.html
                state.tokenize = tokenLiteral(ch);
                return state.tokenize(stream, state);
            } else if ((support.nCharCast && (ch == "n" || ch == "N") || support.charsetCast && ch == "_" && stream.match(/[a-z][a-z0-9]*/i)) && (stream.peek() == "'" || stream.peek() == '"')) {
                // charset casting: _utf8'str', N'str', n'str'
                // ref: http://dev.mysql.com/doc/refman/5.5/en/string-literals.html
                return "keyword";
            } else if (support.escapeConstant && (ch == "e" || ch == "E") && (stream.peek() == "'" || stream.peek() == '"' && support.doubleQuote)) {
                // escape constant: E'str', e'str'
                // ref: https://www.postgresql.org/docs/current/sql-syntax-lexical.html#SQL-SYNTAX-STRINGS-ESCAPE
                state.tokenize = function(stream, state) {
                    return (state.tokenize = tokenLiteral(stream.next(), true))(stream, state);
                };
                return "keyword";
            } else if (support.commentSlashSlash && ch == "/" && stream.eat("/")) {
                // 1-line comment
                stream.skipToEnd();
                return "comment";
            } else if (support.commentHash && ch == "#" || ch == "-" && stream.eat("-") && (!support.commentSpaceRequired || stream.eat(" "))) {
                // 1-line comments
                // ref: https://kb.askmonty.org/en/comment-syntax/
                stream.skipToEnd();
                return "comment";
            } else if (ch == "/" && stream.eat("*")) {
                // multi-line comments
                // ref: https://kb.askmonty.org/en/comment-syntax/
                state.tokenize = tokenComment(1);
                return state.tokenize(stream, state);
            } else if (ch == ".") {
                // .1 for 0.1
                if (support.zerolessFloat && stream.match(/^(?:\d+(?:e[+-]?\d+)?)/i)) return "number";
                if (stream.match(/^\.+/)) return null;
                // .table_name (ODBC)
                // // ref: http://dev.mysql.com/doc/refman/5.6/en/identifier-qualifiers.html
                if (support.ODBCdotTable && stream.match(/^[\w\d_$#]+/)) return "variable-2";
            } else if (operatorChars.test(ch)) {
                // operators
                stream.eatWhile(operatorChars);
                return "operator";
            } else if (brackets.test(ch)) {
                // brackets
                return "bracket";
            } else if (punctuation.test(ch)) {
                // punctuation
                stream.eatWhile(punctuation);
                return "punctuation";
            } else if (ch == "{" && (stream.match(/^( )*(d|D|t|T|ts|TS)( )*'[^']*'( )*}/) || stream.match(/^( )*(d|D|t|T|ts|TS)( )*"[^"]*"( )*}/))) {
                // dates (weird ODBC syntax)
                // ref: http://dev.mysql.com/doc/refman/5.5/en/date-and-time-literals.html
                return "number";
            } else {
                stream.eatWhile(/^[_\w\d]/);
                var word = stream.current().toLowerCase();
                // dates (standard SQL syntax)
                // ref: http://dev.mysql.com/doc/refman/5.5/en/date-and-time-literals.html
                if (dateSQL.hasOwnProperty(word) && (stream.match(/^( )+'[^']*'/) || stream.match(/^( )+"[^"]*"/))) return "number";
                if (atoms.hasOwnProperty(word)) return "atom";
                if (builtin.hasOwnProperty(word)) return "builtin";
                if (keywords.hasOwnProperty(word)) return "keyword";
                if (client.hasOwnProperty(word)) return "string-2";
                return null;
            }
        };
        var tokenLiteral = // 'string', with char specified in quote escaped by '\'
        function tokenLiteral(quote, backslashEscapes) {
            return function(stream, state) {
                var escaped = false, ch;
                while((ch = stream.next()) != null){
                    if (ch == quote && !escaped) {
                        state.tokenize = tokenBase;
                        break;
                    }
                    escaped = (backslashStringEscapes || backslashEscapes) && !escaped && ch == "\\";
                }
                return "string";
            };
        };
        var pushContext = function pushContext(stream, state, type) {
            state.context = {
                prev: state.context,
                indent: stream.indentation(),
                col: stream.column(),
                type: type
            };
        };
        var popContext = function popContext(state) {
            state.indent = state.context.indent;
            state.context = state.context.prev;
        };
        var client = parserConfig.client || {}, atoms = parserConfig.atoms || {
            "false": true,
            "true": true,
            "null": true
        }, builtin = parserConfig.builtin || set(defaultBuiltin), keywords = parserConfig.keywords || set(sqlKeywords), operatorChars = parserConfig.operatorChars || /^[*+\-%<>!=&|~^\/]/, support = parserConfig.support || {}, hooks = parserConfig.hooks || {}, dateSQL = parserConfig.dateSQL || {
            "date": true,
            "time": true,
            "timestamp": true
        }, backslashStringEscapes = parserConfig.backslashStringEscapes !== false, brackets = parserConfig.brackets || /^[\{}\(\)\[\]]/, punctuation = parserConfig.punctuation || /^[;.,:]/;
        function tokenComment(depth) {
            return function(stream, state) {
                var m = stream.match(/^.*?(\/\*|\*\/)/);
                if (!m) stream.skipToEnd();
                else if (m[1] == "/*") state.tokenize = tokenComment(depth + 1);
                else if (depth > 1) state.tokenize = tokenComment(depth - 1);
                else state.tokenize = tokenBase;
                return "comment";
            };
        }
        return {
            startState: function startState() {
                return {
                    tokenize: tokenBase,
                    context: null
                };
            },
            token: function token(stream, state) {
                if (stream.sol()) {
                    if (state.context && state.context.align == null) state.context.align = false;
                }
                if (state.tokenize == tokenBase && stream.eatSpace()) return null;
                var style = state.tokenize(stream, state);
                if (style == "comment") return style;
                if (state.context && state.context.align == null) state.context.align = true;
                var tok = stream.current();
                if (tok == "(") pushContext(stream, state, ")");
                else if (tok == "[") pushContext(stream, state, "]");
                else if (state.context && state.context.type == tok) popContext(state);
                return style;
            },
            indent: function indent(state, textAfter) {
                var cx = state.context;
                if (!cx) return CodeMirror1.Pass;
                var closing = textAfter.charAt(0) == cx.type;
                if (cx.align) return cx.col + (closing ? 0 : 1);
                else return cx.indent + (closing ? 0 : config.indentUnit);
            },
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            lineComment: support.commentSlashSlash ? "//" : support.commentHash ? "#" : "--",
            closeBrackets: "()[]{}''\"\"``"
        };
    });
    // these keywords are used by all SQL dialects (however, a mode can still overwrite it)
    var sqlKeywords = "alter and as asc between by count create delete desc distinct drop from group having in insert into is join like not on or order select set table union update values where limit ";
    var defaultBuiltin = "bool boolean bit blob enum long longblob longtext medium mediumblob mediumint mediumtext time timestamp tinyblob tinyint tinytext text bigint int int1 int2 int3 int4 int8 integer float float4 float8 double char varbinary varchar varcharacter precision real date datetime year unsigned signed decimal numeric";
    // A generic SQL Mode. It's not a standard, it just try to support what is generally supported
    CodeMirror1.defineMIME("text/x-sql", {
        name: "sql",
        keywords: set(sqlKeywords + "begin"),
        builtin: set(defaultBuiltin),
        atoms: set("false true null unknown"),
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable doubleQuote binaryNumber hexNumber")
    });
    CodeMirror1.defineMIME("text/x-mssql", {
        name: "sql",
        client: set("$partition binary_checksum checksum connectionproperty context_info current_request_id error_line error_message error_number error_procedure error_severity error_state formatmessage get_filestream_transaction_context getansinull host_id host_name isnull isnumeric min_active_rowversion newid newsequentialid rowcount_big xact_state object_id"),
        keywords: set(sqlKeywords + "begin trigger proc view index for add constraint key primary foreign collate clustered nonclustered declare exec go if use index holdlock nolock nowait paglock readcommitted readcommittedlock readpast readuncommitted repeatableread rowlock serializable snapshot tablock tablockx updlock with"),
        builtin: set("bigint numeric bit smallint decimal smallmoney int tinyint money float real char varchar text nchar nvarchar ntext binary varbinary image cursor timestamp hierarchyid uniqueidentifier sql_variant xml table "),
        atoms: set("is not null like and or in left right between inner outer join all any some cross unpivot pivot exists"),
        operatorChars: /^[*+\-%<>!=^\&|\/]/,
        brackets: /^[\{}\(\)]/,
        punctuation: /^[;.,:/]/,
        backslashStringEscapes: false,
        dateSQL: set("date datetimeoffset datetime2 smalldatetime datetime time"),
        hooks: {
            "@": hookVar
        }
    });
    CodeMirror1.defineMIME("text/x-mysql", {
        name: "sql",
        client: set("charset clear connect edit ego exit go help nopager notee nowarning pager print prompt quit rehash source status system tee"),
        keywords: set(sqlKeywords + "accessible action add after algorithm all analyze asensitive at authors auto_increment autocommit avg avg_row_length before binary binlog both btree cache call cascade cascaded case catalog_name chain change changed character check checkpoint checksum class_origin client_statistics close coalesce code collate collation collations column columns comment commit committed completion concurrent condition connection consistent constraint contains continue contributors convert cross current current_date current_time current_timestamp current_user cursor data database databases day_hour day_microsecond day_minute day_second deallocate dec declare default delay_key_write delayed delimiter des_key_file describe deterministic dev_pop dev_samp deviance diagnostics directory disable discard distinctrow div dual dumpfile each elseif enable enclosed end ends engine engines enum errors escape escaped even event events every execute exists exit explain extended fast fetch field fields first flush for force foreign found_rows full fulltext function general get global grant grants group group_concat handler hash help high_priority hosts hour_microsecond hour_minute hour_second if ignore ignore_server_ids import index index_statistics infile inner innodb inout insensitive insert_method install interval invoker isolation iterate key keys kill language last leading leave left level limit linear lines list load local localtime localtimestamp lock logs low_priority master master_heartbeat_period master_ssl_verify_server_cert masters match max max_rows maxvalue message_text middleint migrate min min_rows minute_microsecond minute_second mod mode modifies modify mutex mysql_errno natural next no no_write_to_binlog offline offset one online open optimize option optionally out outer outfile pack_keys parser partition partitions password phase plugin plugins prepare preserve prev primary privileges procedure processlist profile profiles purge query quick range read read_write reads real rebuild recover references regexp relaylog release remove rename reorganize repair repeatable replace require resignal restrict resume return returns revoke right rlike rollback rollup row row_format rtree savepoint schedule schema schema_name schemas second_microsecond security sensitive separator serializable server session share show signal slave slow smallint snapshot soname spatial specific sql sql_big_result sql_buffer_result sql_cache sql_calc_found_rows sql_no_cache sql_small_result sqlexception sqlstate sqlwarning ssl start starting starts status std stddev stddev_pop stddev_samp storage straight_join subclass_origin sum suspend table_name table_statistics tables tablespace temporary terminated to trailing transaction trigger triggers truncate uncommitted undo uninstall unique unlock upgrade usage use use_frm user user_resources user_statistics using utc_date utc_time utc_timestamp value variables varying view views warnings when while with work write xa xor year_month zerofill begin do then else loop repeat"),
        builtin: set("bool boolean bit blob decimal double float long longblob longtext medium mediumblob mediumint mediumtext time timestamp tinyblob tinyint tinytext text bigint int int1 int2 int3 int4 int8 integer float float4 float8 double char varbinary varchar varcharacter precision date datetime year unsigned signed numeric"),
        atoms: set("false true null unknown"),
        operatorChars: /^[*+\-%<>!=&|^]/,
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable decimallessFloat zerolessFloat binaryNumber hexNumber doubleQuote nCharCast charsetCast commentHash commentSpaceRequired"),
        hooks: {
            "@": hookVar,
            "`": hookIdentifier,
            "\\": hookClient
        }
    });
    CodeMirror1.defineMIME("text/x-mariadb", {
        name: "sql",
        client: set("charset clear connect edit ego exit go help nopager notee nowarning pager print prompt quit rehash source status system tee"),
        keywords: set(sqlKeywords + "accessible action add after algorithm all always analyze asensitive at authors auto_increment autocommit avg avg_row_length before binary binlog both btree cache call cascade cascaded case catalog_name chain change changed character check checkpoint checksum class_origin client_statistics close coalesce code collate collation collations column columns comment commit committed completion concurrent condition connection consistent constraint contains continue contributors convert cross current current_date current_time current_timestamp current_user cursor data database databases day_hour day_microsecond day_minute day_second deallocate dec declare default delay_key_write delayed delimiter des_key_file describe deterministic dev_pop dev_samp deviance diagnostics directory disable discard distinctrow div dual dumpfile each elseif enable enclosed end ends engine engines enum errors escape escaped even event events every execute exists exit explain extended fast fetch field fields first flush for force foreign found_rows full fulltext function general generated get global grant grants group group_concat handler hard hash help high_priority hosts hour_microsecond hour_minute hour_second if ignore ignore_server_ids import index index_statistics infile inner innodb inout insensitive insert_method install interval invoker isolation iterate key keys kill language last leading leave left level limit linear lines list load local localtime localtimestamp lock logs low_priority master master_heartbeat_period master_ssl_verify_server_cert masters match max max_rows maxvalue message_text middleint migrate min min_rows minute_microsecond minute_second mod mode modifies modify mutex mysql_errno natural next no no_write_to_binlog offline offset one online open optimize option optionally out outer outfile pack_keys parser partition partitions password persistent phase plugin plugins prepare preserve prev primary privileges procedure processlist profile profiles purge query quick range read read_write reads real rebuild recover references regexp relaylog release remove rename reorganize repair repeatable replace require resignal restrict resume return returns revoke right rlike rollback rollup row row_format rtree savepoint schedule schema schema_name schemas second_microsecond security sensitive separator serializable server session share show shutdown signal slave slow smallint snapshot soft soname spatial specific sql sql_big_result sql_buffer_result sql_cache sql_calc_found_rows sql_no_cache sql_small_result sqlexception sqlstate sqlwarning ssl start starting starts status std stddev stddev_pop stddev_samp storage straight_join subclass_origin sum suspend table_name table_statistics tables tablespace temporary terminated to trailing transaction trigger triggers truncate uncommitted undo uninstall unique unlock upgrade usage use use_frm user user_resources user_statistics using utc_date utc_time utc_timestamp value variables varying view views virtual warnings when while with work write xa xor year_month zerofill begin do then else loop repeat"),
        builtin: set("bool boolean bit blob decimal double float long longblob longtext medium mediumblob mediumint mediumtext time timestamp tinyblob tinyint tinytext text bigint int int1 int2 int3 int4 int8 integer float float4 float8 double char varbinary varchar varcharacter precision date datetime year unsigned signed numeric"),
        atoms: set("false true null unknown"),
        operatorChars: /^[*+\-%<>!=&|^]/,
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable decimallessFloat zerolessFloat binaryNumber hexNumber doubleQuote nCharCast charsetCast commentHash commentSpaceRequired"),
        hooks: {
            "@": hookVar,
            "`": hookIdentifier,
            "\\": hookClient
        }
    });
    // provided by the phpLiteAdmin project - phpliteadmin.org
    CodeMirror1.defineMIME("text/x-sqlite", {
        name: "sql",
        // commands of the official SQLite client, ref: https://www.sqlite.org/cli.html#dotcmd
        client: set("auth backup bail binary changes check clone databases dbinfo dump echo eqp exit explain fullschema headers help import imposter indexes iotrace limit lint load log mode nullvalue once open output print prompt quit read restore save scanstats schema separator session shell show stats system tables testcase timeout timer trace vfsinfo vfslist vfsname width"),
        // ref: http://sqlite.org/lang_keywords.html
        keywords: set(sqlKeywords + "abort action add after all analyze attach autoincrement before begin cascade case cast check collate column commit conflict constraint cross current_date current_time current_timestamp database default deferrable deferred detach each else end escape except exclusive exists explain fail for foreign full glob if ignore immediate index indexed initially inner instead intersect isnull key left limit match natural no notnull null of offset outer plan pragma primary query raise recursive references regexp reindex release rename replace restrict right rollback row savepoint temp temporary then to transaction trigger unique using vacuum view virtual when with without"),
        // SQLite is weakly typed, ref: http://sqlite.org/datatype3.html. This is just a list of some common types.
        builtin: set("bool boolean bit blob decimal double float long longblob longtext medium mediumblob mediumint mediumtext time timestamp tinyblob tinyint tinytext text clob bigint int int2 int8 integer float double char varchar date datetime year unsigned signed numeric real"),
        // ref: http://sqlite.org/syntax/literal-value.html
        atoms: set("null current_date current_time current_timestamp"),
        // ref: http://sqlite.org/lang_expr.html#binaryops
        operatorChars: /^[*+\-%<>!=&|/~]/,
        // SQLite is weakly typed, ref: http://sqlite.org/datatype3.html. This is just a list of some common types.
        dateSQL: set("date time timestamp datetime"),
        support: set("decimallessFloat zerolessFloat"),
        identifierQuote: '"',
        hooks: {
            // bind-parameters ref:http://sqlite.org/lang_expr.html#varparam
            "@": hookVar,
            ":": hookVar,
            "?": hookVar,
            "$": hookVar,
            // The preferred way to escape Identifiers is using double quotes, ref: http://sqlite.org/lang_keywords.html
            '"': hookIdentifierDoublequote,
            // there is also support for backticks, ref: http://sqlite.org/lang_keywords.html
            "`": hookIdentifier
        }
    });
    // the query language used by Apache Cassandra is called CQL, but this mime type
    // is called Cassandra to avoid confusion with Contextual Query Language
    CodeMirror1.defineMIME("text/x-cassandra", {
        name: "sql",
        client: {},
        keywords: set("add all allow alter and any apply as asc authorize batch begin by clustering columnfamily compact consistency count create custom delete desc distinct drop each_quorum exists filtering from grant if in index insert into key keyspace keyspaces level limit local_one local_quorum modify nan norecursive nosuperuser not of on one order password permission permissions primary quorum rename revoke schema select set storage superuser table three to token truncate ttl two type unlogged update use user users using values where with writetime"),
        builtin: set("ascii bigint blob boolean counter decimal double float frozen inet int list map static text timestamp timeuuid tuple uuid varchar varint"),
        atoms: set("false true infinity NaN"),
        operatorChars: /^[<>=]/,
        dateSQL: {},
        support: set("commentSlashSlash decimallessFloat"),
        hooks: {}
    });
    // this is based on Peter Raganitsch's 'plsql' mode
    CodeMirror1.defineMIME("text/x-plsql", {
        name: "sql",
        client: set("appinfo arraysize autocommit autoprint autorecovery autotrace blockterminator break btitle cmdsep colsep compatibility compute concat copycommit copytypecheck define describe echo editfile embedded escape exec execute feedback flagger flush heading headsep instance linesize lno loboffset logsource long longchunksize markup native newpage numformat numwidth pagesize pause pno recsep recsepchar release repfooter repheader serveroutput shiftinout show showmode size spool sqlblanklines sqlcase sqlcode sqlcontinue sqlnumber sqlpluscompatibility sqlprefix sqlprompt sqlterminator suffix tab term termout time timing trimout trimspool ttitle underline verify version wrap"),
        keywords: set("abort accept access add all alter and any array arraylen as asc assert assign at attributes audit authorization avg base_table begin between binary_integer body boolean by case cast char char_base check close cluster clusters colauth column comment commit compress connect connected constant constraint crash create current currval cursor data_base database date dba deallocate debugoff debugon decimal declare default definition delay delete desc digits dispose distinct do drop else elseif elsif enable end entry escape exception exception_init exchange exclusive exists exit external fast fetch file for force form from function generic goto grant group having identified if immediate in increment index indexes indicator initial initrans insert interface intersect into is key level library like limited local lock log logging long loop master maxextents maxtrans member minextents minus mislabel mode modify multiset new next no noaudit nocompress nologging noparallel not nowait number_base object of off offline on online only open option or order out package parallel partition pctfree pctincrease pctused pls_integer positive positiven pragma primary prior private privileges procedure public raise range raw read rebuild record ref references refresh release rename replace resource restrict return returning returns reverse revoke rollback row rowid rowlabel rownum rows run savepoint schema segment select separate session set share snapshot some space split sql start statement storage subtype successful synonym tabauth table tables tablespace task terminate then to trigger truncate type union unique unlimited unrecoverable unusable update use using validate value values variable view views when whenever where while with work"),
        builtin: set("abs acos add_months ascii asin atan atan2 average bfile bfilename bigserial bit blob ceil character chartorowid chr clob concat convert cos cosh count dec decode deref dual dump dup_val_on_index empty error exp false float floor found glb greatest hextoraw initcap instr instrb int integer isopen last_day least length lengthb ln lower lpad ltrim lub make_ref max min mlslabel mod months_between natural naturaln nchar nclob new_time next_day nextval nls_charset_decl_len nls_charset_id nls_charset_name nls_initcap nls_lower nls_sort nls_upper nlssort no_data_found notfound null number numeric nvarchar2 nvl others power rawtohex real reftohex round rowcount rowidtochar rowtype rpad rtrim serial sign signtype sin sinh smallint soundex sqlcode sqlerrm sqrt stddev string substr substrb sum sysdate tan tanh to_char text to_date to_label to_multi_byte to_number to_single_byte translate true trunc uid unlogged upper user userenv varchar varchar2 variance varying vsize xml"),
        operatorChars: /^[*\/+\-%<>!=~]/,
        dateSQL: set("date time timestamp"),
        support: set("doubleQuote nCharCast zerolessFloat binaryNumber hexNumber")
    });
    // Created to support specific hive keywords
    CodeMirror1.defineMIME("text/x-hive", {
        name: "sql",
        keywords: set("select alter $elem$ $key$ $value$ add after all analyze and archive as asc before between binary both bucket buckets by cascade case cast change cluster clustered clusterstatus collection column columns comment compute concatenate continue create cross cursor data database databases dbproperties deferred delete delimited desc describe directory disable distinct distribute drop else enable end escaped exclusive exists explain export extended external fetch fields fileformat first format formatted from full function functions grant group having hold_ddltime idxproperties if import in index indexes inpath inputdriver inputformat insert intersect into is items join keys lateral left like limit lines load local location lock locks mapjoin materialized minus msck no_drop nocompress not of offline on option or order out outer outputdriver outputformat overwrite partition partitioned partitions percent plus preserve procedure purge range rcfile read readonly reads rebuild recordreader recordwriter recover reduce regexp rename repair replace restrict revoke right rlike row schema schemas semi sequencefile serde serdeproperties set shared show show_database sort sorted ssl statistics stored streamtable table tables tablesample tblproperties temporary terminated textfile then tmp to touch transform trigger unarchive undo union uniquejoin unlock update use using utc utc_tmestamp view when where while with admin authorization char compact compactions conf cube current current_date current_timestamp day decimal defined dependency directories elem_type exchange file following for grouping hour ignore inner interval jar less logical macro minute month more none noscan over owner partialscan preceding pretty principals protection reload rewrite role roles rollup rows second server sets skewed transactions truncate unbounded unset uri user values window year"),
        builtin: set("bool boolean long timestamp tinyint smallint bigint int float double date datetime unsigned string array struct map uniontype key_type utctimestamp value_type varchar"),
        atoms: set("false true null unknown"),
        operatorChars: /^[*+\-%<>!=]/,
        dateSQL: set("date timestamp"),
        support: set("ODBCdotTable doubleQuote binaryNumber hexNumber")
    });
    CodeMirror1.defineMIME("text/x-pgsql", {
        name: "sql",
        client: set("source"),
        // For PostgreSQL - https://www.postgresql.org/docs/11/sql-keywords-appendix.html
        // For pl/pgsql lang - https://github.com/postgres/postgres/blob/REL_11_2/src/pl/plpgsql/src/pl_scanner.c
        keywords: set(sqlKeywords + "a abort abs absent absolute access according action ada add admin after aggregate alias all allocate also alter always analyse analyze and any are array array_agg array_max_cardinality as asc asensitive assert assertion assignment asymmetric at atomic attach attribute attributes authorization avg backward base64 before begin begin_frame begin_partition bernoulli between bigint binary bit bit_length blob blocked bom boolean both breadth by c cache call called cardinality cascade cascaded case cast catalog catalog_name ceil ceiling chain char char_length character character_length character_set_catalog character_set_name character_set_schema characteristics characters check checkpoint class class_origin clob close cluster coalesce cobol collate collation collation_catalog collation_name collation_schema collect column column_name columns command_function command_function_code comment comments commit committed concurrently condition condition_number configuration conflict connect connection connection_name constant constraint constraint_catalog constraint_name constraint_schema constraints constructor contains content continue control conversion convert copy corr corresponding cost count covar_pop covar_samp create cross csv cube cume_dist current current_catalog current_date current_default_transform_group current_path current_role current_row current_schema current_time current_timestamp current_transform_group_for_type current_user cursor cursor_name cycle data database datalink datatype date datetime_interval_code datetime_interval_precision day db deallocate debug dec decimal declare default defaults deferrable deferred defined definer degree delete delimiter delimiters dense_rank depends depth deref derived desc describe descriptor detach detail deterministic diagnostics dictionary disable discard disconnect dispatch distinct dlnewcopy dlpreviouscopy dlurlcomplete dlurlcompleteonly dlurlcompletewrite dlurlpath dlurlpathonly dlurlpathwrite dlurlscheme dlurlserver dlvalue do document domain double drop dump dynamic dynamic_function dynamic_function_code each element else elseif elsif empty enable encoding encrypted end end_frame end_partition endexec enforced enum equals errcode error escape event every except exception exclude excluding exclusive exec execute exists exit exp explain expression extension external extract false family fetch file filter final first first_value flag float floor following for force foreach foreign fortran forward found frame_row free freeze from fs full function functions fusion g general generated get global go goto grant granted greatest group grouping groups handler having header hex hierarchy hint hold hour id identity if ignore ilike immediate immediately immutable implementation implicit import in include including increment indent index indexes indicator info inherit inherits initially inline inner inout input insensitive insert instance instantiable instead int integer integrity intersect intersection interval into invoker is isnull isolation join k key key_member key_type label lag language large last last_value lateral lead leading leakproof least left length level library like like_regex limit link listen ln load local localtime localtimestamp location locator lock locked log logged loop lower m map mapping match matched materialized max max_cardinality maxvalue member merge message message_length message_octet_length message_text method min minute minvalue mod mode modifies module month more move multiset mumps name names namespace national natural nchar nclob nesting new next nfc nfd nfkc nfkd nil no none normalize normalized not nothing notice notify notnull nowait nth_value ntile null nullable nullif nulls number numeric object occurrences_regex octet_length octets of off offset oids old on only open operator option options or order ordering ordinality others out outer output over overlaps overlay overriding owned owner p pad parallel parameter parameter_mode parameter_name parameter_ordinal_position parameter_specific_catalog parameter_specific_name parameter_specific_schema parser partial partition pascal passing passthrough password path percent percent_rank percentile_cont percentile_disc perform period permission pg_context pg_datatype_name pg_exception_context pg_exception_detail pg_exception_hint placing plans pli policy portion position position_regex power precedes preceding precision prepare prepared preserve primary print_strict_params prior privileges procedural procedure procedures program public publication query quote raise range rank read reads real reassign recheck recovery recursive ref references referencing refresh regr_avgx regr_avgy regr_count regr_intercept regr_r2 regr_slope regr_sxx regr_sxy regr_syy reindex relative release rename repeatable replace replica requiring reset respect restart restore restrict result result_oid return returned_cardinality returned_length returned_octet_length returned_sqlstate returning returns reverse revoke right role rollback rollup routine routine_catalog routine_name routine_schema routines row row_count row_number rows rowtype rule savepoint scale schema schema_name schemas scope scope_catalog scope_name scope_schema scroll search second section security select selective self sensitive sequence sequences serializable server server_name session session_user set setof sets share show similar simple size skip slice smallint snapshot some source space specific specific_name specifictype sql sqlcode sqlerror sqlexception sqlstate sqlwarning sqrt stable stacked standalone start state statement static statistics stddev_pop stddev_samp stdin stdout storage strict strip structure style subclass_origin submultiset subscription substring substring_regex succeeds sum symmetric sysid system system_time system_user t table table_name tables tablesample tablespace temp template temporary text then ties time timestamp timezone_hour timezone_minute to token top_level_count trailing transaction transaction_active transactions_committed transactions_rolled_back transform transforms translate translate_regex translation treat trigger trigger_catalog trigger_name trigger_schema trim trim_array true truncate trusted type types uescape unbounded uncommitted under unencrypted union unique unknown unlink unlisten unlogged unnamed unnest until untyped update upper uri usage use_column use_variable user user_defined_type_catalog user_defined_type_code user_defined_type_name user_defined_type_schema using vacuum valid validate validator value value_of values var_pop var_samp varbinary varchar variable_conflict variadic varying verbose version versioning view views volatile warning when whenever where while whitespace width_bucket window with within without work wrapper write xml xmlagg xmlattributes xmlbinary xmlcast xmlcomment xmlconcat xmldeclaration xmldocument xmlelement xmlexists xmlforest xmliterate xmlnamespaces xmlparse xmlpi xmlquery xmlroot xmlschema xmlserialize xmltable xmltext xmlvalidate year yes zone"),
        // https://www.postgresql.org/docs/11/datatype.html
        builtin: set("bigint int8 bigserial serial8 bit varying varbit boolean bool box bytea character char varchar cidr circle date double precision float8 inet integer int int4 interval json jsonb line lseg macaddr macaddr8 money numeric decimal path pg_lsn point polygon real float4 smallint int2 smallserial serial2 serial serial4 text time without zone with timetz timestamp timestamptz tsquery tsvector txid_snapshot uuid xml"),
        atoms: set("false true null unknown"),
        operatorChars: /^[*\/+\-%<>!=&|^\/#@?~]/,
        backslashStringEscapes: false,
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable decimallessFloat zerolessFloat binaryNumber hexNumber nCharCast charsetCast escapeConstant")
    });
    // Google's SQL-like query language, GQL
    CodeMirror1.defineMIME("text/x-gql", {
        name: "sql",
        keywords: set("ancestor and asc by contains desc descendant distinct from group has in is limit offset on order select superset where"),
        atoms: set("false true"),
        builtin: set("blob datetime first key __key__ string integer double boolean null"),
        operatorChars: /^[*+\-%<>!=]/
    });
    // Greenplum
    CodeMirror1.defineMIME("text/x-gpsql", {
        name: "sql",
        client: set("source"),
        //https://github.com/greenplum-db/gpdb/blob/master/src/include/parser/kwlist.h
        keywords: set("abort absolute access action active add admin after aggregate all also alter always analyse analyze and any array as asc assertion assignment asymmetric at authorization backward before begin between bigint binary bit boolean both by cache called cascade cascaded case cast chain char character characteristics check checkpoint class close cluster coalesce codegen collate column comment commit committed concurrency concurrently configuration connection constraint constraints contains content continue conversion copy cost cpu_rate_limit create createdb createexttable createrole createuser cross csv cube current current_catalog current_date current_role current_schema current_time current_timestamp current_user cursor cycle data database day deallocate dec decimal declare decode default defaults deferrable deferred definer delete delimiter delimiters deny desc dictionary disable discard distinct distributed do document domain double drop dxl each else enable encoding encrypted end enum errors escape every except exchange exclude excluding exclusive execute exists explain extension external extract false family fetch fields filespace fill filter first float following for force foreign format forward freeze from full function global grant granted greatest group group_id grouping handler hash having header hold host hour identity if ignore ilike immediate immutable implicit in including inclusive increment index indexes inherit inherits initially inline inner inout input insensitive insert instead int integer intersect interval into invoker is isnull isolation join key language large last leading least left level like limit list listen load local localtime localtimestamp location lock log login mapping master match maxvalue median merge minute minvalue missing mode modifies modify month move name names national natural nchar new newline next no nocreatedb nocreateexttable nocreaterole nocreateuser noinherit nologin none noovercommit nosuperuser not nothing notify notnull nowait null nullif nulls numeric object of off offset oids old on only operator option options or order ordered others out outer over overcommit overlaps overlay owned owner parser partial partition partitions passing password percent percentile_cont percentile_disc placing plans position preceding precision prepare prepared preserve primary prior privileges procedural procedure protocol queue quote randomly range read readable reads real reassign recheck recursive ref references reindex reject relative release rename repeatable replace replica reset resource restart restrict returning returns revoke right role rollback rollup rootpartition row rows rule savepoint scatter schema scroll search second security segment select sequence serializable session session_user set setof sets share show similar simple smallint some split sql stable standalone start statement statistics stdin stdout storage strict strip subpartition subpartitions substring superuser symmetric sysid system table tablespace temp template temporary text then threshold ties time timestamp to trailing transaction treat trigger trim true truncate trusted type unbounded uncommitted unencrypted union unique unknown unlisten until update user using vacuum valid validation validator value values varchar variadic varying verbose version view volatile web when where whitespace window with within without work writable write xml xmlattributes xmlconcat xmlelement xmlexists xmlforest xmlparse xmlpi xmlroot xmlserialize year yes zone"),
        builtin: set("bigint int8 bigserial serial8 bit varying varbit boolean bool box bytea character char varchar cidr circle date double precision float float8 inet integer int int4 interval json jsonb line lseg macaddr macaddr8 money numeric decimal path pg_lsn point polygon real float4 smallint int2 smallserial serial2 serial serial4 text time without zone with timetz timestamp timestamptz tsquery tsvector txid_snapshot uuid xml"),
        atoms: set("false true null unknown"),
        operatorChars: /^[*+\-%<>!=&|^\/#@?~]/,
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable decimallessFloat zerolessFloat binaryNumber hexNumber nCharCast charsetCast")
    });
    // Spark SQL
    CodeMirror1.defineMIME("text/x-sparksql", {
        name: "sql",
        keywords: set("add after all alter analyze and anti archive array as asc at between bucket buckets by cache cascade case cast change clear cluster clustered codegen collection column columns comment commit compact compactions compute concatenate cost create cross cube current current_date current_timestamp database databases data dbproperties defined delete delimited deny desc describe dfs directories distinct distribute drop else end escaped except exchange exists explain export extended external false fields fileformat first following for format formatted from full function functions global grant group grouping having if ignore import in index indexes inner inpath inputformat insert intersect interval into is items join keys last lateral lazy left like limit lines list load local location lock locks logical macro map minus msck natural no not null nulls of on optimize option options or order out outer outputformat over overwrite partition partitioned partitions percent preceding principals purge range recordreader recordwriter recover reduce refresh regexp rename repair replace reset restrict revoke right rlike role roles rollback rollup row rows schema schemas select semi separated serde serdeproperties set sets show skewed sort sorted start statistics stored stratify struct table tables tablesample tblproperties temp temporary terminated then to touch transaction transactions transform true truncate unarchive unbounded uncache union unlock unset use using values view when where window with"),
        builtin: set("abs acos acosh add_months aggregate and any approx_count_distinct approx_percentile array array_contains array_distinct array_except array_intersect array_join array_max array_min array_position array_remove array_repeat array_sort array_union arrays_overlap arrays_zip ascii asin asinh assert_true atan atan2 atanh avg base64 between bigint bin binary bit_and bit_count bit_get bit_length bit_or bit_xor bool_and bool_or boolean bround btrim cardinality case cast cbrt ceil ceiling char char_length character_length chr coalesce collect_list collect_set concat concat_ws conv corr cos cosh cot count count_if count_min_sketch covar_pop covar_samp crc32 cume_dist current_catalog current_database current_date current_timestamp current_timezone current_user date date_add date_format date_from_unix_date date_part date_sub date_trunc datediff day dayofmonth dayofweek dayofyear decimal decode degrees delimited dense_rank div double e element_at elt encode every exists exp explode explode_outer expm1 extract factorial filter find_in_set first first_value flatten float floor forall format_number format_string from_csv from_json from_unixtime from_utc_timestamp get_json_object getbit greatest grouping grouping_id hash hex hour hypot if ifnull in initcap inline inline_outer input_file_block_length input_file_block_start input_file_name inputformat instr int isnan isnotnull isnull java_method json_array_length json_object_keys json_tuple kurtosis lag last last_day last_value lcase lead least left length levenshtein like ln locate log log10 log1p log2 lower lpad ltrim make_date make_dt_interval make_interval make_timestamp make_ym_interval map map_concat map_entries map_filter map_from_arrays map_from_entries map_keys map_values map_zip_with max max_by md5 mean min min_by minute mod monotonically_increasing_id month months_between named_struct nanvl negative next_day not now nth_value ntile nullif nvl nvl2 octet_length or outputformat overlay parse_url percent_rank percentile percentile_approx pi pmod posexplode posexplode_outer position positive pow power printf quarter radians raise_error rand randn random rank rcfile reflect regexp regexp_extract regexp_extract_all regexp_like regexp_replace repeat replace reverse right rint rlike round row_number rpad rtrim schema_of_csv schema_of_json second sentences sequence sequencefile serde session_window sha sha1 sha2 shiftleft shiftright shiftrightunsigned shuffle sign signum sin sinh size skewness slice smallint some sort_array soundex space spark_partition_id split sqrt stack std stddev stddev_pop stddev_samp str_to_map string struct substr substring substring_index sum tan tanh textfile timestamp timestamp_micros timestamp_millis timestamp_seconds tinyint to_csv to_date to_json to_timestamp to_unix_timestamp to_utc_timestamp transform transform_keys transform_values translate trim trunc try_add try_divide typeof ucase unbase64 unhex uniontype unix_date unix_micros unix_millis unix_seconds unix_timestamp upper uuid var_pop var_samp variance version weekday weekofyear when width_bucket window xpath xpath_boolean xpath_double xpath_float xpath_int xpath_long xpath_number xpath_short xpath_string xxhash64 year zip_with"),
        atoms: set("false true null"),
        operatorChars: /^[*\/+\-%<>!=~&|^]/,
        dateSQL: set("date time timestamp"),
        support: set("ODBCdotTable doubleQuote zerolessFloat")
    });
    // Esper
    CodeMirror1.defineMIME("text/x-esper", {
        name: "sql",
        client: set("source"),
        // http://www.espertech.com/esper/release-5.5.0/esper-reference/html/appendix_keywords.html
        keywords: set("alter and as asc between by count create delete desc distinct drop from group having in insert into is join like not on or order select set table union update values where limit after all and as at asc avedev avg between by case cast coalesce count create current_timestamp day days delete define desc distinct else end escape events every exists false first from full group having hour hours in inner insert instanceof into irstream is istream join last lastweekday left limit like max match_recognize matches median measures metadatasql min minute minutes msec millisecond milliseconds not null offset on or order outer output partition pattern prev prior regexp retain-union retain-intersection right rstream sec second seconds select set some snapshot sql stddev sum then true unidirectional until update variable weekday when where window"),
        builtin: {},
        atoms: set("false true null"),
        operatorChars: /^[*+\-%<>!=&|^\/#@?~]/,
        dateSQL: set("time"),
        support: set("decimallessFloat zerolessFloat binaryNumber hexNumber")
    });
}); /*
  How Properties of Mime Types are used by SQL Mode
  =================================================

  keywords:
    A list of keywords you want to be highlighted.
  builtin:
    A list of builtin types you want to be highlighted (if you want types to be of class "builtin" instead of "keyword").
  operatorChars:
    All characters that must be handled as operators.
  client:
    Commands parsed and executed by the client (not the server).
  support:
    A list of supported syntaxes which are not common, but are supported by more than 1 DBMS.
    * ODBCdotTable: .tableName
    * zerolessFloat: .1
    * doubleQuote
    * nCharCast: N'string'
    * charsetCast: _utf8'string'
    * commentHash: use # char for comments
    * commentSlashSlash: use // for comments
    * commentSpaceRequired: require a space after -- for comments
  atoms:
    Keywords that must be highlighted as atoms,. Some DBMS's support more atoms than others:
    UNKNOWN, INFINITY, UNDERFLOW, NaN...
  dateSQL:
    Used for date/time SQL standard syntax, because not all DBMS's support same temporal types.
*/
});

var stylus = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// Stylus mode created by Dmitry Kiselyov http://git.io/AaRB
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var wordRegexp = function wordRegexp(words) {
        words = words.sort(function(a, b) {
            return b > a;
        });
        return new RegExp("^((" + words.join(")|(") + "))\\b");
    };
    var keySet = function keySet(array) {
        var keys = {};
        for(var i = 0; i < array.length; ++i)keys[array[i]] = true;
        return keys;
    };
    var escapeRegExp = function escapeRegExp(text) {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    };
    CodeMirror1.defineMode("stylus", function(config) {
        var tokenBase = /**
     * Tokenizers
     */ function tokenBase(stream, state) {
            firstWordMatch = stream.string.match(/(^[\w-]+\s*=\s*$)|(^\s*[\w-]+\s*=\s*[\w-])|(^\s*(\.|#|@|\$|\&|\[|\d|\+|::?|\{|\>|~|\/)?\s*[\w-]*([a-z0-9-]|\*|\/\*)(\(|,)?)/);
            state.context.line.firstWord = firstWordMatch ? firstWordMatch[0].replace(/^\s*/, "") : "";
            state.context.line.indent = stream.indentation();
            ch = stream.peek();
            // Line comment
            if (stream.match("//")) {
                stream.skipToEnd();
                return [
                    "comment",
                    "comment"
                ];
            }
            // Block comment
            if (stream.match("/*")) {
                state.tokenize = tokenCComment;
                return tokenCComment(stream, state);
            }
            // String
            if (ch == '"' || ch == "'") {
                stream.next();
                state.tokenize = tokenString(ch);
                return state.tokenize(stream, state);
            }
            // Def
            if (ch == "@") {
                stream.next();
                stream.eatWhile(/[\w\\-]/);
                return [
                    "def",
                    stream.current()
                ];
            }
            // ID selector or Hex color
            if (ch == "#") {
                stream.next();
                // Hex color
                if (stream.match(/^[0-9a-f]{3}([0-9a-f]([0-9a-f]{2}){0,2})?\b(?!-)/i)) {
                    return [
                        "atom",
                        "atom"
                    ];
                }
                // ID selector
                if (stream.match(/^[a-z][\w-]*/i)) {
                    return [
                        "builtin",
                        "hash"
                    ];
                }
            }
            // Vendor prefixes
            if (stream.match(vendorPrefixesRegexp)) {
                return [
                    "meta",
                    "vendor-prefixes"
                ];
            }
            // Numbers
            if (stream.match(/^-?[0-9]?\.?[0-9]/)) {
                stream.eatWhile(/[a-z%]/i);
                return [
                    "number",
                    "unit"
                ];
            }
            // !important|optional
            if (ch == "!") {
                stream.next();
                return [
                    stream.match(/^(important|optional)/i) ? "keyword" : "operator",
                    "important"
                ];
            }
            // Class
            if (ch == "." && stream.match(/^\.[a-z][\w-]*/i)) {
                return [
                    "qualifier",
                    "qualifier"
                ];
            }
            // url url-prefix domain regexp
            if (stream.match(documentTypesRegexp)) {
                if (stream.peek() == "(") state.tokenize = tokenParenthesized;
                return [
                    "property",
                    "word"
                ];
            }
            // Mixins / Functions
            if (stream.match(/^[a-z][\w-]*\(/i)) {
                stream.backUp(1);
                return [
                    "keyword",
                    "mixin"
                ];
            }
            // Block mixins
            if (stream.match(/^(\+|-)[a-z][\w-]*\(/i)) {
                stream.backUp(1);
                return [
                    "keyword",
                    "block-mixin"
                ];
            }
            // Parent Reference BEM naming
            if (stream.string.match(/^\s*&/) && stream.match(/^[-_]+[a-z][\w-]*/)) {
                return [
                    "qualifier",
                    "qualifier"
                ];
            }
            // / Root Reference & Parent Reference
            if (stream.match(/^(\/|&)(-|_|:|\.|#|[a-z])/)) {
                stream.backUp(1);
                return [
                    "variable-3",
                    "reference"
                ];
            }
            if (stream.match(/^&{1}\s*$/)) {
                return [
                    "variable-3",
                    "reference"
                ];
            }
            // Word operator
            if (stream.match(wordOperatorKeywordsRegexp)) {
                return [
                    "operator",
                    "operator"
                ];
            }
            // Word
            if (stream.match(/^\$?[-_]*[a-z0-9]+[\w-]*/i)) {
                // Variable
                if (stream.match(/^(\.|\[)[\w-\'\"\]]+/i, false)) {
                    if (!wordIsTag(stream.current())) {
                        stream.match(".");
                        return [
                            "variable-2",
                            "variable-name"
                        ];
                    }
                }
                return [
                    "variable-2",
                    "word"
                ];
            }
            // Operators
            if (stream.match(operatorsRegexp)) {
                return [
                    "operator",
                    stream.current()
                ];
            }
            // Delimiters
            if (/[:;,{}\[\]\(\)]/.test(ch)) {
                stream.next();
                return [
                    null,
                    ch
                ];
            }
            // Non-detected items
            stream.next();
            return [
                null,
                null
            ];
        };
        var tokenCComment = /**
     * Token comment
     */ function tokenCComment(stream, state) {
            var maybeEnd = false, ch;
            while((ch = stream.next()) != null){
                if (maybeEnd && ch == "/") {
                    state.tokenize = null;
                    break;
                }
                maybeEnd = ch == "*";
            }
            return [
                "comment",
                "comment"
            ];
        };
        var tokenString = /**
     * Token string
     */ function tokenString(quote) {
            return function(stream, state) {
                var escaped = false, ch;
                while((ch = stream.next()) != null){
                    if (ch == quote && !escaped) {
                        if (quote == ")") stream.backUp(1);
                        break;
                    }
                    escaped = !escaped && ch == "\\";
                }
                if (ch == quote || !escaped && quote != ")") state.tokenize = null;
                return [
                    "string",
                    "string"
                ];
            };
        };
        var tokenParenthesized = /**
     * Token parenthesized
     */ function tokenParenthesized(stream, state) {
            stream.next(); // Must be "("
            if (!stream.match(/\s*[\"\')]/, false)) state.tokenize = tokenString(")");
            else state.tokenize = null;
            return [
                null,
                "("
            ];
        };
        var Context = /**
     * Context management
     */ function Context(type, indent, prev, line) {
            this.type = type;
            this.indent = indent;
            this.prev = prev;
            this.line = line || {
                firstWord: "",
                indent: 0
            };
        };
        var pushContext = function pushContext(state, stream, type, indent) {
            indent = indent >= 0 ? indent : indentUnit;
            state.context = new Context(type, stream.indentation() + indent, state.context);
            return type;
        };
        var popContext = function popContext(state, currentIndent) {
            var contextIndent = state.context.indent - indentUnit;
            currentIndent = currentIndent || false;
            state.context = state.context.prev;
            if (currentIndent) state.context.indent = contextIndent;
            return state.context.type;
        };
        var pass = function pass(type, stream, state) {
            return states[state.context.type](type, stream, state);
        };
        var popAndPass = function popAndPass(type, stream, state, n) {
            for(var i = n || 1; i > 0; i--)state.context = state.context.prev;
            return pass(type, stream, state);
        };
        var wordIsTag = /**
     * Parser
     */ function wordIsTag(word) {
            return word.toLowerCase() in tagKeywords;
        };
        var wordIsProperty = function wordIsProperty(word) {
            word = word.toLowerCase();
            return word in propertyKeywords || word in fontProperties;
        };
        var wordIsBlock = function wordIsBlock(word) {
            return word.toLowerCase() in blockKeywords;
        };
        var wordIsVendorPrefix = function wordIsVendorPrefix(word) {
            return word.toLowerCase().match(vendorPrefixesRegexp);
        };
        var wordAsValue = function wordAsValue(word) {
            var wordLC = word.toLowerCase();
            var override = "variable-2";
            if (wordIsTag(word)) override = "tag";
            else if (wordIsBlock(word)) override = "block-keyword";
            else if (wordIsProperty(word)) override = "property";
            else if (wordLC in valueKeywords || wordLC in commonAtoms) override = "atom";
            else if (wordLC == "return" || wordLC in colorKeywords) override = "keyword";
            else if (word.match(/^[A-Z]/)) override = "string";
            return override;
        };
        var typeIsBlock = function typeIsBlock(type, stream) {
            return endOfLine(stream) && (type == "{" || type == "]" || type == "hash" || type == "qualifier") || type == "block-mixin";
        };
        var typeIsInterpolation = function typeIsInterpolation(type, stream) {
            return type == "{" && stream.match(/^\s*\$?[\w-]+/i, false);
        };
        var typeIsPseudo = function typeIsPseudo(type, stream) {
            return type == ":" && stream.match(/^[a-z-]+/, false);
        };
        var startOfLine = function startOfLine(stream) {
            return stream.sol() || stream.string.match(new RegExp("^\\s*" + escapeRegExp(stream.current())));
        };
        var endOfLine = function endOfLine(stream) {
            return stream.eol() || stream.match(/^\s*$/, false);
        };
        var firstWordOfLine = function firstWordOfLine(line) {
            var re = /^\s*[-_]*[a-z0-9]+[\w-]*/i;
            var result = typeof line == "string" ? line.match(re) : line.string.match(re);
            return result ? result[0].replace(/^\s*/, "") : "";
        };
        var indentUnit = config.indentUnit, indentUnitString = "", tagKeywords = keySet(tagKeywords_), tagVariablesRegexp = /^(a|b|i|s|col|em)$/i, propertyKeywords = keySet(propertyKeywords_), nonStandardPropertyKeywords = keySet(nonStandardPropertyKeywords_), valueKeywords = keySet(valueKeywords_), colorKeywords = keySet(colorKeywords_), documentTypes = keySet(documentTypes_), documentTypesRegexp = wordRegexp(documentTypes_), mediaFeatures = keySet(mediaFeatures_), mediaTypes = keySet(mediaTypes_), fontProperties = keySet(fontProperties_), operatorsRegexp = /^\s*([.]{2,3}|&&|\|\||\*\*|[?!=:]?=|[-+*\/%<>]=?|\?:|\~)/, wordOperatorKeywordsRegexp = wordRegexp(wordOperatorKeywords_), blockKeywords = keySet(blockKeywords_), vendorPrefixesRegexp = new RegExp(/^\-(moz|ms|o|webkit)-/i), commonAtoms = keySet(commonAtoms_), firstWordMatch = "", states = {}, ch, style, type, override;
        while(indentUnitString.length < indentUnit)indentUnitString += " ";
        /**
     * Block
     */ states.block = function(type, stream, state) {
            if (type == "comment" && startOfLine(stream) || type == "," && endOfLine(stream) || type == "mixin") {
                return pushContext(state, stream, "block", 0);
            }
            if (typeIsInterpolation(type, stream)) {
                return pushContext(state, stream, "interpolation");
            }
            if (endOfLine(stream) && type == "]") {
                if (!/^\s*(\.|#|:|\[|\*|&)/.test(stream.string) && !wordIsTag(firstWordOfLine(stream))) {
                    return pushContext(state, stream, "block", 0);
                }
            }
            if (typeIsBlock(type, stream)) {
                return pushContext(state, stream, "block");
            }
            if (type == "}" && endOfLine(stream)) {
                return pushContext(state, stream, "block", 0);
            }
            if (type == "variable-name") {
                if (stream.string.match(/^\s?\$[\w-\.\[\]\'\"]+$/) || wordIsBlock(firstWordOfLine(stream))) {
                    return pushContext(state, stream, "variableName");
                } else {
                    return pushContext(state, stream, "variableName", 0);
                }
            }
            if (type == "=") {
                if (!endOfLine(stream) && !wordIsBlock(firstWordOfLine(stream))) {
                    return pushContext(state, stream, "block", 0);
                }
                return pushContext(state, stream, "block");
            }
            if (type == "*") {
                if (endOfLine(stream) || stream.match(/\s*(,|\.|#|\[|:|{)/, false)) {
                    override = "tag";
                    return pushContext(state, stream, "block");
                }
            }
            if (typeIsPseudo(type, stream)) {
                return pushContext(state, stream, "pseudo");
            }
            if (/@(font-face|media|supports|(-moz-)?document)/.test(type)) {
                return pushContext(state, stream, endOfLine(stream) ? "block" : "atBlock");
            }
            if (/@(-(moz|ms|o|webkit)-)?keyframes$/.test(type)) {
                return pushContext(state, stream, "keyframes");
            }
            if (/@extends?/.test(type)) {
                return pushContext(state, stream, "extend", 0);
            }
            if (type && type.charAt(0) == "@") {
                // Property Lookup
                if (stream.indentation() > 0 && wordIsProperty(stream.current().slice(1))) {
                    override = "variable-2";
                    return "block";
                }
                if (/(@import|@require|@charset)/.test(type)) {
                    return pushContext(state, stream, "block", 0);
                }
                return pushContext(state, stream, "block");
            }
            if (type == "reference" && endOfLine(stream)) {
                return pushContext(state, stream, "block");
            }
            if (type == "(") {
                return pushContext(state, stream, "parens");
            }
            if (type == "vendor-prefixes") {
                return pushContext(state, stream, "vendorPrefixes");
            }
            if (type == "word") {
                var word = stream.current();
                override = wordAsValue(word);
                if (override == "property") {
                    if (startOfLine(stream)) {
                        return pushContext(state, stream, "block", 0);
                    } else {
                        override = "atom";
                        return "block";
                    }
                }
                if (override == "tag") {
                    // tag is a css value
                    if (/embed|menu|pre|progress|sub|table/.test(word)) {
                        if (wordIsProperty(firstWordOfLine(stream))) {
                            override = "atom";
                            return "block";
                        }
                    }
                    // tag is an attribute
                    if (stream.string.match(new RegExp("\\[\\s*" + word + "|" + word + "\\s*\\]"))) {
                        override = "atom";
                        return "block";
                    }
                    // tag is a variable
                    if (tagVariablesRegexp.test(word)) {
                        if (startOfLine(stream) && stream.string.match(/=/) || !startOfLine(stream) && !stream.string.match(/^(\s*\.|#|\&|\[|\/|>|\*)/) && !wordIsTag(firstWordOfLine(stream))) {
                            override = "variable-2";
                            if (wordIsBlock(firstWordOfLine(stream))) return "block";
                            return pushContext(state, stream, "block", 0);
                        }
                    }
                    if (endOfLine(stream)) return pushContext(state, stream, "block");
                }
                if (override == "block-keyword") {
                    override = "keyword";
                    // Postfix conditionals
                    if (stream.current(/(if|unless)/) && !startOfLine(stream)) {
                        return "block";
                    }
                    return pushContext(state, stream, "block");
                }
                if (word == "return") return pushContext(state, stream, "block", 0);
                // Placeholder selector
                if (override == "variable-2" && stream.string.match(/^\s?\$[\w-\.\[\]\'\"]+$/)) {
                    return pushContext(state, stream, "block");
                }
            }
            return state.context.type;
        };
        /**
     * Parens
     */ states.parens = function(type, stream, state) {
            if (type == "(") return pushContext(state, stream, "parens");
            if (type == ")") {
                if (state.context.prev.type == "parens") {
                    return popContext(state);
                }
                if (stream.string.match(/^[a-z][\w-]*\(/i) && endOfLine(stream) || wordIsBlock(firstWordOfLine(stream)) || /(\.|#|:|\[|\*|&|>|~|\+|\/)/.test(firstWordOfLine(stream)) || !stream.string.match(/^-?[a-z][\w-\.\[\]\'\"]*\s*=/) && wordIsTag(firstWordOfLine(stream))) {
                    return pushContext(state, stream, "block");
                }
                if (stream.string.match(/^[\$-]?[a-z][\w-\.\[\]\'\"]*\s*=/) || stream.string.match(/^\s*(\(|\)|[0-9])/) || stream.string.match(/^\s+[a-z][\w-]*\(/i) || stream.string.match(/^\s+[\$-]?[a-z]/i)) {
                    return pushContext(state, stream, "block", 0);
                }
                if (endOfLine(stream)) return pushContext(state, stream, "block");
                else return pushContext(state, stream, "block", 0);
            }
            if (type && type.charAt(0) == "@" && wordIsProperty(stream.current().slice(1))) {
                override = "variable-2";
            }
            if (type == "word") {
                var word = stream.current();
                override = wordAsValue(word);
                if (override == "tag" && tagVariablesRegexp.test(word)) {
                    override = "variable-2";
                }
                if (override == "property" || word == "to") override = "atom";
            }
            if (type == "variable-name") {
                return pushContext(state, stream, "variableName");
            }
            if (typeIsPseudo(type, stream)) {
                return pushContext(state, stream, "pseudo");
            }
            return state.context.type;
        };
        /**
     * Vendor prefixes
     */ states.vendorPrefixes = function(type, stream, state) {
            if (type == "word") {
                override = "property";
                return pushContext(state, stream, "block", 0);
            }
            return popContext(state);
        };
        /**
     * Pseudo
     */ states.pseudo = function(type, stream, state) {
            if (!wordIsProperty(firstWordOfLine(stream.string))) {
                stream.match(/^[a-z-]+/);
                override = "variable-3";
                if (endOfLine(stream)) return pushContext(state, stream, "block");
                return popContext(state);
            }
            return popAndPass(type, stream, state);
        };
        /**
     * atBlock
     */ states.atBlock = function(type, stream, state) {
            if (type == "(") return pushContext(state, stream, "atBlock_parens");
            if (typeIsBlock(type, stream)) {
                return pushContext(state, stream, "block");
            }
            if (typeIsInterpolation(type, stream)) {
                return pushContext(state, stream, "interpolation");
            }
            if (type == "word") {
                var word = stream.current().toLowerCase();
                if (/^(only|not|and|or)$/.test(word)) override = "keyword";
                else if (documentTypes.hasOwnProperty(word)) override = "tag";
                else if (mediaTypes.hasOwnProperty(word)) override = "attribute";
                else if (mediaFeatures.hasOwnProperty(word)) override = "property";
                else if (nonStandardPropertyKeywords.hasOwnProperty(word)) override = "string-2";
                else override = wordAsValue(stream.current());
                if (override == "tag" && endOfLine(stream)) {
                    return pushContext(state, stream, "block");
                }
            }
            if (type == "operator" && /^(not|and|or)$/.test(stream.current())) {
                override = "keyword";
            }
            return state.context.type;
        };
        states.atBlock_parens = function(type, stream, state) {
            if (type == "{" || type == "}") return state.context.type;
            if (type == ")") {
                if (endOfLine(stream)) return pushContext(state, stream, "block");
                else return pushContext(state, stream, "atBlock");
            }
            if (type == "word") {
                var word = stream.current().toLowerCase();
                override = wordAsValue(word);
                if (/^(max|min)/.test(word)) override = "property";
                if (override == "tag") {
                    tagVariablesRegexp.test(word) ? override = "variable-2" : override = "atom";
                }
                return state.context.type;
            }
            return states.atBlock(type, stream, state);
        };
        /**
     * Keyframes
     */ states.keyframes = function(type, stream, state) {
            if (stream.indentation() == "0" && (type == "}" && startOfLine(stream) || type == "]" || type == "hash" || type == "qualifier" || wordIsTag(stream.current()))) {
                return popAndPass(type, stream, state);
            }
            if (type == "{") return pushContext(state, stream, "keyframes");
            if (type == "}") {
                if (startOfLine(stream)) return popContext(state, true);
                else return pushContext(state, stream, "keyframes");
            }
            if (type == "unit" && /^[0-9]+\%$/.test(stream.current())) {
                return pushContext(state, stream, "keyframes");
            }
            if (type == "word") {
                override = wordAsValue(stream.current());
                if (override == "block-keyword") {
                    override = "keyword";
                    return pushContext(state, stream, "keyframes");
                }
            }
            if (/@(font-face|media|supports|(-moz-)?document)/.test(type)) {
                return pushContext(state, stream, endOfLine(stream) ? "block" : "atBlock");
            }
            if (type == "mixin") {
                return pushContext(state, stream, "block", 0);
            }
            return state.context.type;
        };
        /**
     * Interpolation
     */ states.interpolation = function(type, stream, state) {
            if (type == "{") popContext(state) && pushContext(state, stream, "block");
            if (type == "}") {
                if (stream.string.match(/^\s*(\.|#|:|\[|\*|&|>|~|\+|\/)/i) || stream.string.match(/^\s*[a-z]/i) && wordIsTag(firstWordOfLine(stream))) {
                    return pushContext(state, stream, "block");
                }
                if (!stream.string.match(/^(\{|\s*\&)/) || stream.match(/\s*[\w-]/, false)) {
                    return pushContext(state, stream, "block", 0);
                }
                return pushContext(state, stream, "block");
            }
            if (type == "variable-name") {
                return pushContext(state, stream, "variableName", 0);
            }
            if (type == "word") {
                override = wordAsValue(stream.current());
                if (override == "tag") override = "atom";
            }
            return state.context.type;
        };
        /**
     * Extend/s
     */ states.extend = function(type, stream, state) {
            if (type == "[" || type == "=") return "extend";
            if (type == "]") return popContext(state);
            if (type == "word") {
                override = wordAsValue(stream.current());
                return "extend";
            }
            return popContext(state);
        };
        /**
     * Variable name
     */ states.variableName = function(type, stream, state) {
            if (type == "string" || type == "[" || type == "]" || stream.current().match(/^(\.|\$)/)) {
                if (stream.current().match(/^\.[\w-]+/i)) override = "variable-2";
                return "variableName";
            }
            return popAndPass(type, stream, state);
        };
        return {
            startState: function startState(base) {
                return {
                    tokenize: null,
                    state: "block",
                    context: new Context("block", base || 0, null)
                };
            },
            token: function token(stream, state) {
                if (!state.tokenize && stream.eatSpace()) return null;
                style = (state.tokenize || tokenBase)(stream, state);
                if (style && typeof style == "object") {
                    type = style[1];
                    style = style[0];
                }
                override = style;
                state.state = states[state.state](type, stream, state);
                return override;
            },
            indent: function indent(state, textAfter, line) {
                var cx = state.context, ch = textAfter && textAfter.charAt(0), indent = cx.indent, lineFirstWord = firstWordOfLine(textAfter), lineIndent = line.match(/^\s*/)[0].replace(/\t/g, indentUnitString).length, prevLineFirstWord = state.context.prev ? state.context.prev.line.firstWord : "", prevLineIndent = state.context.prev ? state.context.prev.line.indent : lineIndent;
                if (cx.prev && (ch == "}" && (cx.type == "block" || cx.type == "atBlock" || cx.type == "keyframes") || ch == ")" && (cx.type == "parens" || cx.type == "atBlock_parens") || ch == "{" && cx.type == "at")) {
                    indent = cx.indent - indentUnit;
                } else if (!/(\})/.test(ch)) {
                    if (/@|\$|\d/.test(ch) || /^\{/.test(textAfter) || /^\s*\/(\/|\*)/.test(textAfter) || /^\s*\/\*/.test(prevLineFirstWord) || /^\s*[\w-\.\[\]\'\"]+\s*(\?|:|\+)?=/i.test(textAfter) || /^(\+|-)?[a-z][\w-]*\(/i.test(textAfter) || /^return/.test(textAfter) || wordIsBlock(lineFirstWord)) {
                        indent = lineIndent;
                    } else if (/(\.|#|:|\[|\*|&|>|~|\+|\/)/.test(ch) || wordIsTag(lineFirstWord)) {
                        if (/\,\s*$/.test(prevLineFirstWord)) {
                            indent = prevLineIndent;
                        } else if (/^\s+/.test(line) && (/(\.|#|:|\[|\*|&|>|~|\+|\/)/.test(prevLineFirstWord) || wordIsTag(prevLineFirstWord))) {
                            indent = lineIndent <= prevLineIndent ? prevLineIndent : prevLineIndent + indentUnit;
                        } else {
                            indent = lineIndent;
                        }
                    } else if (!/,\s*$/.test(line) && (wordIsVendorPrefix(lineFirstWord) || wordIsProperty(lineFirstWord))) {
                        if (wordIsBlock(prevLineFirstWord)) {
                            indent = lineIndent <= prevLineIndent ? prevLineIndent : prevLineIndent + indentUnit;
                        } else if (/^\{/.test(prevLineFirstWord)) {
                            indent = lineIndent <= prevLineIndent ? lineIndent : prevLineIndent + indentUnit;
                        } else if (wordIsVendorPrefix(prevLineFirstWord) || wordIsProperty(prevLineFirstWord)) {
                            indent = lineIndent >= prevLineIndent ? prevLineIndent : lineIndent;
                        } else if (/^(\.|#|:|\[|\*|&|@|\+|\-|>|~|\/)/.test(prevLineFirstWord) || /=\s*$/.test(prevLineFirstWord) || wordIsTag(prevLineFirstWord) || /^\$[\w-\.\[\]\'\"]/.test(prevLineFirstWord)) {
                            indent = prevLineIndent + indentUnit;
                        } else {
                            indent = lineIndent;
                        }
                    }
                }
                return indent;
            },
            electricChars: "}",
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            blockCommentContinue: " * ",
            lineComment: "//",
            fold: "indent"
        };
    });
    // developer.mozilla.org/en-US/docs/Web/HTML/Element
    var tagKeywords_ = [
        "a",
        "abbr",
        "address",
        "area",
        "article",
        "aside",
        "audio",
        "b",
        "base",
        "bdi",
        "bdo",
        "bgsound",
        "blockquote",
        "body",
        "br",
        "button",
        "canvas",
        "caption",
        "cite",
        "code",
        "col",
        "colgroup",
        "data",
        "datalist",
        "dd",
        "del",
        "details",
        "dfn",
        "div",
        "dl",
        "dt",
        "em",
        "embed",
        "fieldset",
        "figcaption",
        "figure",
        "footer",
        "form",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "head",
        "header",
        "hgroup",
        "hr",
        "html",
        "i",
        "iframe",
        "img",
        "input",
        "ins",
        "kbd",
        "keygen",
        "label",
        "legend",
        "li",
        "link",
        "main",
        "map",
        "mark",
        "marquee",
        "menu",
        "menuitem",
        "meta",
        "meter",
        "nav",
        "nobr",
        "noframes",
        "noscript",
        "object",
        "ol",
        "optgroup",
        "option",
        "output",
        "p",
        "param",
        "pre",
        "progress",
        "q",
        "rp",
        "rt",
        "ruby",
        "s",
        "samp",
        "script",
        "section",
        "select",
        "small",
        "source",
        "span",
        "strong",
        "style",
        "sub",
        "summary",
        "sup",
        "table",
        "tbody",
        "td",
        "textarea",
        "tfoot",
        "th",
        "thead",
        "time",
        "tr",
        "track",
        "u",
        "ul",
        "var",
        "video"
    ];
    // github.com/codemirror/CodeMirror/blob/master/mode/css/css.js
    // Note, "url-prefix" should precede "url" in order to match correctly in documentTypesRegexp
    var documentTypes_ = [
        "domain",
        "regexp",
        "url-prefix",
        "url"
    ];
    var mediaTypes_ = [
        "all",
        "aural",
        "braille",
        "handheld",
        "print",
        "projection",
        "screen",
        "tty",
        "tv",
        "embossed"
    ];
    var mediaFeatures_ = [
        "width",
        "min-width",
        "max-width",
        "height",
        "min-height",
        "max-height",
        "device-width",
        "min-device-width",
        "max-device-width",
        "device-height",
        "min-device-height",
        "max-device-height",
        "aspect-ratio",
        "min-aspect-ratio",
        "max-aspect-ratio",
        "device-aspect-ratio",
        "min-device-aspect-ratio",
        "max-device-aspect-ratio",
        "color",
        "min-color",
        "max-color",
        "color-index",
        "min-color-index",
        "max-color-index",
        "monochrome",
        "min-monochrome",
        "max-monochrome",
        "resolution",
        "min-resolution",
        "max-resolution",
        "scan",
        "grid",
        "dynamic-range",
        "video-dynamic-range"
    ];
    var propertyKeywords_ = [
        "align-content",
        "align-items",
        "align-self",
        "alignment-adjust",
        "alignment-baseline",
        "anchor-point",
        "animation",
        "animation-delay",
        "animation-direction",
        "animation-duration",
        "animation-fill-mode",
        "animation-iteration-count",
        "animation-name",
        "animation-play-state",
        "animation-timing-function",
        "appearance",
        "azimuth",
        "backface-visibility",
        "background",
        "background-attachment",
        "background-clip",
        "background-color",
        "background-image",
        "background-origin",
        "background-position",
        "background-repeat",
        "background-size",
        "baseline-shift",
        "binding",
        "bleed",
        "bookmark-label",
        "bookmark-level",
        "bookmark-state",
        "bookmark-target",
        "border",
        "border-bottom",
        "border-bottom-color",
        "border-bottom-left-radius",
        "border-bottom-right-radius",
        "border-bottom-style",
        "border-bottom-width",
        "border-collapse",
        "border-color",
        "border-image",
        "border-image-outset",
        "border-image-repeat",
        "border-image-slice",
        "border-image-source",
        "border-image-width",
        "border-left",
        "border-left-color",
        "border-left-style",
        "border-left-width",
        "border-radius",
        "border-right",
        "border-right-color",
        "border-right-style",
        "border-right-width",
        "border-spacing",
        "border-style",
        "border-top",
        "border-top-color",
        "border-top-left-radius",
        "border-top-right-radius",
        "border-top-style",
        "border-top-width",
        "border-width",
        "bottom",
        "box-decoration-break",
        "box-shadow",
        "box-sizing",
        "break-after",
        "break-before",
        "break-inside",
        "caption-side",
        "clear",
        "clip",
        "color",
        "color-profile",
        "column-count",
        "column-fill",
        "column-gap",
        "column-rule",
        "column-rule-color",
        "column-rule-style",
        "column-rule-width",
        "column-span",
        "column-width",
        "columns",
        "content",
        "counter-increment",
        "counter-reset",
        "crop",
        "cue",
        "cue-after",
        "cue-before",
        "cursor",
        "direction",
        "display",
        "dominant-baseline",
        "drop-initial-after-adjust",
        "drop-initial-after-align",
        "drop-initial-before-adjust",
        "drop-initial-before-align",
        "drop-initial-size",
        "drop-initial-value",
        "elevation",
        "empty-cells",
        "fit",
        "fit-position",
        "flex",
        "flex-basis",
        "flex-direction",
        "flex-flow",
        "flex-grow",
        "flex-shrink",
        "flex-wrap",
        "float",
        "float-offset",
        "flow-from",
        "flow-into",
        "font",
        "font-feature-settings",
        "font-family",
        "font-kerning",
        "font-language-override",
        "font-size",
        "font-size-adjust",
        "font-stretch",
        "font-style",
        "font-synthesis",
        "font-variant",
        "font-variant-alternates",
        "font-variant-caps",
        "font-variant-east-asian",
        "font-variant-ligatures",
        "font-variant-numeric",
        "font-variant-position",
        "font-weight",
        "grid",
        "grid-area",
        "grid-auto-columns",
        "grid-auto-flow",
        "grid-auto-position",
        "grid-auto-rows",
        "grid-column",
        "grid-column-end",
        "grid-column-start",
        "grid-row",
        "grid-row-end",
        "grid-row-start",
        "grid-template",
        "grid-template-areas",
        "grid-template-columns",
        "grid-template-rows",
        "hanging-punctuation",
        "height",
        "hyphens",
        "icon",
        "image-orientation",
        "image-rendering",
        "image-resolution",
        "inline-box-align",
        "justify-content",
        "left",
        "letter-spacing",
        "line-break",
        "line-height",
        "line-stacking",
        "line-stacking-ruby",
        "line-stacking-shift",
        "line-stacking-strategy",
        "list-style",
        "list-style-image",
        "list-style-position",
        "list-style-type",
        "margin",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "marker-offset",
        "marks",
        "marquee-direction",
        "marquee-loop",
        "marquee-play-count",
        "marquee-speed",
        "marquee-style",
        "max-height",
        "max-width",
        "min-height",
        "min-width",
        "move-to",
        "nav-down",
        "nav-index",
        "nav-left",
        "nav-right",
        "nav-up",
        "object-fit",
        "object-position",
        "opacity",
        "order",
        "orphans",
        "outline",
        "outline-color",
        "outline-offset",
        "outline-style",
        "outline-width",
        "overflow",
        "overflow-style",
        "overflow-wrap",
        "overflow-x",
        "overflow-y",
        "padding",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "page",
        "page-break-after",
        "page-break-before",
        "page-break-inside",
        "page-policy",
        "pause",
        "pause-after",
        "pause-before",
        "perspective",
        "perspective-origin",
        "pitch",
        "pitch-range",
        "play-during",
        "position",
        "presentation-level",
        "punctuation-trim",
        "quotes",
        "region-break-after",
        "region-break-before",
        "region-break-inside",
        "region-fragment",
        "rendering-intent",
        "resize",
        "rest",
        "rest-after",
        "rest-before",
        "richness",
        "right",
        "rotation",
        "rotation-point",
        "ruby-align",
        "ruby-overhang",
        "ruby-position",
        "ruby-span",
        "shape-image-threshold",
        "shape-inside",
        "shape-margin",
        "shape-outside",
        "size",
        "speak",
        "speak-as",
        "speak-header",
        "speak-numeral",
        "speak-punctuation",
        "speech-rate",
        "stress",
        "string-set",
        "tab-size",
        "table-layout",
        "target",
        "target-name",
        "target-new",
        "target-position",
        "text-align",
        "text-align-last",
        "text-decoration",
        "text-decoration-color",
        "text-decoration-line",
        "text-decoration-skip",
        "text-decoration-style",
        "text-emphasis",
        "text-emphasis-color",
        "text-emphasis-position",
        "text-emphasis-style",
        "text-height",
        "text-indent",
        "text-justify",
        "text-outline",
        "text-overflow",
        "text-shadow",
        "text-size-adjust",
        "text-space-collapse",
        "text-transform",
        "text-underline-position",
        "text-wrap",
        "top",
        "transform",
        "transform-origin",
        "transform-style",
        "transition",
        "transition-delay",
        "transition-duration",
        "transition-property",
        "transition-timing-function",
        "unicode-bidi",
        "vertical-align",
        "visibility",
        "voice-balance",
        "voice-duration",
        "voice-family",
        "voice-pitch",
        "voice-range",
        "voice-rate",
        "voice-stress",
        "voice-volume",
        "volume",
        "white-space",
        "widows",
        "width",
        "will-change",
        "word-break",
        "word-spacing",
        "word-wrap",
        "z-index",
        "clip-path",
        "clip-rule",
        "mask",
        "enable-background",
        "filter",
        "flood-color",
        "flood-opacity",
        "lighting-color",
        "stop-color",
        "stop-opacity",
        "pointer-events",
        "color-interpolation",
        "color-interpolation-filters",
        "color-rendering",
        "fill",
        "fill-opacity",
        "fill-rule",
        "image-rendering",
        "marker",
        "marker-end",
        "marker-mid",
        "marker-start",
        "shape-rendering",
        "stroke",
        "stroke-dasharray",
        "stroke-dashoffset",
        "stroke-linecap",
        "stroke-linejoin",
        "stroke-miterlimit",
        "stroke-opacity",
        "stroke-width",
        "text-rendering",
        "baseline-shift",
        "dominant-baseline",
        "glyph-orientation-horizontal",
        "glyph-orientation-vertical",
        "text-anchor",
        "writing-mode",
        "font-smoothing",
        "osx-font-smoothing"
    ];
    var nonStandardPropertyKeywords_ = [
        "scrollbar-arrow-color",
        "scrollbar-base-color",
        "scrollbar-dark-shadow-color",
        "scrollbar-face-color",
        "scrollbar-highlight-color",
        "scrollbar-shadow-color",
        "scrollbar-3d-light-color",
        "scrollbar-track-color",
        "shape-inside",
        "searchfield-cancel-button",
        "searchfield-decoration",
        "searchfield-results-button",
        "searchfield-results-decoration",
        "zoom"
    ];
    var fontProperties_ = [
        "font-family",
        "src",
        "unicode-range",
        "font-variant",
        "font-feature-settings",
        "font-stretch",
        "font-weight",
        "font-style"
    ];
    var colorKeywords_ = [
        "aliceblue",
        "antiquewhite",
        "aqua",
        "aquamarine",
        "azure",
        "beige",
        "bisque",
        "black",
        "blanchedalmond",
        "blue",
        "blueviolet",
        "brown",
        "burlywood",
        "cadetblue",
        "chartreuse",
        "chocolate",
        "coral",
        "cornflowerblue",
        "cornsilk",
        "crimson",
        "cyan",
        "darkblue",
        "darkcyan",
        "darkgoldenrod",
        "darkgray",
        "darkgreen",
        "darkkhaki",
        "darkmagenta",
        "darkolivegreen",
        "darkorange",
        "darkorchid",
        "darkred",
        "darksalmon",
        "darkseagreen",
        "darkslateblue",
        "darkslategray",
        "darkturquoise",
        "darkviolet",
        "deeppink",
        "deepskyblue",
        "dimgray",
        "dodgerblue",
        "firebrick",
        "floralwhite",
        "forestgreen",
        "fuchsia",
        "gainsboro",
        "ghostwhite",
        "gold",
        "goldenrod",
        "gray",
        "grey",
        "green",
        "greenyellow",
        "honeydew",
        "hotpink",
        "indianred",
        "indigo",
        "ivory",
        "khaki",
        "lavender",
        "lavenderblush",
        "lawngreen",
        "lemonchiffon",
        "lightblue",
        "lightcoral",
        "lightcyan",
        "lightgoldenrodyellow",
        "lightgray",
        "lightgreen",
        "lightpink",
        "lightsalmon",
        "lightseagreen",
        "lightskyblue",
        "lightslategray",
        "lightsteelblue",
        "lightyellow",
        "lime",
        "limegreen",
        "linen",
        "magenta",
        "maroon",
        "mediumaquamarine",
        "mediumblue",
        "mediumorchid",
        "mediumpurple",
        "mediumseagreen",
        "mediumslateblue",
        "mediumspringgreen",
        "mediumturquoise",
        "mediumvioletred",
        "midnightblue",
        "mintcream",
        "mistyrose",
        "moccasin",
        "navajowhite",
        "navy",
        "oldlace",
        "olive",
        "olivedrab",
        "orange",
        "orangered",
        "orchid",
        "palegoldenrod",
        "palegreen",
        "paleturquoise",
        "palevioletred",
        "papayawhip",
        "peachpuff",
        "peru",
        "pink",
        "plum",
        "powderblue",
        "purple",
        "rebeccapurple",
        "red",
        "rosybrown",
        "royalblue",
        "saddlebrown",
        "salmon",
        "sandybrown",
        "seagreen",
        "seashell",
        "sienna",
        "silver",
        "skyblue",
        "slateblue",
        "slategray",
        "snow",
        "springgreen",
        "steelblue",
        "tan",
        "teal",
        "thistle",
        "tomato",
        "turquoise",
        "violet",
        "wheat",
        "white",
        "whitesmoke",
        "yellow",
        "yellowgreen"
    ];
    var valueKeywords_ = [
        "above",
        "absolute",
        "activeborder",
        "additive",
        "activecaption",
        "afar",
        "after-white-space",
        "ahead",
        "alias",
        "all",
        "all-scroll",
        "alphabetic",
        "alternate",
        "always",
        "amharic",
        "amharic-abegede",
        "antialiased",
        "appworkspace",
        "arabic-indic",
        "armenian",
        "asterisks",
        "attr",
        "auto",
        "avoid",
        "avoid-column",
        "avoid-page",
        "avoid-region",
        "background",
        "backwards",
        "baseline",
        "below",
        "bidi-override",
        "binary",
        "bengali",
        "blink",
        "block",
        "block-axis",
        "bold",
        "bolder",
        "border",
        "border-box",
        "both",
        "bottom",
        "break",
        "break-all",
        "break-word",
        "bullets",
        "button",
        "button-bevel",
        "buttonface",
        "buttonhighlight",
        "buttonshadow",
        "buttontext",
        "calc",
        "cambodian",
        "capitalize",
        "caps-lock-indicator",
        "caption",
        "captiontext",
        "caret",
        "cell",
        "center",
        "checkbox",
        "circle",
        "cjk-decimal",
        "cjk-earthly-branch",
        "cjk-heavenly-stem",
        "cjk-ideographic",
        "clear",
        "clip",
        "close-quote",
        "col-resize",
        "collapse",
        "column",
        "compact",
        "condensed",
        "contain",
        "content",
        "contents",
        "content-box",
        "context-menu",
        "continuous",
        "copy",
        "counter",
        "counters",
        "cover",
        "crop",
        "cross",
        "crosshair",
        "currentcolor",
        "cursive",
        "cyclic",
        "dashed",
        "decimal",
        "decimal-leading-zero",
        "default",
        "default-button",
        "destination-atop",
        "destination-in",
        "destination-out",
        "destination-over",
        "devanagari",
        "disc",
        "discard",
        "disclosure-closed",
        "disclosure-open",
        "document",
        "dot-dash",
        "dot-dot-dash",
        "dotted",
        "double",
        "down",
        "e-resize",
        "ease",
        "ease-in",
        "ease-in-out",
        "ease-out",
        "element",
        "ellipse",
        "ellipsis",
        "embed",
        "end",
        "ethiopic",
        "ethiopic-abegede",
        "ethiopic-abegede-am-et",
        "ethiopic-abegede-gez",
        "ethiopic-abegede-ti-er",
        "ethiopic-abegede-ti-et",
        "ethiopic-halehame-aa-er",
        "ethiopic-halehame-aa-et",
        "ethiopic-halehame-am-et",
        "ethiopic-halehame-gez",
        "ethiopic-halehame-om-et",
        "ethiopic-halehame-sid-et",
        "ethiopic-halehame-so-et",
        "ethiopic-halehame-ti-er",
        "ethiopic-halehame-ti-et",
        "ethiopic-halehame-tig",
        "ethiopic-numeric",
        "ew-resize",
        "expanded",
        "extends",
        "extra-condensed",
        "extra-expanded",
        "fantasy",
        "fast",
        "fill",
        "fixed",
        "flat",
        "flex",
        "footnotes",
        "forwards",
        "from",
        "geometricPrecision",
        "georgian",
        "graytext",
        "groove",
        "gujarati",
        "gurmukhi",
        "hand",
        "hangul",
        "hangul-consonant",
        "hebrew",
        "help",
        "hidden",
        "hide",
        "high",
        "higher",
        "highlight",
        "highlighttext",
        "hiragana",
        "hiragana-iroha",
        "horizontal",
        "hsl",
        "hsla",
        "icon",
        "ignore",
        "inactiveborder",
        "inactivecaption",
        "inactivecaptiontext",
        "infinite",
        "infobackground",
        "infotext",
        "inherit",
        "initial",
        "inline",
        "inline-axis",
        "inline-block",
        "inline-flex",
        "inline-table",
        "inset",
        "inside",
        "intrinsic",
        "invert",
        "italic",
        "japanese-formal",
        "japanese-informal",
        "justify",
        "kannada",
        "katakana",
        "katakana-iroha",
        "keep-all",
        "khmer",
        "korean-hangul-formal",
        "korean-hanja-formal",
        "korean-hanja-informal",
        "landscape",
        "lao",
        "large",
        "larger",
        "left",
        "level",
        "lighter",
        "line-through",
        "linear",
        "linear-gradient",
        "lines",
        "list-item",
        "listbox",
        "listitem",
        "local",
        "logical",
        "loud",
        "lower",
        "lower-alpha",
        "lower-armenian",
        "lower-greek",
        "lower-hexadecimal",
        "lower-latin",
        "lower-norwegian",
        "lower-roman",
        "lowercase",
        "ltr",
        "malayalam",
        "match",
        "matrix",
        "matrix3d",
        "media-controls-background",
        "media-current-time-display",
        "media-fullscreen-button",
        "media-mute-button",
        "media-play-button",
        "media-return-to-realtime-button",
        "media-rewind-button",
        "media-seek-back-button",
        "media-seek-forward-button",
        "media-slider",
        "media-sliderthumb",
        "media-time-remaining-display",
        "media-volume-slider",
        "media-volume-slider-container",
        "media-volume-sliderthumb",
        "medium",
        "menu",
        "menulist",
        "menulist-button",
        "menulist-text",
        "menulist-textfield",
        "menutext",
        "message-box",
        "middle",
        "min-intrinsic",
        "mix",
        "mongolian",
        "monospace",
        "move",
        "multiple",
        "myanmar",
        "n-resize",
        "narrower",
        "ne-resize",
        "nesw-resize",
        "no-close-quote",
        "no-drop",
        "no-open-quote",
        "no-repeat",
        "none",
        "normal",
        "not-allowed",
        "nowrap",
        "ns-resize",
        "numbers",
        "numeric",
        "nw-resize",
        "nwse-resize",
        "oblique",
        "octal",
        "open-quote",
        "optimizeLegibility",
        "optimizeSpeed",
        "oriya",
        "oromo",
        "outset",
        "outside",
        "outside-shape",
        "overlay",
        "overline",
        "padding",
        "padding-box",
        "painted",
        "page",
        "paused",
        "persian",
        "perspective",
        "plus-darker",
        "plus-lighter",
        "pointer",
        "polygon",
        "portrait",
        "pre",
        "pre-line",
        "pre-wrap",
        "preserve-3d",
        "progress",
        "push-button",
        "radial-gradient",
        "radio",
        "read-only",
        "read-write",
        "read-write-plaintext-only",
        "rectangle",
        "region",
        "relative",
        "repeat",
        "repeating-linear-gradient",
        "repeating-radial-gradient",
        "repeat-x",
        "repeat-y",
        "reset",
        "reverse",
        "rgb",
        "rgba",
        "ridge",
        "right",
        "rotate",
        "rotate3d",
        "rotateX",
        "rotateY",
        "rotateZ",
        "round",
        "row-resize",
        "rtl",
        "run-in",
        "running",
        "s-resize",
        "sans-serif",
        "scale",
        "scale3d",
        "scaleX",
        "scaleY",
        "scaleZ",
        "scroll",
        "scrollbar",
        "scroll-position",
        "se-resize",
        "searchfield",
        "searchfield-cancel-button",
        "searchfield-decoration",
        "searchfield-results-button",
        "searchfield-results-decoration",
        "semi-condensed",
        "semi-expanded",
        "separate",
        "serif",
        "show",
        "sidama",
        "simp-chinese-formal",
        "simp-chinese-informal",
        "single",
        "skew",
        "skewX",
        "skewY",
        "skip-white-space",
        "slide",
        "slider-horizontal",
        "slider-vertical",
        "sliderthumb-horizontal",
        "sliderthumb-vertical",
        "slow",
        "small",
        "small-caps",
        "small-caption",
        "smaller",
        "solid",
        "somali",
        "source-atop",
        "source-in",
        "source-out",
        "source-over",
        "space",
        "spell-out",
        "square",
        "square-button",
        "standard",
        "start",
        "static",
        "status-bar",
        "stretch",
        "stroke",
        "sub",
        "subpixel-antialiased",
        "super",
        "sw-resize",
        "symbolic",
        "symbols",
        "table",
        "table-caption",
        "table-cell",
        "table-column",
        "table-column-group",
        "table-footer-group",
        "table-header-group",
        "table-row",
        "table-row-group",
        "tamil",
        "telugu",
        "text",
        "text-bottom",
        "text-top",
        "textarea",
        "textfield",
        "thai",
        "thick",
        "thin",
        "threeddarkshadow",
        "threedface",
        "threedhighlight",
        "threedlightshadow",
        "threedshadow",
        "tibetan",
        "tigre",
        "tigrinya-er",
        "tigrinya-er-abegede",
        "tigrinya-et",
        "tigrinya-et-abegede",
        "to",
        "top",
        "trad-chinese-formal",
        "trad-chinese-informal",
        "translate",
        "translate3d",
        "translateX",
        "translateY",
        "translateZ",
        "transparent",
        "ultra-condensed",
        "ultra-expanded",
        "underline",
        "up",
        "upper-alpha",
        "upper-armenian",
        "upper-greek",
        "upper-hexadecimal",
        "upper-latin",
        "upper-norwegian",
        "upper-roman",
        "uppercase",
        "urdu",
        "url",
        "var",
        "vertical",
        "vertical-text",
        "visible",
        "visibleFill",
        "visiblePainted",
        "visibleStroke",
        "visual",
        "w-resize",
        "wait",
        "wave",
        "wider",
        "window",
        "windowframe",
        "windowtext",
        "words",
        "x-large",
        "x-small",
        "xor",
        "xx-large",
        "xx-small",
        "bicubic",
        "optimizespeed",
        "grayscale",
        "row",
        "row-reverse",
        "wrap",
        "wrap-reverse",
        "column-reverse",
        "flex-start",
        "flex-end",
        "space-between",
        "space-around",
        "unset"
    ];
    var wordOperatorKeywords_ = [
        "in",
        "and",
        "or",
        "not",
        "is not",
        "is a",
        "is",
        "isnt",
        "defined",
        "if unless"
    ], blockKeywords_ = [
        "for",
        "if",
        "else",
        "unless",
        "from",
        "to"
    ], commonAtoms_ = [
        "null",
        "true",
        "false",
        "href",
        "title",
        "type",
        "not-allowed",
        "readonly",
        "disabled"
    ], commonDef_ = [
        "@font-face",
        "@keyframes",
        "@media",
        "@viewport",
        "@page",
        "@host",
        "@supports",
        "@block",
        "@css"
    ];
    var hintWords = tagKeywords_.concat(documentTypes_, mediaTypes_, mediaFeatures_, propertyKeywords_, nonStandardPropertyKeywords_, colorKeywords_, valueKeywords_, fontProperties_, wordOperatorKeywords_, blockKeywords_, commonAtoms_, commonDef_);
    CodeMirror1.registerHelper("hintWords", "stylus", hintWords);
    CodeMirror1.defineMIME("text/x-styl", "stylus");
});
});

var swift = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// Swift mode created by Michael Kaminsky https://github.com/mkaminsky11
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var wordSet = function wordSet(words) {
        var set = {};
        for(var i = 0; i < words.length; i++)set[words[i]] = true;
        return set;
    };
    var tokenBase = //var regexp = /^\/(?!\s)(?:\/\/)?(?:\\.|[^\/])+\//
    function tokenBase(stream, state, prev) {
        if (stream.sol()) state.indented = stream.indentation();
        if (stream.eatSpace()) return null;
        var ch = stream.peek();
        if (ch == "/") {
            if (stream.match("//")) {
                stream.skipToEnd();
                return "comment";
            }
            if (stream.match("/*")) {
                state.tokenize.push(tokenComment);
                return tokenComment(stream, state);
            }
        }
        if (stream.match(instruction)) return "builtin";
        if (stream.match(attribute)) return "attribute";
        if (stream.match(binary)) return "number";
        if (stream.match(octal)) return "number";
        if (stream.match(hexadecimal)) return "number";
        if (stream.match(decimal)) return "number";
        if (stream.match(property)) return "property";
        if (operators.indexOf(ch) > -1) {
            stream.next();
            return "operator";
        }
        if (punc.indexOf(ch) > -1) {
            stream.next();
            stream.match("..");
            return "punctuation";
        }
        var stringMatch;
        if (stringMatch = stream.match(/("""|"|')/)) {
            var tokenize = tokenString.bind(null, stringMatch[0]);
            state.tokenize.push(tokenize);
            return tokenize(stream, state);
        }
        if (stream.match(identifier)) {
            var ident = stream.current();
            if (types.hasOwnProperty(ident)) return "variable-2";
            if (atoms.hasOwnProperty(ident)) return "atom";
            if (keywords.hasOwnProperty(ident)) {
                if (definingKeywords.hasOwnProperty(ident)) state.prev = "define";
                return "keyword";
            }
            if (prev == "define") return "def";
            return "variable";
        }
        stream.next();
        return null;
    };
    var tokenUntilClosingParen = function tokenUntilClosingParen() {
        var depth = 0;
        return function(stream, state, prev) {
            var inner = tokenBase(stream, state, prev);
            if (inner == "punctuation") {
                if (stream.current() == "(") ++depth;
                else if (stream.current() == ")") {
                    if (depth == 0) {
                        stream.backUp(1);
                        state.tokenize.pop();
                        return state.tokenize[state.tokenize.length - 1](stream, state);
                    } else --depth;
                }
            }
            return inner;
        };
    };
    var tokenString = function tokenString(openQuote, stream, state) {
        var singleLine = openQuote.length == 1;
        var ch, escaped = false;
        while(ch = stream.peek()){
            if (escaped) {
                stream.next();
                if (ch == "(") {
                    state.tokenize.push(tokenUntilClosingParen());
                    return "string";
                }
                escaped = false;
            } else if (stream.match(openQuote)) {
                state.tokenize.pop();
                return "string";
            } else {
                stream.next();
                escaped = ch == "\\";
            }
        }
        if (singleLine) {
            state.tokenize.pop();
        }
        return "string";
    };
    var Context = function Context(prev, align, indented) {
        this.prev = prev;
        this.align = align;
        this.indented = indented;
    };
    var pushContext = function pushContext(state, stream) {
        var align = stream.match(/^\s*($|\/[\/\*])/, false) ? null : stream.column() + 1;
        state.context = new Context(state.context, align, state.indented);
    };
    var popContext = function popContext(state) {
        if (state.context) {
            state.indented = state.context.indented;
            state.context = state.context.prev;
        }
    };
    var keywords = wordSet([
        "_",
        "var",
        "let",
        "class",
        "enum",
        "extension",
        "import",
        "protocol",
        "struct",
        "func",
        "typealias",
        "associatedtype",
        "open",
        "public",
        "internal",
        "fileprivate",
        "private",
        "deinit",
        "init",
        "new",
        "override",
        "self",
        "subscript",
        "super",
        "convenience",
        "dynamic",
        "final",
        "indirect",
        "lazy",
        "required",
        "static",
        "unowned",
        "unowned(safe)",
        "unowned(unsafe)",
        "weak",
        "as",
        "is",
        "break",
        "case",
        "continue",
        "default",
        "else",
        "fallthrough",
        "for",
        "guard",
        "if",
        "in",
        "repeat",
        "switch",
        "where",
        "while",
        "defer",
        "return",
        "inout",
        "mutating",
        "nonmutating",
        "catch",
        "do",
        "rethrows",
        "throw",
        "throws",
        "try",
        "didSet",
        "get",
        "set",
        "willSet",
        "assignment",
        "associativity",
        "infix",
        "left",
        "none",
        "operator",
        "postfix",
        "precedence",
        "precedencegroup",
        "prefix",
        "right",
        "Any",
        "AnyObject",
        "Type",
        "dynamicType",
        "Self",
        "Protocol",
        "__COLUMN__",
        "__FILE__",
        "__FUNCTION__",
        "__LINE__"
    ]);
    var definingKeywords = wordSet([
        "var",
        "let",
        "class",
        "enum",
        "extension",
        "import",
        "protocol",
        "struct",
        "func",
        "typealias",
        "associatedtype",
        "for"
    ]);
    var atoms = wordSet([
        "true",
        "false",
        "nil",
        "self",
        "super",
        "_"
    ]);
    var types = wordSet([
        "Array",
        "Bool",
        "Character",
        "Dictionary",
        "Double",
        "Float",
        "Int",
        "Int8",
        "Int16",
        "Int32",
        "Int64",
        "Never",
        "Optional",
        "Set",
        "String",
        "UInt8",
        "UInt16",
        "UInt32",
        "UInt64",
        "Void"
    ]);
    var operators = "+-/*%=|&<>~^?!";
    var punc = ":;,.(){}[]";
    var binary = /^\-?0b[01][01_]*/;
    var octal = /^\-?0o[0-7][0-7_]*/;
    var hexadecimal = /^\-?0x[\dA-Fa-f][\dA-Fa-f_]*(?:(?:\.[\dA-Fa-f][\dA-Fa-f_]*)?[Pp]\-?\d[\d_]*)?/;
    var decimal = /^\-?\d[\d_]*(?:\.\d[\d_]*)?(?:[Ee]\-?\d[\d_]*)?/;
    var identifier = /^\$\d+|(`?)[_A-Za-z][_A-Za-z$0-9]*\1/;
    var property = /^\.(?:\$\d+|(`?)[_A-Za-z][_A-Za-z$0-9]*\1)/;
    var instruction = /^\#[A-Za-z]+/;
    var attribute = /^@(?:\$\d+|(`?)[_A-Za-z][_A-Za-z$0-9]*\1)/;
    function tokenComment(stream, state) {
        var ch;
        while(true){
            stream.match(/^[^/*]+/, true);
            ch = stream.next();
            if (!ch) break;
            if (ch === "/" && stream.eat("*")) {
                state.tokenize.push(tokenComment);
            } else if (ch === "*" && stream.eat("/")) {
                state.tokenize.pop();
            }
        }
        return "comment";
    }
    CodeMirror1.defineMode("swift", function(config) {
        return {
            startState: function startState() {
                return {
                    prev: null,
                    context: null,
                    indented: 0,
                    tokenize: []
                };
            },
            token: function token(stream, state) {
                var prev = state.prev;
                state.prev = null;
                var tokenize = state.tokenize[state.tokenize.length - 1] || tokenBase;
                var style = tokenize(stream, state, prev);
                if (!style || style == "comment") state.prev = prev;
                else if (!state.prev) state.prev = style;
                if (style == "punctuation") {
                    var bracket = /[\(\[\{]|([\]\)\}])/.exec(stream.current());
                    if (bracket) (bracket[1] ? popContext : pushContext)(state, stream);
                }
                return style;
            },
            indent: function indent(state, textAfter) {
                var cx = state.context;
                if (!cx) return 0;
                var closing = /^[\]\}\)]/.test(textAfter);
                if (cx.align != null) return cx.align - (closing ? 1 : 0);
                return cx.indented + (closing ? 0 : config.indentUnit);
            },
            electricInput: /^\s*[\)\}\]]$/,
            lineComment: "//",
            blockCommentStart: "/*",
            blockCommentEnd: "*/",
            fold: "brace",
            closeBrackets: "()[]{}''\"\"``"
        };
    });
    CodeMirror1.defineMIME("text/x-swift", "swift");
});
});

var overlay = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
// Utility function that allows modes to be combined. The mode given
// as the base argument takes care of most of the normal mode
// functionality, but a second (typically simple) mode is used, which
// can override the style of text. Both modes get to parse all of the
// text, but when both assign a non-null style to a piece of code, the
// overlay wins, unless the combine argument was true and not overridden,
// or state.overlay.combineTokens was true, in which case the styles are
// combined.
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.overlayMode = function(base, overlay, combine) {
        return {
            startState: function startState() {
                return {
                    base: CodeMirror1.startState(base),
                    overlay: CodeMirror1.startState(overlay),
                    basePos: 0,
                    baseCur: null,
                    overlayPos: 0,
                    overlayCur: null,
                    streamSeen: null
                };
            },
            copyState: function copyState(state) {
                return {
                    base: CodeMirror1.copyState(base, state.base),
                    overlay: CodeMirror1.copyState(overlay, state.overlay),
                    basePos: state.basePos,
                    baseCur: null,
                    overlayPos: state.overlayPos,
                    overlayCur: null
                };
            },
            token: function token(stream, state) {
                if (stream != state.streamSeen || Math.min(state.basePos, state.overlayPos) < stream.start) {
                    state.streamSeen = stream;
                    state.basePos = state.overlayPos = stream.start;
                }
                if (stream.start == state.basePos) {
                    state.baseCur = base.token(stream, state.base);
                    state.basePos = stream.pos;
                }
                if (stream.start == state.overlayPos) {
                    stream.pos = stream.start;
                    state.overlayCur = overlay.token(stream, state.overlay);
                    state.overlayPos = stream.pos;
                }
                stream.pos = Math.min(state.basePos, state.overlayPos);
                // state.overlay.combineTokens always takes precedence over combine,
                // unless set to null
                if (state.overlayCur == null) return state.baseCur;
                else if (state.baseCur != null && state.overlay.combineTokens || combine && state.overlay.combineTokens == null) return state.baseCur + " " + state.overlayCur;
                else return state.overlayCur;
            },
            indent: base.indent && function(state, textAfter, line) {
                return base.indent(state.base, textAfter, line);
            },
            electricChars: base.electricChars,
            innerMode: function innerMode(state) {
                return {
                    state: state.base,
                    mode: base
                };
            },
            blankLine: function blankLine(state) {
                var baseToken, overlayToken;
                if (base.blankLine) baseToken = base.blankLine(state.base);
                if (overlay.blankLine) overlayToken = overlay.blankLine(state.overlay);
                return overlayToken == null ? baseToken : combine && baseToken != null ? baseToken + " " + overlayToken : overlayToken;
            }
        };
    };
});
});

var coffeescript = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
/**
 * Link to the project's GitHub page:
 * https://github.com/pickhardt/coffeescript-codemirror-mode
 */ (function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("coffeescript", function(conf, parserConf) {
        var wordRegexp = function wordRegexp(words) {
            return new RegExp("^((" + words.join(")|(") + "))\\b");
        };
        var tokenBase = // Tokenizers
        function tokenBase(stream, state) {
            // Handle scope changes
            if (stream.sol()) {
                if (state.scope.align === null) state.scope.align = false;
                var scopeOffset = state.scope.offset;
                if (stream.eatSpace()) {
                    var lineOffset = stream.indentation();
                    if (lineOffset > scopeOffset && state.scope.type == "coffee") {
                        return "indent";
                    } else if (lineOffset < scopeOffset) {
                        return "dedent";
                    }
                    return null;
                } else {
                    if (scopeOffset > 0) {
                        dedent(stream, state);
                    }
                }
            }
            if (stream.eatSpace()) {
                return null;
            }
            var ch = stream.peek();
            // Handle docco title comment (single line)
            if (stream.match("####")) {
                stream.skipToEnd();
                return "comment";
            }
            // Handle multi line comments
            if (stream.match("###")) {
                state.tokenize = longComment;
                return state.tokenize(stream, state);
            }
            // Single line comment
            if (ch === "#") {
                stream.skipToEnd();
                return "comment";
            }
            // Handle number literals
            if (stream.match(/^-?[0-9\.]/, false)) {
                var floatLiteral = false;
                // Floats
                if (stream.match(/^-?\d*\.\d+(e[\+\-]?\d+)?/i)) {
                    floatLiteral = true;
                }
                if (stream.match(/^-?\d+\.\d*/)) {
                    floatLiteral = true;
                }
                if (stream.match(/^-?\.\d+/)) {
                    floatLiteral = true;
                }
                if (floatLiteral) {
                    // prevent from getting extra . on 1..
                    if (stream.peek() == ".") {
                        stream.backUp(1);
                    }
                    return "number";
                }
                // Integers
                var intLiteral = false;
                // Hex
                if (stream.match(/^-?0x[0-9a-f]+/i)) {
                    intLiteral = true;
                }
                // Decimal
                if (stream.match(/^-?[1-9]\d*(e[\+\-]?\d+)?/)) {
                    intLiteral = true;
                }
                // Zero by itself with no other piece of number.
                if (stream.match(/^-?0(?![\dx])/i)) {
                    intLiteral = true;
                }
                if (intLiteral) {
                    return "number";
                }
            }
            // Handle strings
            if (stream.match(stringPrefixes)) {
                state.tokenize = tokenFactory(stream.current(), false, "string");
                return state.tokenize(stream, state);
            }
            // Handle regex literals
            if (stream.match(regexPrefixes)) {
                if (stream.current() != "/" || stream.match(/^.*\//, false)) {
                    state.tokenize = tokenFactory(stream.current(), true, "string-2");
                    return state.tokenize(stream, state);
                } else {
                    stream.backUp(1);
                }
            }
            // Handle operators and delimiters
            if (stream.match(operators) || stream.match(wordOperators)) {
                return "operator";
            }
            if (stream.match(delimiters)) {
                return "punctuation";
            }
            if (stream.match(constants)) {
                return "atom";
            }
            if (stream.match(atProp) || state.prop && stream.match(identifiers)) {
                return "property";
            }
            if (stream.match(keywords)) {
                return "keyword";
            }
            if (stream.match(identifiers)) {
                return "variable";
            }
            // Handle non-detected items
            stream.next();
            return ERRORCLASS;
        };
        var tokenFactory = function tokenFactory(delimiter, singleline, outclass) {
            return function(stream, state) {
                while(!stream.eol()){
                    stream.eatWhile(/[^'"\/\\]/);
                    if (stream.eat("\\")) {
                        stream.next();
                        if (singleline && stream.eol()) {
                            return outclass;
                        }
                    } else if (stream.match(delimiter)) {
                        state.tokenize = tokenBase;
                        return outclass;
                    } else {
                        stream.eat(/['"\/]/);
                    }
                }
                if (singleline) {
                    if (parserConf.singleLineStringErrors) {
                        outclass = ERRORCLASS;
                    } else {
                        state.tokenize = tokenBase;
                    }
                }
                return outclass;
            };
        };
        var longComment = function longComment(stream, state) {
            while(!stream.eol()){
                stream.eatWhile(/[^#]/);
                if (stream.match("###")) {
                    state.tokenize = tokenBase;
                    break;
                }
                stream.eatWhile("#");
            }
            return "comment";
        };
        var indent = function indent(stream, state, type) {
            type = type || "coffee";
            var offset = 0, align = false, alignOffset = null;
            for(var scope = state.scope; scope; scope = scope.prev){
                if (scope.type === "coffee" || scope.type == "}") {
                    offset = scope.offset + conf.indentUnit;
                    break;
                }
            }
            if (type !== "coffee") {
                align = null;
                alignOffset = stream.column() + stream.current().length;
            } else if (state.scope.align) {
                state.scope.align = false;
            }
            state.scope = {
                offset: offset,
                type: type,
                prev: state.scope,
                align: align,
                alignOffset: alignOffset
            };
        };
        var dedent = function dedent(stream, state) {
            if (!state.scope.prev) return;
            if (state.scope.type === "coffee") {
                var _indent = stream.indentation();
                var matched = false;
                for(var scope = state.scope; scope; scope = scope.prev){
                    if (_indent === scope.offset) {
                        matched = true;
                        break;
                    }
                }
                if (!matched) {
                    return true;
                }
                while(state.scope.prev && state.scope.offset !== _indent){
                    state.scope = state.scope.prev;
                }
                return false;
            } else {
                state.scope = state.scope.prev;
                return false;
            }
        };
        var tokenLexer = function tokenLexer(stream, state) {
            var style = state.tokenize(stream, state);
            var current = stream.current();
            // Handle scope changes.
            if (current === "return") {
                state.dedent = true;
            }
            if ((current === "->" || current === "=>") && stream.eol() || style === "indent") {
                indent(stream, state);
            }
            var delimiter_index = "[({".indexOf(current);
            if (delimiter_index !== -1) {
                indent(stream, state, "])}".slice(delimiter_index, delimiter_index + 1));
            }
            if (indentKeywords.exec(current)) {
                indent(stream, state);
            }
            if (current == "then") {
                dedent(stream, state);
            }
            if (style === "dedent") {
                if (dedent(stream, state)) {
                    return ERRORCLASS;
                }
            }
            delimiter_index = "])}".indexOf(current);
            if (delimiter_index !== -1) {
                while(state.scope.type == "coffee" && state.scope.prev)state.scope = state.scope.prev;
                if (state.scope.type == current) state.scope = state.scope.prev;
            }
            if (state.dedent && stream.eol()) {
                if (state.scope.type == "coffee" && state.scope.prev) state.scope = state.scope.prev;
                state.dedent = false;
            }
            return style;
        };
        var ERRORCLASS = "error";
        var operators = /^(?:->|=>|\+[+=]?|-[\-=]?|\*[\*=]?|\/[\/=]?|[=!]=|<[><]?=?|>>?=?|%=?|&=?|\|=?|\^=?|\~|!|\?|(or|and|\|\||&&|\?)=)/;
        var delimiters = /^(?:[()\[\]{},:`=;]|\.\.?\.?)/;
        var identifiers = /^[_A-Za-z$][_A-Za-z$0-9]*/;
        var atProp = /^@[_A-Za-z$][_A-Za-z$0-9]*/;
        var wordOperators = wordRegexp([
            "and",
            "or",
            "not",
            "is",
            "isnt",
            "in",
            "instanceof",
            "typeof"
        ]);
        var indentKeywords = [
            "for",
            "while",
            "loop",
            "if",
            "unless",
            "else",
            "switch",
            "try",
            "catch",
            "finally",
            "class"
        ];
        var commonKeywords = [
            "break",
            "by",
            "continue",
            "debugger",
            "delete",
            "do",
            "in",
            "of",
            "new",
            "return",
            "then",
            "this",
            "@",
            "throw",
            "when",
            "until",
            "extends"
        ];
        var keywords = wordRegexp(indentKeywords.concat(commonKeywords));
        indentKeywords = wordRegexp(indentKeywords);
        var stringPrefixes = /^('{3}|\"{3}|['\"])/;
        var regexPrefixes = /^(\/{3}|\/)/;
        var commonConstants = [
            "Infinity",
            "NaN",
            "undefined",
            "null",
            "true",
            "false",
            "on",
            "off",
            "yes",
            "no"
        ];
        var constants = wordRegexp(commonConstants);
        var external = {
            startState: function startState(basecolumn) {
                return {
                    tokenize: tokenBase,
                    scope: {
                        offset: basecolumn || 0,
                        type: "coffee",
                        prev: null,
                        align: false
                    },
                    prop: false,
                    dedent: 0
                };
            },
            token: function token(stream, state) {
                var fillAlign = state.scope.align === null && state.scope;
                if (fillAlign && stream.sol()) fillAlign.align = false;
                var style = tokenLexer(stream, state);
                if (style && style != "comment") {
                    if (fillAlign) fillAlign.align = true;
                    state.prop = style == "punctuation" && stream.current() == ".";
                }
                return style;
            },
            indent: function indent(state, text) {
                if (state.tokenize != tokenBase) return 0;
                var scope = state.scope;
                var closer = text && "])}".indexOf(text.charAt(0)) > -1;
                if (closer) while(scope.type == "coffee" && scope.prev)scope = scope.prev;
                var closes = closer && scope.type === text.charAt(0);
                if (scope.align) return scope.alignOffset - (closes ? 1 : 0);
                else return (closes ? scope.prev : scope).offset;
            },
            lineComment: "#",
            fold: "indent"
        };
        return external;
    });
    // IANA registered media type
    // https://www.iana.org/assignments/media-types/
    CodeMirror1.defineMIME("application/vnd.coffeescript", "coffeescript");
    CodeMirror1.defineMIME("text/x-coffeescript", "coffeescript");
    CodeMirror1.defineMIME("text/coffeescript", "coffeescript");
});
});

var pug = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, javascript, css, htmlmixed);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("pug", function(config) {
        var State = function State() {
            this.javaScriptLine = false;
            this.javaScriptLineExcludesColon = false;
            this.javaScriptArguments = false;
            this.javaScriptArgumentsDepth = 0;
            this.isInterpolating = false;
            this.interpolationNesting = 0;
            this.jsState = CodeMirror1.startState(jsMode);
            this.restOfLine = "";
            this.isIncludeFiltered = false;
            this.isEach = false;
            this.lastTag = "";
            this.scriptType = "";
            // Attributes Mode
            this.isAttrs = false;
            this.attrsNest = [];
            this.inAttributeName = true;
            this.attributeIsType = false;
            this.attrValue = "";
            // Indented Mode
            this.indentOf = Infinity;
            this.indentToken = "";
            this.innerMode = null;
            this.innerState = null;
            this.innerModeForLine = false;
        };
        var javaScript = function javaScript(stream, state) {
            if (stream.sol()) {
                // if javaScriptLine was set at end of line, ignore it
                state.javaScriptLine = false;
                state.javaScriptLineExcludesColon = false;
            }
            if (state.javaScriptLine) {
                if (state.javaScriptLineExcludesColon && stream.peek() === ":") {
                    state.javaScriptLine = false;
                    state.javaScriptLineExcludesColon = false;
                    return;
                }
                var tok = jsMode.token(stream, state.jsState);
                if (stream.eol()) state.javaScriptLine = false;
                return tok || true;
            }
        };
        var javaScriptArguments = function javaScriptArguments(stream, state) {
            if (state.javaScriptArguments) {
                if (state.javaScriptArgumentsDepth === 0 && stream.peek() !== "(") {
                    state.javaScriptArguments = false;
                    return;
                }
                if (stream.peek() === "(") {
                    state.javaScriptArgumentsDepth++;
                } else if (stream.peek() === ")") {
                    state.javaScriptArgumentsDepth--;
                }
                if (state.javaScriptArgumentsDepth === 0) {
                    state.javaScriptArguments = false;
                    return;
                }
                var tok = jsMode.token(stream, state.jsState);
                return tok || true;
            }
        };
        var yieldStatement = function yieldStatement(stream) {
            if (stream.match(/^yield\b/)) {
                return "keyword";
            }
        };
        var doctype = function doctype(stream) {
            if (stream.match(/^(?:doctype) *([^\n]+)?/)) {
                return DOCTYPE;
            }
        };
        var interpolation = function interpolation(stream, state) {
            if (stream.match("#{")) {
                state.isInterpolating = true;
                state.interpolationNesting = 0;
                return "punctuation";
            }
        };
        var interpolationContinued = function interpolationContinued(stream, state) {
            if (state.isInterpolating) {
                if (stream.peek() === "}") {
                    state.interpolationNesting--;
                    if (state.interpolationNesting < 0) {
                        stream.next();
                        state.isInterpolating = false;
                        return "punctuation";
                    }
                } else if (stream.peek() === "{") {
                    state.interpolationNesting++;
                }
                return jsMode.token(stream, state.jsState) || true;
            }
        };
        var caseStatement = function caseStatement(stream, state) {
            if (stream.match(/^case\b/)) {
                state.javaScriptLine = true;
                return KEYWORD;
            }
        };
        var when = function when(stream, state) {
            if (stream.match(/^when\b/)) {
                state.javaScriptLine = true;
                state.javaScriptLineExcludesColon = true;
                return KEYWORD;
            }
        };
        var defaultStatement = function defaultStatement(stream) {
            if (stream.match(/^default\b/)) {
                return KEYWORD;
            }
        };
        var extendsStatement = function extendsStatement(stream, state) {
            if (stream.match(/^extends?\b/)) {
                state.restOfLine = "string";
                return KEYWORD;
            }
        };
        var append = function append(stream, state) {
            if (stream.match(/^append\b/)) {
                state.restOfLine = "variable";
                return KEYWORD;
            }
        };
        var prepend = function prepend(stream, state) {
            if (stream.match(/^prepend\b/)) {
                state.restOfLine = "variable";
                return KEYWORD;
            }
        };
        var block = function block(stream, state) {
            if (stream.match(/^block\b *(?:(prepend|append)\b)?/)) {
                state.restOfLine = "variable";
                return KEYWORD;
            }
        };
        var include = function include(stream, state) {
            if (stream.match(/^include\b/)) {
                state.restOfLine = "string";
                return KEYWORD;
            }
        };
        var includeFiltered = function includeFiltered(stream, state) {
            if (stream.match(/^include:([a-zA-Z0-9\-]+)/, false) && stream.match("include")) {
                state.isIncludeFiltered = true;
                return KEYWORD;
            }
        };
        var includeFilteredContinued = function includeFilteredContinued(stream, state) {
            if (state.isIncludeFiltered) {
                var tok = filter(stream, state);
                state.isIncludeFiltered = false;
                state.restOfLine = "string";
                return tok;
            }
        };
        var mixin = function mixin(stream, state) {
            if (stream.match(/^mixin\b/)) {
                state.javaScriptLine = true;
                return KEYWORD;
            }
        };
        var call = function call(stream, state) {
            if (stream.match(/^\+([-\w]+)/)) {
                if (!stream.match(/^\( *[-\w]+ *=/, false)) {
                    state.javaScriptArguments = true;
                    state.javaScriptArgumentsDepth = 0;
                }
                return "variable";
            }
            if (stream.match("+#{", false)) {
                stream.next();
                state.mixinCallAfter = true;
                return interpolation(stream, state);
            }
        };
        var callArguments = function callArguments(stream, state) {
            if (state.mixinCallAfter) {
                state.mixinCallAfter = false;
                if (!stream.match(/^\( *[-\w]+ *=/, false)) {
                    state.javaScriptArguments = true;
                    state.javaScriptArgumentsDepth = 0;
                }
                return true;
            }
        };
        var conditional = function conditional(stream, state) {
            if (stream.match(/^(if|unless|else if|else)\b/)) {
                state.javaScriptLine = true;
                return KEYWORD;
            }
        };
        var each = function each(stream, state) {
            if (stream.match(/^(- *)?(each|for)\b/)) {
                state.isEach = true;
                return KEYWORD;
            }
        };
        var eachContinued = function eachContinued(stream, state) {
            if (state.isEach) {
                if (stream.match(/^ in\b/)) {
                    state.javaScriptLine = true;
                    state.isEach = false;
                    return KEYWORD;
                } else if (stream.sol() || stream.eol()) {
                    state.isEach = false;
                } else if (stream.next()) {
                    while(!stream.match(/^ in\b/, false) && stream.next());
                    return "variable";
                }
            }
        };
        var whileStatement = function whileStatement(stream, state) {
            if (stream.match(/^while\b/)) {
                state.javaScriptLine = true;
                return KEYWORD;
            }
        };
        var tag = function tag(stream, state) {
            var captures;
            if (captures = stream.match(/^(\w(?:[-:\w]*\w)?)\/?/)) {
                state.lastTag = captures[1].toLowerCase();
                if (state.lastTag === "script") {
                    state.scriptType = "application/javascript";
                }
                return "tag";
            }
        };
        var filter = function filter(stream, state) {
            if (stream.match(/^:([\w\-]+)/)) {
                var innerMode;
                if (config && config.innerModes) {
                    innerMode = config.innerModes(stream.current().substring(1));
                }
                if (!innerMode) {
                    innerMode = stream.current().substring(1);
                }
                if (typeof innerMode === "string") {
                    innerMode = CodeMirror1.getMode(config, innerMode);
                }
                setInnerMode(stream, state, innerMode);
                return "atom";
            }
        };
        var code = function code(stream, state) {
            if (stream.match(/^(!?=|-)/)) {
                state.javaScriptLine = true;
                return "punctuation";
            }
        };
        var id = function id(stream) {
            if (stream.match(/^#([\w-]+)/)) {
                return ID;
            }
        };
        var className = function className(stream) {
            if (stream.match(/^\.([\w-]+)/)) {
                return CLASS;
            }
        };
        var attrs = function attrs(stream, state) {
            if (stream.peek() == "(") {
                stream.next();
                state.isAttrs = true;
                state.attrsNest = [];
                state.inAttributeName = true;
                state.attrValue = "";
                state.attributeIsType = false;
                return "punctuation";
            }
        };
        var attributesBlock = function attributesBlock(stream, state) {
            if (stream.match(/^&attributes\b/)) {
                state.javaScriptArguments = true;
                state.javaScriptArgumentsDepth = 0;
                return "keyword";
            }
        };
        var indent = function indent(stream) {
            if (stream.sol() && stream.eatSpace()) {
                return "indent";
            }
        };
        var comment = function comment(stream, state) {
            if (stream.match(/^ *\/\/(-)?([^\n]*)/)) {
                state.indentOf = stream.indentation();
                state.indentToken = "comment";
                return "comment";
            }
        };
        var colon = function colon(stream) {
            if (stream.match(/^: */)) {
                return "colon";
            }
        };
        var text = function text(stream, state) {
            if (stream.match(/^(?:\| ?| )([^\n]+)/)) {
                return "string";
            }
            if (stream.match(/^(<[^\n]*)/, false)) {
                // html string
                setInnerMode(stream, state, "htmlmixed");
                state.innerModeForLine = true;
                return innerMode(stream, state, true);
            }
        };
        var dot = function dot(stream, state) {
            if (stream.eat(".")) {
                var innerMode = null;
                if (state.lastTag === "script" && state.scriptType.toLowerCase().indexOf("javascript") != -1) {
                    innerMode = state.scriptType.toLowerCase().replace(/"|'/g, "");
                } else if (state.lastTag === "style") {
                    innerMode = "css";
                }
                setInnerMode(stream, state, innerMode);
                return "dot";
            }
        };
        var fail = function fail(stream) {
            stream.next();
            return null;
        };
        var setInnerMode = function setInnerMode(stream, state, mode) {
            mode = CodeMirror1.mimeModes[mode] || mode;
            mode = config.innerModes ? config.innerModes(mode) || mode : mode;
            mode = CodeMirror1.mimeModes[mode] || mode;
            mode = CodeMirror1.getMode(config, mode);
            state.indentOf = stream.indentation();
            if (mode && mode.name !== "null") {
                state.innerMode = mode;
            } else {
                state.indentToken = "string";
            }
        };
        var innerMode = function innerMode(stream, state, force) {
            if (stream.indentation() > state.indentOf || state.innerModeForLine && !stream.sol() || force) {
                if (state.innerMode) {
                    if (!state.innerState) {
                        state.innerState = state.innerMode.startState ? CodeMirror1.startState(state.innerMode, stream.indentation()) : {};
                    }
                    return stream.hideFirstChars(state.indentOf + 2, function() {
                        return state.innerMode.token(stream, state.innerState) || true;
                    });
                } else {
                    stream.skipToEnd();
                    return state.indentToken;
                }
            } else if (stream.sol()) {
                state.indentOf = Infinity;
                state.indentToken = null;
                state.innerMode = null;
                state.innerState = null;
            }
        };
        var restOfLine = function restOfLine(stream, state) {
            if (stream.sol()) {
                // if restOfLine was set at end of line, ignore it
                state.restOfLine = "";
            }
            if (state.restOfLine) {
                stream.skipToEnd();
                var tok = state.restOfLine;
                state.restOfLine = "";
                return tok;
            }
        };
        var startState = function startState() {
            return new State();
        };
        var copyState = function copyState(state) {
            return state.copy();
        };
        var nextToken = /**
   * Get the next token in the stream
   *
   * @param {Stream} stream
   * @param {State} state
   */ function nextToken(stream, state) {
            var tok = innerMode(stream, state) || restOfLine(stream, state) || interpolationContinued(stream, state) || includeFilteredContinued(stream, state) || eachContinued(stream, state) || attrsContinued(stream, state) || javaScript(stream, state) || javaScriptArguments(stream, state) || callArguments(stream, state) || yieldStatement(stream) || doctype(stream) || interpolation(stream, state) || caseStatement(stream, state) || when(stream, state) || defaultStatement(stream) || extendsStatement(stream, state) || append(stream, state) || prepend(stream, state) || block(stream, state) || include(stream, state) || includeFiltered(stream, state) || mixin(stream, state) || call(stream, state) || conditional(stream, state) || each(stream, state) || whileStatement(stream, state) || tag(stream, state) || filter(stream, state) || code(stream, state) || id(stream) || className(stream) || attrs(stream, state) || attributesBlock(stream, state) || indent(stream) || text(stream, state) || comment(stream, state) || colon(stream) || dot(stream, state) || fail(stream);
            return tok === true ? null : tok;
        };
        // token types
        var KEYWORD = "keyword";
        var DOCTYPE = "meta";
        var ID = "builtin";
        var CLASS = "qualifier";
        var ATTRS_NEST = {
            "{": "}",
            "(": ")",
            "[": "]"
        };
        var jsMode = CodeMirror1.getMode(config, "javascript");
        /**
   * Safely copy a state
   *
   * @return {State}
   */ State.prototype.copy = function() {
            var res = new State();
            res.javaScriptLine = this.javaScriptLine;
            res.javaScriptLineExcludesColon = this.javaScriptLineExcludesColon;
            res.javaScriptArguments = this.javaScriptArguments;
            res.javaScriptArgumentsDepth = this.javaScriptArgumentsDepth;
            res.isInterpolating = this.isInterpolating;
            res.interpolationNesting = this.interpolationNesting;
            res.jsState = CodeMirror1.copyState(jsMode, this.jsState);
            res.innerMode = this.innerMode;
            if (this.innerMode && this.innerState) {
                res.innerState = CodeMirror1.copyState(this.innerMode, this.innerState);
            }
            res.restOfLine = this.restOfLine;
            res.isIncludeFiltered = this.isIncludeFiltered;
            res.isEach = this.isEach;
            res.lastTag = this.lastTag;
            res.scriptType = this.scriptType;
            res.isAttrs = this.isAttrs;
            res.attrsNest = this.attrsNest.slice();
            res.inAttributeName = this.inAttributeName;
            res.attributeIsType = this.attributeIsType;
            res.attrValue = this.attrValue;
            res.indentOf = this.indentOf;
            res.indentToken = this.indentToken;
            res.innerModeForLine = this.innerModeForLine;
            return res;
        };
        function attrsContinued(stream, state) {
            if (state.isAttrs) {
                if (ATTRS_NEST[stream.peek()]) {
                    state.attrsNest.push(ATTRS_NEST[stream.peek()]);
                }
                if (state.attrsNest[state.attrsNest.length - 1] === stream.peek()) {
                    state.attrsNest.pop();
                } else if (stream.eat(")")) {
                    state.isAttrs = false;
                    return "punctuation";
                }
                if (state.inAttributeName && stream.match(/^[^=,\)!]+/)) {
                    if (stream.peek() === "=" || stream.peek() === "!") {
                        state.inAttributeName = false;
                        state.jsState = CodeMirror1.startState(jsMode);
                        if (state.lastTag === "script" && stream.current().trim().toLowerCase() === "type") {
                            state.attributeIsType = true;
                        } else {
                            state.attributeIsType = false;
                        }
                    }
                    return "attribute";
                }
                var tok = jsMode.token(stream, state.jsState);
                if (state.attributeIsType && tok === "string") {
                    state.scriptType = stream.current().toString();
                }
                if (state.attrsNest.length === 0 && (tok === "string" || tok === "variable" || tok === "keyword")) {
                    try {
                        Function("", "var x " + state.attrValue.replace(/,\s*$/, "").replace(/^!/, ""));
                        state.inAttributeName = true;
                        state.attrValue = "";
                        stream.backUp(stream.current().length);
                        return attrsContinued(stream, state);
                    } catch (ex) {
                    //not the end of an attribute
                    }
                }
                state.attrValue += stream.current();
                return tok || true;
            }
        }
        return {
            startState: startState,
            copyState: copyState,
            token: nextToken
        };
    }, "javascript", "css", "htmlmixed");
    CodeMirror1.defineMIME("text/x-pug", "pug");
    CodeMirror1.defineMIME("text/x-jade", "pug");
});
});

var simple = createCommonjsModule(function (module, exports) {
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    var ensureState = function ensureState(states, name) {
        if (!states.hasOwnProperty(name)) throw new Error("Undefined state " + name + " in simple mode");
    };
    var toRegex = function toRegex(val, caret) {
        if (!val) return /(?:)/;
        var flags = "";
        if (_instanceof(val, RegExp)) {
            if (val.ignoreCase) flags = "i";
            if (val.unicode) flags += "u";
            val = val.source;
        } else {
            val = String(val);
        }
        return new RegExp((caret === false ? "" : "^") + "(?:" + val + ")", flags);
    };
    var asToken = function asToken(val) {
        if (!val) return null;
        if (val.apply) return val;
        if (typeof val == "string") return val.replace(/\./g, " ");
        var result = [];
        for(var i = 0; i < val.length; i++)result.push(val[i] && val[i].replace(/\./g, " "));
        return result;
    };
    var Rule = function Rule(data, states) {
        if (data.next || data.push) ensureState(states, data.next || data.push);
        this.regex = toRegex(data.regex);
        this.token = asToken(data.token);
        this.data = data;
    };
    var tokenFunction = function tokenFunction(states, config) {
        return function(stream, state) {
            if (state.pending) {
                var pend = state.pending.shift();
                if (state.pending.length == 0) state.pending = null;
                stream.pos += pend.text.length;
                return pend.token;
            }
            if (state.local) {
                if (state.local.end && stream.match(state.local.end)) {
                    var tok = state.local.endToken || null;
                    state.local = state.localState = null;
                    return tok;
                } else {
                    var tok = state.local.mode.token(stream, state.localState), m;
                    if (state.local.endScan && (m = state.local.endScan.exec(stream.current()))) stream.pos = stream.start + m.index;
                    return tok;
                }
            }
            var curState = states[state.state];
            for(var i = 0; i < curState.length; i++){
                var rule = curState[i];
                var matches = (!rule.data.sol || stream.sol()) && stream.match(rule.regex);
                if (matches) {
                    if (rule.data.next) {
                        state.state = rule.data.next;
                    } else if (rule.data.push) {
                        (state.stack || (state.stack = [])).push(state.state);
                        state.state = rule.data.push;
                    } else if (rule.data.pop && state.stack && state.stack.length) {
                        state.state = state.stack.pop();
                    }
                    if (rule.data.mode) enterLocalMode(config, state, rule.data.mode, rule.token);
                    if (rule.data.indent) state.indent.push(stream.indentation() + config.indentUnit);
                    if (rule.data.dedent) state.indent.pop();
                    var token = rule.token;
                    if (token && token.apply) token = token(matches);
                    if (matches.length > 2 && rule.token && typeof rule.token != "string") {
                        for(var j = 2; j < matches.length; j++)if (matches[j]) (state.pending || (state.pending = [])).push({
                            text: matches[j],
                            token: rule.token[j - 1]
                        });
                        stream.backUp(matches[0].length - (matches[1] ? matches[1].length : 0));
                        return token[0];
                    } else if (token && token.join) {
                        return token[0];
                    } else {
                        return token;
                    }
                }
            }
            stream.next();
            return null;
        };
    };
    var enterLocalMode = function enterLocalMode(config, state, spec, token) {
        var pers;
        if (spec.persistent) {
            for(var p = state.persistentStates; p && !pers; p = p.next)if (spec.spec ? cmp(spec.spec, p.spec) : spec.mode == p.mode) pers = p;
        }
        var mode = pers ? pers.mode : spec.mode || CodeMirror1.getMode(config, spec.spec);
        var lState = pers ? pers.state : CodeMirror1.startState(mode);
        if (spec.persistent && !pers) state.persistentStates = {
            mode: mode,
            spec: spec.spec,
            state: lState,
            next: state.persistentStates
        };
        state.localState = lState;
        state.local = {
            mode: mode,
            end: spec.end && toRegex(spec.end),
            endScan: spec.end && spec.forceEnd !== false && toRegex(spec.end, false),
            endToken: token && token.join ? token[token.length - 1] : token
        };
    };
    var indexOf = function indexOf(val, arr) {
        for(var i = 0; i < arr.length; i++)if (arr[i] === val) return true;
    };
    var indentFunction = function indentFunction(states, meta) {
        return function(state, textAfter, line) {
            if (state.local && state.local.mode.indent) return state.local.mode.indent(state.localState, textAfter, line);
            if (state.indent == null || state.local || meta.dontIndentStates && indexOf(state.state, meta.dontIndentStates) > -1) return CodeMirror1.Pass;
            var pos = state.indent.length - 1, rules = states[state.state];
            scan: for(;;){
                for(var i = 0; i < rules.length; i++){
                    var rule = rules[i];
                    if (rule.data.dedent && rule.data.dedentIfLineStart !== false) {
                        var m = rule.regex.exec(textAfter);
                        if (m && m[0]) {
                            pos--;
                            if (rule.next || rule.push) rules = states[rule.next || rule.push];
                            textAfter = textAfter.slice(m[0].length);
                            continue scan;
                        }
                    }
                }
                break;
            }
            return pos < 0 ? 0 : state.indent[pos];
        };
    };
    CodeMirror1.defineSimpleMode = function(name, states) {
        CodeMirror1.defineMode(name, function(config) {
            return CodeMirror1.simpleMode(config, states);
        });
    };
    CodeMirror1.simpleMode = function(config, states) {
        ensureState(states, "start");
        var states_ = {}, meta = states.meta || {}, hasIndentation = false;
        for(var state in states)if (state != meta && states.hasOwnProperty(state)) {
            var list = states_[state] = [], orig = states[state];
            for(var i = 0; i < orig.length; i++){
                var data = orig[i];
                list.push(new Rule(data, states));
                if (data.indent || data.dedent) hasIndentation = true;
            }
        }
        var mode = {
            startState: function startState() {
                return {
                    state: "start",
                    pending: null,
                    local: null,
                    localState: null,
                    indent: hasIndentation ? [] : null
                };
            },
            copyState: function copyState(state) {
                var s = {
                    state: state.state,
                    pending: state.pending,
                    local: state.local,
                    localState: null,
                    indent: state.indent && state.indent.slice(0)
                };
                if (state.localState) s.localState = CodeMirror1.copyState(state.local.mode, state.localState);
                if (state.stack) s.stack = state.stack.slice(0);
                for(var pers = state.persistentStates; pers; pers = pers.next)s.persistentStates = {
                    mode: pers.mode,
                    spec: pers.spec,
                    state: pers.state == state.localState ? s.localState : CodeMirror1.copyState(pers.mode, pers.state),
                    next: s.persistentStates
                };
                return s;
            },
            token: tokenFunction(states_, config),
            innerMode: function innerMode(state) {
                return state.local && {
                    mode: state.local.mode,
                    state: state.localState
                };
            },
            indent: indentFunction(states_, meta)
        };
        if (meta) {
            for(var prop in meta)if (meta.hasOwnProperty(prop)) mode[prop] = meta[prop];
        }
        return mode;
    };
    function cmp(a, b) {
        if (a === b) return true;
        if (!a || typeof a != "object" || !b || typeof b != "object") return false;
        var props = 0;
        for(var prop in a)if (a.hasOwnProperty(prop)) {
            if (!b.hasOwnProperty(prop) || !cmp(a[prop], b[prop])) return false;
            props++;
        }
        for(var prop in b)if (b.hasOwnProperty(prop)) props--;
        return props == 0;
    }
});
});

var multiplex = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.multiplexingMode = function(outer /*, others */ ) {
        var indexOf = function indexOf(string, pattern, from, returnEnd) {
            if (typeof pattern == "string") {
                var found = string.indexOf(pattern, from);
                return returnEnd && found > -1 ? found + pattern.length : found;
            }
            var m = pattern.exec(from ? string.slice(from) : string);
            return m ? m.index + from + (returnEnd ? m[0].length : 0) : -1;
        };
        // Others should be {open, close, mode [, delimStyle] [, innerStyle] [, parseDelimiters]} objects
        var others = Array.prototype.slice.call(arguments, 1);
        return {
            startState: function startState() {
                return {
                    outer: CodeMirror1.startState(outer),
                    innerActive: null,
                    inner: null,
                    startingInner: false
                };
            },
            copyState: function copyState(state) {
                return {
                    outer: CodeMirror1.copyState(outer, state.outer),
                    innerActive: state.innerActive,
                    inner: state.innerActive && CodeMirror1.copyState(state.innerActive.mode, state.inner),
                    startingInner: state.startingInner
                };
            },
            token: function token(stream, state) {
                if (!state.innerActive) {
                    var cutOff = Infinity, oldContent = stream.string;
                    for(var i = 0; i < others.length; ++i){
                        var other = others[i];
                        var found = indexOf(oldContent, other.open, stream.pos);
                        if (found == stream.pos) {
                            if (!other.parseDelimiters) stream.match(other.open);
                            state.startingInner = !!other.parseDelimiters;
                            state.innerActive = other;
                            // Get the outer indent, making sure to handle CodeMirror.Pass
                            var outerIndent = 0;
                            if (outer.indent) {
                                var possibleOuterIndent = outer.indent(state.outer, "", "");
                                if (possibleOuterIndent !== CodeMirror1.Pass) outerIndent = possibleOuterIndent;
                            }
                            state.inner = CodeMirror1.startState(other.mode, outerIndent);
                            return other.delimStyle && other.delimStyle + " " + other.delimStyle + "-open";
                        } else if (found != -1 && found < cutOff) {
                            cutOff = found;
                        }
                    }
                    if (cutOff != Infinity) stream.string = oldContent.slice(0, cutOff);
                    var outerToken = outer.token(stream, state.outer);
                    if (cutOff != Infinity) stream.string = oldContent;
                    return outerToken;
                } else {
                    var curInner = state.innerActive, oldContent = stream.string;
                    if (!curInner.close && stream.sol()) {
                        state.innerActive = state.inner = null;
                        return this.token(stream, state);
                    }
                    var found = curInner.close && !state.startingInner ? indexOf(oldContent, curInner.close, stream.pos, curInner.parseDelimiters) : -1;
                    if (found == stream.pos && !curInner.parseDelimiters) {
                        stream.match(curInner.close);
                        state.innerActive = state.inner = null;
                        return curInner.delimStyle && curInner.delimStyle + " " + curInner.delimStyle + "-close";
                    }
                    if (found > -1) stream.string = oldContent.slice(0, found);
                    var innerToken = curInner.mode.token(stream, state.inner);
                    if (found > -1) stream.string = oldContent;
                    else if (stream.pos > stream.start) state.startingInner = false;
                    if (found == stream.pos && curInner.parseDelimiters) state.innerActive = state.inner = null;
                    if (curInner.innerStyle) {
                        if (innerToken) innerToken = innerToken + " " + curInner.innerStyle;
                        else innerToken = curInner.innerStyle;
                    }
                    return innerToken;
                }
            },
            indent: function indent(state, textAfter, line) {
                var mode = state.innerActive ? state.innerActive.mode : outer;
                if (!mode.indent) return CodeMirror1.Pass;
                return mode.indent(state.innerActive ? state.inner : state.outer, textAfter, line);
            },
            blankLine: function blankLine(state) {
                var mode = state.innerActive ? state.innerActive.mode : outer;
                if (mode.blankLine) {
                    mode.blankLine(state.innerActive ? state.inner : state.outer);
                }
                if (!state.innerActive) {
                    for(var i = 0; i < others.length; ++i){
                        var other = others[i];
                        if (other.open === "\n") {
                            state.innerActive = other;
                            state.inner = CodeMirror1.startState(other.mode, mode.indent ? mode.indent(state.outer, "", "") : 0);
                        }
                    }
                } else if (state.innerActive.close === "\n") {
                    state.innerActive = state.inner = null;
                }
            },
            electricChars: outer.electricChars,
            innerMode: function innerMode(state) {
                return state.inner ? {
                    state: state.inner,
                    mode: state.innerActive.mode
                } : {
                    state: state.outer,
                    mode: outer
                };
            }
        };
    };
});
});

var handlebars = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror, simple, multiplex);
})(function(CodeMirror1) {
    CodeMirror1.defineSimpleMode("handlebars-tags", {
        start: [
            {
                regex: /\{\{\{/,
                push: "handlebars_raw",
                token: "tag"
            },
            {
                regex: /\{\{!--/,
                push: "dash_comment",
                token: "comment"
            },
            {
                regex: /\{\{!/,
                push: "comment",
                token: "comment"
            },
            {
                regex: /\{\{/,
                push: "handlebars",
                token: "tag"
            }
        ],
        handlebars_raw: [
            {
                regex: /\}\}\}/,
                pop: true,
                token: "tag"
            }
        ],
        handlebars: [
            {
                regex: /\}\}/,
                pop: true,
                token: "tag"
            },
            // Double and single quotes
            {
                regex: /"(?:[^\\"]|\\.)*"?/,
                token: "string"
            },
            {
                regex: /'(?:[^\\']|\\.)*'?/,
                token: "string"
            },
            // Handlebars keywords
            {
                regex: />|[#\/]([A-Za-z_]\w*)/,
                token: "keyword"
            },
            {
                regex: /(?:else|this)\b/,
                token: "keyword"
            },
            // Numeral
            {
                regex: /\d+/i,
                token: "number"
            },
            // Atoms like = and .
            {
                regex: /=|~|@|true|false/,
                token: "atom"
            },
            // Paths
            {
                regex: /(?:\.\.\/)*(?:[A-Za-z_][\w\.]*)+/,
                token: "variable-2"
            }
        ],
        dash_comment: [
            {
                regex: /--\}\}/,
                pop: true,
                token: "comment"
            },
            // Commented code
            {
                regex: /./,
                token: "comment"
            }
        ],
        comment: [
            {
                regex: /\}\}/,
                pop: true,
                token: "comment"
            },
            {
                regex: /./,
                token: "comment"
            }
        ],
        meta: {
            blockCommentStart: "{{--",
            blockCommentEnd: "--}}"
        }
    });
    CodeMirror1.defineMode("handlebars", function(config, parserConfig) {
        var handlebars = CodeMirror1.getMode(config, "handlebars-tags");
        if (!parserConfig || !parserConfig.base) return handlebars;
        return CodeMirror1.multiplexingMode(CodeMirror1.getMode(config, parserConfig.base), {
            open: "{{",
            close: /\}\}\}?/,
            mode: handlebars,
            parseDelimiters: true
        });
    });
    CodeMirror1.defineMIME("text/x-handlebars-template", "handlebars");
});
});

var vue = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    {
        mod(codemirror, overlay, xml, javascript, coffeescript, css, sass, stylus, pug, handlebars);
    }
})(function(CodeMirror1) {
    var tagLanguages = {
        script: [
            [
                "lang",
                /coffee(script)?/,
                "coffeescript"
            ],
            [
                "type",
                /^(?:text|application)\/(?:x-)?coffee(?:script)?$/,
                "coffeescript"
            ],
            [
                "lang",
                /^babel$/,
                "javascript"
            ],
            [
                "type",
                /^text\/babel$/,
                "javascript"
            ],
            [
                "type",
                /^text\/ecmascript-\d+$/,
                "javascript"
            ]
        ],
        style: [
            [
                "lang",
                /^stylus$/i,
                "stylus"
            ],
            [
                "lang",
                /^sass$/i,
                "sass"
            ],
            [
                "lang",
                /^less$/i,
                "text/x-less"
            ],
            [
                "lang",
                /^scss$/i,
                "text/x-scss"
            ],
            [
                "type",
                /^(text\/)?(x-)?styl(us)?$/i,
                "stylus"
            ],
            [
                "type",
                /^text\/sass/i,
                "sass"
            ],
            [
                "type",
                /^(text\/)?(x-)?scss$/i,
                "text/x-scss"
            ],
            [
                "type",
                /^(text\/)?(x-)?less$/i,
                "text/x-less"
            ]
        ],
        template: [
            [
                "lang",
                /^vue-template$/i,
                "vue"
            ],
            [
                "lang",
                /^pug$/i,
                "pug"
            ],
            [
                "lang",
                /^handlebars$/i,
                "handlebars"
            ],
            [
                "type",
                /^(text\/)?(x-)?pug$/i,
                "pug"
            ],
            [
                "type",
                /^text\/x-handlebars-template$/i,
                "handlebars"
            ],
            [
                null,
                null,
                "vue-template"
            ]
        ]
    };
    CodeMirror1.defineMode("vue-template", function(config, parserConfig) {
        var mustacheOverlay = {
            token: function token(stream) {
                if (stream.match(/^\{\{.*?\}\}/)) return "meta mustache";
                while(stream.next() && !stream.match("{{", false)){}
                return null;
            }
        };
        return CodeMirror1.overlayMode(CodeMirror1.getMode(config, parserConfig.backdrop || "text/html"), mustacheOverlay);
    });
    CodeMirror1.defineMode("vue", function(config) {
        return CodeMirror1.getMode(config, {
            name: "htmlmixed",
            tags: tagLanguages
        });
    }, "htmlmixed", "xml", "javascript", "coffeescript", "css", "sass", "stylus", "pug", "handlebars");
    CodeMirror1.defineMIME("script/x-vue", "vue");
    CodeMirror1.defineMIME("text/x-vue", "vue");
});
});

var yaml = createCommonjsModule(function (module, exports) {
// CodeMirror, copyright (c) by Marijn Haverbeke and others
// Distributed under an MIT license: https://codemirror.net/LICENSE
(function(mod) {
    mod(codemirror);
})(function(CodeMirror1) {
    CodeMirror1.defineMode("yaml", function() {
        var cons = [
            "true",
            "false",
            "on",
            "off",
            "yes",
            "no"
        ];
        var keywordRegex = new RegExp("\\b((" + cons.join(")|(") + "))$", "i");
        return {
            token: function token(stream, state) {
                var ch = stream.peek();
                var esc = state.escaped;
                state.escaped = false;
                /* comments */ if (ch == "#" && (stream.pos == 0 || /\s/.test(stream.string.charAt(stream.pos - 1)))) {
                    stream.skipToEnd();
                    return "comment";
                }
                if (stream.match(/^('([^']|\\.)*'?|"([^"]|\\.)*"?)/)) return "string";
                if (state.literal && stream.indentation() > state.keyCol) {
                    stream.skipToEnd();
                    return "string";
                } else if (state.literal) {
                    state.literal = false;
                }
                if (stream.sol()) {
                    state.keyCol = 0;
                    state.pair = false;
                    state.pairStart = false;
                    /* document start */ if (stream.match("---")) {
                        return "def";
                    }
                    /* document end */ if (stream.match("...")) {
                        return "def";
                    }
                    /* array list item */ if (stream.match(/\s*-\s+/)) {
                        return "meta";
                    }
                }
                /* inline pairs/lists */ if (stream.match(/^(\{|\}|\[|\])/)) {
                    if (ch == "{") state.inlinePairs++;
                    else if (ch == "}") state.inlinePairs--;
                    else if (ch == "[") state.inlineList++;
                    else state.inlineList--;
                    return "meta";
                }
                /* list separator */ if (state.inlineList > 0 && !esc && ch == ",") {
                    stream.next();
                    return "meta";
                }
                /* pairs separator */ if (state.inlinePairs > 0 && !esc && ch == ",") {
                    state.keyCol = 0;
                    state.pair = false;
                    state.pairStart = false;
                    stream.next();
                    return "meta";
                }
                /* start of value of a pair */ if (state.pairStart) {
                    /* block literals */ if (stream.match(/^\s*(\||\>)\s*/)) {
                        state.literal = true;
                        return "meta";
                    }
                    /* references */ if (stream.match(/^\s*(\&|\*)[a-z0-9\._-]+\b/i)) {
                        return "variable-2";
                    }
                    /* numbers */ if (state.inlinePairs == 0 && stream.match(/^\s*-?[0-9\.\,]+\s?$/)) {
                        return "number";
                    }
                    if (state.inlinePairs > 0 && stream.match(/^\s*-?[0-9\.\,]+\s?(?=(,|}))/)) {
                        return "number";
                    }
                    /* keywords */ if (stream.match(keywordRegex)) {
                        return "keyword";
                    }
                }
                /* pairs (associative arrays) -> key */ if (!state.pair && stream.match(/^\s*(?:[,\[\]{}&*!|>'"%@`][^\s'":]|[^,\[\]{}#&*!|>'"%@`])[^#]*?(?=\s*:($|\s))/)) {
                    state.pair = true;
                    state.keyCol = stream.indentation();
                    return "atom";
                }
                if (state.pair && stream.match(/^:\s*/)) {
                    state.pairStart = true;
                    return "meta";
                }
                /* nothing found, continue */ state.pairStart = false;
                state.escaped = ch == "\\";
                stream.next();
                return null;
            },
            startState: function startState() {
                return {
                    pair: false,
                    pairStart: false,
                    keyCol: 0,
                    inlinePairs: 0,
                    inlineList: 0,
                    literal: false,
                    escaped: false
                };
            },
            lineComment: "#",
            fold: "indent"
        };
    });
    CodeMirror1.defineMIME("text/x-yaml", "yaml");
    CodeMirror1.defineMIME("text/yaml", "yaml");
});
});

var languages = [
    {
        name: "JavaScript",
        mimes: [
            "text/javascript",
            "text/ecmascript",
            "application/javascript",
            "application/x-javascript",
            "application/ecmascript"
        ],
        mode: "javascript",
        ext: [
            "js"
        ],
        alias: [
            "ecmascript",
            "js",
            "node"
        ]
    },
    {
        name: "Java",
        mime: "text/x-java",
        mode: "clike",
        ext: [
            "java"
        ]
    },
    {
        name: "JSON",
        mimes: [
            "application/json",
            "application/x-json"
        ],
        mode: "javascript",
        ext: [
            "json",
            "map"
        ],
        alias: [
            "json5"
        ]
    },
    {
        name: "Shell",
        mimes: [
            "text/x-sh",
            "application/x-sh"
        ],
        mode: "shell",
        ext: [
            "sh",
            "ksh",
            "bash"
        ],
        alias: [
            "bash",
            "sh",
            "zsh"
        ],
        file: /^PKGBUILD$/
    },
    {
        name: "HTML",
        mime: "text/html",
        mode: "htmlmixed",
        ext: [
            "html",
            "htm",
            "handlebars",
            "hbs"
        ],
        alias: [
            "xhtml"
        ]
    },
    {
        name: "Plain Text",
        mime: "text/plain",
        mode: "null",
        ext: [
            "txt",
            "text",
            "conf",
            "def",
            "list",
            "log"
        ]
    },
    {
        name: "C",
        mime: "text/x-csrc",
        mode: "clike",
        ext: [
            "c",
            "h",
            "ino"
        ]
    },
    {
        name: "C++",
        mime: "text/x-c++src",
        mode: "clike",
        ext: [
            "cpp",
            "c++",
            "cc",
            "cxx",
            "hpp",
            "h++",
            "hh",
            "hxx"
        ],
        alias: [
            "cpp"
        ]
    },
    {
        name: "C#",
        mime: "text/x-csharp",
        mode: "clike",
        ext: [
            "cs"
        ],
        alias: [
            "csharp"
        ]
    },
    {
        name: "CSS",
        mime: "text/css",
        mode: "css",
        ext: [
            "css"
        ]
    },
    {
        name: "Dart",
        mimes: [
            "application/dart",
            "text/x-dart"
        ],
        mode: "dart",
        ext: [
            "dart"
        ]
    },
    {
        name: "Elm",
        mime: "text/x-elm",
        mode: "elm",
        ext: [
            "elm"
        ]
    },
    {
        name: "Go",
        mime: "text/x-go",
        mode: "go",
        ext: [
            "go"
        ]
    },
    {
        name: "Groovy",
        mime: "text/x-groovy",
        mode: "groovy",
        ext: [
            "groovy",
            "gradle"
        ],
        file: /^Jenkinsfile$/
    },
    {
        name: "HTTP",
        mime: "message/http",
        mode: "http"
    },
    {
        name: "JSX",
        mime: "text/jsx",
        mode: "jsx",
        ext: [
            "jsx"
        ]
    },
    {
        name: "Kotlin",
        mime: "text/x-kotlin",
        mode: "clike",
        ext: [
            "kt"
        ]
    },
    {
        name: "LaTeX",
        mime: "text/x-latex",
        mode: "stex",
        ext: [
            "text",
            "ltx",
            "tex"
        ],
        alias: [
            "tex"
        ]
    },
    {
        name: "Lua",
        mime: "text/x-lua",
        mode: "lua",
        ext: [
            "lua"
        ]
    },
    {
        name: "Markdown",
        mime: "text/x-markdown",
        mode: "markdown",
        ext: [
            "markdown",
            "md",
            "mkd"
        ]
    },
    {
        name: "Nginx",
        mime: "text/x-nginx-conf",
        mode: "nginx",
        file: /nginx.*\.conf$/i
    },
    {
        name: "Objective-C",
        mime: "text/x-objectivec",
        mode: "clike",
        ext: [
            "m",
            "mm"
        ],
        alias: [
            "objective-c",
            "objc"
        ]
    },
    {
        name: "Perl",
        mime: "text/x-perl",
        mode: "perl",
        ext: [
            "pl",
            "pm"
        ]
    },
    {
        name: "PHP",
        mimes: [
            "text/x-php",
            "application/x-httpd-php",
            "application/x-httpd-php-open"
        ],
        mode: "php",
        ext: [
            "php",
            "php3",
            "php4",
            "php5",
            "php7",
            "phtml"
        ]
    },
    {
        name: "PowerShell",
        mime: "application/x-powershell",
        mode: "powershell",
        ext: [
            "ps1",
            "psd1",
            "psm1"
        ]
    },
    {
        name: "Python",
        mime: "text/x-python",
        mode: "python",
        ext: [
            "BUILD",
            "bzl",
            "py",
            "pyw"
        ],
        file: /^(BUCK|BUILD)$/
    },
    {
        name: "R",
        mime: "text/x-rsrc",
        mode: "r",
        ext: [
            "r",
            "R"
        ],
        alias: [
            "rscript"
        ]
    },
    {
        name: "Ruby",
        mime: "text/x-ruby",
        mode: "ruby",
        ext: [
            "rb"
        ],
        alias: [
            "jruby",
            "macruby",
            "rake",
            "rb",
            "rbx"
        ]
    },
    {
        name: "Sass",
        mime: "text/x-sass",
        mode: "sass",
        ext: [
            "sass"
        ]
    },
    {
        name: "Scala",
        mime: "text/x-scala",
        mode: "clike",
        ext: [
            "scala"
        ]
    },
    {
        name: "SQL",
        mime: "text/x-sql",
        mode: "sql",
        ext: [
            "sql"
        ]
    },
    {
        name: "Stylus",
        mime: "text/x-styl",
        mode: "stylus",
        ext: [
            "styl"
        ]
    },
    {
        name: "TypeScript",
        mime: "text/typescript",
        mode: "text/typescript",
        ext: [
            "ts"
        ]
    },
    {
        name: "Swift",
        mime: "text/x-swift",
        mode: "swift",
        ext: [
            "swift"
        ]
    },
    {
        name: "Vue.js Component",
        mimes: [
            "script/x-vue",
            "text/x-vue"
        ],
        mode: "vue",
        ext: [
            "vue"
        ]
    },
    {
        name: "XML",
        mimes: [
            "application/xml",
            "text/xml"
        ],
        mode: "xml",
        ext: [
            "xml",
            "xsl",
            "xsd",
            "svg"
        ],
        alias: [
            "rss",
            "wsdl",
            "xsd"
        ]
    },
    {
        name: "YAML",
        mimes: [
            "text/x-yaml",
            "text/yaml"
        ],
        mode: "yaml",
        ext: [
            "yaml",
            "yml"
        ],
        alias: [
            "yml"
        ]
    },
    {
        name: "PlantUML",
        mode: "plantuml"
    }
];
var modeMap = {};
languages.forEach(function(param) {
    var name = param.name, mime = param.mime, mode = param.mode;
    if (mode === "clike" || mode === "sql") {
        modeMap[name.toLowerCase()] = Array.isArray(mime) ? mime[0] : mime;
    } else {
        modeMap[name.toLowerCase()] = mode;
    }
});
function getMode(language) {
    return modeMap[(language || "").toLowerCase()] || "null";
}

var css_248z$2 = ".cm-s-xq-light span.cm-keyword{line-height:1em;font-weight:700;color:#5a5cad}.cm-s-xq-light span.cm-atom{color:#6c8cd5}.cm-s-xq-light span.cm-number{color:#164}.cm-s-xq-light span.cm-def{text-decoration:underline}.cm-s-xq-light span.cm-type,.cm-s-xq-light span.cm-variable,.cm-s-xq-light span.cm-variable-2,.cm-s-xq-light span.cm-variable-3{color:#000}.cm-s-xq-light span.cm-comment{color:#0080ff;font-style:italic}.cm-s-xq-light span.cm-string{color:red}.cm-s-xq-light span.cm-meta{color:#ff0}.cm-s-xq-light span.cm-qualifier{color:grey}.cm-s-xq-light span.cm-builtin{color:#7ea656}.cm-s-xq-light span.cm-bracket{color:#cc7}.cm-s-xq-light span.cm-tag{color:#3f7f7f}.cm-s-xq-light span.cm-attribute{color:#7f007f}.cm-s-xq-light span.cm-error{color:red}.cm-s-xq-light .CodeMirror-activeline-background{background:#e8f2ff}.cm-s-xq-light .CodeMirror-matchingbracket{outline:1px solid grey;color:#000!important;background:#ff0}.CodeMirror{height:auto;background:#fcfcfc}.CodeMirror div{box-sizing:content-box}.CodeMirror pre{white-space:pre!important;padding:0 10px}.CodeMirror pre span{font-family:monospace}.CodeMirror .CodeMirror-sizer{margin-left:46px!important}.CodeMirror .CodeMirror-lines{padding:10px 0}.CodeMirror .CodeMirror-line{counter-increment:linenumber;margin:0}.CodeMirror .CodeMirror-line:before{content:counter(linenumber);position:absolute;display:block;left:-46px;width:32px;line-height:24px;text-align:right;font-size:12px;color:#9b9b9b}.cm-s-xq-light span.cm-def{text-decoration:none}.cm-s-xq-light span.cm-meta{color:red}.cm-s-xq-light span.cm-type{color:green}.CodeMirror-cursor{width:2px!important}";
styleInject(css_248z$2);

var modeSet = new Set();
languages.forEach(function(param) {
    var mode = param.mode;
    if (!modeSet.has(mode) && mode !== "null" && mode !== "plantuml") {
        modeSet.add(mode);
    }
});

var css_248z$3 = '.ct-code-toolbar{align-items:center;background:#f2f2f2;box-sizing:border-box;display:flex;height:30px;padding-left:10px;position:relative;width:100%}.ct-code-toolbar .padding{cursor:pointer;height:100%;width:10px}.ct-code-toolbar .expand{cursor:pointer;height:100%;background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 standalone%3D%22no%22%3F%3E%3C!DOCTYPE svg PUBLIC %22-%2F%2FW3C%2F%2FDTD SVG 1.1%2F%2FEN%22 %22http%3A%2F%2Fwww.w3.org%2FGraphics%2FSVG%2F1.1%2FDTD%2Fsvg11.dtd%22%3E%3Csvg t%3D%221526953416889%22 class%3D%22icon%22 style%3D%22%22 viewBox%3D%220 0 1024 1024%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 p-id%3D%223453%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22 width%3D%228%22 height%3D%228%22%3E%3Cdefs%3E%3Cstyle type%3D%22text%2Fcss%22%3E%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath d%3D%22M375.966782 310.02559401L28.2073 642.809787a89.339756 89.339756 0 0 0 0 130.169816 99.323282 99.323282 0 0 0 136.057537 0L512.024319 440.323405 859.7838 773.107597a99.323282 99.323282 0 0 0 136.057538 0 89.339756 89.339756 0 0 0 0-130.169817l-347.759482-332.784192-65.916869-59.389179a105.722978 105.722978 0 0 0-140.281336 0l-65.916869 59.389179z%22 fill%3D%22%23747272%22 p-id%3D%223454%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E") no-repeat 50%;transition:transform .2s;width:24px}.ct-code-toolbar .collapse{transform:rotate(-180deg)}.ct-code-toolbar .title{color:#333;cursor:text;flex:1;font-size:12px;font-weight:700;height:30px;line-height:30px;outline:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ct-code-toolbar .ct-code-lang-select-list{background:#fff;border-radius:4px;box-shadow:0 4px 16px 0 rgba(106,93,73,.2),0 8px 20px 0 rgba(106,93,73,.1);list-style:none;margin:0;overflow-y:auto;padding:8px 0;position:fixed;width:140px;z-index:11}.ct-code-toolbar .ct-code-lang-select-list ::-webkit-scrollbar{background-color:#f5f5f5;height:8px;width:6px}.ct-code-toolbar .ct-code-lang-select-list ::-webkit-scrollbar-thumb{background-color:#ddd;border-radius:3px}.ct-code-toolbar .list-item{color:#333;cursor:pointer;font-size:12px;height:24px;line-height:24px;padding-left:16px}.ct-code-toolbar .active,.ct-code-toolbar .list-item:hover{background:#f6f6f6}.ct-code-lang-select{height:30px;line-height:30px;overflow:hidden;width:140px}.ct-code-lang-select .lang{color:#999;cursor:pointer;font-family:Tahoma;font-size:12px;height:30px;overflow:hidden;padding-right:32px;text-align:right;text-overflow:ellipsis;white-space:nowrap}.ct-code-lang-select .lang:after{content:"";position:absolute;width:8px;height:8px;margin:0 8px;top:12px;transition:transform .2s;background-size:8px 8px;background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 standalone%3D%22no%22%3F%3E%3C!DOCTYPE svg PUBLIC %22-%2F%2FW3C%2F%2FDTD SVG 1.1%2F%2FEN%22 %22http%3A%2F%2Fwww.w3.org%2FGraphics%2FSVG%2F1.1%2FDTD%2Fsvg11.dtd%22%3E%3Csvg t%3D%221513063069381%22 class%3D%22icon%22 style%3D%22%22 viewBox%3D%220 0 1638 1024%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 p-id%3D%222306%22 id%3D%22mx_n_1513063069382%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22 width%3D%228%22 height%3D%228%22%3E    %3Cdefs%3E        %3Cstyle type%3D%22text%2Fcss%22%3E%3C%2Fstyle%3E    %3C%2Fdefs%3E    %3Cpath d%3D%22M1595.8016 262.7584a160.5632 160.5632 0 0 0 31.5392-167.936A145.2032 145.2032 0 0 0 1493.4016 0H144.9984a145.408 145.408 0 0 0-133.9392 95.0272A163.0208 163.0208 0 0 0 0 154.0096c0 39.936 14.7456 79.2576 42.3936 108.7488L716.8 978.944c27.2384 28.8768 64.1024 45.056 102.4 45.056 38.5024 0 75.3664-16.384 102.4-45.056l674.2016-716.1856z%22 fill%3D%22%23B0B0B0%22 p-id%3D%222307%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E") no-repeat 0 0}.ct-code-lang-select .select-expand:after{transform:rotate(180deg)}';
styleInject(css_248z$3);

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var DEFAULT_TITLE = "代码块";
var ToolBar = /*#__PURE__*/ function(Component) {
    _inherits(ToolBar, Component);
    var _super = _createSuper(ToolBar);
    function ToolBar(props) {
        _classCallCheck(this, ToolBar);
        var _this;
        _this = _super.call(this, props);
        _this.toggleDisplay = function() {
            var _this_props = _this.props, toggleDisplay = _this_props.toggleDisplay, isExpand = _this_props.isExpand;
            toggleDisplay(!isExpand);
        };
        _this.updateTitle = function() {
            var textContent = _this.title.textContent;
            var _this_props = _this.props, updateTitle = _this_props.updateTitle, title = _this_props.title;
            if (textContent !== title) {
                updateTitle(textContent);
            }
        };
        _this.handleClick = function(e) {
            e.preventDefault();
            e.stopPropagation();
            var _this_props = _this.props, changeLanguage = _this_props.changeLanguage, language = _this_props.language;
            var chosenLang = e.target.textContent;
            if (chosenLang === language) {
                return;
            }
            changeLanguage(chosenLang);
            // 默认代码块语言只有用户自己选择变更之后才会更改，不受其他人影响
            localStorage.setItem("cmLanguage", chosenLang);
            _this.setState({
                isShow: false
            });
        };
        _this.hideSelect = function() {
            var isShow = _this.state.isShow;
            if (isShow) {
                _this.setState({
                    isShow: false
                });
            }
        };
        _this.toggleShow = function(e) {
            e.preventDefault();
            e.stopPropagation();
            var isShow = _this.state.isShow;
            if (_this.toolbar.offsetWidth < 100) return;
            var _e_target_getBoundingClientRect = e.target.getBoundingClientRect(), left = _e_target_getBoundingClientRect.left, top = _e_target_getBoundingClientRect.top;
            var _document_body_getBoundingClientRect = document.body.getBoundingClientRect(), pageBottom = _document_body_getBoundingClientRect.bottom;
            var ulTop = top + e.target.offsetHeight;
            var maxHeight = Math.min(Math.max(65, pageBottom - ulTop - 7), 360); // 下拉框高度根据页面底部距离调整
            _this.setState({
                isShow: !isShow,
                left: left,
                top: ulTop,
                maxHeight: maxHeight
            });
        };
        _this.title = null;
        _this.toolbar = null;
        _this.state = {
            isShow: false,
            left: 0,
            top: 0,
            maxHeight: 360
        };
        return _this;
    }
    var _proto = ToolBar.prototype;
    _proto.render = function render() {
        var _this = this;
        var _this_props = this.props, setSelection = _this_props.setSelection, languages = _this_props.languages, title = _this_props.title, language = _this_props.language, isExpand = _this_props.isExpand;
        var _this_state = this.state, isShow = _this_state.isShow, left = _this_state.left, top = _this_state.top, maxHeight = _this_state.maxHeight;
        return /*#__PURE__*/ React.createElement("div", {
            className: "ct-code-toolbar",
            ref: function(refs) {
                _this.toolbar = refs;
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: "expand ".concat(isExpand ? "" : "collapse"),
            onClick: this.toggleDisplay
        }), /*#__PURE__*/ React.createElement("div", {
            className: "title",
            contentEditable: true,
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML: {
                __html: title ? DOMPurify.sanitize(title) : DEFAULT_TITLE
            },
            ref: function(refs) {
                _this.title = refs;
            },
            onFocus: setSelection,
            onBlur: this.updateTitle
        }), /*#__PURE__*/ React.createElement("div", {
            className: "ct-code-lang-select",
            onMouseLeave: this.hideSelect
        }, /*#__PURE__*/ React.createElement("div", {
            className: "lang ".concat(isShow ? "select-expand" : ""),
            onClick: this.toggleShow
        }, language)), isShow && /*#__PURE__*/ React.createElement("ul", {
            className: "ct-code-lang-select-list",
            style: {
                left: "".concat(left, "px"),
                top: "".concat(top, "px"),
                maxHeight: "".concat(maxHeight, "px")
            },
            onMouseLeave: this.hideSelect,
            onMouseEnter: function() {
                return _this.setState({
                    isShow: true
                });
            }
        }, languages.map(function(param) {
            var name = param.name;
            /*#__PURE__*/ return React.createElement("li", {
                className: "list-item ".concat((language === null || language === void 0 ? void 0 : language.toLowerCase()) === (name === null || name === void 0 ? void 0 : name.toLowerCase()) ? "active" : ""),
                key: name,
                onClick: _this.handleClick
            }, name);
        })));
    };
    return ToolBar;
}(Component);

function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function update(attrs, pos) {
    return function(state, dispatch) {
        var tr = state.tr, schema = state.schema;
        dispatch(tr.setNodeMarkup(pos, schema.nodes.code_block, attrs).scrollIntoView());
    };
}
function computeChange(oldVal, newVal) {
    if (oldVal === newVal) return null;
    var start = 0;
    var oldEnd = oldVal.length;
    var newEnd = newVal.length;
    while(start < oldEnd && oldVal.charCodeAt(start) === newVal.charCodeAt(start)){
        ++start;
    }
    while(oldEnd > start && newEnd > start && oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)){
        oldEnd--;
        newEnd--;
    }
    return {
        from: start,
        to: oldEnd,
        text: newVal.slice(start, newEnd)
    };
}
var CodeBlockView = /*#__PURE__*/ function() {
    function CodeBlockView(node, view, getPos) {
        var _this = this;
        _classCallCheck$1(this, CodeBlockView);
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.dom = document.createElement("div");
        this.updateAttrs = function(attrs) {
            var _this_view = _this.view, state = _this_view.state, dispatch = _this_view.dispatch;
            var newAttrs = _objectSpread({}, _this.node.attrs, attrs);
            var command = update(newAttrs, _this.getPos());
            command(state, dispatch);
        };
        this.toggleDisplay = function(isExpand) {
            var $cm = _this.cm.getWrapperElement();
            _this.cm.focus();
            if (isExpand) {
                $cm.style.cssText = "height:auto !important";
                _this.cm.refresh();
            } else {
                $cm.style.cssText = "height:0 !important";
            }
            _this.updateAttrs({
                isExpand: isExpand
            });
        };
        this.updateTitle = function(title) {
            _this.updateAttrs({
                title: title
            });
        };
        this.changeLanguage = function(language) {
            _this.cm.setOption("mode", getMode(language));
            _this.updateAttrs({
                language: language
            });
            _this.cm.focus();
        };
        this.setTextSelection = function() {
            var _this_view = _this.view, state = _this_view.state, dispatch = _this_view.dispatch;
            var doc = state.doc, tr = state.tr;
            var pos = _this.getPos() + 1;
            var selection = TextSelection.create(doc, pos, pos);
            dispatch(tr.setSelection(selection));
        };
        // Store for later
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        var _this_node_attrs = this.node.attrs, language = _this_node_attrs.language, theme = _this_node_attrs.theme;
        var value = this.node.textContent;
        this.cm = new CodeMirror(null, {
            value: value,
            matchBrackets: true,
            tabSize: 2,
            mode: getMode(language),
            theme: theme,
            // readOnly: false,
            extraKeys: this.codeMirrorKeymap()
        });
        this.dom = this.createDom();
        // CodeMirror needs to be in the DOM to properly initialize, so
        // schedule it to update itself
        setTimeout(function() {
            _this.cm.refresh();
        }, 20);
        // This flag is used to avoid an update loop between the outer and
        // inner editor
        this.updating = false;
        this.cm.on("changes", function() {
            if (!_this.updating) _this.valueChanged();
        });
        this.cm.on("focus", function() {
            _this.forwardSelection();
        });
        this.cm.on("drop", function(c, e) {
            var htmlData = e.dataTransfer.getData("text/html");
            if (htmlData && htmlData[1] !== "p" && htmlData[1] !== "h") {
                e.stopPropagation();
                e.preventDefault();
                return false;
            }
        });
    }
    var _proto = CodeBlockView.prototype;
    _proto.createDom = function createDom() {
        var _this_node = this.node, attrs = _this_node.attrs, _this_node_attrs = _this_node.attrs, dataDiffId = _this_node_attrs.dataDiffId, title = _this_node_attrs.title, language = _this_node_attrs.language, isExpand = _this_node_attrs.isExpand;
        var $cm = this.cm.getWrapperElement();
        if (!attrs.isExpand) {
            $cm.style.cssText = "height:0 !important";
        }
        // 创建工具栏
        var $toolbar = document.createElement("div");
        this.$toolbar = $toolbar;
        ReactDOM.render(/*#__PURE__*/ React.createElement(ToolBar, {
            title: title,
            language: language,
            isExpand: isExpand,
            languages: languages,
            setSelection: this.setTextSelection,
            toggleDisplay: this.toggleDisplay,
            updateTitle: this.updateTitle,
            changeLanguage: this.changeLanguage
        }), this.$toolbar);
        var $code = document.createElement("div");
        $code.className = "ct-code";
        if (dataDiffId) {
            $code.setAttribute("data-diff-id", dataDiffId);
        }
        $code.appendChild($toolbar);
        $code.appendChild($cm);
        return $code;
    };
    _proto.forwardSelection = function forwardSelection() {
        if (!this.cm.hasFocus()) return;
        var state = this.view.state;
        var selection = this.asProseMirrorSelection(state.doc);
        if (!selection.eq(state.selection)) {
            this.view.dispatch(state.tr.setSelection(selection));
        }
    };
    _proto.asProseMirrorSelection = function asProseMirrorSelection(doc) {
        var offset = this.getPos() + 1;
        var anchor = this.cm.indexFromPos(this.cm.getCursor("anchor")) + offset;
        var head = this.cm.indexFromPos(this.cm.getCursor("head")) + offset;
        return TextSelection.create(doc, anchor, head);
    };
    _proto.valueChanged = function valueChanged() {
        var change = computeChange(this.node.textContent, this.cm.getValue());
        if (change) {
            var _this_view_state = this.view.state, tr = _this_view_state.tr, schema = _this_view_state.schema;
            var start = this.getPos() + 1;
            var from = start + change.from;
            var to = start + change.to;
            var content = change.text ? schema.text(change.text) : null;
            this.view.dispatch(tr.replaceWith(from, to, content));
        }
    };
    _proto.codeMirrorKeymap = function codeMirrorKeymap() {
        var _this = this;
        var view = this.view;
        var mod = /Mac/.test(navigator.platform) ? "Cmd" : "Ctrl";
        var _obj;
        return CodeMirror.normalizeKeyMap((_obj = {
            Up: function() {
                return _this.maybeEscape("line", -1);
            },
            Left: function() {
                return _this.maybeEscape("char", -1);
            },
            Down: function() {
                return _this.maybeEscape("line", 1);
            },
            Right: function() {
                return _this.maybeEscape("char", 1);
            }
        }, _defineProperty(_obj, "".concat(mod, "-Z"), function() {
            undo(view.state, view.dispatch);
        }), _defineProperty(_obj, "".concat(mod, "-A"), function() {
            return _this.selectAll();
        }), _defineProperty(_obj, "Shift-".concat(mod, "-Z"), function() {
            redo(view.state, view.dispatch);
        }), _defineProperty(_obj, "Shift-".concat(mod, "-A"), function() {
            return _this.selectAllContent();
        }), _defineProperty(_obj, "Shift-Enter", function() {
            _this.cm.execCommand("newlineAndIndent");
        }), _defineProperty(_obj, "".concat(mod, "-Y"), function() {
            redo(view.state, view.dispatch);
        }), _defineProperty(_obj, "Ctrl-Enter", function() {
            if (exitCode(view.state, view.dispatch)) view.focus();
        }), _obj));
    };
    // eslint-disable-next-line consistent-return
    _proto.maybeEscape = function maybeEscape(unit, dir) {
        var pos = this.cm.getCursor();
        if (this.cm.somethingSelected() || pos.line !== (dir < 0 ? this.cm.firstLine() : this.cm.lastLine()) || unit === "char" && pos.ch !== (dir < 0 ? 0 : this.cm.getLine(pos.line).length)) {
            return CodeMirror.Pass;
        }
        this.view.focus();
        var targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize);
        var selection = Selection.near(this.view.state.doc.resolve(targetPos), dir);
        this.view.dispatch(this.view.state.tr.setSelection(selection).scrollIntoView());
        this.view.focus();
    };
    _proto.selectAllContent = function selectAllContent() {
        this.cm.focus();
        this.cm.execCommand("selectAll");
    };
    // TODO: Command+A表现与老编辑器不一致
    _proto.selectAll = function selectAll() {
        var _this_view = this.view, state = _this_view.state, dispatch = _this_view.dispatch;
        var allSelection = new AllSelection(state.doc);
        this.view.focus();
        dispatch(state.tr.setSelection(allSelection));
    };
    // NodeView的方法，该方法将会被调用以用来处理节点内部选区的设置
    _proto.setSelection = function setSelection(anchor, head) {
        this.cm.focus();
        this.updating = true;
        this.cm.setSelection(this.cm.posFromIndex(anchor), this.cm.posFromIndex(head));
        this.updating = false;
    };
    // NodeView的方法，当 NodeView 更新自身的时候会调用该节点的此方法
    _proto.update = function update(node) {
        if (node.type !== this.node.type) return false;
        this.node = node;
        var change = computeChange(this.cm.getValue(), node.textContent);
        if (change) {
            this.updating = true;
            this.cm.replaceRange(change.text, this.cm.posFromIndex(change.from), this.cm.posFromIndex(change.to));
            this.updating = false;
        }
        var _this_node = this.node, _this_node_attrs = _this_node.attrs, title = _this_node_attrs.title, language = _this_node_attrs.language, isExpand = _this_node_attrs.isExpand;
        var $cm = this.cm.getWrapperElement();
        if (isExpand) {
            $cm.style.cssText = "height:auto !important";
        } else {
            $cm.style.cssText = "height:0 !important";
        }
        ReactDOM.render(/*#__PURE__*/ React.createElement(ToolBar, {
            title: title,
            language: language,
            isExpand: isExpand,
            languages: languages,
            setSelection: this.setTextSelection,
            toggleDisplay: this.toggleDisplay,
            updateTitle: this.updateTitle,
            changeLanguage: this.changeLanguage
        }), this.$toolbar);
        return true;
    };
    // NodeView的方法 用来阻止编辑器处理一些或者所有的源自 NodeView 的 DOM 事件。返回 true 表示编辑器不处理该事件。
    _proto.stopEvent = function stopEvent() {
        return true;
    };
    return CodeBlockView;
}();

function _assertThisInitialized$1(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$2(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _getPrototypeOf$1(o) {
    _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$1(o);
}
function _inherits$1(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$1(subClass, superClass);
}
function _possibleConstructorReturn$1(self, call) {
    if (call && (_typeof$1(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$1(self);
}
function _setPrototypeOf$1(o, p) {
    _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$1(o, p);
}
var _typeof$1 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$1() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$1(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$1();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$1(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$1(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$1(this, result);
    };
}
var CodeExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits$1(CodeExtension, NodeExtension);
    var _super = _createSuper$1(CodeExtension);
    function CodeExtension() {
        _classCallCheck$2(this, CodeExtension);
        return _super.apply(this, arguments);
    }
    var _proto = CodeExtension.prototype;
    _proto.plugin = function plugin() {
        return new Plugin({
            key: new PluginKey(this.name),
            props: {
                decorations: function(state) {
                    var selection = state.selection, schema = state.schema, doc = state.doc;
                    var from = selection.from, to = selection.to;
                    var decos = [];
                    var code_block = schema.nodes.code_block;
                    var codeNode = findParentNode(function(item) {
                        return item.type && item.type.name === "code_block";
                    })(selection);
                    if (codeNode) {
                        var attrs = {
                            class: "ct-code-show"
                        };
                        var deco = Decoration.node(codeNode.pos, codeNode.pos + codeNode.node.nodeSize, attrs);
                        decos.push(deco);
                    }
                    var node = doc.nodeAt(from);
                    if (node && node.type === code_block && from + node.nodeSize === to) {
                        var deco1 = Decoration.node(from, to, {
                            nodeName: "div",
                            class: "ct-code-selected"
                        });
                        decos.push(deco1);
                    }
                    return decos.length > 0 ? DecorationSet.create(doc, decos) : DecorationSet.empty;
                },
                nodeViews: {
                    code_block: function(node, view, getPos) {
                        return new CodeBlockView(node, view, getPos);
                    }
                }
            }
        });
    };
    _proto.keys = function keys(param) {
        var editorEventEmitter = param.editorEventEmitter;
        return {
            Backspace: convertCommand(backspaceAfterCode()),
            "Mod-Alt-c": convertCommand(execAfterCommands(insert, function() {
                return editorEventEmitter.emit(AFTER_INSERT, {
                    braceWidth: 250
                });
            })())
        };
    };
    _proto.commands = function commands(param) {
        var getState = param.getState, editorEventEmitter = param.editorEventEmitter;
        return {
            insert: execAfterCommands(insert, function() {
                return editorEventEmitter.emit(AFTER_INSERT, {
                    braceWidth: 250
                });
            }),
            isInCode: isInCode,
            isCodeBlockDisabled: function() {
                return function() {
                    return !canInsertTypes(getState());
                };
            }
        };
    };
    _proto.inputRules = function inputRules(param) {
        var type = param.type;
        return [
            blockInputRule(new RegExp(regexpCode), type)
        ];
    };
    _proto.transformPastedHTML = function transformPastedHTML(html) {
        // PPT粘贴
        if (html.includes('xmlns:m="http://schemas.microsoft.com/office/2004/12/omml"')) {
            return html;
        }
        // 代码块除外的换行符转换为br
        if (!html.includes("ct-code") && !html.includes("ct-html")) {
            html = html.replace(/<body>([\s\S]*)<\/body>/g, function(word) {
                return word.replace(/\n</g, "<").replace(/>\s*\n/g, ">").replace(/\n/g, "<br />");
            });
        }
        // 浏览态复制代码块换行符
        if (html.includes("ct-code")) {
            html = html.replace(/<div class="title[^>]*?>([^<].*?)<\/div>/g, '<div class="title" data-title="$1"></div>').replace(/<div class="language[^>]*?>([^<].*?)<\/div>/g, '<div class="language" data-language="$1"></div>').replace(/<span[^>]*?cm-text.*?<\/span>/g, "").replace(/(<pre[^>]*?CodeMirror-line.*?)(<\/pre>)/g, "$1\n$2");
        }
        return html;
    };
    _createClass(CodeExtension, [
        {
            key: "name",
            get: function get() {
                return "code_block";
            }
        }
    ]);
    return CodeExtension;
}(NodeExtension);

export { CodeExtension };
//# sourceMappingURL=index.js.map
